import { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from './supabaseClient';
import TKTooltip from './TKTooltip';
import NewClientWizard from './wizards/NewClientWizard';
import InviteTeamMemberWizard from './wizards/InviteTeamMemberWizard';
import NewInvoiceWizard from './wizards/NewInvoiceWizard';
import MonthEndWizard from './wizards/MonthEndWizard';
import RateReviewWizard from './wizards/RateReviewWizard';
import ClientMap from './ClientMap';
import BulkImportModal from './BulkImport';
import ImportHistory from './BulkImport/ImportHistory';
import ClientBillingTerms from './ClientBillingTerms';
import ContractsView from './ContractsView';
import MemberContracts from './MemberContracts';
import MyContractsView from './MyContractsView';
import DashboardShareModal from './DashboardShareModal';
import { TabBar, SegmentedControl, StatTiles, Page, Controls, SearchInput, StatusPills, StatusBadge, DataTable, DetailPanel, EmptyState, RowMenu } from './ui';
import { fmtMoney, fmtPercent, fmtHours, fmtRate, fmtDate, fmtPeriod, fmtDue, fmtTimeRange, hoursDecimal, setFormatContext, billingModelLabel } from './format';
import { workValue, memberPay, provisionalNote, marginPct, hasCompSeat, monthsBetween,
         roundBillingHours, distributeRoundedHours } from './metrics';
import { shouldGateBillableSeatUpgrade, isFreeClientCapReached, canSubmitContractorInvoice } from './pricingGates';
import { useOrg } from './orgContext';
import { CHROME_STORE_URL, inviteUrl as buildInviteUrl } from './links';
import { tzOptions, currencyOptions, tzLabel } from './regional';
import { useGettingStarted, GettingStartedPill, GettingStartedPanel } from './GettingStarted';
import { buildPatch, entriesLandingInClosedMonth, planTagChanges, chunk, selectableEntries } from './bulkEntries';
import { filterReportEntries, clientLevelPasses, filterFileSuffix } from './reportFilters';
import { computeEarnings, payStructureLabel } from './earnings';
import { calcOwed, calcOwedFromTerms, modelLabel, modelBadge, overageHours, configFromClient, BILLING_MODELS, MODEL_ORDER } from './billingModels';
import { buildContractorInvoicePdfBase64Async } from './invoicePdf';
import { ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from 'recharts';

// Contracts is still actively being built (guided signing, field placement) and
// send-contract isn't deployed to prod yet -- not ready for real customers.
// Defaults ENABLED (staging, local dev) so the module stays fully testable;
// the prod build sets REACT_APP_CONTRACTS_ENABLED=false to hide it there
// specifically, without touching this file again or affecting staging.
// Flip the env var (not this line) once send-contract ships to prod for real.
const CONTRACTS_ENABLED = process.env.REACT_APP_CONTRACTS_ENABLED !== 'false';

// no-hex:allow-start -- the theme palettes themselves (C defaults, THEMES, contrastText, PRESETS)
export const C = {
  bg: "#0A1526", surface: "#16233F", border: "#28395C", muted: "#7d8ea2",
  text: "#e2e8f0", textBright: "#f1f5f9", textDim: "#94a3b8",
  accent: "#31A7D9", danger: "#f87171", success: "#68d391",
  primary: "#033151",
  badgeGreen:"#68d391", badgeBlue:"#63d3f7", badgeAmber:"#f6c26b",
  badgeRed:"#fc8181", badgePurple:"#a78bfa", badgeGray:"#a0aec0",
};

const THEMES = {
  'dark':  { bg:"#0A1526", surface:"#16233F", border:"#28395C", text:"#e2e8f0", textBright:"#f1f5f9", textDim:"#94a3b8", muted:"#7d8ea2", metric:"#1B2B4D",
             danger:"#f87171", success:"#68d391",
             badgeGreen:"#68d391", badgeBlue:"#63d3f7", badgeAmber:"#f6c26b", badgeRed:"#fc8181", badgePurple:"#a78bfa", badgeGray:"#a0aec0",
             tagText:"#63d3f7", tagBg:"rgba(49,167,217,0.1)" },
  'light': { bg:"#f0f4f8", surface:"#ffffff",  border:"#d1d9e0", text:"#1e2736", textBright:"#0f1117", textDim:"#475569", muted:"#5c6e80", metric:"#f8fafc",
             danger:"#b91c1c", success:"#15803d",
             badgeGreen:"#166534", badgeBlue:"#0369a1", badgeAmber:"#92400e", badgeRed:"#991b1b", badgePurple:"#5b21b6", badgeGray:"#374151",
             tagText:"#0369a1", tagBg:"rgba(3,105,161,0.1)" },
};

export function contrastText(hex) {
  const r = parseInt(hex.slice(1,3),16)/255, g = parseInt(hex.slice(3,5),16)/255, b = parseInt(hex.slice(5,7),16)/255;
  const toL = x => x <= 0.03928 ? x/12.92 : Math.pow((x+0.055)/1.055,2.4);
  const L = 0.2126*toL(r) + 0.7152*toL(g) + 0.0722*toL(b);
  return L > 0.179 ? '#1e2736' : '#ffffff';
}

const PRESETS = [
  { id:'ocean',    label:'Ocean',    mode:'dark',  primary:'#033151', accent:'#31A7D9' },
  { id:'midnight', label:'Midnight', mode:'dark',  primary:'#1e1b4b', accent:'#818cf8' },
  { id:'forest',   label:'Forest',   mode:'dark',  primary:'#14291a', accent:'#4ade80' },
  { id:'ember',    label:'Ember',    mode:'dark',  primary:'#2d1515', accent:'#f97316' },
  { id:'carbon',   label:'Carbon',   mode:'dark',  primary:'#111111', accent:'#a5b4fc' },
  { id:'coastal',  label:'Coastal',  mode:'light', primary:'#1e3a5f', accent:'#0369a1' },
  { id:'sage',     label:'Sage',     mode:'light', primary:'#1a3328', accent:'#166534' },
  { id:'plum',     label:'Plum',     mode:'light', primary:'#3b1f5e', accent:'#6d28d9' },
];

// no-hex:allow-end

export function applyTheme(orgData) {
  const mode = orgData?.theme === 'light' ? 'light' : 'dark';
  const t = THEMES[mode];
  C.bg = t.bg; C.surface = t.surface; C.border = t.border;
  C.text = t.text; C.textBright = t.textBright; C.textDim = t.textDim; C.muted = t.muted;
  C.danger = t.danger; C.success = t.success;
  C.badgeGreen = t.badgeGreen; C.badgeBlue = t.badgeBlue; C.badgeAmber = t.badgeAmber;
  C.badgeRed = t.badgeRed; C.badgePurple = t.badgePurple; C.badgeGray = t.badgeGray;
  C.primary = orgData?.primary_color || '#033151';
  if (orgData?.accent_color) C.accent = orgData.accent_color;
  // colorScheme makes native controls (date/time pickers, scrollbars) follow the theme;
  // without it their icons were dark-on-dark on dark themes.
  // Also on the root, so portalled modals (outside css.wrap) get themed pickers too.
  if (typeof document !== 'undefined') document.documentElement.style.colorScheme = mode;
  css.wrap    = { display:"flex", minHeight:"100vh", background:C.bg, fontFamily:"Inter,sans-serif", color:C.text, fontSize:"14px", colorScheme:mode };
  css.sidebar = { width:"220px", minWidth:"220px", background:C.primary, display:"flex", flexDirection:"column", position:"sticky", top:0, height:"100vh" };
  css.sideHead= { padding:"20px 16px 12px", borderBottom:"1px solid rgba(255,255,255,0.1)" };
  css.h1      = { fontSize:"20px", fontWeight:"700", color:C.textBright, marginBottom:"20px" };
  css.h2      = { fontSize:"14px", fontWeight:"600", color:C.textDim, marginBottom:"10px" };
  css.card    = { background:C.surface, border:`1px solid ${C.border}`, borderRadius:"10px", padding:"20px", marginBottom:"16px" };
  css.metric  = { background:t.metric, border:`1px solid ${C.border}`, borderRadius:"8px", padding:"14px 18px" };
  css.lbl     = { fontSize:"11px", color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:"5px" };
  css.bigN    = { fontSize:"26px", fontWeight:"700", color:C.textBright };
  css.inp     = { background:C.bg, border:`1px solid ${C.border}`, borderRadius:"6px", color:C.text, padding:"8px 12px", fontSize:"13px", width:"100%", outline:"none", boxSizing:"border-box" };
  css.sel     = { background:C.bg, border:`1px solid ${C.border}`, borderRadius:"6px", color:C.text, padding:"8px 12px", fontSize:"13px", width:"100%", outline:"none", boxSizing:"border-box", cursor:"pointer" };
  css.div     = { borderTop:`1px solid ${C.border}`, margin:"14px 0" };
  css.th      = { textAlign:"left", padding:"8px 10px", color:C.muted, fontWeight:"600", fontSize:"11px", textTransform:"uppercase", borderBottom:`1px solid ${C.border}` };
  css.nav     = (a) => ({ display:"flex", alignItems:"center", padding:"10px 16px", cursor:"pointer", fontSize:"13px",
                  fontWeight:a?"600":"400", color:a?C.accent:"rgba(255,255,255,0.55)",
                  background:a?`${C.accent}18`:"transparent",
                  borderLeft:a?`2px solid ${C.accent}`:"2px solid transparent" });
  css.td      = { padding:"9px 10px", borderBottom:`1px solid ${C.border}`, color:C.textDim, fontSize:"13px" };
  css.badge   = (v) => ({ display:"inline-block", padding:"2px 8px", borderRadius:"4px", fontSize:"11px", fontWeight:"600",
                  background: v==="green"?"rgba(72,187,120,0.15)": v==="blue"?"rgba(49,167,217,0.15)": v==="amber"?"rgba(237,167,48,0.15)": v==="red"?"rgba(229,62,62,0.15)": v==="purple"?"rgba(139,92,246,0.15)":"rgba(160,174,192,0.1)",
                  color: v==="green"?C.badgeGreen: v==="blue"?C.badgeBlue: v==="amber"?C.badgeAmber: v==="red"?C.badgeRed: v==="purple"?C.badgePurple:C.badgeGray });
  css.tag      = { display:"inline-block", padding:"2px 8px", borderRadius:"4px", fontSize:"11px", background:t.tagBg, color:t.tagText };
  css.timerDisp = { fontSize:"48px", fontWeight:"700", color:C.textBright, letterSpacing:"2px", fontVariantNumeric:"tabular-nums", textAlign:"center" };
}

export const css = {
  wrap:      { display:"flex", minHeight:"100vh", background:C.bg, fontFamily:"Inter,sans-serif", color:C.text, fontSize:"14px", colorScheme:"dark" },
  sidebar:   { width:"220px", minWidth:"220px", background:C.primary, display:"flex", flexDirection:"column", position:"sticky", top:0, height:"100vh" },
  sideHead:  { padding:"20px 16px 12px", borderBottom:"1px solid rgba(255,255,255,0.1)" },
  main:      { flex:1, overflow:"auto" },
  sec:       { padding:"28px" },
  h1:        { fontSize:"20px", fontWeight:"700", color:C.textBright, marginBottom:"20px" },
  h2:        { fontSize:"14px", fontWeight:"600", color:C.textDim, marginBottom:"10px" },
  card:      { background:C.surface, border:`1px solid ${C.border}`, borderRadius:"10px", padding:"20px", marginBottom:"16px" },
  metric:    { background:"#1B2B4D", border:`1px solid ${C.border}`, borderRadius:"8px", padding:"14px 18px" },
  lbl:       { fontSize:"11px", color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:"5px" },
  bigN:      { fontSize:"26px", fontWeight:"700", color:C.textBright },
  inp:       { background:C.bg, border:`1px solid ${C.border}`, borderRadius:"6px", color:C.text, padding:"8px 12px", fontSize:"13px", width:"100%", outline:"none", boxSizing:"border-box" },
  sel:       { background:C.bg, border:`1px solid ${C.border}`, borderRadius:"6px", color:C.text, padding:"8px 12px", fontSize:"13px", width:"100%", outline:"none", boxSizing:"border-box", cursor:"pointer" },
  // Button hierarchy (page-system spec): ONE 'primary' per surface; 'secondary'
  // (outline) for Export/Share/Cancel; 'ghost' for menus and dismiss; 'dangerOutline'
  // for Delete/Void triggers; filled 'danger' only on the confirm step. 'success' is
  // legacy and kept for existing callers.
  btn:       (v="primary") => {
    const base = { padding:"8px 16px", borderRadius:"6px", fontSize:"13px", fontWeight:"600", cursor:"pointer", border:"none" };
    // Outlined variants trim 1px of padding so they stay the same size as filled ones.
    const outline = { padding:"7px 15px", background:"transparent", border:`1px solid ${C.border}` };
    if (v === "ghost")         return { ...base, background:"transparent", color:C.muted };
    if (v === "secondary")     return { ...base, ...outline, color:C.text };
    if (v === "dangerOutline") return { ...base, ...outline, color:C.danger };
    const bg = v==="danger"?C.danger: v==="success"?C.success: C.accent;
    return { ...base, background:bg, color:contrastText(bg) };
  },
  smBtn:     (color) => ({ background:"transparent", border:`1px solid ${C.border}`, cursor:"pointer", color:color||C.muted, fontSize:"11px", fontWeight:"600", padding:"3px 8px", borderRadius:"4px", fontFamily:"Inter,sans-serif" }),
  nav:       (a) => ({ display:"flex", alignItems:"center", padding:"10px 16px", cursor:"pointer", fontSize:"13px",
               fontWeight:a?"600":"400", color:a?C.accent:C.muted,
               background:a?`${C.accent}14`:"transparent",
               borderLeft:a?`2px solid ${C.accent}`:"2px solid transparent" }),
  badge:     (v) => ({ display:"inline-block", padding:"2px 8px", borderRadius:"4px", fontSize:"11px", fontWeight:"600",
               background: v==="green"?"rgba(72,187,120,0.15)": v==="blue"?"rgba(49,167,217,0.15)": v==="amber"?"rgba(237,167,48,0.15)": v==="red"?"rgba(229,62,62,0.15)": v==="purple"?"rgba(139,92,246,0.15)":"rgba(160,174,192,0.1)",
               color: v==="green"?C.badgeGreen: v==="blue"?C.badgeBlue: v==="amber"?C.badgeAmber: v==="red"?C.badgeRed: v==="purple"?C.badgePurple:C.badgeGray }),
  tag:       { display:"inline-block", padding:"2px 8px", borderRadius:"4px", fontSize:"11px", background:THEMES.dark.tagBg, color:THEMES.dark.tagText },
  div:       { borderTop:`1px solid ${C.border}`, margin:"14px 0" },
  g2:        { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" },
  g3:        { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"12px" },
  th:        { textAlign:"left", padding:"8px 10px", color:C.muted, fontWeight:"600", fontSize:"11px", textTransform:"uppercase", borderBottom:`1px solid ${C.border}` },
  td:        { padding:"9px 10px", borderBottom:`1px solid ${C.border}`, color:C.textDim, fontSize:"13px" },
  timerDisp: { fontSize:"48px", fontWeight:"700", color:C.textBright, letterSpacing:"2px", fontVariantNumeric:"tabular-nums", textAlign:"center" },
  timerBtn:  (r) => ({ width:"64px", height:"64px", borderRadius:"50%", border:"none", background:r?C.danger:C.accent, color:"#fff", fontSize:"24px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }),
};

// ─── ERROR TOASTS ─────────────────────────────────────────────────────────────
// Module-level notifier so any view/helper can surface a failed mutation
// without prop-drilling. ErrorToastHost is rendered once in Dashboard.
let _pushError = null;
const notifyError = (msg) => { if (_pushError) _pushError(String(msg)); else console.error('[notifyError]', msg); };

// Translates raw Postgres/Supabase errors into plain language for the toast.
const friendlyDbError = (error) => {
  const code = error?.code || '';
  const msg = error?.message || '';
  if (code === '23503' || msg.includes('foreign key'))
    return "it's still connected to other records (like time entries or invoices)";
  if (code === '23505' || msg.includes('duplicate key'))
    return 'one like it already exists';
  if (code === '42501' || msg.includes('row-level security'))
    return "you don't have permission to do that";
  if (code === '23502' || msg.includes('null value'))
    return 'a required field is missing';
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError'))
    return 'the connection dropped — check your internet and try again';
  return msg; // fall back to the raw message for anything unrecognized
};

// Awaits a supabase mutation, surfaces its error as a toast. Returns true on success.
const runDb = async (label, query) => {
  const { error } = await query;
  if (error) { notifyError(`${label}: ${friendlyDbError(error)}.`); return false; }
  return true;
};

// ─── SHARED UI HELPERS ────────────────────────────────────────────────────────

// SegmentedControl and TabBar live in ./ui (page-system primitives). TabBar is
// re-exported here because ContractsView imports it from './Dashboard'.
export { TabBar };

// ─── NAV ROUTES ───────────────────────────────────────────────────────────────
const NAV_ADMIN_ONLY = ['dashboard','reports','invoices','team','client-map','clients','contracts'];
const NAV_ALL = ['timer','logs','expenses','statements','business','mycontracts','feedback','settings','account',...NAV_ADMIN_ONLY];
// '#settings:Clients' and '#settings:Account' predate Clients and Account being
// their own pages; old links still land in the right place.
function navFromHash(hash) {
  const h = (hash || '').replace(/^#/, '');
  if (h === 'settings:Clients') return 'clients';
  if (h === 'settings:Account') return 'account';
  return h.split(':')[0];
}

// ─── USER MENU ────────────────────────────────────────────────────────────────
// The person's own things (Account, Sign out) live under their name, not in
// the org's navigation (Page System spec, decision 4).
function UserMenu({ name, onAccount, onSignOut, active }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const item = { display:'block', width:'100%', textAlign:'left', background:'transparent', border:'none', cursor:'pointer',
    padding:'9px 12px', fontSize:'13px', color:C.text, fontFamily:'inherit', borderRadius:'6px' };
  return (
    <div ref={ref} style={{position:'relative',minWidth:0}}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={()=>setOpen(o=>!o)}
        style={{background:'transparent',border:'none',cursor:'pointer',padding:'2px 0',fontFamily:'inherit',
          fontSize:'12px',color:active?C.accent:'rgba(255,255,255,0.75)',display:'flex',alignItems:'center',gap:'5px',maxWidth:'150px'}}>
        <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{name}</span>
        <span aria-hidden="true" style={{fontSize:'9px',opacity:0.7}}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div role="menu" style={{position:'absolute',bottom:'100%',left:0,marginBottom:'6px',minWidth:'170px',zIndex:650,
          background:C.surface,border:`1px solid ${C.border}`,borderRadius:'8px',boxShadow:'0 8px 24px rgba(0,0,0,0.3)',padding:'4px'}}>
          <button type="button" role="menuitem" style={item} onClick={()=>{setOpen(false);onAccount();}}>Account</button>
          <button type="button" role="menuitem" style={item} onClick={()=>{setOpen(false);onSignOut();}}>Sign out</button>
        </div>
      )}
    </div>
  );
}

// ─── QUICK ACTIONS MENU ───────────────────────────────────────────────────────
function QuickActionsMenu({ isAdmin, isIndividual, onAddClient, onAddInvoice, onAddTeamMember, onLogTime, onCloseMonth, onReviewRates }) {
  const [open, setOpen] = useState(false);
  const items = [
    ...(isAdmin ? [
      { label: 'Add Client',      sub: 'New client record',  action: onAddClient },
      { label: 'New Invoice',     sub: 'Draft invoice',      action: onAddInvoice },
      ...(!isIndividual ? [
        { label: 'Add Team Member', sub: 'Send invite',        action: onAddTeamMember },
        { label: 'Review Rates',    sub: 'Audit client rates', action: onReviewRates },
      ] : []),
    ] : []),
    { label: 'Close Month',     sub: 'Lock your entries',  action: onCloseMonth },
    { label: 'Log Time',        sub: 'Manual entry',       action: onLogTime },
  ];
  return (
    <div style={{position:'fixed',bottom:'24px',right:'24px',zIndex:600}}>
      <button
        onClick={()=>setOpen(o=>!o)}
        title="Quick actions"
        aria-label="Quick actions" aria-expanded={open}
        style={{width:'38px',height:'38px',borderRadius:'50%',border:'none',cursor:'pointer',
          background:C.accent,filter:open?'brightness(0.88)':'none',color:contrastText(C.accent),fontSize:'22px',fontWeight:'300',
          display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1,
          boxShadow:'0 2px 10px rgba(0,0,0,0.35)',transition:'filter 0.15s',flexShrink:0}}>
        {open ? '×' : '+'}
      </button>
      {open && (
        <>
          <div onClick={()=>setOpen(false)} style={{position:'fixed',inset:0,zIndex:-1}} />
          <div style={{position:'absolute',bottom:'46px',right:0,minWidth:'200px',
            background:C.surface,border:`1px solid ${C.border}`,borderRadius:'10px',
            boxShadow:'0 8px 32px rgba(0,0,0,0.45)',overflow:'hidden',zIndex:601}}>
            {items.map((item,i)=>(
              <button key={i} onClick={()=>{item.action();setOpen(false);}}
                style={{display:'flex',alignItems:'center',gap:'10px',width:'100%',
                  padding:'11px 16px',background:'transparent',border:'none',
                  borderBottom:i<items.length-1?`1px solid ${C.border}`:'none',
                  cursor:'pointer',textAlign:'left',transition:'background 0.1s'}}
                onMouseEnter={e=>e.currentTarget.style.background=`${C.accent}1a`}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{width:'28px',height:'28px',borderRadius:'6px',background:`${C.accent}26`,
                  display:'flex',alignItems:'center',justifyContent:'center',color:C.accent,fontSize:'16px',flexShrink:0}}>+</div>
                <div>
                  <div style={{fontSize:'13px',fontWeight:'600',color:C.textBright}}>{item.label}</div>
                  <div style={{fontSize:'11px',color:C.muted,marginTop:'1px'}}>{item.sub}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── WIZARDS: see src/wizards/ — remove import + state + JSX to roll back any one ───

function ErrorToastHost() {
  const [errors, setErrors] = useState([]);
  useEffect(() => {
    _pushError = (msg) => {
      const id = Date.now() + Math.random();
      setErrors(prev => [...prev, { id, msg }]);
      setTimeout(() => setErrors(prev => prev.filter(e => e.id !== id)), 8000);
    };
    return () => { _pushError = null; };
  }, []);
  if (!errors.length) return null;
  return (
    <div style={{position:"fixed",bottom:"20px",left:"50%",transform:"translateX(-50%)",zIndex:2000,display:"flex",flexDirection:"column",gap:"8px",maxWidth:"520px",width:"90%"}}>
      {errors.map(e => (
        <div key={e.id} style={{display:"flex",alignItems:"flex-start",gap:"10px",padding:"12px 16px",background:"#2a1518",border:"1px solid rgba(229,62,62,0.5)",borderRadius:"8px",color:"#fc8181",fontSize:"13px",boxShadow:"0 4px 24px rgba(0,0,0,0.5)"}}>
          <span style={{flex:1,lineHeight:"1.4"}}>{e.msg}</span>
          <button onClick={()=>setErrors(prev=>prev.filter(x=>x.id!==e.id))}
            style={{background:"none",border:"none",color:"#fc8181",cursor:"pointer",fontSize:"14px",lineHeight:1,padding:0}}>✕</button>
        </div>
      ))}
    </div>
  );
}

const toHrs = (s) => parseFloat((s / 3600).toFixed(2));
const fmtT  = (s) => `${String(Math.floor(s/3600)).padStart(2,"0")}:${String(Math.floor((s%3600)/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
const $c    = (n) => "$" + Math.round(n).toLocaleString();
const curMonth = () => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; };
// [start, nextStart) bounds for a 'YYYY-MM' period — for date-column range
// filters (entry_date is a DATE; LIKE on it is invalid SQL).
const monthBounds = (period) => {
  const [y,m] = period.split('-').map(Number);
  const next = m===12 ? `${y+1}-01-01` : `${y}-${String(m+1).padStart(2,"0")}-01`;
  return [`${period}-01`, next];
};
// Build a timestamptz ISO string from 'YYYY-MM-DD' + 'HH:MM' interpreted in the
// browser's local timezone. Returns null if either part is missing.
const isoFromDateTime = (dateStr, hhmm) => {
  if (!dateStr || !hhmm) return null;
  const d = new Date(`${dateStr}T${hhmm}:00`);
  return isNaN(d) ? null : d.toISOString();
};
// 'HH:MM' (local) from a stored ISO timestamp, for prefilling edit inputs.
const hhmmFromIso = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};
// fmtTimeRange ("2:25–2:32 PM", in the viewer's time zone) comes from ./format.

export default function Dashboard({ session, profile, org, orgMember, onSignOut }) {
  const { allOrgs, setActiveOrg, reload: reloadOrgs } = useOrg();
  // Permissions are PER ACTIVE ORG (multi-org). The old global profiles.role
  // fallback was removed 2026-06-10: it would have made an owner of any org
  // an admin everywhere.
  const isAdmin      = ['admin','owner'].includes(orgMember?.role);
  const isIndividual = org?.account_type === 'individual';

  const [nav, setNav] = useState(() => {
    const navPart = navFromHash(window.location.hash);
    if (!NAV_ALL.includes(navPart)) return isAdmin ? 'dashboard' : 'timer';
    if (NAV_ADMIN_ONLY.includes(navPart) && !isAdmin) return 'timer';
    return navPart;
  });

  const [clients, setClients]               = useState([]);
  const [taskTypes, setTaskTypes]           = useState([]);
  const [assignments, setAssignments]       = useState([]);
  const [entries, setEntries]               = useState([]);
  const [myEntries, setMyEntries]           = useState([]);
  const [users, setUsers]                   = useState([]);
  const [orgMembers, setOrgMembers]         = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [monthLocks, setMonthLocks]         = useState([]);
  const [invoices, setInvoices]             = useState([]);
  const [statements, setStatements]         = useState([]);
  // client_billing_terms for the org: Work value prices each month by the terms
  // in effect that month (src/metrics.js). Admin-only data (RLS is_org_member read).
  const [billingTerms, setBillingTerms]     = useState(null);
  const [tags, setTags]                     = useState([]);
  const [expenses, setExpenses]             = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [qboConnected, setQboConnected]     = useState(false);
  const [extInstalled, setExtInstalled]     = useState(false);
  const [viewAs, setViewAs]                 = useState(null);
  const [submitNudgeDismissed, setSubmitNudgeDismissed] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen]       = useState(false);
  const [showInvoiceGuide, setShowInvoiceGuide] = useState(false);
  const [showClientWizard, setShowClientWizard]   = useState(false);
  const [showInviteWizard, setShowInviteWizard]   = useState(false);
  const [showInvoiceWizard, setShowInvoiceWizard] = useState(false);
  const [showMonthEndWizard, setShowMonthEndWizard] = useState(false);
  const [showRateReviewWizard, setShowRateReviewWizard] = useState(false);
  const [clientDetailId, setClientDetailId]       = useState(null);
  const [clientEditRequestId, setClientEditRequestId] = useState(null); // record drawer's Edit -> Clients page editor
  const [showHelpPanel, setShowHelpPanel]         = useState(false);
  const [helpTab, setHelpTab]                     = useState('chat'); // 'chat' | 'docs' | 'feedback'
  const [helpMessages, setHelpMessages]           = useState([]); // [{role:'user'|'assistant', text, escalate?:bool}]
  const [helpInput, setHelpInput]                 = useState('');
  const [helpLoading, setHelpLoading]             = useState(false);
  const [feedbackType, setFeedbackType]           = useState('general');
  const [feedbackTitle, setFeedbackTitle]         = useState('');
  const [feedbackArea, setFeedbackArea]           = useState('');
  const [feedbackText, setFeedbackText]           = useState('');
  const [feedbackSent, setFeedbackSent]           = useState(false);
  const [feedbackSending, setFeedbackSending]     = useState(false);

  // `opts` currently only matters for the 'invoices' tab -- carries which status
  // tab (and thus which view, since 'overdue' renders the aging kanban instead of
  // the normal list) InvoicesView should open on. Reset to null on every navigate
  // so a plain nav-bar click into Invoices still gets InvoicesView's own default
  // heuristic rather than a stale filter from a previous dashboard-tile click.
  const [invoicesEntry, setInvoicesEntry] = useState(null);
  const navigate = (tab, opts) => { setNav(tab); window.location.hash = tab; setInvoicesEntry(tab === 'invoices' ? (opts || null) : null); };

  // "Getting started" checklist (src/GettingStarted.js). Progress comes from the data
  // already loaded above, so it needs no queries of its own.
  const gs = useGettingStarted({ org, profile, isAdmin, viewAs, isIndividual, clients, myEntries, invoices, orgMembers, pendingInvites, extInstalled });
  // Arriving from the checklist, the person asked for that page: count its first-visit
  // tour as seen so a modal doesn't appear between the click and the thing they came to do.
  // The "?" button on the page still replays it.
  const skipTourFor = (key) => { try { localStorage.setItem(`tk_tour_${key}`, '1'); } catch { /* storage blocked */ } };
  const goChecklist = (go) => {
    if (go.kind === 'nav') { skipTourFor(go.to); navigate(go.to); gs.setOpen(false); }
    else if (go.kind === 'settings') { skipTourFor('settings'); setNav('settings'); window.location.hash = `settings:${go.section}`; gs.setOpen(false); }
    else if (go.kind === 'link') window.open(go.href, '_blank', 'noreferrer');
  };
  // The checklist and the Help panel share the same corner; only one at a time.
  useEffect(() => { if (gs.open) setShowHelpPanel(false); }, [gs.open]); // eslint-disable-line

  // A starting guess for Area, from wherever the user currently is — not a
  // silent auto-fill, still an editable dropdown they can override before
  // submitting. Seeded once per opening, not re-seeded while it's open.
  useEffect(() => {
    if (helpTab === 'feedback' && !feedbackArea && FB_AREA_FROM_NAV[nav]) {
      setFeedbackArea(FB_AREA_FROM_NAV[nav]);
    }
  }, [helpTab]); // eslint-disable-line

  const buildOrgContext = () => {
    const now = new Date();
    const monthStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
    const clientMap = {};
    (clients || []).forEach(cl => { clientMap[cl.id] = cl; });
    const monthEntries = (entries || []).filter(e => e.entry_date && e.entry_date.startsWith(monthStr));
    const hoursByClient = {};
    const countByClient = {};
    monthEntries.forEach(e => {
      if (!e.client_id) return;
      hoursByClient[e.client_id] = (hoursByClient[e.client_id] || 0) + parseFloat(e.hours || 0);
      countByClient[e.client_id] = (countByClient[e.client_id] || 0) + 1;
    });
    const clientSummary = Object.entries(hoursByClient).map(([cid, hrs]) => {
      const cl = clientMap[cid];
      return `- ${cl?.name || 'Unknown'} (${cl?.model || 'unknown'}): ${Number(hrs).toFixed(1)}h, ${countByClient[cid]} entries`;
    }).join('\n');
    const recentEntries = [...(entries || [])].sort((a,b) => b.entry_date?.localeCompare(a.entry_date)).slice(0,10).map(e => ({
      date: e.entry_date,
      client: clientMap[e.client_id]?.name || '(no client)',
      hours: parseFloat(e.hours || 0).toFixed(2),
      description: e.description,
    }));
    const openInvoices = (invoices || []).filter(i => i.status === 'draft').length;
    const unpaidInvoices = (invoices || []).filter(i => i.status === 'sent').length;
    // Contractor earnings context — assignments scoped to this user
    const myAssignments = (assignments || []).filter(a => a.user_id === profile?.id);
    const assignmentSummary = myAssignments.map(a => {
      const cl = clientMap[a.client_id];
      if (!cl) return null;
      const payDesc = a.pay_type === 'hourly' ? `$${a.rate}/hr`
        : a.pay_type === 'flat_monthly' ? `$${a.monthly_amount}/mo flat`
        : a.pay_type === 'day_rate' ? `$${a.rate}/day`
        : a.pay_type === 'fixed_project' ? `$${a.project_amount} fixed`
        : a.pay_type === 'hybrid' ? `$${a.hybrid_base||0}/mo + $${a.hybrid_overage_rate||0}/hr over ${a.hybrid_included_hours||0}h`
        : a.pay_type === 'percent' ? `${a.pay_percent||0}% of client rate`
        : 'unknown';
      return `- ${cl.name}: ${payDesc}`;
    }).filter(Boolean).join('\n');
    return {
      userName: profile?.name,
      orgName: org?.name,
      userRole: orgMember?.role,
      seatType: orgMember?.seat_type,
      currentMonth: monthStr,
      totalClients: (clients || []).length,
      clientSummary: clientSummary || '(no activity this month)',
      totalHoursMonth: monthEntries.reduce((s,e) => s + parseFloat(e.hours||0), 0).toFixed(1),
      recentEntries,
      openInvoices,
      unpaidInvoices,
      assignmentSummary: assignmentSummary || null,
    };
  };

  const sendHelpMessage = async () => {
    const msg = helpInput.trim();
    if (!msg || helpLoading) return;
    const newMessages = [...helpMessages, { role: 'user', text: msg }];
    setHelpMessages(newMessages);
    setHelpInput('');
    setHelpLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('help-chat', {
        body: { message: msg, history: helpMessages, orgContext: buildOrgContext() }
      });
      if (error || !data?.reply) throw new Error(error?.message || 'No reply');
      setHelpMessages([...newMessages, { role: 'assistant', text: data.reply, escalate: !!data.escalate }]);
    } catch {
      setHelpMessages([...newMessages, { role: 'assistant', text: 'Sorry, something went wrong. Please try again.' }]);
    }
    setHelpLoading(false);
  };

  const submitFeedback = async () => {
    if (!feedbackTitle.trim() || !feedbackText.trim() || !feedbackArea || feedbackSending) return;
    setFeedbackSending(true);
    const { data: row, error } = await supabase.from('feedback').insert({
      org_id: org?.id,
      user_id: profile?.id,
      type: feedbackType,
      title: feedbackTitle.trim(),
      message: feedbackText.trim(),
      area: feedbackArea,
    }).select('id').single();
    if (error) { notifyError(`Submitting feedback: ${friendlyDbError(error)}.`); setFeedbackSending(false); return; }
    // Submitting counts as the first vote automatically.
    await supabase.from('feedback_votes').insert({ feedback_id: row.id, user_id: profile.id });
    setFeedbackSending(false);
    setFeedbackSent(true);
    setFeedbackTitle('');
    setFeedbackArea('');
    setFeedbackText('');
    setTimeout(() => setFeedbackSent(false), 4000);
  };

  // Money formats in the org's own currency (organizations.currency). Set during
  // render, not in an effect, so the first paint already uses it; it's idempotent.
  if (org) setFormatContext({ currency: org.currency });
  useEffect(() => { if (org) applyTheme(org); }, [org?.id, org?.theme, org?.accent_color, org?.primary_color]);
  // Settings previews a theme by mutating C; re-render the shell (page background,
  // sidebar) too, so the preview is the whole app rather than just the Settings cards.
  const [, setShellThemeTick] = useState(0);
  useEffect(() => {
    const bump = () => setShellThemeTick(t => t + 1);
    window.addEventListener('tk-theme-preview', bump);
    return () => window.removeEventListener('tk-theme-preview', bump);
  }, []);

  // Detect if the TimeKeep Chrome extension is active on this page.
  // The extension content script sets data-tk-ext on <html> when it loads.
  // We poll briefly on mount to handle extensions that initialize slightly after the page.
  useEffect(() => {
    const check = () => {
      const detected = document.documentElement.hasAttribute('data-tk-ext');
      if (detected) setExtInstalled(true);
      return detected;
    };
    if (check()) return;
    let tries = 0;
    const iv = setInterval(() => { if (check() || ++tries >= 10) clearInterval(iv); }, 500);
    return () => clearInterval(iv);
  }, []);

  // After a contractor clicks "Set up your business profile" (which switches
  // them into their own workspace), land them on Settings > Organization and
  // explain exactly which fields an invoice needs. Runs once the new org is active.
  useEffect(() => {
    if (!org?.id) return;
    if (localStorage.getItem('tk_setup_profile') === '1' && isAdmin) {
      localStorage.removeItem('tk_setup_profile');
      window.location.hash = 'settings:Organization';
      setNav('settings');
      setShowInvoiceGuide(true);
    }
  }, [org?.id, isAdmin]);

  // After returning from Intuit or Stripe OAuth, navigate to Settings → Integrations
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const providers = [['qbo', 'QuickBooks'], ['stripe', 'Stripe']];
    const hit = providers.find(([key]) => params.get(key));
    if (!hit) return;
    const [key, label] = hit;
    const status = params.get(key);
    // Clean the param from the URL without reloading
    const clean = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', clean);
    // Either way the admin lands back on the card; success shows as its Connected badge.
    window.location.hash = 'settings:Integrations';
    setNav('settings');
    if (status === 'error') {
      const msg = params.get('msg') || 'unknown error';
      notifyError(`${label} connection failed: ${msg}`);
    }
  }, []); // eslint-disable-line

  const enterViewAs = (userId) => {
    if (!userId) { setViewAs(null); return; }
    const contractor = users.find(u => u.id === userId);
    setViewAs(contractor || null);
    navigate('timer');
  };

  const [exitingSupport, setExitingSupport] = useState(false);
  const exitSupportSession = async () => {
    if (!org?.id) return;
    setExitingSupport(true);
    const { error } = await supabase.rpc('platform_admin_exit_org', { p_org_id: org.id });
    setExitingSupport(false);
    if (error) { notifyError(`Exit support session: ${friendlyDbError(error)}.`); return; }
    await reloadOrgs();
  };

  useEffect(() => { if (org?.id) loadAll(); }, [org?.id]);

  // Switching orgs: exit View As and leave admin-only views if the user is
  // only a member of the new org.
  useEffect(() => {
    setViewAs(null);
  }, [org?.id]);
  useEffect(() => {
    if (!isAdmin && NAV_ADMIN_ONLY.includes(nav)) navigate('timer');
  }, [isAdmin]); // eslint-disable-line

  useEffect(() => {
    const onHashChange = () => {
      const navPart = navFromHash(window.location.hash);
      if (!NAV_ALL.includes(navPart)) return;
      if (NAV_ADMIN_ONLY.includes(navPart) && (!isAdmin || viewAs)) return;
      setNav(navPart);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [isAdmin, viewAs]);

  useEffect(() => {
    const entrySelect = '*, profiles(name), task_types(name)';

    const fetchFull = async (id) => {
      const { data } = await supabase.from('time_entries').select(entrySelect).eq('id', id).single();
      return data || null;
    };

    const orgFilter = `org_id=eq.${org.id}`;
    const channel = supabase
      .channel('time_entries_realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'time_entries', filter: orgFilter }, async (payload) => {
        const entry = await fetchFull(payload.new.id);
        if (!entry) return;
        if (entry.user_id === profile.id) {
          setMyEntries(prev => prev.some(e => e.id === entry.id) ? prev : [entry, ...prev]);
        }
        if (isAdmin) {
          setEntries(prev => prev.some(e => e.id === entry.id) ? prev : [entry, ...prev]);
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'time_entries', filter: orgFilter }, async (payload) => {
        const entry = await fetchFull(payload.new.id);
        if (!entry) return;
        if (entry.user_id === profile.id) {
          setMyEntries(prev => prev.map(e => e.id === entry.id ? entry : e));
        }
        if (isAdmin) {
          setEntries(prev => prev.map(e => e.id === entry.id ? entry : e));
        }
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'time_entries', filter: orgFilter }, (payload) => {
        const id = payload.old?.id;
        if (!id) return;
        setMyEntries(prev => prev.filter(e => e.id !== id));
        if (isAdmin) setEntries(prev => prev.filter(e => e.id !== id));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [isAdmin, profile.id, org?.id]);

  useEffect(() => {
    if (!org?.id) return;
    const channel = supabase
      .channel('month_locks_realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'month_locks', filter: `org_id=eq.${org.id}` }, (payload) => {
        setMonthLocks(prev => [...prev.filter(l => l.id !== payload.new.id), payload.new]);
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'month_locks', filter: `org_id=eq.${org.id}` }, (payload) => {
        if (payload.old?.id) setMonthLocks(prev => prev.filter(l => l.id !== payload.old.id));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [org?.id]);

  const loadAll = async () => {
    const entrySelect = '*, profiles(name), task_types(name), time_entry_tags(tag_id, tags(id, name, color))';
    // Rolling cutoffs — month_locks 13mo (full year + current), statements 12mo, invoices 12mo
    const now = new Date();
    const mlCutoff = (() => { const d = new Date(now); d.setMonth(d.getMonth()-13); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();
    const stCutoff = (() => { const d = new Date(now); d.setMonth(d.getMonth()-12); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();
    const invCutoff = (() => { const d = new Date(now); d.setFullYear(d.getFullYear()-1); return d.toISOString().slice(0,10); })();
    // time_entries: 13 months (full year + current) — matches mlCutoff window
    const entryCutoff = (() => { const d = new Date(now); d.setMonth(d.getMonth()-13); return d.toISOString().slice(0,10); })();
    // Expenses use the same 13-month window as time entries.
    // RLS already scopes this: members see only their own rows, admins see the org.
    const [c, t, a, me, ml, st, tg, ex, ec] = await Promise.all([
      supabase.from('clients').select('*').eq('org_id', org.id).order('name'),
      supabase.from('task_types').select('*').eq('org_id', org.id).order('name'),
      supabase.from('assignments').select('*').eq('org_id', org.id),
      supabase.from('time_entries').select(entrySelect).eq('org_id', org.id).eq('user_id', profile.id).gte('entry_date', entryCutoff).order('entry_date', { ascending: false }),
      supabase.from('month_locks').select('id, user_id, client_id, period, locked_at, reopened_at').eq('org_id', org.id).gte('period', mlCutoff),
      (() => { let q = supabase.from('contractor_statements').select('*').eq('org_id', org.id).gte('period', stCutoff); if (!isAdmin) q = q.eq('user_id', profile.id); return q.order('period', { ascending: false }); })(),
      supabase.from('tags').select('*').eq('org_id', org.id).order('name'),
      supabase.from('expenses').select('*').eq('org_id', org.id).gte('expense_date', entryCutoff).order('expense_date', { ascending: false }),
      supabase.from('expense_categories').select('*').eq('org_id', org.id).order('name'),
    ]);
    if (c.error)  console.error('[loadAll] clients:', c.error);
    if (t.error)  console.error('[loadAll] task_types:', t.error);
    if (a.error)  console.error('[loadAll] assignments:', a.error);
    if (me.error) console.error('[loadAll] my time_entries:', me.error);
    if (ml.error) console.error('[loadAll] month_locks:', ml.error);
    if (st.error) console.error('[loadAll] contractor_statements:', st.error);
    if (tg.error) console.error('[loadAll] tags:', tg.error);
    if (ex.error) console.error('[loadAll] expenses:', ex.error);
    if (ec.error) console.error('[loadAll] expense_categories:', ec.error);
    setClients(c.data||[]);
    setTaskTypes(t.data||[]);
    setAssignments(a.data||[]);
    setMyEntries(me.data||[]);
    setMonthLocks(ml.data||[]);
    setStatements(st.data||[]);
    setTags(tg.data||[]);
    setExpenses(ex.data||[]);
    setExpenseCategories(ec.data||[]);
    if (isAdmin) {
      // Fetch org_members first so we can scope the profiles query to only this org's members.
      // This avoids the global profiles scan (RLS would otherwise return profiles from every
      // org the user shares, which grows linearly with platform size).
      const om = await supabase.from('org_members').select('*').eq('org_id', org.id);
      if (om.error) console.error('[loadAll] org_members:', om.error);
      const memberIds = new Set((om.data||[]).map(m=>m.user_id));
      const memberIdList = [...memberIds];

      const [allE, u, inv, allInv, bt] = await Promise.all([
        supabase.from('time_entries').select(entrySelect).eq('org_id', org.id).gte('entry_date', entryCutoff).order('entry_date', { ascending: false }),
        memberIdList.length > 0
          ? supabase.from('profiles').select('*').in('id', memberIdList).order('name')
          : Promise.resolve({ data: [], error: null }),
        supabase.from('pending_invitations').select('*').eq('org_id', org.id).eq('status', 'pending').order('created_at', { ascending: false }),
        supabase.from('invoices').select('*').eq('org_id', org.id).gte('issued_date', invCutoff).order('created_at', { ascending: false }),
        supabase.from('client_billing_terms').select('*').eq('org_id', org.id),
      ]);
      if (allE.error)   console.error('[loadAll] all time_entries:', allE.error);
      if (u.error)      console.error('[loadAll] profiles:', u.error);
      if (inv.error)    console.error('[loadAll] pending_invitations:', inv.error);
      if (allInv.error) console.error('[loadAll] invoices:', allInv.error);
      setEntries(allE.data||[]);
      setUsers(u.data||[]);
      setPendingInvites(inv.data||[]);
      setOrgMembers(om.data||[]);
      setInvoices(allInv.data||[]);
      if (bt.error) console.error('[loadAll] client_billing_terms:', bt.error);
      // null (not []) on error, so metrics fall back to the clients.* cache instead of pricing nothing.
      setBillingTerms(bt.error ? null : (bt.data || []));
      // QBO connection status (non-blocking)
      supabase.from('qbo_connections').select('id').eq('org_id', org.id).maybeSingle()
        .then(({ data }) => setQboConnected(!!data));
    } else {
      // Member in this org — drop any admin data carried over from another org
      setEntries([]);
      setUsers([]);
      setPendingInvites([]);
      setOrgMembers([]);
      setInvoices([]);
    }
  };

  // Admins always keep the tab (it doubles as the Review Queue for everyone
  // else's expenses, regardless of their own toggle). A plain member, or an
  // admin currently viewing-as a member, only sees it if THAT member's
  // org_members row has expenses enabled. Hiding the tab is UX only — the
  // real gate is the INSERT RLS policy, which checks the same column and
  // can't be bypassed by calling Supabase directly.
  const viewAsMember = viewAs ? orgMembers.find(m => m.user_id === viewAs.id) : null;
  const expensesEnabledHere = viewAs
    ? viewAsMember?.expenses_enabled !== false
    : orgMember?.expenses_enabled !== false;

  // "My Contracts" shows only for a team member who actually has one, so the
  // menu isn't padded with an empty tab for everyone else. RLS decides what
  // counts: contracts about them, or ones they personally signed.
  const [myContractCount, setMyContractCount] = useState(0);
  useEffect(() => {
    let off = false;
    if (isAdmin || viewAs || !org?.id || !CONTRACTS_ENABLED) { setMyContractCount(0); return undefined; }
    supabase.from('contracts').select('id', { count: 'exact', head: true })
      .eq('org_id', org.id).neq('workflow_status', 'draft')
      .then(({ count }) => { if (!off) setMyContractCount(count || 0); });
    return () => { off = true; };
  }, [isAdmin, viewAs, org?.id]);

  // Two labeled groups (Page System spec, decision 4): your own time, then the
  // organization. Clients is a main-nav page; Account lives in the user menu.
  const navGroups = (isAdmin && !viewAs)
    ? [
        ['My time', [['timer','Timer'],['logs','My Logs'],['expenses','Expenses'],['business','My Work']]],
        [isIndividual ? 'Business' : 'Organization', [['dashboard','Overview'],['clients','Clients'],['invoices','Invoices'], ...(CONTRACTS_ENABLED ? [['contracts','Contracts']] : []), ['statements','Member Invoices'],['team','Team'],['reports','Client Reports'],['settings','Settings']]],
      ]
    : [
        ['My time', [['timer','Timer'],['logs','My Logs'], ...(expensesEnabledHere ? [['expenses','Expenses']] : []), ['statements','My Invoices'],['business','My Work'], ...(myContractCount > 0 ? [['mycontracts','My Contracts']] : [])]],
      ];
  const navItems = navGroups.flatMap(([, items]) => items);

  // Impersonation candidates: members of THIS org (org-scoped role), never self.
  const impersonatable = orgMembers
    .filter(m => m.role === 'member' && !m.ended_at && m.user_id !== profile.id)
    .map(m => users.find(u => u.id === m.user_id))
    .filter(Boolean);

  const effectiveProfile = viewAs || profile;

  // App-wide "ready to submit" nudge — single source of truth (previously
  // duplicated separately in Timer and My Logs, which meant an extension-only
  // contractor who never opens either tab never saw it at all). Lives above
  // the nav switch below so it's visible on every tab, not just one.
  // Respects the org's own require-all-clients-closed toggle, which the
  // earlier duplicated versions did not (they hardcoded "all locked").
  const requireAllClosedForSubmit = org?.invoice_require_all_clients_closed !== false;
  const submittablePeriod = (!isAdmin || viewAs) && (() => {
    const uid = effectiveProfile.id;
    const myAssignedClientIds = [...new Set((assignments||[]).filter(a=>a.user_id===uid).map(a=>a.client_id))];
    if (myAssignedClientIds.length === 0) return null;
    const myLockPeriods = [...new Set((monthLocks||[]).filter(l=>l.user_id===uid&&!l.reopened_at&&l.client_id).map(l=>l.period))].sort().reverse();
    for (const p of myLockPeriods) {
      const lockedCids = new Set((monthLocks||[]).filter(l=>l.user_id===uid&&l.period===p&&!l.reopened_at&&l.client_id).map(l=>l.client_id));
      const closedCount = myAssignedClientIds.filter(cid=>lockedCids.has(cid)).length;
      const ready = requireAllClosedForSubmit ? myAssignedClientIds.every(cid=>lockedCids.has(cid)) : closedCount>0;
      const hasStmt = (statements||[]).some(s => s.user_id===uid && s.period===p && s.status !== 'void');
      if (ready && !hasStmt) return p;
    }
    return null;
  })();
  const showSubmitNudge = !!submittablePeriod && !submitNudgeDismissed && canSubmitContractorInvoice(org?.plan);

  // Archived clients stay in `clients` (reports/lookups need them) but are
  // excluded from anywhere new work gets logged or assigned.
  const activeClients = clients.filter(c => c.is_active !== false);
  const visibleClients = viewAs
    ? activeClients.filter(c => assignments.some(a => a.user_id===viewAs.id && a.client_id===c.id))
    : isAdmin
      ? activeClients
      : activeClients.filter(c => assignments.some(a => a.user_id===profile.id && a.client_id===c.id));

  return (
    <div style={css.wrap}>
      <ErrorToastHost />
      {showInvoiceGuide && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2000,padding:'20px'}}>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'12px',padding:'28px',maxWidth:'460px',width:'100%'}}>
            <div style={{fontSize:'17px',fontWeight:'800',color:C.textBright,marginBottom:'8px'}}>Set up your business profile</div>
            <div style={{fontSize:'13px',color:C.textDim,lineHeight:'1.6',marginBottom:'14px'}}>
              You are now in your own workspace. These details appear on the invoices you send and are needed before you can submit one. Fill them in on the <strong>Organization</strong> tab below, then return to <strong>Statements</strong> in the firm's workspace to submit.
            </div>
            <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'14px 16px',marginBottom:'18px'}}>
              <div style={{fontSize:'11px',fontWeight:'700',color:C.muted,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'8px'}}>Required</div>
              <ul style={{margin:0,paddingLeft:'18px',fontSize:'13px',color:C.text,lineHeight:'1.8'}}>
                <li>Business name</li>
                <li>Address line 1, City, State, ZIP</li>
              </ul>
              <div style={{fontSize:'11px',fontWeight:'700',color:C.muted,textTransform:'uppercase',letterSpacing:'0.5px',margin:'10px 0 8px'}}>Optional</div>
              <ul style={{margin:0,paddingLeft:'18px',fontSize:'13px',color:C.textDim,lineHeight:'1.8'}}>
                <li>Payment link (so clients can pay you directly)</li>
              </ul>
            </div>
            <button style={{...css.btn('primary'),width:'100%'}} onClick={()=>setShowInvoiceGuide(false)}>Got it</button>
          </div>
        </div>
      )}
      <div style={css.sidebar}>
        <div style={css.sideHead}>
          <img src={org?.logo_url || process.env.PUBLIC_URL + '/timekeep-logo.png'} alt={org?.name || "TimeKeep"} style={{width:"64px", display:"block", margin:"0 auto 8px"}} />
          {allOrgs.length > 1 ? (
            <div style={{position:"relative"}}>
              <div onClick={()=>setOrgMenuOpen(o=>!o)} title="Switch workspace"
                style={{cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"6px",
                  padding:"5px 10px", borderRadius:"6px", border:`1px solid ${C.border}`, background:"rgba(255,255,255,0.03)"}}>
                <span style={{fontSize:"14px", fontWeight:"800", color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:"150px"}}>{org?.name || 'TimeKeep'}</span>
                <span style={{color:C.muted, fontSize:"9px"}}>▼</span>
              </div>
              <div style={{fontSize:"10px", color:"rgba(255,255,255,0.75)", marginTop:"4px", textAlign:"center"}}>Click to switch workspace</div>
              {orgMenuOpen && <>
                <div onClick={()=>setOrgMenuOpen(false)} style={{position:"fixed", inset:0, zIndex:40}} />
                <div style={{position:"absolute", top:"100%", left:0, right:0, marginTop:"6px", background:C.surface,
                  border:`1px solid ${C.border}`, borderRadius:"8px", boxShadow:"0 8px 24px rgba(0,0,0,0.45)", zIndex:50, overflow:"hidden"}}>
                  <div style={{fontSize:"10px", color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px", padding:"8px 12px 4px"}}>My Workspaces</div>
                  {allOrgs.map(m => {
                    const active = m.org_id === org?.id;
                    return (
                      <div key={m.org_id} onClick={()=>{setActiveOrg(m.org_id); setOrgMenuOpen(false);}}
                        style={{padding:"8px 12px", cursor:"pointer", fontSize:"13px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:"8px",
                          background: active?"rgba(49,167,217,0.1)":"transparent", color: active?C.accent:C.text}}>
                        <span style={{whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>
                          {m.organizations?.name || 'Workspace'}
                          {m.organizations?.setup_complete === false ? ' — finish setup' : ''}
                        </span>
                        {active && <span style={{fontSize:"11px", flexShrink:0}}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              </>}
            </div>
          ) : (
            <>
              <div style={{fontSize:"15px", fontWeight:"800", color:"#fff"}}>{org?.name || 'TimeKeep'}</div>
              <div style={{fontSize:"11px", color:"rgba(255,255,255,0.75)", marginTop:"2px"}}>{org?.tagline || 'Time tracking & invoicing'}</div>
            </>
          )}
        </div>
        <nav aria-label="Main" style={{flex:1, paddingTop:"6px", overflowY:"auto"}}>
          {navGroups.map(([group, items]) => (
            <div key={group} role="group" aria-label={group}>
              {navGroups.length > 1 && (
                <div style={{fontSize:"10px",fontWeight:"600",letterSpacing:"0.08em",textTransform:"uppercase",color:"rgba(255,255,255,0.45)",padding:"12px 16px 4px"}}>{group}</div>
              )}
              {items.map(([k,lb]) => (
                <button key={k} type="button" aria-current={nav===k ? 'page' : undefined}
                  style={{...css.nav(nav===k), width:'100%', borderTop:'none', borderRight:'none', borderBottom:'none', fontFamily:'inherit', textAlign:'left'}}
                  onClick={()=>navigate(k)}>{lb}</button>
              ))}
            </div>
          ))}
        </nav>
        <div style={{padding:"12px 16px", borderTop:`1px solid ${C.border}`}}>
          <GettingStartedPill gs={gs} />
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'8px'}}>
            <UserMenu name={profile.name} onAccount={()=>navigate('account')} onSignOut={onSignOut} active={nav==='account'} />
            <button onClick={()=>{ setShowHelpPanel(v=>!v); gs.setOpen(false); }} title="Help & AI Assistant" style={{
              width:'32px',height:'32px',borderRadius:'50%',border:`2px solid ${C.accent}`,
              background: showHelpPanel ? C.accent : `${C.accent}22`,
              color: showHelpPanel ? '#fff' : C.accent,
              cursor:'pointer',fontSize:'15px',fontWeight:'800',display:'flex',alignItems:'center',justifyContent:'center',
              flexShrink:0,transition:'all 0.15s',boxShadow: showHelpPanel ? `0 0 0 3px ${C.accent}33` : 'none'
            }}>?</button>
          </div>
          <span style={css.badge(isAdmin?"blue":"amber")}>{roleBadgeLabel(orgMember?.role || profile.role)}</span>
          {isAdmin && impersonatable.length > 0 && (
            <select
              value={viewAs?.id||''}
              onChange={e=>enterViewAs(e.target.value)}
              style={{...css.sel, fontSize:"11px", padding:"5px 8px", marginTop:"8px",
                background: viewAs ? 'rgba(237,167,48,0.12)' : C.bg,
                color: viewAs ? '#f6c26b' : C.muted,
                borderColor: viewAs ? 'rgba(237,167,48,0.4)' : C.border
              }}
            >
              <option value="">View as...</option>
              {impersonatable.map(u=>(
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          )}
          {!extInstalled && (
            <a href={CHROME_STORE_URL} target="_blank" rel="noreferrer"
              style={{display:'flex',alignItems:'center',gap:'7px',marginTop:'12px',padding:'7px 10px',borderRadius:'7px',
                background:'rgba(49,167,217,0.08)',border:'1px solid rgba(49,167,217,0.22)',textDecoration:'none',cursor:'pointer'}}>
              <span style={{fontSize:'14px'}}>🧩</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:'11px',fontWeight:'700',color:C.accent,lineHeight:'1.2'}}>Get the TimeKeep Extension</div>
                <div style={{fontSize:'10px',color:C.muted,marginTop:'1px'}}>Track time from any web app</div>
              </div>
            </a>
          )}
        </div>
      </div>

      <GettingStartedPanel gs={gs} onGo={goChecklist} />

      {/* ── Help Panel ─────────────────────────────────────────────────────── */}
      {showHelpPanel && (
        <div style={{
          position:'fixed',bottom:'16px',left:'232px',width:'360px',height:'520px',
          background:C.surface,border:`1px solid ${C.border}`,borderRadius:'12px',
          boxShadow:'0 8px 32px rgba(0,0,0,0.4)',display:'flex',flexDirection:'column',
          zIndex:1000,overflow:'hidden'
        }}>
          {/* Header */}
          <div style={{padding:'14px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between',background:C.bg}}>
            <div style={{fontWeight:'700',fontSize:'14px',color:C.text}}>Help & Feedback</div>
            <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
              {helpTab==='chat' && helpMessages.length>0 && (
                <button onClick={()=>setHelpMessages([])} style={{background:'none',border:`1px solid ${C.border}`,borderRadius:'5px',color:C.muted,cursor:'pointer',fontSize:'11px',padding:'3px 8px'}}>New Chat</button>
              )}
              <button onClick={()=>setShowHelpPanel(false)} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:'18px',lineHeight:1}}>×</button>
            </div>
          </div>
          {/* Tab bar */}
          <div style={{display:'flex',borderBottom:`1px solid ${C.border}`,background:C.bg}}>
            {[['chat','💬 Ask'],['docs','📚 Docs'],['feedback','💡 Feedback']].map(([k,lb])=>(
              <button key={k} onClick={()=>setHelpTab(k)} style={{
                flex:1,padding:'9px 4px',border:'none',cursor:'pointer',fontSize:'12px',fontWeight:helpTab===k?'700':'400',
                background:'transparent',color:helpTab===k?C.accent:C.muted,
                borderBottom:helpTab===k?`2px solid ${C.accent}`:'2px solid transparent',
                transition:'all 0.15s'
              }}>{lb}</button>
            ))}
          </div>

          {/* Chat tab */}
          {helpTab==='chat' && (<>
            <div style={{flex:1,overflowY:'auto',padding:'12px',display:'flex',flexDirection:'column',gap:'10px'}}>
              {helpMessages.length===0 && (
                <div style={{padding:'16px 8px',display:'flex',flexDirection:'column',gap:'12px',marginTop:'12px'}}>
                  <div style={{background:`${C.accent}18`,border:`1px solid ${C.accent}44`,borderRadius:'8px',padding:'12px 14px'}}>
                    <div style={{fontSize:'12px',fontWeight:'700',color:C.accent,marginBottom:'6px'}}>✨ Ask about your data</div>
                    <div style={{fontSize:'12px',color:C.text,lineHeight:'1.5',marginBottom:'8px'}}>Your AI assistant knows your actual time entries, clients, hours, and invoices — not just how TimeKeep works.</div>
                    <div style={{display:'flex',flexDirection:'column',gap:'5px'}}>
                      {['How many hours did I log this month?','Which client has the most entries?','Do I have any unpaid invoices?'].map(q=>(
                        <button key={q} onClick={()=>{setHelpInput(q);}} style={{
                          textAlign:'left',background:'rgba(255,255,255,0.05)',border:`1px solid ${C.border}`,
                          borderRadius:'6px',padding:'6px 10px',fontSize:'11px',color:C.muted,cursor:'pointer'
                        }}>{q}</button>
                      ))}
                    </div>
                  </div>
                  <div style={{borderTop:`1px solid ${C.border}`,paddingTop:'12px'}}>
                    <div style={{fontSize:'12px',fontWeight:'600',color:C.muted,marginBottom:'6px'}}>Or ask how TimeKeep works</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'5px'}}>
                      {['How do I close a month?','How does hybrid billing work?','What is a contractor invoice?'].map(q=>(
                        <button key={q} onClick={()=>setHelpInput(q)} style={{
                          background:'rgba(255,255,255,0.04)',border:`1px solid ${C.border}`,
                          borderRadius:'6px',padding:'5px 9px',fontSize:'11px',color:C.muted,cursor:'pointer'
                        }}>{q}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {helpMessages.map((m,i)=>(
                <div key={i} style={{alignSelf:m.role==='user'?'flex-end':'flex-start',maxWidth:'85%',display:'flex',flexDirection:'column',gap:'8px'}}>
                  <div style={{
                    padding:'9px 12px',borderRadius:m.role==='user'?'12px 12px 2px 12px':'12px 12px 12px 2px',
                    background:m.role==='user'?C.accent:'rgba(255,255,255,0.06)',
                    color:m.role==='user'?'#fff':C.text,fontSize:'13px',lineHeight:'1.5',whiteSpace:'pre-wrap'
                  }}>{m.text}</div>
                  {m.escalate && (
                    <div style={{background:`rgba(255,200,0,0.08)`,border:`1px solid rgba(255,200,0,0.25)`,borderRadius:'8px',padding:'10px 12px'}}>
                      <div style={{fontSize:'12px',color:'#fbbf24',fontWeight:'600',marginBottom:'4px'}}>Still not working?</div>
                      <div style={{fontSize:'12px',color:C.muted,marginBottom:'8px',lineHeight:'1.4'}}>Send us a bug report and we'll look into it.</div>
                      <button onClick={()=>{
                        const summary = helpMessages.map(msg => `${msg.role==='user'?'User':'Support'}: ${msg.text}`).join('\n');
                        setFeedbackType('bug');
                        setFeedbackText(summary);
                        setHelpTab('feedback');
                      }} style={{
                        width:'100%',padding:'7px 12px',background:'rgba(251,191,36,0.15)',
                        border:'1px solid rgba(251,191,36,0.4)',borderRadius:'6px',
                        color:'#fbbf24',fontSize:'12px',fontWeight:'600',cursor:'pointer'
                      }}>🐛 Send Bug Report</button>
                    </div>
                  )}
                </div>
              ))}
              {helpLoading && (
                <div style={{alignSelf:'flex-start',color:C.muted,fontSize:'13px',padding:'8px 12px'}}>Thinking…</div>
              )}
            </div>
            <div style={{padding:'10px',borderTop:`1px solid ${C.border}`,display:'flex',gap:'8px'}}>
              <input
                value={helpInput} onChange={e=>setHelpInput(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&(e.preventDefault(),sendHelpMessage())}
                placeholder="Ask a question…"
                style={{flex:1,background:C.bg,border:`1px solid ${C.border}`,borderRadius:'8px',
                  padding:'8px 12px',color:C.text,fontSize:'13px',outline:'none'}}
              />
              <button onClick={sendHelpMessage} disabled={!helpInput.trim()||helpLoading}
                style={{padding:'8px 14px',background:C.accent,color:'#fff',border:'none',borderRadius:'8px',
                  cursor:helpInput.trim()&&!helpLoading?'pointer':'not-allowed',fontSize:'13px',fontWeight:'600',
                  opacity:helpInput.trim()&&!helpLoading?1:0.5}}>↑</button>
            </div>
          </>)}

          {/* Docs tab */}
          {helpTab==='docs' && (
            <div style={{flex:1,overflowY:'auto',padding:'16px',display:'flex',flexDirection:'column',gap:'8px'}}>
              <div style={{fontSize:'13px',color:C.muted,marginBottom:'4px'}}>Browse the full documentation site or jump to a section:</div>
              {[
                ['Getting Started','https://timekeep-docs.netlify.app/getting-started/welcome/','🚀'],
                ['Logging Time','https://timekeep-docs.netlify.app/time-tracking/logging-time/','✏️'],
                ['Closing a Month','https://timekeep-docs.netlify.app/time-tracking/closing-month/','🔒'],
                ['Billing Models','https://timekeep-docs.netlify.app/invoicing/billing-models/','💰'],
                ['Creating Invoices','https://timekeep-docs.netlify.app/invoicing/creating-invoices/','🧾'],
                ['Contractor Invoices','https://timekeep-docs.netlify.app/contractors/contractor-invoices/','👥'],
                ['Team Analytics','https://timekeep-docs.netlify.app/admin/team-analytics/','📊'],
                ['Chrome Extension','https://timekeep-docs.netlify.app/extension/overview/','🧩'],
              ].map(([label,url,icon])=>(
                <a key={url} href={url} target="_blank" rel="noreferrer" style={{
                  display:'flex',alignItems:'center',gap:'10px',padding:'10px 12px',
                  background:'rgba(255,255,255,0.04)',border:`1px solid ${C.border}`,borderRadius:'8px',
                  textDecoration:'none',color:C.text,fontSize:'13px',transition:'border-color 0.15s'
                }}
                  onMouseOver={e=>e.currentTarget.style.borderColor=C.accent}
                  onMouseOut={e=>e.currentTarget.style.borderColor=C.border}
                >
                  <span style={{fontSize:'16px'}}>{icon}</span>
                  <span>{label}</span>
                  <span style={{marginLeft:'auto',color:C.muted,fontSize:'11px'}}>↗</span>
                </a>
              ))}
              <div style={{marginTop:'4px',padding:'12px 14px',background:`${C.accent}10`,border:`1px solid ${C.accent}33`,borderRadius:'8px'}}>
                <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                  <span style={{fontSize:'20px'}}>🧩</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:'12px',fontWeight:'700',color:C.accent,marginBottom:'2px'}}>Get the Chrome Extension</div>
                    <div style={{fontSize:'11px',color:C.muted,lineHeight:'1.4'}}>Track time from any website with a floating timer, live earnings, and one-click time entry.</div>
                  </div>
                </div>
                <div style={{display:'flex',gap:'8px',marginTop:'10px'}}>
                  <a href={CHROME_STORE_URL} target="_blank" rel="noreferrer"
                    style={{flex:1,textAlign:'center',padding:'7px',background:C.accent,color:'#fff',borderRadius:'6px',fontSize:'12px',fontWeight:'600',textDecoration:'none'}}>
                    Install Extension
                  </a>
                  <a href="https://timekeep-extension-docs.netlify.app" target="_blank" rel="noreferrer"
                    style={{flex:1,textAlign:'center',padding:'7px',background:'rgba(255,255,255,0.05)',color:C.text,borderRadius:'6px',fontSize:'12px',textDecoration:'none',border:`1px solid ${C.border}`}}>
                    Extension Docs
                  </a>
                </div>
              </div>
              <a href="https://timekeep-docs.netlify.app" target="_blank" rel="noreferrer"
                style={{display:'block',textAlign:'center',marginTop:'8px',fontSize:'12px',color:C.accent,textDecoration:'none'}}>
                Open full docs site →
              </a>
            </div>
          )}

          {/* Feedback tab */}
          {helpTab==='feedback' && (
            <div style={{flex:1,padding:'16px',display:'flex',flexDirection:'column',gap:'12px',overflowY:'auto'}}>
              <div style={{fontSize:'13px',color:C.muted}}>Share a bug report, feature idea, or general feedback. We read everything.</div>
              <div style={{display:'flex',gap:'6px'}}>
                {[['bug','🐛 Bug'],['feature','💡 Feature'],['general','💬 General']].map(([v,lb])=>(
                  <button key={v} onClick={()=>setFeedbackType(v)} style={{
                    flex:1,padding:'7px 4px',border:`1px solid ${feedbackType===v?C.accent:C.border}`,
                    borderRadius:'7px',background:feedbackType===v?`${C.accent}22`:'transparent',
                    color:feedbackType===v?C.accent:C.muted,fontSize:'11px',fontWeight:'600',cursor:'pointer'
                  }}>{lb}</button>
                ))}
              </div>
              <input
                value={feedbackTitle} onChange={e=>setFeedbackTitle(e.target.value)}
                placeholder="Short title — e.g. Add custom invoice PDF templates"
                style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:'8px',
                  padding:'9px 12px',color:C.text,fontSize:'13px',outline:'none',fontFamily:'inherit'}}
              />
              <select value={feedbackArea} onChange={e=>setFeedbackArea(e.target.value)} style={{...css.sel}}>
                <option value="">Which part of the system?</option>
                {FB_AREAS.map(a=><option key={a} value={a}>{a}</option>)}
              </select>
              <textarea
                value={feedbackText} onChange={e=>setFeedbackText(e.target.value)}
                placeholder="Describe the issue or idea…"
                rows={5}
                style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:'8px',
                  padding:'10px 12px',color:C.text,fontSize:'13px',resize:'none',outline:'none',
                  fontFamily:'inherit',lineHeight:'1.5'}}
              />
              {feedbackSent && <div style={{fontSize:'12px',color:'#4ade80',textAlign:'center'}}>Thanks! Feedback submitted ✓</div>}
              <button onClick={submitFeedback} disabled={!feedbackTitle.trim()||!feedbackText.trim()||!feedbackArea||feedbackSending}
                style={{padding:'10px',background:C.accent,color:'#fff',border:'none',borderRadius:'8px',
                  cursor:feedbackTitle.trim()&&feedbackText.trim()&&feedbackArea&&!feedbackSending?'pointer':'not-allowed',fontSize:'13px',fontWeight:'700',
                  opacity:feedbackTitle.trim()&&feedbackText.trim()&&feedbackArea&&!feedbackSending?1:0.5}}>
                {feedbackSending ? 'Sending…' : 'Submit Feedback'}
              </button>

              <a href="#feedback" target="_blank" rel="noopener noreferrer" style={{
                textAlign:'center',padding:'9px',fontSize:'13px',fontWeight:'600',color:C.accent,
                textDecoration:'none',borderTop:`1px solid ${C.border}`,paddingTop:'14px',marginTop:'2px'
              }}>
                See all feedback ↗
              </a>
            </div>
          )}
        </div>
      )}

      <div style={css.main}>
        {orgMember?.is_support_access && (
          <div style={{background:'rgba(239,68,68,0.14)',borderBottom:'1px solid rgba(239,68,68,0.35)',color:'#fca5a5',padding:'10px 28px',fontSize:'13px',fontWeight:'700',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <span>⚠ TimeKeep Support Session — {org?.name}</span>
            <button onClick={exitSupportSession} disabled={exitingSupport}
              style={{background:'transparent',border:'1px solid rgba(252,165,165,0.5)',color:'#fca5a5',borderRadius:'6px',padding:'4px 12px',fontSize:'12px',fontWeight:'700',cursor:exitingSupport?'default':'pointer',opacity:exitingSupport?0.6:1}}>
              {exitingSupport ? 'Exiting…' : 'Exit Support Session'}
            </button>
          </div>
        )}
        {viewAs && (
          <div style={{background:'rgba(237,167,48,0.12)',borderBottom:'1px solid rgba(237,167,48,0.25)',color:'#f6c26b',padding:'10px 28px',fontSize:'13px',fontWeight:'600',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between'}}
            onClick={()=>enterViewAs('')}>
            <span>Viewing as {viewAs.name}</span>
            <span style={{fontSize:'12px',opacity:0.7}}>Click to exit</span>
          </div>
        )}
        {showSubmitNudge && nav!=='statements' && (
          <div style={{background:'rgba(0,160,200,0.10)',borderBottom:'1px solid rgba(0,160,200,0.3)',padding:'10px 28px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',flexWrap:'wrap'}}>
            <div>
              <span style={{fontSize:'13px',fontWeight:'600',color:C.textBright}}>
                {new Date(submittablePeriod+'-02').toLocaleString('default',{month:'long',year:'numeric'})} is ready to submit
              </span>
              <span style={{fontSize:'12px',color:C.textDim,marginLeft:'8px'}}>Review your line items and submit for approval.</span>
            </div>
            <div style={{display:'flex',gap:'8px',flexShrink:0}}>
              <button style={{padding:'6px 14px',borderRadius:'6px',border:'none',background:C.accent,color:'#fff',fontSize:'12px',fontWeight:'600',cursor:'pointer',whiteSpace:'nowrap'}}
                onClick={()=>navigate('statements')}>
                Go to My Invoices →
              </button>
              <button style={{padding:'6px 10px',borderRadius:'6px',border:`1px solid ${C.border}`,background:'transparent',color:C.muted,fontSize:'12px',cursor:'pointer'}}
                onClick={()=>setSubmitNudgeDismissed(true)}>✕</button>
            </div>
          </div>
        )}
        {nav==='dashboard' && isAdmin && <RevenueDashboardView invoices={invoices} clients={clients} entries={entries} assignments={assignments} orgMembers={orgMembers} users={users} org={org} statements={statements} onNavigate={navigate} />}
        {nav==='timer'    && <TimerView    profile={effectiveProfile} orgMember={viewAs ? null : orgMember} orgId={org?.id} orgPlan={org?.plan} clients={visibleClients} taskTypes={taskTypes} tags={tags} entries={entries} assignments={assignments} monthLocks={monthLocks} statements={statements} onRefresh={loadAll} onNavigate={navigate} />}
        {nav==='logs'     && <LogsView     profile={effectiveProfile} orgMember={viewAs ? null : orgMember} actingAdmin={!!viewAs && isAdmin} org={org} entries={viewAs ? entries.filter(e=>e.user_id===viewAs.id) : myEntries} clients={clients} assignments={assignments} monthLocks={monthLocks} taskTypes={taskTypes} tags={tags} statements={statements} onRefresh={loadAll} onNavigate={navigate} onSubmittableReset={()=>setSubmitNudgeDismissed(false)} />}
        {nav==='expenses' && ((isAdmin && !viewAs) || expensesEnabledHere) && <ExpensesView expenses={expenses} categories={expenseCategories} clients={visibleClients} allClients={clients} org={org} profile={effectiveProfile} isAdmin={isAdmin && !viewAs} users={users} monthLocks={monthLocks} onRefresh={loadAll} />}
        {nav==='feedback' && <FeedbackScreen org={org} profile={effectiveProfile} />}
        {nav==='reports'  && isAdmin && <ReportsView userId={profile?.id} entries={entries} clients={clients} taskTypes={taskTypes} tags={tags} assignments={assignments} users={users} orgMembers={orgMembers} org={org} monthLocks={monthLocks} invoices={invoices} statements={statements} billingTerms={billingTerms} onRefresh={loadAll} onNavigate={setNav} onClientClick={setClientDetailId} />}
        {nav==='invoices' && isAdmin && <InvoicesView invoices={invoices} clients={clients} taskTypes={taskTypes} entries={entries} expenses={expenses} org={org} monthLocks={monthLocks} qboConnected={qboConnected} onRefresh={loadAll} onClientClick={setClientDetailId} initialFilter={invoicesEntry} />}
        {nav==='contracts' && isAdmin && CONTRACTS_ENABLED && <ContractsView clients={clients} users={users} orgMembers={orgMembers} org={org} orgId={org?.id} css={css} C={C} onRefresh={loadAll} onError={notifyError} />}
        {nav==='team'     && isAdmin && <TeamView    users={users} clients={clients} assignments={assignments} pendingInvites={pendingInvites} orgMembers={orgMembers} entries={entries} profile={profile} org={org} monthLocks={monthLocks} statements={statements} billingTerms={billingTerms} onRefresh={loadAll} onClientClick={setClientDetailId} />}
        {nav==='clients' && isAdmin && !viewAs && (
          <SettingsView section="clients" editRequestId={clientEditRequestId} onEditRequestHandled={()=>setClientEditRequestId(null)} clients={clients} taskTypes={taskTypes} tags={tags} org={org} profile={profile} orgId={org?.id} entries={entries} invoices={invoices} assignments={assignments} extInstalled={extInstalled} qboConnected={qboConnected} onRefresh={loadAll} onClientClick={setClientDetailId} />
        )}
        {nav==='account' && <MemberAccountView org={org} orgId={org?.id} />}
        {nav==='settings' && (isAdmin && !viewAs
          ? <SettingsView clients={clients} taskTypes={taskTypes} tags={tags} org={org} profile={profile} orgId={org?.id} entries={entries} invoices={invoices} assignments={assignments} extInstalled={extInstalled} qboConnected={qboConnected} onRefresh={loadAll} onClientClick={setClientDetailId} />
          : <MemberAccountView org={org} orgId={org?.id} />)}
        {nav==='client-map' && isAdmin && (
          <div style={{position:'fixed',inset:0,zIndex:2000}}>
            <ClientMap org={org} />
          </div>
        )}
        {nav==='statements' && <StatementsView statements={statements} isAdmin={isAdmin && !viewAs} profile={effectiveProfile} org={org} clients={clients} assignments={assignments} entries={isAdmin ? entries : myEntries} expenses={expenses} users={users} orgMembers={orgMembers} monthLocks={monthLocks} qboConnected={qboConnected} onRefresh={loadAll} />}
        {/* allOrgs is the signed-in user's own memberships. While viewing as someone else
            only the active org is theirs to see (their other orgs aren't readable). */}
        {nav==='business'   && <MyBusinessView profile={effectiveProfile} allOrgs={viewAs ? allOrgs.filter(m => m.org_id === org?.id) : allOrgs} />}
        {nav==='mycontracts' && !isAdmin && CONTRACTS_ENABLED && <MyContractsView orgId={org?.id} C={C} css={css} />}
      </div>

      {/* Client detail drawer — triggered from anywhere a client name is clicked */}
      {clientDetailId && (
        <ClientDetailDrawer
          clientId={clientDetailId}
          clients={clients}
          entries={entries}
          invoices={invoices}
          assignments={assignments}
          users={users}
          orgMembers={orgMembers}
          monthLocks={monthLocks}
          org={org}
          orgId={org?.id}
          qboConnected={qboConnected}
          supabase={supabase}
          onClose={()=>setClientDetailId(null)}
          onEdit={(id)=>{ setClientDetailId(null); setClientEditRequestId(id); navigate('clients'); }}
        />
      )}

      {/* Global quick actions button */}
      <QuickActionsMenu
        isAdmin={isAdmin && !viewAs}
        isIndividual={isIndividual}
        onAddClient={()=>setShowClientWizard(true)}
        onAddInvoice={()=>setShowInvoiceWizard(true)}
        onAddTeamMember={()=>setShowInviteWizard(true)}
        onCloseMonth={()=>setShowMonthEndWizard(true)}
        onReviewRates={()=>setShowRateReviewWizard(true)}
        onLogTime={()=>navigate('timer')}
      />

      {/* New Client wizard */}
      {showClientWizard && org && (
        <NewClientWizard
          org={org}
          onSave={loadAll}
          onClose={()=>setShowClientWizard(false)}
        />
      )}

      {/* Invite Team Member wizard */}
      {showInviteWizard && org && profile && (
        <InviteTeamMemberWizard
          org={org}
          profile={profile}
          onRefresh={loadAll}
          onClose={()=>setShowInviteWizard(false)}
        />
      )}

      {/* New Invoice wizard */}
      {showInvoiceWizard && org && (
        <NewInvoiceWizard
          org={org}
          clients={clients}
          onRefresh={loadAll}
          onNavigate={navigate}
          onClose={()=>setShowInvoiceWizard(false)}
        />
      )}

      {/* Month-End wizard */}
      {showMonthEndWizard && org && profile && (
        <MonthEndWizard
          org={org}
          profile={effectiveProfile}
          clients={clients}
          monthLocks={monthLocks}
          onRefresh={loadAll}
          onClose={()=>setShowMonthEndWizard(false)}
          onNavigate={(tab)=>{ setShowMonthEndWizard(false); navigate(tab); }}
          isContractor={!isAdmin}
        />
      )}

      {/* Rate Review wizard — admin only */}
      {showRateReviewWizard && org && isAdmin && (
        <RateReviewWizard
          org={org}
          clients={clients}
          onRefresh={loadAll}
          onClose={()=>setShowRateReviewWizard(false)}
        />
      )}
    </div>
  );
}

// ─── TAG HELPERS ──────────────────────────────────────────────────────────────
const TAG_PRESETS = ['#31A7D9','#68d391','#f6c26b','#fc8181','#a78bfa','#a0aec0','#f687b3','#ed8936'];

async function resolveTags(orgId, tagIds) {
  const resolved = [];
  for (const tid of tagIds) {
    if (tid.startsWith('_new_:')) {
      const name = tid.slice(6);
      const { data: ex } = await supabase.from('tags').select('id').eq('org_id', orgId).eq('name', name).maybeSingle();
      if (ex) { resolved.push(ex.id); continue; }
      const { data: cr } = await supabase.from('tags').insert({ org_id: orgId, name }).select('id').single();
      if (cr) resolved.push(cr.id);
    } else {
      resolved.push(tid);
    }
  }
  return resolved;
}

function TagChips({ tagLinks }) {
  const ts = (tagLinks||[]).map(tl=>tl.tags).filter(Boolean);
  if (!ts.length) return null;
  return (
    <div style={{display:'flex',flexWrap:'wrap',gap:'3px',marginTop:'3px'}}>
      {ts.map(t=>(
        <span key={t.id} style={{padding:'1px 6px',borderRadius:'8px',fontSize:'10px',fontWeight:'600',
          background:t.color?`${t.color}22`:'rgba(49,167,217,0.1)',
          color:t.color||C.accent}}>
          {t.name}
        </span>
      ))}
    </div>
  );
}

// Searchable multi-select dropdown for tags. Closed, it's one compact row of
// chips (like a multi-select box); open, it's a search box over the tag list
// with an "add new" row PINNED as the last row at all times -- unlike
// SearchableSelect's add option (only appears once you've typed a
// non-matching name), this one is always visible so the option is
// discoverable before you've typed anything, just inert (muted, inert click)
// until there's an actual name to add.
function TagPicker({ availableTags, selectedTagIds, onSelect }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const isSelected = (id) => selectedTagIds.includes(id);
  const toggleTag = (id) => onSelect(isSelected(id) ? selectedTagIds.filter(x=>x!==id) : [...selectedTagIds, id]);
  const pendingIds = selectedTagIds.filter(id=>id.startsWith('_new_:'));
  const selectedReal = (availableTags||[]).filter(t=>isSelected(t.id));

  const query = q.trim().toLowerCase();
  const filtered = (availableTags||[]).filter(t => !query || t.name.toLowerCase().includes(query));
  const exactMatch = query && (availableTags||[]).some(t => t.name.toLowerCase() === query);
  const canAdd = query.length > 0 && !exactMatch;

  const addPending = () => {
    const name = q.trim();
    if (!name) return;
    const existing = (availableTags||[]).find(t=>t.name.toLowerCase()===name.toLowerCase());
    if (existing) { toggleTag(existing.id); setQ(''); return; }
    const pid = `_new_:${name}`;
    if (!selectedTagIds.includes(pid)) onSelect([...selectedTagIds, pid]);
    setQ('');
  };

  return (
    <div ref={ref} style={{position:'relative'}}>
      <div onClick={()=>setOpen(o=>!o)}
        style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:'4px',minHeight:'32px',
          padding:'4px 10px',borderRadius:'6px',border:`1px solid ${C.border}`,background:C.bg,cursor:'pointer'}}>
        {selectedReal.length===0 && pendingIds.length===0 && <span style={{color:C.muted,fontSize:'13px'}}>Add tags…</span>}
        {selectedReal.map(t=>(
          <span key={t.id} style={{display:'inline-flex',alignItems:'center',gap:'3px',padding:'2px 8px',borderRadius:'10px',
            fontSize:'11px',fontWeight:'600',background:t.color?`${t.color}22`:'rgba(49,167,217,0.12)',color:t.color||C.accent}}>
            {t.name}
            <span onClick={e=>{e.stopPropagation();toggleTag(t.id);}} style={{cursor:'pointer',fontSize:'12px',lineHeight:1}}>×</span>
          </span>
        ))}
        {pendingIds.map(id=>{
          const name=id.slice(6);
          return (
            <span key={id} style={{display:'inline-flex',alignItems:'center',gap:'3px',padding:'2px 8px',borderRadius:'10px',
              fontSize:'11px',fontWeight:'600',background:'rgba(139,92,246,0.15)',color:C.badgePurple}}>
              {name}
              <span onClick={e=>{e.stopPropagation();onSelect(selectedTagIds.filter(x=>x!==id));}} style={{cursor:'pointer',fontSize:'12px',lineHeight:1}}>×</span>
            </span>
          );
        })}
        <span style={{marginLeft:'auto',color:C.muted,fontSize:'9px',flexShrink:0}}>{open?'▲':'▼'}</span>
      </div>
      {open && (
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,width:'280px',maxWidth:'90vw',background:C.surface,
          border:`1px solid ${C.border}`,borderRadius:'8px',boxShadow:'0 6px 24px rgba(0,0,0,0.5)',
          zIndex:300,display:'flex',flexDirection:'column',maxHeight:'280px'}}>
          <div style={{padding:'6px 8px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
            <input autoFocus value={q} onChange={e=>setQ(e.target.value)} placeholder="Search or add a tag…"
              style={{width:'100%',boxSizing:'border-box',padding:'5px 8px',borderRadius:'5px',
                border:`1px solid ${C.border}`,background:C.bg,color:C.text,fontSize:'12px',outline:'none'}}
              onKeyDown={e=>{if(e.key==='Enter'&&canAdd){e.preventDefault();addPending();}}} />
          </div>
          <div style={{overflowY:'auto',flex:1}}>
            {filtered.length===0 && <div style={{padding:'8px 12px',fontSize:'12px',color:C.muted}}>No matching tags</div>}
            {filtered.map(t=>{
              const sel = isSelected(t.id);
              return (
                <div key={t.id} onClick={()=>toggleTag(t.id)}
                  style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 12px',cursor:'pointer',fontSize:'13px',
                    color:sel?C.accent:C.text,background:sel?'rgba(49,167,217,0.08)':'transparent'}}>
                  <span style={{width:'14px',height:'14px',borderRadius:'3px',flexShrink:0,
                    border:`1px solid ${sel?C.accent:C.border}`,background:sel?C.accent:'transparent',
                    display:'flex',alignItems:'center',justifyContent:'center',fontSize:'10px',color:'#fff'}}>
                    {sel?'✓':''}
                  </span>
                  {t.name}
                </div>
              );
            })}
          </div>
          {/* Pinned "add new" row -- always the last row in the list, active
              (colored, clickable) only once there's a name to add that isn't
              already an existing tag. Stays put as you type/filter above. */}
          <div onClick={canAdd?addPending:undefined}
            style={{padding:'8px 12px',cursor:canAdd?'pointer':'default',fontSize:'13px',fontWeight:'500',
              borderTop:`1px solid ${C.border}`,flexShrink:0,color:canAdd?C.accent:C.muted,opacity:canAdd?1:0.6}}>
            {q.trim() ? `+ Add "${q.trim()}"` : '+ Add new tag'}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── WALKTHROUGH SYSTEM ───────────────────────────────────────────────────────

const TOURS = {
  timer: [
    { target:'[data-tour="timer-live"]',   position:'bottom', icon:'⏱️', title:'Live Timer', body:'Run a live timer while you work. Select a client and task type, add a description, then hit the play button. The timer keeps running even if you close this tab. The Billable to client switch follows the task type, and your pay is the same either way.' },
    { target:'[data-tour="timer-today"]',  position:'top',    icon:'📅', title:'Today', body:'Everything you\'ve logged today, whichever way you logged it, with a running total. Open My Logs for past days and to edit entries.' },
    { target:'[data-tour="timer-manual"]', position:'bottom', icon:'✏️', title:'Manual Entry', body:'Prefer to log after the fact? Switch to Manual entry above to record hours for any date. Enter start/end times and the duration calculates automatically, or type hours directly.' },
    { target:null, icon:'🏷️', title:'Task Types and Tags', body:'Task types categorize your work (e.g. General Support, Training, Project Management) and drive line items on invoices. An admin can set a task type to start new entries as not billable to the client, like internal meetings. Tags are flexible labels for filtering in reports — they don\'t affect billing.' },
    { target:'[data-tour="setup-guide"]', optional:true, position:'right', icon:'✅', title:'Getting started', body:'Your setup checklist: add a client, check your billing settings, log time, invite your team and send your first invoice. Each item opens the right screen, and it ticks itself off as you go. Close it any time and open it again from here.' },
    { target:null, icon:'🧩', title:'Get the Extension', body:'The TimeKeep Chrome extension lets you start timers directly from Gmail, Google Calendar, Asana, Trello, Jira, GitHub, and more — without switching tabs. Find it in Settings → Extension.' },
  ],
  logs: [
    { target:'[data-tour="logs-controls"]', position:'bottom', icon:'📋', title:'Your Time Log', body:'My Logs shows all your time entries for the selected month. You can edit descriptions, adjust hours, change task types, and delete entries before closing the period.' },
    { target:'[data-tour="logs-entries"]',  position:'top',    icon:'🔍', title:'Your Entries', body:'Entries are grouped by client. Click any row to edit inline — adjust hours, description, task type, or billable status. The row\'s ⋯ menu has Delete and Don\'t pay for this time, which keeps an entry on the timesheet but leaves it out of pay. Clients you\'ve already closed start collapsed; click the name to expand.' },
    { target:'[data-tour="logs-select"]',   optional:true, position:'bottom', icon:'☑️', title:'Change Several at Once', body:'Tick the boxes to select entries: hold Shift to take a range, or tick a table\'s header box for the whole client. A bar appears at the bottom to change the client, task type, tags, description, billing or date on all of them together, or to delete them. Entries in a closed month can\'t be selected.' },
    { target:'[data-tour="logs-close"]',    position:'top',    icon:'🔒', title:'Close a Month', body:'When you\'re done logging for a billing period, close it: one client at a time here, or all open clients at once with Close month at the top. Closed entries lock from editing and signal to your admin that you\'re ready for invoicing. You can reopen if needed. Admins can also close and reopen a month for a member by choosing them in View as...' },
  ],
  reports: [
    { target:'[data-tour="reports-header"]',  position:'bottom', icon:'📊', title:'Client Reports', body:'Client Reports shows hours and billing amounts across all your clients for any time period. It\'s your hub for reviewing work before invoicing.' },
    { target:'[data-tour="reports-tabs"]',    position:'bottom', icon:'🗂️', title:'Four Views', body:'Summary shows per-client totals. Detailed lists every entry. Profitability shows margin by client. Insights flags billing anomalies and time left out of a member\'s pay. My Reports holds your saved filter configurations.' },
    { target:'[data-tour="reports-filters"]', position:'bottom', icon:'🎛️', title:'Filters', body:'Filter by client, task type, tag, billing model, or activity, and pick the period on the right. When task type or tag filters are active, per-client totals and any generated invoices are scoped to only the matching entries. Export CSV downloads exactly the time entries your filters select.' },
    { target:'[data-tour="reports-save"]',    position:'bottom', icon:'💾', title:'Save Report', body:'Save Report stores every filter you have set plus the date range as a named configuration. Load it anytime from the My Reports tab. When a single client is selected, a Create invoice button appears at the top right to generate an invoice pre-loaded with the filtered data.' },
  ],
  invoices: [
    { target:'[data-tour="invoices-header"]',  position:'bottom', icon:'🧾', title:'Invoice Management', body:'Invoices tracks all client invoices across their lifecycle — draft, sent, and paid. Filter by status with the pills (each shows a count and total), then narrow by search, client, or date range.' },
    { target:'[data-tour="invoices-summary"]', position:'bottom', icon:'💰', title:'At-a-Glance Totals', body:'Invoiced, Collected, Outstanding, and Overdue for whatever the search, client, and date range are set to (all time by default).' },
    { target:'[data-tour="invoices-list"]',    position:'top',    icon:'📧', title:'Send, Track, and Pay', body:'Click any invoice to open it: its main button is the next step (Send invoice, Request PO, or Mark paid when payment arrives). Sending emails the client\'s billing contact, plus a PDF copy to their AP inbox if the client has one. Everything else is in the row\'s ⋯ menu. Click a column header to sort.' },
  ],
  team: [
    { target:'[data-tour="team-tabs"]',    position:'bottom', icon:'👥', title:'Team Overview', body:'Members is your roster: rates, assignments, and invitations (+ Invite member, top right). Utilization, Margin, and Rate Planner are the performance views.' },
    { target:'[data-tour="team-members"]', position:'top', before: () => { document.querySelectorAll('[data-tour="team-tabs"] button')[0]?.click(); }, icon:'✉️', title:'Manage Members', body:'One row per person. Click a member to edit their role, seat type, pay, client assignments and weekly capacity in a side panel; Off-board and Delete are in the row\'s ⋯ menu. The Active / Off-boarded / All pills, plus the seat and role filters, narrow the list; off-boarded people leave the Active list but are kept, and you can undo an off-boarding or reactivate them from the Off-boarded pill. + Invite member emails an invitation, or, with "Email them the invitation" unticked, gives you a link to send yourself. Pending invitations are listed below the table; an expired one can be renewed and resent, or renewed as a link you send yourself.' },
    { target:null, icon:'🎭', title:'Roles and Seat Types', body:'Role (member, admin, owner) controls permissions. Seat type (contractor, employee hourly/salary) determines how member pay is calculated in analytics. Owner and admin seat types are excluded from member pay.' },
  ],
  analytics: [
    { target:'[data-tour="analytics-header"]', position:'bottom', icon:'📈', title:'Team Analytics', body:'A real-time view of team performance: utilization, work value (what the hours are worth under each client\'s terms), member pay, and margin on work value. Pick the period on the right; Export CSV downloads the member × client breakdown.' },
    { target:'[data-tour="team-tabs"]',   position:'bottom', icon:'⚡', title:'Three Views', body:'Utilization shows hours vs. capacity per member — click a member for their per-client breakdown. Margin breaks down profit and margin on work value, by member or by client. Rate Planner prices a placement before either side is in the system: pick client rate, pay rate, or margin % to solve for, and the other two fill it in.' },
    { target:null, icon:'💵', title:'Set Capacity for Utilization', body:'Utilization bars only appear when a member has a weekly capacity set. Go to Team → Members → Edit a member to add their Weekly Capacity in hours.' },
  ],
  statements: [
    { target:'[data-tour="statements-period"]',  position:'bottom', icon:'📄', title:'My Invoices', body:'This is where you submit invoices to your admin for work you\'ve completed. Select the billing period — each period gets one invoice generated from your closed time entries.' },
    { target:'[data-tour="statements-actions"]', position:'top',    icon:'📤', title:'Submit for Approval', body:'Once all your clients are closed for the period, the Submit Invoice button appears. Your admin reviews it, then approves and sends it to your accounts payable inbox as a PDF.' },
    { target:null, icon:'🖨️', title:'Print or Download', body:'You can view a hosted version of any submitted or approved invoice and use your browser\'s print function to save it as a PDF — available from the invoice list.' },
  ],
  statements_admin: [
    { target:'[data-tour="statements-controls"]', position:'bottom', icon:'📄', title:'Member Invoices', body:'Invoices your members (contractors and employees) submit for their work, one per billing period, across every period. The page opens on Needs review; use the status pills and date range to look further back.' },
    { target:'[data-tour="statements-list"]',     position:'top',    icon:'✅', title:'Review and Approve', body:'Click a Needs review row to see its line items and the timesheet behind them, then Approve or Dispute. Approving emails the PDF to the address set in Settings → Billing → Invoices → Member Invoice Recipients (overridable per team member).' },
    { target:'[data-tour="statements-stats"]',    position:'bottom', icon:'🗂️', title:'Not Submitted Yet', body:'Not submitted lists members who logged work in a period but haven\'t sent an invoice for it. Draft → Submitted (member) → Approved or Disputed (you). A disputed invoice goes back to the member with your note.' },
  ],
  business: [
    { target:'[data-tour="business-header"]', position:'bottom', icon:'🏢', title:'My Work', body:'Aggregates your time and earnings across every organization you belong to. Useful if you work with multiple clients under different workspaces.' },
    { target:'[data-tour="business-orgs"]',   position:'top',    icon:'📦', title:'Per-Org Breakdown', body:'Each card shows your hours, estimated earnings, and member invoice status for one org. The entries table at the bottom lists your recent logs across all orgs for the selected month.' },
  ],
  clients: [
    { target:'[data-tour="clients-list"]', position:'top', icon:'🏫', title:'Your clients', body:'One row per client: billing model, rate, hours logged this month, and sent invoices not yet paid. Click a column header to sort. Active, Archived and All above the table switch which clients are shown.' },
    { target:null, icon:'🔎', title:'Details and editing', body:'Click a client to see their billing, team, time logs, invoices and emails. Edit, Sync to QuickBooks, End contract and Delete are in each row\'s ⋯ menu. Rates are changed in the editor\'s Billing Terms panel, with the date they take effect.' },
  ],
  settings: [
    { target:'[data-tour="settings-tabs"]', position:'bottom', icon:'⚙️', title:'Settings', body:'Five tabs: Organization, Billing & invoices, Notifications, Data (task types, tags, imports), and Connections (integrations, the Chrome extension, the mobile app). Changes apply to everyone in your org. Notifications, Data, and Connections each have sub-tabs along the top, like Billing & invoices, so every option is one click away. When you change something, a bar at the bottom lets you save or discard.' },
    { target:null, icon:'🎨', title:'Org — Branding and Identity', body:'Set your org name, logo, colors, website, address, and contact info. Your logo and primary color appear on client invoices and outbound emails.' },
    { target:null, icon:'💳', title:'Billing and Invoice Defaults', body:'Configure billing increments, default rates, invoice number format, payment terms, and payment link. The Invoice tab controls what\'s shown on each invoice — logo, address, line item mode.' },
    { target:null, icon:'🔔', title:'Notifications', body:'Toggle each automated email independently: weekly summaries, inactivity alerts, threshold warnings, invoice reminders, and member notifications.' },
    { target:null, icon:'🧩', title:'Data and Connections', body:'Data has sub-tabs for task types (each can start new entries billable or not billable to the client), tags (color-coded labels your team can apply to entries), and bulk imports. Connections has sub-tabs for integrations (QuickBooks and Stripe), the Chrome extension (button position, idle detection, which apps show the timer), and the mobile app. Clients and your own Account moved out of Settings: Clients is in the main navigation, Account is under your name.' },
  ],
  contracts: [
    { target:'[data-tour="contracts-tabs"]',     position:'bottom', icon:'📄', title:'Contracts', body:'Send agreements for signature and keep the signed copies. Contract types and contract settings (email templates, signing defaults) are the other two tabs.' },
    { target:'[data-tour="contracts-controls"]', position:'bottom', icon:'🔎', title:'Find a Contract', body:'Open shows everything still in progress. Filter by status, search by number, title, or who it\'s with, or narrow to one contract type.' },
    { target:'[data-tour="contracts-list"]',     position:'top',    icon:'✍️', title:'Signing and the Agreement', body:'Signing shows where a contract is in the signature process; Agreement shows whether it\'s in force. Click a row to see the document, signers, and the audit trail.' },
  ],
  expenses: [
    { target:'[data-tour="expenses-tabs"]',     position:'bottom', icon:'🧾', title:'Your Expenses and the Review Queue', body:'My expenses is what you\'ve logged. Admins also get the Review queue: everything members have submitted and are waiting on you to approve or reject.' },
    { target:'[data-tour="expenses-controls"]', position:'bottom', icon:'🔎', title:'Filter', body:'Narrow the list by status and by month. The totals below follow whatever you filter.' },
    { target:'[data-tour="expenses-stats"]',    position:'bottom', icon:'💵', title:'Billable vs. Reimbursable', body:'Billable expenses go on the client\'s invoice (with any markup). Reimbursable ones are repaid at cost on the member\'s own invoice. One expense can be both, so the two can add up to more than the total.' },
    { target:'[data-tour="expenses-table"]',    position:'top',    icon:'📎', title:'Open an Expense', body:'Click a row to see its receipt and details, edit a draft, or approve and reject in the review queue.' },
  ],
  dashboard: [
    { target:'[data-tour="dashboard-kpis"]',     position:'bottom', icon:'💰', title:'Four Numbers', body:'Invoiced, Collected, Outstanding (with anything overdue), and Profit on invoiced: invoiced minus member pay. Member pay is each member\'s approved invoice, or an estimate until it\'s approved; the Provisional chip says how many are approved. Click a tile to go where that number is managed.' },
    { target:'[data-tour="dashboard-chart"]',    position:'bottom', icon:'📊', title:'Revenue Trend', body:'Line and Bar show monthly revenue for the last 12 months (hover a bar for exact amounts; use the range controls to switch between month, YTD, and custom views, and "Count invoices by" to file invoices under the month the work was for or the month payment arrived), Pie shows profit by client for the period, and Snapshot shows Invoiced, Collected, Member pay, and Profit as four bars for whatever period is currently selected — each starts at zero and reaches its own real value rather than stacking.' },
    { target:'[data-tour="dashboard-pipeline"]', position:'top',    icon:'📋', title:'Invoice Pipeline', body:'Outstanding invoices broken down by aging buckets — current, 1–30 days, 31–60 days, and 60+ days overdue. A quick health check on receivables.' },
    { target:'[data-tour="dashboard-clients"]',  position:'top',    icon:'🏆', title:'Top Clients', body:'Your clients ranked by invoiced revenue for the selected period. Spot which clients are driving the most revenue at a glance.' },
  ],
};

// SpotlightTour: spotlight around a DOM element (data-tour attr), falls back to centered modal
export function WalkthroughModal({ tourKey, onClose }) {
  // A step marked `optional` is for something only some people have (e.g. the Getting
  // started pill, shown to a new workspace's admin). Left out when its target isn't there.
  const [steps] = useState(() => (TOURS[tourKey] || []).filter(st => !st.optional || !st.target || document.querySelector(st.target)));
  const [stepIdx, setStepIdx] = useState(0);
  const [spotRect, setSpotRect] = useState(null);
  const PAD = 10;
  const step = steps[stepIdx];

  useEffect(() => {
    if (!step?.target) { setSpotRect(null); return; }
    step.before?.();
    const t = setTimeout(() => {
      const el = document.querySelector(step.target);
      if (!el) { setSpotRect(null); return; }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        const el2 = document.querySelector(step.target);
        if (!el2) return;
        const r = el2.getBoundingClientRect();
        setSpotRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD*2, height: r.height + PAD*2 });
      }, 200);
    }, step.before ? 150 : 0);
    return () => clearTimeout(t);
  }, [stepIdx, step?.target]);

  useEffect(() => {
    if (!step?.target) return;
    const update = () => {
      const el = document.querySelector(step.target);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setSpotRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD*2, height: r.height + PAD*2 });
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [step?.target]);

  if (!step) return null;
  const isFirst = stepIdx === 0;
  const isLast  = stepIdx === steps.length - 1;
  const next = () => isLast ? onClose() : setStepIdx(i => i + 1);

  const dots = (
    <div style={{display:'flex',gap:'5px',justifyContent:'center',marginBottom:'12px'}}>
      {steps.map((_,i) => (
        <div key={i} onClick={()=>setStepIdx(i)}
          style={{width:i===stepIdx?18:6,height:6,borderRadius:'3px',background:i===stepIdx?C.accent:'rgba(255,255,255,0.18)',cursor:'pointer',transition:'all 0.2s'}} />
      ))}
    </div>
  );

  const navBtns = (
    <div style={{display:'flex',gap:'8px',justifyContent:'center',marginTop:'14px'}}>
      {!isFirst && <button style={css.btn()} onClick={()=>setStepIdx(i=>i-1)}>← Back</button>}
      <button style={css.btn('primary')} onClick={next}>{isLast ? 'Done' : 'Next →'}</button>
    </div>
  );

  const closeBtn = (
    <button onClick={onClose} style={{position:'fixed',top:'16px',right:'16px',zIndex:9010,
      background:C.surface,border:`1px solid ${C.border}`,borderRadius:'50%',
      width:'32px',height:'32px',display:'flex',alignItems:'center',justifyContent:'center',
      cursor:'pointer',color:C.muted,fontSize:'16px',boxShadow:'0 2px 8px rgba(0,0,0,0.4)'}}>✕</button>
  );

  // ── Centered modal fallback (no target or element not found) ──────────────
  if (!spotRect) {
    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9000,padding:'20px'}}
        onClick={e => { if (e.target === e.currentTarget) next(); }}>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'16px',width:'100%',maxWidth:'420px',padding:'32px 28px 24px',boxShadow:'0 24px 64px rgba(0,0,0,0.5)',position:'relative'}}>
          <button onClick={onClose} style={{position:'absolute',top:'14px',right:'14px',background:'transparent',border:'none',color:C.muted,fontSize:'18px',cursor:'pointer'}}>✕</button>
          {dots}
          {step.icon && <div style={{fontSize:'36px',textAlign:'center',marginBottom:'10px'}}>{step.icon}</div>}
          <div style={{fontSize:'17px',fontWeight:'700',color:C.textBright,marginBottom:'8px',textAlign:'center'}}>{step.title}</div>
          <div style={{fontSize:'13px',color:C.text,lineHeight:'1.65',textAlign:'center'}}>{step.body}</div>
          <div style={{textAlign:'center',fontSize:'11px',color:C.muted,marginTop:'10px'}}>Step {stepIdx+1} of {steps.length}</div>
          {navBtns}
        </div>
      </div>
    );
  }

  // ── Spotlight mode ────────────────────────────────────────────────────────
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const TIP_W = 300;
  const MARGIN = 14;
  const cx = spotRect.left + spotRect.width / 2;
  const clampL = l => Math.max(MARGIN, Math.min(l, vw - TIP_W - MARGIN));

  const pref = step.position || 'bottom';
  let tipStyle;
  const spBot = spotRect.top + spotRect.height;
  const spRight = spotRect.left + spotRect.width;

  if (pref === 'bottom' && spBot + 220 < vh)
    tipStyle = { top: spBot + MARGIN, left: clampL(cx - TIP_W/2) };
  else if (pref === 'top' && spotRect.top - 220 > 0)
    tipStyle = { top: spotRect.top - 220 - MARGIN, left: clampL(cx - TIP_W/2) };
  else if (pref === 'right' && spRight + TIP_W + MARGIN < vw)
    tipStyle = { top: Math.max(MARGIN, spotRect.top), left: spRight + MARGIN };
  else if (pref === 'left' && spotRect.left - TIP_W - MARGIN > 0)
    tipStyle = { top: Math.max(MARGIN, spotRect.top), left: spotRect.left - TIP_W - MARGIN };
  else if (spBot + 220 < vh)
    tipStyle = { top: spBot + MARGIN, left: clampL(cx - TIP_W/2) };
  else
    tipStyle = { top: Math.max(MARGIN, spotRect.top - 220 - MARGIN), left: clampL(cx - TIP_W/2) };

  const OV = 'rgba(0,0,0,0.72)';
  return (
    <>
      {/* Four dark overlay panels — creates spotlight hole */}
      <div onClick={next} style={{position:'fixed',top:0,left:0,right:0,height:`${Math.max(0,spotRect.top)}px`,background:OV,zIndex:9000}} />
      <div onClick={next} style={{position:'fixed',top:`${spBot}px`,left:0,right:0,bottom:0,background:OV,zIndex:9000}} />
      <div onClick={next} style={{position:'fixed',top:`${spotRect.top}px`,left:0,width:`${Math.max(0,spotRect.left)}px`,height:`${spotRect.height}px`,background:OV,zIndex:9000}} />
      <div onClick={next} style={{position:'fixed',top:`${spotRect.top}px`,left:`${spRight}px`,right:0,height:`${spotRect.height}px`,background:OV,zIndex:9000}} />
      {/* Accent ring around spotlight */}
      <div style={{position:'fixed',top:`${spotRect.top}px`,left:`${spotRect.left}px`,width:`${spotRect.width}px`,height:`${spotRect.height}px`,borderRadius:'8px',boxShadow:`0 0 0 3px ${C.accent}, 0 0 0 6px ${C.accent}33`,zIndex:9001,pointerEvents:'none',transition:'all 0.25s ease'}} />
      {/* Tooltip bubble */}
      <div style={{position:'fixed',...tipStyle,width:`${TIP_W}px`,background:C.surface,border:`1px solid ${C.border}`,borderRadius:'12px',padding:'18px 18px 14px',zIndex:9005,boxShadow:'0 8px 40px rgba(0,0,0,0.6)'}}>
        {dots}
        {step.icon && <div style={{fontSize:'24px',textAlign:'center',marginBottom:'6px'}}>{step.icon}</div>}
        <div style={{fontSize:'14px',fontWeight:'700',color:C.textBright,marginBottom:'6px',textAlign:'center'}}>{step.title}</div>
        <div style={{fontSize:'12px',color:C.text,lineHeight:'1.6',textAlign:'center'}}>{step.body}</div>
        <div style={{textAlign:'center',fontSize:'11px',color:C.muted,marginTop:'8px'}}>Step {stepIdx+1} of {steps.length} · click backdrop to advance</div>
        {navBtns}
      </div>
      {closeBtn}
    </>
  );
}

export function TourButton({ onClick }) {
  return (
    <button onClick={onClick} title="Page walkthrough"
      style={{width:'26px',height:'26px',borderRadius:'50%',border:`1px solid ${C.border}`,background:'rgba(255,255,255,0.05)',color:C.muted,fontSize:'13px',fontWeight:'700',cursor:'pointer',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1}}>
      ?
    </button>
  );
}

export function useTour(key) {
  const storageKey = `tk_tour_${key}`;
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem(storageKey)) {
      // First arrival from the sign-up wizard: the Getting started checklist opens instead
      // (see GettingStarted.js). Count this tour as seen so it doesn't pop up later on
      // some unrelated visit; the "?" button on the page still runs it.
      if (localStorage.getItem('tk_gs_pending')) { localStorage.setItem(storageKey, '1'); return; }
      setShow(true);
      localStorage.setItem(storageKey, '1');
    }
  }, []);
  const launch = () => setShow(true);
  const close  = () => setShow(false);
  return { show, launch, close };
}

// ─── TIMER ────────────────────────────────────────────────────────────────────
function TimerView({ profile, orgMember, orgId, orgPlan, clients, taskTypes, tags, entries, assignments, monthLocks, statements, onRefresh, onNavigate }) {
  const tour = useTour('timer');
  const isAdminUser = orgMember && ['admin','owner'].includes(orgMember.role);
  const [upgradePending, setUpgradePending] = useState(null); // holds the pending saveEntry(...) args while the modal is open
  const [upgrading, setUpgrading] = useState(false);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [cid, setCid] = useState(''); const [tid, setTid] = useState(''); const [desc, setDesc] = useState('');
  const [mDate, setMDate] = useState(new Date().toISOString().slice(0,10));
  const [mHrs, setMHrs] = useState(''); const [mMins, setMMins] = useState(''); const [mCid, setMCid] = useState(''); const [mTid, setMTid] = useState(''); const [mDesc, setMDesc] = useState('');
  const [mStart, setMStart] = useState(''); const [mEnd, setMEnd] = useState('');
  const [billable, setBillable] = useState(true); const [mBillable, setMBillable] = useState(true);
  // Where the switch's current value came from: 'task' (the task type's default) or 'you' (changed by hand), for the hint under it.
  const [billableSrc, setBillableSrc] = useState(null); const [mBillableSrc, setMBillableSrc] = useState(null);
  const [timerTagIds, setTimerTagIds] = useState([]); const [mTagIds, setMTagIds] = useState([]);
  const [flash, setFlash] = useState(false); const [mErr, setMErr] = useState(''); const [timerErr, setTimerErr] = useState('');
  // Neither save button had an in-flight guard — a double-click, or one click
  // followed by an anxious second click during the round-trip, fired saveEntry
  // twice and inserted two identical rows. This is what caused Brandy
  // Mouchet's duplicate Timer entries (2026-08-24).
  const [savingTimer, setSavingTimer] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const [timerMode, setTimerMode] = useState('live');
  const iRef = useRef(null); const t0 = useRef(null); const elapsedRef = useRef(0);

  useEffect(() => {
    if (running) {
      t0.current = Date.now()-elapsed*1000;
      iRef.current = setInterval(()=>{
        const val = Math.floor((Date.now()-t0.current)/1000);
        elapsedRef.current = val;
        setElapsed(val);
      },1000);
    }
    else clearInterval(iRef.current);
    return () => clearInterval(iRef.current);
  }, [running]);

  // The actual insert, shared by the normal path and the post-confirm path
  // (confirmUpgrade calls this directly, having already gotten consent, so
  // it must never re-run the gate below).
  const doInsert = async ({ hours, clientId, taskTypeId, description, date, isBillable, startTime, endTime, tagIds }) => {
    const { data: newEntry, error } = await supabase.from('time_entries').insert({ org_id:orgId, user_id:profile.id, client_id:clientId||null, task_type_id:taskTypeId||null, description, hours, entry_date:date, is_billable:isBillable, start_time:startTime, end_time:endTime }).select('id').single();
    if (error) return error;
    if (tagIds.length > 0 && newEntry?.id) {
      const resolved = await resolveTags(orgId, tagIds);
      if (resolved.length) await supabase.from('time_entry_tags').insert(resolved.map(tid=>({time_entry_id:newEntry.id,tag_id:tid})));
    }
    setFlash(true); setTimeout(()=>setFlash(false),2000);
    onRefresh();
    return null;
  };

  const saveEntry = async (hours, clientId, taskTypeId, description, date, isBillable = true, startTime = null, endTime = null, tagIds = []) => {
    // Gate: an administrative seat saving real billable time, on a priced org,
    // pauses here for the confirm-and-upgrade modal instead of inserting.
    // orgPlan is null for every org that pre-dates the tier system (including
    // GuidePoint's own), which shouldGateBillableSeatUpgrade checks explicitly
    // so this stays a no-op for those orgs. See src/pricingGates.js.
    const targetClient = clientId ? clients.find(c => c.id === clientId) : null;
    if (shouldGateBillableSeatUpgrade({ orgPlan, seatClassification: orgMember?.seat_classification, isBillable, client: targetClient })) {
      setUpgradePending({ hours, clientId, taskTypeId, description, date, isBillable, startTime, endTime, tagIds });
      return 'PENDING_UPGRADE'; // sentinel: callers must treat this as neither error nor success
    }
    return doInsert({ hours, clientId, taskTypeId, description, date, isBillable, startTime, endTime, tagIds });
  };

  const confirmUpgrade = async () => {
    if (!upgradePending || upgrading) return;
    setUpgrading(true);
    const { error: upgradeErr } = await supabase.rpc('self_upgrade_to_billable_seat', { p_org_id: orgId });
    if (upgradeErr) {
      setUpgrading(false);
      notifyError('Upgrading this seat failed: ' + (upgradeErr.message || 'unknown error'));
      return;
    }
    const insertErr = await doInsert(upgradePending);
    setUpgrading(false);
    setUpgradePending(null);
    if (insertErr) { notifyError('Saving the time entry failed: ' + (insertErr.message || 'unknown error')); return; }
    // Local orgMember.seat_classification is stale until the next org-wide
    // refresh; onRefresh() inside doInsert reloads org data, which picks it up.
    setCid(''); setTid(''); setDesc(''); setBillable(true); setBillableSrc(null); setTimerTagIds([]);
    setMHrs(''); setMMins(''); setMCid(''); setMTid(''); setMDesc(''); setMStart(''); setMEnd(''); setMBillable(true); setMBillableSrc(null); setMTagIds([]);
  };

  const cancelUpgrade = () => {
    setUpgradePending(null);
    setCid(''); setMCid(''); // reset the target selector per the confirm/cancel spec
  };

  const UpgradeSeatModal = () => {
    if (!upgradePending) return null;
    const rate = orgPlan === 'max' ? '$18' : orgPlan === 'pro' ? '$9' : '';
    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
        <div style={{...css.card,maxWidth:'420px',margin:'16px'}}>
          <div style={{fontWeight:'700',fontSize:'15px',color:C.textBright,marginBottom:'10px'}}>Log billable hours?</div>
          <div style={{fontSize:'13px',color:C.textDim,marginBottom:'18px',lineHeight:'1.5'}}>
            Logging billable hours will upgrade this seat to a paid billable seat{rate && ` (${rate}/mo)`} for the active billing cycle.
          </div>
          <div style={{display:'flex',gap:'10px',justifyContent:'flex-end'}}>
            <button style={css.btn('ghost')} onClick={cancelUpgrade} disabled={upgrading}>Cancel</button>
            <button style={css.btn('primary')} onClick={confirmUpgrade} disabled={upgrading}>{upgrading ? 'Upgrading…' : 'Confirm & Upgrade'}</button>
          </div>
        </div>
      </div>
    );
  };

  const stop = () => { setRunning(false); };

  const addNewClient = async (name) => {
    if (isFreeClientCapReached(orgPlan, clients)) {
      notifyError('The Free plan is limited to 5 active clients. Upgrade to Pro or Max to add more.');
      return null;
    }
    const { data, error } = await supabase.from('clients').insert({ org_id: orgId, name, model: 'hourly', rate: 0, is_active: true }).select('id').single();
    if (error || !data) return null;
    onRefresh();
    return data.id;
  };

  const addNewTaskType = async (name) => {
    const { data, error } = await supabase.from('task_types').insert({ org_id: orgId, name }).select('id').single();
    if (error || !data) return null;
    onRefresh();
    return data.id;
  };

  // A task type can start new entries as not billable to the client: an admin sets
  // "Charge client by default" once in Settings, Task Types, so picking "Internal
  // meeting" turns the switch off with no extra step. One entry can still be changed
  // by hand. Pay is never affected, and the hint says so when it matters.
  const taskCharges = (id) => !id || taskTypes.find(t => t.id === id)?.billable_by_default !== false;
  const pickTask  = (id) => { setTid(id);  setBillable(taskCharges(id));   setBillableSrc(taskCharges(id) ? null : 'task'); };
  const pickMTask = (id) => { setMTid(id); setMBillable(taskCharges(id));  setMBillableSrc(taskCharges(id) ? null : 'task'); };

  const BillableToggle = ({ value, onChange, src, taskId }) => (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
        <div onClick={()=>onChange(!value)} style={{
          width:'36px',height:'20px',borderRadius:'10px',cursor:'pointer',flexShrink:0,
          background:value?'rgba(72,187,120,0.8)':'rgba(100,116,139,0.35)',
          position:'relative',transition:'background 0.2s'
        }}>
          <div style={{
            position:'absolute',top:'2px',left:value?'18px':'2px',
            width:'16px',height:'16px',borderRadius:'50%',background:'#fff',
            transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.3)'
          }} />
        </div>
        <span style={{fontSize:'13px',color:value?C.text:C.muted,userSelect:'none'}}>
          <TKTooltip text="On: this time goes on the client's invoice. Off: it is left off their invoice. Your pay is the same either way.">{value ? 'Billable to client' : 'Not billable to client'} ⓘ</TKTooltip>
        </span>
      </div>
      {!value && (
        <div style={{fontSize:'11px',color:C.muted,marginTop:'4px',marginLeft:'46px'}}>
          {src === 'task'
            ? `Off because “${taskTypes.find(t=>t.id===taskId)?.name || 'this task'}” isn’t charged by default. Your pay isn’t affected.`
            : 'Not charged to the client. Your pay isn’t affected.'}
        </div>
      )}
    </div>
  );

  const saveTimer = async () => {
    if (savingTimer) return; // already in flight — the guard that was missing
    setTimerErr('');
    const secs = elapsedRef.current;
    if (secs < 10) { setTimerErr('Timer must run for at least 10 seconds before saving.'); return; }
    setSavingTimer(true);
    const endTs = new Date();
    const startTs = new Date(endTs.getTime() - secs*1000);
    const err = await saveEntry(toHrs(secs), cid, tid, desc, endTs.toISOString().slice(0,10), billable, startTs.toISOString(), endTs.toISOString(), timerTagIds);
    setSavingTimer(false);
    if (err === 'PENDING_UPGRADE') return; // modal is open; form stays as-is until it resolves
    if (err) { setTimerErr(err.message); return; }
    elapsedRef.current = 0; setElapsed(0); setCid(''); setTid(''); setDesc(''); setBillable(true); setBillableSrc(null); setTimerTagIds([]);
  };

  const mTimeHours = (() => {
    if (!mStart && !mEnd) return null;
    const [sh, sm] = mStart.split(':').map(Number);
    const [eh, em] = mEnd.split(':').map(Number);
    let startMins = sh * 60 + sm;
    let endMins = eh * 60 + em;
    if (endMins <= startMins) endMins += 24 * 60;
    return (endMins - startMins) / 60;
  })();

  const mTimeDuration = (() => {
    if (mTimeHours === null) return null;
    const totalMins = Math.round(mTimeHours * 60);
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  })();

  const addManual = async () => {
    if (savingManual) return; // already in flight
    setMErr('');
    const hasStart = mStart !== '';
    const hasEnd = mEnd !== '';
    if (hasStart !== hasEnd) { setMErr('Fill in both Start Time and End Time, or leave both blank.'); return; }
    if (!mCid || !mDate) return;
    let hours, startTime = null, endTime = null;
    if (hasStart && hasEnd) {
      hours = mTimeHours;
      startTime = isoFromDateTime(mDate, mStart);
      // mTimeHours rolls past midnight when end <= start; reflect that in end_time.
      endTime = startTime ? new Date(new Date(startTime).getTime() + mTimeHours*3600*1000).toISOString() : null;
    } else {
      if (!mHrs && !mMins) return;
      hours = (parseFloat(mHrs) || 0) + (parseFloat(mMins) || 0) / 60;
    }
    setSavingManual(true);
    const err = await saveEntry(hours, mCid, mTid, mDesc, mDate, mBillable, startTime, endTime, mTagIds);
    setSavingManual(false);
    if (err === 'PENDING_UPGRADE') return; // modal is open; form stays as-is until it resolves
    if (err) { setMErr(err.message); return; }
    setMHrs(''); setMMins(''); setMCid(''); setMTid(''); setMDesc(''); setMStart(''); setMEnd(''); setMBillable(true); setMBillableSrc(null); setMTagIds([]);
  };


  // Today's entries for whoever is logging, so a just-saved entry is visible
  // without a trip to My Logs (and it's harder to log the same work twice).
  const todayISO = new Date().toLocaleDateString('en-CA');
  const todays = (entries || [])
    .filter(e => e.user_id === profile.id && e.entry_date === todayISO)
    .sort((a, b) => String(a.start_time || a.created_at || '').localeCompare(String(b.start_time || b.created_at || '')));
  const todaysTotal = todays.reduce((s, e) => s + parseFloat(e.hours || 0), 0);

  // Client comes first in both forms: it's the one field that drives billing,
  // and the easiest to skip when it sits below everything else.
  const clientPicker = (value, onChange) => (
    <SearchableSelect options={clients.map(c=>({value:c.id,label:c.name}))} value={value} onChange={onChange} allLabel="Select client..." placeholder="Search clients…" style={{width:'100%'}} onAddNew={addNewClient} />
  );
  const taskPicker = (value, onChange) => (
    <SearchableSelect options={taskTypes.map(t=>({value:t.id,label:t.name}))} value={value} onChange={onChange} allLabel="Task type (optional)" placeholder="Search task types…" style={{width:'100%'}} onAddNew={addNewTaskType} />
  );
  const tagsField = (value, onChange) => (
    <div><div style={css.lbl}><TKTooltip text="Labels for organizing entries. Tags don't affect billing — use them for filtering in My Logs and Client Reports.">Tags ⓘ</TKTooltip></div><TagPicker availableTags={tags} selectedTagIds={value} onSelect={onChange} /></div>
  );

  return (
    <Page title="Timer" onTour={tour.launch}
      controls={<SegmentedControl options={[['live','Live timer'],['manual','Manual entry']]} value={timerMode} onChange={setTimerMode} />}>
      {tour.show && <WalkthroughModal tourKey="timer" onClose={tour.close} />}
      <UpgradeSeatModal />

      {timerMode === 'live' ? (
        <div style={{...css.card, maxWidth:'520px', marginBottom:0}} data-tour="timer-live">
          <div style={{display:"flex", flexDirection:"column", gap:"8px"}}>
            {clientPicker(cid, setCid)}
            {taskPicker(tid, pickTask)}
            <input style={css.inp} placeholder="Description (optional)" value={desc} onChange={e=>setDesc(e.target.value)} />
            <BillableToggle value={billable} onChange={v=>{setBillable(v);setBillableSrc('you');}} src={billableSrc} taskId={tid} />
            {tagsField(timerTagIds, setTimerTagIds)}
          </div>
          <div style={css.div} />
          <div style={{textAlign:"center", padding:"12px 0 8px"}}>
            <div style={css.timerDisp}>{fmtT(elapsed)}</div>
            <div style={{display:"flex", flexDirection:"column", alignItems:"center", marginTop:"20px", gap:"12px"}}>
              <button style={css.timerBtn(running)} aria-label={running ? 'Stop timer' : 'Start timer'} onClick={()=>running?stop():setRunning(true)}>{running?"■":"▶"}</button>
              {!running && elapsed > 0 && (
                <button style={{...css.btn("primary"), opacity:savingTimer?0.6:1, cursor:savingTimer?'default':'pointer'}}
                        disabled={savingTimer} onClick={saveTimer}>{savingTimer?'Saving…':'Save entry'}</button>
              )}
            </div>
            {flash && <div style={{color:C.success, fontSize:"12px", marginTop:"10px"}}>Entry saved ✓</div>}
            {timerErr && <div style={{color:C.danger, fontSize:"12px", marginTop:"10px"}}>{timerErr}</div>}
          </div>
        </div>
      ) : (
        <div style={{...css.card, maxWidth:'520px', marginBottom:0}} data-tour="timer-manual">
          <div style={{display:"flex", flexDirection:"column", gap:"8px"}}>
            {clientPicker(mCid, setMCid)}
            {taskPicker(mTid, pickMTask)}
            <input style={css.inp} placeholder="Description (optional)" value={mDesc} onChange={e=>setMDesc(e.target.value)} />
            <div><div style={css.lbl}>Date</div><input type="date" style={css.inp} value={mDate} onChange={e=>setMDate(e.target.value)} /></div>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px"}}>
              <div><div style={css.lbl}><TKTooltip text="Optional. Stored for reference only — billing is always calculated from the Hours field, not clock times.">Start Time ⓘ</TKTooltip></div><input type="time" style={css.inp} value={mStart} onChange={e=>{setMStart(e.target.value);setMErr('');}} /></div>
              <div><div style={css.lbl}><TKTooltip text="Optional. Stored for reference only — billing is always calculated from the Hours field, not clock times.">End Time ⓘ</TKTooltip></div><input type="time" style={css.inp} value={mEnd} onChange={e=>{setMEnd(e.target.value);setMErr('');}} /></div>
            </div>
            {mTimeDuration && (
              <div style={{fontSize:"12px", color:C.text, padding:"6px 10px", background:C.bg, borderRadius:"6px", border:`1px solid ${C.border}`}}>
                Duration: <strong>{mTimeDuration}</strong>
              </div>
            )}
            {!mStart && !mEnd && (
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px"}}>
                <div><div style={css.lbl}><TKTooltip text="Whole hours worked. Use decimals (e.g. 1.5) or combine with the Minutes field below.">Hours ⓘ</TKTooltip></div><input style={css.inp} type="number" min="0" step="any" placeholder="0" value={mHrs} onChange={e=>setMHrs(e.target.value)} /></div>
                <div><div style={css.lbl}><TKTooltip text="Minutes worked in addition to hours. Combined with Hours to calculate the total entry duration.">Minutes ⓘ</TKTooltip></div><input style={css.inp} type="number" min="0" max="59" step="1" placeholder="0" value={mMins} onChange={e=>setMMins(e.target.value)} /></div>
              </div>
            )}
            <BillableToggle value={mBillable} onChange={v=>{setMBillable(v);setMBillableSrc('you');}} src={mBillableSrc} taskId={mTid} />
            {tagsField(mTagIds, setMTagIds)}
            {mErr && <div style={{fontSize:"12px", color:C.danger, padding:"6px 10px", background:`${C.danger}14`, borderRadius:"6px", border:`1px solid ${C.danger}33`}}>{mErr}</div>}
            <button style={{...css.btn("primary"), opacity:savingManual?0.6:1, cursor:savingManual?'default':'pointer'}}
                    disabled={savingManual} onClick={addManual}>{savingManual?'Saving…':'Add entry'}</button>
            {flash && <div style={{color:C.success, fontSize:"12px"}}>Entry saved ✓</div>}
          </div>
        </div>
      )}

      {/* Today */}
      <div style={{maxWidth:'720px'}} data-tour="timer-today">
        <div style={{display:'flex',alignItems:'baseline',gap:'8px',margin:'8px 0 8px'}}>
          <h2 style={{margin:0,fontSize:'14px',fontWeight:'700',color:C.textBright}}>Today</h2>
          <span style={{fontSize:'12px',color:C.muted}}>{todays.length ? `${fmtHours(todaysTotal)} logged` : ''}</span>
          <span style={{flex:1}} />
          {onNavigate && <button type="button" style={css.btn('ghost')} onClick={()=>onNavigate('logs')}>All entries →</button>}
        </div>
        <DataTable rows={todays}
          columns={[
            { key:'client', label:'Client', render: e => clients.find(c => c.id === e.client_id)?.name || '—' },
            { key:'task', label:'Task', render: e => taskTypes.find(t => t.id === e.task_type_id)?.name || '—' },
            { key:'description', label:'Description', render: e => e.description || '—' },
            { key:'time', label:'Time', render: e => fmtTimeRange(e.start_time, e.end_time) || '—' },
            { key:'hours', label:'Hours', align:'right', render: e => fmtHours(parseFloat(e.hours || 0)) },
          ]}
          empty={<EmptyState title="Nothing logged today yet" body="Start the timer or add a manual entry, and it'll show up here." />} />
      </div>
    </Page>
  );
}


// ─── ENTRY EDIT ROW ───────────────────────────────────────────────────────────
function EntryEditRow({ entry, clients, taskTypes, tags, orgId, colSpan, onSave, onCancel }) {
  const initMins = Math.round(parseFloat(entry.hours||0)*60);
  const [eDate, setEDate]   = useState(entry.entry_date||'');
  // Prefill start/end from the stored timestamps (local time) so editing keeps them.
  const [eStart, setEStart] = useState(hhmmFromIso(entry.start_time));
  const [eEnd, setEEnd]     = useState(hhmmFromIso(entry.end_time));
  const [eHrs, setEHrs]     = useState(String(Math.floor(initMins/60)));
  const [eMins, setEMins]   = useState(String(initMins%60));
  const [eCid, setECid]         = useState(entry.client_id||'');
  const [eTid, setETid]         = useState(entry.task_type_id||'');
  const [eDesc, setEDesc]       = useState(entry.description||'');
  const [eBillable, setEBillable] = useState(entry.is_billable !== false);
  const [eTagIds, setETagIds]   = useState((entry.time_entry_tags||[]).map(tl=>tl.tag_id).filter(Boolean));
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  const timeHrs = (() => {
    if (!eStart||!eEnd) return null;
    const [sh,sm]=eStart.split(':').map(Number),[eh,em]=eEnd.split(':').map(Number);
    let sM=sh*60+sm, eM=eh*60+em; if(eM<=sM) eM+=24*60; return (eM-sM)/60;
  })();
  const timeDur = timeHrs===null ? null : (() => {
    const tot=Math.round(timeHrs*60), h=Math.floor(tot/60), m=tot%60;
    return h===0?`${m}m`:m===0?`${h}h`:`${h}h ${m}m`;
  })();

  const save = async () => {
    setErr(''); setSaving(true);
    const hasS=eStart!=='', hasE=eEnd!=='';
    if (hasS!==hasE) { setErr('Fill in both start and end time, or leave both blank.'); setSaving(false); return; }
    let hours, startTime = null, endTime = null;
    if (hasS&&hasE) {
      hours=timeHrs;
      startTime = isoFromDateTime(eDate, eStart);
      endTime = startTime ? new Date(new Date(startTime).getTime() + timeHrs*3600*1000).toISOString() : null;
    }
    else { hours=(parseFloat(eHrs)||0)+(parseFloat(eMins)||0)/60; if(hours<=0){setErr('Enter hours or minutes.');setSaving(false);return;} }
    const { error } = await supabase.from('time_entries')
      .update({ entry_date:eDate, hours, client_id:eCid||null, task_type_id:eTid||null, description:eDesc, is_billable:eBillable, start_time:startTime, end_time:endTime })
      .eq('id', entry.id);
    if (error) { setSaving(false); setErr(error.message); return; }
    // Sync tags: delete all then reinsert resolved IDs
    if (orgId && tags) {
      await supabase.from('time_entry_tags').delete().eq('time_entry_id', entry.id);
      if (eTagIds.length > 0) {
        const resolved = await resolveTags(orgId, eTagIds);
        if (resolved.length) await supabase.from('time_entry_tags').insert(resolved.map(tid=>({time_entry_id:entry.id,tag_id:tid})));
      }
    }
    setSaving(false);
    onSave();
  };

  return (
    <tr>
      <td colSpan={colSpan} style={{padding:"14px",background:"rgba(49,167,217,0.05)",borderBottom:`1px solid ${C.border}`}}>
        {/* Capped width -- this used to stretch across the whole table (5-6
            columns wide on a real monitor), which made a one-row edit feel
            like a full-page form. A compact, left-aligned panel reads more
            like editing a single row and less like a separate page. */}
        <div style={{display:"flex",flexDirection:"column",gap:"7px",maxWidth:"440px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"6px"}}>
            <div><div style={css.lbl}>Date</div><input type="date" style={{...css.inp,padding:'6px 8px',fontSize:'12px'}} value={eDate} onChange={e=>setEDate(e.target.value)} /></div>
            <div><div style={css.lbl}><TKTooltip text="Optional. Stored for reference only — billing is always calculated from the Hours field, not clock times.">Start ⓘ</TKTooltip></div><input type="time" style={{...css.inp,padding:'6px 8px',fontSize:'12px'}} value={eStart} onChange={e=>{setEStart(e.target.value);setErr('');}} /></div>
            <div><div style={css.lbl}><TKTooltip text="Optional. Stored for reference only — billing is always calculated from the Hours field, not clock times.">End ⓘ</TKTooltip></div><input type="time" style={{...css.inp,padding:'6px 8px',fontSize:'12px'}} value={eEnd} onChange={e=>{setEEnd(e.target.value);setErr('');}} /></div>
          </div>
          {timeDur && <div style={{fontSize:"12px",color:C.accent,padding:"4px 8px",background:"rgba(49,167,217,0.08)",borderRadius:"4px"}}>Duration: <strong>{timeDur}</strong></div>}
          {!eStart&&!eEnd&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px"}}>
              <div><div style={css.lbl}>Hours</div><input style={{...css.inp,padding:'6px 8px',fontSize:'12px'}} type="number" min="0" step="any" placeholder="0" value={eHrs} onChange={e=>setEHrs(e.target.value)} /></div>
              <div><div style={css.lbl}>Minutes</div><input style={{...css.inp,padding:'6px 8px',fontSize:'12px'}} type="number" min="0" max="59" placeholder="0" value={eMins} onChange={e=>setEMins(e.target.value)} /></div>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px"}}>
            <SearchableSelect options={clients.map(c=>({value:c.id,label:c.name}))} value={eCid} onChange={setECid} allLabel="No client" placeholder="Search clients…" style={{width:'100%'}} />
            <SearchableSelect options={taskTypes.map(t=>({value:t.id,label:t.name}))} value={eTid} onChange={setETid} allLabel="Task type (optional)" placeholder="Search task types…" style={{width:'100%'}} />
          </div>
          <input style={{...css.inp,padding:'6px 8px',fontSize:'13px'}} placeholder="Description (optional)" value={eDesc} onChange={e=>setEDesc(e.target.value)} />
          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
            <div onClick={()=>setEBillable(b=>!b)} style={{
              width:'36px',height:'20px',borderRadius:'10px',cursor:'pointer',flexShrink:0,
              background:eBillable?'rgba(72,187,120,0.8)':'rgba(100,116,139,0.35)',
              position:'relative',transition:'background 0.2s'
            }}>
              <div style={{position:'absolute',top:'2px',left:eBillable?'18px':'2px',width:'16px',height:'16px',borderRadius:'50%',background:'#fff',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.3)'}} />
            </div>
            <span style={{fontSize:'13px',color:eBillable?C.text:C.muted,userSelect:'none'}}>{eBillable?'Billable to client':'Not billable to client'}</span>
          </div>
          {tags && <div><div style={css.lbl}><TKTooltip text="Labels for organizing entries. Tags are for filtering and reporting only — they don't affect billing.">Tags ⓘ</TKTooltip></div><TagPicker availableTags={tags||[]} selectedTagIds={eTagIds} onSelect={setETagIds} /></div>}
          {err && <div style={{fontSize:"12px",color:C.danger,padding:"6px 10px",background:"rgba(229,62,62,0.08)",borderRadius:"4px"}}>{err}</div>}
          <div style={{display:"flex",gap:"8px"}}>
            <button style={css.btn("primary")} onClick={save} disabled={saving}>{saving?"Saving...":"Save"}</button>
            <button style={css.btn()} onClick={onCancel} disabled={saving}>Cancel</button>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ─── SEARCHABLE SELECT ────────────────────────────────────────────────────────
function SearchableSelect({ options=[], value='', onChange, allLabel='All', placeholder='Search…', minWidth=150, style:outerStyle={}, onAddNew }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pendingOptions, setPendingOptions] = useState([]);
  const [adding, setAdding] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const allOptions = [...options, ...pendingOptions.filter(p => !options.find(o => o.value === p.value))];
  const filtered = allOptions.filter(o => !q || o.label.toLowerCase().includes(q.toLowerCase()));
  const selected = allOptions.find(o => o.value === value);
  const showAdd = onAddNew && q.trim() && !allOptions.find(o => o.label.toLowerCase() === q.trim().toLowerCase());
  const handleAdd = async () => {
    if (!q.trim() || adding) return;
    setAdding(true);
    const newId = await onAddNew(q.trim());
    if (newId) {
      setPendingOptions(prev => [...prev, {value: newId, label: q.trim()}]);
      onChange(newId);
      setOpen(false);
      setQ('');
    }
    setAdding(false);
  };
  return (
    <div ref={ref} style={{position:'relative',minWidth,flexShrink:0,...outerStyle}}>
      <div onClick={()=>setOpen(o=>!o)}
        style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'6px',
          padding:'6px 10px',borderRadius:'6px',border:`1px solid ${C.border}`,background:C.bg,
          cursor:'pointer',fontSize:'13px',color:selected?C.text:C.muted,userSelect:'none'}}>
        <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{selected?.label||allLabel}</span>
        <span style={{color:C.muted,fontSize:'9px',flexShrink:0}}>{open?'▲':'▼'}</span>
      </div>
      {open && (
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,minWidth:'100%',background:C.surface,
          border:`1px solid ${C.border}`,borderRadius:'8px',boxShadow:'0 6px 24px rgba(0,0,0,0.5)',
          zIndex:300,display:'flex',flexDirection:'column',maxHeight:'240px'}}>
          <div style={{padding:'6px 8px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
            <input autoFocus value={q} onChange={e=>setQ(e.target.value)} placeholder={placeholder}
              style={{width:'100%',boxSizing:'border-box',padding:'5px 8px',borderRadius:'5px',
                border:`1px solid ${C.border}`,background:C.bg,color:C.text,fontSize:'12px',outline:'none'}}
              onKeyDown={e=>{if(e.key==='Enter'&&showAdd)handleAdd();}} />
          </div>
          <div style={{overflowY:'auto',flex:1}}>
            <div onClick={()=>{onChange('');setOpen(false);setQ('');}}
              style={{padding:'8px 12px',cursor:'pointer',fontSize:'13px',color:!value?C.accent:C.text,
                background:!value?'rgba(49,167,217,0.08)':'transparent'}}>
              {allLabel}
            </div>
            {filtered.map(o=>(
              <div key={o.value} onClick={()=>{onChange(o.value);setOpen(false);setQ('');}}
                style={{padding:'8px 12px',cursor:'pointer',fontSize:'13px',
                  color:o.value===value?C.accent:C.text,
                  background:o.value===value?'rgba(49,167,217,0.08)':'transparent'}}>
                {o.label}
              </div>
            ))}
            {showAdd && (
              <div onClick={handleAdd}
                style={{padding:'8px 12px',cursor:adding?'not-allowed':'pointer',fontSize:'13px',
                  color:C.accent,borderTop:filtered.length?`1px solid ${C.border}`:'none',
                  fontWeight:'500',opacity:adding?0.6:1}}>
                {adding ? 'Adding…' : `+ Add "${q.trim()}"`}
              </div>
            )}
            {filtered.length===0&&!showAdd&&<div style={{padding:'8px 12px',fontSize:'12px',color:C.muted}}>No matches</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// Free-text client filter for My Logs -- typing narrows the list live
// (bound directly to the caller's filter string, "contains" match against
// name/alias), and a dropdown of matching clients offers the same result by
// click instead of by typing the whole name out.
function ClientLogSearch({ clients, clientIds, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const q = value.trim().toLowerCase();
  const pool = clientIds.map(cid => clients.find(c => c.id === cid)).filter(Boolean);
  const suggestions = q
    ? pool.filter(c => c.name?.toLowerCase().includes(q) || c.alias?.toLowerCase().includes(q))
    : pool;
  return (
    <div ref={ref} style={{position:'relative',minWidth:'200px'}}>
      <div style={{position:'relative'}}>
        <input
          value={value}
          onChange={e=>{onChange(e.target.value);setOpen(true);}}
          onFocus={()=>setOpen(true)}
          placeholder="Search clients…"
          style={{...css.inp,padding:'6px 26px 6px 10px',fontSize:'13px',width:'auto'}}
        />
        {value && (
          <button onClick={()=>{onChange('');setOpen(false);}}
            title="Clear"
            style={{position:'absolute',right:'6px',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:'13px',padding:'2px',lineHeight:1}}>✕</button>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,minWidth:'220px',background:C.surface,
          border:`1px solid ${C.border}`,borderRadius:'8px',boxShadow:'0 6px 24px rgba(0,0,0,0.5)',
          zIndex:300,maxHeight:'240px',overflowY:'auto'}}>
          {suggestions.map(c => (
            <div key={c.id} onClick={()=>{onChange(c.name);setOpen(false);}}
              style={{padding:'8px 12px',cursor:'pointer',fontSize:'13px',color:C.text}}>
              {c.name}{c.alias ? <span style={{color:C.muted,fontSize:'11px'}}> · {c.alias}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── LOGS ─────────────────────────────────────────────────────────────────────
// ─── TIME LEFT OUT OF PAY ──────────────────────────────────────────────────────
// time_entries.is_payable = false leaves one entry out of a member's pay (the
// hourly, hybrid and day-rate math in src/earnings.js). The entry stays on the
// timesheet, and it never touches is_billable, which is only about the client's
// invoice. The database stamps who changed it and when, and keeps a member from
// undoing an admin's decision, so none of that is decided here.
async function setEntryPayable(id, payable, note) {
  return runDb('Updating pay for this entry failed', supabase.from('time_entries')
    .update({ is_payable: payable, payable_note: payable ? null : ((note || '').trim() || null) }).eq('id', id));
}

// The two badges an entry can carry. "Not billable" is about the client,
// "Not paid" is about the member; a hover says exactly what each one does.
function EntryBadges({ entry, ownerName, viewerIsOwner }) {
  const who = entry.payable_changed_by && entry.payable_changed_by === entry.user_id
    ? (viewerIsOwner ? 'you' : (ownerName || 'the member')) : 'an admin';
  const when = entry.payable_changed_at
    ? ` on ${new Date(entry.payable_changed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '';
  return (
    <>
      {entry.is_billable === false && <span style={{marginLeft:'6px',verticalAlign:'middle'}}><StatusBadge tone="neutral" title="Not billable to the client, so it is left off their invoice. Pay is not affected.">Not billable</StatusBadge></span>}
      {entry.is_payable === false && <span style={{marginLeft:'6px',verticalAlign:'middle'}}><StatusBadge tone="warn" title={`Left out of pay by ${who}${when}.${entry.payable_note ? ` Why: ${entry.payable_note}.` : ''} It still counts as time worked, and the client invoice is not affected.`}>Not paid</StatusBadge></span>}
    </>
  );
}

// payEffect: dollars this entry takes off the member's pay for its month, 0 when
// their pay for this client is a fixed amount, null when unknown.
function LeaveOutOfPayModal({ entry, ownerName, viewerIsOwner, clientName, payEffect, onClose, onDone }) {
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const whose = viewerIsOwner ? 'your' : `${ownerName || 'the member'}’s`;
  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    const ok = await setEntryPayable(entry.id, false, why);
    setBusy(false);
    if (ok) onDone();
  };
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={onClose}>
      <div role="dialog" aria-label="Leave this time out of pay" onClick={e=>e.stopPropagation()}
        style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'12px',padding:'24px',maxWidth:'440px',width:'90%'}}>
        <div style={{fontSize:'16px',fontWeight:'700',color:C.textBright,marginBottom:'6px'}}>Leave this time out of {whose} pay?</div>
        <div style={{fontSize:'13px',color:C.muted,marginBottom:'14px',lineHeight:1.5}}>
          {formatExactTime(parseFloat(entry.hours||0))} on {entry.entry_date}{entry.description ? ` (${entry.description})` : ''} will not count toward {whose} pay. It stays on the timesheet. Whether {clientName || 'the client'} is charged does not change.
        </div>
        {payEffect !== null && (
          <div style={{padding:'10px 12px',background:C.bg,border:`1px solid ${C.border}`,borderRadius:'6px',marginBottom:'14px',fontSize:'13px',color:C.text}}>
            {payEffect > 0 ? `Pay goes down by ${$c(payEffect)} for this month.` : 'No change to pay: this client pays a fixed amount, so hours do not affect it.'}
          </div>
        )}
        <div style={css.lbl}>Why? (optional)</div>
        <input style={css.inp} value={why} maxLength={200} autoFocus placeholder="Redone after a mapping error"
          onChange={e=>setWhy(e.target.value)} onKeyDown={e=>{ if (e.key==='Enter') confirm(); }} />
        <div style={{fontSize:'12px',color:C.muted,marginTop:'8px'}}>
          {viewerIsOwner ? 'An admin can see this under Reports, Unpaid time.' : `${ownerName || 'The member'} will see “Not paid” on this entry, with your reason.`}
        </div>
        <div style={{display:'flex',gap:'10px',justifyContent:'flex-end',marginTop:'18px'}}>
          <button style={css.btn()} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={css.btn('primary')} onClick={confirm} disabled={busy}>{busy ? 'Saving…' : 'Leave out of pay'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── BULK EDIT (My Logs) ──────────────────────────────────────────────────────
// Tick several entries, then change what they have in common in one go. The dialog
// starts with a single "Choose a field to change…" drop-down; picking a field shows just
// that field's control, and "Add another field" adds a row so one update can still
// change several things. Nothing is written for a field that isn't chosen. The rules
// (what may be selected, what may not land in a closed month, how tag changes are
// planned) are in src/bulkEntries.js and pinned by scripts/test-bulk-entries.mjs. The
// scalar fields go in one request per batch of ids, so a batch either changes or it
// doesn't; tags are separate rows, so they are a second step.
const BULK_BATCH = 60; // ids per request: keeps the URL short
const BULK_NONE = '__none__'; // "set it to nothing", as opposed to "not chosen yet" ('')
const BULK_FIELDS = [['client', 'Client'], ['task', 'Task type'], ['desc', 'Description'], ['billable', 'Billing'], ['date', 'Date'], ['tags', 'Tags']];
const BULK_START = { client: '', task: '', desc: '', descClear: false, billable: '', date: '', tagMode: 'add', tagIds: [] };
// Which values belong to which field, so dropping or changing a row can reset exactly its own.
const BULK_KEYS = { client: ['client'], task: ['task'], desc: ['desc', 'descClear'], billable: ['billable'], date: ['date'], tags: ['tagMode', 'tagIds'] };
const bulkReset = (vals, field) => ({ ...vals, ...Object.fromEntries((BULK_KEYS[field] || []).map(k => [k, BULK_START[k]])) });

// One change: the drop-down says WHAT to change, the control beside it says what to change it
// TO. Module level on purpose: a component defined inside BulkEditModal would be a new type
// every render, remount its children, and drop focus from an input mid-keystroke.
function BulkRow({ field, options, onField, onRemove, children }) {
  return (
    <div style={{display:'grid',gridTemplateColumns:'150px 1fr 24px',gap:'10px',alignItems:'start',padding:'10px 0',borderTop:`1px solid ${C.border}`}}>
      <select value={field} onChange={e=>onField(e.target.value)} aria-label="What to change"
        style={{...css.sel,padding:'6px 8px',fontSize:'13px',width:'100%'}}>
        <option value="">Choose a field…</option>
        {options.map(o => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
      </select>
      <div style={{minWidth:0}}>
        {field ? children : <div style={{fontSize:'12px',color:C.muted,padding:'7px 2px'}}>Pick a field to change.</div>}
      </div>
      <button type="button" onClick={onRemove} aria-label="Remove this change" title="Remove this change"
        style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:'18px',lineHeight:1,padding:'4px 0'}}>×</button>
    </div>
  );
}

function BulkEditModal({ entries, clients, taskTypes, tags, orgId, userId, monthLocks, canEditTags, isAssigned, onClose, onDone }) {
  const [rows, setRows] = useState([{ key: 1, field: '' }]);
  const [vals, setVals] = useState(BULK_START);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const nextKey = useRef(2);
  const setVal = (k, v) => setVals(p => ({ ...p, [k]: v }));

  const count = entries.length;
  const hours = entries.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
  const clientName = (id) => clients.find(c => c.id === id)?.name || 'that client';
  const taskName = (id) => taskTypes.find(t => t.id === id)?.name || 'that task type';

  // Remove mode only makes sense for tags the selection actually has.
  const tagsOnSelection = useMemo(() => {
    const seen = new Map();
    entries.forEach(e => (e.time_entry_tags || []).forEach(l => { if (l.tags && !seen.has(l.tags.id)) seen.set(l.tags.id, l.tags); }));
    return [...seen.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [entries]);
  const tagNames = (ids) => ids.map(id => id.startsWith('_new_:') ? id.slice(6) : ((tags || []).find(t => t.id === id)?.name || '?')).join(', ');

  const used = new Set(rows.map(r => r.field).filter(Boolean));
  const optionsFor = (row) => BULK_FIELDS
    .filter(([f]) => f === row.field || !used.has(f))
    .map(([f, label]) => (f === 'tags' && !canEditTags)
      ? { value: f, label: `${label} (not available here)`, disabled: true }
      : { value: f, label });
  const setRowField = (key, field) => {
    const prev = rows.find(r => r.key === key)?.field;
    setRows(rs => rs.map(r => r.key === key ? { ...r, field } : r));
    // Starting a field fresh, so a value left from a row that was changed away from can't come back.
    if (prev) setVals(p => bulkReset(p, prev));
    setErr('');
  };
  const removeRow = (key) => {
    const gone = rows.find(r => r.key === key)?.field;
    setRows(rs => { const next = rs.filter(r => r.key !== key); return next.length ? next : [{ key: nextKey.current++, field: '' }]; });
    if (gone) setVals(p => bulkReset(p, gone));
    setErr('');
  };
  const canAddRow = rows.every(r => r.field) && used.size < BULK_FIELDS.length - (canEditTags ? 0 : 1);

  // What each chosen field would write, and what is still missing.
  const missing = [];
  const clientChosen = used.has('client') && vals.client !== '';
  const taskChosen = used.has('task') && vals.task !== '';
  const descChosen = used.has('desc') && (vals.descClear || vals.desc.trim() !== '');
  const billChosen = used.has('billable') && vals.billable !== '';
  const dateChosen = used.has('date') && vals.date !== '';
  if (used.has('client') && !clientChosen) missing.push('Client');
  if (used.has('task') && !taskChosen) missing.push('Task type');
  if (used.has('desc') && !descChosen) missing.push('Description');
  if (used.has('billable') && !billChosen) missing.push('Billing');
  if (used.has('date') && !dateChosen) missing.push('Date');
  const tagsChosen = canEditTags && used.has('tags') && (vals.tagMode === 'replace' || vals.tagIds.length > 0);
  if (canEditTags && used.has('tags') && !tagsChosen) missing.push('Tags');

  const patch = buildPatch({
    client:   { on: clientChosen, value: vals.client === BULK_NONE ? '' : vals.client },
    task:     { on: taskChosen,   value: vals.task === BULK_NONE ? '' : vals.task },
    desc:     { on: descChosen,   value: vals.descClear ? '' : vals.desc },
    billable: { on: billChosen,   value: vals.billable === 'yes' },
    date:     { on: dateChosen,   value: vals.date },
  });
  const nothing = Object.keys(patch).length === 0 && !tagsChosen;
  const ready = !nothing && missing.length === 0;

  // A plain-words list of exactly what Update will do.
  const summary = [
    'client_id' in patch && `Client: ${patch.client_id ? clientName(patch.client_id) : 'none'}`,
    'task_type_id' in patch && `Task type: ${patch.task_type_id ? taskName(patch.task_type_id) : 'none'}`,
    'description' in patch && (patch.description ? `Description: “${patch.description}”` : 'Description: cleared'),
    'is_billable' in patch && `Billing: ${patch.is_billable ? 'billable to the client' : 'not billable to the client'}`,
    'entry_date' in patch && `Date: ${patch.entry_date}`,
    tagsChosen && (vals.tagMode === 'add' ? `Tags: add ${tagNames(vals.tagIds)}` : vals.tagMode === 'remove' ? `Tags: remove ${tagNames(vals.tagIds)}` : vals.tagIds.length ? `Tags: set to ${tagNames(vals.tagIds)}` : 'Tags: remove all'),
  ].filter(Boolean);

  const apply = async () => {
    setErr('');
    if (!ready) { setErr(missing.length ? `Finish the change you started: ${missing.join(', ')}.` : 'Choose something to change.'); return; }
    // A closed month is closed: refuse, before anything is sent, a change that would put
    // entries into one (a different client or date whose month is already submitted).
    const landing = entriesLandingInClosedMonth(entries, patch, monthLocks, userId);
    if (landing.length) {
      const names = [...new Set(landing.map(l => clientName(l.clientId)))].join(', ');
      setErr(`${landing.length} of these entries would land in a month that is already closed for ${names}. Reopen that month first, or choose a different ${'client_id' in patch ? 'client' : 'date'}.`);
      return;
    }
    setSaving(true);
    const ids = entries.map(e => e.id);
    let scalarsDone = false;
    try {
      if (Object.keys(patch).length) {
        for (const part of chunk(ids, BULK_BATCH)) {
          // select() returns the rows that really changed, so a row the database refused to
          // touch is reported instead of silently assumed done.
          const { data, error } = await supabase.from('time_entries').update(patch).in('id', part).select('id');
          if (error) throw new Error(`${scalarsDone ? 'Some entries were changed before this stopped. ' : ''}${friendlyDbError(error)}`);
          if ((data || []).length !== part.length) throw new Error(`${(data || []).length} of ${part.length} entries could be changed; the rest may be locked.${scalarsDone ? ' Earlier ones were already changed.' : ''}`);
          scalarsDone = true;
        }
      }
      if (tagsChosen) {
        const wanted = vals.tagIds.filter(id => vals.tagMode === 'remove' ? !id.startsWith('_new_:') : true);
        const real = vals.tagMode === 'remove' ? wanted : await resolveTags(orgId, wanted);
        const plan = planTagChanges(entries, vals.tagMode, real);
        for (const r of plan.removals) {
          for (const part of chunk(r.entryIds, BULK_BATCH)) {
            const { error } = await supabase.from('time_entry_tags').delete().eq('tag_id', r.tagId).in('time_entry_id', part);
            if (error) throw new Error(`${scalarsDone ? 'The other changes were saved, but the' : 'The'} tags could not be changed: ${friendlyDbError(error)}`);
          }
        }
        for (const part of chunk(plan.inserts, 200)) {
          const { error } = await supabase.from('time_entry_tags').insert(part);
          if (error) throw new Error(`${scalarsDone ? 'The other changes were saved, but the' : 'The'} tags could not be changed: ${friendlyDbError(error)}`);
        }
      }
    } catch (e) {
      setSaving(false);
      setErr(e.message || 'Something went wrong. Nothing more was changed.');
      return;
    }
    setSaving(false);
    onDone(count);
  };

  const small = {...css.inp,padding:'6px 8px',fontSize:'13px'};
  const control = (field) => {
    if (field === 'client') return (
      <>
        <SearchableSelect options={[{value:BULK_NONE,label:'No client (clear it)'}, ...clients.map(c=>({value:c.id,label:c.name}))]} value={vals.client}
          onChange={v=>setVal('client', v)} allLabel="Choose a client…" placeholder="Search clients…" style={{width:'100%'}} />
        {vals.client && vals.client !== BULK_NONE && !isAssigned(vals.client) && (
          <div style={{fontSize:'11px',color:C.badgeAmber,marginTop:'6px',lineHeight:1.4}}>You aren't assigned to {clientName(vals.client)}, so this time won't count toward your pay there.</div>
        )}
      </>
    );
    if (field === 'task') return (
      <SearchableSelect options={[{value:BULK_NONE,label:'No task type (clear it)'}, ...taskTypes.map(t=>({value:t.id,label:t.name}))]} value={vals.task}
        onChange={v=>setVal('task', v)} allLabel="Choose a task type…" placeholder="Search task types…" style={{width:'100%'}} />
    );
    if (field === 'desc') return (
      <>
        <input style={{...small,opacity:vals.descClear?0.5:1}} placeholder="New description for every selected entry" value={vals.desc} disabled={vals.descClear}
          onChange={e=>setVal('desc', e.target.value)} aria-label="New description" />
        <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'12px',color:C.muted,marginTop:'6px',cursor:'pointer'}}>
          <input type="checkbox" checked={vals.descClear} onChange={e=>setVal('descClear', e.target.checked)} /> Clear the description instead
        </label>
      </>
    );
    if (field === 'billable') return (
      <select style={small} value={vals.billable} onChange={e=>setVal('billable', e.target.value)} aria-label="Billing">
        <option value="">Choose…</option>
        <option value="yes">Billable to client</option>
        <option value="no">Not billable to client</option>
      </select>
    );
    if (field === 'date') return <input type="date" style={{...small,maxWidth:'180px'}} value={vals.date} onChange={e=>setVal('date', e.target.value)} aria-label="New date" />;
    if (field === 'tags') return (
      <>
        <select style={{...small,marginBottom:'6px'}} value={vals.tagMode} onChange={e=>setVals(p=>({ ...p, tagMode: e.target.value, tagIds: [] }))} aria-label="How to change tags">
          <option value="add">Add these tags (keep the others)</option>
          <option value="remove">Remove these tags</option>
          <option value="replace">Replace all tags with these</option>
        </select>
        <TagPicker availableTags={vals.tagMode === 'remove' ? tagsOnSelection : (tags || [])} selectedTagIds={vals.tagIds} onSelect={ids=>setVal('tagIds', ids)} />
        {vals.tagMode === 'replace' && vals.tagIds.length === 0 && <div style={{fontSize:'11px',color:C.badgeAmber,marginTop:'6px'}}>With none chosen, every tag comes off these entries.</div>}
        {vals.tagMode === 'remove' && tagsOnSelection.length === 0 && <div style={{fontSize:'11px',color:C.muted,marginTop:'6px'}}>None of these entries has a tag.</div>}
      </>
    );
    return null;
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'20px'}}
      onClick={e=>{ if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div role="dialog" aria-label={`Edit ${count} entries`}
        style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'12px',padding:'22px 24px',width:'100%',maxWidth:'560px',maxHeight:'calc(100vh - 40px)',overflowY:'auto'}}>
        <div style={{fontSize:'16px',fontWeight:'700',color:C.textBright}}>Edit {count} {count === 1 ? 'entry' : 'entries'}</div>
        <div style={{fontSize:'12px',color:C.muted,margin:'4px 0 10px'}}>{formatExactTime(hours)} in total. Pick a field to change; everything else stays as it is.</div>

        {rows.map(r => (
          <BulkRow key={r.key} field={r.field} options={optionsFor(r)} onField={f=>setRowField(r.key, f)} onRemove={()=>removeRow(r.key)}>
            {control(r.field)}
          </BulkRow>
        ))}
        <div style={{borderTop:`1px solid ${C.border}`,paddingTop:'8px'}}>
          <button type="button" onClick={()=>setRows(rs=>[...rs, { key: nextKey.current++, field: '' }])} disabled={!canAddRow}
            style={{background:'none',border:'none',padding:'4px 0',cursor:canAddRow?'pointer':'default',color:canAddRow?C.accent:C.muted,fontSize:'12px',fontFamily:'inherit',opacity:canAddRow?1:0.6}}>+ Add another field</button>
        </div>

        <div style={{borderTop:`1px solid ${C.border}`,paddingTop:'12px',marginTop:'6px'}}>
          {summary.length === 0
            ? <div style={{fontSize:'12px',color:C.muted}}>Nothing will change yet.</div>
            : <div style={{fontSize:'12px',color:C.text,lineHeight:1.6}}>
                <div style={{color:C.muted,marginBottom:'2px'}}>This will change {count} {count === 1 ? 'entry' : 'entries'}:</div>
                {summary.map((s, i) => <div key={i}>• {s}</div>)}
              </div>}
          {missing.length > 0 && <div style={{fontSize:'11px',color:C.badgeAmber,marginTop:'6px'}}>Still to fill in: {missing.join(', ')}.</div>}
        </div>

        {err && <div role="alert" style={{fontSize:'12px',color:C.danger,padding:'8px 10px',background:'rgba(229,62,62,0.08)',borderRadius:'6px',marginTop:'12px',lineHeight:1.45}}>{err}</div>}
        <div style={{display:'flex',gap:'10px',marginTop:'16px',justifyContent:'flex-end'}}>
          <button style={css.btn()} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{...css.btn('primary'),opacity:(saving||!ready)?0.5:1,cursor:(saving||!ready)?'default':'pointer'}} onClick={apply} disabled={saving || !ready}>{saving ? 'Saving…' : `Update ${count} ${count === 1 ? 'entry' : 'entries'}`}</button>
        </div>
      </div>
    </div>
  );
}

function BulkDeleteModal({ entries, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const count = entries.length;
  const hours = entries.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
  const run = async () => {
    setBusy(true); setErr('');
    let deleted = 0;
    for (const part of chunk(entries.map(e => e.id), BULK_BATCH)) {
      // select() returns the rows that really went, so a row the database refused to
      // delete is counted as not deleted instead of silently assumed gone.
      const { data, error } = await supabase.from('time_entries').delete().in('id', part).select('id');
      if (error) { setBusy(false); setErr(`${deleted ? `${deleted} deleted before this stopped. ` : ''}${friendlyDbError(error)}`); if (deleted) onDone(deleted, true); return; }
      deleted += (data || []).length;
    }
    setBusy(false);
    if (deleted < count) { setErr(`Only ${deleted} of ${count} could be deleted.`); onDone(deleted, true); return; }
    onDone(deleted, false);
  };
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'20px'}}
      onClick={e=>{ if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div role="alertdialog" aria-label={`Delete ${count} entries`} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'12px',padding:'24px',width:'100%',maxWidth:'420px'}}>
        <div style={{fontSize:'16px',fontWeight:'700',color:C.textBright,marginBottom:'8px'}}>Delete {count} {count === 1 ? 'entry' : 'entries'}?</div>
        <div style={{fontSize:'13px',color:C.text,lineHeight:1.55}}>
          This removes {formatExactTime(hours)} of logged time. It can't be undone.
        </div>
        {err && <div role="alert" style={{fontSize:'12px',color:C.danger,padding:'8px 10px',background:'rgba(229,62,62,0.08)',borderRadius:'6px',marginTop:'12px'}}>{err}</div>}
        <div style={{display:'flex',gap:'10px',marginTop:'18px',justifyContent:'flex-end'}}>
          <button style={css.btn()} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={css.btn('danger')} onClick={run} disabled={busy}>{busy ? 'Deleting…' : `Delete ${count} ${count === 1 ? 'entry' : 'entries'}`}</button>
        </div>
      </div>
    </div>
  );
}

function LogsView({ profile, orgMember, actingAdmin = false, org, entries, clients, assignments, monthLocks, taskTypes, tags, statements, onRefresh, onNavigate, onSubmittableReset }) {
  const tour = useTour('logs');
  // Default to most recent month that has entries; fall back to current month
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const cur = curMonth();
    if (entries.some(e => e.user_id === profile.id && e.entry_date?.slice(0,7) === cur)) return cur;
    const months = [...new Set(entries.filter(e => e.user_id === profile.id).map(e => e.entry_date?.slice(0,7)).filter(Boolean))].sort().reverse();
    return months[0] || cur;
  });
  const [rangeMode, setRangeMode] = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');
  const allMonths = useMemo(() => { const months=[],d=new Date(); for(let i=0;i<24;i++){months.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);d.setMonth(d.getMonth()-1);} return months; },[]);

  const fiscalStart = org?.fiscal_year_start || 1;
  const now = new Date();
  const ytdStart = (() => {
    const yr = now.getMonth() + 1 >= fiscalStart ? now.getFullYear() : now.getFullYear() - 1;
    return `${yr}-${String(fiscalStart).padStart(2,'0')}-01`;
  })();
  const ytdEnd = now.toISOString().slice(0,10);

  const mine = (() => {
    const byUser = entries.filter(e => e.user_id === profile.id);
    if (rangeMode === 'month')  return byUser.filter(e => e.entry_date?.slice(0,7) === selectedMonth);
    if (rangeMode === 'ytd')    return byUser.filter(e => e.entry_date >= ytdStart && e.entry_date <= ytdEnd);
    if (rangeMode === 'custom' && customStart && customEnd)
      return byUser.filter(e => e.entry_date >= customStart && e.entry_date <= customEnd);
    return byUser.filter(e => e.entry_date?.slice(0,7) === selectedMonth);
  })();

  const rangeLabel = (() => {
    if (rangeMode === 'month')  return new Date(selectedMonth+'-02').toLocaleString('default',{month:'long',year:'numeric'});
    if (rangeMode === 'ytd')    return `YTD (since ${new Date(ytdStart+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})})`;
    if (rangeMode === 'custom' && customStart && customEnd)
      return `${new Date(customStart+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})} – ${new Date(customEnd+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}`;
    return 'Custom Range';
  })();
  const totalHrs = mine.reduce((s,e)=>s+parseFloat(e.hours||0),0);
  // Internal clients are excluded from billable hours and earnings — they are
  // internal overhead, not client-billable work.
  const internalClientIds = new Set(clients.filter(c=>c.is_internal).map(c=>c.id));
  const billableHrs = mine.filter(e=>e.is_billable!==false && !internalClientIds.has(e.client_id)).reduce((s,e)=>s+parseFloat(e.hours||0),0);
  const nonBillableHrs = mine.filter(e=>e.is_billable===false).reduce((s,e)=>s+parseFloat(e.hours||0),0);
  const unpaidHrs = mine.filter(e=>e.is_payable===false).reduce((s,e)=>s+parseFloat(e.hours||0),0);
  // Earnings via the shared module (all six pay types). Include clients with
  // entries this month, plus assignments that pay a monthly amount even with no
  // time logged (flat_monthly, this-month fixed_project, hybrid, and percent on
  // a retainer/hybrid client). Internal clients excluded.
  const myAssignments = assignments.filter(a => a.user_id===profile.id);
  const paysWithoutHours = (a) => a.pay_type==='flat_monthly' || a.pay_type==='hybrid' || a.pay_type==='percent' || (a.pay_type==='fixed_project' && a.project_bill_month===selectedMonth);
  const earnClientIds = [...new Set([
    ...mine.filter(e=>e.client_id && !internalClientIds.has(e.client_id)).map(e=>e.client_id),
    ...myAssignments.filter(a => !internalClientIds.has(a.client_id) && paysWithoutHours(a)).map(a=>a.client_id),
  ])];
  const earnByClient = {};
  earnClientIds.forEach(cid => {
    const a = myAssignments.find(a=>a.client_id===cid);
    if (a) earnByClient[cid] = computeEarnings(a, mine.filter(e=>e.client_id===cid), selectedMonth, clients.find(c=>c.id===cid));
  });
  const estimatedPay = Object.values(earnByClient).reduce((s,x)=>s+(x.lineTotal||0),0);
  // "Your pay" (glossary): your approved member invoice for the month when there
  // is one (its pay lines, not expense reimbursements), otherwise the estimate.
  const approvedStmt = rangeMode === 'month'
    ? (statements || []).find(s => s.user_id === profile.id && s.period === selectedMonth && s.status === 'approved')
    : null;
  const earned = approvedStmt
    ? (Array.isArray(approvedStmt.line_items) ? approvedStmt.line_items : [])
        .filter(li => li.basis !== 'reimbursement' && !li.expense_id)
        .reduce((s, li) => s + parseFloat(li.line_total || 0), 0)
    : estimatedPay;
  const monthLabel = rangeLabel;

  // actingAdmin: an admin is viewing AS this member (orgMember is null then, so
  // the member's own close rules can't apply). Closing and reopening a member's
  // month on their behalf is allowed by the database for admins; what changes
  // here is that nothing is submitted for the member (see confirmClose).
  const memberName = profile?.name || 'this member';
  const eligibleForClose = (!!orgMember || actingAdmin) && rangeMode === 'month';
  const isAdminUser = orgMember && ['admin','owner'].includes(orgMember.role);
  const canReopen = !!isAdminUser || actingAdmin;

  // Derive per-client lock state from Dashboard-level prop
  // A lock counts as closed only when not reopened. month_locks is the single
  // source of truth for lock state; the time_entries.locked flag is no longer read.
  const clientLocks = Object.fromEntries(
    (monthLocks || []).filter(l => l.user_id === profile.id && l.period === selectedMonth && !l.reopened_at).map(l => [l.client_id, l])
  );
  const [showModal, setShowModal]   = useState(null); // null | client_id | '__all__'
  const [locking, setLocking]       = useState(false);
  const [lockMsg, setLockMsg]       = useState('');
  const [submitWarning, setSubmitWarning] = useState(''); // auto-submit-statement failed silently otherwise
  const [editEntryId, setEditEntryId] = useState(null);
  const [editToast, setEditToast]   = useState('');
  const [payModal, setPayModal]     = useState(null); // entry being left out of pay
  // Bulk edit: the entries ticked in the tables below, and which dialog (if any) is open.
  const [picked, setPicked]         = useState(() => new Set());
  const [bulkModal, setBulkModal]   = useState(null); // null | 'edit' | 'delete'
  const lastPicked = useRef(null);

  const handleEditSave = () => {
    setEditEntryId(null);
    setEditToast('Entry updated.');
    setTimeout(()=>setEditToast(''), 3000);
    onRefresh();
  };

  // What leaving one entry out of pay does to this member's pay for its month,
  // computed with the shared pay math (0 for flat/fixed pay, null with no assignment).
  const payEffectFor = (ent) => {
    const a = myAssignments.find(x => x.client_id === ent.client_id);
    if (!a) return null;
    const mo = ent.entry_date?.slice(0,7);
    const ces = mine.filter(e => e.client_id === ent.client_id && e.entry_date?.slice(0,7) === mo);
    const cl = clients.find(c => c.id === ent.client_id);
    const before = computeEarnings(a, ces, mo, cl).lineTotal;
    const after = computeEarnings(a, ces.map(e => e.id === ent.id ? { ...e, is_payable: false } : e), mo, cl).lineTotal;
    return Math.max(0, before - after);
  };
  // Entry ⋯ menu. Pay is only offered where the member has pay for that client.
  const rowActions = (ent) => [
    { label:'Edit', onClick:()=>setEditEntryId(ent.id) },
    ...(myAssignments.some(a => a.client_id === ent.client_id) ? [
      ent.is_payable === false
        ? { label:'Pay for this time again', onClick: async () => { if (await setEntryPayable(ent.id, true)) onRefresh(); } }
        : { label:'Don’t pay for this time…', onClick: () => setPayModal(ent) },
    ] : []),
    { label:'Delete', danger:true, onClick:()=>{ if(window.confirm('Delete this time entry?')) del(ent.id); } },
  ];

  const del = async (id) => {
    await runDb('Delete entry failed', supabase.from('time_entries').delete().eq('id',id));
    onRefresh();
  };

  const reopenClient = async (cid) => {
    const lock = clientLocks[cid];
    if (!lock) return;
    // Warn if a submitted or approved invoice exists for this period — reopening will void it.
    const activeStmt = (statements || []).find(s =>
      s.user_id === profile.id && s.period === selectedMonth &&
      ['submitted','approved'].includes(s.status)
    );
    if (activeStmt) {
      const stLabel = activeStmt.status === 'approved' ? 'approved' : 'submitted';
      const confirmed = window.confirm(
        actingAdmin
          ? `⚠️ ${memberName} has a ${stLabel} invoice for ${monthLabel}.\n\nReopening this client will VOID that invoice and it will need to be resubmitted for admin approval.\n\nAre you sure you want to reopen?`
          : `⚠️ You have a ${stLabel} invoice for ${monthLabel}.\n\nReopening this client will VOID that invoice and it will need to be resubmitted for admin approval.\n\nAre you sure you want to reopen?`
      );
      if (!confirmed) return;
    }
    const okReopen = await runDb('Reopen failed', supabase.rpc('reopen_client_month',
      { p_org: org.id, p_user: profile.id, p_client: cid, p_period: selectedMonth }));
    if (!okReopen) return;
    // A submitted or approved statement for this period is now invalid: void it.
    await supabase.from('contractor_statements').update({ status:'void' })
      .eq('org_id', org.id).eq('user_id', profile.id).eq('period', selectedMonth).in('status', ['submitted','approved','confirmed']);
    setLockMsg(`${clients.find(c=>c.id===cid)?.name||'Client'} — ${monthLabel} reopened.`);
    setTimeout(() => setLockMsg(''), 4000);
    onRefresh();
  };

  // Group entries by client — for contractors, also include assigned clients with no entries
  // so they can confirm $0 and understand the full close-month picture.
  const entryClientIds = [...new Set(mine.filter(e => e.client_id).map(e => e.client_id))];
  const assignedClientIds = !isAdminUser
    ? [...new Set(assignments.filter(a=>a.user_id===profile.id).map(a=>a.client_id))]
    : [];
  const activeClientIds = [...new Set([...entryClientIds, ...assignedClientIds])];
  const unassignedEntries = mine.filter(e => !e.client_id);
  const openClientIds = activeClientIds.filter(cid => !clientLocks[cid]);

  // Client search — narrows which client cards render below, "contains"
  // match against name or alias. Deliberately doesn't touch activeClientIds
  // itself: lock counts, Close All eligibility, and the earnings summary
  // all stay based on the real full set regardless of what's currently
  // filtered into view.
  const [clientSearch, setClientSearch] = useState('');
  const [logsStatus, setLogsStatus] = useState('all'); // client groups: open | closed | all
  const [groupOpen, setGroupOpen] = useState({}); // client id -> explicitly opened (true) or collapsed (false)
  const searching = clientSearch.trim().length > 0;
  // Closing is per month, so the Open/Closed pills only apply in Month mode.
  const statusClientIds = activeClientIds.filter(cid => logsStatus === 'all' || rangeMode !== 'month' || (logsStatus === 'closed' ? !!clientLocks[cid] : !clientLocks[cid]));
  const visibleClientIds = searching
    ? statusClientIds.filter(cid => {
        const c = clients.find(x => x.id === cid);
        if (!c) return false;
        const q = clientSearch.trim().toLowerCase();
        return c.name?.toLowerCase().includes(q) || c.alias?.toLowerCase().includes(q);
      })
    : statusClientIds;

  // ── Selecting entries for a bulk change ─────────────────────────────────────
  // Only what is on screen right now can be ticked (a collapsed client's rows are hidden,
  // so they are never changed by accident), and never an entry in a closed month.
  const isCollapsed = (cid) => groupOpen[cid] === undefined ? !!clientLocks[cid] : !groupOpen[cid];
  const shownEntries = [
    ...visibleClientIds.filter(cid => !isCollapsed(cid)).flatMap(cid => mine.filter(e => e.client_id === cid)),
    ...(!searching ? unassignedEntries : []),
  ];
  const tickable = selectableEntries(shownEntries, monthLocks, profile.id);
  const tickableIds = new Set(tickable.map(e => e.id));
  const pickedEntries = tickable.filter(e => picked.has(e.id));
  const pickedHours = pickedEntries.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
  // Drop anything that is no longer tickable (a refresh, a filter, a month that just closed).
  const tickableKey = tickable.map(e => e.id).join(',');
  useEffect(() => {
    setPicked(prev => { const next = new Set([...prev].filter(id => tickableIds.has(id))); return next.size === prev.size ? prev : next; });
    if (lastPicked.current && !tickableIds.has(lastPicked.current)) lastPicked.current = null;
  }, [tickableKey]); // eslint-disable-line
  const togglePick = (id, shift) => {
    const next = new Set(picked);
    const turnOn = !picked.has(id);
    const last = lastPicked.current;
    if (shift && last && tickableIds.has(last)) {
      // Shift-click ticks (or unticks) everything between this row and the last one clicked.
      const order = tickable.map(e => e.id);
      const a = order.indexOf(last), b = order.indexOf(id);
      if (a >= 0 && b >= 0) order.slice(Math.min(a, b), Math.max(a, b) + 1).forEach(x => turnOn ? next.add(x) : next.delete(x));
      else turnOn ? next.add(id) : next.delete(id);
    } else turnOn ? next.add(id) : next.delete(id);
    setPicked(next);
    lastPicked.current = id;
  };
  const togglePickGroup = (list) => {
    const ids = list.filter(e => tickableIds.has(e.id)).map(e => e.id);
    if (!ids.length) return;
    const all = ids.every(id => picked.has(id));
    const next = new Set(picked);
    ids.forEach(id => all ? next.delete(id) : next.add(id));
    setPicked(next);
  };
  const bulkDone = (message) => {
    setPicked(new Set());
    setEditToast(message);
    setTimeout(() => setEditToast(''), 3500);
    onRefresh();
  };
  const bulkHeadBox = (list) => {
    const mineTickable = list.filter(e => tickableIds.has(e.id));
    if (!mineTickable.length) return <th style={{...css.th,width:'28px'}} />;
    const all = mineTickable.every(e => picked.has(e.id));
    const some = mineTickable.some(e => picked.has(e.id));
    return (
      <th style={{...css.th,width:'28px',paddingRight:0}}>
        <input type="checkbox" data-tour="logs-select" checked={all} ref={el => { if (el) el.indeterminate = some && !all; }}
          onChange={() => togglePickGroup(list)} aria-label="Select all entries in this table" title="Select all entries in this table" />
      </th>
    );
  };
  const bulkRowBox = (ent) => tickableIds.has(ent.id)
    ? <td style={{...css.td,width:'28px',paddingRight:0}} onClick={e => e.stopPropagation()}>
        <input type="checkbox" checked={picked.has(ent.id)} onChange={e => togglePick(ent.id, e.nativeEvent.shiftKey)}
          aria-label={`Select entry from ${ent.entry_date}${ent.description ? `: ${ent.description}` : ''}`} title="Select. Hold Shift to select a range." />
      </td>
    : <td style={{...css.td,width:'28px',paddingRight:0}} />;
  const pickedRowStyle = (ent) => picked.has(ent.id) ? { background:'rgba(49,167,217,0.08)' } : null;

  const downloadCSVForClose = (entriesToExport, filename, includeTeamMember) => {
    const header = includeTeamMember
      ? ["Date", "Team Member", "Client", "Task Type", "Description", "Hours"]
      : ["Date", "Client", "Task Type", "Description", "Hours"];
    const rows = entriesToExport.map(e => {
      const clientName = clients.find(c => c.id === e.client_id)?.name || '';
      const taskName = e.task_types?.name || '';
      if (includeTeamMember) {
        return [e.entry_date, e.profiles?.name || '', clientName, taskName, e.description || '', parseFloat(e.hours || 0).toFixed(2)];
      }
      return [e.entry_date, clientName, taskName, e.description || '', parseFloat(e.hours || 0).toFixed(2)];
    });
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const confirmClose = async (clientIds) => {
    setLocking(true);
    setSubmitWarning('');
    for (const clientId of clientIds) {
      // Close via SECURITY DEFINER RPC (upsert needs the UPDATE privilege that
      // authenticated lacks on month_locks). reopened_at goes back to NULL so a
      // previously reopened period re-closes. The month_locks row IS the lock;
      // the check_time_entry_not_locked trigger blocks new entries server-side.
      const { error: lockErr } = await supabase.rpc('close_client_month',
        { p_org: org.id, p_user: profile.id, p_client: clientId, p_period: selectedMonth });
      if (lockErr) {
        setLockMsg(`Submission error: ${lockErr.message}`);
        setLocking(false);
        setShowModal(null);
        return;
      }
    }
    const mName = new Date(selectedMonth+'-02').toLocaleString("default",{month:"long"});
    const mYear = selectedMonth.split('-')[0];
    // The CSV is the closer's own copy of what they just submitted. An admin
    // closing on a member's behalf submitted nothing of their own, so no download.
    if (!actingAdmin) {
      if (clientIds.length === 1) {
        const cName = (clients.find(c=>c.id===clientIds[0])?.name||'Client').replace(/\s+/g,'_');
        downloadCSVForClose(mine.filter(e=>e.client_id===clientIds[0]), `TimeKeep_${cName}_${mName}_${mYear}.csv`, isAdminUser);
      } else {
        downloadCSVForClose(mine.filter(e=>clientIds.includes(e.client_id)), `TimeKeep_All_Clients_${mName}_${mYear}.csv`, isAdminUser);
      }
    }
    const closedNames = clientIds.map(cid => clients.find(c=>c.id===cid)?.name||cid).join(', ');
    setLockMsg(actingAdmin
      ? `${closedNames} — ${monthLabel} closed on behalf of ${memberName}. No invoice was submitted for them.`
      : `${closedNames} — ${monthLabel} timesheet submitted.`);
    setLocking(false);
    setShowModal(null);
    supabase.functions.invoke('check-all-locked', { body: { org_id: org.id, period: selectedMonth } }).catch(console.error);

    // When the org requires ALL of a contractor's clients closed before they
    // can submit, that's a single unambiguous trigger moment -- the last one
    // locking IS "I'm done for this period" -- so generate and submit their
    // statement right here instead of making them find a separate button.
    // (When the toggle is off, closing one client is a legitimate partial
    // state, not a completion signal, so this stays manual -- see the
    // app-wide "ready to submit" bar in the Dashboard shell instead.)
    // An admin closing on a member's behalf never auto-submits: the invoice is
    // the member's own statement to review and send (Austin, 2026-10-05).
    const nowFullyClosed = !actingAdmin && !isAdminUser && openClientIds.length > 0 && openClientIds.every(cid => clientIds.includes(cid));
    if (nowFullyClosed && org?.invoice_require_all_clients_closed !== false) {
      const alreadyHasStatement = (statements||[]).some(s => s.user_id===profile.id && s.period===selectedMonth && s.status !== 'void');
      if (!alreadyHasStatement) {
        supabase.functions.invoke('auto-submit-statement', { body: { org_id: org.id, user_id: profile.id, period: selectedMonth } })
          .then(({ data, error }) => {
            // Silent failure here means the contractor sees "timesheet submitted"
            // and has no way to know their invoice was never actually generated
            // — so any non-success path has to say so out loud, not just log it.
            if (error || !data?.ok) {
              console.warn('auto-submit-statement:', error?.message || data?.error);
              setSubmitWarning(`We couldn't auto-generate your ${monthLabel} invoice. Please submit it manually from Member Invoices.`);
              return;
            }
            if (data.submitted) {
              // expenses_left_off: approved expenses that could not be attached to the invoice, so the
              // function took them off it (an expense on an invoice but not attached could be paid twice).
              // They stay approved and unclaimed; recalling and submitting again picks them up.
              const left = Number(data.expenses_left_off) || 0;
              setLockMsg(`${closedNames} — ${monthLabel} timesheet submitted, and your invoice for ${monthLabel} was generated and sent for approval automatically.`
                + (left ? ` ${left} approved expense${left === 1 ? '' : 's'} could not be added to it and ${left === 1 ? 'was' : 'were'} left off. Recall the invoice from Member Invoices and submit it again to include ${left === 1 ? 'it' : 'them'}.` : ''));
              // The success message above is already in state, but `statements`
              // itself is stale until this refetches — without it, the app-wide
              // "ready to submit" banner keeps nudging for a period that was
              // just auto-submitted, since its own hasStmt check reads this
              // same stale list.
              onRefresh();
            }
            // data.submitted === false with no error is a deliberate skip (e.g.
            // identity not set up yet, or another admin/process already handled
            // it) — the manual Submit flow on Member Invoices remains available,
            // so no warning needed for that case.
          });
      }
    }

    onRefresh();
    onSubmittableReset?.(); // reset the app-wide nudge's dismiss state so it re-shows if newly eligible
  };

  const modalClientIds = showModal === '__all__' ? openClientIds : showModal ? [showModal] : [];
  const modalHrs = modalClientIds.reduce((s,cid) => s + mine.filter(e=>e.client_id===cid).reduce((ss,e)=>ss+parseFloat(e.hours||0),0), 0);
  const modalEntryCount = modalClientIds.reduce((s,cid) => s + mine.filter(e=>e.client_id===cid).length, 0);

  // Summary tile values (Your pay: approved invoice when there is one, else estimated).
  const logsAllLocked = activeClientIds.length > 0 && activeClientIds.every(cid => clientLocks[cid]);
  const logsOpenCount = activeClientIds.filter(cid => !clientLocks[cid]).length;
  const logsHasAssignments = myAssignments.length > 0;
  // One primary action: close the month for every open client (or the only one).
  const closeMonthAction = eligibleForClose && rangeMode==='month' && openClientIds.length > 0
    ? <button style={css.btn('primary')} onClick={()=>setShowModal(openClientIds.length > 1 ? '__all__' : openClientIds[0])}>
        {openClientIds.length > 1 ? `Close month (${openClientIds.length} clients)` : 'Close month'}
      </button>
    : null;

  return (
    <Page title="My Logs" onTour={tour.launch} primary={closeMonthAction}
      tour={{ controls: 'logs-controls' }}
      controls={<Controls
        pills={rangeMode==='month' && activeClientIds.length > 0 ? <StatusPills value={logsStatus} onChange={setLogsStatus} options={[
          { key:'open',   label:'Open',   count: activeClientIds.filter(cid => !clientLocks[cid]).length },
          { key:'closed', label:'Closed', count: activeClientIds.filter(cid => !!clientLocks[cid]).length },
          { key:'all',    label:'All',    count: activeClientIds.length },
        ]} /> : null}
        search={<ClientLogSearch clients={clients} clientIds={activeClientIds} value={clientSearch} onChange={setClientSearch} />}
        range={<>
          <SegmentedControl options={[['month','Month'],['ytd','YTD'],['custom','Custom']]} value={rangeMode} onChange={setRangeMode} />
          {rangeMode==='month' && (
            <select aria-label="Month" style={{...css.sel,width:"auto",padding:"6px 10px",fontSize:"13px"}} value={selectedMonth} onChange={e=>setSelectedMonth(e.target.value)}>
              {allMonths.map(m=><option key={m} value={m}>{fmtPeriod(m)}</option>)}
            </select>
          )}
          {rangeMode==='custom' && (
            <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
              <input type="date" aria-label="From" style={{...css.inp,padding:'4px 8px',fontSize:'12px',width:'136px'}} value={customStart} onChange={e=>setCustomStart(e.target.value)} />
              <span style={{color:C.muted,fontSize:'12px'}}>to</span>
              <input type="date" aria-label="To" style={{...css.inp,padding:'4px 8px',fontSize:'12px',width:'136px'}} value={customEnd} onChange={e=>setCustomEnd(e.target.value)} />
            </div>
          )}
        </>} />}
      stats={[
        { label:'Total hours', value: fmtHours(totalHrs), basis: `${mine.length} ${mine.length===1?'entry':'entries'}`,
          tip:'Every hour you logged in the selected period, billable or not, internal clients included.' },
        { label:'Billable', value: fmtHours(billableHrs), basis: `${totalHrs>0 ? Math.round(billableHrs/totalHrs*100) : 0}% of total`,
          tip:'Hours marked billable, excluding internal clients. This is what gets billed to your clients.' },
        { label:'Non-billable', value: fmtHours(nonBillableHrs), basis: `${totalHrs>0 ? Math.round(nonBillableHrs/totalHrs*100) : 0}% of total`,
          tip:'Hours marked non-billable (admin, meetings, overhead). Not invoiced, but counted in your total.' },
        { label:'Your pay', value: (logsHasAssignments || approvedStmt) ? fmtMoney(earned) : '—',
          basis: (approvedStmt ? 'approved' : logsHasAssignments
            ? `estimated${logsOpenCount > 0 ? ` · ${logsOpenCount} client${logsOpenCount>1?'s':''} open` : logsAllLocked ? ' · all closed' : ''}`
            : 'no assignments') + (unpaidHrs > 0 ? ` · ${fmtHours(unpaidHrs)} not paid` : ''),
          tip:"Your approved member invoice for this month when there is one. Until then, an estimate from your assignments' pay type and rate. Expense reimbursements aren't included. Time marked Not paid (an entry's ⋯ menu) stays on the timesheet but is left out of pay." },
      ]}>
      {tour.show && <WalkthroughModal tourKey="logs" onClose={tour.close} />}

      {lockMsg && (
        <div style={{marginBottom:"16px",padding:"12px 16px",background:"rgba(72,187,120,0.1)",border:"1px solid rgba(72,187,120,0.3)",borderRadius:"8px",color:C.success,fontSize:"13px"}}>
          {lockMsg}
        </div>
      )}
      {submitWarning && (
        <div style={{marginBottom:"16px",padding:"10px 14px",background:"rgba(237,167,48,0.08)",border:"1px solid rgba(237,167,48,0.25)",borderRadius:"6px",color:"#f6c26b",fontSize:"13px"}}>
          ⚠ {submitWarning}
        </div>
      )}
      {editToast && (
        <div style={{marginBottom:"16px",padding:"12px 16px",background:"rgba(72,187,120,0.1)",border:"1px solid rgba(72,187,120,0.3)",borderRadius:"8px",color:C.success,fontSize:"13px"}}>
          {editToast}
        </div>
      )}

      {/* Per-client sections */}
      {visibleClientIds.map(cid => {
        const client = clients.find(c=>c.id===cid);
        const clientEntries = mine.filter(e=>e.client_id===cid);
        const clientHrs = clientEntries.reduce((s,e)=>s+parseFloat(e.hours||0),0);
        const isLocked = !!clientLocks[cid];
        // Closed clients start collapsed: their entries are read-only and usually done with.
        const collapsed = isCollapsed(cid);
        return (
          <div key={cid} style={css.card} data-tour="logs-entries">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom: collapsed ? 0 : "12px",gap:"10px",flexWrap:"wrap"}}>
              <div style={{display:"flex",alignItems:"center",gap:"10px",flexWrap:"wrap"}}>
                <button type="button" aria-expanded={!collapsed} aria-label={collapsed ? `Show ${client?.name||'client'} entries` : `Hide ${client?.name||'client'} entries`}
                  onClick={()=>setGroupOpen(g=>({...g,[cid]:collapsed}))}
                  style={{background:'transparent',border:'none',cursor:'pointer',color:C.muted,fontSize:'12px',padding:'2px 4px',lineHeight:1}}>{collapsed ? '▶' : '▼'}</button>
                <span style={{fontWeight:"600",color:C.textBright,fontSize:"15px",cursor:'pointer'}} onClick={()=>setGroupOpen(g=>({...g,[cid]:collapsed}))}>{client?.name||'Unknown Client'}</span>
                {collapsed && <span style={{color:C.muted,fontSize:"12px"}}>{clientEntries.length} entr{clientEntries.length===1?'y':'ies'}</span>}
                <span style={{color:C.muted,fontSize:"12px"}}>{formatExactTime(clientHrs)}</span>
                {profile.role==="member" && earnByClient[cid] && (() => {
                  const ec = earnByClient[cid];
                  const detail = ec.basis==='days' ? `${ec.note} × ${$c(ec.rateOrAmount)}` : ec.basis==='hours' ? `${ec.quantity.toFixed(2)}h × ${$c(ec.rateOrAmount)}` : ec.note;
                  return <span style={{color:C.accent,fontSize:"12px",fontWeight:"600"}}>{$c(ec.lineTotal)} <span style={{color:C.muted,fontWeight:"400"}}>({detail})</span></span>;
                })()}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                <button style={css.smBtn(C.muted)} onClick={()=>{
                  const label = rangeMode==='month' ? new Date(selectedMonth+'-02').toLocaleString("default",{month:"long",year:"numeric"}).replace(' ','_') : rangeLabel.replace(/[\s–]/g,'_');
                  const cN=(client?.name||'Client').replace(/\s+/g,'_');
                  downloadCSVForClose(clientEntries,`TimeKeep_${cN}_${label}.csv`,isAdminUser);
                }}>Download</button>
                {eligibleForClose && (
                  isLocked
                    ? <>
                        <span style={{...css.badge("green"),padding:"4px 10px",fontSize:"12px"}}>Submitted</span>
                        {canReopen && <button style={css.smBtn(C.muted)} onClick={()=>reopenClient(cid)} title={actingAdmin ? `Reopens ${memberName}'s month for this client so entries can be edited. Any submitted or approved invoice for the month is voided.` : undefined}>Reopen</button>}
                      </>
                    : <button style={{...css.btn("secondary"),padding:"5px 12px",fontSize:"12px"}} data-tour="logs-close" onClick={()=>setShowModal(cid)}>Close month</button>
                )}
              </div>
            </div>
            {!collapsed && <table style={{width:"100%",borderCollapse:"collapse",fontSize:"13px"}}>
              <thead><tr>
                {bulkHeadBox(clientEntries)}
                <th style={css.th}>Date</th><th style={css.th}>Task</th>
                <th style={css.th}>Description</th><th style={css.th}>Hours</th><th style={{...css.th,width:'40px'}} aria-label="Actions"></th>
              </tr></thead>
              <tbody>
                {clientEntries.length===0 && <tr><td colSpan={6} style={{...css.td,color:C.muted,textAlign:"center",padding:"16px"}}>No entries in this period.</td></tr>}
                {clientEntries.map(ent=>(
                  editEntryId===ent.id
                    ? <EntryEditRow key={ent.id} entry={ent} clients={clients} taskTypes={taskTypes||[]} tags={tags||[]} orgId={org?.id} colSpan={6} onSave={handleEditSave} onCancel={()=>setEditEntryId(null)} />
                    : <tr key={ent.id} style={{cursor:isLocked?"default":"pointer",opacity:ent.is_billable===false?0.75:1,...pickedRowStyle(ent)}} onClick={()=>{if(!isLocked)setEditEntryId(ent.id);}}>
                        {bulkRowBox(ent)}
                        <td style={css.td}>{ent.entry_date}{fmtTimeRange(ent.start_time,ent.end_time) && <div style={{fontSize:"10px",color:C.muted,marginTop:"2px"}}>{fmtTimeRange(ent.start_time,ent.end_time)}</div>}</td>
                        <td style={css.td}>{ent.task_types?.name?<span style={css.tag}>{ent.task_types.name}</span>:"—"}</td>
                        <td style={{...css.td,color:C.muted}}>{ent.description||"—"}<TagChips tagLinks={ent.time_entry_tags} /></td>
                        <td style={{...css.td,fontWeight:"600",color:C.textBright}}>
                          {formatExactTime(parseFloat(ent.hours||0))}
                          <EntryBadges entry={ent} ownerName={profile.name} viewerIsOwner={!actingAdmin} />
                        </td>
                        <td style={css.td}>
                          {isLocked
                            ?<span style={{fontSize:"14px",cursor:"default"}} title={`Locked — month closed${clientLocks[cid]?.locked_at ? ' on ' + new Date(clientLocks[cid].locked_at).toLocaleDateString() : ''}. Reopen the client's month to edit.`}>🔒</span>
                            :<RowMenu actions={rowActions(ent)} />
                          }
                        </td>
                      </tr>
                ))}
              </tbody>
            </table>}
          </div>
        );
      })}

      {/* Unassigned / no-client entries — hidden during a client search, same
          as any other section that wouldn't match the typed name/alias. */}
      {!searching && unassignedEntries.length > 0 && (
        <div style={css.card}>
          <div style={{fontWeight:"600",color:C.textBright,marginBottom:"12px",fontSize:"15px"}}>Unassigned</div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"13px"}}>
            <thead><tr>
              {bulkHeadBox(unassignedEntries)}
              <th style={css.th}>Date</th><th style={css.th}>Client</th><th style={css.th}>Task</th>
              <th style={css.th}>Description</th><th style={css.th}>Hours</th><th style={css.th}></th>
            </tr></thead>
            <tbody>
              {unassignedEntries.map(ent=>{
                const entClientLocked = !!clientLocks[ent.client_id];
                return editEntryId===ent.id
                  ? <EntryEditRow key={ent.id} entry={ent} clients={clients} taskTypes={taskTypes||[]} tags={tags||[]} orgId={org?.id} colSpan={7} onSave={handleEditSave} onCancel={()=>setEditEntryId(null)} />
                  : <tr key={ent.id} style={{cursor:entClientLocked?"default":"pointer",opacity:ent.is_billable===false?0.75:1,...pickedRowStyle(ent)}} onClick={()=>{if(!entClientLocked)setEditEntryId(ent.id);}}>
                      {bulkRowBox(ent)}
                      <td style={css.td}>{ent.entry_date}{fmtTimeRange(ent.start_time,ent.end_time) && <div style={{fontSize:"10px",color:C.muted,marginTop:"2px"}}>{fmtTimeRange(ent.start_time,ent.end_time)}</div>}</td>
                      <td style={css.td}>{clients.find(c=>c.id===ent.client_id)?.name||'—'}</td>
                      <td style={css.td}>{ent.task_types?.name?<span style={css.tag}>{ent.task_types.name}</span>:"—"}</td>
                      <td style={{...css.td,color:C.muted}}>{ent.description||"—"}<TagChips tagLinks={ent.time_entry_tags} /></td>
                      <td style={{...css.td,fontWeight:"600",color:C.textBright}}>
                        {formatExactTime(parseFloat(ent.hours||0))}
                        <EntryBadges entry={ent} ownerName={profile.name} viewerIsOwner={!actingAdmin} />
                      </td>
                      <td style={css.td}>
                        {entClientLocked
                          ?<span style={{fontSize:"14px",cursor:"default"}} title={`Locked — month closed${clientLocks[ent.client_id]?.locked_at ? ' on ' + new Date(clientLocks[ent.client_id].locked_at).toLocaleDateString() : ''}. Reopen the client's month to edit.`}>🔒</span>
                          :<RowMenu actions={rowActions(ent)} />
                        }
                      </td>
                    </tr>;
              })}
            </tbody>
          </table>
        </div>
      )}

      {visibleClientIds.length===0 && (searching || unassignedEntries.length===0) && (
        <div style={css.card}>
          <div style={{textAlign:"center",color:C.muted,padding:"32px",fontSize:"13px"}}>
            {searching ? `No clients match "${clientSearch.trim()}".` : 'No entries this month yet.'}
          </div>
        </div>
      )}

      {/* Bulk bar: appears once something is ticked, and stays in view while scrolling a long month. */}
      {pickedEntries.length > 0 && (<>
        {/* Room at the end of the page so the bar never covers the last rows. */}
        <div style={{height:'72px'}} aria-hidden="true" />
        {/* Fixed to the window, not sticky: the page's main area is an overflow container as tall as its
            content, so sticky positioning never engaged there. 236px = the 220px sidebar plus a gutter. */}
        <div style={{position:'fixed',bottom:'16px',left:'236px',right:'16px',zIndex:60,pointerEvents:'none'}}>
          <div role="toolbar" aria-label="Selected entries" style={{pointerEvents:'auto',display:'inline-flex',alignItems:'center',gap:'12px',flexWrap:'wrap',maxWidth:'100%',boxSizing:'border-box',padding:'10px 14px',background:C.surface,border:`1px solid ${C.accent}`,borderRadius:'10px',boxShadow:'0 8px 28px rgba(0,0,0,0.45)'}}>
            <span style={{fontWeight:700,color:C.textBright,fontSize:'13px'}}>{pickedEntries.length} selected</span>
            <span style={{color:C.muted,fontSize:'12px'}}>{formatExactTime(pickedHours)}</span>
            <button style={{...css.btn('primary'),padding:'6px 14px',fontSize:'12px'}} onClick={()=>setBulkModal('edit')}
              title="Change the client, task type, tags, description, billing or date on all of them at once">Edit…</button>
            <button style={{...css.btn('dangerOutline'),padding:'5px 12px',fontSize:'12px'}} onClick={()=>setBulkModal('delete')}>Delete…</button>
            {pickedEntries.length < tickable.length && (
              <button style={{background:'none',border:'none',padding:0,cursor:'pointer',color:C.accent,fontSize:'12px',fontFamily:'inherit'}}
                onClick={()=>setPicked(new Set(tickable.map(e=>e.id)))}>Select all {tickable.length}</button>
            )}
            <button style={{background:'none',border:'none',padding:0,cursor:'pointer',color:C.muted,fontSize:'12px',fontFamily:'inherit'}}
              onClick={()=>setPicked(new Set())}>Clear selection</button>
          </div>
        </div>
      </>)}

      {bulkModal==='edit' && pickedEntries.length > 0 && (
        <BulkEditModal entries={pickedEntries} clients={clients.filter(c=>c.is_active!==false)} taskTypes={taskTypes||[]} tags={tags||[]}
          orgId={org?.id} userId={profile.id} monthLocks={monthLocks} canEditTags={!actingAdmin}
          isAssigned={(cid)=>!!isAdminUser || myAssignments.some(a=>a.client_id===cid)}
          onClose={()=>setBulkModal(null)}
          onDone={(n)=>{ setBulkModal(null); bulkDone(`${n} ${n===1?'entry':'entries'} updated.`); }} />
      )}
      {bulkModal==='delete' && pickedEntries.length > 0 && (
        <BulkDeleteModal entries={pickedEntries} onClose={()=>setBulkModal(null)}
          onDone={(n, partial)=>{ if (!partial) { setBulkModal(null); bulkDone(`${n} ${n===1?'entry':'entries'} deleted.`); } else { setEditToast(`${n} deleted.`); setTimeout(()=>setEditToast(''),3500); onRefresh(); } }} />
      )}

      {/* Close Month Modal */}
      {showModal && modalClientIds.length > 0 && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",padding:"28px",maxWidth:"420px",width:"90%"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:C.textBright,marginBottom:"8px"}}>
              {showModal==='__all__'
                ? (actingAdmin ? `Close all of ${memberName}'s clients?` : 'Close All Clients?')
                : (actingAdmin ? `Close ${clients.find(c=>c.id===showModal)?.name||''} for ${memberName}?` : `Close ${clients.find(c=>c.id===showModal)?.name||''}?`)}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px",marginBottom:"16px"}}>
              <div style={css.metric}><div style={css.lbl}>Period</div><div style={{fontSize:"14px",fontWeight:"600",color:C.textBright,marginTop:"4px"}}>{monthLabel}</div></div>
              <div style={css.metric}><div style={css.lbl}>Total Hours</div><div style={{fontSize:"14px",fontWeight:"600",color:C.textBright,marginTop:"4px"}}>{formatExactTime(modalHrs)}</div></div>
              <div style={css.metric}><div style={css.lbl}>Entries</div><div style={{fontSize:"14px",fontWeight:"600",color:C.textBright,marginTop:"4px"}}>{modalEntryCount}</div></div>
              {showModal==='__all__' && <div style={css.metric}><div style={css.lbl}>Clients</div><div style={{fontSize:"14px",fontWeight:"600",color:C.textBright,marginTop:"4px"}}>{modalClientIds.length}</div></div>}
            </div>
            <div style={{padding:"12px 14px",background:"rgba(237,167,48,0.08)",border:"1px solid rgba(237,167,48,0.25)",borderRadius:"6px",marginBottom:"20px",fontSize:"13px",color:"#f6c26b"}}>
              {actingAdmin
                ? `You are closing ${memberName}'s timesheet for this period. It will be locked so they cannot edit it, and you can reopen it later. No invoice is submitted for them; it can still be submitted from Member Invoices.`
                : 'Once submitted, your timesheet for this period will be locked and cannot be edited.'}
            </div>
            <div style={{display:"flex",gap:"10px"}}>
              <button style={css.btn()} onClick={()=>setShowModal(null)} disabled={locking}>Cancel</button>
              <button style={css.btn("primary")} onClick={()=>confirmClose(modalClientIds)} disabled={locking}>{locking?(actingAdmin?'Closing...':'Submitting...'):(actingAdmin?'Close for member':'Confirm and Submit')}</button>
            </div>
          </div>
        </div>
      )}

      {payModal && (
        <LeaveOutOfPayModal entry={payModal} ownerName={profile.name} viewerIsOwner={!actingAdmin}
          clientName={clients.find(c=>c.id===payModal.client_id)?.name} payEffect={payEffectFor(payModal)}
          onClose={()=>setPayModal(null)} onDone={()=>{ setPayModal(null); onRefresh(); }} />
      )}
    </Page>
  );
}

// ─── REPORTS ──────────────────────────────────────────────────────────────────
// Returns array of non-empty address display lines from a structured address object
function formatAddressLines(obj) {
  if (!obj) return [];
  const lines = [];
  if (obj.address_line1) lines.push(obj.address_line1);
  if (obj.address_line2) lines.push(obj.address_line2);
  const cityState = [obj.city, obj.state].filter(Boolean).join(', ');
  const cityStateLine = obj.zip ? `${cityState} ${obj.zip}`.trim() : cityState;
  if (cityStateLine) lines.push(cityStateLine);
  if (obj.country) lines.push(obj.country);
  return lines;
}

function fmtPhone(raw) {
  if (!raw) return raw;
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `+1 (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  return raw;
}

// Billing rounding lives in metrics.js (roundBillingHours) and is applied to a
// client's MONTHLY TOTAL. There used to be a second copy here, in minutes,
// which by_task invoicing applied PER TASK -- so the same month produced one
// number on an invoice and a different one on the Revenue Dashboard. Austin's
// call, 2026-10-12: the monthly total is correct. Past invoices are left as
// they were issued.

function formatExactTime(decimalHours) {
  const totalMinutes = Math.round(parseFloat(decimalHours || 0) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function roleBadgeLabel(role) {
  return { owner: 'Owner', admin: 'Admin', member: 'Member' }[role] || (role || '');
}
function seatTypeLabel(s) {
  return { owner: 'Owner', admin: 'Admin', contractor: 'Contractor', employee_hourly: 'Employee (Hourly)', employee_salary: 'Employee (Salary)' }[s] || (s || '');
}

// calcOwed now lives in ./billingModels alongside the rest of each model's
// definition. Imported above; verified identical across 172k input
// combinations before the swap.

function AiInsightsPanel({ rangeEntries, clients, taskTypes, org, period }) {
  const [status, setStatus]     = useState('idle'); // 'idle' | 'loading' | 'done' | 'error'
  const [analysis, setAnalysis] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const run = async () => {
    if (rangeEntries.length === 0 || status === 'loading') return;
    setStatus('loading');
    setAnalysis(null);

    const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));
    const taskMap   = Object.fromEntries(taskTypes.map(t => [t.id, t]));

    const entrySummary = rangeEntries.map(e => ({
      date: e.entry_date,
      client: clientMap[e.client_id]?.name || 'Unknown',
      task: taskMap[e.task_type_id]?.name || null,
      hours: parseFloat(e.hours || 0),
      description: e.description?.trim() || null,
      billable: e.is_billable !== false,
    }));

    const periodLabel = period
      ? new Date(period + '-02').toLocaleString('en-US', { month: 'long', year: 'numeric' })
      : 'the selected period';

    const prompt = `You are a billing analyst reviewing time entry data for a consulting firm.

Period: ${periodLabel}
Total entries: ${entrySummary.length}
Clients: ${[...new Set(entrySummary.map(e => e.client))].join(', ')}

Time entries (date, client, task, hours, description, billable):
${entrySummary.map(e =>
  `${e.date} | ${e.client} | ${e.task || 'no task'} | ${e.hours}h | ${e.description || 'no description'} | ${e.billable ? 'billable' : 'NON-BILLABLE'}`
).join('\n')}

Analyze this data and return a JSON object with this exact structure:
{
  "narrative": "2-3 sentence summary of the period: total hours, client distribution, any notable patterns",
  "anomalies": [
    {
      "severity": "high" | "medium" | "low",
      "type": "short label",
      "description": "specific finding with numbers and client names",
      "dates": ["YYYY-MM-DD"]
    }
  ],
  "recommendations": ["actionable string", ...]
}

Look for: unusually long days (>8h to one client), missing descriptions or task types, high non-billable rates (>20% for a client), weekend heavy work, potential duplicate entries (same client/hours/date), anything else that looks unusual for a consulting context.

If data looks clean, return an empty anomalies array. Be specific — mention client names and dates. Return only the JSON, no markdown.`;

    try {
      const { data, error } = await supabase.functions.invoke('help-chat', {
        body: { message: prompt, history: [] }
      });
      if (error) throw new Error(error.message);
      const raw = data?.reply || '';
      const jsonStr = raw.replace(/```json\n?/g,'').replace(/```\n?/g,'').trim();
      const parsed = JSON.parse(jsonStr);
      setAnalysis(parsed);
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  const severityColor = (s) => s==='high' ? C.danger : s==='medium' ? C.badgeAmber : C.muted;
  const severityBg    = (s) => s==='high' ? 'rgba(239,68,68,0.08)' : s==='medium' ? 'rgba(245,158,11,0.08)' : 'rgba(100,116,139,0.06)';

  return (
    <div style={{marginBottom:'12px',border:`1px solid ${C.border}`,borderRadius:'8px',overflow:'hidden',flexShrink:0}}>
      {/* Header row */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:`${C.accent}0d`,cursor:'pointer'}}
        onClick={()=>setExpanded(e=>!e)}>
        <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
          <span style={{fontSize:'14px'}}>✨</span>
          <span style={{fontSize:'13px',fontWeight:'600',color:C.accent}}>AI Billing Insights</span>
          {status==='done' && analysis?.anomalies?.length > 0 && (
            <span style={{fontSize:'11px',padding:'2px 7px',borderRadius:'10px',background:'rgba(239,68,68,0.15)',color:C.danger,fontWeight:'600'}}>
              {analysis.anomalies.length} {analysis.anomalies.length===1?'issue':'issues'}
            </span>
          )}
          {status==='done' && analysis?.anomalies?.length === 0 && (
            <span style={{fontSize:'11px',padding:'2px 7px',borderRadius:'10px',background:'rgba(72,187,120,0.15)',color:C.success,fontWeight:'600'}}>All clear</span>
          )}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
          {status==='idle' && rangeEntries.length > 0 && (
            <button onClick={e=>{e.stopPropagation();run();}}
              style={{...css.smBtn(C.accent),fontSize:'12px',padding:'4px 12px'}}>
              Analyze {rangeEntries.length} entries
            </button>
          )}
          {status==='loading' && (
            <span style={{fontSize:'12px',color:C.muted}}>Analyzing…</span>
          )}
          {(status==='done'||status==='error') && (
            <button onClick={e=>{e.stopPropagation();setStatus('idle');setAnalysis(null);}}
              style={{fontSize:'11px',color:C.muted,background:'none',border:'none',cursor:'pointer'}}>Re-run</button>
          )}
          <span style={{fontSize:'11px',color:C.muted}}>{expanded?'▲':'▼'}</span>
        </div>
      </div>

      {expanded && (
        <div style={{padding:'14px'}}>
          {status==='idle' && (
            <div style={{textAlign:'center',padding:'12px 0',fontSize:'13px',color:C.muted}}>
              {rangeEntries.length === 0
                ? 'No entries in this period to analyze.'
                : 'Click "Analyze" to run AI analysis on billing patterns, anomalies, and missing details.'}
            </div>
          )}
          {status==='loading' && (
            <div style={{textAlign:'center',padding:'16px 0',fontSize:'13px',color:C.muted}}>
              Gemini is reviewing {rangeEntries.length} entries…
            </div>
          )}
          {status==='error' && (
            <div style={{textAlign:'center',padding:'12px 0',fontSize:'13px',color:C.danger}}>
              Analysis failed. Check your connection and try again.
            </div>
          )}

          {status==='done' && analysis && (
            <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
              {/* Narrative */}
              <div style={{fontSize:'13px',color:C.text,lineHeight:'1.6',padding:'10px 14px',
                background:C.bg,borderRadius:'6px',border:`1px solid ${C.border}`}}>
                {analysis.narrative}
              </div>

              {/* Anomalies */}
              {analysis.anomalies?.length > 0 && (
                <div>
                  <div style={{fontSize:'11px',fontWeight:'700',color:C.muted,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'8px'}}>Flagged Issues</div>
                  <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                    {analysis.anomalies.map((a, i) => (
                      <div key={i} style={{padding:'10px 12px',borderRadius:'6px',
                        background:severityBg(a.severity),border:`1px solid ${severityColor(a.severity)}33`}}>
                        <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'3px'}}>
                          <span style={{fontSize:'11px',fontWeight:'700',color:severityColor(a.severity),
                            textTransform:'uppercase',letterSpacing:'0.4px'}}>{a.severity}</span>
                          <span style={{fontSize:'12px',fontWeight:'600',color:C.textBright}}>{a.type}</span>
                        </div>
                        <div style={{fontSize:'12px',color:C.text}}>{a.description}</div>
                        {a.dates?.length > 0 && (
                          <div style={{fontSize:'11px',color:C.muted,marginTop:'4px'}}>
                            Dates: {a.dates.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {analysis.recommendations?.length > 0 && (
                <div>
                  <div style={{fontSize:'11px',fontWeight:'700',color:C.muted,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'8px'}}>Recommendations</div>
                  <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
                    {analysis.recommendations.map((r, i) => (
                      <div key={i} style={{display:'flex',gap:'8px',fontSize:'12px',color:C.text}}>
                        <span style={{color:C.accent,flexShrink:0}}>→</span>
                        <span>{r}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const PIE_COLORS = ['#31A7D9','#68d391','#f6c26b','#a78bfa','#fc8181','#63d3f7','#f97316','#e879f9','#34d399','#fb923c'];

function ClientReportsPieChart({ rangeEntries, clients, taskTypes, tags, invoices, clientData, rangeMode, selectedMonth, customStart, customEnd }) {
  const [groupBy, setGroupBy] = useState('client');
  const [metric, setMetric]   = useState('hours');

  // Bar chart: hours by day for the current range
  const barData = useMemo(() => {
    if (rangeMode === 'month') {
      const [y, m] = selectedMonth.split('-').map(Number);
      const days = new Date(y, m, 0).getDate();
      return Array.from({ length: days }, (_, i) => {
        const d = String(i + 1).padStart(2, '0');
        const date = `${selectedMonth}-${d}`;
        const dayEntries = rangeEntries.filter(e => e.entry_date === date);
        const hrs = dayEntries.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
        const dow = new Date(date + 'T12:00:00').toLocaleString('en-US', { weekday: 'short' });
        return { label: `${dow} ${parseInt(d)}`, hrs: parseFloat(hrs.toFixed(2)), date };
      }).filter(d => d.hrs > 0);
    }
    if (customStart && customEnd) {
      const result = [];
      const cur = new Date(customStart + 'T12:00:00');
      const end = new Date(customEnd + 'T12:00:00');
      while (cur <= end) {
        const date = cur.toISOString().slice(0, 10);
        const hrs = rangeEntries.filter(e => e.entry_date === date).reduce((s, e) => s + parseFloat(e.hours || 0), 0);
        const dow = cur.toLocaleString('en-US', { weekday: 'short' });
        if (hrs > 0) result.push({ label: `${dow} ${cur.getDate()}`, hrs: parseFloat(hrs.toFixed(2)), date });
        cur.setDate(cur.getDate() + 1);
      }
      return result;
    }
    return [];
  }, [rangeEntries, rangeMode, selectedMonth, customStart, customEnd]);

  const slices = useMemo(() => {
    const fmtVal = (v) => {
      if (metric === 'invoiced') return '$' + (v >= 1000 ? (v/1000).toFixed(1)+'k' : Math.round(v));
      if (metric === 'hours')    return v.toFixed(1) + 'h';
      return String(Math.round(v));
    };
    const invoicedByClient = {};
    invoices.forEach(inv => {
      const inPeriod = rangeMode === 'month' ? inv.issued_date?.slice(0,7) === selectedMonth : true;
      if (inPeriod) invoicedByClient[inv.client_id] = (invoicedByClient[inv.client_id] || 0) + parseFloat(inv.total || 0);
    });

    const buildSlices = (rows) => {
      const sorted = rows.filter(r => r.val > 0).sort((a, b) => b.val - a.val);
      const top = sorted.slice(0, 8);
      const rest = sorted.slice(8);
      if (rest.length > 0) top.push({ name: 'Other', val: rest.reduce((s, r) => s + r.val, 0) });
      const total = top.reduce((s, r) => s + r.val, 0);
      return top.map((r, i) => ({ ...r, pct: total > 0 ? r.val / total * 100 : 0, fmt: fmtVal(r.val), color: PIE_COLORS[i % PIE_COLORS.length] }));
    };

    if (groupBy === 'client') {
      return buildSlices(clientData.map(r => ({
        name: r.cl.name,
        val: metric === 'entries' ? r.ces.length : metric === 'hours' ? r.roundedHrs : (invoicedByClient[r.cl.id] || 0),
      })));
    }

    if (groupBy === 'tasktype') {
      const map = {};
      rangeEntries.forEach(e => {
        const key = e.task_type_id || '__none__';
        if (!map[key]) map[key] = { name: e.task_types?.name || 'No Task Type', entries: 0, hours: 0, invoiced: 0 };
        map[key].entries += 1;
        map[key].hours   += parseFloat(e.hours || 0);
      });
      if (metric === 'invoiced') {
        clientData.forEach(r => {
          const inv = invoicedByClient[r.cl.id] || 0;
          if (!inv) return;
          const th = r.ces.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
          r.ces.forEach(e => { const k = e.task_type_id || '__none__'; if (map[k] && th > 0) map[k].invoiced += inv * parseFloat(e.hours || 0) / th; });
        });
      }
      return buildSlices(Object.values(map).map(r => ({ name: r.name, val: metric === 'entries' ? r.entries : metric === 'hours' ? r.hours : r.invoiced })));
    }

    // tag
    const map = {};
    rangeEntries.forEach(e => {
      const et = e.time_entry_tags || [];
      if (!et.length) {
        if (!map['__none__']) map['__none__'] = { name: 'Untagged', entries: 0, hours: 0, invoiced: 0 };
        map['__none__'].entries += 1; map['__none__'].hours += parseFloat(e.hours || 0);
      } else {
        et.forEach(t => {
          if (!map[t.tag_id]) map[t.tag_id] = { name: t.tags?.name || 'Unknown', entries: 0, hours: 0, invoiced: 0 };
          map[t.tag_id].entries += 1; map[t.tag_id].hours += parseFloat(e.hours || 0);
        });
      }
    });
    if (metric === 'invoiced') {
      clientData.forEach(r => {
        const inv = invoicedByClient[r.cl.id] || 0;
        if (!inv) return;
        const th = r.ces.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
        r.ces.forEach(e => {
          const et = e.time_entry_tags || [];
          const share = th > 0 ? inv * parseFloat(e.hours || 0) / th : 0;
          if (!et.length) { if (map['__none__']) map['__none__'].invoiced += share; }
          else et.forEach(t => { if (map[t.tag_id]) map[t.tag_id].invoiced += share / et.length; });
        });
      });
    }
    return buildSlices(Object.values(map).map(r => ({ name: r.name, val: metric === 'entries' ? r.entries : metric === 'hours' ? r.hours : r.invoiced })));
  }, [groupBy, metric, rangeEntries, clientData, invoices, rangeMode, selectedMonth, tags]);

  const metricLabel = metric === 'entries' ? 'Entries' : metric === 'hours' ? 'Hours' : 'Invoiced';
  const isEmpty = slices.length === 0 && barData.length === 0;

  const BarTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background:'#28395C', border:'1px solid #3A4F7D', borderRadius:'6px', padding:'7px 10px', fontSize:'12px', color:'#e2e8f0' }}>
        <div style={{ fontWeight:'700', marginBottom:'2px' }}>{label}</div>
        <div>{payload[0].value}h</div>
      </div>
    );
  };
  const PieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div style={{ background:'#28395C', border:'1px solid #3A4F7D', borderRadius:'6px', padding:'7px 10px', fontSize:'12px', color:'#e2e8f0' }}>
        <div style={{ fontWeight:'700', marginBottom:'2px' }}>{d.name}</div>
        <div>{metricLabel}: <strong>{d.fmt}</strong> · {d.pct.toFixed(1)}%</div>
      </div>
    );
  };


  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:'10px', padding:'20px 24px', marginBottom:'12px', flexShrink:0 }}>
      {isEmpty ? (
        <div style={{ textAlign:'center', color:C.muted, fontSize:'13px', padding:'32px 0' }}>No data for the selected period.</div>
      ) : (
        <div style={{ display:'flex', gap:'0', alignItems:'stretch' }}>

          {/* ── Left: hours by day bar chart ── */}
          <div style={{ flex:'1 1 0', minWidth:0, borderRight:`1px solid ${C.border}`, paddingRight:'24px' }}>
            <div style={{ fontSize:'12px', fontWeight:'700', color:C.textBright, marginBottom:'12px' }}>Hours by Day</div>
            {barData.length === 0 ? (
              <div style={{ color:C.muted, fontSize:'12px', paddingTop:'24px' }}>No entries in this period.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData} margin={{ top:4, right:4, left:-20, bottom:16 }} barSize={barData.length > 20 ? 14 : barData.length > 10 ? 24 : 40}>
                  <CartesianGrid strokeDasharray="4 6" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize:10, fill:C.muted }} axisLine={{ stroke:C.border, strokeWidth:1 }} tickLine={false} interval={barData.length > 15 ? Math.floor(barData.length/10) : 0}
                    label={{ value: rangeMode==='month' ? new Date(selectedMonth+'-02').toLocaleString('default',{month:'long',year:'numeric'}) : (customStart&&customEnd ? `${customStart} — ${customEnd}` : ''), position:'insideBottom', offset:-4, fontSize:10, fill:C.muted }} />
                  <YAxis tick={{ fontSize:10, fill:C.muted }} axisLine={false} tickLine={false} tickFormatter={v => v+'h'} />
                  <Tooltip content={<BarTooltip />} cursor={{ fill:'rgba(255,255,255,0.05)' }} />
                  <Bar dataKey="hrs" fill={C.accent} radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* ── Right: pie chart + legend — fixed width so legend values stay close to names ── */}
          <div style={{ flexShrink:0, width:'360px', paddingLeft:'24px', display:'flex', flexDirection:'column' }}>
            {/* Controls */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px', flexWrap:'wrap', gap:'6px' }}>
              <div style={{ fontSize:'12px', fontWeight:'700', color:C.textBright }}>Distribution</div>
              <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                <SegmentedControl options={[['client','Clients'],['tasktype','Task types'],['tag','Tags']]} value={groupBy} onChange={setGroupBy} />
                <SegmentedControl options={[['entries','Entries'],['hours','Hours'],['invoiced','Invoiced']]} value={metric} onChange={setMetric} />
              </div>
            </div>

            {/* Donut centered above legend */}
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'16px', flex:1 }}>
              <PieChart width={180} height={180}>
                <Pie data={slices} dataKey="val" cx={88} cy={88} innerRadius={54} outerRadius={84} strokeWidth={0} paddingAngle={1}>
                  {slices.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
              {/* Column-aligned legend — fixed-width container keeps values flush to names */}
              <div style={{ width:'100%', display:'flex', flexDirection:'column', gap:'6px' }}>
                {slices.map((s, i) => (
                  <div key={i} style={{ display:'grid', gridTemplateColumns:'10px minmax(0,1fr) 52px 38px', alignItems:'center', gap:'6px' }}>
                    <div style={{ width:'10px', height:'10px', borderRadius:'2px', background:s.color, flexShrink:0 }} />
                    <div style={{ fontSize:'11px', color:C.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{s.name}</div>
                    <div style={{ fontSize:'11px', fontWeight:'700', color:C.textBright, textAlign:'right' }}>{s.fmt}</div>
                    <div style={{ fontSize:'10px', color:C.muted, textAlign:'right' }}>{s.pct.toFixed(0)}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

function ReportsView({ userId, entries, clients, taskTypes, tags=[], assignments=[], users=[], orgMembers=[], org, monthLocks, invoices, statements=[], billingTerms=null, onRefresh, onNavigate, onClientClick }) {
  const tour = useTour('reports');
  // ── Detail panel width (user-resizable via the drag handle on its left edge) ──
  const [panelWidth, setPanelWidth] = useState(460);
  const resizingRef = useRef(false);
  const startResize = (e) => {
    e.preventDefault();
    resizingRef.current = { startX: e.clientX, startWidth: panelWidth };
    const onMove = (ev) => {
      if (!resizingRef.current) return;
      const delta = resizingRef.current.startX - ev.clientX; // dragging left grows the panel
      const next = Math.min(900, Math.max(360, resizingRef.current.startWidth + delta));
      setPanelWidth(next);
    };
    const onUp = () => {
      resizingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  // ── Range ─────────────────────────────────────────────────────────────────
  const [rangeMode, setRangeMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const cur = curMonth();
    if (entries.some(e => e.entry_date?.slice(0,7) === cur)) return cur;
    const months = [...new Set(entries.map(e => e.entry_date?.slice(0,7)).filter(Boolean))].sort().reverse();
    return months[0] || cur;
  });
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');
  const allMonths = useMemo(() => { const months=[],d=new Date(); for(let i=0;i<24;i++){months.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);d.setMonth(d.getMonth()-1);} return months; },[]);

  // Year to date follows the org's fiscal year start, like Team Analytics and the Overview.
  const ytdBounds = (() => {
    const fs = org?.fiscal_year_start || 1, now = new Date();
    const yr = now.getMonth() + 1 >= fs ? now.getFullYear() : now.getFullYear() - 1;
    const local = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    return { start: `${yr}-${String(fs).padStart(2,'0')}-01`, end: local };
  })();
  const rangeEntries = useMemo(() => rangeMode === 'month'
    ? entries.filter(e => e.entry_date?.slice(0,7) === selectedMonth)
    : rangeMode === 'ytd'
      ? entries.filter(e => e.entry_date >= ytdBounds.start && e.entry_date <= ytdBounds.end)
      : (customStart && customEnd ? entries.filter(e => e.entry_date >= customStart && e.entry_date <= customEnd) : []),
  [entries, rangeMode, selectedMonth, customStart, customEnd, ytdBounds.start, ytdBounds.end]);
  const rangeLabel = rangeMode === 'month'
    ? fmtPeriod(selectedMonth)
    : rangeMode === 'ytd' ? `Year to date (since ${fmtDate(ytdBounds.start)})`
    : (customStart && customEnd ? `${fmtDate(customStart)} – ${fmtDate(customEnd)}` : 'Custom range');

  // ── Search / filter / sort ────────────────────────────────────────────────
  const [search, setSearch]       = useState('');
  const [filterModel, setFilterModel] = useState('all'); // 'all' | 'hourly' | 'retainer' | 'hybrid'
  const [filterActivity, setFilterActivity] = useState('all'); // 'all' | 'active' | 'inactive'
  const [filterBucket, setFilterBucket] = useState('all'); // 'all' | 'uninvoiced' | 'overlimit'
  const [filterTaskType, setFilterTaskType] = useState(''); // task type id or ''
  const [filterClientId, setFilterClientId] = useState(''); // client id or ''
  const [filterTagId, setFilterTagId]       = useState(''); // tag id or ''
  const [reportBucket, setReportBucket] = useState(null); // null | 'large_entries' | 'missing_task' | 'unbillable'
  const [sortKey, setSortKey]     = useState('name');   // 'name' | 'hours' | 'owed'
  const [sortDir, setSortDir]     = useState('asc');

  // ── Tabs ──────────────────────────────────────────────────────────────────
  const [reportTab, setReportTab] = useState('summary'); // 'summary'|'detailed'|'profitability'|'insights'|'saved'

  // ── Saved reports ─────────────────────────────────────────────────────────
  const [savedReports, setSavedReports] = useState([]);
  const [saveReportName, setSaveReportName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [savingReport, setSavingReport] = useState(false);

  const loadSavedReports = async () => {
    const { data, error } = await supabase.from('saved_reports').select('*').eq('org_id', org?.id).order('created_at', { ascending: false });
    if (error) { notifyError(`Loading saved reports: ${friendlyDbError(error)}.`); return; }
    if (data) setSavedReports(data);
  };

  useEffect(() => { if (org?.id) loadSavedReports(); }, [org?.id]);

  // Every filter and the date range go into a saved report, and loadReport puts every one back.
  // scripts/test-report-filters.mjs fails if a filter is added to this view and left out of either.
  const saveReport = async () => {
    if (!saveReportName.trim()) return;
    if (!userId) { notifyError('Saving report: you appear to be signed out. Reload the page and try again.'); return; }
    setSavingReport(true);
    // user_id is required: the table's row-level security only accepts a report saved as yourself.
    // Leaving it out made every save fail without a word, so nothing was ever saved.
    const { error } = await supabase.from('saved_reports').insert({
      org_id: org.id,
      user_id: userId,
      name: saveReportName.trim(),
      range_mode: rangeMode,
      selected_month: rangeMode === 'month' ? selectedMonth : null,
      custom_start: rangeMode === 'custom' && customStart ? customStart : null,
      custom_end:   rangeMode === 'custom' && customEnd   ? customEnd   : null,
      filter_client_id:   filterClientId   || null,
      filter_task_type_id: filterTaskType  || null,
      filter_tag_id:      filterTagId      || null,
      filter_model:    filterModel,
      filter_activity: filterActivity,
      report_tab:      reportTab === 'saved' ? 'summary' : reportTab,
    });
    setSavingReport(false);
    if (error) { notifyError(`Saving report: ${friendlyDbError(error)}.`); return; }
    setSaveReportName(''); setShowSaveInput(false); await loadSavedReports(); showToast('Report saved!');
  };

  const loadReport = (r) => {
    setRangeMode(r.range_mode || 'month');
    if (r.range_mode === 'month' && r.selected_month) setSelectedMonth(r.selected_month);
    if (r.range_mode === 'custom') { setCustomStart(r.custom_start || ''); setCustomEnd(r.custom_end || ''); }
    setFilterClientId(r.filter_client_id || '');
    setFilterTaskType(r.filter_task_type_id || '');
    setFilterTagId(r.filter_tag_id || '');
    setFilterModel(r.filter_model || 'all');
    setFilterActivity(r.filter_activity || 'all');
    setReportTab(r.report_tab || 'summary');
  };

  const deleteReport = async (id) => {
    // select() returns the rows that really went, so a delete the database refused isn't shown as done.
    const { data, error } = await supabase.from('saved_reports').delete().eq('id', id).select('id');
    if (error) { notifyError(`Deleting report: ${friendlyDbError(error)}.`); return; }
    if (!data || data.length === 0) { notifyError('Deleting report: it was already gone, or could not be deleted.'); loadSavedReports(); return; }
    setSavedReports(rs => rs.filter(r => r.id !== id));
  };

  // ── Detail panel ──────────────────────────────────────────────────────────
  const [detailClient, setDetailClient] = useState(null); // client object | null
  const [editEntryId, setEditEntryId]   = useState(null);

  // ── Detailed tab sort ─────────────────────────────────────────────────────
  const [detSortKey, setDetSortKey] = useState('date');
  const [detSortDir, setDetSortDir] = useState('desc');
  const toggleDetSort = (k) => { if (detSortKey===k) setDetSortDir(d=>d==='asc'?'desc':'asc'); else { setDetSortKey(k); setDetSortDir(k==='date'?'desc':'asc'); } };
  const detArrow = (k) => detSortKey===k ? (detSortDir==='asc' ? ' ↑' : ' ↓') : '';

  // ── Modals ────────────────────────────────────────────────────────────────
  const [genInvoice, setGenInvoice]   = useState(null);
  const [toast, setToast]             = useState('');
  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(''), 3500); };

  const mBadge = (m) => modelBadge(m);
  const REPORT_COLS = 'minmax(140px,1fr) 80px 104px 96px 96px 96px 126px';
  const mLabel = (m) => modelLabel(m, 'tableLabel');

  // Calendar months the range touches. Work value, invoiced and member pay are
  // all per month (src/metrics.js), so a custom range is summed month by month.
  const rangeMonths = useMemo(() => rangeMode === 'month' ? [selectedMonth]
    : rangeMode === 'ytd' ? monthsBetween(ytdBounds.start, ytdBounds.end)
    : (customStart && customEnd ? monthsBetween(customStart, customEnd) : []),
  [rangeMode, selectedMonth, customStart, customEnd, ytdBounds.start, ytdBounds.end]);

  // ── Per-client computed data ──────────────────────────────────────────────
  const clientData = useMemo(() => clients.filter(cl => !cl.is_internal).map(cl => {
    const ces = rangeEntries.filter(e => {
      if (e.client_id !== cl.id) return false;
      if (filterTaskType && e.task_type_id !== filterTaskType) return false;
      if (filterTagId && !(e.time_entry_tags||[]).some(t => t.tag_id === filterTagId)) return false;
      return true;
    }).sort((a,b) => b.entry_date?.localeCompare(a.entry_date));
    const billableHrs = ces.filter(e => e.is_billable !== false).reduce((s,e) => s + parseFloat(e.hours||0), 0);
    // "owed" is the glossary's Work value: billable hours, rounded per month,
    // priced by the terms in effect that month; retainers count every month.
    const wv          = workValue({ client: cl, entries: ces, months: rangeMonths, terms: billingTerms });
    const roundedHrs  = wv.roundedHours;
    const owed        = wv.total;
    const effectiveRate = BILLING_MODELS[cl.model]?.isFixedMonthly && roundedHrs > 0 ? owed/roundedHrs : null;
    const ovHrs       = Object.values(wv.byMonth).reduce((s, b) => s + overageHours(cl, b.rounded), 0);
    const invoiced    = invoices.filter(i => i.client_id === cl.id && i.status !== 'draft' && rangeMonths.includes(i.period))
      .reduce((s, i) => s + parseFloat(i.total || 0), 0);
    return { cl, ces, billableHrs, roundedHrs, owed, invoiced, effectiveRate, ovHrs };
  }), [clients, rangeEntries, filterTaskType, filterTagId, rangeMonths, billingTerms, invoices]);

  // The filters as one object, shared with the CSV export and the Detailed and Profitability views
  // (src/reportFilters.js), so what is exported is what the filters select.
  const reportFilters = { clientId: filterClientId, taskTypeId: filterTaskType, tagId: filterTagId, model: filterModel, activity: filterActivity };

  // ── Filtered + sorted rows ────────────────────────────────────────────────
  const sortedRows = clientData
    .filter(r => {
      if (search) {
        const q = search.toLowerCase();
        const nameMatch = r.cl.name.toLowerCase().includes(q);
        const taskMatch = r.ces.some(e => e.task_types?.name?.toLowerCase().includes(q));
        const tagMatch  = r.ces.some(e => (e.time_entry_tags||[]).some(t => t.tags?.name?.toLowerCase().includes(q)));
        if (!nameMatch && !taskMatch && !tagMatch) return false;
      }
      if (!clientLevelPasses(r.cl, r.ces, reportFilters)) return false; // client, billing model, activity
      if (filterTaskType && !r.ces.some(e => e.task_type_id === filterTaskType)) return false;
      if (filterTagId && !r.ces.some(e => (e.time_entry_tags||[]).some(t => t.tag_id === filterTagId))) return false;
      if (filterBucket === 'uninvoiced') {
        const hasInvoice = invoices.some(i => i.client_id === r.cl.id && i.period === selectedMonth);
        if (hasInvoice || r.ces.length === 0) return false;
      }
      if (filterBucket === 'overlimit') {
        if (!r.cl.monthly_hours_threshold || r.roundedHrs < r.cl.monthly_hours_threshold * 0.8) return false;
      }
      return true;
    })
    .sort((a,b) => {
      let va, vb;
      if (sortKey === 'name')  { va = a.cl.name.toLowerCase(); vb = b.cl.name.toLowerCase(); }
      if (sortKey === 'hours') { va = a.roundedHrs; vb = b.roundedHrs; }
      if (sortKey === 'owed')  { va = a.owed; vb = b.owed; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'name' ? 'asc' : 'desc'); }
  };
  const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // ── Summary totals: the clients the table below is showing ────────────────
  // The four tiles and the chart describe exactly the rows in the table, so they follow every
  // filter. (They used to cover all clients whatever was filtered.)
  const totalBilled  = sortedRows.reduce((s,r) => s + r.owed, 0);
  const totalHrs     = sortedRows.reduce((s,r) => s + r.roundedHrs, 0);
  const activeCount  = sortedRows.filter(r => r.ces.length > 0).length;
  // "of how many": the clients the client and billing-model filters leave in scope, whether or not
  // they logged time (so the activity filter doesn't shrink its own denominator).
  const scopeClientCount = clients.filter(c => !c.is_internal && clientLevelPasses(c, [], { clientId: filterClientId, model: filterModel })).length;
  // The chart's time entries: the same ones the CSV and the Detailed tab use.
  const reportEntries = useMemo(() => filterReportEntries(rangeEntries, reportFilters, clients),
    [rangeEntries, clients, filterClientId, filterTaskType, filterTagId, filterModel, filterActivity]);

  // ── Overall effective rate (the clients shown — revenue per hour logged this period) ──
  const effectiveHourlyRate = totalHrs > 0 ? totalBilled / totalHrs : null;

  // ── Report buckets (alert-style) ─────────────────────────────────────────
  const largeEntries   = rangeEntries.filter(e => parseFloat(e.hours||0) > 3);
  const missingTask    = rangeEntries.filter(e => !e.task_type_id);
  const unbillable     = rangeEntries.filter(e => e.is_billable === false);
  const unpaid         = rangeEntries.filter(e => e.is_payable === false);
  const uninvoicedClients = clientData.filter(r => r.ces.length > 0 &&
    !invoices.some(i => i.client_id === r.cl.id && i.period === selectedMonth));
  const overLimitClients  = clientData.filter(r =>
    r.cl.monthly_hours_threshold && r.roundedHrs >= r.cl.monthly_hours_threshold * 0.8);

  const REPORT_BUCKETS = [
    { key:'large_entries', label:'Large Entries', count: largeEntries.length,
      desc:'Single entries > 3 hours', color:'#f59e0b', entries: largeEntries },
    { key:'missing_task',  label:'Missing Task Type', count: missingTask.length,
      desc:'Entries with no task type set', color:C.danger, entries: missingTask },
    { key:'unbillable',    label:'Unbillable Work', count: unbillable.length,
      desc:'Entries marked non-billable', color:C.muted, entries: unbillable },
    { key:'unpaid',        label:'Unpaid time', count: unpaid.length,
      desc:'Entries left out of a member’s pay', color:C.muted, entries: unpaid },
    { key:'uninvoiced',    label:'Not Yet Invoiced', count: uninvoicedClients.length,
      desc:'Clients with hours but no invoice', color:C.accent, entries: null, clientRows: uninvoicedClients },
    { key:'overlimit',     label:'Near/Over Threshold', count: overLimitClients.length,
      desc:'At or near monthly hours limit', color:C.danger, entries: null, clientRows: overLimitClients },
  ].filter(b => b.count > 0);

  // ── CSV export ────────────────────────────────────────────────────────────
  const downloadCSV = (rows, filename) => {
    const label = rangeLabel.replace(/[^a-z0-9]/gi,'_');
    const header = ['Date','Team Member','Client','Task Type','Description','Hours','Billable','Paid to member'];
    const data = rows.map(e => [e.entry_date, e.profiles?.name||'', clients.find(c=>c.id===e.client_id)?.name||'', e.task_types?.name||'', e.description||'', parseFloat(e.hours||0).toFixed(2), e.is_billable===false?'No':'Yes', e.is_payable===false?'No':'Yes']);
    const csv = [header,...data].map(r => r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = filename || `TimeKeep_ClientReports_${label}.csv`; a.click();
  };

  // keep detail panel in sync after refresh
  const detailData = detailClient ? clientData.find(r => r.cl.id === detailClient.id) : null;

  // Contractor cost for the selected client (month mode only).
  // Prefer frozen approved statement line_items; fall back to live computeEarnings
  // for contractors whose statement is still open.
  const contractorCost = (() => {
    if (!detailData || rangeMode !== 'month') return null;
    const clId = detailData.cl.id;
    // Restrict to comp-tracked seat types only — same rule as Team Analytics.
    // Excludes admin/owner seat types so their entries don't show as contractor cost.
    const compTracked = new Set(
      orgMembers.filter(m => ['contractor','employee_hourly','employee_salary'].includes(m.seat_type)).map(m => m.user_id)
    );
    const clAssignments = assignments.filter(a => a.client_id === clId && compTracked.has(a.user_id));
    if (clAssignments.length === 0) return null;
    // Build a set of user_ids covered by an approved statement for this period.
    const approvedStmts = statements.filter(s => s.period === selectedMonth && s.status === 'approved');
    const approvedUserIds = new Set(approvedStmts.map(s => s.user_id));
    let cost = 0;
    clAssignments.forEach(a => {
      if (approvedUserIds.has(a.user_id)) {
        // Sum frozen line_items for this client from the approved statement.
        const stmt = approvedStmts.find(s => s.user_id === a.user_id);
        const items = Array.isArray(stmt?.line_items) ? stmt.line_items : [];
        cost += items.filter(li => li.client_id === clId).reduce((s, li) => s + parseFloat(li.line_total || 0), 0);
      } else {
        // Live computation for open periods.
        const ues = detailData.ces.filter(e => e.user_id === a.user_id);
        if (ues.length > 0 || a.pay_type === 'flat_monthly' || a.pay_type === 'hybrid' || a.pay_type === 'percent' || (a.pay_type === 'fixed_project' && a.project_bill_month === selectedMonth)) {
          cost += computeEarnings(a, ues, selectedMonth, detailData.cl).lineTotal;
        }
      }
    });
    return cost > 0 ? cost : null;
  })();

  // Per-client contractor close status (month mode).
  // "Ready to Invoice" fires when all assigned contractors have an APPROVED
  // contractor_statement for the period (or when there are no assignments).
  const closeStatus = (() => {
    if (!detailData || rangeMode !== 'month') return null;
    const clId = detailData.cl.id;
    const assigned = assignments.filter(a => a.client_id === clId);
    // Show close status rows only when there are assignments.
    const rows = assigned.map(a => {
      const u = users.find(u => u.id === a.user_id);
      const lock = (monthLocks||[]).find(l => l.user_id===a.user_id && l.client_id===clId && l.period===selectedMonth && !l.reopened_at);
      const approved = statements.some(s => s.user_id===a.user_id && s.period===selectedMonth && s.status==='approved');
      return { name: u?.name || 'Member', closed: !!lock, closedAt: lock?.locked_at, approved };
    });
    // Ready to Invoice: all contractors approved (or no contractors assigned).
    const readyToInvoice = assigned.length === 0 || rows.every(r => r.approved);
    return assigned.length === 0 ? null : { rows, allClosed: rows.every(r => r.closed), readyToInvoice };
  })();

  // ── Profitability ─────────────────────────────────────────────────────────
  // Member pay from src/metrics.js: approved member-invoice lines first, else an
  // estimate from assignments, checked month by month -- the same figure Org
  // Overview and Team Analytics use. Reimbursements are not pay.
  const pay = useMemo(() => memberPay({ members: orgMembers, assignments, entries: rangeEntries, statements, clients, months: rangeMonths }),
    [orgMembers, assignments, rangeEntries, statements, clients, rangeMonths]);
  const payProvisional = provisionalNote(pay);
  const profitabilityRows = useMemo(() => clientData
      .filter(r => clientLevelPasses(r.cl, r.ces, { clientId: filterClientId, model: filterModel, activity: filterActivity }))
      .filter(r => r.ces.length > 0 || r.owed > 0 || pay.byClient[r.cl.id])
      .map(r => {
        const cost = pay.byClient[r.cl.id] || 0;
        return { cl: r.cl, billed: r.owed, cost, profit: r.owed - cost, margin: marginPct(r.owed, cost), hrs: r.roundedHrs };
      })
      .sort((a, b) => b.billed - a.billed),
  [clientData, pay, filterClientId, filterModel, filterActivity]);
  const profitabilityClientFiltered = !!filterClientId || filterModel !== 'all' || filterActivity !== 'all';
  // Pay for internal (overhead) clients and lines not tied to a client.
  const internalPay = Object.entries(pay.byClient)
    .filter(([cid]) => cid === '_none' || clients.find(c => c.id === cid)?.is_internal)
    .reduce((s, [, v]) => s + v, 0);

  // ── Detailed tab: filtered + sorted flat entry list ───────────────────────
  const detailedEntries = useMemo(() => {
    const rows = [...reportEntries].sort((a, b) => {
      if (detSortKey === 'date')    return detSortDir === 'asc' ? a.entry_date.localeCompare(b.entry_date) : b.entry_date.localeCompare(a.entry_date);
      if (detSortKey === 'client')  { const na = clients.find(c=>c.id===a.client_id)?.name||''; const nb = clients.find(c=>c.id===b.client_id)?.name||''; return detSortDir==='asc'?na.localeCompare(nb):nb.localeCompare(na); }
      if (detSortKey === 'hours')   return detSortDir === 'asc' ? parseFloat(a.hours||0) - parseFloat(b.hours||0) : parseFloat(b.hours||0) - parseFloat(a.hours||0);
      return 0;
    });
    return rows;
  }, [reportEntries, detSortKey, detSortDir, clients]);

  const marginColor = (m) => m === null ? C.muted : m >= 40 ? C.success : m >= 20 ? C.badgeAmber : C.danger;

  // One primary action, and only when it applies: a single client with billable
  // work in range can be invoiced straight from here.
  const reportInvoiceClient = (() => {
    const singleClient = filterClientId
      ? clients.find(c => c.id === filterClientId)
      : sortedRows?.length === 1 ? sortedRows[0].cl : null;
    const hasBillable = singleClient && rangeEntries.filter(e => e.client_id === singleClient.id && e.is_billable !== false).length > 0;
    return singleClient && hasBillable ? singleClient : null;
  })();
  const filtersActive = !!(filterClientId||filterTaskType||filterTagId||filterModel!=='all'||filterActivity!=='all'||filterBucket!=='all');
  const showFilters = reportTab === 'summary' || reportTab === 'detailed' || reportTab === 'profitability';

  // What Export CSV downloads is the time entries the filters select, the same ones the
  // Detailed tab lists. (Insights and My Reports show no filters and work on the whole period,
  // so they export the whole period rather than a hidden, leftover filter.)
  const exportEntries = showFilters ? reportEntries : rangeEntries;
  const exportCsv = () => {
    if (exportEntries.length === 0) {
      showToast(showFilters && filtersActive ? 'Nothing to export: no time entries match these filters in this period.' : 'Nothing to export: there are no time entries in this period.');
      return;
    }
    const suffix = showFilters ? filterFileSuffix(reportFilters, { clients, taskTypes, tags, modelLabel: billingModelLabel }) : '';
    downloadCSV(exportEntries, `TimeKeep_ClientReports_${suffix ? suffix + '_' : ''}${rangeLabel.replace(/[^a-z0-9]/gi,'_')}.csv`);
  };

  return (
    <Page title="Client Reports" onTour={tour.launch}
      tour={{ header: 'reports-header', tabs: 'reports-tabs', controls: 'reports-filters' }}
      primary={reportInvoiceClient ? (
        <button style={css.btn('primary')} onClick={() => setGenInvoice(
          rangeMode === 'month'
            ? { clientId: reportInvoiceClient.id, period: selectedMonth, filterTaskTypeId: filterTaskType || null, filterTagId: filterTagId || null }
            : { clientId: reportInvoiceClient.id, customStart: rangeMode === 'ytd' ? ytdBounds.start : customStart, customEnd: rangeMode === 'ytd' ? ytdBounds.end : customEnd, filterTaskTypeId: filterTaskType || null, filterTagId: filterTagId || null }
        )}>Create invoice</button>
      ) : null}
      actions={<>
        <button style={css.btn('secondary')} onClick={exportCsv}
          title={showFilters && filtersActive ? `Downloads the ${exportEntries.length} time ${exportEntries.length === 1 ? 'entry' : 'entries'} your filters select, for ${rangeLabel}` : `Downloads every time entry in ${rangeLabel}`}>Export CSV</button>
        {showSaveInput ? (
          <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
            <input autoFocus aria-label="Report name" placeholder="Report name…" value={saveReportName}
              onChange={e=>setSaveReportName(e.target.value)}
              onKeyDown={e=>{ if (e.key==='Enter') saveReport(); if (e.key==='Escape') { setShowSaveInput(false); setSaveReportName(''); } }}
              style={{...css.inp,padding:'5px 10px',fontSize:'13px',width:'160px'}} />
            <button style={css.btn('secondary')} onClick={saveReport} disabled={savingReport||!saveReportName.trim()}>Save</button>
            <button style={css.btn('ghost')} onClick={()=>{ setShowSaveInput(false); setSaveReportName(''); }}>Cancel</button>
          </div>
        ) : (
          <button style={css.btn('secondary')} data-tour="reports-save" onClick={()=>setShowSaveInput(true)}>Save report</button>
        )}
      </>}
      tabs={{ tabs: [['summary','Summary'],['detailed','Detailed'],['profitability','Profitability'],['insights','Insights'],['saved',`My Reports${savedReports.length ? ` (${savedReports.length})` : ''}`]], active: reportTab, onChange: setReportTab }}
      controls={<Controls
        filters={showFilters ? <>
          <SearchableSelect options={clients.filter(c=>!c.is_internal).map(c=>({value:c.id,label:c.name}))} value={filterClientId} onChange={setFilterClientId} allLabel="All clients" placeholder="Search clients…" minWidth={160} />
          <SearchableSelect options={taskTypes.map(t=>({value:t.id,label:t.name}))} value={filterTaskType} onChange={setFilterTaskType} allLabel="All task types" placeholder="Search task types…" minWidth={150} />
          <SearchableSelect options={(tags||[]).map(t=>({value:t.id,label:t.name}))} value={filterTagId} onChange={setFilterTagId} allLabel="All tags" placeholder="Search tags…" minWidth={130} />
          <SearchableSelect options={[['hourly'],['retainer'],['hybrid']].map(([k])=>({value:k,label:billingModelLabel(k)}))} value={filterModel==='all'?'':filterModel} onChange={v=>setFilterModel(v||'all')} allLabel="All billing models" placeholder="Search models…" minWidth={150} />
          <SearchableSelect options={[{value:'active',label:'Has activity'},{value:'inactive',label:'No activity'}]} value={filterActivity==='all'?'':filterActivity} onChange={v=>setFilterActivity(v||'all')} allLabel="Any activity" placeholder="Search…" minWidth={130} />
          {filtersActive && (
            <button style={css.btn('ghost')} onClick={()=>{setFilterClientId('');setFilterTaskType('');setFilterTagId('');setFilterModel('all');setFilterActivity('all');setFilterBucket('all');}}>Clear filters</button>
          )}
        </> : null}
        range={<>
          <SegmentedControl options={[['month','Month'],['ytd','YTD'],['custom','Custom']]} value={rangeMode} onChange={setRangeMode} />
          {rangeMode==='month' && (
            <select aria-label="Month" style={{...css.sel,width:'auto',padding:'5px 10px',fontSize:'13px'}} value={selectedMonth} onChange={e=>setSelectedMonth(e.target.value)}>
              {allMonths.map(m=><option key={m} value={m}>{fmtPeriod(m)}</option>)}
            </select>
          )}
          {rangeMode==='custom' && (
            <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
              <input type="date" aria-label="From" style={{...css.inp,padding:'4px 8px',fontSize:'12px',width:'136px'}} value={customStart} onChange={e=>setCustomStart(e.target.value)} />
              <span style={{color:C.muted,fontSize:'12px'}}>to</span>
              <input type="date" aria-label="To" style={{...css.inp,padding:'4px 8px',fontSize:'12px',width:'136px'}} value={customEnd} onChange={e=>setCustomEnd(e.target.value)} />
            </div>
          )}
        </>} />}
      stats={[
        { label:'Active clients', value: activeCount + ' / ' + scopeClientCount, basis:'logged billable time in range',
          tip:'Clients with hours logged in the selected period vs. total non-internal clients. When a client or billing model filter is on, the total counts only the clients it leaves.' },
        { label:'Billable hours', value: fmtHours(totalHrs), basis:'rounded per client per month',
          tip:'Billable hours after billing rounding, which is applied to each client\'s monthly total.' },
        { label:'Work value', value: fmtMoney(totalBilled), basis:'hours × billing terms',
          tip:'What the billable hours in this range are worth under each client\'s billing terms in effect that month. Retainers count in full for every month. It is what you would invoice, not what you did invoice (see the Invoiced column).' },
        { label:'Work value per hour', value: effectiveHourlyRate ? fmtRate(Math.round(effectiveHourlyRate)) : '—', basis: totalHrs > 0 ? `${fmtHours(totalHrs)} billable` : 'no billable hours',
          tip:'Work value ÷ billable hours. Org Overview shows Invoiced per hour, which uses invoices instead.' },
      ]}>

      {tour.show && <WalkthroughModal tourKey="reports" onClose={tour.close} />}

      {toast && <div style={{marginBottom:'12px',padding:'10px 14px',background:'rgba(72,187,120,0.1)',border:'1px solid rgba(72,187,120,0.3)',borderRadius:'8px',color:C.success,fontSize:'13px',flexShrink:0}}>{toast}</div>}

      {/* ── SUMMARY TAB ── */}
      {reportTab === 'summary' && (<>
        <ClientReportsPieChart
          rangeEntries={reportEntries} clients={clients} taskTypes={taskTypes} tags={tags}
          invoices={invoices} clientData={sortedRows} rangeMode={rangeMode} selectedMonth={selectedMonth}
          customStart={customStart} customEnd={customEnd} />

        {/* Report bucket drill-down */}
        {reportBucket && (() => {
        const bucket = REPORT_BUCKETS.find(b => b.key === reportBucket);
        if (!bucket) return null;
        const entries = bucket.entries ?? bucket.clientRows?.flatMap(r => r.ces) ?? [];
        const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));
        const ttMap = Object.fromEntries(taskTypes.map(t => [t.id, t]));
        return (
          <div style={{marginBottom:'12px',border:`1px solid ${bucket.color}`,borderRadius:'8px',overflow:'hidden',flexShrink:0}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 16px',background:`rgba(${bucket.color==='#f59e0b'?'245,158,11':bucket.color===C.danger?'229,62,62':bucket.color===C.accent?'49,167,217':'100,116,139'},0.12)`}}>
              <div>
                <span style={{fontSize:'13px',fontWeight:'700',color:bucket.color}}>{bucket.label}</span>
                <span style={{fontSize:'12px',color:C.muted,marginLeft:'10px'}}>{entries.length} {bucket.entries?'entries':'clients'}</span>
              </div>
              <button onClick={()=>setReportBucket(null)} style={{background:'transparent',border:'none',color:C.muted,fontSize:'16px',cursor:'pointer',padding:'0 4px'}}>✕</button>
            </div>
            <div style={{overflowX:'auto',maxHeight:'260px',overflowY:'auto'}}>
              {bucket.entries !== null ? (
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead>
                    <tr style={{background:C.bg,position:'sticky',top:0}}>
                      {['Date','Member','Client','Task','Description','Hours','Billable','Paid',...(bucket.key==='unpaid' ? ['Why'] : [])].map(h=>(
                        <th key={h} style={{padding:'7px 12px',textAlign:h==='Hours'?'right':'left',color:C.muted,fontWeight:'600',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.4px',borderBottom:`1px solid ${C.border}`}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(e => (
                      <tr key={e.id} style={{borderBottom:`1px solid ${C.border}`}}>
                        <td style={{padding:'7px 12px',color:C.muted,whiteSpace:'nowrap'}}>{e.entry_date}</td>
                        <td style={{padding:'7px 12px',color:C.text,whiteSpace:'nowrap'}}>{e.profiles?.name ?? '—'}</td>
                        <td style={{padding:'7px 12px',color:C.text}}>{clientMap[e.client_id]?.name ?? '—'}</td>
                        <td style={{padding:'7px 12px',color:C.muted}}>{ttMap[e.task_type_id]?.name ?? <span style={{color:C.danger,fontStyle:'italic'}}>None</span>}</td>
                        <td style={{padding:'7px 12px',color:C.text,maxWidth:'220px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.description||'—'}</td>
                        <td style={{padding:'7px 12px',textAlign:'right',color:parseFloat(e.hours||0)>3?bucket.color:C.textBright,fontWeight:'600'}}>{parseFloat(e.hours||0).toFixed(2)}h</td>
                        <td style={{padding:'7px 12px',textAlign:'center'}}>{e.is_billable===false?<span style={{color:C.muted}}>No</span>:<span style={{color:C.accent}}>Yes</span>}</td>
                        <td style={{padding:'7px 12px',textAlign:'center'}}>{e.is_payable===false?<span style={{color:C.badgeAmber}}>No</span>:<span style={{color:C.accent}}>Yes</span>}</td>
                        {bucket.key==='unpaid' && <td style={{padding:'7px 12px',color:C.muted,maxWidth:'200px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={e.payable_note||''}>{e.payable_note||'—'}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead>
                    <tr style={{background:C.bg,position:'sticky',top:0}}>
                      {['Client','Model','Hours','Owed'].map(h=>(
                        <th key={h} style={{padding:'7px 12px',textAlign:h==='Hours'||h==='Owed'?'right':'left',color:C.muted,fontWeight:'600',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.4px',borderBottom:`1px solid ${C.border}`}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(bucket.clientRows??[]).map(r => (
                      <tr key={r.cl.id} style={{borderBottom:`1px solid ${C.border}`,cursor:'pointer'}}
                        onClick={()=>setDetailClient(r.cl)}>
                        <td style={{padding:'7px 12px',color:C.text}}>
                          <span style={{color:C.accent,cursor:'pointer'}} onClick={e=>{e.stopPropagation();onClientClick?.(r.cl.id);}}>{r.cl.name}</span>
                        </td>
                        <td style={{padding:'7px 12px',color:C.muted}}>{billingModelLabel(r.cl.model)}</td>
                        <td style={{padding:'7px 12px',textAlign:'right',color:C.textBright,fontWeight:'600'}}>{r.roundedHrs.toFixed(1)}h</td>
                        <td style={{padding:'7px 12px',textAlign:'right',color:C.accent,fontWeight:'600'}}>{$c(r.owed)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Main layout: table + detail panel ── */}
      <div style={{display:'flex',gap:'16px',flex:1,minHeight:0}}>

        {/* Table */}
        <div style={{flex:1,minWidth:0,overflowY:'auto',borderRadius:'8px',border:`1px solid ${C.border}`}}>
          {/* Column headers */}
          <div style={{display:'grid',gridTemplateColumns:REPORT_COLS,gap:'0',
            padding:'9px 16px',fontSize:'11px',fontWeight:'700',color:C.muted,textTransform:'uppercase',
            letterSpacing:'0.5px',background:C.bg,position:'sticky',top:0,borderBottom:`1px solid ${C.border}`,zIndex:1,maxWidth:'1100px'}}>
            <div style={{cursor:'pointer',userSelect:'none'}} onClick={()=>toggleSort('name')}>Client{sortArrow('name')}</div>
            <div style={{textAlign:'right',cursor:'pointer',userSelect:'none'}} onClick={()=>toggleSort('hours')}>Hours{sortArrow('hours')}</div>
            <div style={{textAlign:'right',cursor:'pointer',userSelect:'none'}} onClick={()=>toggleSort('owed')}><TKTooltip text="Billable hours priced by the client's billing terms in effect each month.">Work value ⓘ</TKTooltip>{sortArrow('owed')}</div>
            <div style={{textAlign:'right'}}><TKTooltip text="Sent and paid invoices whose service month is in this range. Drafts aren't counted.">Invoiced ⓘ</TKTooltip></div>
            <div style={{textAlign:'right'}}><TKTooltip align="left" text="Work value minus invoiced. Positive means work not invoiced yet. Negative means more was invoiced than the hours were worth, e.g. a retainer billed ahead or expenses on the invoice.">Difference ⓘ</TKTooltip></div>
            <div style={{textAlign:'center'}}>Model</div>
            <div style={{textAlign:'right'}}>Actions</div>
          </div>

          {sortedRows.length === 0 && (
            <div style={{padding:'48px',textAlign:'center',color:C.muted,fontSize:'14px'}}>
              {(search || filterClientId || filterTaskType || filterTagId || filterModel!=='all' || filterActivity!=='all' || filterBucket!=='all') ? 'No clients match your filters.' : 'No entries in this period.'}
            </div>
          )}

          {sortedRows.map(r => {
            const isSelected = detailClient?.id === r.cl.id;
            const rateColor = r.effectiveRate === null ? C.textBright
              : org?.default_hourly_rate && r.effectiveRate >= org.default_hourly_rate ? C.accent : C.textBright;
            return (
              <div key={r.cl.id}
                onClick={()=>setDetailClient(isSelected ? null : r.cl)}
                style={{display:'grid',gridTemplateColumns:REPORT_COLS,gap:'0',maxWidth:'1100px',
                  padding:'11px 16px',alignItems:'center',cursor:'pointer',
                  borderBottom:`1px solid ${C.border}`,
                  background: isSelected ? `${C.accent}0f` : 'transparent',
                  borderLeft: isSelected ? `3px solid ${C.accent}` : '3px solid transparent',
                  transition:'background 0.12s'}}>
                <div>
                  <div style={{fontWeight:'600',color:C.textBright,fontSize:'13px'}}>{r.cl.name}</div>
                  <div style={{fontSize:'11px',color:C.muted,marginTop:'2px'}}>
                    {r.ces.length} {r.ces.length===1?'entry':'entries'}
                    {r.effectiveRate !== null && <span style={{marginLeft:'6px',color:rateColor}}> · {fmtRate(Math.round(r.effectiveRate))} eff.</span>}
                    {r.cl.model==='hybrid' && r.ovHrs>0 && <span style={{marginLeft:'6px',color:C.badgeAmber}}> · {fmtHours(r.ovHrs)} overage</span>}
                  </div>
                </div>
                <div style={{textAlign:'right',fontSize:'13px',fontWeight:'600',color:r.roundedHrs>0?C.textBright:C.muted}}>
                  {r.roundedHrs>0 ? fmtHours(r.roundedHrs) : '—'}
                </div>
                <div style={{textAlign:'right',fontSize:'13px',fontWeight:'700',color:r.owed>0?C.textBright:C.muted}}>
                  {r.owed>0 ? fmtMoney(r.owed) : '—'}
                </div>
                <div style={{textAlign:'right',fontSize:'13px',color:r.invoiced>0?C.text:C.muted}}>
                  {r.invoiced>0 ? fmtMoney(r.invoiced) : '—'}
                </div>
                <div style={{textAlign:'right',fontSize:'13px',color:Math.abs(r.owed-r.invoiced)<0.5?C.muted:C.text}}>
                  {Math.abs(r.owed-r.invoiced)<0.5 ? '—' : fmtMoney(r.owed-r.invoiced)}
                </div>
                <div style={{textAlign:'center'}}>
                  <span style={css.badge(mBadge(r.cl.model))}>{mLabel(r.cl.model)}</span>
                </div>
                <div style={{textAlign:'right',display:'flex',justifyContent:'flex-end',gap:'6px'}} onClick={e=>e.stopPropagation()}>
                  {rangeMode==='month' && (() => {
                    const existing = invoices.find(i => i.client_id === r.cl.id && i.period === selectedMonth);
                    if (existing) {
                      const statusColor = existing.status==='paid' ? C.success : existing.status==='sent' ? C.accent : C.muted;
                      const statusLabel = existing.status==='paid' ? 'Paid' : existing.status==='sent' ? 'Sent' : 'Draft';
                      return (
                        <span style={{fontSize:'11px',fontWeight:'600',color:statusColor,
                          padding:'2px 8px',borderRadius:'4px',
                          background:`${statusColor}18`,border:`1px solid ${statusColor}44`,
                          whiteSpace:'nowrap'}}>
                          ✓ {statusLabel}
                        </span>
                      );
                    }
                    if (r.ces.filter(e=>e.is_billable!==false).length > 0) {
                      return (
                        <button style={{...css.smBtn(C.accent),fontSize:'11px'}}
                          onClick={()=>setGenInvoice({clientId:r.cl.id,period:selectedMonth,filterTaskTypeId:filterTaskType||null,filterTagId:filterTagId||null})}>
                          Invoice
                        </button>
                      );
                    }
                    return null;
                  })()}
                  {rangeMode==='custom' && customStart && customEnd && r.ces.filter(e=>e.is_billable!==false).length > 0 && (
                    <button style={{...css.smBtn(C.accent),fontSize:'11px'}}
                      onClick={()=>setGenInvoice({clientId:r.cl.id,customStart,customEnd,filterTaskTypeId:filterTaskType||null,filterTagId:filterTagId||null})}>
                      Invoice
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail panel */}
        {detailData && (
          <div style={{position:'relative',width:`${panelWidth}px`,flexShrink:0,display:'flex',flexDirection:'column',
            background:C.surface,border:`1px solid ${C.border}`,borderRadius:'8px',overflow:'hidden'}}>
            {/* Drag handle — grab the left edge to resize the panel */}
            <div onMouseDown={startResize} title="Drag to resize"
              style={{position:'absolute',top:0,bottom:0,left:'-5px',width:'10px',cursor:'ew-resize',zIndex:5}}>
              <div style={{position:'absolute',top:0,bottom:0,left:'4px',width:'2px',background:'transparent'}} />
            </div>
            {/* Panel header */}
            <div style={{padding:'14px 18px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexShrink:0}}>
              <div>
                <div style={{fontWeight:'700',color:C.textBright,fontSize:'15px'}}>{detailData.cl.name}</div>
                <div style={{fontSize:'12px',color:C.muted,marginTop:'2px'}}>{mLabel(detailData.cl.model)} · {rangeLabel}</div>
              </div>
              <button onClick={()=>setDetailClient(null)} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:'18px',lineHeight:1,padding:'0 0 0 8px'}}>×</button>
            </div>

            {/* Metrics */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr'+(detailData.effectiveRate!==null?' 1fr':''),gap:'1px',background:C.border,flexShrink:0}}>
              {[
                {label:'Hours', value:detailData.roundedHrs.toFixed(2)+'h'},
                {label:'Work value', value:fmtMoney(detailData.owed)},
                ...(detailData.effectiveRate!==null ? [{label:'Eff. Rate', value:'$'+Math.round(detailData.effectiveRate)+'/hr',
                  accent: org?.default_hourly_rate ? detailData.effectiveRate >= org.default_hourly_rate : false}] : []),
              ].map(s=>(
                <div key={s.label} style={{padding:'12px 16px',background:C.surface}}>
                  <div style={css.lbl}>{s.label}</div>
                  <div style={{fontSize:'17px',fontWeight:'700',color:s.accent?C.accent:C.textBright,marginTop:'2px'}}>{s.value}</div>
                </div>
              ))}
            </div>
            {detailData.cl.model==='hybrid' && (
              <div style={{padding:'8px 16px',fontSize:'12px',color:C.muted,borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
                {$c(detailData.cl.retainer)} base · {detailData.roundedHrs.toFixed(2)}/{detailData.cl.hours}h included
                {detailData.ovHrs>0 ? ` · ${detailData.ovHrs.toFixed(2)}h overage @ ${$c(detailData.cl.overage)}/hr` : ' · no overage'}
              </div>
            )}
            {contractorCost !== null && (
              <div style={{padding:'8px 16px',fontSize:'12px',color:C.textDim,borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
                Contractor cost: <strong style={{color:C.textBright}}>{$c(contractorCost)}</strong>
                {detailData.owed>0 && <span style={{color:C.muted}}> ({Math.round(contractorCost/detailData.owed*100)}% of billed)</span>}
              </div>
            )}
            {closeStatus && (
              <div style={{padding:'8px 16px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
                <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'4px'}}>
                  <span style={{...css.lbl,marginBottom:0}}>Member close</span>
                  {closeStatus.readyToInvoice && <span style={{...css.badge('green'),fontSize:'10px'}}>Ready to Invoice</span>}
                </div>
                {closeStatus.rows.map((r,i) => (
                  <div key={i} style={{fontSize:'12px',color:C.textDim}}>
                    {r.name}: {r.approved
                      ? <span style={{color:C.success}}>approved ✓</span>
                      : r.closed
                        ? <span style={{color:'#a8d8a8'}}>closed (pending approval)</span>
                        : <span style={{color:'#f6c26b'}}>open</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Retainer / Hybrid utilization bar */}
            {(detailData.cl.model==='retainer' || detailData.cl.model==='hybrid') && (
              <div style={{padding:'10px 16px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
                <div style={{fontSize:'11px',color:C.muted,fontWeight:'600',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'6px'}}>Included Hours</div>
                {detailData.cl.hours > 0 ? (() => {
                  const included = detailData.cl.hours;
                  const used = detailData.roundedHrs;
                  const pct = Math.min(used / included, 1);
                  const over = Math.max(0, used - included);
                  const overAmt = detailData.cl.model==='hybrid' ? over * (detailData.cl.overage||0) : 0;
                  const barColor = over > 0 ? C.danger : C.accent;
                  return (
                    <div>
                      <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                        <div style={{flex:1,height:'8px',background:'rgba(255,255,255,0.08)',borderRadius:'4px',overflow:'hidden'}}>
                          <div style={{width:`${pct*100}%`,height:'100%',background:barColor,borderRadius:'4px',transition:'width 0.3s'}} />
                        </div>
                        <div style={{fontSize:'12px',color:C.textBright,fontWeight:'600',whiteSpace:'nowrap'}}>
                          {used.toFixed(1)}h of {included}h
                          <span style={{color:C.muted,fontWeight:'400'}}> ({Math.round(pct*100)}%)</span>
                        </div>
                      </div>
                      {over > 0 && (
                        <div style={{fontSize:'11px',color:C.danger,marginTop:'4px'}}>
                          +{over.toFixed(1)}h overage{overAmt > 0 ? ` · ${$c(overAmt)} additional` : ''}
                        </div>
                      )}
                    </div>
                  );
                })() : (
                  <div style={{fontSize:'12px',color:C.muted}}>Flat monthly fee — no included hours configured</div>
                )}
              </div>
            )}

            {/* Invoice history */}
            {(() => {
              const clInvoices = (invoices||[])
                .filter(i => i.client_id === detailData.cl.id)
                .sort((a,b) => (b.issued_date||'').localeCompare(a.issued_date||''))
                .slice(0, 5);
              const sBadgeColor = s => s==='paid'?C.success:s==='sent'?C.accent:'#f6c26b';
              return (
                <div style={{padding:'10px 16px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'6px'}}>
                    <div style={{fontSize:'11px',color:C.muted,fontWeight:'600',textTransform:'uppercase',letterSpacing:'0.05em'}}>Recent Invoices</div>
                    {onNavigate && (invoices||[]).filter(i=>i.client_id===detailData.cl.id).length>0 && (
                      <button style={{...css.smBtn(C.muted),fontSize:'10px',padding:'2px 7px'}} onClick={()=>onNavigate('invoices')}>View all →</button>
                    )}
                  </div>
                  {clInvoices.length === 0 ? (
                    <div style={{fontSize:'12px',color:C.muted}}>No invoices yet</div>
                  ) : (
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                      <tbody>
                        {clInvoices.map(inv => (
                          <tr key={inv.id} style={{cursor:'pointer',borderBottom:`1px solid rgba(30,39,54,0.4)`}}
                            onClick={()=>onNavigate&&onNavigate('invoices')}
                            title="Go to Invoices">
                            <td style={{padding:'5px 4px',color:C.textBright,fontWeight:'600'}}>{inv.invoice_number}</td>
                            <td style={{padding:'5px 4px',color:C.muted}}>{inv.issued_date||'—'}</td>
                            <td style={{padding:'5px 4px'}}>
                              <span style={{fontSize:'10px',fontWeight:'700',color:sBadgeColor(inv.status),textTransform:'uppercase'}}>
                                {inv.status}
                              </span>
                            </td>
                            <td style={{padding:'5px 4px',textAlign:'right',fontWeight:'700',color:C.accent}}>{$c(parseFloat(inv.total||0))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })()}

            {/* Actions */}
            <div style={{padding:'10px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',gap:'8px',flexShrink:0}}>
              <button style={{...css.smBtn(C.muted),fontSize:'12px'}} onClick={()=>downloadCSV(detailData.ces, `TimeKeep_${detailData.cl.name.replace(/\s+/g,'_')}_${rangeLabel.replace(/[^a-z0-9]/gi,'_')}.csv`)}>Export CSV</button>
              {rangeMode==='month' && (detailData.ces.filter(e=>e.is_billable!==false).length>0 || detailData.cl.model==='retainer' || detailData.cl.model==='hybrid') && (() => {
                const readyToInvoice = closeStatus?.readyToInvoice || detailData.cl.model==='retainer';
                const approvedStmtsForClient = statements.filter(s =>
                  s.period === selectedMonth && s.status === 'approved' &&
                  Array.isArray(s.line_items) && s.line_items.some(li => li.client_id === detailData.cl.id)
                );
                return readyToInvoice && approvedStmtsForClient.length > 0 ? (
                  <button style={{...css.smBtn(C.success),fontSize:'12px'}} title="Pre-seeds invoice from approved contractor hours at client billing rate"
                    onClick={()=>setGenInvoice({clientId:detailData.cl.id,period:selectedMonth,approvedStmts:approvedStmtsForClient,client:detailData.cl})}>
                    Generate from Approved Hours
                  </button>
                ) : (
                  <button style={{...css.smBtn(C.accent),fontSize:'12px'}} onClick={()=>setGenInvoice({clientId:detailData.cl.id,period:selectedMonth})}>Generate Invoice</button>
                );
              })()}
            </div>

            {/* Entry list */}
            <div style={{flex:1,overflowY:'auto'}}>
              {detailData.ces.length===0 ? (
                <div style={{padding:'32px',textAlign:'center',color:C.muted,fontSize:'13px'}}>No entries in this period.</div>
              ) : (
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead>
                    <tr style={{background:C.bg,position:'sticky',top:0}}>
                      <th style={css.th}>Date</th>
                      <th style={css.th}>Member</th>
                      <th style={css.th}>Description</th>
                      <th style={css.th}>Task</th>
                      <th style={{...css.th,textAlign:'right'}}>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.ces.map(ent => {
                      const periodKey = ent.entry_date?.slice(0,7);
                      const isLocked = (monthLocks||[]).some(l=>l.user_id===ent.user_id&&l.client_id===detailData.cl.id&&l.period===periodKey&&!l.reopened_at);
                      return editEntryId===ent.id
                        ? <EntryEditRow key={ent.id} entry={ent} clients={clients} taskTypes={taskTypes||[]} colSpan={5}
                            onSave={()=>{setEditEntryId(null);showToast('Entry updated.');onRefresh();}}
                            onCancel={()=>setEditEntryId(null)} />
                        : <tr key={ent.id} style={{cursor:isLocked?'default':'pointer',opacity:ent.is_billable===false?0.6:1}}
                            title={isLocked?'Locked':'Click to edit'} onClick={()=>{if(!isLocked)setEditEntryId(ent.id);}}>
                            <td style={css.td}>{ent.entry_date}{fmtTimeRange(ent.start_time,ent.end_time) && <div style={{fontSize:"10px",color:C.muted,marginTop:"2px"}}>{fmtTimeRange(ent.start_time,ent.end_time)}</div>}</td>
                            <td style={css.td}>{ent.profiles?.name||'—'}</td>
                            <td style={{...css.td,maxWidth:'160px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={ent.description||''}>{ent.description || <span style={{color:C.muted,fontStyle:'italic'}}>—</span>}</td>
                            <td style={css.td}>{ent.task_types?.name?<span style={css.tag}>{ent.task_types.name}</span>:'—'}<TagChips tagLinks={ent.time_entry_tags} /></td>
                            <td style={{...css.td,textAlign:'right',fontWeight:'600',color:C.textBright}}>
                              {formatExactTime(parseFloat(ent.hours||0))}
                              <EntryBadges entry={ent} ownerName={ent.profiles?.name} viewerIsOwner={false} />
                              {isLocked && <span style={{marginLeft:'4px',fontSize:'10px',color:C.muted}}>🔒</span>}
                            </td>
                          </tr>;
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{...css.td,fontWeight:'600',color:C.textDim,fontSize:'11px',textTransform:'uppercase'}}>Billable Total</td>
                      <td style={{...css.td,textAlign:'right',fontWeight:'700',color:C.accent}}>{detailData.roundedHrs.toFixed(2)}h</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      </>)}

      {/* ── DETAILED TAB ── */}
      {reportTab === 'detailed' && (<>
        <div style={{display:'flex',gap:'8px',marginBottom:'12px',flexWrap:'wrap',flexShrink:0,alignItems:'center'}}>
          <SearchableSelect options={clients.filter(c=>!c.is_internal).map(c=>({value:c.id,label:c.name}))} value={filterClientId} onChange={setFilterClientId} allLabel="All Clients" placeholder="Search clients…" minWidth={160} />
          <SearchableSelect options={taskTypes.map(t=>({value:t.id,label:t.name}))} value={filterTaskType} onChange={setFilterTaskType} allLabel="All Task Types" placeholder="Search task types…" minWidth={160} />
          <SearchableSelect options={(tags||[]).map(t=>({value:t.id,label:t.name}))} value={filterTagId} onChange={setFilterTagId} allLabel="All Tags" placeholder="Search tags…" minWidth={140} />
          {(filterClientId||filterTaskType||filterTagId) && (
            <button style={css.smBtn(C.muted)} onClick={()=>{setFilterClientId('');setFilterTaskType('');setFilterTagId('');}}>Clear</button>
          )}
          <span style={{fontSize:'12px',color:C.muted,marginLeft:'4px'}}>{detailedEntries.length} entries</span>
        </div>
        <div style={{flex:1,minHeight:0,overflowY:'auto',borderRadius:'8px',border:`1px solid ${C.border}`}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
            <thead>
              <tr style={{background:C.bg,position:'sticky',top:0,zIndex:1}}>
                {[['date','Date'],['client','Client'],['task','Task Type'],['desc','Description'],['hours','Hours'],['bill','Billable']].map(([k,lb])=>(
                  <th key={k} onClick={['date','client','hours'].includes(k)?()=>toggleDetSort(k):undefined}
                    style={{padding:'8px 12px',textAlign:k==='hours'?'right':'left',color:C.muted,fontWeight:'600',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.4px',borderBottom:`1px solid ${C.border}`,cursor:['date','client','hours'].includes(k)?'pointer':'default',userSelect:'none',whiteSpace:'nowrap'}}>
                    {lb}{['date','client','hours'].includes(k) && detArrow(k)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detailedEntries.length === 0 && (
                <tr><td colSpan={6} style={{padding:'48px',textAlign:'center',color:C.muted}}>No entries match the selected filters.</td></tr>
              )}
              {detailedEntries.map(e=>{
                const cl = clients.find(c=>c.id===e.client_id);
                const tt = taskTypes.find(t=>t.id===e.task_type_id);
                return (
                  <tr key={e.id} style={{borderBottom:`1px solid ${C.border}`,opacity:e.is_billable===false?0.7:1}}>
                    <td style={{padding:'8px 12px',color:C.muted,whiteSpace:'nowrap'}}>{e.entry_date}</td>
                    <td style={{padding:'8px 12px',color:C.textBright,fontWeight:'600'}}>{cl?.name||'—'}</td>
                    <td style={{padding:'8px 12px'}}>{tt ? <span style={css.tag}>{tt.name}</span> : <span style={{color:C.muted}}>—</span>}</td>
                    <td style={{padding:'8px 12px',color:C.text,maxWidth:'260px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.description||<span style={{color:C.muted}}>—</span>}</td>
                    <td style={{padding:'8px 12px',textAlign:'right',fontWeight:'700',color:C.textBright,whiteSpace:'nowrap'}}>{parseFloat(e.hours||0).toFixed(2)}h</td>
                    <td style={{padding:'8px 12px',textAlign:'center'}}>{e.is_billable===false?<span style={{color:C.muted,fontSize:'11px'}}>No</span>:<span style={{color:C.accent,fontSize:'11px'}}>Yes</span>}</td>
                  </tr>
                );
              })}
            </tbody>
            {detailedEntries.length > 0 && (
              <tfoot>
                <tr style={{borderTop:`1px solid ${C.border}`,background:C.bg}}>
                  <td colSpan={4} style={{padding:'8px 12px',fontSize:'11px',fontWeight:'700',color:C.muted,textTransform:'uppercase'}}>Total</td>
                  <td style={{padding:'8px 12px',textAlign:'right',fontWeight:'700',color:C.accent}}>{detailedEntries.reduce((s,e)=>s+parseFloat(e.hours||0),0).toFixed(2)}h</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </>)}

      {/* ── PROFITABILITY TAB ── */}
      {reportTab === 'profitability' && (<>
        {payProvisional && (
          <div style={{padding:'10px 14px',background:`${C.badgeAmber}14`,border:`1px solid ${C.badgeAmber}4d`,borderRadius:'8px',marginBottom:'12px',fontSize:'13px',color:C.badgeAmber}}>
            Provisional · {payProvisional}. Member pay is estimated from assignments until each member invoice is approved.
          </div>
        )}
        <div style={{flex:1,minHeight:0,overflowY:'auto',borderRadius:'8px',border:`1px solid ${C.border}`}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',fontVariantNumeric:'tabular-nums'}}>
            <thead>
              <tr style={{background:C.bg,position:'sticky',top:0,zIndex:1}}>
                {[
                  ['Client', null],
                  ['Hours', 'Billable hours, rounded per client per month.'],
                  ['Work value', "Billable hours priced by each client's billing terms in effect that month. Retainers count in full for every month."],
                  ['Member pay', 'Approved member invoice lines for each month, or an estimate from assignments where not approved yet. Excludes expense reimbursements.'],
                  ['Profit', 'Work value − Member pay.'],
                  ['Margin', '(Work value − Member pay) ÷ Work value.'],
                ].map(([h,tip],i)=>(
                  <th key={h} style={{padding:'9px 14px',textAlign:i===0?'left':'right',color:C.muted,fontWeight:'600',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.4px',borderBottom:`1px solid ${C.border}`}}>
                    {tip ? <TKTooltip text={tip} align={i>3?'left':'right'}>{h} ⓘ</TKTooltip> : h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {profitabilityRows.length === 0 && (
                <tr><td colSpan={6} style={{padding:'48px',textAlign:'center',color:C.muted}}>No client data for this period.</td></tr>
              )}
              {profitabilityRows.map(r=>(
                <tr key={r.cl.id} style={{borderBottom:`1px solid ${C.border}`,cursor:'pointer'}} onClick={()=>{setDetailClient(r.cl);setReportTab('summary');}}>
                  <td style={{padding:'10px 14px'}}>
                    <div style={{fontWeight:'600',color:C.textBright}}>{r.cl.name}</div>
                    <div style={{fontSize:'11px',color:C.muted,marginTop:'1px'}}>{billingModelLabel(r.cl.model)}</div>
                  </td>
                  <td style={{padding:'10px 14px',textAlign:'right',color:C.text}}>{fmtHours(r.hrs)}</td>
                  <td style={{padding:'10px 14px',textAlign:'right',fontWeight:'700',color:C.textBright}}>{fmtMoney(r.billed)}</td>
                  <td style={{padding:'10px 14px',textAlign:'right',color:r.cost>0?C.text:C.muted}}>{r.cost>0?fmtMoney(r.cost):'—'}</td>
                  <td style={{padding:'10px 14px',textAlign:'right',fontWeight:'600',color:r.profit>=0?C.success:C.danger}}>{fmtMoney(r.profit)}</td>
                  <td style={{padding:'10px 14px',textAlign:'right'}}>
                    {r.margin===null ? <span style={{color:C.muted}}>—</span> : (
                      <span style={{fontWeight:'700',color:marginColor(r.margin)}}>{fmtPercent(r.margin)}</span>
                    )}
                  </td>
                </tr>
              ))}
              {internalPay > 0 && !profitabilityClientFiltered && (
                <tr style={{borderBottom:`1px solid ${C.border}`}}>
                  <td style={{padding:'10px 14px',color:C.textDim}}><TKTooltip text="Member pay for internal (overhead) clients and pay lines not tied to a client. It's a real cost, so it's in the total, but no client pays for it.">Internal work ⓘ</TKTooltip></td>
                  <td /><td />
                  <td style={{padding:'10px 14px',textAlign:'right',color:C.text}}>{fmtMoney(internalPay)}</td>
                  <td style={{padding:'10px 14px',textAlign:'right',color:C.danger}}>{fmtMoney(-internalPay)}</td>
                  <td />
                </tr>
              )}
            </tbody>
            {profitabilityRows.length > 0 && (()=>{
              const tb = profitabilityRows.reduce((s,r)=>s+r.billed,0);
              // Org-wide member pay, unless the client, billing model or activity filter has narrowed the
              // rows, in which case the total is the pay for the rows shown (internal work is not one of them).
              const tc = profitabilityClientFiltered ? profitabilityRows.reduce((s,r)=>s+r.cost,0) : pay.total;
              const tp = tb - tc;
              const tm = marginPct(tb, tc);
              return (
                <tfoot>
                  <tr style={{borderTop:`2px solid ${C.border}`,background:C.bg}}>
                    <td style={{padding:'10px 14px',fontWeight:'700',color:C.muted,fontSize:'11px',textTransform:'uppercase'}}>Total</td>
                    <td style={{padding:'10px 14px',textAlign:'right',fontWeight:'700',color:C.textBright}}>{fmtHours(profitabilityRows.reduce((s,r)=>s+r.hrs,0))}</td>
                    <td style={{padding:'10px 14px',textAlign:'right',fontWeight:'700',color:C.textBright}}>{fmtMoney(tb)}</td>
                    <td style={{padding:'10px 14px',textAlign:'right',fontWeight:'700',color:C.text}}>{tc>0?fmtMoney(tc):'—'}</td>
                    <td style={{padding:'10px 14px',textAlign:'right',fontWeight:'700',color:tp>=0?C.success:C.danger}}>{fmtMoney(tp)}</td>
                    <td style={{padding:'10px 14px',textAlign:'right',fontWeight:'700',color:marginColor(tm)}}>{fmtPercent(tm)}</td>
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>
      </>)}

      {/* ── INSIGHTS TAB ── */}
      {reportTab === 'insights' && (<>
        <AiInsightsPanel rangeEntries={rangeEntries} clients={clients} taskTypes={taskTypes} org={org} period={rangeMode==='month'?selectedMonth:null} />
        {REPORT_BUCKETS.length > 0 && (
          <div style={{display:'flex',gap:'8px',marginBottom:'12px',flexWrap:'wrap',flexShrink:0}}>
            {REPORT_BUCKETS.map(b=>(
              <div key={b.key} onClick={()=>setReportBucket(reportBucket===b.key?null:b.key)}
                style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 14px',borderRadius:'8px',cursor:'pointer',
                  border:`1px solid ${reportBucket===b.key?b.color:'rgba(255,255,255,0.08)'}`,
                  background:reportBucket===b.key?`rgba(${b.color==='#f59e0b'?'245,158,11':b.color===C.danger?'229,62,62':b.color===C.accent?'49,167,217':'100,116,139'},0.1)`:'rgba(255,255,255,0.03)',
                  transition:'all 0.15s'}}>
                <span style={{fontSize:'20px',fontWeight:'700',color:b.color,lineHeight:1}}>{b.count}</span>
                <div>
                  <div style={{fontSize:'12px',fontWeight:'600',color:b.color}}>{b.label}</div>
                  <div style={{fontSize:'11px',color:C.muted}}>{b.desc}</div>
                </div>
                {reportBucket===b.key && <span style={{fontSize:'11px',color:b.color,marginLeft:'4px'}}>✕</span>}
              </div>
            ))}
          </div>
        )}
        {reportBucket && (() => {
          const bucket = REPORT_BUCKETS.find(b => b.key === reportBucket);
          if (!bucket) return null;
          const bEntries = bucket.entries ?? bucket.clientRows?.flatMap(r => r.ces) ?? [];
          const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));
          const ttMap = Object.fromEntries(taskTypes.map(t => [t.id, t]));
          return (
            <div style={{marginBottom:'12px',border:`1px solid ${bucket.color}`,borderRadius:'8px',overflow:'hidden',flexShrink:0}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 16px',background:`rgba(${bucket.color==='#f59e0b'?'245,158,11':bucket.color===C.danger?'229,62,62':bucket.color===C.accent?'49,167,217':'100,116,139'},0.12)`}}>
                <div><span style={{fontSize:'13px',fontWeight:'700',color:bucket.color}}>{bucket.label}</span><span style={{fontSize:'12px',color:C.muted,marginLeft:'10px'}}>{bEntries.length} {bucket.entries?'entries':'clients'}</span></div>
                <button onClick={()=>setReportBucket(null)} style={{background:'transparent',border:'none',color:C.muted,fontSize:'16px',cursor:'pointer',padding:'0 4px'}}>✕</button>
              </div>
              <div style={{overflowX:'auto',maxHeight:'300px',overflowY:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead>
                    <tr style={{background:C.bg,position:'sticky',top:0}}>
                      {(bucket.entries!==null?['Date','Member','Client','Task','Description','Hours','Billable','Paid',...(bucket.key==='unpaid' ? ['Why'] : [])]:['Client','Model','Hours','Owed']).map(h=>(
                        <th key={h} style={{padding:'7px 12px',textAlign:['Hours','Owed'].includes(h)?'right':'left',color:C.muted,fontWeight:'600',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.4px',borderBottom:`1px solid ${C.border}`}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bucket.entries!==null ? bEntries.map(e=>(
                      <tr key={e.id} style={{borderBottom:`1px solid ${C.border}`}}>
                        <td style={{padding:'7px 12px',color:C.muted,whiteSpace:'nowrap'}}>{e.entry_date}</td>
                        <td style={{padding:'7px 12px',color:C.text,whiteSpace:'nowrap'}}>{e.profiles?.name??'—'}</td>
                        <td style={{padding:'7px 12px',color:C.text}}>{clientMap[e.client_id]?.name??'—'}</td>
                        <td style={{padding:'7px 12px',color:C.muted}}>{ttMap[e.task_type_id]?.name??<span style={{color:C.danger,fontStyle:'italic'}}>None</span>}</td>
                        <td style={{padding:'7px 12px',color:C.text,maxWidth:'220px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.description||'—'}</td>
                        <td style={{padding:'7px 12px',textAlign:'right',color:parseFloat(e.hours||0)>3?bucket.color:C.textBright,fontWeight:'600'}}>{parseFloat(e.hours||0).toFixed(2)}h</td>
                        <td style={{padding:'7px 12px',textAlign:'center'}}>{e.is_billable===false?<span style={{color:C.muted}}>No</span>:<span style={{color:C.accent}}>Yes</span>}</td>
                        <td style={{padding:'7px 12px',textAlign:'center'}}>{e.is_payable===false?<span style={{color:C.badgeAmber}}>No</span>:<span style={{color:C.accent}}>Yes</span>}</td>
                        {bucket.key==='unpaid' && <td style={{padding:'7px 12px',color:C.muted,maxWidth:'200px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={e.payable_note||''}>{e.payable_note||'—'}</td>}
                      </tr>
                    )) : (bucket.clientRows??[]).map(r=>(
                      <tr key={r.cl.id} style={{borderBottom:`1px solid ${C.border}`,cursor:'pointer'}} onClick={()=>{setDetailClient(r.cl);setReportTab('summary');}}>
                        <td style={{padding:'7px 12px',color:C.text}}>
                          <span style={{color:C.accent,cursor:'pointer'}} onClick={e=>{e.stopPropagation();onClientClick?.(r.cl.id);}}>{r.cl.name}</span>
                        </td>
                        <td style={{padding:'7px 12px',color:C.muted}}>{billingModelLabel(r.cl.model)}</td>
                        <td style={{padding:'7px 12px',textAlign:'right',color:C.textBright,fontWeight:'600'}}>{r.roundedHrs.toFixed(1)}h</td>
                        <td style={{padding:'7px 12px',textAlign:'right',color:C.accent,fontWeight:'600'}}>{$c(r.owed)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
        {REPORT_BUCKETS.length === 0 && rangeEntries.length > 0 && (
          <div style={{padding:'32px',textAlign:'center',color:C.success,fontSize:'14px',border:`1px solid rgba(104,211,145,0.3)`,borderRadius:'8px',background:'rgba(104,211,145,0.05)'}}>
            ✓ No billing issues found for this period.
          </div>
        )}
        {rangeEntries.length === 0 && (
          <div style={{padding:'32px',textAlign:'center',color:C.muted,fontSize:'14px'}}>No entries in this period.</div>
        )}
      </>)}

      {/* ── MY REPORTS TAB ── */}
      {reportTab === 'saved' && (
        <div style={{flex:1,overflowY:'auto'}}>
          {savedReports.length === 0 ? (
            <div style={{padding:'48px',textAlign:'center',color:C.muted,fontSize:'14px'}}>
              <div style={{fontSize:'32px',marginBottom:'12px'}}>📋</div>
              <div style={{fontWeight:'600',color:C.text,marginBottom:'6px'}}>No saved reports yet</div>
              <div>Configure filters and a date range, then click <strong>Save Report</strong> to save it here.</div>
            </div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:'10px',paddingBottom:'16px'}}>
              {savedReports.map(r => {
                const clientName = r.filter_client_id ? clients.find(c => c.id === r.filter_client_id)?.name : null;
                const taskName   = r.filter_task_type_id ? taskTypes.find(t => t.id === r.filter_task_type_id)?.name : null;
                const tagName    = r.filter_tag_id ? (tags||[]).find(t => t.id === r.filter_tag_id)?.name : null;
                const rangeLabel = r.range_mode === 'month' && r.selected_month
                  ? fmtPeriod(r.selected_month)
                  : r.range_mode === 'ytd' ? 'Year to date'
                  : r.custom_start && r.custom_end ? `${fmtDate(r.custom_start)} – ${fmtDate(r.custom_end)}` : 'Custom range';
                const chips = [
                  clientName && { label: clientName, icon: '👤' },
                  taskName   && { label: taskName,   icon: '🏷️' },
                  tagName    && { label: tagName,     icon: '🔖' },
                  r.filter_model !== 'all'    && { label: billingModelLabel(r.filter_model) },
                  r.filter_activity !== 'all' && { label: r.filter_activity === 'active' ? 'Has Activity' : 'No Activity' },
                  r.report_tab !== 'summary'  && { label: r.report_tab.charAt(0).toUpperCase() + r.report_tab.slice(1) + ' view' },
                ].filter(Boolean);
                return (
                  <div key={r.id} style={{...css.card,display:'flex',alignItems:'center',gap:'14px',padding:'14px 18px',cursor:'pointer',transition:'border-color 0.15s',borderColor:'rgba(255,255,255,0.07)'}}
                    onClick={()=>{ loadReport(r); }}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=C.accent}
                    onMouseLeave={e=>e.currentTarget.style.borderColor='rgba(255,255,255,0.07)'}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:'700',color:C.textBright,fontSize:'14px',marginBottom:'4px'}}>{r.name}</div>
                      <div style={{fontSize:'12px',color:C.accent,fontWeight:'600',marginBottom:'5px'}}>{rangeLabel}</div>
                      {chips.length > 0 && (
                        <div style={{display:'flex',gap:'5px',flexWrap:'wrap'}}>
                          {chips.map((ch,i) => (
                            <span key={i} style={{fontSize:'11px',padding:'2px 8px',borderRadius:'12px',background:'rgba(255,255,255,0.07)',color:C.muted}}>
                              {ch.icon && ch.icon + ' '}{ch.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{display:'flex',gap:'8px',flexShrink:0}}>
                      <button style={css.btn('primary')} onClick={e=>{e.stopPropagation();loadReport(r);}}>Load</button>
                      <button style={{...css.smBtn(C.muted),fontSize:'13px',padding:'5px 10px'}} onClick={e=>{e.stopPropagation();deleteReport(r.id);}}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Invoice Editor Modal ── */}
      {genInvoice && (
        <InvoiceEditorModal org={org} clients={clients} taskTypes={taskTypes} entries={entries} monthLocks={monthLocks}
          initialClientId={genInvoice.clientId} initialPeriod={genInvoice.period}
          initialCustomStart={genInvoice.customStart} initialCustomEnd={genInvoice.customEnd}
          initialFilterTaskTypeId={genInvoice.filterTaskTypeId} initialFilterTagId={genInvoice.filterTagId}
          approvedStmts={genInvoice.approvedStmts}
          onClose={()=>setGenInvoice(null)} onSaved={()=>{setGenInvoice(null);onRefresh();}} />
      )}

    </Page>
  );
}

// ─── TEAM ─────────────────────────────────────────────────────────────────────
function TeamView({ users, clients, assignments, pendingInvites, orgMembers=[], entries=[], profile, org, monthLocks, statements=[], billingTerms=null, onRefresh }) {
  const [showInvite, setShowInvite] = useState(false);
  const tour = useTour('team');
  const defaultCompRate = org?.default_compensation_rate ?? null;
  // One tab level (Page System spec): Members, then the three analytics views.
  const [teamTab, setTeamTab]         = useState('members');
  const [editId, setEditId]           = useState(null);
  const [editName, setEditName]       = useState('');
  // Permission check updated Phase 1A — role model cleanup
  const [editRole, setEditRole]       = useState('member');
  const [editSeatType, setEditSeatType] = useState('contractor');
  const [editCompRate, setEditCompRate] = useState(0);
  const [editCapacity, setEditCapacity] = useState('');
  const [editRecipients, setEditRecipients] = useState('');
  const [editExpensesEnabled, setEditExpensesEnabled] = useState(true);
  const [invEmail, setInvEmail]       = useState('');
  const [invRole, setInvRole]         = useState('member');
  const [invSeatType, setInvSeatType] = useState('contractor');
  const [invLink, setInvLink]         = useState('');
  const [invErr, setInvErr]           = useState('');
  const [copied, setCopied]           = useState(false);
  const [copiedInvId, setCopiedInvId] = useState(null);
  const [invSendEmail, setInvSendEmail] = useState(true);  // false = create the link only; the admin sends it themselves
  const [invEmailed, setInvEmailed]   = useState(true);    // whether the link currently shown in the Invite panel was also emailed
  const [localInvites, setLocalInvites] = useState(null);
  const [teamSearch, setTeamSearch] = useState('');
  const [memberStatus, setMemberStatus] = useState('active'); // Members pills: active | offboarded | all. Active is the default: off-boarded people stay out of the way until asked for.
  const [memberSeat, setMemberSeat] = useState('');   // seat type filter, '' = all
  const [memberRole, setMemberRole] = useState('');   // role filter, '' = all
  const [teamNotice, setTeamNotice] = useState(null);  // {text, showOff, link} confirmation after off-board / undo / reactivate / renew. A notice carrying a link stays until dismissed: it is the only copy on screen.
  useEffect(() => { if (!teamNotice || teamNotice.link) return undefined; const t = setTimeout(() => setTeamNotice(null), 9000); return () => clearTimeout(t); }, [teamNotice]);
  const [offboardModal,  setOffboardModal]  = useState(null); // {user, endDate, endReason}
  const [deleteUserModal, setDeleteUserModal] = useState(null); // {user}
  const [endAssignmentModal, setEndAssignmentModal] = useState(null); // {assignment, client, endDate}
  const [deleteAssignmentModal, setDeleteAssignmentModal] = useState(null); // {assignment, client}
  const [reactivateModal, setReactivateModal] = useState(null); // {user, endedMember, startDate, mode: 'undo' | 'reactivate'}
  const [priorTenureCheck, setPriorTenureCheck] = useState(null); // {name, ended_at, user_id, role, seat_type}
  // Static 24-month rolling picker — avoids deriving from windowed data
  const allPeriods = useMemo(() => {
    const periods = [];
    const d = new Date();
    for (let i = 0; i < 24; i++) {
      periods.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
      d.setMonth(d.getMonth()-1);
    }
    return periods;
  }, []);
  const [selectedPeriod, setSelectedPeriod] = useState(curMonth);

  const openEdit = async (u) => {
    setEditId(u.id); setEditName(u.name); setEditRole(u.role);
    setEditSeatType('contractor');
    setEditCompRate(0);
    setEditCapacity('');
    setEditRecipients('');
    setEditExpensesEnabled(true);
    const { data: member } = await supabase.from('org_members')
      .select('compensation_rate, seat_type, role, capacity_hours_week, invoice_recipient_emails, expenses_enabled')
      .eq('user_id', u.id).eq('org_id', org.id).is('ended_at', null).single();
    if (member) {
      setEditSeatType(member.seat_type || 'contractor');
      if (member.role) setEditRole(member.role);
      setEditCompRate(member.compensation_rate || 0);
      setEditCapacity(member.capacity_hours_week ?? '');
      setEditRecipients(member.invoice_recipient_emails ?? '');
      setEditExpensesEnabled(member.expenses_enabled !== false);
    }
  };
  const saveUser = async () => {
    const okProfile = await runDb('Saving member failed', supabase.rpc('admin_update_profile', { target_id: editId, new_name: editName, new_role: editRole, new_seat_type: editSeatType, p_org_id: org.id }));
    const okMember = await runDb('Saving member rate/capacity failed', supabase.from('org_members')
      .update({ compensation_rate: editCompRate, capacity_hours_week: editCapacity === '' ? null : parseFloat(editCapacity) || null, invoice_recipient_emails: editRecipients.trim() === '' ? null : editRecipients.trim(), expenses_enabled: editExpensesEnabled })
      .eq('user_id', editId).eq('org_id', org.id));
    if (okProfile && okMember) setEditId(null);
    onRefresh();
  };
  const today = new Date().toISOString().slice(0,10);

  const memberHasEntries  = (uid) => entries.some(e => e.user_id === uid);
  // One row per person for the Members table: their active tenure, else their latest one.
  const memberRows = users.map(u => {
    const active = orgMembers.find(m => m.user_id === u.id && !m.ended_at);
    const endedMember = active ? null : [...orgMembers].filter(m=>m.user_id===u.id&&m.ended_at).sort((a,b)=>new Date(b.ended_at)-new Date(a.ended_at))[0] || null;
    const m = active || endedMember;
    return { u, m, isActive: !!active, endedMember, role: m?.role, ua: assignments.filter(a=>a.user_id===u.id && !a.ended_at) };
  }).filter(r => r.m);
  // Search, seat and role narrow the list; the Active / Off-boarded / All pills then pick a status
  // from what is left, and count it.
  const scopedMemberRows = memberRows.filter(r => {
    if (memberSeat && r.m?.seat_type !== memberSeat) return false;
    if (memberRole && r.m?.role !== memberRole) return false;
    const q = teamSearch.trim().toLowerCase();
    return !q || r.u.name?.toLowerCase().includes(q) || r.u.email?.toLowerCase().includes(q);
  });
  const visibleMemberRows = scopedMemberRows.filter(r => memberStatus === 'all' || (memberStatus === 'active') === r.isActive);
  const memberFiltersOn = !!(teamSearch.trim() || memberSeat || memberRole);
  // Bringing someone back: a mistake is usually caught soon, so an off-boarding in the last 30 days
  // opens on "Undo", anything older on "Reactivate". The dialog always offers both.
  const openBringBack = (r, mode) => setReactivateModal({
    user: r.u, endedMember: r.endedMember, startDate: today,
    mode: mode || (r.endedMember?.ended_at && Date.now() - new Date(r.endedMember.ended_at).getTime() < 30*86400000 ? 'undo' : 'reactivate'),
  });
  const memberMonthKey = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();
  const memberMonthHours = {};
  entries.forEach(e => { if (e.entry_date?.slice(0,7) === memberMonthKey) memberMonthHours[e.user_id] = (memberMonthHours[e.user_id]||0) + parseFloat(e.hours||0); });
  const assignHasEntries  = (a)   => entries.some(e => e.user_id === a.user_id && e.client_id === a.client_id);

  const delUser = async (userId) => {
    const ok = await runDb('Delete failed', supabase.rpc('admin_delete_profile', { target_id: userId, p_org_id: org.id }));
    if (ok) setDeleteUserModal(null);
    onRefresh();
  };
  const nameOf = (userId) => users.find(u => u.id === userId)?.name || 'They';
  const offboardMember = async (userId, endedAt, endReason) => {
    const ok = await runDb('Off-board failed', supabase.rpc('offboard_member', { p_org_id: org.id, p_user_id: userId, p_ended_at: new Date(endedAt).toISOString(), p_end_reason: endReason || null }));
    if (ok) {
      setOffboardModal(null); if (editId === userId) setEditId(null);
      setTeamNotice({ text: `${nameOf(userId)} was off-boarded. Their time entries and history are kept, and you can bring them back from the Off-boarded filter.`, showOff: true });
    }
    onRefresh();
  };
  // A returning member: a NEW period on the chosen date. The database carries their previous role's
  // pay settings over; client assignments are not restored.
  const reactivateMember = async (userId, startDate) => {
    const endedRow = [...orgMembers].filter(m => m.user_id === userId && m.ended_at).sort((a,b) => new Date(b.ended_at) - new Date(a.ended_at))[0];
    const ok = await runDb('Reactivation failed', supabase.rpc('reactivate_member', { p_org_id: org.id, p_user_id: userId, p_started_at: new Date(startDate).toISOString(), p_role: endedRow?.role || 'member', p_seat_type: endedRow?.seat_type || 'contractor' }));
    if (ok) {
      setReactivateModal(null); setMemberStatus('active');
      setTeamNotice({ text: `${nameOf(userId)} is active again from ${fmtDate(startDate)}, with their previous role, seat and pay settings. Assign their clients again when they are ready.` });
    }
    onRefresh();
  };
  // A mistake: the SAME membership is put back, with the client assignments that ended with it.
  const undoOffboard = async (userId) => {
    const { data, error } = await supabase.rpc('undo_offboard_member', { p_org_id: org.id, p_user_id: userId });
    if (error) { notifyError(`Undo off-boarding failed: ${friendlyDbError(error)}.`); return; }
    setReactivateModal(null); setMemberStatus('active');
    setTeamNotice({ text: `${nameOf(userId)}'s off-boarding was undone${data ? `, with ${data} client assignment${data === 1 ? '' : 's'} restored` : ''}.` });
    onRefresh();
  };
  const addAssignment = async (uid, cid, fields = {}) => {
    if (!cid) return;
    const n = (v) => (v == null || v === '' ? null : parseFloat(v) || 0);
    const payload = {
      org_id: org.id, user_id: uid, client_id: cid,
      pay_type: fields.pay_type || 'hourly',
      rate: n(fields.rate) || 0,
      monthly_amount: n(fields.monthly_amount) || 0,
      project_amount: n(fields.project_amount) || 0,
      project_bill_month: fields.project_bill_month || null,
      pay_percent: n(fields.pay_percent),
      hybrid_base: n(fields.hybrid_base),
      hybrid_included_hours: n(fields.hybrid_included_hours),
      hybrid_overage_rate: n(fields.hybrid_overage_rate),
    };
    await runDb('Adding assignment failed', supabase.from('assignments').insert(payload));
    onRefresh();
  };
  const endAssignment = async (assignmentId, endedAt) => {
    const ok = await runDb('End contract failed', supabase.rpc('end_assignment', { p_assignment_id: assignmentId, p_ended_at: new Date(endedAt).toISOString() }));
    if (ok) setEndAssignmentModal(null);
    onRefresh();
  };
  const deleteAssignment = async (assignmentId) => {
    const ok = await runDb('Delete failed', supabase.rpc('delete_assignment', { p_assignment_id: assignmentId }));
    if (ok) setDeleteAssignmentModal(null);
    onRefresh();
  };
  const updateRate = async (aid, rate) => {
    await runDb('Updating rate failed', supabase.from('assignments').update({rate:parseFloat(rate)||0}).eq('id',aid));
    onRefresh();
  };
  const updateAssignment = async (aid, patch) => {
    await runDb('Updating assignment failed', supabase.from('assignments').update(patch).eq('id',aid));
    onRefresh();
  };

  const resetLock = async (lock) => {
    // Reopen = mark the lock row reopened (audit trail kept) and clear any stale
    // locked flag, via the SECURITY DEFINER RPC.
    const ok = await runDb('Reset failed', supabase.rpc('reopen_client_month',
      { p_org: org.id, p_user: lock.user_id, p_client: lock.client_id, p_period: lock.period }));
    if (ok) {
      // Any confirmed statement covering this period is now invalid: void it.
      await supabase.from('contractor_statements').update({ status:'void' })
        .eq('org_id', org.id).eq('user_id', lock.user_id).eq('period', lock.period).in('status', ['submitted','approved','confirmed']);
    }
    onRefresh();
  };

  const newInviteToken = () => Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map(b => b.toString(16).padStart(2,'0')).join('');
  const emailInvite = (email, role, seatType, url) => supabase.functions.invoke('send-invitation', {
    body: { invitee_email: email, org_id: org.id, org_name: org.name, role, seat_type: seatType, invite_url: url },
  }).catch(console.error);

  // Creates the invitation. With sendEmail false nothing is emailed from here: the admin gets the link and sends it themselves.
  const createInvite = async ({ email, role, seatType, sendEmail }) => {
    const token = newInviteToken();
    const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await supabase.from('pending_invitations').insert({
      org_id: org.id, email, role, seat_type: seatType, token, expires_at: expiresAt, invited_by: profile.id, status: 'pending',
    });
    if (error) { setInvErr(error.message); return false; }
    const inviteUrl = buildInviteUrl(token);
    setInvLink(inviteUrl);
    setInvEmailed(sendEmail);
    setInvEmail('');
    if (sendEmail) emailInvite(email, role, seatType, inviteUrl);
    onRefresh();
    return true;
  };

  const sendInvite = async () => {
    setInvErr('');
    if (!invEmail.trim()) { setInvErr('Email is required.'); return; }
    // Check for prior tenure before sending an invitation.
    const { data: prior } = await supabase.rpc('find_prior_tenure', { p_org_id: org.id, p_email: invEmail.trim() });
    if (prior && prior.length > 0) {
      setPriorTenureCheck({ ...prior[0], startDate: today, email: invEmail.trim() });
      return;
    }
    await createInvite({ email: invEmail.trim(), role: invRole, seatType: invSeatType, sendEmail: invSendEmail });
  };

  const revokeInvite = async (id) => {
    // Optimistically remove from local view so the row disappears immediately.
    setLocalInvites((localInvites ?? pendingInvites).filter(i => i.id !== id));
    const { error } = await supabase.from('pending_invitations').update({ status: 'revoked' }).eq('id', id);
    if (error) {
      // Roll back optimistic removal and surface the error.
      setLocalInvites(null);
      setInvErr(error.message);
      return;
    }
    if (invLink) setInvLink('');
    onRefresh();
  };

  const dismissInvite = async (id) => {
    await runDb('Dismissing invitation failed', supabase.from('pending_invitations').delete().eq('id', id));
    onRefresh();
  };

  const copyLink = () => {
    navigator.clipboard.writeText(invLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyInvLink = (token) => {
    const url = buildInviteUrl(token);
    navigator.clipboard.writeText(url);
    setCopiedInvId(token);
    setTimeout(() => setCopiedInvId(null), 2000);
  };

  // Renews an expired invitation with a fresh token and a new 7 days. sendEmail false emails nobody: the new link is
  // copied and shown so the admin can send it themselves.
  const renewInvite = async (inv, sendEmail = true) => {
    const token = newInviteToken();
    const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    const patch = { token, expires_at: expiresAt, status: 'pending' };
    // The old email's delivery status describes a link that no longer works, so a link-only renewal must not keep showing it.
    if (!sendEmail) Object.assign(patch, { email_id: null, email_status: null, email_status_at: null });
    const ok = await runDb('Renewing invitation failed', supabase.from('pending_invitations').update(patch).eq('id', inv.id));
    if (!ok) return;
    const inviteUrl = buildInviteUrl(token);
    if (sendEmail) {
      emailInvite(inv.email, inv.role, inv.seat_type, inviteUrl);
    } else {
      let copiedNow = false;
      try { await navigator.clipboard.writeText(inviteUrl); copiedNow = true; } catch (e) { /* the box in the notice still has it */ }
      setTeamNotice({ text: `New link for ${inv.email}${copiedNow ? ' (copied)' : ''}. Nothing was emailed; send it to them yourself. It works for 7 days.`, link: inviteUrl });
    }
    onRefresh();
  };

  const seatLabel = (s) => ({ owner:'Owner', admin:'Admin', employee_hourly:'Employee (Hourly)', employee_salary:'Employee (Salary)', contractor:'Contractor' }[s] || s);

  return (
    <Page title="Team" onTour={tour.launch}
      primary={<button style={css.btn('primary')} onClick={()=>setShowInvite(true)}>+ Invite member</button>}
      tabs={{ tabs: [['members','Members'],['utilization','Utilization'],['margin','Margin'],['planner','Rate Planner']], active: teamTab, onChange: setTeamTab }}
      tour={{ tabs: 'team-tabs' }}>
      {tour.show && <WalkthroughModal tourKey="team" onClose={tour.close} />}
      <DetailPanel open={showInvite} onClose={()=>setShowInvite(false)} title="Invite a team member"
        subtitle="They can get an email with a link to join, or you can copy the link and send it yourself. If this email belonged to someone who left, you'll be offered Reactivate instead.">
        <div style={{display:"grid",gridTemplateColumns:invRole==='employee'?"1fr 1fr 1fr":"1fr 1fr",gap:"10px",marginBottom:"10px"}}>
          <div>
            <div style={css.lbl}><TKTooltip text="The person must sign up with this same email. If 'Email them the invitation' is on, the invitation link is sent here.">Email ⓘ</TKTooltip></div>
            <input style={css.inp} type="email" placeholder="name@example.com" value={invEmail}
              onChange={e=>setInvEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendInvite()} />
          </div>
          <div>
            <div style={css.lbl}><TKTooltip text="Admin: full access including invoices, reports, team, and settings. Member: timer and own logs only.">Role ⓘ</TKTooltip></div>
            {/* Permission check updated Phase 1A — role model cleanup */}
            <select style={css.sel} value={invRole} onChange={e=>{
              const r = e.target.value;
              setInvRole(r);
              if (r==='member') setInvSeatType('contractor');
              else if (r==='admin') setInvSeatType('admin');
              else if (r==='employee') setInvSeatType('employee_hourly');
            }}>
              <option value="member">Contractor</option>
              <option value="employee">Employee</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {invRole==='employee' && (
            <div>
              <div style={css.lbl}><TKTooltip text="Hourly employees are compensated per hour logged. Salary employees receive a fixed annual amount and are excluded from per-hour margin calculations.">Employment Type ⓘ</TKTooltip></div>
              <select style={css.sel} value={invSeatType} onChange={e=>setInvSeatType(e.target.value)}>
                <option value="employee_hourly">Hourly</option>
                <option value="employee_salary">Salary</option>
              </select>
            </div>
          )}
        </div>
        <label style={{display:"flex",alignItems:"center",gap:"8px",cursor:"pointer",marginBottom:"12px"}}>
          <input type="checkbox" checked={invSendEmail} onChange={e=>setInvSendEmail(e.target.checked)} style={{accentColor:C.accent}} />
          <span style={css.lbl}><TKTooltip text="On: TimeKeep emails the invitation link for you. Off: nothing is emailed from TimeKeep; you copy the link and send it yourself, in your own email or message. The link works for 7 days either way, and you can revoke it under Pending invitations.">Email them the invitation ⓘ</TKTooltip></span>
        </label>
        {invErr && <div style={{fontSize:"12px",color:C.danger,marginBottom:"8px"}}>{invErr}</div>}
        <button style={css.btn("primary")} onClick={sendInvite}>{invSendEmail ? 'Send Invite' : 'Create invite link'}</button>
        {invLink && (
          <div style={{marginTop:"14px",padding:"12px",background:"rgba(49,167,217,0.06)",border:`1px solid rgba(49,167,217,0.2)`,borderRadius:"8px"}}>
            <div style={{fontSize:"11px",color:C.muted,marginBottom:"6px",textTransform:"uppercase",letterSpacing:"0.5px"}}>Invite link — expires in 7 days</div>
            <div style={{fontSize:"12px",color:C.textDim,marginBottom:"8px"}}>{invEmailed ? 'We also emailed this link to them.' : 'Nothing was emailed. Copy this link and send it to them yourself.'}</div>
            <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
              <input readOnly value={invLink} style={{...css.inp,flex:1,fontSize:"11px",color:C.textDim}} />
              <button style={css.btn(copied?"ghost":"primary")} onClick={copyLink}>{copied?"Copied!":"Copy"}</button>
            </div>
          </div>
        )}
      </DetailPanel>

      {teamTab!=='members' && <TeamAnalyticsView view={teamTab} entries={entries} clients={clients} assignments={assignments} orgMembers={orgMembers} users={users} org={org} statements={statements} billingTerms={billingTerms} />}
      {teamTab==='members' && <>
      {teamNotice && (
        <div role="status" style={{display:'flex',flexDirection:'column',gap:'8px',padding:'10px 14px',background:'rgba(72,187,120,0.1)',border:'1px solid rgba(72,187,120,0.3)',borderRadius:'8px',color:C.success,fontSize:'13px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
            <span style={{flex:1}}>{teamNotice.text}</span>
            {teamNotice.showOff && <button style={css.btn('ghost')} onClick={()=>{ setMemberStatus('offboarded'); setTeamNotice(null); }}>Show off-boarded</button>}
            <button aria-label="Dismiss" style={{background:'none',border:'none',color:C.success,cursor:'pointer',fontSize:'16px',padding:'0 4px'}} onClick={()=>setTeamNotice(null)}>×</button>
          </div>
          {teamNotice.link && (
            <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
              <input readOnly value={teamNotice.link} aria-label="Invite link" onFocus={e=>e.target.select()} style={{...css.inp,flex:1,fontSize:'11px',color:C.textDim}} />
              <button style={css.btn(copied?'ghost':'primary')} onClick={async()=>{ try { await navigator.clipboard.writeText(teamNotice.link); setCopied(true); setTimeout(()=>setCopied(false),2000); } catch (e) { /* select-and-copy by hand still works */ } }}>{copied?'Copied!':'Copy'}</button>
            </div>
          )}
        </div>
      )}
      <Controls
        pills={<StatusPills value={memberStatus} onChange={setMemberStatus} options={[
          { key:'active', label:'Active', count: scopedMemberRows.filter(r=>r.isActive).length },
          { key:'offboarded', label:'Off-boarded', count: scopedMemberRows.filter(r=>!r.isActive).length },
          { key:'all', label:'All', count: scopedMemberRows.length },
        ]} />}
        search={<SearchInput value={teamSearch} onChange={setTeamSearch} placeholder="Search team members…" />}
        filters={<>
          <SearchableSelect options={['owner','admin','contractor','employee_hourly','employee_salary'].filter(k => memberRows.some(r => r.m?.seat_type === k)).map(k=>({value:k,label:seatLabel(k)}))}
            value={memberSeat} onChange={setMemberSeat} allLabel="All seats" placeholder="Search seats…" minWidth={140} />
          <SearchableSelect options={['owner','admin','member'].filter(k => memberRows.some(r => r.m?.role === k)).map(k=>({value:k,label:roleBadgeLabel(k)}))}
            value={memberRole} onChange={setMemberRole} allLabel="All roles" placeholder="Search roles…" minWidth={130} />
          {memberFiltersOn && <button style={css.btn('ghost')} onClick={()=>{ setTeamSearch(''); setMemberSeat(''); setMemberRole(''); }}>Clear filters</button>}
        </>} />
      <div data-tour="team-members">
        <DataTable
          rows={visibleMemberRows}
          rowKey={r => r.u.id}
          onRowClick={r => r.isActive ? openEdit(r.u) : openBringBack(r)}
          selectedKey={editId}
          columns={[
            { key:'name', label:'Name', sortValue:r=>(r.u.name||'').toLowerCase(),
              render:r=>(<span>{r.u.name}{r.u.email && <span style={{display:'block',color:C.muted,fontWeight:'400',fontSize:'12px'}}>{r.u.email}</span>}</span>) },
            { key:'role', label:'Role', sortValue:r=>roleBadgeLabel(r.role)||'',
              render:r=>r.role ? <StatusBadge tone={['admin','owner'].includes(r.role) ? 'info' : 'neutral'}>{roleBadgeLabel(r.role)}</StatusBadge> : '—' },
            { key:'seat', label:'Seat', tip:'How this person is paid. Contractor and employee seats count toward member pay and margin; owner and admin seats don\'t.',
              sortValue:r=>seatLabel(r.m?.seat_type)||'', render:r=>seatLabel(r.m?.seat_type)||'—' },
            { key:'clients', label:'Clients', align:'right', tip:'Active client assignments. Employees are paid a fixed rate and have none.',
              sortValue:r=>r.ua.length,
              render:r=>r.ua.length ? <span title={r.ua.map(a=>clients.find(c=>c.id===a.client_id)?.name).filter(Boolean).join(', ')}>{r.ua.length}</span> : <span style={{color:C.muted}}>—</span> },
            { key:'pay', label:'Pay', sortValue:r=>parseFloat(r.m?.compensation_rate||0),
              render:r=>{ const m=r.m; if (!m) return '—';
                if (m.seat_type==='employee_hourly' && m.compensation_rate) return fmtRate(m.compensation_rate);
                if (m.seat_type==='employee_salary' && m.compensation_rate) return fmtRate(m.compensation_rate,'yr');
                if (m.seat_type==='contractor') return <span style={{color:C.muted}}>{r.ua.length ? 'Per assignment' : '—'}</span>;
                return <span style={{color:C.muted}}>—</span>; } },
            { key:'hours', label:'This month', align:'right', tip:'Hours this person has logged so far this calendar month, across all clients.',
              sortValue:r=>memberMonthHours[r.u.id]||0,
              render:r=>memberMonthHours[r.u.id] ? fmtHours(memberMonthHours[r.u.id]) : <span style={{color:C.muted}}>—</span> },
            { key:'capacity', label:'Capacity', align:'right', sortValue:r=>parseFloat(r.m?.capacity_hours_week||0),
              render:r=>r.m?.capacity_hours_week ? `${r.m.capacity_hours_week}h/wk` : <span style={{color:C.muted}}>—</span> },
            { key:'status', label:'Status', sortValue:r=>r.isActive?0:1,
              render:r=>r.isActive ? <StatusBadge tone="good">Active</StatusBadge> : <StatusBadge tone="neutral">Off-boarded{r.endedMember?.ended_at ? ` ${fmtDate(r.endedMember.ended_at)}` : ''}</StatusBadge> },
          ]}
          rowActions={r => r.isActive ? [
            { label:'Edit', onClick:()=>openEdit(r.u) },
            { label:'Off-board', onClick:()=>setOffboardModal({user:r.u, endDate:today, endReason:''}) },
            ...(!memberHasEntries(r.u.id) ? [{ label:'Delete', danger:true, onClick:()=>setDeleteUserModal({user:r.u}) }] : []),
          ] : [
            { label:'Undo off-boarding', onClick:()=>openBringBack(r, 'undo') },
            { label:'Reactivate', onClick:()=>openBringBack(r, 'reactivate') },
          ]}
          empty={<EmptyState title={memberFiltersOn ? 'No team members match' : memberStatus === 'offboarded' ? 'No one has been off-boarded' : 'No team members yet'}
            body={memberFiltersOn ? 'Nothing matches these filters.' : memberStatus === 'offboarded' ? 'When you off-board someone they move here, and you can bring them back.' : 'Invite someone with + Invite member.'} />}
        />
      </div>

      {/* Member editor */}
      {(() => {
        const r = editId ? memberRows.find(x => x.u.id === editId) : null;
        if (!r) return null;
        const u = r.u, ua = r.ua;
        return (
          <DetailPanel open onClose={()=>setEditId(null)} title={u.name}
            badges={r.role && <StatusBadge tone={['admin','owner'].includes(r.role) ? 'info' : 'neutral'}>{roleBadgeLabel(r.role)}</StatusBadge>}
            subtitle={u.email} width={680}>
            <div>
                  <div style={{...css.g2,marginBottom:"14px"}}>
                    <div><div style={css.lbl}>Name</div><input style={css.inp} value={editName} onChange={e=>setEditName(e.target.value)} /></div>
                    <div>
                      <div style={css.lbl}><TKTooltip text="Admin: full access to all features including invoices, reports, and team management. Member: can only log time and view their own entries.">Permission Level ⓘ</TKTooltip></div>
                      {editRole==='owner'
                        ? <div style={{...css.inp,color:C.muted,cursor:'not-allowed',userSelect:'none'}}>Owner (cannot change)</div>
                        : <select style={css.sel} value={editRole} onChange={e=>setEditRole(e.target.value)}>
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                      }
                    </div>
                    <div>
                      <div style={css.lbl}><TKTooltip text="Controls how this member's compensation is calculated. Contractor, Employee (Hourly), and Employee (Salary) appear in margin analytics. Owner and Admin seat types are excluded from comp calculations.">Seat Type ⓘ</TKTooltip></div>
                      {editRole==='owner'
                        ? <div style={{...css.inp,color:C.muted,cursor:'not-allowed',userSelect:'none'}}>Owner</div>
                        : <select style={css.sel} value={editSeatType} onChange={e=>setEditSeatType(e.target.value)}>
                            <option value="contractor">Contractor</option>
                            <option value="employee_hourly">Employee (Hourly)</option>
                            <option value="employee_salary">Employee (Salary)</option>
                          </select>
                      }
                    </div>
                  </div>
                  {editSeatType==='employee_hourly' && (
                    <div style={{marginBottom:"14px"}}>
                      <div style={css.lbl}><TKTooltip text="This member's hourly compensation rate. Used to calculate gross profit and margin % in Team Analytics.">Hourly Rate ($) ⓘ</TKTooltip></div>
                      <input style={css.inp} type="number" min="0" step="0.01" value={editCompRate} onChange={e=>setEditCompRate(parseFloat(e.target.value)||0)} />
                      <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>~${Math.round(editCompRate*40*52).toLocaleString()}/yr est. based on 40hr week</div>
                    </div>
                  )}
                  {editSeatType==='employee_salary' && (
                    <div style={{marginBottom:"14px"}}>
                      <div style={css.lbl}><TKTooltip text="Full annual salary used to compute monthly comp ($salary ÷ 12) and margin. Not shown to the member.">Annual Salary ($) ⓘ</TKTooltip></div>
                      <input style={css.inp} type="number" min="0" step="1" value={editCompRate} onChange={e=>setEditCompRate(parseFloat(e.target.value)||0)} />
                      <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>${Math.round(editCompRate/12).toLocaleString()}/mo</div>
                    </div>
                  )}
                  {['employee_hourly','employee_salary'].includes(editSeatType) ? (
                    <div style={{marginBottom:"14px",fontSize:"12px",color:C.muted,fontStyle:"italic",padding:"8px 10px",background:C.bg,borderRadius:"6px",border:`1px solid ${C.border}`}}>
                      Employees are compensated at a fixed rate, not per client assignment.
                    </div>
                  ) : (
                    <div style={{marginBottom:"14px"}}>
                      <div style={{...css.lbl,marginBottom:"8px"}}>Client Assignments</div>
                      {ua.length===0 && <div style={{fontSize:"12px",color:C.muted,marginBottom:"8px"}}>No clients assigned yet.</div>}
                      {ua.map(a=>{
                        const cl=clients.find(c=>c.id===a.client_id);
                        return cl?(
                          <div key={a.id} style={{display:"flex",alignItems:"flex-start",gap:"8px",marginBottom:"6px",padding:"8px 10px",background:C.bg,borderRadius:"6px",border:`1px solid ${C.border}`,flexWrap:"wrap"}}>
                            <span style={{flex:1,minWidth:"110px",fontSize:"13px",color:C.textBright,paddingTop:"6px"}}>{cl.name}</span>
                            <PayStructureEditor value={a} client={cl} onChange={patch=>updateAssignment(a.id, patch)} />
                            <button style={{...css.smBtn(C.accent),marginTop:"4px"}} onClick={()=>setEndAssignmentModal({assignment:a, client:cl, endDate:today})}>End Contract</button>
                            {!assignHasEntries(a) && <button style={{...css.smBtn(C.danger),marginTop:"4px"}} onClick={()=>setDeleteAssignmentModal({assignment:a, client:cl})}>Delete</button>}
                          </div>
                        ):null;
                      })}
                      <AddAssignmentRow clients={clients.filter(c=>c.is_active!==false)} existing={ua.map(a=>a.client_id)} onAdd={(cid,fields)=>addAssignment(u.id,cid,fields)} defaultRate={defaultCompRate} />
                    </div>
                  )}
                  <div style={{marginBottom:"14px",paddingTop:"10px",borderTop:`1px solid ${C.border}`}}>
                    <div style={css.lbl}><TKTooltip text="This member's available hours per week. Used to calculate utilization percentage in Team Analytics. Leave blank if capacity is not tracked.">Weekly Capacity (hours) ⓘ</TKTooltip></div>
                    <input style={{...css.inp,width:"120px"}} type="number" min="0" max="168" step="0.5"
                      placeholder="e.g. 40" value={editCapacity}
                      onChange={e=>setEditCapacity(e.target.value)} />
                    <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>
                      Used for utilization % in Team Analytics. Leave blank if not applicable.
                    </div>
                  </div>
                  <div style={{marginBottom:"14px"}}>
                    <div style={css.lbl}><TKTooltip text="Per-member AP email for receiving their invoices. Overrides the org-level Recipient Email setting. Leave blank to use the org default.">Invoice Recipient Override ⓘ</TKTooltip></div>
                    <input style={css.inp} placeholder="Leave blank to use the org default"
                      value={editRecipients} onChange={e=>setEditRecipients(e.target.value)} />
                    <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>
                      Where this member's approved invoice is emailed. Overrides Settings → Billing → Invoices. Comma or semicolon separated.
                    </div>
                  </div>
                  <div style={{marginBottom:"14px",paddingTop:"10px",borderTop:`1px solid ${C.border}`}}>
                    <label style={{display:"flex",alignItems:"center",gap:"8px",cursor:"pointer"}}>
                      <input type="checkbox" checked={editExpensesEnabled} onChange={e=>setEditExpensesEnabled(e.target.checked)} />
                      <span style={css.lbl}><TKTooltip text="When off, this member cannot log expenses — the Expenses tab is hidden for them and the server rejects any attempt to log one, so this can't be bypassed from outside the app.">Expense logging enabled ⓘ</TKTooltip></span>
                    </label>
                    {!editExpensesEnabled && (
                      <div style={{fontSize:"11px",color:C.badgeAmber,marginTop:"4px",marginLeft:"24px"}}>
                        Existing expenses are unaffected — this only blocks logging new ones.
                      </div>
                    )}
                  </div>
                  {CONTRACTS_ENABLED && <MemberContracts userId={u.id} orgId={org?.id} C={C} css={css} />}
                  <div style={{display:"flex",gap:"8px"}}>
                    <button style={css.btn("primary")} onClick={saveUser}>Save</button>
                    <button style={css.btn("secondary")} onClick={()=>setEditId(null)}>Cancel</button>
                  </div>
            </div>
          </DetailPanel>
        );
      })()}

      {(localInvites ?? pendingInvites).length > 0 && (
        <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
          <div style={css.h2}>Pending invitations</div>
          <DataTable
            rows={localInvites ?? pendingInvites}
            columns={[
              { key:'email', label:'Email', sortValue:i=>i.email },
              { key:'seat', label:'Seat', sortValue:i=>seatLabel(i.seat_type), render:i=>seatLabel(i.seat_type) },
              { key:'sent', label:'Sent', sortValue:i=>i.created_at, render:i=>fmtDate(i.created_at) },
              { key:'expires', label:'Expires', sortValue:i=>i.expires_at,
                render:i=>{ const x = new Date(i.expires_at) < new Date(); return <span style={{color:x?C.danger:C.textDim}}>{fmtDate(i.expires_at)}</span>; } },
              { key:'status', label:'Status', render:i=> new Date(i.expires_at) < new Date() ? <StatusBadge tone="danger">Expired</StatusBadge> : <StatusBadge tone="warn">Pending</StatusBadge> },
              { key:'email_status', label:'Email', tip:'Delivery status of the invitation email, as reported by the email provider. A dash means no email is on record: either you chose to send the link yourself, or the email did not go through.',
                render:i=>{ const e = ({ delivered:['Delivered','good'], opened:['Opened','info'], clicked:['Clicked','purple'], bounced:['Bounced','danger'],
                  spam_complaint:['Marked spam','danger'], delayed:['Delayed','warn'], sent:['Sent','neutral'] })[i.email_status];
                  return e ? <StatusBadge tone={e[1]}>{e[0]}</StatusBadge> : <span style={{color:C.muted}}>{i.email_status || '—'}</span>; } },
            ]}
            rowActions={i => new Date(i.expires_at) < new Date() ? [
              { label:'Renew and resend', onClick:()=>renewInvite(i) },
              { label:'Renew and copy link', onClick:()=>renewInvite(i, false) },
              { label:'Dismiss', onClick:()=>dismissInvite(i.id) },
            ] : [
              { label: copiedInvId===i.token ? 'Copied!' : 'Copy invite link', onClick:()=>copyInvLink(i.token) },
              { label:'Revoke', danger:true, onClick:()=>revokeInvite(i.id) },
            ]}
          />
        </div>
      )}

      {/* ── Month Close Status ── */}
      {(() => {
        const period = selectedPeriod;
        const monthLabel = new Date(selectedPeriod + '-02').toLocaleString("default",{month:"long",year:"numeric"});
        const eligible = orgMembers.filter(m => !m.ended_at && ['contractor','employee_hourly'].includes(m.seat_type)); // off-boarded members have nothing left to close
        if (eligible.length === 0) return null;
        const allLocked = eligible.every(m => {
          const ua = assignments.filter(a => a.user_id === m.user_id);
          return ua.length > 0 && ua.every(a => monthLocks.some(l => l.user_id === m.user_id && l.client_id === a.client_id && l.period === period && !l.reopened_at));
        });
        return (
          <div style={css.card}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"14px"}}>
              <div style={{...css.h2,marginBottom:0}}>Month Close Status — {monthLabel}</div>
              <select style={{...css.sel,width:"auto",padding:"6px 10px",fontSize:"13px"}} value={selectedPeriod} onChange={e=>setSelectedPeriod(e.target.value)}>
                {allPeriods.map(p=><option key={p} value={p}>{new Date(p+'-02').toLocaleString("default",{month:"long",year:"numeric"})}</option>)}
              </select>
            </div>
            {allLocked && (
              <div style={{padding:"10px 14px",background:"rgba(72,187,120,0.1)",border:"1px solid rgba(72,187,120,0.3)",borderRadius:"6px",marginBottom:"14px",fontSize:"13px",color:C.success}}>
                All timesheets submitted for {monthLabel}. Ready for billing.
              </div>
            )}
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:"13px"}}>
              <thead><tr>
                <th style={css.th}>Member</th>
                <th style={css.th}>Client</th>
                <th style={css.th}>Hours</th>
                <th style={css.th}>Status</th>
                <th style={css.th}></th>
              </tr></thead>
              <tbody>
                {eligible.flatMap(m => {
                  const u = users.find(u => u.id === m.user_id);
                  const ua = assignments.filter(a => a.user_id === m.user_id);
                  if (ua.length === 0) {
                    return [(
                      <tr key={m.user_id}>
                        <td style={css.td}>{u?.name || m.user_id}</td>
                        <td style={{...css.td,color:C.muted,fontStyle:'italic'}}>No clients assigned</td>
                        <td style={css.td}>—</td>
                        <td style={css.td}><span style={css.badge('amber')}>Pending</span></td>
                        <td style={css.td}></td>
                      </tr>
                    )];
                  }
                  return ua.map((a, idx) => {
                    const cl = clients.find(c => c.id === a.client_id);
                    const lock = monthLocks.find(l => l.user_id === m.user_id && l.client_id === a.client_id && l.period === period && !l.reopened_at);
                    const hrs = entries.filter(e => e.user_id===m.user_id && e.client_id===a.client_id && e.entry_date?.slice(0,7)===period)
                      .reduce((s,e)=>s+parseFloat(e.hours||0),0);
                    return (
                      <tr key={`${m.user_id}_${a.client_id}`}>
                        <td style={{...css.td,fontWeight:idx===0?'600':'400',color:idx===0?C.textBright:C.muted}}>
                          {idx===0?(u?.name||m.user_id):''}
                        </td>
                        <td style={css.td}>{cl?.name||'—'}</td>
                        <td style={css.td}>{formatExactTime(hrs)}</td>
                        <td style={css.td}><span style={css.badge(lock?'green':'amber')}>{lock?'Submitted':'Pending'}</span></td>
                        <td style={css.td}>{lock && <button style={css.smBtn(C.muted)} onClick={()=>resetLock(lock)}>Reset</button>}</td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* ── Off-board modal ── */}
      {offboardModal && (() => {
        const u = offboardModal.user;
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000}}>
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",padding:"28px",width:"440px",maxWidth:"90vw"}}>
              <div style={{fontSize:"16px",fontWeight:"700",color:C.textBright,marginBottom:"8px"}}>Off-board {u.name}?</div>
              <div style={{fontSize:"13px",color:C.textDim,marginBottom:"20px",lineHeight:"1.5"}}>
                This will remove {u.name} from your active team and end all their current client assignments. Their time entries, reports, and history will be preserved. They move to the Off-boarded filter, where you can undo this or reactivate them later.
              </div>
              <div style={{marginBottom:"14px"}}>
                <div style={css.lbl}>End Date</div>
                <input type="date" style={css.inp} value={offboardModal.endDate}
                  onChange={e=>setOffboardModal(s=>({...s,endDate:e.target.value}))} />
              </div>
              <div style={{marginBottom:"20px"}}>
                <div style={css.lbl}>Reason (optional)</div>
                <input style={css.inp} placeholder="e.g. contract ended, project complete…"
                  value={offboardModal.endReason||''} onChange={e=>setOffboardModal(s=>({...s,endReason:e.target.value}))} />
              </div>
              <div style={{display:"flex",gap:"8px"}}>
                <button style={css.btn("primary")} onClick={()=>offboardMember(u.id,offboardModal.endDate,offboardModal.endReason)}>Off-board</button>
                <button style={css.btn()} onClick={()=>setOffboardModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Delete member modal ── */}
      {deleteUserModal && (() => {
        const u = deleteUserModal.user;
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000}}>
            <div style={{background:C.surface,border:`1px solid ${C.danger}44`,borderRadius:"12px",padding:"28px",width:"440px",maxWidth:"90vw"}}>
              <div style={{fontSize:"16px",fontWeight:"700",color:C.danger,marginBottom:"8px"}}>Permanently delete {u.name}?</div>
              <div style={{fontSize:"13px",color:C.textDim,marginBottom:"20px",lineHeight:"1.5"}}>
                This will permanently remove {u.name} from your org, including their profile and all client assignments. This action <strong style={{color:C.danger}}>cannot be undone</strong>. Only members with no time entries can be deleted — use Off-board to retain history.
              </div>
              <div style={{display:"flex",gap:"8px"}}>
                <button style={css.btn()} onClick={()=>setDeleteUserModal(null)}>Cancel</button>
                <button style={{...css.btn("primary"),background:C.danger,borderColor:C.danger}} onClick={()=>delUser(u.id)}>Delete permanently</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── End contract (assignment) modal ── */}
      {endAssignmentModal && (() => {
        const { assignment: a, client: cl } = endAssignmentModal;
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000}}>
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",padding:"28px",width:"440px",maxWidth:"90vw"}}>
              <div style={{fontSize:"16px",fontWeight:"700",color:C.textBright,marginBottom:"8px"}}>End contract with {cl?.name}?</div>
              <div style={{fontSize:"13px",color:C.textDim,marginBottom:"20px",lineHeight:"1.5"}}>
                This ends the active assignment. Existing time entries and history are preserved. The contractor can be reassigned to this client again in the future.
              </div>
              <div style={{marginBottom:"20px"}}>
                <div style={css.lbl}>End Date</div>
                <input type="date" style={css.inp} value={endAssignmentModal.endDate}
                  onChange={e=>setEndAssignmentModal(s=>({...s,endDate:e.target.value}))} />
              </div>
              <div style={{display:"flex",gap:"8px"}}>
                <button style={css.btn("primary")} onClick={()=>endAssignment(a.id,endAssignmentModal.endDate)}>End Contract</button>
                <button style={css.btn()} onClick={()=>setEndAssignmentModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Delete assignment modal ── */}
      {deleteAssignmentModal && (() => {
        const { assignment: a, client: cl } = deleteAssignmentModal;
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000}}>
            <div style={{background:C.surface,border:`1px solid ${C.danger}44`,borderRadius:"12px",padding:"28px",width:"440px",maxWidth:"90vw"}}>
              <div style={{fontSize:"16px",fontWeight:"700",color:C.danger,marginBottom:"8px"}}>Delete assignment for {cl?.name}?</div>
              <div style={{fontSize:"13px",color:C.textDim,marginBottom:"20px",lineHeight:"1.5"}}>
                This will permanently delete this assignment record. This action <strong style={{color:C.danger}}>cannot be undone</strong>. Assignments with time entries cannot be deleted — use End Contract instead.
              </div>
              <div style={{display:"flex",gap:"8px"}}>
                <button style={css.btn()} onClick={()=>setDeleteAssignmentModal(null)}>Cancel</button>
                <button style={{...css.btn("primary"),background:C.danger,borderColor:C.danger}} onClick={()=>deleteAssignment(a.id)}>Delete permanently</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Bring an off-boarded member back: undo a mistake, or reactivate someone returning ── */}
      {reactivateModal && (() => {
        const { user: u, endedMember, mode } = reactivateModal;
        // The client assignments that ended with the off-boarding (same end time), which an undo re-opens.
        const restorable = endedMember?.ended_at
          ? assignments.filter(a => a.user_id === u.id && a.ended_at && new Date(a.ended_at).getTime() === new Date(endedMember.ended_at).getTime()).length
          : 0;
        const choice = (key, title, body) => (
          <button type="button" aria-pressed={mode === key} onClick={()=>setReactivateModal(st=>({...st, mode:key}))}
            style={{display:'block',width:'100%',textAlign:'left',padding:'12px 14px',marginBottom:'10px',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',
              background:C.bg,border:`1px solid ${mode === key ? C.accent : C.border}`,boxShadow:mode === key ? `0 0 0 1px ${C.accent}` : 'none'}}>
            <div style={{fontSize:'13px',fontWeight:'700',color:C.textBright,marginBottom:'3px'}}>{title}</div>
            <div style={{fontSize:'12px',color:C.textDim,lineHeight:'1.5'}}>{body}</div>
          </button>
        );
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000}}>
            <div role="dialog" aria-label={`Bring ${u.name} back`} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",padding:"28px",width:"480px",maxWidth:"90vw"}}>
              <div style={{fontSize:"16px",fontWeight:"700",color:C.textBright,marginBottom:"4px"}}>Bring {u.name} back</div>
              <div style={{fontSize:"12px",color:C.muted,marginBottom:"16px"}}>
                Off-boarded {endedMember?.ended_at ? fmtDate(endedMember.ended_at) : ''}{endedMember?.end_reason ? ` · ${endedMember.end_reason}` : ''}. Nothing of theirs was deleted.
              </div>
              {choice('undo', 'Undo the off-boarding',
                `It was a mistake. ${u.name} is restored exactly as they were: same role, seat and pay settings${restorable ? `, and the ${restorable} client assignment${restorable === 1 ? '' : 's'} that ended with the off-boarding come back` : ''}. There is no gap in their history.`)}
              {choice('reactivate', 'Reactivate on a new date',
                `They're coming back. A new period starts on the date you pick, with their previous role, seat and pay settings. Client assignments are not restored, so assign their clients again.`)}
              {mode === 'reactivate' && (
                <div style={{margin:"4px 0 16px"}}>
                  <div style={css.lbl}>Start date</div>
                  <input type="date" style={css.inp} value={reactivateModal.startDate}
                    onChange={e=>setReactivateModal(st=>({...st,startDate:e.target.value}))} />
                </div>
              )}
              <div style={{display:"flex",gap:"8px",marginTop:"6px"}}>
                {mode === 'undo'
                  ? <button style={css.btn("primary")} onClick={()=>undoOffboard(u.id)}>Undo off-boarding</button>
                  : <button style={css.btn("primary")} onClick={()=>reactivateMember(u.id,reactivateModal.startDate)}>Reactivate</button>}
                <button style={css.btn()} onClick={()=>setReactivateModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Prior tenure / returning member modal ── */}
      {priorTenureCheck && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000}}>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",padding:"28px",width:"460px",maxWidth:"90vw"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:C.textBright,marginBottom:"8px"}}>Returning member?</div>
            <div style={{fontSize:"13px",color:C.textDim,marginBottom:"16px",lineHeight:"1.5"}}>
              It looks like <strong style={{color:C.textBright}}>{priorTenureCheck.name}</strong> was previously a {seatLabel(priorTenureCheck.seat_type)} here, with their relationship ending on <strong style={{color:C.textBright}}>{new Date(priorTenureCheck.ended_at).toLocaleDateString()}</strong>.
              <br /><br />
              You can reactivate them (which preserves the gap in their history) or invite them again with a new link.
              <br /><br />
              {invSendEmail ? 'A new invitation will be emailed to them.' : 'Nothing will be emailed. You will get a link to send them yourself.'}
            </div>
            <div style={{marginBottom:"20px"}}>
              <div style={css.lbl}>Reactivation Start Date</div>
              <input type="date" style={css.inp} value={priorTenureCheck.startDate}
                onChange={e=>setPriorTenureCheck(s=>({...s,startDate:e.target.value}))} />
            </div>
            <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
              <button style={css.btn("primary")} onClick={async()=>{
                await reactivateMember(priorTenureCheck.user_id, priorTenureCheck.startDate);
                setPriorTenureCheck(null);
              }}>Reactivate {priorTenureCheck.name}</button>
              <button style={css.btn()} onClick={async()=>{
                const p = priorTenureCheck;
                setPriorTenureCheck(null);
                // Proceed with a fresh invite, using the role and seat chosen in the Invite panel.
                await createInvite({ email: p.email, role: invRole, seatType: invSeatType, sendEmail: invSendEmail });
              }}>{invSendEmail ? 'Send fresh invitation' : 'Create invite link'}</button>
              <button style={css.btn()} onClick={()=>setPriorTenureCheck(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      </>}
    </Page>
  );
}

// What the client's rate card scaled by `pct` works out to, for the percent
// pay type's live preview in the assignment editor. Returns null when there
// isn't enough on the client to show anything.
function percentPreview(client, pctRaw) {
  const pct = parseFloat(pctRaw || 0) / 100;
  if (!client || !pct) return null;
  const f = (n) => '$' + (Math.round(n * 100) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const model = client.model || 'hourly';
  if (model === 'retainer') {
    const r = parseFloat(client.retainer || 0);
    return r ? `≈ ${f(r * pct)}/mo` : null;
  }
  if (model === 'hybrid') {
    const r = parseFloat(client.retainer || 0), h = parseFloat(client.hours || 0), o = parseFloat(client.overage || 0);
    if (!r && !o) return null;
    return `≈ ${f(r * pct)}/mo for ${h}h, then ${f(o * pct)}/hr`;
  }
  const rate = parseFloat(client.rate || 0);
  return rate ? `≈ ${f(rate * pct)}/hr` : null;
}

// What the client is billed, reduced to one comparable figure -- the base
// retainer for hybrid/retainer clients (overage is a second, separate rate
// that doesn't fit a single number, so it's excluded and the tooltip says so).
function marginBasisForClient(client) {
  if (!client) return null;
  const model = client.model || 'hourly';
  if (model === 'hourly') {
    const r = parseFloat(client.rate || 0);
    return r > 0 ? { unit: 'hr', amount: r } : null;
  }
  if (model === 'retainer' || model === 'hybrid') {
    const r = parseFloat(client.retainer || 0);
    return r > 0 ? { unit: 'mo', amount: r, approximate: model === 'hybrid' } : null;
  }
  return null;
}

// Same reduction for what the member is paid. day_rate and fixed_project have
// no clean per-hour/per-month equivalent (a day rate depends on how many days
// are actually worked), so they're left out rather than guessed at.
function marginBasisForMember(value) {
  if (!value) return null;
  const pt = value.pay_type || 'hourly';
  if (pt === 'hourly') {
    const r = parseFloat(value.rate || 0);
    return r > 0 ? { unit: 'hr', amount: r } : null;
  }
  if (pt === 'flat_monthly') {
    const r = parseFloat(value.monthly_amount || 0);
    return r > 0 ? { unit: 'mo', amount: r } : null;
  }
  if (pt === 'hybrid') {
    const r = parseFloat(value.hybrid_base || 0);
    return r > 0 ? { unit: 'mo', amount: r, approximate: true } : null;
  }
  return null;
}

// Live margin readout for the Add/Edit Assignment flow -- the moment a rate
// actually gets set, with the client's real billing rate already on screen.
// Estimate only: no expenses, non-billable time, taxes, or (for hybrid/
// retainer) overage factored in.
function AssignmentMarginPreview({ client, value, onChange }) {
  const [target, setTarget] = useState('');
  const clientBasis = marginBasisForClient(client);
  if (!clientBasis) return null;

  const memberBasis = marginBasisForMember(value);
  const canCompare = memberBasis && memberBasis.unit === clientBasis.unit;
  const marginPct = canCompare ? ((clientBasis.amount - memberBasis.amount) / clientBasis.amount) * 100 : null;
  const marginAmt = canCompare ? clientBasis.amount - memberBasis.amount : null;
  const color = marginPct === null ? C.muted : marginPct >= 40 ? C.success : marginPct >= 20 ? '#f6c26b' : C.danger;

  const applyTarget = () => {
    const t = parseFloat(target);
    if (isNaN(t)) return;
    const maxPay = Math.round(clientBasis.amount * (1 - t / 100) * 100) / 100;
    if (clientBasis.unit === 'hr') onChange({ pay_type: 'hourly', rate: maxPay });
    else onChange({ pay_type: 'flat_monthly', monthly_amount: maxPay });
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', fontSize: '12px',
      marginTop: '6px', paddingTop: '8px', borderTop: `1px dashed ${C.border}`, width: '100%' }}>
      <span style={{ color: C.muted }}>Client bills</span>
      <span style={{ fontWeight: '700', color: C.textBright }}>
        ${clientBasis.amount.toLocaleString()}/{clientBasis.unit}{clientBasis.approximate ? ' base' : ''}
      </span>
      {canCompare ? (
        <>
          <span style={{ color: C.muted }}>·</span>
          <TKTooltip text={`Margin = (client rate − pay rate) ÷ client rate. Estimate only — doesn't account for expenses, non-billable time, taxes${clientBasis.approximate ? ', or overage beyond the base retainer' : ''}.`}>
            <span style={{ color: C.muted }}>Margin ⓘ</span>
          </TKTooltip>
          <span style={{ fontWeight: '700', color }}>
            {Math.round(marginPct)}% (${Math.round(Math.abs(marginAmt)).toLocaleString()}{marginAmt < 0 ? ' loss' : ''}/{clientBasis.unit})
          </span>
        </>
      ) : (
        <span style={{ color: C.muted }}>— set a pay rate to see margin</span>
      )}
      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ color: C.muted }}>Target margin</span>
        <input type="number" min="0" max="99" step="1" placeholder="%"
          style={{ ...css.inp, width: '52px', padding: '3px 6px', fontSize: '11px' }}
          value={target} onChange={e => setTarget(e.target.value)} />
        <button style={{ ...css.btn('ghost'), padding: '3px 10px', fontSize: '11px', opacity: target ? 1 : 0.5 }}
          disabled={!target} onClick={applyTarget}>Set pay rate</button>
      </span>
    </div>
  );
}

// Pay-type select + the fields that go with it. Shared by the per-assignment
// editor row and the "add assignment" row so both offer the full set at once
// (adding an assignment used to only take a client + rate, then you had to
// re-open it to change the pay type). `value` is an assignment-shaped object;
// `onChange` gets a partial patch. `client` feeds the percent preview.
function PayStructureEditor({ value, client, onChange }) {
  const pt = value?.pay_type || 'hourly';
  const lbl = { fontSize: '11px', color: C.muted };
  const num = (col, width = 90) => (
    <input type="number" min="0" step="0.01" style={{ ...css.inp, width: `${width}px` }}
      value={value?.[col] ?? ''} placeholder="0"
      onChange={e => onChange({ [col]: e.target.value === '' ? null : parseFloat(e.target.value) })} />
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
      <TKTooltip text="How this member is paid for this client — changes the fields shown below and how their statement lines are calculated."><span style={lbl}>Pay type ⓘ</span></TKTooltip>
      <select style={{ ...css.sel, width: 'auto', padding: '6px 8px', fontSize: '12px' }} value={pt}
        onChange={e => onChange({ pay_type: e.target.value })}>
        <option value="hourly">Hourly</option>
        <option value="flat_monthly">Flat Monthly</option>
        <option value="day_rate">Day Rate</option>
        <option value="fixed_project">Fixed Project</option>
        <option value="hybrid">Hybrid</option>
        <option value="percent">% of client rate</option>
      </select>
      {pt === 'hourly' && <><span style={lbl}>Rate $/hr</span>{num('rate')}</>}
      {pt === 'flat_monthly' && <><span style={lbl}>Monthly $</span>{num('monthly_amount')}</>}
      {pt === 'day_rate' && <><span style={lbl}>Day rate $</span>{num('rate')}</>}
      {pt === 'fixed_project' && <>
        <span style={lbl}>Project $</span>{num('project_amount')}
        <TKTooltip text="The one month this project amount is billed in. Prevents billing the same fixed project twice."><span style={lbl}>Bill in ⓘ</span></TKTooltip>
        <input type="month" style={{ ...css.inp, width: '130px' }} value={value?.project_bill_month || ''}
          onChange={e => onChange({ project_bill_month: e.target.value || null })} />
      </>}
      {pt === 'hybrid' && <>
        <TKTooltip text="Paid every month regardless of hours worked."><span style={lbl}>Base $/mo ⓘ</span></TKTooltip>{num('hybrid_base')}
        <TKTooltip text="Hours covered by the base fee before overage kicks in."><span style={lbl}>Incl. hrs ⓘ</span></TKTooltip>{num('hybrid_included_hours', 70)}
        <TKTooltip text="Paid per hour beyond the included threshold, on top of the base. These numbers are independent of the client's own hybrid billing terms — they don't have to match."><span style={lbl}>Overage $/hr ⓘ</span></TKTooltip>{num('hybrid_overage_rate')}
      </>}
      {pt === 'percent' && (() => {
        const preview = percentPreview(client, value?.pay_percent);
        return <>
          <TKTooltip text="The member is paid this percentage of whatever the client is billed — it scales the client's whole rate card, so it moves automatically if their billing terms change (retainer, hourly, or hybrid with overage)."><span style={lbl}>% ⓘ</span></TKTooltip>
          <input type="number" min="0" max="100" step="1" style={{ ...css.inp, width: '70px' }} placeholder="0"
            value={value?.pay_percent ?? ''}
            onChange={e => onChange({ pay_percent: e.target.value === '' ? null : parseFloat(e.target.value) })} />
          {preview
            ? <span style={{ fontSize: '11px', color: C.accent, fontWeight: '600' }}>{preview}</span>
            : (value?.pay_percent ? <span style={{ fontSize: '11px', color: C.muted }}>set the client's billing rate to preview</span> : null)}
        </>;
      })()}
      {pt !== 'percent' && pt !== 'fixed_project' && pt !== 'day_rate' && (
        <AssignmentMarginPreview client={client} value={value} onChange={onChange} />
      )}
    </div>
  );
}

function AddAssignmentRow({ clients, existing, onAdd, defaultRate }) {
  const emptyDraft = () => ({ pay_type: 'hourly', rate: defaultRate != null ? defaultRate : null });
  const [cid, setCid] = useState('');
  const [draft, setDraft] = useState(emptyDraft);
  const available = clients.filter(c=>!existing.includes(c.id));
  const selectedClient = clients.find(c => c.id === cid);
  const handle = () => { if(!cid) return; onAdd(cid, draft); setCid(''); setDraft(emptyDraft()); };
  if (available.length===0) return <div style={{fontSize:"12px",color:C.muted,marginTop:"6px"}}>All clients assigned.</div>;
  return (
    <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:"8px",marginTop:"8px",padding:"8px 10px",background:"rgba(49,167,217,0.05)",borderRadius:"6px",border:`1px dashed ${C.border}`}}>
      <SearchableSelect options={available.map(c=>({value:c.id,label:c.name}))} value={cid} onChange={setCid} allLabel="Add client..." placeholder="Search clients…" style={{flex:"1 1 150px",minWidth:"140px"}} />
      <PayStructureEditor value={draft} client={selectedClient} onChange={patch=>setDraft(d=>({...d,...patch}))} />
      <button style={css.btn("primary")} onClick={handle}>Add</button>
    </div>
  );
}

// ─── TEAM ANALYTICS ───────────────────────────────────────────────────────────
// Rendered inside Team's Page frame: `view` is Team's tab ('utilization' | 'margin' | 'planner').
function TeamAnalyticsView({ view = 'utilization', entries, clients, assignments, orgMembers, users, org, statements = [], billingTerms = null }) {
  const tour = useTour('analytics');
  const curMonthStr = curMonth();

  // ── Date range state ──────────────────────────────────────────────────────
  const [rangeMode, setRangeMode] = useState('month'); // 'month' | 'custom' | 'ytd'
  const [selectedMonth, setSelectedMonth] = useState(curMonthStr);
  const [customStart, setCustomStart]     = useState('');
  const [customEnd, setCustomEnd]         = useState('');
  const [marginBy, setMarginBy]           = useState('member'); // Margin tab: 'member' | 'client'
  const activeTab = view === 'margin' ? (marginBy === 'client' ? 'clients' : 'margin') : view;
  const [expandedContractor, setExpandedContractor] = useState(null);

  const allMonths = useMemo(() => { const months=[],d=new Date(); for(let i=0;i<24;i++){months.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);d.setMonth(d.getMonth()-1);} return months; },[]);

  const fiscalStart = org?.fiscal_year_start || 1;
  const now = new Date();
  const ytdStart = (() => {
    const yr = now.getMonth() + 1 >= fiscalStart ? now.getFullYear() : now.getFullYear() - 1;
    return `${yr}-${String(fiscalStart).padStart(2,'0')}-01`;
  })();
  const ytdEnd = now.toISOString().slice(0,10);

  const filteredEntries = (() => {
    if (rangeMode === 'month')  return entries.filter(e => e.entry_date?.slice(0,7) === selectedMonth);
    if (rangeMode === 'ytd')    return entries.filter(e => e.entry_date >= ytdStart && e.entry_date <= ytdEnd);
    if (rangeMode === 'custom' && customStart && customEnd)
      return entries.filter(e => e.entry_date >= customStart && e.entry_date <= customEnd);
    return entries;
  })();

  const rangeLabel = (() => {
    if (rangeMode === 'month')  return new Date(selectedMonth+'-02').toLocaleString('en-US',{month:'long',year:'numeric'});
    if (rangeMode === 'ytd')    return `YTD (since ${new Date(ytdStart+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})})`;
    if (rangeMode === 'custom' && customStart && customEnd)
      return `${new Date(customStart+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})} – ${new Date(customEnd+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}`;
    return 'Custom Range';
  })();

  const fmt$ = (n) => fmtMoney(n);
  const fmtPct = fmtPercent;

  // Real calendar months touched by the current range. Work value and member
  // pay both reason in discrete months (a retainer or flat_monthly pay applies
  // once per month; approvals are per member per month), so a YTD/Custom range
  // is always summed month by month, never treated as one lump period.
  const rangeMonths = (() => {
    if (rangeMode === 'ytd') return monthsBetween(ytdStart, ytdEnd);
    if (rangeMode === 'custom' && customStart && customEnd) return monthsBetween(customStart, customEnd);
    return [selectedMonth];
  })();

  // ── Shared metric definitions (src/metrics.js) ────────────────────────────
  // Work value: billable hours priced by each client's terms, per month.
  // Member pay: approved member-invoice lines first, else computeEarnings --
  // the same calculation Org Overview and Client Reports use, so the three
  // pages agree once anything is approved (this page used to always estimate).
  const billableClients = clients.filter(cl => !cl.is_internal);
  const wvByClient = useMemo(() => {
    const out = {};
    for (const cl of billableClients) {
      out[cl.id] = workValue({ client: cl, entries: filteredEntries.filter(e => e.client_id === cl.id), months: rangeMonths, terms: billingTerms });
    }
    return out;
  }, [billableClients, filteredEntries, rangeMonths.join(','), billingTerms]);
  const pay = useMemo(() => memberPay({ members: orgMembers, assignments, entries: filteredEntries, statements, clients, months: rangeMonths }),
    [orgMembers, assignments, filteredEntries, statements, clients, rangeMonths.join(',')]);
  const provisional = provisionalNote(pay);

  // A client's work value is split between members by their share of its
  // billable hours, month by month. A retainer month with no hours logged has
  // nobody to attribute it to -- it's in the totals but in no member's row.
  const wvShare = (uid, clId) => {
    const wv = wvByClient[clId]; if (!wv) return 0;
    let v = 0;
    for (const [mo, b] of Object.entries(wv.byMonth)) {
      if (!b.hours) continue;
      const mine = filteredEntries.reduce((s, e) => s + (e.user_id === uid && e.client_id === clId && e.is_billable !== false && e.entry_date?.slice(0,7) === mo ? parseFloat(e.hours||0) : 0), 0);
      v += b.amount * (mine / b.hours);
    }
    return v;
  };

  // ── Member rows ───────────────────────────────────────────────────────────
  // One row per person: their active membership, else their latest tenure if
  // they logged time or were paid in the range (off-boarded mid-range).
  const memberRowsSource = [...new Map(
    [...orgMembers].sort((a,b) => (a.ended_at ? 1 : 0) - (b.ended_at ? 1 : 0) || String(b.ended_at||'').localeCompare(String(a.ended_at||'')))
      .reverse().map(m => [m.user_id, m])
  ).values()].filter(m => users.find(u => u.id === m.user_id)
    && (!m.ended_at || filteredEntries.some(e => e.user_id === m.user_id) || pay.byMember[m.user_id]));
  const contractorRows = memberRowsSource
    .map(m => {
      const user = users.find(u => u.id === m.user_id);
      const myEntries = filteredEntries.filter(e => e.user_id === m.user_id);
      const rawHrs = myEntries.reduce((s,e) => s + parseFloat(e.hours||0), 0);
      const capacityWk = parseFloat(m.capacity_hours_week || 0);

      const weeksInRange = (() => {
        if (rangeMode === 'month') {
          const [yr, mo] = selectedMonth.split('-').map(Number);
          return new Date(yr, mo, 0).getDate() / 7;
        }
        if (rangeMode === 'ytd') return Math.max(1, (new Date(ytdEnd) - new Date(ytdStart)) / (7 * 86400000));
        if (rangeMode === 'custom' && customStart && customEnd)
          return Math.max(1, (new Date(customEnd) - new Date(customStart)) / (7 * 86400000));
        return 4;
      })();
      const capacityHrs = capacityWk * weeksInRange;
      const utilPct = capacityHrs > 0 ? (rawHrs / capacityHrs) * 100 : null;

      // Pay-tracked seats are estimated; anyone with an approved member invoice
      // (even an admin seat) was actually paid, so they show pay too.
      const compTracked = hasCompSeat(m) || !!pay.byMember[m.user_id];
      const compRate = hasCompSeat(m) ? parseFloat(m.compensation_rate || 0) : 0;

      const clientIds = new Set([
        ...myEntries.map(e => e.client_id).filter(Boolean),
        ...Object.keys(pay.byMemberClient).filter(k => k.startsWith(m.user_id + '|')).map(k => k.split('|')[1]),
      ]);
      const clientBreakdown = [...clientIds].map(cid => {
        const cl = clients.find(c => c.id === cid);
        if (!cl || cl.is_internal) return null;
        const ces = myEntries.filter(e => e.client_id === cid);
        const hrs = ces.reduce((s,e) => s + parseFloat(e.hours||0), 0);
        const billed = wvShare(m.user_id, cid);
        const comp = compTracked ? (pay.byMemberClient[`${m.user_id}|${cid}`] || 0) : 0;
        return { cl, hrs, billed, comp, entries: ces };
      }).filter(Boolean).sort((a,b) => b.billed - a.billed);

      const totalBilled = clientBreakdown.reduce((s,r) => s + r.billed, 0);
      const totalComp   = compTracked ? (pay.byMember[m.user_id] || 0) : 0; // includes internal-client pay
      const margin      = compTracked ? marginPct(totalBilled, totalComp) : null;

      return { m, user, rawHrs, capacityHrs, capacityWk, utilPct, compRate, compTracked, totalBilled, totalComp, margin, clientBreakdown };
    }).filter(Boolean);

  // ── Client rows ───────────────────────────────────────────────────────────
  const clientRows = billableClients.map(cl => {
    const wv = wvByClient[cl.id];
    const comp = pay.byClient[cl.id] || 0;
    if (!wv || (wv.total === 0 && wv.hours === 0 && comp === 0)) return null;
    const rawHrs = filteredEntries.reduce((s,e) => s + (e.client_id === cl.id ? parseFloat(e.hours||0) : 0), 0);
    const contractors = [...new Set(filteredEntries.filter(e => e.client_id === cl.id).map(e => users.find(u => u.id === e.user_id)?.name).filter(Boolean))];
    return { cl, rawHrs, billed: wv.total, comp, margin: marginPct(wv.total, comp), contractors };
  }).filter(Boolean).sort((a,b) => b.billed - a.billed);

  // Pay for work on internal (overhead) clients, and anything not tied to a client.
  const internalPay = Object.entries(pay.byClient)
    .filter(([cid]) => cid === '_none' || clients.find(c => c.id === cid)?.is_internal)
    .reduce((s, [, v]) => s + v, 0);

  // ── Summary totals ────────────────────────────────────────────────────────
  const totalHrs    = contractorRows.reduce((s,r) => s + r.rawHrs, 0);
  const totalBilled = clientRows.reduce((s,r) => s + r.billed, 0);
  const totalComp   = pay.total;
  const totalMargin = marginPct(totalBilled, totalComp);
  const attributedWV = contractorRows.reduce((s,r) => s + r.totalBilled, 0);
  // Write-in payments ("Log a Payment") to people with no TimeKeep account.
  const writeInPay = Object.entries(pay.byMember).filter(([k]) => k.startsWith('_writein')).reduce((s, [, v]) => s + v, 0);
  const unattributedWV = Math.max(0, totalBilled - attributedWV);

  // ── CSV export ────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const rows = [
      ['Member','Client','Hours','Work value','Member pay','Pay estimated','Margin on work value %'],
      ...contractorRows.flatMap(r =>
        r.clientBreakdown.map(cb => [
          r.user.name,
          cb.cl.name,
          hoursDecimal(cb.hrs),
          cb.billed.toFixed(2),
          cb.comp.toFixed(2),
          provisional ? 'some months' : 'no',
          cb.billed > 0 ? Math.round(((cb.billed - cb.comp) / cb.billed) * 100) + '%' : '',
        ])
      )
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = `TimeKeep_TeamAnalytics_${rangeLabel.replace(/[^a-z0-9]/gi,'_')}.csv`;
    a.click();
  };

  // ── Margin color ──────────────────────────────────────────────────────────
  const marginColor = (pct) => {
    if (pct === null || pct === undefined) return C.muted;
    if (pct >= 40) return C.success;
    if (pct >= 20) return C.badgeAmber;
    return C.danger;
  };
  const marginArrow = (pct) => {
    if (pct === null || pct === undefined || isNaN(pct) || !isFinite(pct)) return '';
    if (pct >= 40) return '↑';
    if (pct >= 20) return '~';
    return '↓';
  };
  const fmtMargin = (pct) => { const a = marginArrow(pct); const p = fmtPct(pct); return a ? `${a} ${p}` : p; };

  const utilColor = (pct) => {
    if (pct === null) return C.muted;
    if (pct >= 90) return C.danger;
    if (pct >= 70) return C.success;
    if (pct >= 40) return C.badgeAmber;
    return C.muted;
  };

  const TAB = ['utilization','margin','clients','planner'];
  const TAB_LABELS = { utilization: 'Team Utilization', margin: 'Margin by Member', clients: 'Margin by Client', planner: 'Rate Planner' };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
      {tour.show && <WalkthroughModal tourKey="analytics" onClose={tour.close} />}
      {activeTab !== 'planner' && (
        <div data-tour="analytics-header"><Controls
          filters={<>
            {view === 'margin' && <SegmentedControl options={[['member','By member'],['client','By client']]} value={marginBy} onChange={setMarginBy} />}
            <button type="button" style={css.btn('secondary')} onClick={exportCSV}>Export CSV</button>
            <TourButton onClick={tour.launch} />
          </>}
          range={<>
            <SegmentedControl options={[['month','Month'],['ytd','YTD'],['custom','Custom']]} value={rangeMode} onChange={setRangeMode} />
            {rangeMode === 'month' && (
              <select aria-label="Month" style={{...css.sel,width:'auto',padding:'5px 10px',fontSize:'13px'}} value={selectedMonth} onChange={e=>setSelectedMonth(e.target.value)}>
                {allMonths.map(m=><option key={m} value={m}>{fmtPeriod(m)}</option>)}
              </select>
            )}
            {rangeMode === 'custom' && <>
              <input type="date" aria-label="From" style={{...css.inp,padding:'4px 8px',fontSize:'12px',width:'130px'}} value={customStart} onChange={e=>setCustomStart(e.target.value)} />
              <span style={{color:C.muted,fontSize:'12px'}}>to</span>
              <input type="date" aria-label="To" style={{...css.inp,padding:'4px 8px',fontSize:'12px',width:'130px'}} value={customEnd} onChange={e=>setCustomEnd(e.target.value)} />
            </>}
          </>} /></div>
      )}

      {/* Summary tiles -- glossary names + basis lines (src/metrics.js) */}
      {activeTab !== 'planner' && (
        <div>
          <StatTiles items={[
            { label:'Total hours', value: fmtHours(totalHrs), basis: 'logged by the team in range',
              tip:'Sum of all hours logged by the team in the selected period, billable or not.' },
            { label:'Work value', value: fmt$(totalBilled), basis: 'hours × billing terms',
              tip:'What the billable hours in this range are worth under each client\'s billing terms in effect that month. Retainers count in full for every month. It is what you would invoice, not what you did invoice.' },
            { label:'Member pay', value: fmt$(totalComp), basis: provisional ? 'approved, or estimated' : (pay.memberMonths ? 'approved member invoices' : 'nothing to pay in range'),
              provisional,
              tip:'Per member per month: their approved member invoice when there is one, otherwise an estimate from their assignments. Expense reimbursements are not pay. Owners and admins are not included.' },
            { label:'Margin on work value', value: fmtPct(totalMargin), basis: totalMargin === null ? 'no work value in range' : `after ${fmt$(totalComp)} member pay`,
              provisional,
              tip:'(Work value − Member pay) ÷ Work value. Org Overview shows Margin on invoiced, which is measured against invoices instead.' },
          ]} />
        </div>
      )}

      {/* ── UTILIZATION TAB ── table + member panel */}
      {activeTab === 'utilization' && (
        <DataTable
          rows={contractorRows}
          rowKey={r => r.user.id}
          onRowClick={r => setExpandedContractor(r.user.id)}
          selectedKey={expandedContractor}
          columns={[
            { key:'name', label:'Member', sortValue:r=>(r.user.name||'').toLowerCase(),
              render:r=>(<span>{r.user.name}<span style={{display:'block',color:C.muted,fontWeight:'400',fontSize:'12px'}}>{seatTypeLabel(r.m.seat_type)}{r.m.capacity_hours_week ? ` · ${r.m.capacity_hours_week}h/wk` : ''}</span></span>) },
            { key:'util', label:'Utilization', tip:'Hours logged ÷ capacity hours in the period. Needs a weekly capacity on the member.',
              sortValue:r=>r.utilPct ?? -1, width:'170px',
              render:r=> r.utilPct === null ? <span style={{color:C.muted,fontStyle:'italic',fontSize:'12px'}}>No capacity set</span> : (
                <span style={{display:'flex',alignItems:'center',gap:'8px'}}>
                  <span style={{flex:1,height:'6px',background:C.border,borderRadius:'3px',overflow:'hidden',minWidth:'60px'}}>
                    <span style={{display:'block',height:'100%',width:`${Math.min(r.utilPct,100)}%`,background:utilColor(r.utilPct),borderRadius:'3px'}} />
                  </span>
                  <span style={{fontWeight:'600',color:utilColor(r.utilPct),minWidth:'36px',textAlign:'right'}}>{fmtPct(r.utilPct)}</span>
                </span>) },
            { key:'hours', label:'Hours', align:'right', sortValue:r=>r.rawHrs, render:r=><span style={{whiteSpace:'nowrap'}}>{fmtHours(r.rawHrs)}</span> },
            { key:'value', label:'Work value', align:'right', tip:"This member's share of each client's work value, by their share of its billable hours.",
              sortValue:r=>r.totalBilled, render:r=><span style={{color:C.textBright,fontWeight:'600'}}>{fmt$(r.totalBilled)}</span> },
            { key:'pay', label:'Member pay', align:'right', tip:"Their approved member invoice for each month, or an estimate from their assignments where it isn't approved yet. Excludes expense reimbursements.",
              sortValue:r=>r.compTracked ? r.totalComp : -1, render:r=>r.compTracked ? fmt$(r.totalComp) : <span style={{color:C.muted}}>N/A</span> },
            { key:'margin', label:'Margin', align:'right', tip:'Margin on work value: (Work value − Member pay) ÷ Work value. N/A for owner and admin seats.',
              sortValue:r=>r.compTracked && r.margin != null ? r.margin : -1e9,
              render:r=>r.compTracked ? <span style={{fontWeight:'700',color:marginColor(r.margin)}}>{fmtMargin(r.margin)}</span> : <span style={{color:C.muted}}>N/A</span> },
          ]}
          empty={<EmptyState title="No time in this range" body="Pick another month or range above." />}
        />
      )}
      {activeTab === 'utilization' && (() => {
        const r = expandedContractor ? contractorRows.find(x => x.user.id === expandedContractor) : null;
        if (!r) return null;
        return (
          <DetailPanel open onClose={()=>setExpandedContractor(null)} title={r.user.name}
            subtitle={`${seatTypeLabel(r.m.seat_type)} · ${fmtHours(r.rawHrs)} logged${r.utilPct !== null ? ` · ${fmtPct(r.utilPct)} utilization` : ''}`}>
            <DataTable
              rows={r.clientBreakdown}
              rowKey={cb => cb.cl.id}
              columns={[
                { key:'client', label:'Client', sortValue:cb=>cb.cl.name, render:cb=>cb.cl.name },
                { key:'hrs', label:'Hours', align:'right', sortValue:cb=>cb.hrs, render:cb=>fmtHours(cb.hrs) },
                { key:'billed', label:'Work value', align:'right', sortValue:cb=>cb.billed, render:cb=><span style={{color:C.textBright,fontWeight:'600'}}>{fmt$(cb.billed)}</span> },
                { key:'comp', label:'Pay', align:'right', sortValue:cb=>cb.comp, render:cb=>fmt$(cb.comp) },
                { key:'margin', label:'Margin', align:'right', sortValue:cb=>cb.billed > 0 ? (cb.billed-cb.comp)/cb.billed : -1e9,
                  render:cb=>{ const m = cb.billed > 0 ? ((cb.billed - cb.comp) / cb.billed) * 100 : null; return <span style={{fontWeight:'700',color:marginColor(m)}}>{fmtMargin(m)}</span>; } },
              ]}
              empty={<EmptyState title="No client work in this range" />}
            />
          </DetailPanel>
        );
      })()}

      {/* ── MARGIN BY CONTRACTOR TAB ── */}
      {activeTab === 'margin' && (
        <div style={{...css.card,padding:0,overflow:'hidden'}}>
          <div style={{display:'grid',gridTemplateColumns:'200px 80px 100px 100px 100px 100px 100px',gap:'0',
            padding:'10px 18px',fontSize:'11px',fontWeight:'700',color:C.muted,textTransform:'uppercase',
            background:C.bg,borderBottom:`1px solid ${C.border}`,letterSpacing:'0.5px'}}>
            <div>Member</div><div style={{textAlign:'right'}}>Hours</div>
            <div style={{textAlign:'right'}}>Work value</div><div style={{textAlign:'right'}}>Pay rate</div>
            <div style={{textAlign:'right'}}>Member pay</div><div style={{textAlign:'right'}}>Profit</div>
            <div style={{textAlign:'right'}}>Margin</div>
          </div>
          {contractorRows.length === 0 && (
            <div style={{padding:'32px',textAlign:'center',color:C.muted,fontSize:'14px'}}>No entries in this range.</div>
          )}
          {contractorRows.map(r => (
            <div key={r.user.id} style={{display:'grid',gridTemplateColumns:'200px 80px 100px 100px 100px 100px 100px',
              gap:'0',padding:'12px 18px',borderBottom:`1px solid ${C.border}`,alignItems:'center'}}>
              <div>
                <div style={{fontWeight:'600',color:C.textBright,fontSize:'13px'}}>{r.user.name}</div>
                <div style={{fontSize:'11px',color:C.muted,textTransform:'capitalize'}}>{seatTypeLabel(r.m.seat_type)}</div>
              </div>
              <div style={{textAlign:'right',fontSize:'13px',color:C.textDim}}>{fmtHours(r.rawHrs)}</div>
              <div style={{textAlign:'right',fontSize:'13px',fontWeight:'600',color:C.textBright}}>{fmt$(r.totalBilled)}</div>
              <div style={{textAlign:'right',fontSize:'13px',color:C.textDim}}>{r.compRate > 0 ? fmtRate(r.compRate, r.m.seat_type === 'employee_salary' ? 'yr' : 'hr') : '—'}</div>
              <div style={{textAlign:'right',fontSize:'13px',color:C.textDim}}>{fmt$(r.totalComp)}</div>
              <div style={{textAlign:'right',fontSize:'13px',fontWeight:'600',color:r.totalBilled - r.totalComp >= 0 ? C.success : C.danger}}>
                {fmt$(r.totalBilled - r.totalComp)}
              </div>
              <div style={{textAlign:'right',fontSize:'14px',fontWeight:'700',color:marginColor(r.margin)}}>{r.compTracked ? fmtMargin(r.margin) : <span style={{color:C.muted,fontSize:'11px'}}>—</span>}</div>
            </div>
          ))}
          {writeInPay > 0 && (
            <div style={{display:'grid',gridTemplateColumns:'200px 80px 100px 100px 100px 100px 100px',gap:'0',padding:'10px 18px',borderBottom:`1px solid ${C.border}`,alignItems:'center'}}>
              <div style={{fontSize:'13px',color:C.textDim}}><TKTooltip text="Payments logged with Log a Payment to people who don't have a TimeKeep account.">Write-in payments ⓘ</TKTooltip></div>
              <div></div><div></div><div></div>
              <div style={{textAlign:'right',fontSize:'13px',color:C.textDim}}>{fmt$(writeInPay)}</div>
              <div></div><div></div>
            </div>
          )}
          {unattributedWV > 0.5 && (
            <div style={{display:'grid',gridTemplateColumns:'200px 80px 100px 100px 100px 100px 100px',gap:'0',padding:'10px 18px',borderBottom:`1px solid ${C.border}`,alignItems:'center'}}>
              <div style={{fontSize:'13px',color:C.textDim}}><TKTooltip text="Retainer months with no hours logged. The client pays for them, but no member's hours to split them by.">Not attributed ⓘ</TKTooltip></div>
              <div></div>
              <div style={{textAlign:'right',fontSize:'13px',color:C.textDim}}>{fmt$(unattributedWV)}</div>
              <div></div><div></div><div></div><div></div>
            </div>
          )}
          {contractorRows.length > 0 && (
            <div style={{display:'grid',gridTemplateColumns:'200px 80px 100px 100px 100px 100px 100px',
              gap:'0',padding:'12px 18px',background:C.bg,borderTop:`2px solid ${C.border}`,alignItems:'center'}}>
              <div style={{fontWeight:'700',color:C.textBright,fontSize:'13px'}}>Total</div>
              <div style={{textAlign:'right',fontSize:'13px',color:C.textDim}}>{fmtHours(totalHrs)}</div>
              <div style={{textAlign:'right',fontSize:'13px',fontWeight:'700',color:C.textBright}}>{fmt$(totalBilled)}</div>
              <div></div>
              <div style={{textAlign:'right',fontSize:'13px',color:C.textDim}}>{fmt$(totalComp)}</div>
              <div style={{textAlign:'right',fontSize:'13px',fontWeight:'700',color:totalBilled-totalComp>=0?C.success:C.danger}}>{fmt$(totalBilled-totalComp)}</div>
              <div style={{textAlign:'right',fontSize:'14px',fontWeight:'700',color:marginColor(totalMargin)}}>{fmtMargin(totalMargin)}</div>
            </div>
          )}
        </div>
      )}

      {/* ── MARGIN BY CLIENT TAB ── */}
      {activeTab === 'clients' && (
        <div style={{...css.card,padding:0,overflow:'hidden'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 100px 100px 100px 100px',gap:'0',
            padding:'10px 18px',fontSize:'11px',fontWeight:'700',color:C.muted,textTransform:'uppercase',
            background:C.bg,borderBottom:`1px solid ${C.border}`,letterSpacing:'0.5px'}}>
            <div>Client</div><div style={{textAlign:'right'}}>Model</div><div style={{textAlign:'right'}}>Hours</div>
            <div style={{textAlign:'right'}}>Work value</div><div style={{textAlign:'right'}}>Member pay</div>
            <div style={{textAlign:'right'}}>Profit</div><div style={{textAlign:'right'}}>Margin</div>
          </div>
          {clientRows.length === 0 && (
            <div style={{padding:'32px',textAlign:'center',color:C.muted,fontSize:'14px'}}>No entries in this range.</div>
          )}
          {clientRows.map(r => (
            <div key={r.cl.id} style={{padding:'12px 18px',borderBottom:`1px solid ${C.border}`}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 100px 100px 100px 100px',gap:'0',alignItems:'center'}}>
                <div>
                  <div style={{fontWeight:'600',color:C.textBright,fontSize:'13px'}}>{r.cl.name}</div>
                  <div style={{fontSize:'11px',color:C.muted,marginTop:'2px'}}>{r.contractors.join(', ')}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <span style={{...css.badge(r.cl.model==='retainer'?'green':r.cl.model==='hybrid'?'amber':'blue'),fontSize:'10px'}}>
                    {modelLabel(r.cl.model, 'shortLabel')}
                  </span>
                </div>
                <div style={{textAlign:'right',fontSize:'13px',color:C.textDim}}>{fmtHours(r.rawHrs)}</div>
                <div style={{textAlign:'right',fontSize:'13px',fontWeight:'600',color:C.textBright}}>{fmt$(r.billed)}</div>
                <div style={{textAlign:'right',fontSize:'13px',color:C.textDim}}>{fmt$(r.comp)}</div>
                <div style={{textAlign:'right',fontSize:'13px',fontWeight:'600',color:r.billed-r.comp>=0?C.success:C.danger}}>{fmt$(r.billed-r.comp)}</div>
                <div style={{textAlign:'right',fontSize:'14px',fontWeight:'700',color:marginColor(r.margin)}}>{fmtMargin(r.margin)}</div>
              </div>
            </div>
          ))}
          {internalPay > 0 && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 100px 100px 100px 100px',gap:'0',padding:'10px 18px',borderBottom:`1px solid ${C.border}`,alignItems:'center'}}>
              <div style={{fontSize:'13px',color:C.textDim}}><TKTooltip text="Member pay for internal (overhead) clients and pay lines not tied to a client. It's a real cost, so it's in the total, but no client pays for it.">Internal work ⓘ</TKTooltip></div>
              <div></div><div></div><div></div>
              <div style={{textAlign:'right',fontSize:'13px',color:C.textDim}}>{fmt$(internalPay)}</div>
              <div></div><div></div>
            </div>
          )}
          {clientRows.length > 0 && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 100px 100px 100px 100px',
              gap:'0',padding:'12px 18px',background:C.bg,borderTop:`2px solid ${C.border}`,alignItems:'center'}}>
              <div style={{fontWeight:'700',color:C.textBright,fontSize:'13px'}}>Total</div>
              <div></div>
              <div style={{textAlign:'right',fontSize:'13px',color:C.textDim}}>{fmtHours(totalHrs)}</div>
              <div style={{textAlign:'right',fontSize:'13px',fontWeight:'700',color:C.textBright}}>{fmt$(totalBilled)}</div>
              <div style={{textAlign:'right',fontSize:'13px',color:C.textDim}}>{fmt$(totalComp)}</div>
              <div style={{textAlign:'right',fontSize:'13px',fontWeight:'700',color:totalBilled-totalComp>=0?C.success:C.danger}}>{fmt$(totalBilled-totalComp)}</div>
              <div style={{textAlign:'right',fontSize:'14px',fontWeight:'700',color:marginColor(totalMargin)}}>{fmtMargin(totalMargin)}</div>
            </div>
          )}
        </div>
      )}

      {/* ── RATE PLANNER TAB ── */}
      {activeTab === 'planner' && <RatePlannerPanel clients={clients} />}
    </div>
  );
}

// Price a placement before either side is in the system -- no real client or
// assignment record required. Any two of client rate / pay rate / margin %
// determine the third; "Solve for" picks which one is computed rather than
// typed, so there's never an ambiguous third free-typed field to reconcile.
function RatePlannerPanel({ clients }) {
  const [unit, setUnit] = useState('hr'); // 'hr' | 'mo'
  const [solveFor, setSolveFor] = useState('pay'); // 'pay' | 'margin' | 'client'
  const [clientRateStr, setClientRateStr] = useState('');
  const [payRateStr, setPayRateStr] = useState('');
  const [marginStr, setMarginStr] = useState('');
  const [expenseStr, setExpenseStr] = useState('');
  const [prefillId, setPrefillId] = useState('');

  const fmt$ = (n) => {
    const v = parseFloat(n ?? 0);
    return v === Math.round(v) ? '$' + Math.round(v).toLocaleString() : '$' + v.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2});
  };

  const cRate = parseFloat(clientRateStr);
  const pRate = parseFloat(payRateStr);
  const mPct  = parseFloat(marginStr);
  // Expenses are always a known cost, never a solved-for unknown -- blank
  // just means "none", unlike the other three fields where blank means
  // "not entered yet."
  const eRate = parseFloat(expenseStr) || 0;

  let derivedClient = !isNaN(cRate) ? cRate : null;
  let derivedPay    = !isNaN(pRate) ? pRate : null;
  let derivedMargin = !isNaN(mPct)  ? mPct  : null;

  if (solveFor === 'pay' && derivedClient > 0 && derivedMargin !== null) {
    derivedPay = Math.round((derivedClient * (1 - derivedMargin / 100) - eRate) * 100) / 100;
  } else if (solveFor === 'margin' && derivedClient > 0 && derivedPay !== null) {
    derivedMargin = ((derivedClient - derivedPay - eRate) / derivedClient) * 100;
  } else if (solveFor === 'client' && derivedMargin !== null && derivedMargin < 100 && (derivedPay || 0) + eRate > 0) {
    derivedClient = Math.round((((derivedPay || 0) + eRate) / (1 - derivedMargin / 100)) * 100) / 100;
  }

  const marginColorFor = (pct) => pct === null ? C.muted : pct >= 40 ? C.success : pct >= 20 ? '#f6c26b' : C.danger;
  const solvedFieldStyle = { background: C.bg, color: C.accent, fontWeight: '700' };

  const applyPrefill = (id) => {
    setPrefillId(id);
    const cl = clients.find(c => c.id === id);
    if (!cl) return;
    if (cl.model === 'hourly' && cl.rate) { setUnit('hr'); setClientRateStr(String(cl.rate)); }
    else if ((cl.model === 'retainer' || cl.model === 'hybrid') && cl.retainer) { setUnit('mo'); setClientRateStr(String(cl.retainer)); }
  };

  const showSummary = derivedClient > 0 && derivedPay !== null && derivedPay >= 0 && derivedMargin !== null && !isNaN(derivedMargin);

  return (
    <div style={{maxWidth:'640px'}}>
      <div style={{...css.card, marginBottom:'16px'}}>
        <div style={{fontSize:'13px',color:C.muted,marginBottom:'16px',lineHeight:1.5}}>
          Price a placement before either side is in the system yet. Pick which number to solve for, fill in the other two, and it fills itself in. Add estimated expenses to weigh them against margin too. For hybrid or retainer clients, price the base fee here — overage is a separate rate this doesn't model.
        </div>

        <div style={{display:'flex',gap:'10px',alignItems:'center',marginBottom:'18px',flexWrap:'wrap'}}>
          <SegmentedControl options={[['hr','Per Hour'],['mo','Per Month']]} value={unit} onChange={setUnit} />
          <SearchableSelect
            options={clients.filter(c=>c.is_active!==false).map(c=>({value:c.id,label:c.name}))}
            value={prefillId} onChange={applyPrefill}
            allLabel="Prefill from a client…" placeholder="Search clients…" minWidth={200} />
        </div>

        <div style={{fontSize:'11px',color:C.muted,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'8px'}}>Solve for</div>
        <div style={{marginBottom:'18px'}}>
          <SegmentedControl options={[['pay','Contractor Pay'],['margin','Margin %'],['client','Client Rate']]} value={solveFor} onChange={setSolveFor} />
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))',gap:'14px'}}>
          <div>
            <div style={css.lbl}>Client bills (${unit === 'hr' ? '/hr' : '/mo'})</div>
            <input type="number" min="0" step="0.01" placeholder="0.00"
              style={{...css.inp, ...(solveFor==='client' ? solvedFieldStyle : {})}}
              readOnly={solveFor==='client'}
              value={solveFor==='client' ? (derivedClient !== null ? derivedClient.toFixed(2) : '') : clientRateStr}
              onChange={e=>setClientRateStr(e.target.value)} />
          </div>
          <div>
            <div style={css.lbl}>Contractor pay (${unit === 'hr' ? '/hr' : '/mo'})</div>
            <input type="number" min="0" step="0.01" placeholder="0.00"
              style={{...css.inp, ...(solveFor==='pay' ? solvedFieldStyle : {})}}
              readOnly={solveFor==='pay'}
              value={solveFor==='pay' ? (derivedPay !== null ? derivedPay.toFixed(2) : '') : payRateStr}
              onChange={e=>setPayRateStr(e.target.value)} />
          </div>
          <div>
            <div style={css.lbl}><TKTooltip text="Margin = (client rate − pay rate − expenses) ÷ client rate. Doesn't account for taxes.">Margin % ⓘ</TKTooltip></div>
            <input type="number" step="1" placeholder="0"
              style={{...css.inp, ...(solveFor==='margin' ? {...solvedFieldStyle, color: marginColorFor(derivedMargin)} : {})}}
              readOnly={solveFor==='margin'}
              value={solveFor==='margin' ? (derivedMargin !== null ? Math.round(derivedMargin) : '') : marginStr}
              onChange={e=>setMarginStr(e.target.value)} />
          </div>
          <div>
            <div style={css.lbl}><TKTooltip text="Reimbursable costs the org will pay on this member's behalf for this engagement (travel, tools, etc.) — treated as an added cost, same as pay, when computing margin. Leave blank for none.">Est. expenses (${unit === 'hr' ? '/hr' : '/mo'}) ⓘ</TKTooltip></div>
            <input type="number" min="0" step="0.01" placeholder="0.00" style={css.inp}
              value={expenseStr} onChange={e=>setExpenseStr(e.target.value)} />
          </div>
        </div>
      </div>

      {showSummary && (
        <div style={{...css.card, fontSize:'13px', lineHeight:1.6}}>
          <span style={{color:C.muted}}>At </span>
          <span style={{fontWeight:'700',color:C.textBright}}>{fmt$(derivedClient)}/{unit}</span>
          <span style={{color:C.muted}}> billed, </span>
          <span style={{fontWeight:'700',color:C.textBright}}>{fmt$(derivedPay)}/{unit}</span>
          <span style={{color:C.muted}}> paid</span>
          {eRate > 0 && <>
            <span style={{color:C.muted}}>, and </span>
            <span style={{fontWeight:'700',color:C.textBright}}>{fmt$(eRate)}/{unit}</span>
            <span style={{color:C.muted}}> in expenses</span>
          </>}
          <span style={{color:C.muted}}>, that's a </span>
          <span style={{fontWeight:'700',color:marginColorFor(derivedMargin)}}>{Math.round(derivedMargin)}% margin</span>
          <span style={{color:C.muted}}> ({fmt$(derivedClient - derivedPay - eRate)}/{unit}).</span>
        </div>
      )}
    </div>
  );
}

// ─── SETTINGS ─────────────────────────────────────────────────────────────────
// section='clients' renders the client list as its own main-nav page (Page
// System spec, decision 4); it shares this component's client editing state.
function SettingsView({ clients, taskTypes, tags=[], org, profile, orgId, entries=[], invoices=[], assignments=[], extInstalled=false, qboConnected=false, onRefresh, onClientClick, editRequestId=null, onEditRequestHandled, section='settings' }) {
  // Clients and Settings are separate mounts of this view, each with its own tour.
  const tour = useTour(section === 'clients' ? 'clients' : 'settings');
  const isClientsPage = section === 'clients';
  // Five tabs instead of eleven (Page System spec). Each tab stacks its sections;
  // old '#settings:<Section>' links (e.g. the QuickBooks/Stripe OAuth return to
  // 'settings:Integrations') still work: they open the right tab and scroll to it.
  const SETTINGS_GROUPS = [
    ['Organization',  'Organization',       ['Organization']],
    ['Billing',       'Billing & invoices', ['Billing']],
    ['Notifications', 'Notifications',      ['Notifications']],
    ['Data',          'Data',               ['Task Types','Tags','Import Data']],
    ['Connections',   'Connections',        ['Integrations','Extension','Mobile App']],
  ];
  // Notifications, Data and Connections open on a row of sub-tabs, like Billing &
  // invoices, so every option is visible at the top instead of down a long scroll.
  // The key is what '#settings:<key>' deep links use (e.g. the Stripe and QuickBooks
  // return to 'settings:Integrations'), so a link to a section opens its sub-tab.
  const SUBTABS = {
    Notifications: [['My','My notifications'],['Team','Team notifications']],
    Data:          [['Task Types','Task types'],['Tags','Tags'],['Import Data','Import data']],
    Connections:   [['Integrations','Integrations'],['Extension','Extension'],['Mobile App','Mobile app']],
  };
  const groupOfSection = (name) => (SETTINGS_GROUPS.find(g => g[0] === name || g[2].includes(name)) || [])[0];
  const scrollToSection = useRef(null);
  const [tab, setTab] = useState(() => {
    if (section === 'clients') return 'Clients';
    const hash = window.location.hash.slice(1);
    if (hash.startsWith('settings:')) {
      const name = decodeURIComponent(hash.slice(9));
      const g = groupOfSection(name);
      if (g) { if (g !== name && !SUBTABS[g]) scrollToSection.current = name; return g; }
    }
    return 'Organization';
  });
  const [subTab, setSubTab] = useState(() => {
    const init = { Notifications: 'My', Data: 'Task Types', Connections: 'Integrations' };
    const hash = window.location.hash.slice(1);
    if (hash.startsWith('settings:')) {
      const name = decodeURIComponent(hash.slice(9)); const g = groupOfSection(name);
      if (g && g !== name && init[g]) init[g] = name;
    }
    return init;
  });
  // A section inside a stacked tab (e.g. Integrations under Connections): scroll to it once.
  useEffect(() => {
    const name = scrollToSection.current;
    if (!name) return;
    scrollToSection.current = null;
    setTimeout(() => document.getElementById(`settings-${name.replace(/\s+/g,'-')}`)?.scrollIntoView({ block: 'start' }), 50);
  }, [tab]);
  // Follow in-app links to a Settings section while Settings is already open.
  useEffect(() => {
    if (section === 'clients') return;
    const onHash = () => {
      const h = window.location.hash.slice(1);
      if (!h.startsWith('settings:')) return;
      const name = decodeURIComponent(h.slice(9)); const g = groupOfSection(name);
      if (!g) return;
      if (g !== name && SUBTABS[g]) setSubTab(st => ({ ...st, [g]: name }));
      else if (g !== name) scrollToSection.current = name;
      setTab(g);
      if (g === tab && g !== name && !SUBTABS[g]) setTimeout(() => document.getElementById(`settings-${name.replace(/\s+/g,'-')}`)?.scrollIntoView({ block: 'start' }), 50);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [section, tab]);
  const inGroup = (name) => groupOfSection(name) === tab;
  // A section that lives behind a sub-tab: shown only when its tab AND sub-tab are selected.
  const inSub = (name) => inGroup(name) && subTab[tab] === name;
  const changeSubTab = (group, key) => {
    setSubTab(st => ({ ...st, [group]: key }));
    if (group !== 'Notifications') window.location.hash = `settings:${key}`; // shareable, and survives a reload
  };
  // Heading for a section that shares its tab with others.
  const sectionHead = (name, title) => {
    const g = SETTINGS_GROUPS.find(x => x[0] === tab);
    if (!g || g[2].length < 2) return <span id={`settings-${name.replace(/\s+/g,'-')}`} />;
    return <h2 id={`settings-${name.replace(/\s+/g,'-')}`} style={{margin:'8px 0 10px',fontSize:'15px',fontWeight:'700',color:C.textBright,scrollMarginTop:'16px'}}>{title || name}</h2>;
  };
  const [importEntity, setImportEntity] = useState(null); // 'clients' | 'team' | 'time_entries' | null
  const [taskHelpOpen, setTaskHelpOpen] = useState(false); // Task types explainer: short by default, full text on "Read more"
  const taskHelpLink = { background:'none', border:'none', padding:0, cursor:'pointer', color:C.accent, fontSize:'12px', fontFamily:'inherit', textDecoration:'underline' };
  const [importHistoryKey, setImportHistoryKey] = useState(0); // bumped on modal close so the history list refetches
  const changeTab = (t) => { setTab(t); window.location.hash = `settings:${t}`; };
  const [orgForm, setOrgForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [billingOther, setBillingOther] = useState(false);
  const [billingSubTab, setBillingSubTab] = useState('Time Billing');
  const [invoiceSection, setInvoiceSection] = useState('display');
  const orgOriginal = useRef({});

  const [editClientId, setEditClientId] = useState(null);
  const [clientForm, setClientForm] = useState({});
  const [clientEditTab, setClientEditTab] = useState('overview'); // 'overview' | 'contact' | 'settings'
  const [clientStatus, setClientStatus] = useState('active'); // Clients page pills: active | archived | all
  const [clientSearch, setClientSearch] = useState('');
  // Same "contains" match against name/alias as My Logs' client search --
  // narrows the Clients tab list below without touching the showArchived
  // toggle's own set (search operates within whatever that toggle shows).
  const clientsShownByArchiveToggle = clients.filter(c => clientStatus === 'all' || (clientStatus === 'archived' ? c.is_active === false : c.is_active !== false));
  const clientSearchQ = clientSearch.trim().toLowerCase();
  const visibleSettingsClients = clientSearchQ
    ? clientsShownByArchiveToggle.filter(c => c.name?.toLowerCase().includes(clientSearchQ) || c.alias?.toLowerCase().includes(clientSearchQ))
    : clientsShownByArchiveToggle;
  // Per-client figures for the Clients table.
  const thisMonthKey = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();
  const clientMonthHours = useMemo(() => {
    const m = {};
    (entries||[]).forEach(e => { if (e.client_id && e.entry_date?.slice(0,7) === thisMonthKey) m[e.client_id] = (m[e.client_id]||0) + parseFloat(e.hours||0); });
    return m;
  }, [entries, thisMonthKey]);
  const clientOutstanding = useMemo(() => {
    const m = {};
    (invoices||[]).forEach(i => { if (i.status === 'sent') m[i.client_id] = (m[i.client_id]||0) + parseFloat(i.total||0); });
    return m;
  }, [invoices]);
  const [confirmRemove, setConfirmRemove] = useState(null); // client object | null
  const [endClientModal, setEndClientModal] = useState(null); // {client, endDate}

  const [newTask, setNewTask] = useState('');
  const [editTask, setEditTask] = useState(null);
  const [editTaskVal, setEditTaskVal] = useState('');

  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#31A7D9');
  const [editTagId, setEditTagId] = useState(null);
  const [editTagName, setEditTagName] = useState('');
  const [editTagColor, setEditTagColor] = useState('#31A7D9');
  const [, setThemeVersion] = useState(0);
  const savedTheme = useRef(null);
  const loadedOrgForm = useRef(null); // JSON of the org as last loaded/saved, for the unsaved-changes bar

  const [qboConn, setQboConn] = useState(null);      // null = loading, false = none, object = connected
  const [qboLoading, setQboLoading] = useState(false);

  const loadQboConn = async () => {
    if (!orgId) return;
    const { data } = await supabase.from('qbo_connections').select('realm_id,environment,connected_at').eq('org_id', orgId).maybeSingle();
    setQboConn(data || false);
  };

  const connectQbo = async () => {
    setQboLoading(true);
    const { data, error } = await supabase.functions.invoke('qbo-auth-url', { body: { org_id: orgId } });
    setQboLoading(false);
    if (error || !data?.url) { notifyError('Could not start QuickBooks connection: ' + (error?.message || 'unknown error')); return; }
    window.location.href = data.url;
  };

  // Revokes TimeKeep's access at Intuit, then removes the connection (qbo-disconnect).
  // It used to delete the row from the browser, which left the tokens valid at Intuit.
  const disconnectQbo = async () => {
    if (!window.confirm('Disconnect QuickBooks Online? TimeKeep will stop syncing with QuickBooks. Anything already in QuickBooks stays there.')) return;
    setQboLoading(true);
    const { data, error } = await supabase.functions.invoke('qbo-disconnect', { body: { org_id: orgId } });
    setQboLoading(false);
    if (error || !data?.ok) { notifyError('Could not disconnect QuickBooks: ' + (data?.error || error?.message || 'unknown error')); return; }
    if (data.note) notifyError(data.note);
    setQboConn(false);
  };

  useEffect(() => { loadQboConn(); }, [orgId]); // eslint-disable-line

  // ── Stripe (Standard Connect) ──────────────────────────────────────────────
  // The org links its OWN Stripe account; payments land there and Stripe bills
  // its fees there. All writes go through the stripe-connect edge function;
  // organizations.stripe_enabled is derived server-side (connected + Stripe
  // lets it take charges + Pay Now switched on), never set from here.
  const [stripeConn, setStripeConn] = useState(null);       // null = loading, false = none, object = connected
  const [stripeBusy, setStripeBusy] = useState('');          // '', 'connect', 'toggle', 'disconnect'
  const [stripeNote, setStripeNote] = useState('');
  const [confirmStripeDisconnect, setConfirmStripeDisconnect] = useState(false);

  const stripeCall = async (action, extra = {}) => {
    const { data, error } = await supabase.functions.invoke('stripe-connect', { body: { org_id: orgId, action, ...extra } });
    if (error || !data?.ok) {
      let detail = data?.error || error?.message || 'unknown error';
      try { const j = await error?.context?.json?.(); if (j?.error) detail = j.error; } catch (_) {}
      throw new Error(detail);
    }
    return data;
  };

  // What Stripe says is still outstanding (from the status call); null when unknown.
  const [stripeReq, setStripeReq] = useState(null);
  const stripeRecheck = useRef(0);

  // Plain-language names for Stripe's requirement keys, grouped so the card
  // lists a handful of things to do rather than every field id.
  const stripeReqLabels = (req) => {
    const keys = [...(req?.past_due || []), ...(req?.currently_due || [])];
    const label = (k) =>
      k.startsWith('external_account') ? 'a bank account for payouts'
      : k.startsWith('tos_acceptance') ? "accepting Stripe's terms"
      : k.includes('verification.document') ? 'an ID document'
      : k.startsWith('business_profile') ? 'business details'
      : k.startsWith('company') ? 'company details'
      : (k.startsWith('individual') || k.startsWith('representative') || k.startsWith('person')) ? 'personal details'
      : 'more account details';
    return Array.from(new Set(keys.map(label)));
  };

  const loadStripeConn = async () => {
    if (!orgId) return;
    try {
      const data = await stripeCall('status');
      setStripeConn(data.connection || false);
      setStripeReq(data.requirements || null);
      setStripeNote(data.revoked ? 'Stripe says TimeKeep no longer has access to that account, so it was disconnected here too.' : (data.warning ? `Couldn't refresh status from Stripe: ${data.warning}` : ''));
      // Deliberately not mirrored into orgForm.stripe_enabled: nothing here reads
      // it (the card uses stripeConn), and writing it would trip the Settings
      // save bar's unsaved-changes check for a change the admin never made.
    } catch (e) {
      // Fall back to the stored row so the card still renders if Stripe is unreachable.
      const { data } = await supabase.from('stripe_connections')
        .select('stripe_account_id,livemode,account_name,charges_enabled,payouts_enabled,details_submitted,pay_now_enabled,connected_at,status_checked_at')
        .eq('org_id', orgId).maybeSingle();
      setStripeConn(data || false);
      setStripeNote(e.message.includes('not configured') ? '' : `Couldn't refresh status from Stripe: ${e.message}`);
    }
  };

  useEffect(() => { loadStripeConn(); }, [orgId]); // eslint-disable-line

  // A just-connected account often can't take charges for a minute while Stripe
  // verifies it. When Stripe lists nothing for the org to do, re-check every 5s,
  // capped at 12 tries so a stuck account never polls forever.
  useEffect(() => {
    const verifying = stripeConn && !stripeConn.charges_enabled && stripeReqLabels(stripeReq).length === 0;
    if (!verifying) { stripeRecheck.current = 0; return; }
    if (stripeRecheck.current >= 12) return;
    const t = setTimeout(() => { stripeRecheck.current += 1; loadStripeConn(); }, 5000);
    return () => clearTimeout(t);
  }, [stripeConn, stripeReq]); // eslint-disable-line

  const connectStripe = async () => {
    setStripeBusy('connect');
    try {
      const data = await stripeCall('start', { return_to: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setStripeBusy('');
      notifyError('Could not start the Stripe connection: ' + e.message);
    }
  };

  const toggleStripePayNow = async () => {
    if (!stripeConn) return;
    setStripeBusy('toggle');
    try {
      const data = await stripeCall('set_pay_now', { pay_now_enabled: !stripeConn.pay_now_enabled });
      setStripeConn(data.connection || false);
    } catch (e) { notifyError('Could not update Pay Now: ' + e.message); }
    setStripeBusy('');
  };

  const disconnectStripe = async () => {
    setStripeBusy('disconnect');
    try {
      await stripeCall('disconnect');
      setStripeConn(false);
      setStripeNote('');
    } catch (e) { notifyError('Could not disconnect Stripe: ' + e.message); }
    setStripeBusy('');
    setConfirmStripeDisconnect(false);
  };

  // Self-contained toggle (not tied to the Organization tab's Save button) —
  // matches the immediate-effect pattern QBO Connect/Disconnect already use here.
  // A field saved on its own (toggles, extension settings, logo upload) is saved:
  // record it in the unsaved-changes baseline so the save bar doesn't claim otherwise.
  const markOrgFieldSaved = (field, value) => {
    if (!loadedOrgForm.current) return;
    loadedOrgForm.current = JSON.stringify({ ...JSON.parse(loadedOrgForm.current), [field]: value });
  };
  const toggleOrgFlag = async (field) => {
    const newVal = !orgForm[field];
    setOrg(field, newVal);
    const { error } = await supabase.from('organizations').update({ [field]: newVal }).eq('id', orgId);
    if (error) { notifyError(`Update failed: ${error.message}`); setOrg(field, !newVal); return; }
    markOrgFieldSaved(field, newVal);
    onRefresh();
  };

  const [qboSyncingClient, setQboSyncingClient] = useState(null); // client id being synced
  const [qboSyncAllProgress, setQboSyncAllProgress] = useState(null); // null | {synced,total}

  const syncClientToQbo = async (clientId) => {
    setQboSyncingClient(clientId);
    const { data, error } = await supabase.functions.invoke('qbo-sync-client', { body: { org_id: orgId, client_id: clientId } });
    setQboSyncingClient(null);
    if (error || !data?.ok) {
      notifyError('QBO client sync failed: ' + (error?.message || data?.error || 'unknown error'));
    } else {
      onRefresh();
    }
  };

  const [settingsToast, setSettingsToast] = useState('');
  const showSettingsToast = (msg, ms = 4000) => { setSettingsToast(msg); setTimeout(() => setSettingsToast(''), ms); };

  const syncAllClientsToQbo = async () => {
    const eligible = clients.filter(c => c.is_active !== false && c.qbo_sync_enabled !== false);
    setQboSyncAllProgress({ synced: 0, total: eligible.length });
    const { data, error } = await supabase.functions.invoke('qbo-sync-all-clients', { body: { org_id: orgId } });
    setQboSyncAllProgress(null);
    if (error || !data?.ok) {
      notifyError('QBO sync all failed: ' + (error?.message || data?.error || 'unknown error'));
    } else {
      onRefresh();
      if (data.failed > 0) {
        notifyError(`Synced ${data.synced} clients to QBO. ${data.failed} failed: ${data.errors?.join(', ')}`);
      } else {
        showSettingsToast(`✓ Synced ${data.synced} client${data.synced !== 1 ? 's' : ''} to QuickBooks`);
      }
    }
  };

  useEffect(() => {
    if (!orgId) return;
    supabase.from('organizations').select('*').eq('id', orgId).single()
      .then(({ data }) => {
        if (data) {
          setOrgForm(data);
          loadedOrgForm.current = JSON.stringify(data);
          applyTheme(data);
          orgOriginal.current = { name: data.name, tagline: data.tagline, logo_url: data.logo_url };
          savedTheme.current = { theme: data.theme, primary_color: data.primary_color, accent_color: data.accent_color };
          if (data.billing_increment && ![6,15,30,0].includes(data.billing_increment)) setBillingOther(true);
        }
      });
  }, [orgId]);

  // Live theme preview — apply changes immediately as user picks colors/theme
  useEffect(() => {
    if (!orgForm.theme && !orgForm.accent_color) return;
    applyTheme(orgForm);
    setThemeVersion(v => v + 1);
    window.dispatchEvent(new Event('tk-theme-preview'));
  }, [orgForm.theme, orgForm.accent_color, orgForm.primary_color]);

  // Leaving Settings with an unsaved theme preview puts the saved theme back
  // (savedTheme tracks what's actually in the database: loaded, or last saved).
  useEffect(() => () => {
    if (!savedTheme.current) return;
    applyTheme(savedTheme.current);
    window.dispatchEvent(new Event('tk-theme-preview'));
  }, []);

  const setOrg = (k, v) => setOrgForm(f => ({...f, [k]: v}));

  const saveOrg = async () => {
    setSaving(true); setSaveMsg('');
    const sidebarChanged = orgOriginal.current.name !== orgForm.name ||
                           orgOriginal.current.tagline !== orgForm.tagline ||
                           orgOriginal.current.logo_url !== orgForm.logo_url;
    // stripe_enabled is derived server-side from the Stripe connection; the
    // database ignores browser writes to it, so leave it out of the save.
    const { stripe_enabled, ...orgPayload } = orgForm; // eslint-disable-line no-unused-vars
    const { error } = await supabase.from('organizations').update(orgPayload).eq('id', orgId);
    setSaveMsg(error ? error.message : 'Changes saved.');
    setSaving(false);
    if (!error) {
      savedTheme.current = { theme: orgForm.theme, primary_color: orgForm.primary_color, accent_color: orgForm.accent_color };
      loadedOrgForm.current = JSON.stringify(orgForm);
      onRefresh();
      if (sidebarChanged) setTimeout(() => window.location.reload(), 800);
    }
    setTimeout(() => setSaveMsg(''), 3000);
  };

  const setCF = (k, v) => setClientForm(f => ({...f, [k]: v}));
  const openClient = (c) => { if (editClientId===c.id){setEditClientId(null);return;} setEditClientId(c.id); setClientForm({...c}); setClientEditTab('overview'); };
  // The client record drawer's Edit button asks for this same editor from any tab or page.
  useEffect(() => {
    if (!editRequestId) return;
    const c = clients.find(x => x.id === editRequestId);
    if (c) { setEditClientId(c.id); setClientForm({ ...c }); setClientEditTab('overview'); }
    onEditRequestHandled?.();
  }, [editRequestId]); // eslint-disable-line
  // clients.model/rate/retainer/hours/overage are a deprecated cache fed from
  // client_billing_terms by trigger. Writing them here would make the cache
  // disagree with the terms history, which billing_cache_drift then reports.
  // Billing is edited through the ClientBillingTerms panel instead.
  const BILLING_CACHE_COLUMNS = ['model','rate','retainer','hours','overage'];
  const saveClient = async () => {
    const payload = Object.fromEntries(
      Object.entries(clientForm).filter(([k]) => !BILLING_CACHE_COLUMNS.includes(k)));
    const ok = await runDb('Saving client failed', supabase.from('clients').update(payload).eq('id',editClientId));
    if (ok) setEditClientId(null);
    onRefresh();
  };
  // Free plan: hard cap of 5 active clients (spec/pricing-tiers.html §free tier).
  const FREE_ACTIVE_CLIENT_CAP = 5;
  const freeClientCapReached = isFreeClientCapReached(org?.plan, clients, FREE_ACTIVE_CLIENT_CAP);
  const addClient  = async () => {
    if (freeClientCapReached) { notifyError(`The Free plan is limited to ${FREE_ACTIVE_CLIENT_CAP} active clients. Upgrade to Pro or Max to add more.`); return; }
    const { data, error } = await supabase.from('clients').insert({org_id:orgId,name:"New Client",model:"hourly",rate:orgForm.default_hourly_rate??275}).select().single();
    if (error || !data) { notifyError('Adding client failed: ' + (error?.message || 'unknown error')); return; }
    setEditClientId(data.id); setClientForm({...data}); onRefresh();
  };
  const delClient  = async (id) => {
    const { error } = await supabase.from('clients').delete().eq('id',id);
    if (error) {
      if (error.code === '23503' || error.message?.includes('foreign key')) {
        notifyError("Clients cannot be deleted if related records exist (time entries, invoices, assignments, etc.). If you recently deleted entries, try refreshing the page and deleting again. Otherwise, use End Contract to archive the client instead.");
      } else {
        notifyError('Removing client failed: ' + friendlyDbError(error) + '.');
      }
    }
    setConfirmRemove(null);
    onRefresh();
  };

  // Archiving: retirement path for clients with billing history (deletion is
  // blocked by FK RESTRICT). Archived clients keep all historical data but
  // disappear from timer dropdowns, assignment pickers, and new invoices.
  const clientHasHistory = (id) => entries.some(e=>e.client_id===id) || invoices.some(i=>i.client_id===id) || assignments.some(a=>a.client_id===id);
  const archiveClient = async (id, endedAt) => {
    if (editClientId===id) setEditClientId(null);
    await runDb('Ending client contract failed', supabase.from('clients').update({ is_active: false, ended_at: endedAt ? new Date(endedAt).toISOString() : new Date().toISOString() }).eq('id',id));
    setEndClientModal(null);
    onRefresh();
  };
  const restoreClient = async (id) => {
    if (freeClientCapReached) { notifyError(`The Free plan is limited to ${FREE_ACTIVE_CLIENT_CAP} active clients. Upgrade to Pro or Max to restore more.`); return; }
    const c = clients.find(cl=>cl.id===id);
    await runDb('Restoring client failed', supabase.from('clients').update({ is_active: true, last_ended_at: c?.ended_at || null, ended_at: null }).eq('id',id));
    onRefresh();
  };

  const addTask  = async () => { const t=newTask.trim(); if(!t)return; const ok = await runDb('Adding task type failed', supabase.from('task_types').insert({org_id:orgId,name:t})); if (ok) setNewTask(''); onRefresh(); };
  const delTask  = async (id) => { await runDb('Removing task type failed', supabase.from('task_types').delete().eq('id',id)); onRefresh(); };
  const saveTaskDefault = async (id, value) => { await runDb('Saving task type failed', supabase.from('task_types').update({ billable_by_default: value }).eq('id',id)); onRefresh(); };
  const saveTask = async (id) => { const v=editTaskVal.trim(); if(!v)return; const ok = await runDb('Renaming task type failed', supabase.from('task_types').update({name:v}).eq('id',id)); if (ok) setEditTask(null); onRefresh(); };

  const addTag  = async () => { const n=newTagName.trim(); if(!n)return; const ok = await runDb('Adding tag failed', supabase.from('tags').insert({org_id:orgId,name:n,color:newTagColor||null})); if(ok){setNewTagName('');setNewTagColor('#31A7D9');} onRefresh(); };
  const delTag  = async (id) => { await runDb('Removing tag failed', supabase.from('tags').delete().eq('id',id)); onRefresh(); };
  const saveTag = async (id) => { const v=editTagName.trim(); if(!v)return; const ok = await runDb('Saving tag failed', supabase.from('tags').update({name:v,color:editTagColor||null}).eq('id',id)); if(ok)setEditTagId(null); onRefresh(); };

  // Live-save a single extension settings column without touching the rest of orgForm.
  const saveExtSetting = async (key, value) => {
    setOrg(key, value);
    if (await runDb('Saving extension setting failed', supabase.from('organizations').update({ [key]: value }).eq('id', orgId))) markOrgFieldSaved(key, value);
  };
  const saveExtFabApp = async (appKey, value) => {
    const next = { ...(orgForm.ext_fab_apps || {}), [appKey]: value };
    setOrg('ext_fab_apps', next);
    if (await runDb('Saving extension setting failed', supabase.from('organizations').update({ ext_fab_apps: next }).eq('id', orgId))) markOrgFieldSaved('ext_fab_apps', next);
  };

  const mBadge = (m) => modelBadge(m);
  const mLabel = (m) => modelLabel(m, "tableLabel");
  // Unknown model renders empty rather than falling through to the hybrid
  // price string, which is what the old ternary chain did.
  const mSub   = (c) => BILLING_MODELS[c.model] ? BILLING_MODELS[c.model].summary(configFromClient(c)) : "";

  const DAYS   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  // Org settings save through one sticky bar (below) that appears only when the
  // form differs from what's saved. The old per-section Save rows are gone.
  const saveBar = null;
  const orgDirty = !isClientsPage && loadedOrgForm.current !== null && JSON.stringify(orgForm) !== loadedOrgForm.current;
  const discardOrg = () => { if (loadedOrgForm.current) setOrgForm(JSON.parse(loadedOrgForm.current)); };

  const Toggle = ({ label, desc, k }) => (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"12px 0",borderBottom:`1px solid rgba(30,39,54,0.5)`}}>
      <div>
        <div style={{fontSize:"13px",color:C.textBright,fontWeight:"500"}}>{label}</div>
        {desc && <div style={{fontSize:"11px",color:C.muted,marginTop:"2px"}}>{desc}</div>}
      </div>
      <button
        onClick={() => setOrg(k, !orgForm[k])}
        style={{width:"40px",height:"22px",borderRadius:"11px",border:"none",cursor:"pointer",flexShrink:0,
          background:orgForm[k]?C.accent:C.border,position:"relative",transition:"background 0.2s"}}
      >
        <div style={{position:"absolute",top:"3px",left:orgForm[k]?"21px":"3px",
          width:"16px",height:"16px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
      </button>
    </div>
  );

  // One-line settings-list row for the client editor -- invertDefault covers
  // invoice_reminders_enabled/qbo_sync_enabled, which default to true when
  // the column is null (unlike is_nonprofit/is_internal/po_required, which
  // default to false). Same on/off semantics as the original per-field
  // toggles, just laid out as a compact row instead of its own bordered card.
  const CToggle = ({ label, desc, k, invertDefault }) => {
    const on = invertDefault ? clientForm[k] !== false : !!clientForm[k];
    const flip = () => setCF(k, invertDefault ? (clientForm[k] === false ? true : false) : !clientForm[k]);
    return (
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid rgba(30,39,54,0.4)`}}>
        <div>
          <div style={{fontSize:"12px",color:C.textBright}}>{label}</div>
          {desc && <div style={{fontSize:"10px",color:C.muted,marginTop:"1px"}}>{desc}</div>}
        </div>
        <button onClick={flip}
          style={{width:"32px",height:"18px",borderRadius:"9px",border:"none",cursor:"pointer",flexShrink:0,
            background:on?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
          <div style={{position:"absolute",top:"2px",left:on?"16px":"2px",
            width:"14px",height:"14px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
        </button>
      </div>
    );
  };

  const setOptOutAllowed = (k, v) => setOrg('notifications_opt_out_allowed', {...(orgForm.notifications_opt_out_allowed||{}), [k]: v});

  const ContractorToggle = ({ label, desc, k, children }) => {
    const canOptOut = !!orgForm.notifications_opt_out_allowed?.[k];
    return (
      <div style={{padding:"12px 0",borderBottom:`1px solid rgba(30,39,54,0.5)`}}>
        <div style={{display:"flex",alignItems:"flex-start",gap:"12px"}}>
          <div style={{flex:1}}>
            <div style={{fontSize:"13px",color:C.textBright,fontWeight:"500"}}>{label}</div>
            {desc && <div style={{fontSize:"11px",color:C.muted,marginTop:"2px"}}>{desc}</div>}
          </div>
          <button onClick={() => setOrg(k, !orgForm[k])}
            style={{width:"40px",height:"22px",borderRadius:"11px",border:"none",cursor:"pointer",flexShrink:0,
              background:orgForm[k]?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
            <div style={{position:"absolute",top:"3px",left:orgForm[k]?"21px":"3px",
              width:"16px",height:"16px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
          </button>
          <button onClick={() => setOptOutAllowed(k, !canOptOut)} title="Let members turn this off for themselves"
            style={{width:"40px",height:"22px",borderRadius:"11px",border:"none",cursor:"pointer",flexShrink:0,
              background:canOptOut?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
            <div style={{position:"absolute",top:"3px",left:canOptOut?"21px":"3px",
              width:"16px",height:"16px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
          </button>
        </div>
        {orgForm[k] && children && <div style={{marginTop:"10px"}}>{children}</div>}
      </div>
    );
  };

  return (
    <Page title={isClientsPage ? 'Clients' : 'Settings'}
      onTour={tour.launch}
      actions={isClientsPage ? <button style={css.btn('secondary')} onClick={()=>window.open(window.location.origin+'/#client-map','_blank')}>Client map</button> : null}
      primary={isClientsPage ? <button style={css.btn('primary')} onClick={addClient}>+ Add client</button> : null}
      tabs={isClientsPage ? null : { tabs: SETTINGS_GROUPS.map(g=>[g[0],g[1]]), active: tab, onChange: changeTab }}
      tour={{ tabs: 'settings-tabs' }}>
      {tour.show && <WalkthroughModal tourKey={isClientsPage ? 'clients' : 'settings'} onClose={tour.close} />}

      {/* ── Organization ── */}
      {SUBTABS[tab] && (
        <div style={{margin:'0 0 20px'}}>
          <TabBar tabs={SUBTABS[tab]} active={subTab[tab]} onChange={k=>changeSubTab(tab,k)} />
        </div>
      )}

      {inGroup('Organization') && sectionHead('Organization','Organization')}
      {inGroup('Organization') && (
        <div style={{maxWidth:"600px"}}>
          <div style={{display:'flex',alignItems:'flex-start',gap:'10px',padding:'12px 14px',marginBottom:'12px',
            background:`${C.accent}0f`,border:`1px solid ${C.accent}33`,borderRadius:'8px',fontSize:'12px',color:C.muted,lineHeight:'1.5'}}>
            <span style={{fontSize:'16px',flexShrink:0,marginTop:'1px'}}>🏢</span>
            <div>
              <span style={{fontWeight:'600',color:C.text}}>Organization settings apply to everyone in your org.</span>{' '}
              Identity controls your logo and sidebar appearance. Theme &amp; Colors customizes your branding. Contact info and address print on client invoices. Billing defaults set the starting rates for new clients.
            </div>
          </div>
          <div style={css.card}>
            <div style={{...css.h2,marginBottom:"16px"}}>Identity</div>
            <div style={{marginBottom:"12px"}}>
              <div style={css.lbl}>Organization Name</div>
              <input style={css.inp} value={orgForm.name||''} onChange={e=>setOrg('name',e.target.value)} />
            </div>
            <div style={{marginBottom:"12px"}}>
              <div style={css.lbl}><TKTooltip text="Short phrase shown under your org name in the sidebar. Defaults to 'Billing Dashboard'.">Tagline ⓘ</TKTooltip></div>
              <input style={css.inp} value={orgForm.tagline||''} onChange={e=>setOrg('tagline',e.target.value)} />
            </div>
            <div>
              <div style={css.lbl}><TKTooltip text="Shown in the sidebar and on all outgoing invoices. Recommended: PNG with transparent background, at least 200px wide.">Logo ⓘ</TKTooltip></div>
              <div style={{padding:"20px",border:`2px dashed ${C.border}`,borderRadius:"8px",textAlign:"center",color:C.muted,fontSize:"12px"}}>
                {orgForm.logo_url && (
                  // Previewed on the sidebar color, where the logo actually appears. On the form
                  // background a white logo was invisible under the light theme.
                  <div style={{marginBottom:"12px",display:"inline-flex",flexDirection:"column",alignItems:"center",gap:"6px"}}>
                    <div style={{background:orgForm.primary_color || C.primary,borderRadius:"8px",padding:"14px 20px"}}>
                      <img src={orgForm.logo_url} alt="Logo" style={{maxHeight:"60px",maxWidth:"200px",display:"block"}} />
                    </div>
                    <div style={{fontSize:"11px",color:C.muted}}>As it appears in the sidebar</div>
                  </div>
                )}
                {logoUploading
                  ? <div style={{color:C.accent}}>Uploading...</div>
                  : (
                    <label style={{cursor:"pointer",color:C.accent,fontWeight:600}}>
                      {orgForm.logo_url ? 'Replace logo' : 'Upload logo'}
                      <input
                        type="file"
                        accept="image/*"
                        style={{display:"none"}}
                        onChange={async e => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setLogoUploading(true);
                          const path = `${orgId}/${file.name}`;
                          const { error: upErr } = await supabase.storage.from('org-logos').upload(path, file, { upsert: true });
                          if (upErr) { notifyError('Logo upload failed: ' + upErr.message); setLogoUploading(false); return; }
                          const { data: { publicUrl } } = supabase.storage.from('org-logos').getPublicUrl(path);
                          setOrg('logo_url', publicUrl);
                          if (await runDb('Saving logo failed', supabase.from('organizations').update({ logo_url: publicUrl }).eq('id', orgId))) markOrgFieldSaved('logo_url', publicUrl);
                          setLogoUploading(false);
                        }}
                      />
                    </label>
                  )
                }
                <div style={{marginTop:"6px",fontSize:"11px",color:C.muted}}>PNG, JPG, or SVG recommended</div>
              </div>
            </div>
          </div>

          <div style={css.card}>
            <div style={{...css.h2,marginBottom:"16px"}}>Theme &amp; Colors</div>

            {/* Preset Cards */}
            <div style={{marginBottom:"20px"}}>
              <div style={css.lbl}><TKTooltip text="One-click theme presets. Selecting one sets the sidebar color, highlight color, and dark/light mode together.">Presets ⓘ</TKTooltip></div>
              <div style={{display:"flex",flexWrap:"wrap",gap:"10px",marginTop:"8px"}}>
                {PRESETS.map(p => {
                  const isActive = orgForm.theme===p.mode && orgForm.primary_color===p.primary && orgForm.accent_color===p.accent;
                  const bgMain = p.mode==='dark' ? '#0A1526' : '#f0f4f8';
                  return (
                    <div key={p.id} onClick={()=>{setOrg('theme',p.mode);setOrg('primary_color',p.primary);setOrg('accent_color',p.accent);}}
                      style={{cursor:'pointer',borderRadius:'8px',overflow:'hidden',width:'76px',
                        border:isActive?`2px solid ${C.accent}`:`2px solid ${C.border}`,
                        boxShadow:isActive?`0 0 0 1px ${C.accent}44`:'none'}}>
                      <div style={{display:'flex',height:'48px'}}>
                        <div style={{width:'20px',background:p.primary,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'4px',padding:'4px 0'}}>
                          <div style={{width:'10px',height:'2px',borderRadius:'2px',background:p.accent}} />
                          <div style={{width:'10px',height:'2px',borderRadius:'2px',background:'rgba(255,255,255,0.25)'}} />
                          <div style={{width:'10px',height:'2px',borderRadius:'2px',background:'rgba(255,255,255,0.25)'}} />
                          <div style={{width:'10px',height:'2px',borderRadius:'2px',background:'rgba(255,255,255,0.25)'}} />
                        </div>
                        <div style={{flex:1,background:bgMain,padding:'5px 4px',display:'flex',flexDirection:'column',gap:'3px'}}>
                          <div style={{height:'5px',borderRadius:'2px',background:p.mode==='dark'?'#16233F':'#e2e8f0',width:'100%'}} />
                          <div style={{height:'3px',borderRadius:'2px',background:p.mode==='dark'?'#28395C':'#d1d9e0',width:'80%'}} />
                          <div style={{height:'3px',borderRadius:'2px',background:p.accent+'55',width:'55%'}} />
                          <div style={{height:'3px',borderRadius:'2px',background:p.mode==='dark'?'#28395C':'#d1d9e0',width:'70%'}} />
                        </div>
                      </div>
                      <div style={{padding:'4px 0',fontSize:'10px',fontWeight:'500',color:C.text,textAlign:'center',background:C.surface,borderTop:`1px solid ${C.border}`}}>
                        {p.label}
                        <span style={{fontSize:'9px',color:C.muted,marginLeft:'3px',textTransform:'capitalize'}}>{p.mode}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Customize */}
            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:"16px"}}>
              <div style={css.lbl}><TKTooltip text="Override individual colors without switching the full preset. Changes here take effect immediately.">Customize ⓘ</TKTooltip></div>

              {/* Dark / Light mode toggle */}
              <div style={{marginTop:'8px',marginBottom:'14px'}}>
                <SegmentedControl options={[['dark','Dark'],['light','Light']]} value={orgForm.theme==='light' ? 'light' : 'dark'} onChange={(m)=>setOrg('theme',m)} />
              </div>

              <div style={css.g2}>
                <div>
                  <div style={css.lbl}><TKTooltip text="Background color of the left navigation sidebar. Enter a hex value or use the color picker.">Sidebar Color ⓘ</TKTooltip></div>
                  <div style={{display:"flex",alignItems:"center",gap:"8px",marginTop:"4px"}}>
                    <input type="color" value={orgForm.primary_color||'#033151'} onChange={e=>setOrg('primary_color',e.target.value)}
                      style={{width:"36px",height:"36px",padding:"2px",borderRadius:"6px",border:`1px solid ${C.border}`,background:C.bg,cursor:"pointer"}} />
                    <input style={{...css.inp,flex:1}} value={orgForm.primary_color||''} onChange={e=>setOrg('primary_color',e.target.value)} placeholder="#033151" />
                  </div>
                </div>
                <div>
                  <div style={css.lbl}><TKTooltip text="Accent color used for active nav items, buttons, badges, and links throughout the app.">Highlight Color ⓘ</TKTooltip></div>
                  <div style={{display:"flex",alignItems:"center",gap:"8px",marginTop:"4px"}}>
                    <input type="color" value={orgForm.accent_color||'#31A7D9'} onChange={e=>setOrg('accent_color',e.target.value)}
                      style={{width:"36px",height:"36px",padding:"2px",borderRadius:"6px",border:`1px solid ${C.border}`,background:C.bg,cursor:"pointer"}} />
                    <input style={{...css.inp,flex:1}} value={orgForm.accent_color||''} onChange={e=>setOrg('accent_color',e.target.value)} placeholder="#31A7D9" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={css.card}>
            <div style={{...css.h2,marginBottom:"16px"}}>Contact &amp; Locale</div>
            <div style={{...css.g2,marginBottom:"12px"}}>
              <div><div style={css.lbl}>Website</div><input style={css.inp} value={orgForm.website||''} onChange={e=>setOrg('website',e.target.value)} placeholder="https://" /></div>
              <div><div style={css.lbl}>Phone</div><input style={css.inp} value={orgForm.phone||''} onChange={e=>setOrg('phone',e.target.value)} /></div>
            </div>
            <div style={{marginBottom:"12px"}}>
              <div style={css.lbl}>Address Line 1</div>
              <input style={css.inp} placeholder="123 Main St" value={orgForm.address_line1||''} onChange={e=>setOrg('address_line1',e.target.value)} />
            </div>
            <div style={{marginBottom:"12px"}}>
              <div style={css.lbl}>Address Line 2 <span style={{color:C.muted,fontWeight:"400"}}>(Suite, Unit, etc.)</span></div>
              <input style={css.inp} placeholder="Suite 200" value={orgForm.address_line2||''} onChange={e=>setOrg('address_line2',e.target.value)} />
            </div>
            <div style={{...css.g2,marginBottom:"12px"}}>
              <div><div style={css.lbl}>City</div><input style={css.inp} placeholder="New York" value={orgForm.city||''} onChange={e=>setOrg('city',e.target.value)} /></div>
              <div><div style={css.lbl}>State</div><input style={css.inp} placeholder="NY" value={orgForm.state||''} onChange={e=>setOrg('state',e.target.value)} /></div>
            </div>
            <div style={{...css.g2,marginBottom:"12px"}}>
              <div><div style={css.lbl}>ZIP / Postal Code</div><input style={css.inp} placeholder="10001" value={orgForm.zip||''} onChange={e=>setOrg('zip',e.target.value)} /></div>
              <div><div style={css.lbl}>Country</div><input style={css.inp} placeholder="United States" value={orgForm.country||''} onChange={e=>setOrg('country',e.target.value)} /></div>
            </div>
            <div style={css.g2}>
              <div>
                <div style={css.lbl}><TKTooltip text="Used to schedule notification emails at the correct local time (weekly summaries, inactivity alerts, etc.).">Timezone ⓘ</TKTooltip></div>
                <select style={css.sel} value={orgForm.timezone||'America/New_York'} onChange={e=>setOrg('timezone',e.target.value)}>
                  {tzOptions(orgForm.timezone||'America/New_York').map(tz=><option key={tz} value={tz}>{tzLabel(tz)}</option>)}
                </select>
              </div>
              <div>
                <div style={css.lbl}><TKTooltip text="Display currency for all dollar amounts throughout the app and on invoices.">Currency ⓘ</TKTooltip></div>
                <select style={css.sel} value={orgForm.currency||'USD'} onChange={e=>setOrg('currency',e.target.value)}>
                  {currencyOptions(orgForm.currency||'USD').map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>

          {saveBar}
        </div>
      )}

      {/* ── Billing ── */}
      {inGroup('Billing') && sectionHead('Billing','Billing')}
      {inGroup('Billing') && (
        <div style={{maxWidth: billingSubTab==='Invoices' ? "1180px" : "600px"}}>
          {/* Billing sub-tabs */}
          <div style={{marginBottom:"20px"}}>
            <TabBar tabs={[['Time Billing','Time Billing'],['Invoices','Invoices'],['Expenses','Expenses']]} active={billingSubTab} onChange={setBillingSubTab} />
          </div>

          {/* ── Expenses sub-tab ── */}
          {billingSubTab==='Expenses' && (
            <div>
              <div style={css.card}>
                <div style={{...css.h2,marginBottom:"16px"}}>Expense Policy</div>
                <div style={{...css.g2,marginBottom:"12px"}}>
                  <div>
                    <div style={css.lbl}>
                      <TKTooltip text="Expenses above this amount cannot be submitted for approval without a receipt attached. The IRS substantiation threshold is $75. Set to 0 to require a receipt on everything.">Require receipt above ⓘ</TKTooltip>
                    </div>
                    <input style={css.inp} type="number" min="0" step="5"
                      value={orgForm.expense_require_receipt_over ?? 75}
                      onChange={e=>setOrg('expense_require_receipt_over', parseFloat(e.target.value)||0)} />
                  </div>
                  <div>
                    <div style={css.lbl}>
                      <TKTooltip text="Default markup applied when an expense is billed to a client. Overridable per expense. Markup never affects reimbursement — the person who paid is always repaid at cost.">Default client markup % ⓘ</TKTooltip>
                    </div>
                    <input style={css.inp} type="number" min="0" step="1"
                      value={orgForm.expense_default_markup ?? 0}
                      onChange={e=>setOrg('expense_default_markup', parseFloat(e.target.value)||0)} />
                  </div>
                </div>
                <div style={{fontSize:'12px',color:C.muted}}>
                  Markup applies to the client invoice only. If a contractor spends $100 and you
                  bill at 10%, the client is charged $110 and the contractor is repaid $100.
                </div>
              </div>

              <MileageRatesPanel orgId={orgId} />
            </div>
          )}

          {/* ── Time Billing sub-tab ── */}
          {billingSubTab==='Time Billing' && (
            <div>
              <div style={css.card}>
                <div style={{...css.h2,marginBottom:"16px"}}>Time Billing</div>
                <div style={{...css.g2,marginBottom:"12px"}}>
                  <div>
                    <div style={css.lbl}><TKTooltip text="Time entries are rounded up to this increment when calculating billing totals. e.g. 15 min means 8 minutes → billed as 15 minutes.">Billing Increment ⓘ</TKTooltip></div>
                    <select style={css.sel}
                      value={billingOther ? 'other' : [6,15,30].includes(orgForm.billing_increment) ? orgForm.billing_increment : orgForm.billing_increment===0 ? 'none' : 'other'}
                      onChange={e => {
                        const v = e.target.value;
                        if (v==='none') { setOrg('billing_increment', 0); setBillingOther(false); }
                        else if (v==='other') { setBillingOther(true); }
                        else { setOrg('billing_increment', parseInt(v)); setBillingOther(false); }
                      }}
                    >
                      <option value={6}>6 minutes</option>
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value="none">No rounding</option>
                      <option value="other">Other</option>
                    </select>
                    {billingOther && (
                      <input style={{...css.inp, marginTop:"8px"}} type="number" min="1" placeholder="Enter minutes"
                        value={[6,15,30,0].includes(orgForm.billing_increment) ? '' : orgForm.billing_increment||''}
                        onChange={e => setOrg('billing_increment', parseInt(e.target.value)||0)} />
                    )}
                  </div>
                  <div>
                    <div style={css.lbl}><TKTooltip text="Any session shorter than this duration is still billed for this many minutes. Set to 0 to disable.">Minimum Charge (minutes) ⓘ</TKTooltip></div>
                    <input style={css.inp} type="number" min="0" step="5"
                      value={orgForm.minimum_charge_minutes??0}
                      onChange={e=>setOrg('minimum_charge_minutes',parseInt(e.target.value)||0)} />
                  </div>
                </div>
                <div style={css.g2}>
                  <div>
                    <div style={css.lbl}><TKTooltip text="Pre-populates the rate field when creating a new client. Also used as the benchmark for color-coding effective rates in Client Reports.">Default Client Rate ($/hr) ⓘ</TKTooltip></div>
                    <input style={css.inp} type="number" min="0" step="0.01" placeholder="e.g. 150"
                      value={orgForm.default_hourly_rate??''}
                      onChange={e=>setOrg('default_hourly_rate',e.target.value==='' ? null : parseFloat(e.target.value)||0)} />
                  </div>
                  <div>
                    <div style={css.lbl}><TKTooltip text="Pre-populates the compensation rate when adding a new team member. Used in margin calculations if no per-assignment rate is set.">Default Team Member Rate ($/hr) ⓘ</TKTooltip></div>
                    <input style={css.inp} type="number" min="0" step="0.01" placeholder="e.g. 75"
                      value={orgForm.default_compensation_rate??''}
                      onChange={e=>setOrg('default_compensation_rate',e.target.value==='' ? null : parseFloat(e.target.value)||0)} />
                  </div>
                </div>
              </div>
              {saveBar}
            </div>
          )}

          {/* ── Invoices sub-tab ── */}
          {billingSubTab==='Invoices' && (() => {
            const INVOICE_SECTIONS = [
              { key:'display',   icon:'🧾', label:'Display & Branding',   desc:'Logo, line items, what shows on the page' },
              { key:'numbering', icon:'#️⃣', label:'Numbering & Defaults', desc:'Invoice #, payment terms, bill-day defaults' },
              { key:'payment',   icon:'💳', label:'Payment Link',         desc:'"Pay Now" button on emailed invoices' },
              { key:'reminders', icon:'🔔', label:'Reminders',            desc:'Automated due-date emails + templates' },
              { key:'member',    icon:'👥', label:'Member Invoices',      desc:'AP recipients + submission policy' },
            ];
            const sampleClient = clients?.find(c => c.is_active !== false) || { name:'Sample Client, LLC' };
            const rate = orgForm.default_hourly_rate || 150;
            const sampleLines = [
              { description:'Consulting Services', hours:12.5, rate, amount: 12.5*rate },
              { description:'Strategy Review',      hours:3,    rate, amount: 3*rate },
            ];
            const sampleTotal = sampleLines.reduce((s,l)=>s+l.amount,0);
            const fmt$ = (n) => {
              const v = parseFloat(n||0);
              return v === Math.round(v) ? '$'+Math.round(v).toLocaleString() : '$'+v.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2});
            };
            const effectiveLogo = orgForm.invoice_logo_url || orgForm.logo_url || null;
            const today = new Date();
            const dueDate = new Date(today.getTime() + 30*86400000);

            const InvoiceMockup = () => (
              <div style={{position:"sticky",top:"20px"}}>
                <div style={{fontSize:"11px",fontWeight:"700",color:C.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"10px"}}>Live Preview</div>
                <div style={{background:"#fff",color:"#1e293b",padding:"32px",fontFamily:"Arial,Helvetica,sans-serif",boxShadow:"0 4px 30px rgba(0,0,0,0.25)",borderRadius:"8px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"28px"}}>
                    <div>
                      {effectiveLogo && <img src={effectiveLogo} alt={orgForm.name||''} style={{maxHeight:"44px",maxWidth:"150px",marginBottom:"8px",display:"block"}} />}
                      <div style={{fontSize:"17px",fontWeight:"bold",color:"#1e293b"}}>{orgForm.name||'Your Company'}</div>
                      {orgForm.invoice_show_address !== false && formatAddressLines(orgForm).map((ln,i) => (
                        <div key={i} style={{fontSize:"10px",color:"#64748b",marginTop:i===0?"3px":0}}>{ln}</div>
                      ))}
                      {orgForm.invoice_show_phone !== false && orgForm.phone && <div style={{fontSize:"10px",color:"#64748b"}}>{fmtPhone(orgForm.phone)}</div>}
                      {orgForm.invoice_show_website !== false && orgForm.website && <div style={{fontSize:"10px",color:"#64748b"}}>{orgForm.website}</div>}
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:"26px",fontWeight:"bold",color:"#1e293b",letterSpacing:"-1px"}}>INVOICE</div>
                      <div style={{fontSize:"12px",color:"#64748b",marginTop:"3px"}}>{(orgForm.invoice_number_format||'INV-{YYYY}-{MM}-{SEQ}').replace('{YYYY}',today.getFullYear()).replace('{MM}',String(today.getMonth()+1).padStart(2,'0')).replace('{DD}',String(today.getDate()).padStart(2,'0')).replace('{SEQ}','001')}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:"22px",paddingBottom:"16px",borderBottom:"2px solid #e2e8f0"}}>
                    <div>
                      <div style={{fontSize:"10px",fontWeight:"bold",color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.7px",marginBottom:"5px"}}>Bill To</div>
                      <div style={{fontSize:"13px",fontWeight:"600",color:"#1e293b"}}>{sampleClient.name}</div>
                      {orgForm.invoice_show_client_address !== false && (
                        formatAddressLines(sampleClient).length > 0
                          ? formatAddressLines(sampleClient).map((ln,i) => <div key={i} style={{fontSize:"10px",color:"#475569",marginTop:i===0?"3px":0}}>{ln}</div>)
                          : <div style={{fontSize:"10px",color:"#94a3b8",marginTop:"3px",fontStyle:"italic"}}>123 Main St, Springfield, IL</div>
                      )}
                      {orgForm.invoice_show_client_phone !== false && sampleClient.phone && <div style={{fontSize:"10px",color:"#475569"}}>{fmtPhone(sampleClient.phone)}</div>}
                    </div>
                    <div style={{textAlign:"right",fontSize:"11px",lineHeight:"1.9"}}>
                      <div><span style={{color:"#94a3b8"}}>Invoice Date&nbsp;&nbsp;</span><strong>{today.toISOString().slice(0,10)}</strong></div>
                      <div><span style={{color:"#94a3b8"}}>Due Date&nbsp;&nbsp;</span><strong>{dueDate.toISOString().slice(0,10)}</strong></div>
                      <div><span style={{color:"#94a3b8"}}>Terms&nbsp;&nbsp;</span><strong>{orgForm.invoice_payment_terms||'Net 30'}</strong></div>
                    </div>
                  </div>
                  <table style={{width:"100%",borderCollapse:"collapse",marginBottom:"18px"}}>
                    <thead>
                      <tr style={{borderBottom:"2px solid #e2e8f0"}}>
                        <th style={{textAlign:"left",padding:"6px 0",fontSize:"10px",fontWeight:"bold",color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.4px"}}>Description</th>
                        <th style={{textAlign:"right",padding:"6px 0",fontSize:"10px",fontWeight:"bold",color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.4px",width:"60px"}}>Qty</th>
                        <th style={{textAlign:"right",padding:"6px 0",fontSize:"10px",fontWeight:"bold",color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.4px",width:"70px"}}>Rate</th>
                        <th style={{textAlign:"right",padding:"6px 0",fontSize:"10px",fontWeight:"bold",color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.4px",width:"80px"}}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sampleLines.map((l,i) => (
                        <tr key={i}>
                          <td style={{padding:"9px 0",fontSize:"12px",color:"#1e293b",borderBottom:"1px solid #f1f5f9"}}>{l.description}</td>
                          <td style={{padding:"9px 0",textAlign:"right",fontSize:"12px",color:"#64748b",borderBottom:"1px solid #f1f5f9"}}>{l.hours.toFixed(1)}</td>
                          <td style={{padding:"9px 0",textAlign:"right",fontSize:"12px",color:"#64748b",borderBottom:"1px solid #f1f5f9"}}>{fmt$(l.rate)}</td>
                          <td style={{padding:"9px 0",textAlign:"right",fontSize:"12px",fontWeight:"600",color:"#1e293b",borderBottom:"1px solid #f1f5f9"}}>{fmt$(l.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{display:"flex",justifyContent:"flex-end",marginBottom:"20px"}}>
                    <div style={{minWidth:"180px",borderTop:"2px solid #1e293b",paddingTop:"9px",display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                      <span style={{fontSize:"13px",fontWeight:"bold",color:"#1e293b"}}>Total Due</span>
                      <span style={{fontSize:"18px",fontWeight:"bold",color:"#1e293b"}}>{fmt$(sampleTotal)}</span>
                    </div>
                  </div>
                  {orgForm.invoice_payment_link && (
                    <div style={{textAlign:"center"}}>
                      <span style={{display:"inline-block",padding:"8px 20px",background:C.accent,color:contrastText(C.accent),borderRadius:"6px",fontSize:"12px",fontWeight:"600"}}>Pay Now</span>
                    </div>
                  )}
                </div>
                <div style={{fontSize:"11px",color:C.muted,marginTop:"10px",lineHeight:"1.5"}}>Sample data shown for illustration — reflects your live display, logo, and payment settings.</div>
              </div>
            );

            return (
            <div style={{display:"flex",gap:"20px",alignItems:"flex-start"}}>
              {/* Left rail: section buttons */}
              <div style={{width:"200px",flexShrink:0,display:"flex",flexDirection:"column",gap:"4px"}}>
                {INVOICE_SECTIONS.map(sec => (
                  <button key={sec.key} onClick={()=>setInvoiceSection(sec.key)}
                    style={{display:"flex",alignItems:"flex-start",gap:"10px",textAlign:"left",padding:"10px 12px",
                      borderRadius:"8px",border:"none",cursor:"pointer",
                      background: invoiceSection===sec.key ? `${C.accent}18` : "transparent",
                      borderLeft: invoiceSection===sec.key ? `2px solid ${C.accent}` : "2px solid transparent",
                    }}>
                    <span style={{fontSize:"15px",lineHeight:"1.3"}}>{sec.icon}</span>
                    <span>
                      <div style={{fontSize:"13px",fontWeight:invoiceSection===sec.key?"600":"500",color:invoiceSection===sec.key?C.accent:C.textBright}}>{sec.label}</div>
                      <div style={{fontSize:"11px",color:C.muted,marginTop:"2px",lineHeight:"1.35"}}>{sec.desc}</div>
                    </span>
                  </button>
                ))}
              </div>

              {/* Right: section content */}
              <div style={{flex:1,minWidth:0}}>

                {invoiceSection==='display' && (
                  <div style={{display:"flex",gap:"24px",alignItems:"flex-start"}}>
                    <div style={{flex:"1 1 340px",minWidth:"300px"}}>
                      <div style={css.card}>
                        <div style={{...css.h2,marginBottom:"16px"}}>Invoice Line Items</div>
                        <div>
                          <div style={css.lbl}><TKTooltip text="How time entries are grouped on generated invoices. 'By task' collapses entries into one line per task type. 'By entry' lists each entry separately. 'Manual' skips auto-generation. Can be overridden per invoice.">Default Line Item Mode ⓘ</TKTooltip></div>
                          <select style={css.sel} value={orgForm.invoice_line_item_mode||'by_task'} onChange={e=>setOrg('invoice_line_item_mode',e.target.value)}>
                            <option value="by_task">Group by task type — one line per task</option>
                            <option value="by_entry">One line per time entry</option>
                            <option value="manual">Manual — I'll enter line items myself</option>
                          </select>
                          <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>Can be overridden per invoice at generation time.</div>
                        </div>
                      </div>

                      <div style={css.card}>
                        <div style={{...css.h2,marginBottom:"12px"}}>Invoice Logo</div>
                        <div style={{fontSize:"12px",color:C.muted,marginBottom:"12px"}}>Defaults to your app logo. Upload a separate logo if your app logo doesn't render well on white backgrounds (e.g. a logo with no background and white text).</div>
                        <div style={{display:"flex",alignItems:"flex-start",gap:"16px"}}>
                          <div style={{width:"120px",height:"60px",background:"#f8fafc",border:`1px solid ${C.border}`,borderRadius:"6px",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}>
                            {(orgForm.invoice_logo_url||orgForm.logo_url)
                              ? <img src={orgForm.invoice_logo_url||orgForm.logo_url} alt="Invoice logo" style={{maxWidth:"110px",maxHeight:"52px",objectFit:"contain"}} />
                              : <span style={{fontSize:"11px",color:C.muted}}>No logo</span>
                            }
                          </div>
                          <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                            <label style={{cursor:"pointer",padding:"6px 12px",borderRadius:"6px",border:`1px solid ${C.border}`,fontSize:"12px",color:C.textDim,background:C.bg}}>
                              {orgForm.invoice_logo_url ? 'Replace Invoice Logo' : 'Upload Invoice Logo'}
                              <input type="file" accept="image/*" style={{display:"none"}} onChange={async e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const path = `${orgId}/invoice-logo-${file.name}`;
                                const { error: upErr } = await supabase.storage.from('org-logos').upload(path, file, { upsert: true });
                                if (upErr) { notifyError('Invoice logo upload failed: ' + upErr.message); return; }
                                const { data: { publicUrl } } = supabase.storage.from('org-logos').getPublicUrl(path);
                                setOrg('invoice_logo_url', publicUrl);
                              }} />
                            </label>
                            {orgForm.invoice_logo_url && (
                              <button style={{...css.smBtn(C.muted),fontSize:"11px"}} onClick={()=>setOrg('invoice_logo_url',null)}>Use App Logo</button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div style={css.card}>
                        <div style={{...css.h2,marginBottom:"4px"}}>Invoice Display Settings</div>
                        <div style={{fontSize:"12px",color:C.muted,marginBottom:"14px"}}>Control what appears on your invoices. Changes take effect immediately in the preview.</div>
                        {[
                          ['invoice_show_address','Show your company address'],
                          ['invoice_show_phone','Show your company phone number'],
                          ['invoice_show_website','Show your company website'],
                          ['invoice_show_client_address','Show client billing address (if set on client)'],
                          ['invoice_show_client_phone','Show client phone number (if set on client)'],
                        ].map(([k,label])=>(
                          <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid rgba(30,39,54,0.5)`}}>
                            <span style={{fontSize:"13px",color:C.textBright}}>{label}</span>
                            <button onClick={()=>setOrg(k,!orgForm[k])}
                              style={{width:"36px",height:"20px",borderRadius:"10px",border:"none",cursor:"pointer",flexShrink:0,
                                background:orgForm[k]!==false?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
                              <div style={{position:"absolute",top:"2px",left:orgForm[k]!==false?"18px":"2px",
                                width:"16px",height:"16px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={{flex:"1 1 400px",minWidth:"320px"}}>
                      <InvoiceMockup />
                    </div>
                  </div>
                )}

                {invoiceSection==='numbering' && (
                  <div>
                    <div style={css.card}>
                      <div style={{...css.h2,marginBottom:"16px"}}>Billing Defaults by Contract Type</div>
                      <div style={{fontSize:"12px",color:C.muted,marginBottom:"16px"}}>Controls which period is pre-selected and which bill day is used when generating new invoices.</div>

                      {/* Retainer */}
                      <div style={{padding:"14px 0",borderBottom:`1px solid rgba(30,39,54,0.5)`}}>
                        <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright,marginBottom:"10px"}}>{billingModelLabel('retainer')}</div>
                        <div style={css.g2}>
                          <div>
                            <div style={css.lbl}><TKTooltip text="Which month's time entries are pulled when generating a retainer invoice. 'Current' = this month, 'Previous' = last month.">Default Period ⓘ</TKTooltip></div>
                            <select style={css.sel} value={orgForm.invoice_default_period_retainer||'current'} onChange={e=>setOrg('invoice_default_period_retainer',e.target.value)}>
                              <option value="current">Current month</option>
                              <option value="previous">Previous month</option>
                            </select>
                          </div>
                          <div>
                            <div style={css.lbl}><TKTooltip text="The day of month used when setting the invoice issue date by default (1st, 15th, or last day of month).">Default Bill Day ⓘ</TKTooltip></div>
                            <select style={css.sel} value={orgForm.invoice_default_bill_day_retainer||'1st'} onChange={e=>setOrg('invoice_default_bill_day_retainer',e.target.value)}>
                              <option value="1st">1st of month</option>
                              <option value="15th">15th of month</option>
                              <option value="end">End of month</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Hourly */}
                      <div style={{padding:"14px 0",borderBottom:`1px solid rgba(30,39,54,0.5)`}}>
                        <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright,marginBottom:"10px"}}>{billingModelLabel('hourly')}</div>
                        <div style={css.g2}>
                          <div>
                            <div style={css.lbl}><TKTooltip text="Which month's time entries are pulled when generating an hourly invoice. Typically 'Previous' so you bill after the work is done.">Default Period ⓘ</TKTooltip></div>
                            <select style={css.sel} value={orgForm.invoice_default_period_hourly||'previous'} onChange={e=>setOrg('invoice_default_period_hourly',e.target.value)}>
                              <option value="current">Current month</option>
                              <option value="previous">Previous month</option>
                            </select>
                          </div>
                          <div>
                            <div style={css.lbl}><TKTooltip text="The day of month used when setting the invoice issue date by default (1st, 15th, or last day of month).">Default Bill Day ⓘ</TKTooltip></div>
                            <select style={css.sel} value={orgForm.invoice_default_bill_day_hourly||'1st'} onChange={e=>setOrg('invoice_default_bill_day_hourly',e.target.value)}>
                              <option value="1st">1st of month</option>
                              <option value="15th">15th of month</option>
                              <option value="end">End of month</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Retainer + overage (model id 'hybrid') */}
                      <div style={{padding:"14px 0"}}>
                        <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright,marginBottom:"4px"}}>{billingModelLabel('hybrid')}</div>
                        <div style={{fontSize:"11px",color:C.muted,marginBottom:"10px"}}>Retainer always bills for the selected period; overage always pulls from the previous month.</div>
                        <div>
                          <div style={css.lbl}><TKTooltip text="The day of month used when setting the hybrid invoice issue date. Retainer covers the selected period; overage always pulls from the prior month.">Default Bill Day ⓘ</TKTooltip></div>
                          <select style={{...css.sel,maxWidth:"200px"}} value={orgForm.invoice_default_bill_day_hybrid||'1st'} onChange={e=>setOrg('invoice_default_bill_day_hybrid',e.target.value)}>
                            <option value="1st">1st of month</option>
                            <option value="15th">15th of month</option>
                            <option value="end">End of month</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div style={css.card}>
                      <div style={{...css.h2,marginBottom:"16px"}}>Invoicing</div>
                      <div style={{marginBottom:"12px"}}>
                        <div style={css.lbl}><TKTooltip text="Default number of days until payment is due after an invoice is issued. 'Net 30' means due 30 days after the issue date.">Payment Terms ⓘ</TKTooltip></div>
                        <select style={css.sel} value={orgForm.invoice_payment_terms||'Net 30'} onChange={e=>setOrg('invoice_payment_terms',e.target.value)}>
                          {['Due on Receipt','Net 7','Net 15','Net 30','Net 45','Net 60'].map(v=><option key={v} value={v}>{v}</option>)}
                        </select>
                      </div>
                      <div style={{...css.g2,marginBottom:"12px"}}>
                        <div>
                          <div style={css.lbl}><TKTooltip text="Short text prepended to every invoice number (e.g. 'INV'). Changing this updates the format template automatically.">Invoice Number Prefix ⓘ</TKTooltip></div>
                          <input style={css.inp} value={orgForm.invoice_number_prefix||'INV'} onChange={e=>{
                            const np=e.target.value, op=orgForm.invoice_number_prefix||'INV';
                            const cf=orgForm.invoice_number_format||'INV-{YYYY}-{MM}-{SEQ}';
                            setOrg('invoice_number_prefix', np);
                            setOrg('invoice_number_format', cf.startsWith(op) ? np+cf.slice(op.length) : np+'-{YYYY}-{MM}-{SEQ}');
                          }} />
                        </div>
                        <div>
                          <div style={css.lbl}><TKTooltip text="The month your fiscal year begins. Used to calculate 'Year to Date' ranges in Client Reports and Team Analytics.">Fiscal Year Start ⓘ</TKTooltip></div>
                          <select style={css.sel} value={orgForm.fiscal_year_start||1} onChange={e=>setOrg('fiscal_year_start',parseInt(e.target.value))}>
                            {MONTHS.map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <div style={css.lbl}><TKTooltip text="Template for invoice numbers. Tokens: {YYYY} = year, {MM} = month, {DD} = day, {SEQ} = auto-incrementing sequence number. Sequence is atomic — no duplicates.">Invoice Number Format ⓘ</TKTooltip></div>
                        <input style={css.inp} value={orgForm.invoice_number_format||'INV-{YYYY}-{MM}-{SEQ}'} onChange={e=>setOrg('invoice_number_format',e.target.value)} />
                        <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>Tokens: {'{YYYY} {MM} {DD} {SEQ}'}</div>
                        {(() => {
                          const now=new Date(), fmt=orgForm.invoice_number_format||'INV-{YYYY}-{MM}-{SEQ}';
                          const preview=fmt.replace('{YYYY}',now.getFullYear()).replace('{MM}',String(now.getMonth()+1).padStart(2,'0')).replace('{DD}',String(now.getDate()).padStart(2,'0')).replace('{SEQ}','001');
                          return <div style={{fontSize:"12px",color:C.accent,marginTop:"6px",padding:"6px 10px",background:"rgba(49,167,217,0.08)",borderRadius:"4px",border:`1px solid rgba(49,167,217,0.15)`}}>Preview: <strong>{preview}</strong></div>;
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {invoiceSection==='payment' && (
                  <div>
                    <div style={css.card}>
                      <div style={{...css.h2,marginBottom:"4px"}}>Email Sender</div>
                      <div style={{fontSize:"12px",color:C.muted,marginBottom:"12px"}}>
                        The display name shown in the "From" field of invoice emails. Defaults to your organization name.
                      </div>
                      <div style={{marginBottom:"16px"}}>
                        <div style={css.lbl}><TKTooltip text="How your name appears in the From field — e.g. 'Acme Consulting'. Leave blank to use your org name.">From Name ⓘ</TKTooltip></div>
                        <input style={css.inp} placeholder={orgForm.name || 'Your organization name'} value={orgForm.invoice_from_name||''} onChange={e=>setOrg('invoice_from_name',e.target.value)} />
                        <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>Preview: <em>{orgForm.invoice_from_name || orgForm.name || 'Your Org'} &lt;billing@usetimekeep.com&gt;</em></div>
                      </div>
                    </div>
                    <div style={{...css.card,marginTop:"12px"}}>
                      <div style={{...css.h2,marginBottom:"4px"}}>Payment Link</div>
                      <div style={{fontSize:"12px",color:C.muted,marginBottom:"12px"}}>
                        Add a payment link from your bank, Relay, or any payment platform. A <strong>"Pay Now"</strong> button will appear on all emailed invoices when this is set. Leave blank to hide it.
                      </div>
                      <div>
                        <div style={css.lbl}><TKTooltip text="Shown as a 'Pay Now' button on emailed invoices and the public invoice page. Accepts any payment URL — Relay, Stripe, PayPal, Square, etc. Can be overridden per invoice.">Payment URL ⓘ</TKTooltip></div>
                        <input style={css.inp} placeholder="https://pay.relay.fi/your-link or https://buy.stripe.com/..." value={orgForm.invoice_payment_link||''} onChange={e=>setOrg('invoice_payment_link',e.target.value)} />
                        <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>Works with Relay Bank, Stripe, PayPal, Square, or any URL. When integrations are connected (Stripe, QBO), the payment button will link directly to the integrated platform.</div>
                      </div>
                    </div>
                  </div>
                )}

                {invoiceSection==='reminders' && (
                  <div>
                    <div style={css.card}>
                      <div style={{...css.h2,marginBottom:"4px"}}>Invoice Reminder Emails</div>
                      <div style={{fontSize:"12px",color:C.muted,marginBottom:"14px"}}>
                        Automatically send payment reminders to clients with a billing email. Reminders fire once per invoice per trigger point.
                      </div>
                      {[
                        ['notifications_reminder_14d',         '14 days before due date'],
                        ['notifications_reminder_7d',          '7 days before due date'],
                        ['notifications_reminder_2d',          '2 days before due date'],
                        ['notifications_reminder_due',         'On the due date'],
                        ['notifications_reminder_2d_after',    '2 days after due date'],
                        ['notifications_reminder_7d_after',    '7 days after due date'],
                        ['notifications_reminder_weekly_after','Weekly after 7 days overdue (up to 12 weeks)'],
                      ].map(([k, label]) => (
                        <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid rgba(30,39,54,0.5)`}}>
                          <span style={{fontSize:"13px",color:C.textBright}}>{label}</span>
                          <button onClick={()=>setOrg(k, orgForm[k]===false ? true : false)}
                            style={{width:"36px",height:"20px",borderRadius:"10px",border:"none",cursor:"pointer",flexShrink:0,
                              background:orgForm[k]!==false?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
                            <div style={{position:"absolute",top:"2px",left:orgForm[k]!==false?"18px":"2px",
                              width:"16px",height:"16px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
                          </button>
                        </div>
                      ))}
                      <div style={{fontSize:"11px",color:C.muted,marginTop:"10px"}}>
                        Reminders are triggered by the <strong>invoice-reminders</strong> function (runs daily). Only applies to invoices with status "Sent" that have a due date and a billing contact email.
                      </div>
                    </div>

                    <div style={css.card}>
                      <div style={{...css.h2,marginBottom:"4px"}}>Reminder Email Templates</div>
                      <div style={{fontSize:"12px",color:C.muted,marginBottom:"16px"}}>
                        Customize the message that appears at the top of reminder emails. Use tokens: <code style={{background:C.bg,padding:"1px 4px",borderRadius:"3px",fontSize:"11px"}}>{'{invoice_number} {client_name} {due_date} {amount} {days_until} {days_overdue} {org_name}'}</code>
                      </div>

                      {/* Pre-due template */}
                      <div style={{marginBottom:"20px",paddingBottom:"20px",borderBottom:`1px solid rgba(30,39,54,0.5)`}}>
                        <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright,marginBottom:"10px"}}>Before Due Date <span style={{fontWeight:"400",color:C.muted,fontSize:"11px"}}>(friendly tone)</span></div>
                        <div style={{marginBottom:"8px"}}>
                          <div style={css.lbl}><TKTooltip text="Available tokens: {invoice_number} {client_name} {due_date} {amount} {org_name} {days_until}">Subject Line ⓘ</TKTooltip></div>
                          <input style={css.inp}
                            value={(orgForm.reminder_message_templates?.pre_due?.subject) ?? 'Payment Reminder — Invoice {invoice_number} due {due_date}'}
                            onChange={e=>setOrg('reminder_message_templates', {
                              ...(orgForm.reminder_message_templates||{}),
                              pre_due: { ...(orgForm.reminder_message_templates?.pre_due||{}), subject: e.target.value }
                            })} />
                        </div>
                        <div>
                          <div style={css.lbl}><TKTooltip text="Available tokens: {invoice_number} {client_name} {due_date} {amount} {org_name} {days_until}">Message Body ⓘ</TKTooltip></div>
                          <textarea rows={4} style={{...css.inp,resize:"vertical",fontFamily:"Inter,sans-serif",lineHeight:"1.5",fontSize:"13px"}}
                            value={(orgForm.reminder_message_templates?.pre_due?.body) ?? 'Hi {client_name},\n\nThis is a friendly reminder that invoice {invoice_number} for {amount} is due on {due_date}.\n\nPlease don\'t hesitate to reach out if you have any questions.\n\nThank you,\n{org_name}'}
                            onChange={e=>setOrg('reminder_message_templates', {
                              ...(orgForm.reminder_message_templates||{}),
                              pre_due: { ...(orgForm.reminder_message_templates?.pre_due||{}), body: e.target.value }
                            })} />
                        </div>
                      </div>

                      {/* Overdue template */}
                      <div>
                        <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright,marginBottom:"10px"}}>Overdue <span style={{fontWeight:"400",color:C.muted,fontSize:"11px"}}>(firm tone)</span></div>
                        <div style={{marginBottom:"8px"}}>
                          <div style={css.lbl}><TKTooltip text="Available tokens: {invoice_number} {client_name} {due_date} {amount} {org_name} {days_overdue}">Subject Line ⓘ</TKTooltip></div>
                          <input style={css.inp}
                            value={(orgForm.reminder_message_templates?.overdue?.subject) ?? 'Past Due — Invoice {invoice_number} from {org_name}'}
                            onChange={e=>setOrg('reminder_message_templates', {
                              ...(orgForm.reminder_message_templates||{}),
                              overdue: { ...(orgForm.reminder_message_templates?.overdue||{}), subject: e.target.value }
                            })} />
                        </div>
                        <div>
                          <div style={css.lbl}><TKTooltip text="Available tokens: {invoice_number} {client_name} {due_date} {amount} {org_name} {days_overdue}">Message Body ⓘ</TKTooltip></div>
                          <textarea rows={4} style={{...css.inp,resize:"vertical",fontFamily:"Inter,sans-serif",lineHeight:"1.5",fontSize:"13px"}}
                            value={(orgForm.reminder_message_templates?.overdue?.body) ?? 'Hi {client_name},\n\nInvoice {invoice_number} for {amount} was due on {due_date} and remains unpaid.\n\nPlease remit payment at your earliest convenience, or contact us if you need to make arrangements.\n\nThank you,\n{org_name}'}
                            onChange={e=>setOrg('reminder_message_templates', {
                              ...(orgForm.reminder_message_templates||{}),
                              overdue: { ...(orgForm.reminder_message_templates?.overdue||{}), body: e.target.value }
                            })} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {invoiceSection==='member' && (
                  <div>
                    <div style={css.card}>
                      <div style={{...css.h2,marginBottom:"4px"}}>Member Invoice Recipients</div>
                      <div style={{fontSize:"12px",color:C.muted,marginBottom:"12px"}}>
                        When you <strong>approve</strong> a member's invoice, it is emailed here as a PDF. Use your AP intake address (Relay, QuickBooks, Xero, Bill.com) and/or a filing address. Separate multiple addresses with commas or semicolons. You can override this per member in Team.
                      </div>
                      <div>
                        <div style={css.lbl}><TKTooltip text="AP inbox(es) that receive member invoices as PDF attachments when approved. Compatible with Relay, QuickBooks, Xero, and Bill.com intake addresses. Separate multiple with commas.">Recipient email(s) ⓘ</TKTooltip></div>
                        <input style={css.inp} placeholder="ap@yourbank.relay.fi, filing@yourfirm.com" value={orgForm.contractor_invoice_recipients||''} onChange={e=>setOrg('contractor_invoice_recipients',e.target.value)} />
                      </div>
                    </div>

                    <div style={css.card}>
                      <div style={{...css.h2,marginBottom:"4px"}}>Member Submission Policy</div>
                      <div style={{fontSize:"12px",color:C.muted,marginBottom:"16px"}}>
                        Control when members are allowed to submit their monthly invoice for approval.
                      </div>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px',background:C.bg,borderRadius:'8px',border:`1px solid ${C.border}`}}>
                        <div>
                          <div style={{fontSize:'13px',fontWeight:'600',color:C.textBright}}>Require all clients closed before submitting</div>
                          <div style={{fontSize:'12px',color:C.muted,marginTop:'3px'}}>
                            {orgForm.invoice_require_all_clients_closed !== false
                              ? 'ON — Member must close every assigned client before submitting an invoice.'
                              : 'OFF — Member can submit once at least one client is closed. Unclosed clients are excluded from that invoice.'}
                          </div>
                        </div>
                        <label style={{display:'flex',alignItems:'center',cursor:'pointer',gap:'8px',flexShrink:0,marginLeft:'16px'}}>
                          <input type="checkbox" checked={orgForm.invoice_require_all_clients_closed !== false} onChange={e=>setOrg('invoice_require_all_clients_closed', e.target.checked)} style={{width:'16px',height:'16px',cursor:'pointer',accentColor:C.accent}} />
                          <span style={{fontSize:'12px',color:C.textDim,userSelect:'none'}}>{orgForm.invoice_require_all_clients_closed !== false ? 'On' : 'Off'}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {saveBar}
              </div>
            </div>
            );
          })()}
        </div>
      )}

      {/* ── Notifications ── */}
      {inGroup('Notifications') && sectionHead('Notifications','Notifications')}
      {inGroup('Notifications') && (
        <div style={{maxWidth:"600px"}}>
          <div style={{display:'flex',alignItems:'flex-start',gap:'10px',padding:'12px 14px',marginBottom:'12px',
            background:`${C.accent}0f`,border:`1px solid ${C.accent}33`,borderRadius:'8px',fontSize:'12px',color:C.muted,lineHeight:'1.5'}}>
            <span style={{fontSize:'16px',flexShrink:0,marginTop:'1px'}}>🔔</span>
            <div>
              <span style={{fontWeight:'600',color:C.text}}>My notifications</span> are emails sent to you as the admin.{' '}
              <span style={{fontWeight:'600',color:C.text}}>Team notifications</span> are sent to your contractors and team members.
              The <span style={{fontWeight:'600',color:C.text}}>AI Narrative</span> toggle adds a Gemini-generated summary to your weekly and monthly emails — turn it on to get insights alongside the data.
            </div>
          </div>
          {subTab.Notifications==='My' && (
          <div style={css.card}>
            <Toggle k="notifications_weekly_summary" label="Weekly Summary Email" desc="Receive a weekly report of all time entries" />
            {orgForm.notifications_weekly_summary && (
              <div style={{...css.g2,padding:"10px 0 4px"}}>
                <div><div style={css.lbl}><TKTooltip text="Day of the week the weekly summary email is sent to the admin.">Day ⓘ</TKTooltip></div>
                  <select style={css.sel} value={orgForm.notifications_weekly_summary_day??1} onChange={e=>setOrg('notifications_weekly_summary_day',parseInt(e.target.value))}>
                    {DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
                <div><div style={css.lbl}><TKTooltip text="Time of day the weekly summary fires (your org's timezone).">Time ⓘ</TKTooltip></div>
                  <input type="time" style={css.inp} value={orgForm.notifications_weekly_summary_time||'08:00'} onChange={e=>setOrg('notifications_weekly_summary_time',e.target.value)} />
                </div>
              </div>
            )}
            <Toggle k="notifications_inactivity_alert" label="Inactivity Alert" desc="Alert when a member hasn't logged time recently" />
            {orgForm.notifications_inactivity_alert && (
              <div style={{padding:"10px 0 4px"}}>
                <div style={css.lbl}><TKTooltip text="If no time entries are logged for this many days, an inactivity alert email is sent to the admin.">Days of inactivity before alert ⓘ</TKTooltip></div>
                <input style={{...css.inp,width:"120px"}} type="number" min="1"
                  value={orgForm.notifications_inactivity_days||7}
                  onChange={e=>setOrg('notifications_inactivity_days',parseInt(e.target.value)||7)} />
              </div>
            )}
            <Toggle k="notifications_threshold_alert" label="Hours Threshold Alert" desc="Alert when a client approaches their included hours" />
            <Toggle k="notifications_monthly_reset" label="Monthly Summary Email" desc="Send a monthly summary email on the 1st of each month" />
            <Toggle k="notifications_ai_narrative" label="AI Narrative in Summaries" desc="Add a Gemini-generated narrative and insights to weekly and monthly summary emails" />
          </div>
          )}

          {subTab.Notifications==='Team' && (
          <div style={css.card}>
            <div style={{display:"flex",alignItems:"center",padding:"8px 0 10px",borderBottom:`1px solid ${C.border}`,gap:"12px"}}>
              <div style={{flex:1,fontSize:"11px",fontWeight:"600",color:C.muted,textTransform:"uppercase",letterSpacing:"0.5px"}}>Notification</div>
              <div style={{width:"40px",flexShrink:0}} />
              <div style={{width:"40px",flexShrink:0,fontSize:"11px",fontWeight:"600",color:C.muted,textTransform:"uppercase",letterSpacing:"0.5px",textAlign:"center"}}>Can opt out</div>
            </div>
            <ContractorToggle k="notifications_contractor_weekly" label="Weekly Summary for Contractors" desc="Send each contractor a summary of their logged hours">
              <div style={css.g2}>
                <div><div style={css.lbl}><TKTooltip text="Day of the week the member weekly summary is sent to each team member.">Day ⓘ</TKTooltip></div>
                  <select style={css.sel} value={orgForm.notifications_contractor_weekly_day??5} onChange={e=>setOrg('notifications_contractor_weekly_day',parseInt(e.target.value))}>
                    {DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
                <div><div style={css.lbl}><TKTooltip text="Time of day the member weekly summary fires (your org's timezone).">Time ⓘ</TKTooltip></div>
                  <input type="time" style={css.inp} value={orgForm.notifications_contractor_weekly_time||'08:00'} onChange={e=>setOrg('notifications_contractor_weekly_time',e.target.value)} />
                </div>
              </div>
            </ContractorToggle>
            <ContractorToggle k="notifications_contractor_month_end" label="Month-End Summary" desc="Send contractors a summary at end of each billing period">
              <div style={css.lbl}><TKTooltip text="Day of the month the month-end close reminder is sent to members (e.g. 28 gives them a few days before month end).">Send on day of month ⓘ</TKTooltip></div>
              <input style={{...css.inp,width:"120px"}} type="number" min="1" max="28"
                value={orgForm.notifications_contractor_month_end_day||28}
                onChange={e=>setOrg('notifications_contractor_month_end_day',parseInt(e.target.value)||28)} />
            </ContractorToggle>
            <ContractorToggle k="notifications_timer_reminder" label="Timer Reminder" desc="Alert contractors when a live timer has been running too long">
              <div style={css.lbl}><TKTooltip text="If a timer has been running continuously for this many hours, a reminder notification is sent so the user doesn't forget to stop it.">Remind after how many hours ⓘ</TKTooltip></div>
              <input style={{...css.inp,width:"120px"}} type="number" min="1"
                value={orgForm.notifications_timer_reminder_hours||2}
                onChange={e=>setOrg('notifications_timer_reminder_hours',parseInt(e.target.value)||2)} />
            </ContractorToggle>
            <Toggle k="notifications_all_contractors_locked" label="Pause All Member Notifications" desc="Temporarily disable all outbound emails to members. Individual settings are preserved." />
          </div>
          )}

          {saveBar}
        </div>
      )}

      {/* ── Mobile App ── */}
      {inSub('Mobile App') && (
        <div style={{maxWidth:"560px"}}>
          <div style={{fontSize:"13px",color:C.muted,marginBottom:"20px"}}>A lightweight mobile companion for logging time on the go — no app store required.</div>

          {/* Hero card */}
          <div style={{display:'flex',alignItems:'center',gap:'16px',padding:'16px 18px',borderRadius:'10px',
            background:'rgba(0,160,200,0.06)',border:`1px solid rgba(0,160,200,0.2)`,marginBottom:'20px'}}>
            <span style={{fontSize:'32px',flexShrink:0}}>📱</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:'14px',fontWeight:'700',color:C.textBright,marginBottom:'2px'}}>TimeKeep Mobile</div>
              <div style={{fontSize:'12px',color:C.muted,lineHeight:'1.5'}}>Start timers, log time, and close your month — all from your phone.</div>
              <a href="https://mobile.usetimekeep.com" target="_blank" rel="noreferrer"
                style={{display:'inline-block',marginTop:'8px',fontSize:'12px',fontWeight:'700',color:C.accent,textDecoration:'none'}}>
                mobile.usetimekeep.com ↗
              </a>
            </div>
            <a href="https://mobile.usetimekeep.com" target="_blank" rel="noreferrer"
              style={{flexShrink:0,padding:'8px 16px',borderRadius:'7px',background:C.accent,color:'#fff',
                fontSize:'12px',fontWeight:'700',textDecoration:'none',whiteSpace:'nowrap'}}>
              Open
            </a>
          </div>

          {/* Install instructions */}
          <div style={css.card}>
            <div style={{fontSize:'13px',fontWeight:'700',color:C.textBright,marginBottom:'14px'}}>How to add to your home screen</div>

            <div style={{marginBottom:'16px'}}>
              <div style={{fontSize:'12px',fontWeight:'700',color:C.accent,marginBottom:'8px',textTransform:'uppercase',letterSpacing:'0.5px'}}>iPhone / iPad (Safari)</div>
              <ol style={{margin:0,paddingLeft:'18px',display:'flex',flexDirection:'column',gap:'6px'}}>
                {[
                  'Open mobile.usetimekeep.com in Safari',
                  'Tap the Share button (□↑) at the bottom of the screen',
                  'Scroll down and tap "Add to Home Screen"',
                  'Tap "Add" — TimeKeep will appear on your home screen like an app',
                ].map((step, i) => (
                  <li key={i} style={{fontSize:'13px',color:C.text,lineHeight:'1.5'}}>{step}</li>
                ))}
              </ol>
            </div>

            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:'16px'}}>
              <div style={{fontSize:'12px',fontWeight:'700',color:C.accent,marginBottom:'8px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Android (Chrome)</div>
              <ol style={{margin:0,paddingLeft:'18px',display:'flex',flexDirection:'column',gap:'6px'}}>
                {[
                  'Open mobile.usetimekeep.com in Chrome',
                  'Tap the three-dot menu (⋮) in the top-right corner',
                  'Tap "Add to Home screen"',
                  'Tap "Add" — TimeKeep will appear on your home screen',
                ].map((step, i) => (
                  <li key={i} style={{fontSize:'13px',color:C.text,lineHeight:'1.5'}}>{step}</li>
                ))}
              </ol>
            </div>
          </div>

          <div style={{marginTop:'12px',fontSize:'12px',color:C.muted,textAlign:'center'}}>
            Works on any device with a browser — no app store, no installation, no updates to manage.
          </div>
        </div>
      )}

      {/* ── Integrations ── */}
      {inSub('Integrations') && (
        <div style={{maxWidth:"600px"}}>
          {settingsToast && (
            <div style={{background:C.success,color:"#fff",padding:"10px 16px",borderRadius:"8px",fontSize:"13px",fontWeight:"600",marginBottom:"12px"}}>
              {settingsToast}
            </div>
          )}
          {/* QuickBooks Online — live */}
          <div style={{...css.card}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"16px"}}>
              <div style={{flex:1}}>
                <div style={{fontWeight:"600",color:C.textBright,marginBottom:"4px",display:"flex",alignItems:"center",gap:"8px"}}>
                  QuickBooks Online
                  {qboConn && <span style={{...css.badge('green'),fontSize:"11px"}}>{qboConn.environment === 'sandbox' ? 'Sandbox' : 'Connected'}</span>}
                </div>
                <div style={{fontSize:"12px",color:C.muted,marginBottom: qboConn ? "8px" : 0}}>Sync invoices and clients with QuickBooks Online</div>
                {qboConn && (
                  <div style={{fontSize:"11px",color:C.muted}}>
                    Company ID: {qboConn.realm_id} · Connected {new Date(qboConn.connected_at).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div style={{flexShrink:0}}>
                {qboConn === null
                  ? <span style={{fontSize:"12px",color:C.muted}}>Loading…</span>
                  : qboConn
                    ? <button style={css.smBtn(C.danger)} onClick={disconnectQbo} disabled={qboLoading}>{qboLoading ? 'Disconnecting…' : 'Disconnect'}</button>
                    : <button style={css.btn("primary")} onClick={connectQbo} disabled={qboLoading}>{qboLoading ? 'Redirecting…' : 'Connect'}</button>
                }
              </div>
            </div>
            {qboConn && (
              <div style={{marginTop:"12px",paddingTop:"12px",borderTop:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:"12px"}}>
                <button
                  style={{...css.btn("primary"), fontSize:"12px", padding:"6px 14px", opacity: qboSyncAllProgress ? 0.6 : 1}}
                  disabled={!!qboSyncAllProgress}
                  onClick={syncAllClientsToQbo}
                >
                  {qboSyncAllProgress ? `Syncing…` : '↻ Sync All Clients'}
                </button>
                <span style={{fontSize:"11px",color:C.muted}}>
                  Syncs all active clients where "Sync to QuickBooks" is enabled. Per-client control is in Settings → Clients → Edit.
                </span>
              </div>
            )}
          </div>
          {/* Stripe — live */}
          <div style={{...css.card}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"16px"}}>
              <div style={{flex:1}}>
                <div style={{fontWeight:"600",color:C.textBright,marginBottom:"4px",display:"flex",alignItems:"center",gap:"8px",flexWrap:"wrap"}}>
                  Stripe
                  {stripeConn && stripeConn.charges_enabled && <span style={{...css.badge('green'),fontSize:"11px"}}>Connected</span>}
                  {stripeConn && !stripeConn.charges_enabled && (
                    <span style={{...css.badge('amber'),fontSize:"11px"}} title="Stripe needs more information before this account can take payments. Finish setup in your Stripe dashboard.">Setup incomplete</span>
                  )}
                  {stripeConn && !stripeConn.livemode && (
                    <span style={{...css.badge('blue'),fontSize:"11px"}} title="Connected in Stripe's test mode: payments use test cards and no real money moves.">Test mode</span>
                  )}
                </div>
                <div style={{fontSize:"12px",color:C.muted,marginBottom: stripeConn ? "8px" : 0}}>
                  {stripeConn
                    ? 'Clients pay invoices online with the "Pay Now" button. Payments go straight into your own Stripe account; Stripe\'s processing fees come out of each payment, and TimeKeep takes nothing.'
                    : 'Let clients pay invoices online with a "Pay Now" button. Use the Stripe account you already have, or create one in a couple of minutes. Payments go straight into your Stripe account.'}
                </div>
                {stripeConn && (
                  <div style={{fontSize:"11px",color:C.muted}}>
                    {stripeConn.account_name ? `${stripeConn.account_name} · ` : ''}Account {stripeConn.stripe_account_id} · Connected {new Date(stripeConn.connected_at).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div style={{flexShrink:0}}>
                {stripeConn === null
                  ? <span style={{fontSize:"12px",color:C.muted}}>Loading…</span>
                  : stripeConn
                    ? (!confirmStripeDisconnect && <button style={css.smBtn(C.danger)} onClick={()=>setConfirmStripeDisconnect(true)} disabled={!!stripeBusy}>Disconnect</button>)
                    : <button style={css.btn("primary")} onClick={connectStripe} disabled={!!stripeBusy}>{stripeBusy === 'connect' ? 'Redirecting…' : 'Connect with Stripe'}</button>
                }
              </div>
            </div>
            {stripeNote && <div style={{marginTop:"10px",fontSize:"12px",color:C.badgeAmber}}>{stripeNote}</div>}
            {stripeConn && confirmStripeDisconnect && (
              <div style={{marginTop:"12px",padding:"12px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:"8px"}}>
                <div style={{fontSize:"12px",color:C.textBright,marginBottom:"10px"}}>
                  Disconnect Stripe? The Pay Now button disappears from your invoices, and TimeKeep loses access to your Stripe account. Your Stripe account itself, its money, and its payment history aren't affected.
                </div>
                <div style={{display:"flex",gap:"8px",justifyContent:"flex-end"}}>
                  <button style={css.btn()} onClick={()=>setConfirmStripeDisconnect(false)} disabled={stripeBusy==='disconnect'}>Cancel</button>
                  <button style={{...css.btn(),color:C.danger,borderColor:C.danger}} onClick={disconnectStripe} disabled={stripeBusy==='disconnect'}>
                    {stripeBusy==='disconnect' ? 'Disconnecting…' : 'Disconnect Stripe'}
                  </button>
                </div>
              </div>
            )}
            {stripeConn && !stripeConn.charges_enabled && (() => {
              const needs = stripeReqLabels(stripeReq);
              const recheck = <button onClick={loadStripeConn} style={{background:"none",border:"none",padding:0,color:C.accent,cursor:"pointer",fontSize:"12px",textDecoration:"underline"}}>check again</button>;
              return (
                <div style={{marginTop:"12px",padding:"10px 12px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:"8px",fontSize:"12px",color:C.text}}>
                  {needs.length > 0 ? (<>
                    Stripe needs {needs.join(', ')} before this account can take payments, so Pay Now is hidden for now.{' '}
                    <a href={stripeConn.livemode ? 'https://dashboard.stripe.com' : 'https://dashboard.stripe.com/test/dashboard'} target="_blank" rel="noreferrer" style={{color:C.accent}}>Finish setup in Stripe</a>, then {recheck}.
                  </>) : (<>
                    Stripe is verifying the account details. This usually takes a minute or two, and Pay Now turns on by itself once it's done.
                    {' '}TimeKeep checks again every few seconds while this page is open, or you can {recheck} now.
                  </>)}
                </div>
              );
            })()}
            {stripeConn && (
              <div style={{marginTop:"12px",paddingTop:"12px",borderTop:`1px solid ${C.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:"12px"}}>
                  <div style={{fontSize:"12px",color:C.textBright,fontWeight:"600"}}>
                    <TKTooltip text="When on, emailed invoices, reminders, and the invoice page show a Pay Now button that takes your client to a Stripe checkout for the invoice total. Turn it off to keep Stripe connected but stop offering online payment.">Show Pay Now on invoices ⓘ</TKTooltip>
                  </div>
                  <button onClick={toggleStripePayNow} disabled={!!stripeBusy} aria-label="Show Pay Now on invoices"
                    style={{width:"36px",height:"20px",borderRadius:"10px",border:"none",cursor:stripeBusy?"default":"pointer",
                      background:stripeConn.pay_now_enabled?C.accent:C.border,position:"relative",transition:"background 0.2s",flexShrink:0}}>
                    <div style={{position:"absolute",top:"2px",left:stripeConn.pay_now_enabled?"18px":"2px",
                      width:"16px",height:"16px",borderRadius:"50%",background:contrastText(C.accent),transition:"left 0.2s"}} />
                  </button>
                </div>
                <div style={{fontSize:"11px",color:C.muted,marginTop:"6px"}}>
                  Which payment methods clients see (cards, bank transfer and others) is set in your Stripe dashboard.
                </div>
              </div>
            )}
            {stripeConn && qboConn && (
              <div style={{marginTop:"12px",paddingTop:"12px",borderTop:`1px solid ${C.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:"12px"}}>
                  <div style={{fontSize:"12px",color:C.textBright,fontWeight:"600"}}>Also record Stripe payments in QuickBooks</div>
                  <button onClick={()=>toggleOrgFlag('stripe_qbo_cascade')}
                    style={{width:"36px",height:"20px",borderRadius:"10px",border:"none",cursor:"pointer",
                      background:orgForm.stripe_qbo_cascade?C.accent:C.border,position:"relative",transition:"background 0.2s",flexShrink:0}}>
                    <div style={{position:"absolute",top:"2px",left:orgForm.stripe_qbo_cascade?"18px":"2px",
                      width:"16px",height:"16px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
                  </button>
                </div>
                <div style={{fontSize:"11px",color:C.muted,marginTop:"6px"}}>
                  Only enable this if your QuickBooks account doesn't already have its own Stripe app connected —
                  otherwise the same payment can get recorded twice. Check QuickBooks → Apps → My Apps first.
                </div>
              </div>
            )}
          </div>
          {/* Coming-soon integrations */}
          {[
            { name:'Supabase Storage', desc:'Connect storage for logo and document uploads' },
            { name:'Slack',            desc:'Send notifications and summaries to a Slack channel' },
            { name:'Xero',             desc:'Sync invoices and expense data with Xero' },
            { name:'Gusto',            desc:'Push payroll data and hours to Gusto' },
            { name:'ADP',              desc:'Sync payroll and workforce data with ADP' },
            { name:'Bill.com',         desc:'Automate AP/AR and invoice payments via Bill.com' },
            { name:'Google Calendar',  desc:'Sync time blocks and deadlines with Google Calendar' },
            { name:'Resend',           desc:'Send transactional and notification emails via Resend' },
            { name:'Zapier',           desc:'Connect TimeKeep to 5,000+ apps via Zapier' },
          ].map(int => (
            <div key={int.name} style={{...css.card,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontWeight:"600",color:C.textBright,marginBottom:"4px"}}>{int.name}</div>
                <div style={{fontSize:"12px",color:C.muted}}>{int.desc}</div>
              </div>
              <span style={css.badge('')}>Coming Soon</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Clients ── */}
      {tab==='Clients' && (
        <div>
          <div style={{display:"flex",flexDirection:"column",gap:"10px",marginBottom:"14px"}}>
            <StatusPills value={clientStatus} onChange={setClientStatus} options={[
              { key:'active',   label:'Active',   count: clients.filter(c=>c.is_active!==false).length },
              { key:'archived', label:'Archived', count: clients.filter(c=>c.is_active===false).length },
              { key:'all',      label:'All',      count: clients.length },
            ]} />
            <ClientLogSearch clients={clients} clientIds={clientsShownByArchiveToggle.map(c=>c.id)} value={clientSearch} onChange={setClientSearch} />
          </div>
          <div data-tour="clients-list">
            <DataTable
              rows={visibleSettingsClients}
              rowKey={c => c.id}
              onRowClick={c => onClientClick?.(c.id)}
              columns={[
                { key:'name', label:'Client', sortValue:c=>(c.name||'').toLowerCase(),
                  render:c=>(<span>{c.name}{c.alias && c.alias!==c.name && <span style={{color:C.muted,fontWeight:'400',marginLeft:'6px',fontSize:'12px'}}>{c.alias}</span>}</span>) },
                { key:'model', label:'Billing', sortValue:c=>c.is_internal ? 'zz' : mLabel(c.model),
                  render:c=>c.is_internal ? <StatusBadge tone="neutral" title="Overhead client: excluded from billable hours and earnings.">Internal</StatusBadge> : <span style={{...css.badge(mBadge(c.model)),whiteSpace:'nowrap'}}>{mLabel(c.model)}</span> },
                { key:'terms', label:'Rate', render:c=>c.is_internal ? <span style={{color:C.muted}}>—</span> : mSub(c) },
                { key:'hours', label:'This month', align:'right', tip:'Hours logged against this client so far this calendar month, by everyone.',
                  sortValue:c=>clientMonthHours[c.id]||0, render:c=>clientMonthHours[c.id] ? fmtHours(clientMonthHours[c.id]) : <span style={{color:C.muted}}>—</span> },
                { key:'outstanding', label:'Outstanding', align:'right', tip:'Sent invoices for this client that aren\'t marked paid yet.',
                  sortValue:c=>clientOutstanding[c.id]||0,
                  render:c=>clientOutstanding[c.id] ? <span style={{color:C.textBright,fontWeight:'600'}}>{fmtMoney(clientOutstanding[c.id])}</span> : <span style={{color:C.muted}}>—</span> },
                { key:'status', label:'Status', sortValue:c=>c.is_active===false ? 1 : 0,
                  render:c=>(
                    <span style={{display:'inline-flex',gap:'6px',flexWrap:'wrap'}}>
                      {c.is_active===false
                        ? <StatusBadge tone="neutral">Ended{c.ended_at ? ` ${fmtDate(c.ended_at)}` : ''}</StatusBadge>
                        : <StatusBadge tone="good">Active</StatusBadge>}
                      {c.invoice_reminders_enabled===false && <StatusBadge tone="neutral" title="Automatic invoice reminders are off for this client.">Reminders off</StatusBadge>}
                    </span>
                  ) },
              ]}
              rowActions={c => c.is_active===false ? [
                { label:'Reactivate', onClick:()=>restoreClient(c.id) },
                { label:'Delete', danger:true, onClick:()=>setConfirmRemove(c) },
              ] : [
                { label:'Edit', onClick:()=>openClient(c) },
                ...(qboConnected && c.qbo_sync_enabled!==false ? [{ label: qboSyncingClient===c.id ? 'Syncing…' : c.qbo_customer_id ? 'Re-sync to QuickBooks' : 'Sync to QuickBooks', disabled: qboSyncingClient===c.id, onClick:()=>syncClientToQbo(c.id) }] : []),
                { label:'End contract', onClick:()=>setEndClientModal({client:c,endDate:new Date().toISOString().slice(0,10)}) },
                { label:'Delete', danger:true, onClick:()=>setConfirmRemove(c) },
              ]}
              empty={<EmptyState title={clientSearchQ ? 'No clients match' : clientStatus==='archived' ? 'No archived clients' : 'No clients yet'}
                body={clientSearchQ ? `Nothing matches "${clientSearch.trim()}".` : clientStatus==='archived' ? 'Clients whose contract you end show up here.' : 'Add your first client with + Add client.'} />}
            />
          </div>

          {editClientId && (() => {
                  const c = clients.find(x => x.id === editClientId);
                  if (!c) return null;
                  const settingsBadge = [clientForm.is_nonprofit, clientForm.po_required, clientForm.is_internal].filter(Boolean).length;
                  const TabBtn = ({ id, label, badge }) => (
                    <div onClick={()=>setClientEditTab(id)} style={{padding:"7px 14px",fontSize:"12px",fontWeight:"600",cursor:"pointer",
                      color:clientEditTab===id?C.accent:C.muted,borderBottom:`2px solid ${clientEditTab===id?C.accent:'transparent'}`}}>
                      {label}{badge>0 && <span style={{background:"rgba(0,160,200,0.15)",color:C.accent,borderRadius:"8px",padding:"1px 6px",fontSize:"10px",marginLeft:"5px"}}>{badge}</span>}
                    </div>
                  );
                  return (
                  <DetailPanel open onClose={()=>setEditClientId(null)} title={`Edit ${c.name}`} width={660}>
                  <div>
                    <div style={{display:"flex",gap:"4px",borderBottom:`1px solid ${C.border}`,marginBottom:"14px"}}>
                      <TabBtn id="overview" label="Overview" />
                      <TabBtn id="contact" label="Contact" />
                      <TabBtn id="settings" label="Settings" badge={settingsBadge} />
                    </div>

                    {clientEditTab==='overview' && (
                      <div>
                        <div style={{display:"flex",gap:"10px",marginBottom:"10px"}}>
                          <div style={{width:"260px"}}><div style={css.lbl}>Client Name</div><input style={css.inp} value={clientForm.name||""} onChange={e=>setCF("name",e.target.value)} /></div>
                          <div style={{width:"160px"}}><div style={css.lbl}><TKTooltip text="Short name or acronym for this client (e.g. 'Acme Corporation' → 'Acme'). Optional.">Client Alias ⓘ</TKTooltip></div><input style={css.inp} placeholder="Short name" value={clientForm.alias||""} onChange={e=>setCF("alias",e.target.value)} /></div>
                        </div>
                        <div style={{marginBottom:"14px",width:"400px",maxWidth:"100%"}}>
                          <div style={css.lbl}><TKTooltip text="Full legal name shown on invoices. Leave blank to use the Client Name.">Invoice / Billing Name ⓘ</TKTooltip></div>
                          <input style={css.inp} placeholder="Same as client name"
                            value={clientForm._billingNameSameAsClient ? '' : (clientForm.billing_name||"")}
                            onChange={e=>setCF("billing_name",e.target.value)} />
                          <div style={{display:"flex",alignItems:"center",gap:"6px",marginTop:"5px"}}>
                            <input type="checkbox" id={`bnSame-${editClientId}`} checked={!clientForm.billing_name}
                              onChange={e=>setCF("billing_name", e.target.checked ? '' : (clientForm.name||''))} />
                            <label htmlFor={`bnSame-${editClientId}`} style={{fontSize:"11px",color:C.muted,cursor:"pointer"}}>Same as client name</label>
                          </div>
                        </div>
                        <ClientBillingTerms clientId={editClientId} css={css} C={C}
                          onError={notifyError} onChanged={onRefresh} />
                      </div>
                    )}

                    {clientEditTab==='contact' && (
                      <div>
                        <div style={{fontSize:"11px",color:C.muted,marginBottom:"12px"}}>Shown on invoices sent to this client.</div>
                        <div style={{marginBottom:"10px",width:"380px",maxWidth:"100%"}}>
                          <div style={css.lbl}>Address Line 1</div>
                          <input style={css.inp} placeholder="123 Main St" value={clientForm.address_line1||""} onChange={e=>setCF("address_line1",e.target.value)} />
                        </div>
                        <div style={{marginBottom:"10px",width:"380px",maxWidth:"100%"}}>
                          <div style={css.lbl}>Address Line 2 <span style={{color:C.muted,fontWeight:"400"}}>(Suite, Unit, etc.)</span></div>
                          <input style={css.inp} placeholder="Suite 100" value={clientForm.address_line2||""} onChange={e=>setCF("address_line2",e.target.value)} />
                        </div>
                        <div style={{display:"flex",gap:"10px",marginBottom:"10px"}}>
                          <div style={{width:"200px"}}><div style={css.lbl}>City</div><input style={css.inp} placeholder="New York" value={clientForm.city||""} onChange={e=>setCF("city",e.target.value)} /></div>
                          <div style={{width:"70px"}}><div style={css.lbl}>State</div><input style={css.inp} placeholder="NY" value={clientForm.state||""} onChange={e=>setCF("state",e.target.value)} /></div>
                          <div style={{width:"100px"}}><div style={css.lbl}>ZIP</div><input style={css.inp} placeholder="10001" value={clientForm.zip||""} onChange={e=>setCF("zip",e.target.value)} /></div>
                        </div>
                        <div style={{marginBottom:"14px",width:"220px"}}>
                          <div style={css.lbl}>Country</div><input style={css.inp} placeholder="United States" value={clientForm.country||""} onChange={e=>setCF("country",e.target.value)} />
                        </div>
                        <div style={{display:"flex",gap:"10px",marginBottom:"10px"}}>
                          <div style={{width:"220px"}}><div style={css.lbl}><TKTooltip text="Name shown on invoices and used in reminder emails sent to this client.">Billing Contact Name ⓘ</TKTooltip></div><input style={css.inp} placeholder="Jane Smith" value={clientForm.billing_contact_name||""} onChange={e=>setCF("billing_contact_name",e.target.value)} /></div>
                          <div style={{width:"170px"}}><div style={css.lbl}><TKTooltip text="Phone number shown on invoices when the 'Show Client Phone' toggle is enabled in invoice settings.">Phone ⓘ</TKTooltip></div><input style={css.inp} type="tel" placeholder="(555) 555-5555" value={clientForm.phone||""} onChange={e=>setCF("phone",e.target.value)} /></div>
                        </div>
                        <div style={{width:"400px",maxWidth:"100%"}}>
                          <div style={css.lbl}><TKTooltip text="Email address invoices and payment reminders are sent to for this client. Optional if the client has an AP inbox email and only wants invoices there.">Billing Contact Email ⓘ</TKTooltip></div>
                          <input style={css.inp} type="text" placeholder="billing@client.com" value={clientForm.billing_contact_email||""} onChange={e=>setCF("billing_contact_email",e.target.value)} />
                          <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>Separate multiple addresses with a comma or semicolon.</div>
                        </div>
                        <div style={{width:"400px",maxWidth:"100%",marginTop:"14px"}}>
                          <div style={css.lbl}><TKTooltip text="Optional. The client's accounts-payable intake address, such as a Bill.com, Relay or Ramp inbox. The first time each invoice is sent, this address gets a separate copy with the invoice attached as a PDF and no amounts in the email body, so their AP system records the total once. Resending the invoice does not send it here again. If the client has no billing contact, invoices go only here and no reminder emails are sent.">AP Inbox Email ⓘ</TKTooltip></div>
                          <input style={css.inp} type="text" placeholder="ap@example.com" value={clientForm.ap_inbox_emails||""} onChange={e=>setCF("ap_inbox_emails",e.target.value)} />
                          <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>Receives the invoice as a PDF only. Separate multiple addresses with a comma or semicolon.</div>
                        </div>
                      </div>
                    )}

                    {clientEditTab==='settings' && (
                      <div>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid rgba(30,39,54,0.4)`}}>
                          <div style={css.lbl}><TKTooltip text="When monthly hours exceed this number, an alert email is sent to the admin. Useful for catching scope creep early. Leave blank to disable.">Hours threshold alert ⓘ</TKTooltip></div>
                          <input style={{...css.inp,width:"90px"}} type="number" min="0" step="0.5" placeholder="e.g. 20"
                            value={clientForm.monthly_hours_threshold??""} onChange={e=>setCF("monthly_hours_threshold",e.target.value===''?null:parseFloat(e.target.value))} />
                        </div>
                        <CToggle k="invoice_reminders_enabled" invertDefault label="Send automated reminder emails" desc="When off, no reminder emails are sent for any invoice for this client." />
                        <CToggle k="is_internal" label="Mark as internal" desc="Hides billing fields; excluded from Client Reports, invoices, and revenue tracking." />
                        {qboConnected && <>
                          <CToggle k="qbo_sync_enabled" invertDefault label="Sync to QuickBooks" desc="Include this client in QBO client syncs. Disable for internal or non-billable clients." />
                          {c.qbo_customer_id && <div style={{fontSize:"11px",color:C.muted,padding:"4px 0"}}>QBO Customer ID: {c.qbo_customer_id}</div>}
                        </>}

                        <div style={{borderTop:`1px solid rgba(30,39,54,0.5)`,paddingTop:"12px",marginTop:"10px"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                            <div>
                              <div style={{fontSize:"13px",color:C.textBright}}>Nonprofit client</div>
                              <div style={{fontSize:"11px",color:C.muted,marginTop:"2px"}}>Shows a donation callout at invoice generation and on emails.</div>
                            </div>
                            <button onClick={()=>setCF('is_nonprofit', !clientForm.is_nonprofit)}
                              style={{width:"36px",height:"20px",borderRadius:"10px",border:"none",cursor:"pointer",flexShrink:0,
                                background:clientForm.is_nonprofit?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
                              <div style={{position:"absolute",top:"2px",left:clientForm.is_nonprofit?"18px":"2px",
                                width:"16px",height:"16px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
                            </button>
                          </div>
                          {clientForm.is_nonprofit && (
                            <div style={{marginTop:"10px"}}>
                              <div style={css.lbl}><TKTooltip text="This is a helpful reference when you choose to give — there's no obligation to donate. The suggested amount is tracked in the Invoices → Donations tab.">Donation Percentage (%) ⓘ</TKTooltip></div>
                              <input style={{...css.inp,width:"110px"}} type="number" min="0" max="100" step="0.1"
                                placeholder="e.g. 5" value={clientForm.donation_percentage??''}
                                onChange={e=>setCF('donation_percentage', e.target.value===''?null:parseFloat(e.target.value))} />
                              <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>Used to calculate a suggested donation amount from each invoice total.</div>
                            </div>
                          )}
                        </div>

                        <div style={{borderTop:`1px solid rgba(30,39,54,0.5)`,paddingTop:"12px",marginTop:"12px"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                            <div>
                              <div style={{fontSize:"13px",color:C.textBright}}>PO required for invoices</div>
                              <div style={{fontSize:"11px",color:C.muted,marginTop:"2px"}}>When on, a PO number must be entered before an invoice can be sent to this client.</div>
                            </div>
                            <button onClick={()=>setCF('po_required', !clientForm.po_required)}
                              style={{width:"36px",height:"20px",borderRadius:"10px",border:"none",cursor:"pointer",flexShrink:0,
                                background:clientForm.po_required?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
                              <div style={{position:"absolute",top:"2px",left:clientForm.po_required?"18px":"2px",
                                width:"16px",height:"16px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
                            </button>
                          </div>
                          {clientForm.po_required && (
                            <div style={{marginTop:"10px"}}>
                              <div style={{width:"280px"}}>
                                <div style={css.lbl}><TKTooltip text="A blanket PO covers every invoice for this client with the same number — set it once here and it auto-fills every invoice. A per-invoice PO requires a fresh number each time, either typed in manually or requested from the billing contact.">How does this client issue POs? ⓘ</TKTooltip></div>
                                <select style={css.sel} value={clientForm.po_mode||'per_invoice'} onChange={e=>setCF('po_mode', e.target.value)}>
                                  <option value="per_invoice">A new PO for each invoice</option>
                                  <option value="blanket">One blanket/standing PO for all invoices</option>
                                </select>
                              </div>
                              {clientForm.po_mode === 'blanket' && (
                                <div style={{marginTop:"10px",width:"280px"}}>
                                  <div style={css.lbl}><TKTooltip text="The single PO number this client wants on every invoice. Set it once here — it auto-fills on new invoices automatically, and can still be overridden per invoice for a one-off exception.">Blanket PO Number ⓘ</TKTooltip></div>
                                  <input style={css.inp} type="text" placeholder="e.g. PO-2026-STANDING"
                                    value={clientForm.blanket_po_number||''} onChange={e=>setCF('blanket_po_number', e.target.value)} />
                                  <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>Auto-fills on every invoice for this client. Still editable per-invoice if an exception ever comes up.</div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div style={{display:"flex",gap:"8px",paddingTop:"16px",marginTop:"14px",borderTop:`1px solid ${C.border}`}}>
                      <button style={css.btn("primary")} onClick={saveClient}>Save Changes</button>
                      <button style={css.btn("secondary")} onClick={()=>setEditClientId(null)}>Cancel</button>
                    </div>
                  </div>
                  </DetailPanel>
                  );
                })()}

          {/* Remove client confirmation */}
          {confirmRemove && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1001}}>
              <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",padding:"28px",maxWidth:"380px",width:"90%"}}>
                <div style={{fontSize:"15px",fontWeight:"700",color:C.danger,marginBottom:"8px"}}>Delete {confirmRemove.name}?</div>
                <div style={{fontSize:"13px",color:C.textDim,marginBottom:"20px"}}>
                  This client has no time entries or invoices, so they can be permanently deleted. This action <strong style={{color:C.danger}}>cannot be undone</strong>. If you think you might work with this client again in the future, use End Contract instead.
                </div>
                <div style={{display:"flex",gap:"10px"}}>
                  <button style={css.btn()} onClick={()=>setConfirmRemove(null)}>Cancel</button>
                  <button style={{...css.btn("danger"),background:C.danger,borderColor:C.danger}} onClick={()=>delClient(confirmRemove.id)}>Delete permanently</button>
                </div>
              </div>
            </div>
          )}

          {endClientModal && (() => {
            const c = endClientModal.client;
            return (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1001}}>
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",padding:"28px",maxWidth:"420px",width:"90%"}}>
                  <div style={{fontSize:"15px",fontWeight:"700",color:C.textBright,marginBottom:"8px"}}>End contract with {c.name}?</div>
                  <div style={{fontSize:"13px",color:C.textDim,marginBottom:"18px",lineHeight:"1.5"}}>
                    This will mark the client relationship as ended and remove {c.name} from active views. All time entries, invoices, and history will be preserved. You can reactivate this client at any time.
                  </div>
                  <div style={{marginBottom:"20px"}}>
                    <div style={css.lbl}>End Date</div>
                    <input type="date" style={css.inp} value={endClientModal.endDate}
                      onChange={e=>setEndClientModal(s=>({...s,endDate:e.target.value}))} />
                  </div>
                  <div style={{display:"flex",gap:"10px"}}>
                    <button style={css.btn("primary")} onClick={()=>archiveClient(c.id, endClientModal.endDate)}>End Contract</button>
                    <button style={css.btn()} onClick={()=>setEndClientModal(null)}>Cancel</button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Extension ── */}
      {inSub('Extension') && (
        <div style={{maxWidth:"560px"}}>
          <div style={{fontSize:"13px",color:C.muted,marginBottom:"16px"}}>Control Chrome extension features for all members of your organization.</div>

          {/* Download CTA — always visible; status indicator shows whether installed */}
          <div style={{display:'flex',alignItems:'center',gap:'14px',padding:'14px 16px',borderRadius:'10px',
            background:'rgba(49,167,217,0.06)',border:`1px solid rgba(49,167,217,0.2)`,marginBottom:'20px'}}>
            <span style={{fontSize:'28px',flexShrink:0}}>🧩</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:'14px',fontWeight:'700',color:C.textBright,marginBottom:'2px'}}>TimeKeep Chrome Extension</div>
              <div style={{fontSize:'12px',color:C.muted}}>Track time directly from Gmail, Calendar, Asana, GitHub, and more.</div>
              {extInstalled
                ? <div style={{fontSize:'11px',color:C.success,marginTop:'4px',fontWeight:'600'}}>✓ Extension detected — you're all set</div>
                : <div style={{fontSize:'11px',color:C.muted,marginTop:'4px'}}>Not detected on this browser</div>}
            </div>
            <a href={CHROME_STORE_URL} target="_blank" rel="noreferrer"
              style={{flexShrink:0,padding:'8px 16px',borderRadius:'7px',background:C.accent,color:'#fff',
                fontSize:'12px',fontWeight:'700',textDecoration:'none',whiteSpace:'nowrap'}}>
              {extInstalled ? 'Reinstall' : 'Add to Chrome'}
            </a>
          </div>
          <div style={css.card}>
            {/* In-page tracking button */}
            <div style={{padding:"12px 0",borderBottom:`1px solid rgba(30,39,54,0.5)`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{fontSize:"13px",color:C.textBright,fontWeight:"500"}}>In-page tracking button</div>
                  <div style={{fontSize:"11px",color:C.muted,marginTop:"2px"}}>Show a floating "Track in TimeKeep" button on supported web apps</div>
                </div>
                <button onClick={()=>saveExtSetting('ext_embedded_tracking',!orgForm.ext_embedded_tracking)}
                  style={{width:"40px",height:"22px",borderRadius:"11px",border:"none",cursor:"pointer",flexShrink:0,
                    background:orgForm.ext_embedded_tracking?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
                  <div style={{position:"absolute",top:"3px",left:orgForm.ext_embedded_tracking?"21px":"3px",
                    width:"16px",height:"16px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
                </button>
              </div>
              {orgForm.ext_embedded_tracking && (
                <div style={{marginTop:"14px",paddingLeft:"12px",borderLeft:`2px solid ${C.border}`}}>
                  <div style={{marginBottom:"12px"}}>
                    <div style={{...css.lbl,marginBottom:"8px"}}>Button position</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px"}}>
                      {[['top-left','Top Left'],['top-right','Top Right'],['bottom-left','Bottom Left'],['bottom-right','Bottom Right']].map(([val,label])=>(
                        <label key={val} style={{display:"flex",alignItems:"center",gap:"8px",cursor:"pointer",fontSize:"13px",color:C.text}}>
                          <input type="radio" name="ext_fab_position" value={val}
                            checked={orgForm.ext_fab_position===val}
                            onChange={()=>saveExtSetting('ext_fab_position',val)}
                            style={{accentColor:C.accent}} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{...css.lbl,marginBottom:"8px"}}>Show on</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px"}}>
                      {[['gmail','Gmail'],['calendar','Google Calendar'],['asana','Asana'],['trello','Trello'],['jira','Jira'],['github','GitHub']].map(([key,label])=>{
                        const checked = !!(orgForm.ext_fab_apps?.[key]??true);
                        return (
                          <label key={key} style={{display:"flex",alignItems:"center",gap:"8px",cursor:"pointer",fontSize:"13px",color:C.text}}>
                            <input type="checkbox" checked={checked}
                              onChange={e=>saveExtFabApp(key,e.target.checked)}
                              style={{accentColor:C.accent}} />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Remaining toggles */}
            {[
              {k:'ext_idle_detection',    label:'Idle detection',           desc:'Pause the timer and prompt the user when they\'ve been idle for 5+ minutes'},
              {k:'ext_keyboard_shortcut', label:'Keyboard shortcut',        desc:'Allow start/stop timer via keyboard shortcut (Ctrl+Shift+S / Cmd+Shift+S)'},
              {k:'ext_live_earnings',     label:'Live earnings display',    desc:'Show estimated earnings on the running timer based on the member\'s pay rate'},
              {k:'ext_burn_indicator',    label:'Retainer/threshold burn',  desc:'Show how much of a client\'s retainer or hour threshold has been used this month (admin/owner only)'},
              {k:'ext_description_prefill',label:'Auto-fill description',   desc:'Pre-fill the timer description from the current page (email subject, event title, task name)'},
            ].map(({k,label,desc})=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"12px 0",borderBottom:`1px solid rgba(30,39,54,0.5)`}}>
                <div>
                  <div style={{fontSize:"13px",color:C.textBright,fontWeight:"500"}}>{label}</div>
                  <div style={{fontSize:"11px",color:C.muted,marginTop:"2px"}}>{desc}</div>
                </div>
                <button onClick={()=>saveExtSetting(k,!orgForm[k])}
                  style={{width:"40px",height:"22px",borderRadius:"11px",border:"none",cursor:"pointer",flexShrink:0,
                    background:orgForm[k]?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
                  <div style={{position:"absolute",top:"3px",left:orgForm[k]?"21px":"3px",
                    width:"16px",height:"16px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Task Types ── */}
      {inSub('Task Types') && (
        <div style={{maxWidth:"500px"}}>
          <div style={{display:'flex',alignItems:'flex-start',gap:'10px',padding:'12px 14px',marginBottom:'12px',
            background:`${C.accent}0f`,border:`1px solid ${C.accent}33`,borderRadius:'8px',fontSize:'12px',color:C.muted,lineHeight:'1.5'}}>
            <span style={{fontSize:'16px',flexShrink:0,marginTop:'1px'}}>🏷️</span>
            <div>
              {!taskHelpOpen ? (
                <div>
                  <span style={{fontWeight:'600',color:C.text}}>Task types</span> say what kind of work a time entry was, like Meeting, Research, or Development.
                  They show up on invoices, in reports, and wherever time is logged.{' '}
                  <button type="button" aria-expanded="false" onClick={()=>setTaskHelpOpen(true)} style={taskHelpLink}>Read more</button>
                </div>
              ) : (<>
              <div>
                <span style={{fontWeight:'600',color:C.text}}>Task types</span> say what kind of work a time entry was, like Meeting, Research, or Development.
                People pick one when they log time, and it is optional.
              </div>
              <div style={{marginTop:'8px'}}>
                <span style={{fontWeight:'600',color:C.text}}>How teams use them.</span> Keep the list short and about the kind of work, not the client or project
                (the client already says who it was for). Turn off <span style={{fontWeight:'600',color:C.text}}>Charge client by default</span> for work you do not
                charge for, like internal meetings.
              </div>
              <div style={{marginTop:'8px',fontWeight:'600',color:C.text}}>Where they show up</div>
              <ul style={{margin:'4px 0 0',paddingLeft:'18px'}}>
                <li><span style={{fontWeight:'600',color:C.text}}>Invoices.</span> When an invoice is grouped by task type (the default), each task type becomes one line with its hours.
                  Time with no task type goes under “General”. Change the default under Billing &amp; invoices, Invoices, or per invoice.</li>
                <li><span style={{fontWeight:'600',color:C.text}}>Reports.</span> Client Reports break hours down by task type and can filter by it, and Insights flags entries that are missing one.</li>
                <li><span style={{fontWeight:'600',color:C.text}}>Logging.</span> The Timer, Manual entry, My Logs, the Chrome extension, and the mobile app all offer this list. Time entry imports match task types by name.</li>
              </ul>
              <div style={{marginTop:'8px'}}>
                <span style={{fontWeight:'600',color:C.text}}>Good to know.</span> Renaming a task type changes its name wherever it is shown from now on, and invoices you have already created keep their wording.
                Removing one never deletes time: those entries just lose the label. For labels that never affect billing, use{' '}
                <button type="button" onClick={()=>changeSubTab('Data','Tags')}
                  style={{background:'none',border:'none',padding:0,cursor:'pointer',color:C.accent,fontSize:'12px',fontFamily:'inherit',textDecoration:'underline'}}>Tags</button>.
              </div>
              <div style={{marginTop:'8px'}}>
                <button type="button" aria-expanded="true" onClick={()=>setTaskHelpOpen(false)} style={taskHelpLink}>Show less</button>
              </div>
              </>)}
            </div>
          </div>
          <div style={css.card}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 150px 116px",alignItems:"end",padding:"0 11px 6px",fontSize:"11px",color:C.muted}}>
              <span>Task type</span>
              <span style={{display:"flex",justifyContent:"center",textAlign:"center"}}>
                <TKTooltip align="center" text="New entries with this task type start as billable (on) or not billable to the client (off). Turn it off for work you do not charge for, like internal meetings. Anyone can still change a single entry, and pay is not affected.">Charge client by default ⓘ</TKTooltip>
              </span>
              <span />
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:"6px",marginBottom:"14px"}}>
              {taskTypes.map(t=>(
                <div key={t.id} style={{display:"grid",gridTemplateColumns:"1fr 150px 116px",alignItems:"center",padding:"7px 10px",background:C.bg,borderRadius:"6px",border:`1px solid ${C.border}`}}>
                  {editTask===t.id
                    ?<input style={{...css.inp,minWidth:0,marginRight:"8px"}} value={editTaskVal} autoFocus
                        onChange={e=>setEditTaskVal(e.target.value)}
                        onKeyDown={e=>{if(e.key==="Enter")saveTask(t.id);if(e.key==="Escape")setEditTask(null);}} />
                    :<span style={{fontSize:"13px",color:C.text,minWidth:0,overflowWrap:"anywhere"}}>{t.name}</span>
                  }
                  <div style={{display:"flex",justifyContent:"center"}}>
                    <button type="button" role="switch" aria-checked={t.billable_by_default!==false} aria-label={`Charge client by default for ${t.name}`}
                      title={t.billable_by_default!==false ? 'New entries start billable to the client. Click to start them not billable.' : 'New entries start not billable to the client. Click to start them billable.'}
                      onClick={()=>saveTaskDefault(t.id, t.billable_by_default===false)}
                      style={{width:'36px',height:'20px',borderRadius:'10px',border:'none',padding:0,cursor:'pointer',flexShrink:0,position:'relative',
                        background:t.billable_by_default!==false?'rgba(72,187,120,0.8)':'rgba(100,116,139,0.35)',transition:'background 0.2s'}}>
                      <span style={{position:'absolute',top:'2px',left:t.billable_by_default!==false?'18px':'2px',width:'16px',height:'16px',borderRadius:'50%',background:C.surface,transition:'left 0.2s'}} />
                    </button>
                  </div>
                  <div style={{display:"flex",gap:"4px",justifyContent:"flex-end"}}>
                    {editTask===t.id
                      ?<><button style={{...css.btn("primary"),padding:"4px 10px",fontSize:"12px"}} onClick={()=>saveTask(t.id)}>Save</button>
                          <button style={{...css.btn(),padding:"4px 10px",fontSize:"12px"}} onClick={()=>setEditTask(null)}>Cancel</button></>
                      :<><button style={css.smBtn(C.textDim)} onClick={()=>{setEditTask(t.id);setEditTaskVal(t.name);}}>Edit</button>
                          <button style={css.smBtn(C.danger)} onClick={()=>delTask(t.id)}>Remove</button></>
                    }
                  </div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:"8px"}}>
              <input style={{...css.inp,flex:1}} placeholder="New task type..." value={newTask}
                onChange={e=>setNewTask(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addTask()} />
              <button style={css.btn("primary")} onClick={addTask}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tags ── */}
      {inSub('Tags') && (
        <div style={{maxWidth:"500px"}}>
          <div style={{fontSize:"13px",color:C.muted,marginBottom:"14px"}}>Tags can be added to time entries and are for organization only — they do not affect billing.</div>
          <div style={css.card}>
            <div style={{display:"flex",flexDirection:"column",gap:"6px",marginBottom:"14px"}}>
              {tags.map(t=>(
                <div key={t.id} style={{display:"flex",alignItems:editTagId===t.id?"flex-start":"center",justifyContent:"space-between",padding:"7px 10px",background:C.bg,borderRadius:"6px",border:`1px solid ${C.border}`}}>
                  {editTagId===t.id
                    ?<div style={{flex:1,marginRight:"8px"}}>
                        <div style={{display:"flex",gap:"6px",marginBottom:"6px",flexWrap:"wrap"}}>
                          {TAG_PRESETS.map(c=>(
                            <button key={c} onClick={()=>setEditTagColor(c)} style={{width:"20px",height:"20px",borderRadius:"50%",background:c,border:editTagColor===c?`2px solid #fff`:`2px solid transparent`,cursor:"pointer",padding:0,flexShrink:0}} />
                          ))}
                        </div>
                        <input style={{...css.inp,width:"100%"}} value={editTagName} autoFocus
                          onChange={e=>setEditTagName(e.target.value)}
                          onKeyDown={e=>{if(e.key==="Enter")saveTag(t.id);if(e.key==="Escape")setEditTagId(null);}} />
                      </div>
                    :<div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                        <span style={{width:"10px",height:"10px",borderRadius:"50%",background:t.color||'transparent',border:t.color?'none':`1px solid ${C.muted}`,display:"inline-block",flexShrink:0}} />
                        <span style={{fontSize:"13px",color:C.text}}>{t.name}</span>
                      </div>
                  }
                  <div style={{display:"flex",gap:"4px",flexShrink:0,alignSelf:"flex-end"}}>
                    {editTagId===t.id
                      ?<><button style={{...css.btn("primary"),padding:"4px 10px",fontSize:"12px"}} onClick={()=>saveTag(t.id)}>Save</button>
                          <button style={{...css.btn(),padding:"4px 10px",fontSize:"12px"}} onClick={()=>setEditTagId(null)}>Cancel</button></>
                      :<><button style={css.smBtn(C.textDim)} onClick={()=>{setEditTagId(t.id);setEditTagName(t.name);setEditTagColor(t.color||'#31A7D9');}}>Edit</button>
                          <button style={css.smBtn(C.danger)} onClick={()=>delTag(t.id)}>Remove</button></>
                    }
                  </div>
                </div>
              ))}
              {tags.length === 0 && <div style={{fontSize:"13px",color:C.muted,textAlign:"center",padding:"12px 0"}}>No tags yet.</div>}
            </div>
            <div>
              <div style={css.lbl}><TKTooltip text="Color shown as a swatch on the tag chip. Pick a preset or enter a custom hex value below.">Color ⓘ</TKTooltip></div>
              <div style={{display:"flex",gap:"6px",marginBottom:"8px",flexWrap:"wrap"}}>
                {TAG_PRESETS.map(c=>(
                  <button key={c} onClick={()=>setNewTagColor(c)} style={{width:"22px",height:"22px",borderRadius:"50%",background:c,border:newTagColor===c?`2px solid #fff`:`2px solid transparent`,cursor:"pointer",padding:0}} />
                ))}
              </div>
              <div style={{display:"flex",gap:"8px"}}>
                <input style={{...css.inp,flex:1}} placeholder="New tag name…" value={newTagName}
                  onChange={e=>setNewTagName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addTag()} />
                <button style={css.btn("primary")} onClick={addTag}>Add</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {inSub('Import Data') && (
        <div style={{maxWidth:"900px"}}>
          <div style={{fontSize:"13px",color:C.muted,marginBottom:"16px"}}>Bulk-load clients, team members, time entries, or historical invoices from a .csv or .xlsx file. Nothing is saved until you review a preview and confirm.</div>
          <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
            {[
              ['clients','Clients','Create or update clients by name.'],
              ['team','Team Members','Sends real invitations — the same email flow as inviting one person at a time.'],
              ['time_entries','Time Entries','Requires the team members and clients on the sheet to already exist in TimeKeep.'],
              ['invoices','Invoices','For historical invoices already issued elsewhere — does not send email or sync to QuickBooks/Stripe.'],
            ].map(([key,label,desc])=>(
              <div key={key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",borderRadius:"8px",border:`1px solid ${C.border}`,background:C.bg}}>
                <div>
                  <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright}}>{label}</div>
                  <div style={{fontSize:"11px",color:C.muted,marginTop:"2px"}}>{desc}</div>
                </div>
                <button style={css.btn("secondary")} onClick={()=>setImportEntity(key)}>Import…</button>
              </div>
            ))}
          </div>
          <ImportHistory org={org} refreshKey={importHistoryKey} />
        </div>
      )}

      {(orgDirty || saveMsg) && (
        // Fixed, not sticky: the main column's overflow:auto ancestor has no set height,
        // which silently disables position:sticky. Left clears the 220px sidebar; right
        // clears the floating quick-actions button.
        <div role="region" aria-label="Unsaved changes" style={{position:'fixed',bottom:'16px',left:'244px',right:'76px',zIndex:620,display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap',
          background:C.surface,border:`1px solid ${C.border}`,borderRadius:'10px',padding:'12px 16px',boxShadow:'0 6px 24px rgba(0,0,0,0.25)',maxWidth:'760px'}}>
          <span style={{fontSize:'13px',color:saveMsg && saveMsg!=='Changes saved.' ? C.danger : saveMsg ? C.success : C.text,flex:1}}>
            {saveMsg || 'You have unsaved changes.'}
          </span>
          {orgDirty && <button style={css.btn('secondary')} onClick={discardOrg} disabled={saving}>Discard</button>}
          {orgDirty && <button style={css.btn('primary')} onClick={saveOrg} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>}
        </div>
      )}
      {importEntity && (
        <BulkImportModal entityType={importEntity} org={org} profile={profile}
          onClose={()=>{setImportEntity(null);setImportHistoryKey(k=>k+1);}} onComplete={onRefresh} />
      )}
    </Page>
  );
}

const MEMBER_NOTIF_TYPES = [
  { k: 'notifications_contractor_weekly',    label: 'Weekly Summary',     desc: 'A summary of your logged hours and earnings' },
  { k: 'notifications_contractor_month_end', label: 'Month-End Reminder', desc: 'A reminder to log outstanding time before the billing period closes' },
  { k: 'notifications_timer_reminder',       label: 'Timer Reminder',     desc: "An alert if you've left a timer running too long" },
];

// Non-admin landing page for nav==='settings' — a minimal shell around the
// same AccountTab an admin sees, without any of SettingsView's admin-only tabs.
// Account (opened from the user menu, for everyone): your login and your own
// notification choices.
function MemberAccountView({ org, orgId }) {
  return (
    <Page title="Account" subtitle="Your login applies to every workspace you belong to.">
      <AccountTab org={org} orgId={orgId} />
    </Page>
  );
}

function AccountTab({ org, orgId }) {
  const [session, setSession] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const [myMember, setMyMember] = useState(null);
  const [prefsSaving, setPrefsSaving] = useState(null); // key currently saving, or null
  const [jobTitle, setJobTitle] = useState('');
  const [titleSaving, setTitleSaving] = useState(false);
  const [titleSaved, setTitleSaved] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
  }, []);

  useEffect(() => {
    if (!orgId) return;
    let canceled = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user?.id) return;
      supabase.from('org_members').select('id, notification_prefs, job_title')
        .eq('org_id', orgId).eq('user_id', session.user.id).is('ended_at', null).single()
        .then(({ data }) => {
          if (canceled) return;
          setMyMember(data || null);
          setJobTitle(data?.job_title || '');
        });
    });
    return () => { canceled = true; };
  }, [orgId]);

  const toggleNotifPref = async (key) => {
    if (!orgId || !myMember) return;
    const current = myMember.notification_prefs?.[key];
    const next = current === false ? true : false; // default (undefined) counts as opted-in
    setPrefsSaving(key);
    const { error } = await supabase.rpc('update_own_notification_prefs', {
      p_org_id: orgId,
      p_prefs: { [key]: next },
    });
    setPrefsSaving(null);
    if (error) { notifyError('Saving notification preference failed: ' + error.message); return; }
    setMyMember(m => ({ ...m, notification_prefs: { ...(m?.notification_prefs || {}), [key]: next } }));
  };

  // Written through an RPC, not a direct update: org_members' UPDATE policy is
  // admins only, and widening it so members could edit their own row would also
  // let them edit their own role and pay. Same shape as the notification prefs
  // above.
  const saveJobTitle = async () => {
    if (!orgId) return;
    setTitleSaving(true);
    const { error } = await supabase.rpc('set_own_job_title', {
      p_org_id: orgId, p_job_title: jobTitle,
    });
    setTitleSaving(false);
    if (error) { notifyError('Saving your job title failed: ' + error.message); return; }
    const saved = jobTitle.trim() || '';
    setJobTitle(saved);
    setMyMember(m => (m ? { ...m, job_title: saved || null } : m));
    setTitleSaved(true);
    setTimeout(() => setTitleSaved(false), 2500);
  };

  const handleChangeEmail = async () => {
    setMsg(''); setErr('');
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setErr('Please enter a valid email address.'); return; }
    if (trimmed === session?.user?.email?.toLowerCase()) { setErr('That\'s already your current email address.'); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ email: trimmed });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    setMsg(`Confirmation sent to ${trimmed}. Click the link in that email to complete the change.`);
    setNewEmail('');
  };

  const titleDirty = (myMember?.job_title || '') !== jobTitle.trim();

  return (
    <div style={{maxWidth:'480px'}}>
      {orgId && (
        <div style={{...css.card,marginBottom:'16px'}}>
          <div style={{fontSize:'13px',fontWeight:'600',color:C.textBright,marginBottom:'4px'}}>
            <TKTooltip text="Printed next to your signature on contracts you countersign, wherever a Job title line was placed. Leave it blank and the line is simply left off.">
              Job title ⓘ
            </TKTooltip>
          </div>
          <div style={{fontSize:'12px',color:C.muted,marginBottom:'12px'}}>
            Your title at {org?.name || 'this organization'}. You can have a different one in each workspace you belong to.
          </div>
          <input style={css.inp} type="text" placeholder="e.g. Principal Consultant" maxLength={120}
            value={jobTitle}
            onChange={e=>setJobTitle(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&titleDirty&&saveJobTitle()} />
          <div style={{display:'flex',alignItems:'center',gap:'10px',marginTop:'10px'}}>
            <button style={css.btn('primary')} onClick={saveJobTitle} disabled={titleSaving || !titleDirty}>
              {titleSaving ? 'Saving…' : 'Save'}
            </button>
            {titleSaved && !titleDirty && <span style={{fontSize:'12px',color:C.success}}>Saved</span>}
          </div>
        </div>
      )}

      <div style={css.card}>
        <div style={{fontSize:'13px',fontWeight:'600',color:C.textBright,marginBottom:'14px'}}>Change Email Address</div>
        <div style={{marginBottom:'12px'}}>
          <div style={css.lbl}>Current email</div>
          <div style={{fontSize:'13px',color:C.muted,padding:'8px 12px',background:C.bg,border:`1px solid ${C.border}`,borderRadius:'6px'}}>{session?.user?.email || '—'}</div>
        </div>
        <div style={{marginBottom:'12px'}}>
          <div style={css.lbl}>New email address</div>
          <input style={css.inp} type="email" placeholder="name@example.com" value={newEmail}
            onChange={e=>{setNewEmail(e.target.value);setErr('');setMsg('');}}
            onKeyDown={e=>e.key==='Enter'&&handleChangeEmail()} />
        </div>
        {err && <div style={{fontSize:'12px',color:C.danger,marginBottom:'10px',padding:'8px 10px',background:'rgba(229,62,62,0.08)',borderRadius:'6px',border:`1px solid rgba(229,62,62,0.2)`}}>{err}</div>}
        {msg && <div style={{fontSize:'12px',color:C.success,marginBottom:'10px',padding:'8px 10px',background:'rgba(72,187,120,0.08)',borderRadius:'6px',border:`1px solid rgba(72,187,120,0.2)`}}>{msg}</div>}
        <button style={css.btn('primary')} onClick={handleChangeEmail} disabled={saving || !newEmail.trim()}>
          {saving ? 'Sending…' : 'Send Confirmation Email'}
        </button>
        <div style={{fontSize:'12px',color:C.muted,marginTop:'10px',lineHeight:'1.5'}}>
          We'll send a confirmation link to your new address. Your current email stays active until you click the link.
        </div>
      </div>

      {orgId && (() => {
        const visible = MEMBER_NOTIF_TYPES.filter(n => org?.[n.k] !== false);
        if (visible.length === 0) return null;
        return (
          <div style={{...css.card,marginTop:'16px'}}>
            <div style={{fontSize:'13px',fontWeight:'600',color:C.textBright,marginBottom:'4px'}}>Notifications</div>
            <div style={{fontSize:'12px',color:C.muted,marginBottom:'12px'}}>Your organization controls which of these you can turn off for yourself.</div>
            {visible.map(n => {
              const canOptOut = !!org?.notifications_opt_out_allowed?.[n.k];
              const optedOut = myMember?.notification_prefs?.[n.k] === false;
              return (
                <div key={n.k} style={{display:"flex",alignItems:"flex-start",padding:"10px 0",borderBottom:`1px solid ${C.border}`,gap:"12px"}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:"13px",color:C.textBright,fontWeight:"500"}}>{n.label}</div>
                    <div style={{fontSize:"11px",color:C.muted,marginTop:"2px"}}>{n.desc}</div>
                  </div>
                  {!canOptOut ? (
                    // Locked on by the organization: say so plainly rather than drawing a
                    // switch in the "on" color that does nothing when clicked.
                    <TKTooltip align="left" text="Your organization sends this to everyone and doesn't allow turning it off. Ask an admin if you'd like that changed.">
                      <span style={{fontSize:"12px",color:C.muted,whiteSpace:"nowrap"}}>🔒 Always on ⓘ</span>
                    </TKTooltip>
                  ) : (() => {
                    // Locked notifications are always being sent (that's what "locked" means) —
                    // the read-only toggle should show ON, not the member's inert preference.
                    const isOn = canOptOut ? !optedOut : true;
                    return (
                      <button onClick={() => canOptOut && toggleNotifPref(n.k)} disabled={!canOptOut || !myMember || prefsSaving===n.k}
                        title={canOptOut ? undefined : "Your organization doesn't allow opting out of this notification"}
                        style={{width:"40px",height:"22px",borderRadius:"11px",border:"none",cursor:canOptOut&&myMember?"pointer":"default",flexShrink:0,opacity:!canOptOut?0.5:prefsSaving===n.k?0.5:1,
                          background:isOn?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
                        <div style={{position:"absolute",top:"3px",left:isOn?"21px":"3px",
                          width:"16px",height:"16px",borderRadius:"50%",background:isOn?contrastText(C.accent):C.surface,transition:"left 0.2s"}} />
                      </button>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

function SettingsTeamTab({ orgId, clients, onRefresh }) {
  const [users, setUsers]               = useState([]);
  const [assignments, setAssignments]   = useState([]);
  const [orgMembers, setOrgMembers]     = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [profile, setProfile]           = useState(null);
  const [org, setOrg]                   = useState(null);

  const load = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session || !orgId) return;
    const [u, a, inv, p, o, m] = await Promise.all([
      supabase.from('profiles').select('*').order('name'),
      supabase.from('assignments').select('*').eq('org_id', orgId),
      supabase.from('pending_invitations').select('*').eq('org_id', orgId).eq('status', 'pending').order('created_at', { ascending: false }),
      supabase.from('profiles').select('*').eq('id', session.user.id).single(),
      supabase.from('organizations').select('*').eq('id', orgId).single(),
      supabase.from('org_members').select('*').eq('org_id', orgId),
    ]);
    setUsers(u.data || []);
    setAssignments(a.data || []);
    setPendingInvites(inv.data || []);
    setProfile(p.data || null);
    setOrg(o.data || null);
    setOrgMembers(m.data || []);
  };

  useEffect(() => { load(); }, [orgId]); // load is stable per mount; orgId is the real trigger

  const refresh = async () => { await load(); onRefresh(); };

  if (!profile || !org) return (
    <div style={{color:C.muted,fontSize:"13px",padding:"20px 0"}}>Loading team data...</div>
  );

  return <TeamView users={users} clients={clients} assignments={assignments} pendingInvites={pendingInvites} orgMembers={orgMembers} profile={profile} org={org} onRefresh={refresh} />;
}

// ─── CONTRACTOR STATEMENTS ──────────────────────────────────────────────────────
// ─── MY BUSINESS ─────────────────────────────────────────────────────────────
function MyBusinessView({ profile, allOrgs }) {
  const tour = useTour('business');
  const [selectedMonth, setSelectedMonth] = useState(curMonth());
  const [rangeMode, setRangeMode] = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');
  const [entries, setEntries] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch 6 months of entries on mount, and re-fetch when the impersonated user changes.
  useEffect(() => { load(); }, [profile.id]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    if (!profile?.id) { setLoading(false); return; }
    const sixMonthsAgo = (() => {
      const d = new Date();
      d.setMonth(d.getMonth() - 5);
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
    })();
    // The 6-month trend needs six months back; the YTD view needs everything
    // since January 1. Fetch whichever reaches further, or YTD silently drops
    // its earliest months from July onward.
    const fetchFrom = [sixMonthsAgo, `${new Date().getFullYear()}-01-01`].sort()[0];
    const orgIds = allOrgs.map(m => m.org_id).filter(Boolean);
    // PostgREST returns at most 1000 rows per request, so page until a short page comes back.
    const fetchEntries = async () => {
      const out = [];
      for (let from = 0; ; from += 1000) {
        const { data } = await supabase.from('time_entries').select('id,org_id,client_id,task_type_id,description,hours,entry_date,is_billable,is_payable')
          .eq('user_id', profile.id).gte('entry_date', fetchFrom).order('entry_date', { ascending: false }).order('id').range(from, from + 999);
        out.push(...(data || []));
        if (!data || data.length < 1000) break;
      }
      return { data: out };
    };
    const [{ data: ents }, { data: asgns }, { data: cls }, { data: stmts }] = await Promise.all([
      fetchEntries(),
      supabase.from('assignments').select('id,org_id,client_id,pay_type,rate,monthly_amount,project_amount,project_bill_month,project_billed_at,pay_percent,hybrid_base,hybrid_included_hours,hybrid_overage_rate').eq('user_id', profile.id),
      orgIds.length > 0
        ? supabase.from('clients').select('id,org_id,name,model,rate,retainer,hours,overage').in('org_id', orgIds)
        : Promise.resolve({ data: [] }),
      supabase.from('contractor_statements').select('id,org_id,period,status,total').eq('user_id', profile.id),
    ]);
    setEntries(ents || []);
    setAssignments(asgns || []);
    setClients(cls || []);
    setStatements(stmts || []);
    setLoading(false);
  };

  // 6-month trend: rolling window ending this month, most recent last for the chart.
  const trendMonths = [...Array(6)].map((_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - (5 - i));
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });

  const trendData = trendMonths.map(mo => {
    const moEntries = entries.filter(e => e.entry_date?.slice(0,7) === mo);
    const hours = moEntries.reduce((s, e) => s + parseFloat(e.hours||0), 0);
    // Sum earnings across all orgs for this month
    let earnings = 0;
    allOrgs.forEach(m => {
      const orgId = m.org_id;
      const orgEntries = moEntries.filter(e => e.org_id === orgId);
      const orgAssignments = assignments.filter(a => a.org_id === orgId);
      const clientIds = [...new Set([
        ...orgEntries.map(e => e.client_id).filter(Boolean),
        ...orgAssignments.filter(a => a.pay_type==='flat_monthly' || a.pay_type==='hybrid' || a.pay_type==='percent' || (a.pay_type==='fixed_project' && a.project_bill_month===mo)).map(a => a.client_id),
      ])];
      clientIds.forEach(cid => {
        const asgn = orgAssignments.find(a => a.client_id === cid);
        if (!asgn) return;
        earnings += computeEarnings(asgn, orgEntries.filter(e => e.client_id===cid), mo, clients.find(c=>c.id===cid)).lineTotal;
      });
    });
    const label = new Date(mo + '-02').toLocaleString('en-US', { month: 'short' });
    return { month: label, hours: parseFloat(hours.toFixed(2)), earnings: parseFloat(earnings.toFixed(2)) };
  });

  const clientName = (clientId) => clients.find(c => c.id === clientId)?.name || 'Unassigned client';
  const clientById = (clientId) => clients.find(c => c.id === clientId);

  const nowBiz = new Date();
  const ytdStart = (() => {
    const yr = nowBiz.getMonth() >= 0 ? nowBiz.getFullYear() : nowBiz.getFullYear() - 1;
    return `${yr}-01-01`;
  })();
  const ytdEnd = nowBiz.toISOString().slice(0,10);
  const bizRangeLabel = (() => {
    if (rangeMode === 'month')  return new Date(selectedMonth+'-02').toLocaleString('en-US',{month:'long',year:'numeric'});
    if (rangeMode === 'ytd')    return `Year to Date (${nowBiz.getFullYear()})`;
    if (rangeMode === 'custom' && customStart && customEnd)
      return `${new Date(customStart+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})} – ${new Date(customEnd+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}`;
    return 'Custom Range';
  })();

  // YTD and Custom ranges: pay is the sum of each month's own calculation.
  // Flat, day-rate, project and retainer pay are all defined per month, so one
  // pass over a multi-month range can't price them. Same rule as My Invoices >
  // Earnings: a month pays for a client when there is work for that client in it.
  // In a Custom range a month the range only partly covers pays only the
  // hours-based types; flat/retainer/project amounts need the whole month.
  const PERIOD_PAY_TYPES = ['flat_monthly','hybrid','percent','fixed_project'];
  const rangeMonths = (() => {
    if (rangeMode === 'month') return [];
    const from = rangeMode === 'ytd' ? ytdStart : customStart;
    const to = rangeMode === 'ytd' ? ytdEnd : customEnd;
    if (!from || !to || from > to) return [];
    const out = [];
    let y = parseInt(from.slice(0,4), 10), mo = parseInt(from.slice(5,7), 10);
    const endKey = to.slice(0,7);
    for (;;) {
      const key = `${y}-${String(mo).padStart(2,'0')}`;
      if (key > endKey) break;
      const lastDay = `${key}-${String(new Date(y, mo, 0).getDate()).padStart(2,'0')}`;
      out.push({ mo: key, full: rangeMode === 'ytd' || (from <= `${key}-01` && to >= lastDay) });
      mo += 1; if (mo > 12) { mo = 1; y += 1; }
    }
    return out;
  })();
  const rangeEarnings = (asgn, clientEntries, cid) => {
    let lineTotal = 0;
    rangeMonths.forEach(({ mo, full }) => {
      const monthEntries = clientEntries.filter(e => e.entry_date?.slice(0,7) === mo);
      if (monthEntries.length === 0) return;
      if (!full && PERIOD_PAY_TYPES.includes(asgn.pay_type)) return;
      lineTotal += computeEarnings(asgn, monthEntries, mo, clientById(cid)).lineTotal;
    });
    return { lineTotal };
  };

  // Build per-org summary
  const orgSummaries = allOrgs.map(m => {
    const o = m.organizations;
    if (!o) return null;
    const orgId = m.org_id;
    // Scope to selected range for per-org detail cards.
    const orgEntries = (() => {
      const base = entries.filter(e => e.org_id === orgId);
      if (rangeMode === 'month')  return base.filter(e => e.entry_date?.slice(0,7) === selectedMonth);
      if (rangeMode === 'ytd')    return base.filter(e => e.entry_date >= ytdStart && e.entry_date <= ytdEnd);
      if (rangeMode === 'custom' && customStart && customEnd)
        return base.filter(e => e.entry_date >= customStart && e.entry_date <= customEnd);
      return base.filter(e => e.entry_date?.slice(0,7) === selectedMonth);
    })();
    if (orgEntries.length === 0 && allOrgs.length > 1) return null; // hide orgs with no activity (but keep when single-org)
    const orgAssignments = assignments.filter(a => a.org_id === orgId);
    const totalHrs = orgEntries.reduce((s, e) => s + parseFloat(e.hours || 0), 0);

    // Per-client breakdown. Month mode prices the one month directly (and also lists
    // clients whose flat/retainer pay applies with no hours); YTD/Custom sum months.
    const clientIds = [...new Set([
      ...orgEntries.map(e => e.client_id).filter(Boolean),
      ...(rangeMode === 'month' ? orgAssignments.filter(a => ['flat_monthly','hybrid','percent'].includes(a.pay_type)).map(a => a.client_id) : []),
    ])];
    const clientRows = clientIds.map(cid => {
      const ces = orgEntries.filter(e => e.client_id === cid);
      const asgn = orgAssignments.find(a => a.client_id === cid);
      const earn = !asgn ? null
        : rangeMode === 'month' ? computeEarnings(asgn, ces, selectedMonth, clientById(cid))
        : rangeEarnings(asgn, ces, cid);
      const hrs = ces.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
      return { cid, name: clientName(cid), hrs, earn };
    }).sort((a, b) => b.hrs - a.hrs);

    // Entries with no client
    const unclientedEntries = orgEntries.filter(e => !e.client_id);
    if (unclientedEntries.length > 0) {
      const hrs = unclientedEntries.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
      clientRows.push({ cid: null, name: 'No client', hrs, earn: null });
    }

    const totalEarnings = clientRows.reduce((s, r) => s + (r.earn?.lineTotal || 0), 0);
    const orgStatement = rangeMode === 'month' ? statements.find(s => s.org_id === orgId && s.period === selectedMonth) : null;

    return { orgId, orgName: o.name, totalHrs, totalEarnings, clientRows, orgStatement, entryCount: orgEntries.length };
  }).filter(Boolean);

  // If single-org user has no activity: show empty state but still show org
  const showOrgs = orgSummaries.length > 0
    ? orgSummaries
    : allOrgs.slice(0, 1).map(m => ({ orgId: m.org_id, orgName: m.organizations?.name || '', totalHrs: 0, totalEarnings: 0, clientRows: [], orgStatement: null, entryCount: 0 }));

  const grandHrs = showOrgs.reduce((s, o) => s + o.totalHrs, 0);
  const grandEarnings = showOrgs.reduce((s, o) => s + o.totalEarnings, 0);
  const grandEntries = (() => {
    if (rangeMode === 'month')  return entries.filter(e => e.entry_date?.slice(0,7) === selectedMonth).length;
    if (rangeMode === 'ytd')    return entries.filter(e => e.entry_date >= ytdStart && e.entry_date <= ytdEnd).length;
    if (rangeMode === 'custom' && customStart && customEnd) return entries.filter(e => e.entry_date >= customStart && e.entry_date <= customEnd).length;
    return 0;
  })();

  const fmt$ = (n) => {
    const v = parseFloat(n || 0);
    return v === 0 ? '—' : ('$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };
  const fmtHrs = formatExactTime;

  const periodLabel = bizRangeLabel;

  const stColors = { submitted: C.accent, approved: C.success, disputed: C.danger, draft: C.muted, void: C.muted, confirmed: C.success };

  return (
    <Page title="My Work" subtitle="Your hours and pay across every organization you belong to" onTour={tour.launch}
      tour={{ header: 'business-header' }}
      controls={<Controls range={<>
        <SegmentedControl options={[['month','Month'],['ytd','YTD'],['custom','Custom']]} value={rangeMode} onChange={setRangeMode} />
        {rangeMode==='month' && (
          <select aria-label="Month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
            style={{ ...css.sel, width:'auto', padding:'6px 10px', fontSize:'13px' }}>
            {[...Array(12)].map((_, i) => {
              const d = new Date(); d.setMonth(d.getMonth() - i);
              const val = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
              return <option key={val} value={val}>{fmtPeriod(val)}</option>;
            })}
          </select>
        )}
        {rangeMode==='custom' && (
          <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
            <input type="date" aria-label="From" style={{...css.inp,padding:'4px 8px',fontSize:'12px',width:'136px'}} value={customStart} onChange={e=>setCustomStart(e.target.value)} />
            <span style={{color:C.muted,fontSize:'12px'}}>to</span>
            <input type="date" aria-label="To" style={{...css.inp,padding:'4px 8px',fontSize:'12px',width:'136px'}} value={customEnd} onChange={e=>setCustomEnd(e.target.value)} />
          </div>
        )}
      </>} />}>
      {tour.show && <WalkthroughModal tourKey="business" onClose={tour.close} />}

      {loading ? (
        <div style={{ color: C.muted, fontSize: '14px', textAlign: 'center', padding: '60px 0' }}>Loading…</div>
      ) : (
        <>
          {/* Summary tiles -- glossary names (spec Metrics). The Organizations count
              tile was dropped: it rarely changes and doesn't help decide anything. */}
          <div style={{ marginBottom: '28px' }}>
            <StatTiles items={[
              { label: 'Total hours', value: fmtHours(grandHrs), basis: 'across all your organizations',
                tip: 'Sum of all hours logged across every organization you belong to in the selected range.' },
              { label: 'Your pay', value: grandEarnings > 0 ? fmtMoney(grandEarnings) : '—', basis: 'estimated from your assignments',
                tip: "An estimate across all organizations from each assignment's pay type and rate. For Year to Date and Custom ranges it adds up each month separately. In a Custom range, flat-monthly, retainer-based and fixed-project pay only counts for months the range fully covers. Each organization's approved member invoice is the final figure." },
              { label: 'Time entries', value: String(grandEntries), basis: `in ${allOrgs.length} organization${allOrgs.length === 1 ? '' : 's'}`,
                tip: 'Number of individual time entries logged across all organizations in the selected range.' },
            ]} />
          </div>

          {/* 6-month trend chart */}
          {trendData.some(d => d.hours > 0 || d.earnings > 0) && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px', marginBottom: '24px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: C.textBright, marginBottom: '16px' }}>Last 6 Months</div>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={trendData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                  <XAxis dataKey="month" tick={{ fill: C.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="earn" orientation="left" tick={{ fill: C.muted, fontSize: 11 }} axisLine={false} tickLine={false}
                    tickFormatter={v => v === 0 ? '' : '$' + (v >= 1000 ? Math.round(v/1000)+'k' : v)} width={44} />
                  <YAxis yAxisId="hrs" orientation="right" tick={{ fill: C.muted, fontSize: 11 }} axisLine={false} tickLine={false}
                    tickFormatter={v => v === 0 ? '' : v+'h'} width={36} />
                  <Tooltip
                    contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '12px' }}
                    labelStyle={{ color: C.textBright, fontWeight: '600', marginBottom: '4px' }}
                    formatter={(value, name) => name === 'earnings' ? ['$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'Earnings'] : [value + 'h', 'Hours']}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px', color: C.muted }} formatter={v => v === 'earnings' ? 'Earnings' : 'Hours'} />
                  <Bar yAxisId="earn" dataKey="earnings" fill={C.success} radius={[3,3,0,0]} maxBarSize={48} />
                  <Line yAxisId="hrs" dataKey="hours" stroke={C.accent} strokeWidth={2} dot={{ fill: C.accent, r: 3 }} activeDot={{ r: 5 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Per-org cards */}
          {showOrgs.map(org => (
            <div key={org.orgId} data-tour="business-orgs" style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
              {/* Org header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={{ fontSize: '15px', fontWeight: '700', color: C.textBright }}>{org.orgName}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {org.orgStatement && (
                    <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '4px', background: 'rgba(255,255,255,0.06)', color: stColors[org.orgStatement.status] || C.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Invoice {org.orgStatement.status}
                    </span>
                  )}
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', color: C.muted }}>Hours</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: C.textBright }}>{fmtHrs(org.totalHrs)}</div>
                  </div>
                  {org.totalEarnings > 0 && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '11px', color: C.muted }}>Earnings</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: C.success }}>${org.totalEarnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Client breakdown table */}
              {org.clientRows.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr>
                      {['Client','Hours','Earnings'].map(h => (
                        <th key={h} style={{ padding: '6px 0', textAlign: h === 'Client' ? 'left' : 'right', fontSize: '10px', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {org.clientRows.map(row => (
                      <tr key={row.cid || 'none'}>
                        <td style={{ padding: '8px 0', color: C.textBright, borderBottom: `1px solid ${C.border}` }}>{row.name}</td>
                        <td style={{ padding: '8px 0', textAlign: 'right', color: C.text, borderBottom: `1px solid ${C.border}` }}>{fmtHrs(row.hrs)}</td>
                        <td style={{ padding: '8px 0', textAlign: 'right', color: row.earn?.lineTotal > 0 ? C.success : C.muted, fontWeight: row.earn?.lineTotal > 0 ? '600' : '400', borderBottom: `1px solid ${C.border}` }}>
                          {row.earn ? fmt$(row.earn.lineTotal) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ fontSize: '13px', color: C.muted }}>No time entries for {periodLabel}.</div>
              )}
            </div>
          ))}

          {/* Recent entries */}
          {entries.length > 0 && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px', marginTop: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: C.textBright, marginBottom: '12px' }}>Recent Entries</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr>
                      {['Date','Org','Client','Description','Hours'].map(h => (
                        <th key={h} style={{ padding: '6px 0', textAlign: h === 'Hours' ? 'right' : 'left', fontSize: '10px', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap', paddingRight: h !== 'Hours' ? '16px' : '0' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.slice(0, 25).map(e => {
                      const orgName = allOrgs.find(m => m.org_id === e.org_id)?.organizations?.name || '';
                      return (
                        <tr key={e.id}>
                          <td style={{ padding: '7px 16px 7px 0', color: C.muted, whiteSpace: 'nowrap', borderBottom: `1px solid ${C.border}` }}>{e.entry_date}</td>
                          <td style={{ padding: '7px 16px 7px 0', color: C.text, whiteSpace: 'nowrap', borderBottom: `1px solid ${C.border}` }}>{orgName}</td>
                          <td style={{ padding: '7px 16px 7px 0', color: C.text, whiteSpace: 'nowrap', borderBottom: `1px solid ${C.border}` }}>{e.client_id ? clientName(e.client_id) : '—'}</td>
                          <td style={{ padding: '7px 16px 7px 0', color: C.muted, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderBottom: `1px solid ${C.border}` }}>{e.description || '—'}</td>
                          <td style={{ padding: '7px 0', textAlign: 'right', color: C.textBright, fontWeight: '600', whiteSpace: 'nowrap', borderBottom: `1px solid ${C.border}` }}>{formatExactTime(e.hours)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {entries.length > 25 && <div style={{ fontSize: '11px', color: C.muted, textAlign: 'center', paddingTop: '10px' }}>Showing 25 of {entries.length} entries</div>}
              </div>
            </div>
          )}
        </>
      )}
    </Page>
  );
}

const COMP_SEATS = ['contractor','employee_hourly','employee_salary'];

const stBadge = (s) => ({draft:'amber',submitted:'blue',approved:'green',disputed:'red',void:'',confirmed:'green'}[s] || 'amber');
const stLabel = (s) => s==='confirmed' ? 'Approved' : (s ? s.charAt(0).toUpperCase()+s.slice(1) : '');
const normalizePayType = (pt) => ({ hourly:'Hourly', flat_monthly:'Flat monthly', day_rate:'Day rate', fixed_project:'Fixed project', hybrid:'Hybrid', percent:'% of client rate' }[pt] || pt);
const normalizeBasis = (payType, basis) => {
  const b = (basis || '').toLowerCase().trim();
  if (b === 'hours')        return 'Hours';
  if (b === 'flat')         return 'Flat monthly';
  if (b === 'project')      return 'Fixed project';
  if (b === 'flat monthly') return 'Flat monthly';
  if (b === 'days')         return 'Days';
  if (b === 'hybrid')       return 'Hybrid';
  if (b === 'percent')      return '% of client rate';
  if (/^\d+ days?$/.test(b)) return b.charAt(0).toUpperCase() + b.slice(1);
  if (!b && payType === 'hourly') return 'Hours';
  return basis || '—';
};

function StatementsContractorView({
  isAdmin, recallStatement,
  profile, org, clients, assignments, monthLocks, requireAllClosed, statements, mine, period, setPeriod,
  allPeriods, periodLabel, orgName, cs, existing, previewLines, previewTotal,
  submittable, busy, toast, identityReady, ownMembership, ownOrg,
  reviewing, setReviewing, submitInvoice, confirmZero, setActiveOrg, css, C,
}) {
  const tour = useTour('statements');
  const [stmtView, setStmtView] = useState('list');
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear-1, currentYear-2, currentYear-3];
  const [earningsYear, setEarningsYear] = useState(currentYear);
  const [yearEntries, setYearEntries] = useState(null);
  const [loadingYear, setLoadingYear] = useState(false);

  const loadYear = async (yr) => {
    setLoadingYear(true);
    setYearEntries(null);
    const { data } = await supabase.from('time_entries')
      .select('id,client_id,hours,entry_date,org_id,is_payable')
      .eq('user_id', profile.id)
      .gte('entry_date', `${yr}-01-01`)
      .lte('entry_date', `${yr}-12-31`);
    setYearEntries(data || []);
    setLoadingYear(false);
  };

  useEffect(() => { if (stmtView === 'earnings') loadYear(earningsYear); }, [stmtView, earningsYear]);

  const clientName = (id) => clients.find(c=>c.id===id)?.name || 'Unknown';
  // computeEarnings needs the client row for hybrid and % pay types.
  const clientById = (id) => clients.find(c => c.id === id);

  // Build earnings table rows for the selected year
  const earningsRows = useMemo(() => {
    if (!yearEntries) return [];
    const periods = [...new Set(yearEntries.map(e => e.entry_date?.slice(0,7)))].filter(Boolean).sort().reverse();
    const rows = [];
    periods.forEach(p => {
      const periodClientIds = [...new Set(yearEntries.filter(e=>e.entry_date?.slice(0,7)===p).map(e=>e.client_id).filter(Boolean))];
      const stmt = statements.find(s => s.period===p && ['submitted','approved','confirmed'].includes(s.status));
      let periodTotal = 0;
      periodClientIds.forEach(cid => {
        const assignment = assignments.find(a => a.client_id===cid);
        if (!assignment) return;
        const periodClientEntries = yearEntries.filter(e => e.entry_date?.slice(0,7)===p && e.client_id===cid);
        // If approved/submitted statement exists, use its frozen line_items for this client
        let amount = 0;
        let basis = '';
        let payType = '';
        if (stmt && Array.isArray(stmt.line_items)) {
          const frozenLine = stmt.line_items.find(li => li.client_id === cid);
          if (frozenLine) {
            amount = parseFloat(frozenLine.line_total || 0);
            basis = frozenLine.basis || '';
            payType = frozenLine.pay_type || '';
          } else {
            // Line not in frozen statement — compute live
            const calc = computeEarnings(assignment, periodClientEntries, p, clientById(cid));
            if (!calc.appliesThisPeriod) return;
            amount = calc.lineTotal;
            basis = calc.note || calc.basis || '';
            payType = calc.payType;
          }
        } else {
          const calc = computeEarnings(assignment, periodClientEntries, p, clientById(cid));
          if (!calc.appliesThisPeriod) return;
          amount = calc.lineTotal;
          basis = calc.note || calc.basis || '';
          payType = calc.payType;
        }
        periodTotal += amount;
        rows.push({ p, cid, clientName: clientName(cid), payType, basis, amount, stmtStatus: stmt?.status || null });
      });
      // Period subtotal marker
      if (periodTotal > 0) rows.push({ p, subtotal: periodTotal, isSubtotal: true, stmtStatus: stmt?.status || null });
    });
    return rows;
  }, [yearEntries, assignments, statements, clients]);

  const grandTotal = earningsRows.filter(r=>r.isSubtotal).reduce((s,r)=>s+(r.subtotal||0),0);
  const periodsActive = [...new Set(earningsRows.filter(r=>r.isSubtotal).map(r=>r.p))].length;
  const clientsServed = [...new Set(earningsRows.filter(r=>!r.isSubtotal).map(r=>r.cid))].length;

  const exportEarningsCSV = () => {
    const dataRows = earningsRows.filter(r=>!r.isSubtotal);
    const header = 'Period,Client,Pay Type,Basis,Amount';
    const lines = dataRows.map(r => [
      r.p, `"${r.clientName.replace(/"/g,'""')}"`, normalizePayType(r.payType),
      `"${normalizeBasis(r.payType, r.basis).replace(/"/g,'""')}"`, r.amount.toFixed(2)
    ].join(','));
    lines.push(`,,,,${grandTotal.toFixed(2)}`);
    const blob = new Blob([[header, ...lines].join('\n')], {type:'text/csv'});
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `Earnings_${earningsYear}_${profile.name?.replace(/\s+/g,'_')||'contractor'}.csv`;
    a.click();
  };

  const printEarnings = () => {
    const dataRows = earningsRows.filter(r=>!r.isSubtotal);
    const today = new Date().toLocaleDateString();
    const html = `<!DOCTYPE html><html><head><title>Earnings ${earningsYear}</title>
      <style>body{font-family:Arial,sans-serif;padding:32px;color:#111}
      h1{font-size:18px;margin:0 0 4px}h2{font-size:14px;color:#555;margin:0 0 24px;font-weight:normal}
      table{width:100%;border-collapse:collapse;font-size:13px}
      th{text-align:left;border-bottom:2px solid #333;padding:6px 4px;font-weight:700}
      td{padding:5px 4px;border-bottom:1px solid #ddd}
      .subtotal td{font-weight:700;background:#f0f0f0}
      .grand td{font-weight:700;font-size:14px;border-top:2px solid #333}
      .right{text-align:right}</style></head>
      <body>
      <h1>Earnings Summary — ${earningsYear}</h1>
      <h2>${profile.name||''} · Generated ${today}</h2>
      <table>
        <thead><tr><th>Period</th><th>Client</th><th>Pay Type</th><th>Basis</th><th class="right">Amount</th></tr></thead>
        <tbody>
        ${dataRows.map(r=>`<tr><td>${r.p}</td><td>${r.clientName}</td><td>${normalizePayType(r.payType)}</td><td>${normalizeBasis(r.payType, r.basis)}</td><td class="right">$${r.amount.toFixed(2)}</td></tr>`).join('')}
        </tbody>
        <tfoot><tr class="grand"><td colspan="4">Annual Total</td><td class="right">$${grandTotal.toFixed(2)}</td></tr></tfoot>
      </table>
      </body></html>`;
    const w = window.open('','_blank'); w.document.write(html); w.document.close();
    w.onload = () => { w.focus(); w.print(); };
  };

  return (
    <Page title="My Invoices" onTour={tour.launch}
      tabs={{ tabs: [['list','Invoices'],['earnings','Earnings']], active: stmtView, onChange: setStmtView }}
      tour={{ controls: 'statements-period' }}
      controls={<Controls range={stmtView==='list' ? (
          <select aria-label="Period" style={{...css.sel,width:'auto',padding:'6px 10px',fontSize:'13px'}} value={period} onChange={e=>setPeriod(e.target.value)}>
            {allPeriods.map(p=><option key={p} value={p}>{fmtPeriod(p)}</option>)}
          </select>
        ) : (
          <select aria-label="Year" style={{...css.sel,width:'auto',padding:'6px 10px',fontSize:'13px'}} value={earningsYear} onChange={e=>setEarningsYear(parseInt(e.target.value,10))}>
            {yearOptions.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        )} />}>
      {tour.show && <WalkthroughModal tourKey="statements" onClose={tour.close} />}

      {toast && <div style={{marginBottom:'16px',padding:'12px 16px',background:'rgba(72,187,120,0.1)',border:'1px solid rgba(72,187,120,0.3)',borderRadius:'8px',color:C.success,fontSize:'13px'}}>{toast}</div>}

      {stmtView==='list' && (<>
        {/* Current period action card */}
        <div style={css.card}>
          <div style={{...css.h2,marginBottom:'10px'}}>{periodLabel} — Invoice to {orgName}</div>
          {!canSubmitContractorInvoice(org?.plan) ? (
            <div style={{padding:'14px 16px',background:`${C.badgeAmber}14`,border:`1px solid ${C.badgeAmber}4d`,borderRadius:'8px'}}>
              <div style={{fontSize:'14px',fontWeight:'700',color:C.textBright,marginBottom:'4px'}}>Upgrade to submit invoices</div>
              <div style={{fontSize:'13px',color:C.textDim}}>Contractor payout statements and automated workflows are available on Pro and Max plans.</div>
            </div>
          ) : existing && ['submitted','approved','confirmed'].includes(existing.status) ? (
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'10px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <span style={css.badge(stBadge(existing.status))}>{stLabel(existing.status)}</span>
                <span style={{color:C.muted,fontSize:'13px'}}>{existing.status==='submitted'?'Awaiting approval':'Approved'}</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <span style={{fontWeight:'700',color:C.accent,fontSize:'15px'}}>{$c(existing.total)}</span>
                <button style={css.smBtn(C.textDim)} onClick={()=>setReviewing(existing)}>View</button>
                {existing.public_token && (
                  <a href={`/cinvoice/${existing.public_token}`} target="_blank" rel="noreferrer" style={{...css.smBtn(C.textDim),textDecoration:'none',display:'inline-block'}}>Print / PDF</a>
                )}
                {!isAdmin && existing.status==='submitted' && (
                  <button style={css.smBtn(C.textDim)} disabled={busy} onClick={()=>recallStatement(existing)} title="Pull this back to make a change before your admin reviews it">Recall</button>
                )}
              </div>
            </div>
          ) : cs.assigned===0 ? (
            <div style={{color:C.muted,fontSize:'13px'}}>You have no client assignments in this workspace.</div>
          ) : (!cs.readyToSubmit || !cs.allClosed) ? (
            <div>
              <div style={{fontSize:'13px',color:C.textDim,marginBottom:'12px'}}>
                {requireAllClosed
                  ? `Close all clients for ${periodLabel} to submit your invoice. (${cs.closed}/${cs.assigned} closed)`
                  : cs.closed === 0
                    ? `Close at least one client for ${periodLabel} to submit your invoice.`
                    : `${cs.closed} of ${cs.assigned} clients closed — you can submit now or close more first.`
                }
              </div>
              {(() => {
                const assignedClientIds = [...new Set(assignments.filter(a=>a.user_id===profile.id).map(a=>a.client_id))];
                return assignedClientIds.map(cid => {
                  const isLocked = (monthLocks||[]).some(l => l.user_id===profile.id && l.client_id===cid && l.period===period && !l.reopened_at);
                  const cName = clients.find(c=>c.id===cid)?.name || 'Unknown client';
                  const optional = !requireAllClosed && !isLocked;
                  return (
                    <div key={cid} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 12px',marginBottom:'6px',background:'rgba(255,255,255,0.03)',border:`1px solid ${isLocked?'rgba(72,187,120,0.3)':'rgba(255,255,255,0.08)'}`,borderRadius:'8px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                        <span style={{fontSize:'14px'}}>{isLocked ? '✅' : '⬜'}</span>
                        <div>
                          <span style={{fontSize:'13px',color:isLocked?C.success:C.textBright}}>{cName}</span>
                          {optional && <span style={{fontSize:'11px',color:C.muted,marginLeft:'6px'}}>(not billed this period)</span>}
                        </div>
                      </div>
                      {!isLocked && (
                        <div style={{display:'flex',gap:'6px'}}>
                          <button style={{...css.smBtn(C.textDim),fontSize:'11px'}} disabled={busy} onClick={()=>confirmZero(cid)} title="Confirm you have no hours for this client this month">
                            Confirm $0
                          </button>
                          <button style={{...css.smBtn(C.accent),fontSize:'11px'}} onClick={()=>{window.location.hash='logs';}}>
                            Log Time
                          </button>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
              {!requireAllClosed && cs.readyToSubmit && (
                <div style={{marginTop:'10px',fontSize:'12px',color:C.muted}}>
                  Unclosed clients are excluded from this invoice. Close them first if you want to include them.
                </div>
              )}
            </div>
          ) : !identityReady ? (
            <div style={{padding:'14px 16px',background:'rgba(49,167,217,0.06)',border:`1px solid rgba(49,167,217,0.3)`,borderRadius:'8px'}}>
              <div style={{fontSize:'14px',fontWeight:'700',color:C.textBright,marginBottom:'4px'}}>Set up your business profile to submit invoices</div>
              <div style={{fontSize:'13px',color:C.textDim,marginBottom:'12px'}}>Your invoice is issued from your own company. Add your business name and address (and an optional payment link) in your own workspace, then come back here to submit.</div>
              {ownMembership
                ? <button style={css.btn('primary')} onClick={()=>{ localStorage.setItem('tk_setup_profile','1'); setActiveOrg(ownMembership.org_id); }}>Set up business profile →</button>
                : <div style={{fontSize:'12px',color:C.muted}}>No personal workspace found on your account. Contact your admin.</div>}
            </div>
          ) : (
            <div>
              {existing?.status==='disputed' && existing.dispute_note && (
                <div style={{padding:'10px 14px',background:'rgba(229,62,62,0.08)',border:'1px solid rgba(229,62,62,0.25)',borderRadius:'6px',marginBottom:'12px',fontSize:'13px',color:'#fc8181'}}>
                  <strong>Disputed:</strong> {existing.dispute_note} — revise your entries and resubmit.
                </div>
              )}
              {previewLines.length===0 ? <div style={{color:C.muted,fontSize:'13px',marginBottom:'12px'}}>No billable work for this period.</div> : (
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',marginBottom:'12px'}}>
                  <thead><tr><th style={css.th}>Client</th><th style={{...css.th,textAlign:'right'}}>Amount</th></tr></thead>
                  <tbody>{previewLines.map((l,i)=>(<tr key={i}><td style={css.td}>{l.pay_type==='expense' && '🧾 '}{l.pay_type==='expense' ? (l.note||l.client_name) : l.client_name}</td><td style={{...css.td,textAlign:'right',fontWeight:'600',color:C.textBright}}>{$c(l.line_total)}</td></tr>))}</tbody>
                  <tfoot><tr><td style={{...css.td,fontWeight:'700',color:C.textBright}}>Total</td><td style={{...css.td,textAlign:'right',fontWeight:'700',color:C.accent}}>{$c(previewTotal)}</td></tr></tfoot>
                </table>
              )}
              <button style={css.btn('primary')} data-tour="statements-actions" disabled={busy || !submittable} onClick={()=>submitInvoice(ownOrg)}>{busy?'Submitting…':existing?.status==='disputed'?'Revise & Resubmit':'Submit Invoice'}</button>
            </div>
          )}
        </div>
        {/* History */}
        {mine.length>0 && (
          <div style={css.card}>
            <div style={{...css.h2,marginBottom:'10px'}}>History</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
              <thead><tr><th style={css.th}>Period</th><th style={css.th}>Status</th><th style={{...css.th,textAlign:'right'}}>Total</th><th style={css.th}></th></tr></thead>
              <tbody>{mine.map(s=>(
                <tr key={s.id}>
                  <td style={css.td}>{new Date(s.period+'-02').toLocaleString('default',{month:'long',year:'numeric'})}</td>
                  <td style={css.td}><span style={css.badge(stBadge(s.status))}>{stLabel(s.status)}</span></td>
                  <td style={{...css.td,textAlign:'right',fontWeight:'600',color:C.accent}}>{$c(s.total)}</td>
                  <td style={{...css.td,textAlign:'right'}}><button style={css.smBtn(C.textDim)} onClick={()=>setReviewing(s)}>View</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {reviewing && <InvoiceModal stmt={reviewing} isAdmin={false} busy={busy} recipientName={orgName} onClose={()=>setReviewing(null)} onApprove={null} onDispute={null} />}
      </>)}

      {stmtView==='earnings' && (<>
        {/* Year totals strip */}
        {!loadingYear && yearEntries && (
          <div style={{display:'flex',gap:'12px',marginBottom:'20px',flexWrap:'wrap'}}>
            {[['Total Earned',$c(grandTotal)],['Periods Active',periodsActive],['Clients Served',clientsServed]].map(([label,val])=>(
              <div key={label} style={{flex:'1 1 140px',background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'10px',padding:'14px 16px'}}>
                <div style={{fontSize:'11px',color:C.muted,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'4px'}}>{label}</div>
                <div style={{fontSize:'20px',fontWeight:'700',color:C.textBright}}>{val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        {!loadingYear && yearEntries && earningsRows.length > 0 && (
          <div style={{display:'flex',gap:'8px',marginBottom:'16px'}}>
            <button style={css.smBtn(C.accent)} onClick={exportEarningsCSV}>Export CSV</button>
            <button style={css.smBtn(C.textDim)} onClick={printEarnings}>Print / PDF</button>
          </div>
        )}

        {loadingYear && <div style={{color:C.muted,fontSize:'13px',padding:'24px 0'}}>Loading…</div>}

        {!loadingYear && yearEntries && earningsRows.length===0 && (
          <div style={{color:C.muted,fontSize:'13px',padding:'24px 0'}}>No earnings data found for {earningsYear}.</div>
        )}

        {!loadingYear && yearEntries && earningsRows.length > 0 && (
          <div style={css.card}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
              <thead>
                <tr>
                  <th style={css.th}>Period</th>
                  <th style={css.th}>Client</th>
                  <th style={css.th}>Pay Type</th>
                  <th style={css.th}>Basis</th>
                  <th style={{...css.th,textAlign:'right'}}>Amount</th>
                  <th style={css.th}>Invoice Status</th>
                </tr>
              </thead>
              <tbody>
                {earningsRows.map((r, i) => r.isSubtotal ? (
                  <tr key={`sub-${r.p}`} style={{background:'rgba(255,255,255,0.04)'}}>
                    <td style={{...css.td,fontWeight:'700',color:C.textBright}} colSpan={4}>{new Date(r.p+'-02').toLocaleString('default',{month:'long',year:'numeric'})} — Subtotal</td>
                    <td style={{...css.td,textAlign:'right',fontWeight:'700',color:C.accent}}>{$c(r.subtotal)}</td>
                    <td style={css.td}>{r.stmtStatus ? <span style={css.badge(stBadge(r.stmtStatus))}>{stLabel(r.stmtStatus)}</span> : <span style={{color:C.muted,fontSize:'12px'}}>—</span>}</td>
                  </tr>
                ) : (
                  <tr key={`${r.p}-${r.cid}`}>
                    <td style={{...css.td,color:C.muted}}>{r.p}</td>
                    <td style={css.td}>{r.clientName}</td>
                    <td style={{...css.td,color:C.muted,fontSize:'12px'}}>{normalizePayType(r.payType)}</td>
                    <td style={{...css.td,color:C.muted,fontSize:'12px'}}>{normalizeBasis(r.payType, r.basis)}</td>
                    <td style={{...css.td,textAlign:'right',fontWeight:'600',color:C.textBright}}>{$c(r.amount)}</td>
                    <td style={css.td}></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{borderTop:`2px solid rgba(255,255,255,0.15)`}}>
                  <td style={{...css.td,fontWeight:'700',color:C.textBright}} colSpan={4}>{earningsYear} Total</td>
                  <td style={{...css.td,textAlign:'right',fontWeight:'700',color:C.accent,fontSize:'15px'}}>{$c(grandTotal)}</td>
                  <td style={css.td}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </>)}
    </Page>
  );
}

function StatementsView({ statements=[], isAdmin, profile, org, clients=[], assignments=[], entries=[], expenses=[], users=[], orgMembers=[], monthLocks=[], qboConnected=false, onRefresh }) {
  const tourKey = isAdmin ? 'statements_admin' : 'statements';
  const tour = useTour(tourKey);
  const { allOrgs=[], setActiveOrg } = useOrg();
  const allPeriods = [...new Set([curMonth(), ...statements.map(s=>s.period), ...(monthLocks||[]).map(l=>l.period)])].sort().reverse();
  const [period, setPeriod] = useState(() => {
    // For contractors: default to the most recent period where all assigned clients are locked.
    // This ensures the submit button is visible immediately without manual period navigation.
    if (!isAdmin) {
      const assignedClientIds = [...new Set((assignments||[]).filter(a=>a.user_id===profile.id).map(a=>a.client_id))];
      if (assignedClientIds.length > 0) {
        const myLockPeriods = [...new Set((monthLocks||[]).filter(l=>l.user_id===profile.id&&!l.reopened_at&&l.client_id).map(l=>l.period))].sort().reverse();
        for (const p of myLockPeriods) {
          const lockedCids = new Set((monthLocks||[]).filter(l=>l.user_id===profile.id&&l.period===p&&!l.reopened_at&&l.client_id).map(l=>l.client_id));
          if (assignedClientIds.every(cid => lockedCids.has(cid))) return p;
        }
      }
    }
    return allPeriods[0] || curMonth();
  });
  const [rangeMode, setRangeMode] = useState(isAdmin ? 'all' : 'month');
  const [miStatus, setMiStatus] = useState('review');
  const [miSearch, setMiSearch] = useState('');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');
  const [reviewing, setReviewing] = useState(null);
  const [loggingPayment, setLoggingPayment] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const showToast = (m) => { setToast(m); setTimeout(()=>setToast(''), 4000); };
  const periodLabel = new Date(period+'-02').toLocaleString('default',{month:'long',year:'numeric'});

  const fiscalStart = org?.fiscal_year_start || 1;
  const now = new Date();
  const ytdStart = (() => {
    const yr = now.getMonth() + 1 >= fiscalStart ? now.getFullYear() : now.getFullYear() - 1;
    return `${yr}-${String(fiscalStart).padStart(2,'0')}-01`;
  })();
  const ytdEnd = now.toISOString().slice(0,10);

  const rangeLabel = (() => {
    if (rangeMode === 'month')  return periodLabel;
    if (rangeMode === 'ytd')    return `YTD (since ${new Date(ytdStart+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})})`;
    if (rangeMode === 'custom' && customStart && customEnd)
      return `${new Date(customStart+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})} – ${new Date(customEnd+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}`;
    return 'Custom Range';
  })();

  // In range modes, filter statements to the date range (by period string comparison)
  const statementsInRange = (() => {
    if (rangeMode === 'month') return statements.filter(s => s.period === period);
    if (rangeMode === 'ytd')   return statements.filter(s => s.period >= ytdStart.slice(0,7) && s.period <= ytdEnd.slice(0,7));
    if (rangeMode === 'custom' && customStart && customEnd)
      return statements.filter(s => s.period >= customStart.slice(0,7) && s.period <= customEnd.slice(0,7));
    return [];
  })();
  const clientName = (id) => clients.find(c=>c.id===id)?.name || 'Unknown client';

  // Frozen line items for a contractor+period via the shared earnings module.
  const requireAllClosed = org?.invoice_require_all_clients_closed !== false;

  const buildLines = (userId) => {
    const lines = [];
    const closedClientIds = new Set(
      (monthLocks||[]).filter(l => l.user_id===userId && l.period===period && !l.reopened_at && l.client_id).map(l => l.client_id)
    );
    assignments.filter(a => a.user_id===userId).forEach(a => {
      // When require-all is OFF, only invoice clients whose month is closed.
      if (!requireAllClosed && !closedClientIds.has(a.client_id)) return;
      const ce = entries.filter(e => e.user_id===userId && e.client_id===a.client_id && e.entry_date?.slice(0,7)===period);
      const calc = computeEarnings(a, ce, period, clients.find(c=>c.id===a.client_id));
      if (!calc.appliesThisPeriod) return;
      // Only say "not paid" when it actually changed this line (flat pay doesn't count hours).
      const note = calc.unpaidCount > 0 && calc.unpaidPayEffect > 0
        ? `${formatExactTime(calc.unpaidHours)} not paid (${calc.unpaidCount} ${calc.unpaidCount===1?'entry':'entries'})`
        : null;
      lines.push({ client_id:a.client_id, client_name:clientName(a.client_id), pay_type:calc.payType, basis:calc.basis, quantity:calc.quantity, rate_or_amount:calc.rateOrAmount, line_total:calc.lineTotal, ...(note ? { unpaid_note: note } : {}) });
    });
    lines.sort((x,y)=>x.client_name.localeCompare(y.client_name));
    return lines;
  };
  const closeState = (userId) => {
    const assigned = [...new Set(assignments.filter(a=>a.user_id===userId).map(a=>a.client_id))];
    const closed = assigned.filter(cid => (monthLocks||[]).some(l => l.user_id===userId && l.client_id===cid && l.period===period && !l.reopened_at));
    const allClosed = assigned.length>0 && closed.length===assigned.length;
    const readyToSubmit = requireAllClosed ? allClosed : closed.length>0;
    return { assigned: assigned.length, closed: closed.length, allClosed, readyToSubmit };
  };
  const stmtFor = (userId) => statements.find(s => s.user_id===userId && s.period===period);
  const orgName = org?.name || 'the firm';

  // Reimbursable expenses belonging to this contractor, approved and not yet
  // claimed by any statement (or already sitting on the one being rebuilt).
  // Always at cost -- markup_percent is a client-billing concept and plays no
  // part here, so it is never read.
  const reimbursableExpensesFor = (userId, existingStmt) => expenses.filter(e =>
    e.user_id === userId &&
    e.status === 'approved' &&
    e.is_reimbursable &&
    (!e.reimbursed_statement_id || e.reimbursed_statement_id === existingStmt?.id)
  );

  const expenseLineFor = (e) => ({
    client_id: e.client_id || null,
    client_name: e.client_id ? clientName(e.client_id) : 'Internal / overhead',
    pay_type: 'expense',
    basis: 'reimbursement',
    quantity: null,
    rate_or_amount: null,
    line_total: Number(e.amount || 0),
    note: [e.vendor, e.description].filter(Boolean).join(' — ') || null,
    expense_id: e.id,          // lets the detail view and future tooling trace the line back
    expense_date: e.expense_date,
    // Snapshotted so the admin can open the receipt straight from the review
    // modal without a second query. The underlying expense row is immutable
    // once approved, so this stays accurate for the life of the statement.
    receipt_path: e.receipt_path || null,
    receipt_mime: e.receipt_mime || null,
  });

  // Contractor submits their own invoice (issuer = their own org identity).
  const submitInvoice = async (ownOrg) => {
    setBusy(true);
    const timeLines = buildLines(profile.id);
    const existingStmt = stmtFor(profile.id);
    const myExpenses = reimbursableExpensesFor(profile.id, existingStmt);
    const lines = [...timeLines, ...myExpenses.map(expenseLineFor)];
    const total = lines.reduce((s,l)=>s+parseFloat(l.line_total||0),0);
    const snap = { name: ownOrg.name, address_line1: ownOrg.address_line1, address_line2: ownOrg.address_line2, city: ownOrg.city, state: ownOrg.state, zip: ownOrg.zip, country: ownOrg.country, payment_link: ownOrg.invoice_payment_link, logo_url: ownOrg.logo_url || null, invoice_number_prefix: ownOrg.invoice_number_prefix || null };
    const now = new Date().toISOString();
    const { data: stmtRow, error: stmtErr } = await supabase.from('contractor_statements').upsert({
      org_id: org.id, user_id: profile.id, contractor_org_id: ownOrg.id, period, status:'submitted',
      line_items: lines, total, issuer_snapshot: snap, submitted_at: now, generated_at: now,
      approved_at:null, disputed_at:null, dispute_note:null, confirmed_at:null,
    }, { onConflict:'org_id,user_id,period' }).select('id').single();
    if (stmtErr) { notifyError(`Submitting invoice: ${friendlyDbError(stmtErr)}.`); setBusy(false); return; }

    // Claim after the statement row exists, same reasoning as invoices: the
    // FK target has to be real before it can be pointed at. Compare-and-swap
    // inside the RPC, so a race against another submission just returns fewer
    // ids rather than double-claiming.
    if (myExpenses.length) {
      const { data: claimed, error: claimErr } = await supabase.rpc('claim_expenses_for_statement', {
        p_statement_id: stmtRow.id, p_ids: myExpenses.map(e=>e.id),
      });
      if (claimErr) notifyError(`Invoice submitted, but attaching expenses failed: ${friendlyDbError(claimErr)}.`);
      else if ((claimed?.length ?? 0) < myExpenses.length) {
        notifyError('Some expenses were claimed elsewhere first and were left off this invoice.');
      }
    }
    setBusy(false);
    showToast('Invoice submitted for approval.'); onRefresh();
    supabase.functions.invoke('notify-statement-submitted', { body: { statement_id: stmtRow.id } }).catch(console.error);
  };

  // Safety net for both the manual Submit flow and auto-submit-statement:
  // pulls a still-pending (not yet approved) statement back to draft so a
  // mistake can be fixed before the admin ever sees it. Server-side RPC
  // re-checks ownership and status -- this isn't just a UI-level gate.
  const recallStatement = async (stmt) => {
    setBusy(true);
    const { error } = await supabase.rpc('recall_own_statement', { p_statement_id: stmt.id });
    setBusy(false);
    if (error) { notifyError(`Recalling invoice failed: ${friendlyDbError(error)}.`); return; }
    showToast('Invoice recalled — make your changes, then submit again when ready.');
    onRefresh();
  };

  // Pushes an approved member invoice to QuickBooks as a Bill (qbo-sync-contractor-bill).
  // Safe to repeat: a statement already linked to a Bill updates that Bill instead.
  const [qboSendingId, setQboSendingId] = useState(null);
  const sendBillToQbo = async (stmt, { afterApprove = false } = {}) => {
    setQboSendingId(stmt.id);
    const { data, error } = await supabase.functions.invoke('qbo-sync-contractor-bill', { body: { org_id: org.id, statement_id: stmt.id } });
    setQboSendingId(null);
    if (error || !data?.ok) {
      let reason = data?.error || error?.message || 'unknown error';
      try { const j = await error?.context?.json?.(); if (j?.error) reason = j.error; } catch (_) {}
      notifyError(`${afterApprove ? 'Invoice approved, but sending' : 'Sending'} it to QuickBooks failed: ${reason}. Use "Send to QuickBooks" on the invoice to try again.`);
      return false;
    }
    if (data.skipped) return false;
    if (!afterApprove) showToast('Sent to QuickBooks as a bill.');
    onRefresh();
    return true;
  };

  const approveInvoice = async (stmt, note) => {
    setBusy(true);
    const ok = await runDb('Approving invoice failed', supabase.from('contractor_statements')
      .update({ status:'approved', approved_at:new Date().toISOString(), approval_note:note||null }).eq('id', stmt.id));
    if (!ok) { setBusy(false); return; }
    // QuickBooks Bill push, not awaited (approval must not wait on Intuit). A
    // failure used to be console-only, so an approved invoice could silently never
    // reach QuickBooks; it now says so, and "Send to QuickBooks" retries it.
    if (qboConnected && stmt.user_id) sendBillToQbo(stmt, { afterApprove: true });
    // Email the invoice to the configured AP recipient(s).
    // PDF is attached as the single document of record for AP tools (Relay, QBO).
    // The email body is a minimal notification with no dollar amounts so AP tools
    // cannot parse amounts from two sources and create duplicate payables.
    let msg = 'Invoice approved.';
    try {
      let pdf_base64 = null;
      let pdfErr = null;
      try { pdf_base64 = await buildContractorInvoicePdfBase64Async(stmt, orgName); } catch (e) { pdfErr = e?.message || String(e); }
      const { data, error } = await supabase.functions.invoke('send-contractor-invoice', { body: { statement_id: stmt.id, pdf_base64 } });
      if (error || !data?.ok) {
        let reason = data?.error || error?.message || 'unknown error';
        try { const j = await error?.context?.json?.(); if (j?.error) reason = j.error; } catch (_) {}
        msg = reason.includes('no_recipients')
          ? 'Invoice approved. Set AP recipient email(s) in Settings → Billing → Invoices (or per member in Team) to email it.'
          : `Invoice approved, but emailing failed: ${reason}`;
      } else {
        msg = `Invoice approved and emailed to ${(data.sent_to||[]).join(', ')}.`;
        if (pdfErr) msg += ` (PDF skipped: ${pdfErr})`;
        else if (!pdf_base64) msg += ' (PDF skipped: generation returned empty)';
      }
    } catch (e) { msg = `Invoice approved, but emailing failed: ${e?.message||e}`; }
    setBusy(false);
    showToast(msg); setReviewing(null); onRefresh();
  };

  const disputeInvoice = async (stmt, note) => {
    if (!note?.trim()) return;
    setBusy(true);
    const ok = await runDb('Disputing invoice failed', supabase.from('contractor_statements')
      .update({ status:'disputed', disputed_at:new Date().toISOString(), dispute_note:note.trim() }).eq('id', stmt.id));
    if (ok) {
      // Reopen the contractor's closed clients for this period so they can fix and resubmit.
      const closedLocks = (monthLocks||[]).filter(l => l.user_id===stmt.user_id && l.period===stmt.period && !l.reopened_at && l.client_id);
      for (const l of closedLocks) {
        await supabase.rpc('reopen_client_month', { p_org: org.id, p_user: stmt.user_id, p_client: l.client_id, p_period: stmt.period });
      }
    }
    setBusy(false);
    if (ok) { showToast('Invoice disputed and the month reopened for the member.'); setReviewing(null); onRefresh(); }
  };

  // Void a write-in payment (a contractor_statements row with no user_id,
  // logged directly by an admin for a payee outside TimeKeep entirely).
  // There's no contractor account or month_locks entry to reopen -- unlike
  // voidInvoice, this is just a correction to a manually-entered record.
  const voidWriteIn = async (stmt) => {
    if (!window.confirm(`Void this write-in payment to ${stmt.payee_name}?`)) return;
    setBusy(true);
    const ok = await runDb('Voiding payment failed', supabase.from('contractor_statements')
      .update({ status:'void' }).eq('id', stmt.id));
    setBusy(false);
    if (ok) { showToast('Write-in payment voided.'); setReviewing(null); onRefresh(); }
  };

  // Void an invoice (undo a mistaken approval, or reset for testing). The
  // contractor can then submit a fresh one without reopening each client.
  const voidInvoice = async (stmt) => {
    if (!window.confirm('Void this invoice? This will unlock the member\'s entries for the period so they can make corrections and resubmit.')) return;
    setBusy(true);
    const ok = await runDb('Voiding invoice failed', supabase.from('contractor_statements')
      .update({ status:'void' }).eq('id', stmt.id));
    if (ok) {
      // Reopen all locked clients for this member+period so they can correct and resubmit.
      const closedLocks = (monthLocks||[]).filter(l => l.user_id===stmt.user_id && l.period===stmt.period && !l.reopened_at && l.client_id);
      for (const l of closedLocks) {
        await supabase.rpc('reopen_client_month', { p_org: org.id, p_user: stmt.user_id, p_client: l.client_id, p_period: stmt.period });
      }
    }
    setBusy(false);
    if (ok) { showToast('Invoice voided and period unlocked. The member can correct entries and resubmit.'); setReviewing(null); onRefresh(); }
  };

  // ── Contractor (member) view: issue your own invoice ──
  if (!isAdmin) {
    const ownMembership = allOrgs.find(m => m.role==='owner');
    const ownOrg = ownMembership?.organizations || null;
    const identityReady = !!(ownOrg && ownOrg.name && ownOrg.address_line1);
    const cs = closeState(profile.id);
    const existing = stmtFor(profile.id);
    const previewExpenses = reimbursableExpensesFor(profile.id, existing);
    const previewLines = [...buildLines(profile.id), ...previewExpenses.map(expenseLineFor)];
    const previewTotal = previewLines.reduce((s,l)=>s+parseFloat(l.line_total||0),0);
    const mine = statements.filter(s => s.user_id===profile.id).sort((a,b)=>b.period.localeCompare(a.period));
    const submittable = cs.readyToSubmit && (!existing || ['draft','disputed','void'].includes(existing.status));

    const confirmZero = async (clientId) => {
      setBusy(true);
      const ok = await runDb('Failed to confirm $0', supabase.rpc('close_client_month',
        { p_org: org.id, p_user: profile.id, p_client: clientId, p_period: period }));
      setBusy(false);
      if (ok) { supabase.functions.invoke('check-all-locked', { body: { org_id: org.id, period } }).catch(console.error); onRefresh(); }
    };

    return (
      <StatementsContractorView
        isAdmin={isAdmin} recallStatement={recallStatement}
        profile={profile} org={org} clients={clients} assignments={assignments}
        monthLocks={monthLocks} requireAllClosed={requireAllClosed}
        statements={statements} mine={mine} period={period} setPeriod={setPeriod}
        allPeriods={allPeriods} periodLabel={periodLabel} orgName={orgName}
        cs={cs} existing={existing} previewLines={previewLines} previewTotal={previewTotal}
        submittable={submittable} busy={busy} toast={toast}
        identityReady={identityReady} ownMembership={ownMembership} ownOrg={ownOrg}
        reviewing={reviewing} setReviewing={setReviewing}
        submitInvoice={submitInvoice} confirmZero={confirmZero} setActiveOrg={setActiveOrg}
        css={css} C={C}
      />
    );
  }

  // ── Admin view: approve / dispute member invoices ──
  // Page System decision 5: one list across all periods, opening on what needs
  // review; the date range narrows it instead of being the starting point.
  const miMonthsInRange = (() => {
    if (rangeMode === 'month') return [period];
    if (rangeMode === 'ytd') return allPeriods.filter(p => p >= ytdStart.slice(0,7) && p <= ytdEnd.slice(0,7));
    if (rangeMode === 'custom' && customStart && customEnd) return allPeriods.filter(p => p >= customStart.slice(0,7) && p <= customEnd.slice(0,7));
    return null; // all time
  })();
  const inMiRange = (p) => !miMonthsInRange || miMonthsInRange.includes(p);
  const closeStateFor = (userId, p) => {
    const assigned = [...new Set(assignments.filter(a=>a.user_id===userId).map(a=>a.client_id))];
    const closed = assigned.filter(cid => (monthLocks||[]).some(l => l.user_id===userId && l.client_id===cid && l.period===p && !l.reopened_at));
    return { assigned: assigned.length, closed: closed.length };
  };
  const memberName = (s) => s.user_id ? (users.find(u=>u.id===s.user_id)?.name || 'Former member') : `${s.payee_name || 'Payee'} (write-in)`;
  // Every invoice in range, plus a "Not submitted" row for each pay-tracked member
  // with work in a period but no invoice. Without a range those rows are limited to
  // this month and last month, so years of history don't fill the list.
  const recentPeriods = [curMonth(), (() => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth()-1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })()];
  const notSubmittedPeriods = (miMonthsInRange || recentPeriods).filter(p => p <= curMonth());
  const miRows = [
    ...statements.filter(s => inMiRange(s.period)).map(s => ({
      key: s.id, stmt: s, name: memberName(s), period: s.period,
      status: s.status === 'confirmed' ? 'approved' : s.status,
      total: parseFloat(s.total || 0),
      closed: s.user_id ? closeStateFor(s.user_id, s.period) : null,
    })),
    ...orgMembers.filter(m => COMP_SEATS.includes(m.seat_type) && !m.ended_at).flatMap(m => notSubmittedPeriods
      .filter(p => entries.some(e => e.user_id === m.user_id && e.entry_date?.slice(0,7) === p))
      .filter(p => !statements.some(s => s.user_id === m.user_id && s.period === p && s.status !== 'void'))
      .map(p => ({ key: `${m.user_id}|${p}`, stmt: null, name: users.find(u=>u.id===m.user_id)?.name || 'Member', period: p,
        status: 'not_submitted', total: null, closed: closeStateFor(m.user_id, p) }))),
  ];
  const MI_STATUS = {
    submitted:     { label: 'Needs review',  tone: 'warn' },
    disputed:      { label: 'Disputed',      tone: 'danger' },
    approved:      { label: 'Approved',      tone: 'good' },
    draft:         { label: 'Draft',         tone: 'neutral' },
    void:          { label: 'Void',          tone: 'neutral' },
    not_submitted: { label: 'Not submitted', tone: 'neutral' },
  };
  const miSets = {
    review: miRows.filter(r => r.status === 'submitted'),
    disputed: miRows.filter(r => r.status === 'disputed'),
    approved: miRows.filter(r => r.status === 'approved'),
    not_submitted: miRows.filter(r => r.status === 'not_submitted'),
    all: miRows,
  };
  const miSum = (list) => list.reduce((s, r) => s + (r.total || 0), 0);
  const miShown = (miSets[miStatus] || miRows).filter(r => !miSearch.trim() || r.name.toLowerCase().includes(miSearch.trim().toLowerCase()))
    .sort((a, b) => b.period.localeCompare(a.period) || a.name.localeCompare(b.name));

  return (
    <Page title="Member Invoices" onTour={tour.launch}
      actions={<button style={css.btn('secondary')} onClick={()=>setLoggingPayment(true)} title="For a payment made outside TimeKeep to a payee with no account here. Recorded as already approved, so it counts toward member pay and client margin right away.">Log payment</button>}
      tour={{ controls: 'statements-controls', stats: 'statements-stats' }}
      controls={<Controls
        pills={<StatusPills value={miStatus} onChange={setMiStatus} options={[
          { key:'review',        label:'Needs review',  count: miSets.review.length,        total: fmtMoney(miSum(miSets.review)) },
          { key:'disputed',      label:'Disputed',      count: miSets.disputed.length,      total: fmtMoney(miSum(miSets.disputed)), tone:'danger' },
          { key:'approved',      label:'Approved',      count: miSets.approved.length,      total: fmtMoney(miSum(miSets.approved)) },
          { key:'not_submitted', label:'Not submitted', count: miSets.not_submitted.length },
          { key:'all',           label:'All',           count: miSets.all.length },
        ]} />}
        search={<SearchInput value={miSearch} onChange={setMiSearch} placeholder="Search members…" />}
        range={<>
          <SegmentedControl options={[['all','All time'],['month','Month'],['ytd','YTD'],['custom','Custom']]} value={rangeMode} onChange={setRangeMode} />
          {rangeMode==='month' && (
            <select aria-label="Month" style={{...css.sel,width:'auto',padding:'6px 10px',fontSize:'13px'}} value={period} onChange={e=>setPeriod(e.target.value)}>
              {allPeriods.map(p=><option key={p} value={p}>{fmtPeriod(p)}</option>)}
            </select>
          )}
          {rangeMode==='custom' && (
            <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
              <input type="date" aria-label="From" style={{...css.inp,padding:'4px 8px',fontSize:'12px',width:'136px'}} value={customStart} onChange={e=>setCustomStart(e.target.value)} />
              <span style={{color:C.muted,fontSize:'12px'}}>to</span>
              <input type="date" aria-label="To" style={{...css.inp,padding:'4px 8px',fontSize:'12px',width:'136px'}} value={customEnd} onChange={e=>setCustomEnd(e.target.value)} />
            </div>
          )}
        </>} />}
      stats={[
        { label:'Waiting for review', value: fmtMoney(miSum(miSets.review)), basis: `${miSets.review.length} invoice${miSets.review.length===1?'':'s'}`,
          tone: miSets.review.length ? 'warn' : undefined, tip:'Submitted member invoices waiting for you to approve or dispute.' },
        { label:'Approved', value: fmtMoney(miSum(miSets.approved)), basis: rangeMode==='all' ? 'all time' : rangeLabel,
          tip:'Approved member invoices, including write-in payments. This is the approved part of member pay on the Overview.' },
        { label:'Not submitted', value: String(miSets.not_submitted.length), basis: rangeMode==='all' ? 'this month and last' : 'in range',
          tip:'Members who logged work in a period but haven\'t submitted an invoice for it yet. Until they do, their pay is estimated on the Overview.' },
      ]}>
      {tour.show && <WalkthroughModal tourKey="statements_admin" onClose={tour.close} />}
      {toast && <div style={{padding:'12px 16px',background:`${C.success}1a`,border:`1px solid ${C.success}4d`,borderRadius:'8px',color:C.success,fontSize:'13px'}}>{toast}</div>}

      <div data-tour="statements-list">
        <DataTable rows={miShown} rowKey={r => r.key}
          onRowClick={r => r.stmt && setReviewing(r.stmt)}
          columns={[
            { key:'name', label:'Member', sortValue: r => r.name },
            { key:'period', label:'Period', sortValue: r => r.period, render: r => fmtPeriod(r.period, { short: true }) },
            { key:'closed', label:'Clients closed', tip:'Clients this member has closed for the period, out of the clients they\'re assigned to. When your org requires all clients closed, the invoice can only be submitted once they all are.',
              render: r => r.closed ? (r.closed.assigned === 0 ? '—' : `${r.closed.closed} / ${r.closed.assigned}`) : '—' },
            { key:'status', label:'Status', render: r => <StatusBadge tone={(MI_STATUS[r.status]||{}).tone}>{(MI_STATUS[r.status]||{}).label || r.status}</StatusBadge> },
            { key:'total', label:'Total', align:'right', sortValue: r => r.total ?? -1, render: r => r.total == null ? '—' : <span style={{color:C.textBright,fontWeight:600}}>{fmtMoney(r.total)}</span> },
          ]}
          rowActions={r => !r.stmt ? [] : [
            { label: r.status === 'submitted' ? 'Review' : 'View', onClick: () => setReviewing(r.stmt) },
            ...(qboConnected && r.stmt.user_id && r.stmt.status === 'approved' ? [{ label: qboSendingId === r.stmt.id ? 'Sending…' : (r.stmt.qbo_bill_id ? 'Update in QuickBooks' : 'Send to QuickBooks'), disabled: qboSendingId === r.stmt.id, onClick: () => sendBillToQbo(r.stmt) }] : []),
            ...(r.stmt.user_id && ['submitted','approved'].includes(r.stmt.status) ? [{ label:'Void', danger:true, onClick: () => voidInvoice(r.stmt) }] : []),
            ...(!r.stmt.user_id && r.stmt.status !== 'void' ? [{ label:'Void payment', danger:true, onClick: () => voidWriteIn(r.stmt) }] : []),
          ]}
          empty={<EmptyState
            title={miStatus === 'review' ? 'Nothing waiting for review' : 'No member invoices here'}
            body={miStatus === 'review' ? 'Submitted member invoices show up here for you to approve or dispute.' : 'Try another status or date range.'}
            action={miStatus !== 'all' ? <button style={css.btn('ghost')} onClick={()=>setMiStatus('all')}>Show all</button> : null} />} />
      </div>

      {reviewing && <InvoiceModal stmt={reviewing} isAdmin={isAdmin} busy={busy} recipientName={orgName} onClose={()=>setReviewing(null)} onApprove={(note)=>approveInvoice(reviewing, note)} onDispute={(note)=>disputeInvoice(reviewing, note)} />}
      {loggingPayment && <LogPaymentModal org={org} clients={clients} defaultPeriod={period} onClose={()=>setLoggingPayment(false)} onSaved={()=>{setLoggingPayment(false); showToast('Payment logged.'); onRefresh();}} />}
    </Page>
  );
}

// Line items on a contractor statement are already-aggregated totals per
// client (hours × rate, or a flat/day-rate amount) — they don't carry the
// individual entries behind them. This pulls the raw time_entries for the
// statement's user+period and groups them by client with a subtotal header,
// so an admin reviewing an invoice can see exactly what makes up the hours
// without leaving the review flow.
function TimesheetReviewModal({ stmt, clientNames, onClose }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let canceled = false;
    (async () => {
      const [year, month] = stmt.period.split('-').map(Number);
      const start = `${stmt.period}-01`;
      const nextMonth = month === 12 ? `${year+1}-01-01` : `${year}-${String(month+1).padStart(2,'0')}-01`;
      const { data, error } = await supabase
        .from('time_entries')
        .select('id, client_id, entry_date, description, hours, is_billable, is_payable, payable_note, payable_changed_by, payable_changed_at, task_types(name), time_entry_tags(tags(name,color))')
        .eq('org_id', stmt.org_id)
        .eq('user_id', stmt.user_id)
        .gte('entry_date', start)
        .lt('entry_date', nextMonth)
        .order('entry_date');
      if (canceled) return;
      if (error) { notifyError('Loading timesheet failed: ' + error.message); setLoading(false); return; }
      setRows(data || []);
      setLoading(false);
    })();
    return () => { canceled = true; };
  }, [stmt.org_id, stmt.user_id, stmt.period]);

  // Group by client, in the same order clients appear on the invoice.
  const byClient = {};
  rows.forEach(r => { (byClient[r.client_id] ||= []).push(r); });
  const clientIds = Object.keys(byClient).sort((a,b) =>
    (clientNames[a] || '').localeCompare(clientNames[b] || ''));

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200,padding:'20px'}}>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'12px',width:'min(760px,96vw)',maxHeight:'90vh',display:'flex',flexDirection:'column'}}>
        <div style={{padding:'18px 22px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:'15px',fontWeight:'700',color:C.textBright}}>Timesheet — {new Date(stmt.period+'-02').toLocaleString('default',{month:'long',year:'numeric'})}</div>
          <button onClick={onClose} style={{background:'none',border:'none',color:C.muted,fontSize:'20px',cursor:'pointer',lineHeight:1}}>&times;</button>
        </div>
        <div style={{padding:'18px 22px',overflowY:'auto'}}>
          {loading ? (
            <div style={{color:C.muted,fontSize:'13px',textAlign:'center',padding:'20px'}}>Loading…</div>
          ) : clientIds.length === 0 ? (
            <div style={{color:C.muted,fontSize:'13px',textAlign:'center',padding:'20px'}}>No time entries found for this period.</div>
          ) : clientIds.map(cid => {
            const clientRows = byClient[cid];
            const subtotal = clientRows.reduce((s,r)=>s+parseFloat(r.hours||0),0);
            return (
              <div key={cid} style={{marginBottom:'18px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',padding:'6px 0',borderBottom:`2px solid ${C.border}`,marginBottom:'6px'}}>
                  <div style={{fontSize:'13px',fontWeight:'700',color:C.textBright}}>{clientNames[cid] || 'Unknown client'}</div>
                  <div style={{fontSize:'12px',fontWeight:'600',color:C.accent}}>{formatExactTime(subtotal)}</div>
                </div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead><tr>
                    <th style={{...css.th,width:'90px'}}>Date</th>
                    <th style={css.th}>Description</th>
                    <th style={{...css.th,width:'130px'}}>Task Type</th>
                    <th style={{...css.th,width:'110px'}}>Tags</th>
                    <th style={{...css.th,textAlign:'right',width:'70px'}}>Hours</th>
                  </tr></thead>
                  <tbody>{clientRows.map(r => (
                    <tr key={r.id}>
                      <td style={{...css.td,color:C.muted}}>{r.entry_date}</td>
                      <td style={css.td}>{r.description || <span style={{color:C.muted,fontStyle:'italic'}}>—</span>}</td>
                      <td style={{...css.td,color:C.muted}}>{r.task_types?.name || <span style={{fontStyle:'italic'}}>None</span>}</td>
                      <td style={css.td}>
                        {(r.time_entry_tags||[]).length === 0 ? <span style={{color:C.muted,fontStyle:'italic'}}>—</span> :
                          (r.time_entry_tags||[]).map((t,i) => (
                            <span key={i} style={{fontSize:'10px',padding:'2px 6px',borderRadius:'4px',background:(t.tags?.color||C.border)+'33',color:t.tags?.color||C.muted,marginRight:'4px',whiteSpace:'nowrap'}}>{t.tags?.name}</span>
                          ))}
                      </td>
                      <td style={{...css.td,textAlign:'right',fontWeight:'600',color:C.textBright}}>{formatExactTime(r.hours)}<EntryBadges entry={{...r, user_id: stmt.user_id}} ownerName={null} viewerIsOwner={false} /></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            );
          })}
        </div>
        <div style={{padding:'14px 22px',borderTop:`1px solid ${C.border}`,display:'flex',justifyContent:'flex-end'}}>
          <button style={css.btn()} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function InvoiceModal({ stmt, isAdmin, busy, recipientName, onClose, onApprove, onDispute }) {
  const [approveNote, setApproveNote] = useState('');
  const [disputeNote, setDisputeNote] = useState('');
  const [mode, setMode] = useState(null); // null | 'approve' | 'dispute'
  const [receiptUrls, setReceiptUrls] = useState({}); // expense_id -> signed url | 'loading' | 'error'
  const [showTimesheet, setShowTimesheet] = useState(false);
  const lines = stmt.line_items || [];
  const total = lines.reduce((s,l)=>s+parseFloat(l.line_total||0),0);
  const iss = stmt.issuer_snapshot || {};
  const baseBasisLabel = (l) => l.basis==='hours' ? `${parseFloat(l.quantity).toFixed(2)}h × ${$c(l.rate_or_amount)}` : l.basis==='days' ? `${l.quantity} day${l.quantity===1?'':'s'} × ${$c(l.rate_or_amount)}` : l.basis==='project' ? 'fixed project' : l.basis==='reimbursement' ? (l.note||'Expense reimbursement') : l.basis==='writein' ? (l.note || 'Write-in payment') : (l.basis==='hybrid' || l.basis==='percent') ? (l.note || (l.basis==='hybrid' ? 'hybrid' : '% of client rate')) : 'flat monthly';
  const basisLabel = (l) => l.unpaid_note ? `${baseBasisLabel(l)} · ${l.unpaid_note}` : baseBasisLabel(l);
  const canDecide = isAdmin && stmt.status==='submitted' && onApprove && onDispute;

  // Signed URLs are minted on demand, not up front — most statements have no
  // expense lines, and the ones that do rarely need every receipt opened at
  // once. 5-minute expiry matches the receipt-preview pattern used elsewhere.
  const viewReceipt = async (l) => {
    if (!l.receipt_path) return;
    setReceiptUrls(prev => ({ ...prev, [l.expense_id]: 'loading' }));
    const url = await signedReceiptUrl(l.receipt_path);
    setReceiptUrls(prev => ({ ...prev, [l.expense_id]: url || 'error' }));
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1100,padding:'20px'}}>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'12px',width:'min(640px,96vw)',maxHeight:'90vh',display:'flex',flexDirection:'column'}}>
        <div style={{padding:'18px 22px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center',gap:'10px'}}>
          <div style={{fontSize:'15px',fontWeight:'700',color:C.textBright}}>Invoice — {new Date(stmt.period+'-02').toLocaleString('default',{month:'long',year:'numeric'})}</div>
          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
            {stmt.user_id && <button style={css.smBtn(C.accent)} onClick={()=>setShowTimesheet(true)}>🕐 Review Timesheet</button>}
            <span style={css.badge(stBadge(stmt.status))}>{stLabel(stmt.status)}</span>
          </div>
        </div>
        <div style={{padding:'18px 22px',overflowY:'auto'}}>
          <div style={{display:'flex',justifyContent:'space-between',gap:'16px',marginBottom:'14px',fontSize:'12px'}}>
            <div><div style={{...css.lbl}}>From</div><div style={{color:C.textBright,fontWeight:'600'}}>{iss.name||'—'}</div>{iss.address_line1 && <div style={{color:C.muted}}>{iss.address_line1}</div>}{(iss.city||iss.state)&&<div style={{color:C.muted}}>{[iss.city,iss.state].filter(Boolean).join(', ')} {iss.zip||''}</div>}</div>
            <div style={{textAlign:'right'}}><div style={{...css.lbl}}>To</div><div style={{color:C.textBright,fontWeight:'600'}}>{recipientName}</div></div>
          </div>
          {lines.length===0 ? <div style={{color:C.muted,fontSize:'13px'}}>No billable lines for this period.</div> : (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
              <thead><tr><th style={css.th}>Client</th><th style={css.th}>Basis</th><th style={{...css.th,textAlign:'right'}}>Amount</th><th style={css.th}></th></tr></thead>
              <tbody>{lines.map((l,i)=>(
                <tr key={i}>
                  <td style={css.td}>{l.client_name}</td>
                  <td style={{...css.td,color:C.muted}}>{basisLabel(l)}</td>
                  <td style={{...css.td,textAlign:'right',fontWeight:'600',color:C.textBright}}>{$c(l.line_total)}</td>
                  <td style={{...css.td,textAlign:'right'}}>
                    {l.receipt_path && (
                      <button style={css.smBtn(C.accent)} disabled={receiptUrls[l.expense_id]==='loading'}
                              onClick={()=>viewReceipt(l)} title="Open the receipt this contractor attached">
                        {receiptUrls[l.expense_id]==='loading' ? '…' : receiptUrls[l.expense_id]==='error' ? 'Failed' : '📎 Receipt'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}</tbody>
              <tfoot><tr><td colSpan={2} style={{...css.td,fontWeight:'700',color:C.textBright}}>Total</td><td style={{...css.td,textAlign:'right',fontWeight:'700',color:C.accent}}>{$c(total)}</td><td style={css.td}></td></tr></tfoot>
            </table>
          )}
          {stmt.dispute_note && stmt.status==='disputed' && <div style={{fontSize:'12px',color:'#fc8181',marginTop:'12px'}}><strong>Dispute reason:</strong> {stmt.dispute_note}</div>}
          {stmt.status==='void' && <div style={{fontSize:'12px',color:C.danger,marginTop:'12px'}}>Voided because a period was reopened.</div>}
          {mode==='approve' && (
            <div style={{marginTop:'14px'}}><div style={css.lbl}><TKTooltip text="Optional message to the member explaining the approval or any conditions. Visible to them on their invoice page.">Approval note (optional) ⓘ</TKTooltip></div>
              <textarea rows={2} style={{...css.inp,resize:'vertical'}} value={approveNote} onChange={e=>setApproveNote(e.target.value)} /></div>
          )}
          {mode==='dispute' && (
            <div style={{marginTop:'14px'}}><div style={css.lbl}><TKTooltip text="Explain what needs to be corrected. The member will see this and can revise their entries before resubmitting.">Dispute reason (required) ⓘ</TKTooltip></div>
              <textarea rows={2} style={{...css.inp,resize:'vertical'}} value={disputeNote} onChange={e=>setDisputeNote(e.target.value)} placeholder="Tell the member what to fix" /></div>
          )}
        </div>
        <div style={{padding:'14px 22px',borderTop:`1px solid ${C.border}`,display:'flex',gap:'10px',justifyContent:'flex-end'}}>
          <button style={css.btn()} onClick={onClose} disabled={busy}>Close</button>
          {canDecide && mode===null && <>
            <button style={css.btn('danger')} onClick={()=>setMode('dispute')} disabled={busy}>Dispute</button>
            <button style={css.btn('primary')} onClick={()=>setMode('approve')} disabled={busy}>Approve</button>
          </>}
          {canDecide && mode==='approve' && <button style={css.btn('primary')} disabled={busy} onClick={()=>onApprove(approveNote)}>{busy?'Approving…':'Confirm Approve'}</button>}
          {canDecide && mode==='dispute' && <button style={css.btn('danger')} disabled={busy || !disputeNote.trim()} onClick={()=>onDispute(disputeNote)}>{busy?'Submitting…':'Confirm Dispute'}</button>}
        </div>
      </div>
      {showTimesheet && (() => {
        const clientNames = {};
        lines.forEach(l => { if (l.client_id) clientNames[l.client_id] = l.client_name; });
        return <TimesheetReviewModal stmt={stmt} clientNames={clientNames} onClose={()=>setShowTimesheet(false)} />;
      })()}
    </div>
  );
}

// A contractor_statements row with no user_id at all -- a payment made outside
// TimeKeep to a vendor/subcontractor who has no team member account here (and
// isn't meant to get one). payee_name stands in for the profiles(name) join;
// basis:'writein' keeps it out of buildApprovedLines' hours-summing (it never
// represents real logged hours), while total/line_items still flow into every
// dashboard and margin figure that reads contractor_statements normally.
function LogPaymentModal({ org, clients, defaultPeriod, onClose, onSaved }) {
  const [payeeName, setPayeeName] = useState('');
  const [clientId, setClientId] = useState('');
  const [period, setPeriod] = useState(defaultPeriod || curMonth());
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const activeClients = [...clients].filter(c => c.is_active !== false).sort((a,b)=>a.name.localeCompare(b.name));
  const amt = parseFloat(amount);
  const canSave = payeeName.trim() && clientId && /^\d{4}-\d{2}$/.test(period) && amt > 0;

  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    const client = clients.find(c=>c.id===clientId);
    const nowIso = new Date().toISOString();
    const ok = await runDb('Logging payment failed', supabase.from('contractor_statements').insert({
      org_id: org.id, user_id: null, payee_name: payeeName.trim(), period, status: 'approved',
      line_items: [{ client_id: clientId, client_name: client?.name || '', basis: 'writein', pay_type: 'writein', quantity: null, rate_or_amount: null, line_total: amt, note: note.trim() || null }],
      total: amt, issuer_snapshot: { name: payeeName.trim() }, generated_at: nowIso, approved_at: nowIso,
    }));
    setSaving(false);
    if (ok) onSaved();
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1150,padding:'20px'}}>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'12px',width:'min(440px,96vw)'}}>
        <div style={{padding:'18px 22px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:'15px',fontWeight:'700',color:C.textBright}}>Log a Payment</div>
          <button onClick={onClose} style={{background:'none',border:'none',color:C.muted,fontSize:'20px',cursor:'pointer',lineHeight:1}}>&times;</button>
        </div>
        <div style={{padding:'18px 22px',display:'flex',flexDirection:'column',gap:'14px'}}>
          <div style={{fontSize:'11px',color:C.muted}}>For a payment made outside TimeKeep — a vendor or subcontractor with no account here. Recorded as already approved and counts toward "Paid to members" and client margin.</div>
          <div>
            <div style={css.lbl}>Payee name</div>
            <input style={css.inp} value={payeeName} onChange={e=>setPayeeName(e.target.value)} placeholder="e.g. Acme Consulting" />
          </div>
          <div>
            <div style={css.lbl}>Client</div>
            <select style={css.sel} value={clientId} onChange={e=>setClientId(e.target.value)}>
              <option value="">Select a client…</option>
              {activeClients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={{display:'flex',gap:'12px'}}>
            <div style={{flex:1}}>
              <div style={css.lbl}>Period</div>
              <input type="month" style={css.inp} value={period} onChange={e=>setPeriod(e.target.value)} />
            </div>
            <div style={{flex:1}}>
              <div style={css.lbl}>Amount</div>
              <input type="number" step="0.01" min="0" style={css.inp} value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0.00" />
            </div>
          </div>
          <div>
            <div style={css.lbl}>Note (optional)</div>
            <input style={css.inp} value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. paid via ACH 8/17" />
          </div>
        </div>
        <div style={{padding:'14px 22px',borderTop:`1px solid ${C.border}`,display:'flex',gap:'10px',justifyContent:'flex-end'}}>
          <button style={css.btn()} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={css.btn('primary')} onClick={save} disabled={saving || !canSave}>{saving?'Saving…':'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Revenue Snapshot: Invoiced / Collected / Paid to Members / Profit for
// the selected period (whatever the page's own Month/YTD/Custom controls
// are set to), each its own bar from zero -- not stacked. These four
// numbers aren't parts of one whole: Collected is a subset of Invoiced, and
// Profit is literally Invoiced minus Paid to Members, so adding all four
// into one stacked total would be a meaningless number. Bars are nested by
// value instead (largest widest and furthest back, each smaller one
// narrower and in front, so every bar's own top edge stays visible -- the
// standard "bullet graph" technique), and each $ label sits right at its
// own bar's real height inside the chart. Hand-built in SVG since this
// isn't a chart shape Recharts has a built-in type for.
const SNAPSHOT_WIDTH_RATIOS = [1, 0.72, 0.48, 0.26]; // telescoping bar width by rank, largest value first
const SNAPSHOT_LABEL_GAP = 20; // minimum px between two labels so close values never overlap

function RevenueSnapshotBar({ bars, height=190, trackWidth=100 }) {
  const maxValue = Math.max(1, ...bars.map(b => Math.max(0, b.amount || 0)));
  const usableH = height - 8;
  const barsWithHeight = bars.map(b => ({ ...b, barH: maxValue > 0 ? Math.max(0, (Math.max(0,b.amount)/maxValue) * usableH) : 0 }));
  const ranked = [...barsWithHeight].sort((a,b) => b.amount - a.amount); // widest (largest value) first

  // Every label wants to sit right at its own bar's true height so the $
  // figure and the bar it belongs to are unambiguous at a glance -- but two
  // metrics landing close in value would collide illegibly if placed at
  // their literal heights. Push top-to-bottom: whichever label is too close
  // to the one already placed above it gets nudged down just enough to
  // clear a minimum gap, then a short leader line ties it back to its bar's
  // real height wherever that nudge actually moved it.
  const byTrueY = [...barsWithHeight].map(b => ({ ...b, trueY: height - b.barH })).sort((a,b) => a.trueY - b.trueY);
  let prevY = -Infinity;
  const placed = byTrueY.map(b => {
    const labelY = Math.max(b.trueY, prevY + SNAPSHOT_LABEL_GAP);
    prevY = labelY;
    return { ...b, labelY };
  });

  const labelColW = 190;
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%' }}>
      <svg width={trackWidth + 20 + labelColW} height={height + 40} style={{ overflow:'visible' }}>
        <line x1={0} y1={height} x2={trackWidth} y2={height} stroke={C.border} strokeWidth={1} />
        {ranked.map((b,i) => {
          const w = trackWidth * (SNAPSHOT_WIDTH_RATIOS[i] ?? 0.2);
          const y = height - b.barH;
          return (
            <rect key={b.name} x={0} y={y} width={w} height={b.barH} fill={b.color} rx={3}>
              <title>{b.name}: {'$'+Math.round(b.amount||0).toLocaleString()}</title>
            </rect>
          );
        })}
        {placed.map(b => {
          const leaderNeeded = Math.abs(b.labelY - b.trueY) > 3;
          return (
            <g key={b.name}>
              {leaderNeeded && <line x1={trackWidth} y1={b.trueY} x2={trackWidth+14} y2={b.labelY} stroke={b.color} strokeWidth={1} strokeDasharray="2,2" opacity={0.55} />}
              <text x={trackWidth+18} y={b.labelY+4} fontSize="12" fontWeight="700" fill={b.color}>{b.name} · {'$'+Math.round(b.amount||0).toLocaleString()}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── INVOICES ─────────────────────────────────────────────────────────────────
export function RevenueDashboardView({ invoices, clients, entries, assignments, orgMembers, users, org, statements, onNavigate, readOnly=false, initialBillingTerms=null }) {
  const tour = useTour('dashboard');
  const curMonthStr = curMonth();
  const [rangeMode, setRangeMode]     = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(curMonthStr);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');
  const [clientSort, setClientSort]   = useState('invoiced');
  const [chartType, setChartType]     = useState('bar'); // 'line' | 'bar' | 'pie'
  const [showForecast, setShowForecast] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null); // {type:'month'|'client', data} | null
  const [billingTerms, setBillingTerms] = useState(() => initialBillingTerms || []);
  const [showShareModal, setShowShareModal] = useState(false);
  // Which date decides the month an invoice counts toward. Most firms bill in
  // arrears (January's work invoiced in February), so filing by issue date
  // pushes revenue a month later than the member pay it is netted against
  // (statements are filed by service period) and left January empty despite a
  // paid January invoice. 'period' (default) files everything by service
  // month; 'paid' is a cash view -- Invoiced by issue date, Collected by the
  // month the payment actually arrived.
  const [revBasis, setRevBasis] = useState('period'); // 'period' | 'paid'

  // Fetched once per org for the forecast -- not on the critical path for the
  // rest of the dashboard, so a slow or failed fetch just leaves the forecast
  // toggle showing nothing rather than blocking anything else. Skipped in
  // readOnly mode: a shared-dashboard viewer has no Supabase Auth session at
  // all (they authenticated via the dashboard-share email flow instead), so
  // this direct client_billing_terms query would just fail RLS (is_org_member)
  // silently. initialBillingTerms carries the same data down from
  // get-shared-dashboard's own service-role fetch instead.
  useEffect(() => {
    if (!org?.id || readOnly) return;
    let canceled = false;
    supabase.from('client_billing_terms').select('*').eq('org_id', org.id).then(({ data, error }) => {
      if (!canceled && !error) setBillingTerms(data || []);
    });
    return () => { canceled = true; };
  }, [org?.id, readOnly]);

  // recharts 3.x's <Pie> renders zero sectors -- silently, no console error --
  // when it is part of React 18 StrictMode's synthetic dev-mode double-mount
  // (mount -> unmount -> mount). Confirmed by reproducing with StrictMode
  // removed (renders fine) and with recharts bumped to 3.10.1 (still broken,
  // so it's not a since-patched bug -- as of 2026-09 this is open upstream).
  // Bar and Line don't hit this. StrictMode double-invokes EVERY component's
  // first mount, not just the app's initial one -- so the original fix here
  // (gate the whole view's mount behind a `pieReady` flip) didn't actually
  // help, since <PieChart> was still being freshly unmounted/remounted on
  // every click of the Pie toggle down in the JSX (chartType ternary), and
  // each of those fresh mounts got double-invoked all over again. The real
  // fix is below in the chart card: <PieChart> is now kept permanently
  // mounted (never conditionally rendered by chartType) and only CSS
  // visibility toggles -- so it mounts exactly once, ever. `pieReady` still
  // defers *that one* mount to just past StrictMode's synthetic cycle.
  const [pieReady, setPieReady] = useState(false);
  useEffect(() => { setPieReady(true); }, []);

  // The month an invoice counts toward under the selected basis. A missing or
  // malformed service period falls back to the issue month so nothing drops out.
  const invMonthOf = (inv) => {
    const issuedMo = inv.issued_date?.slice(0,7);
    if (revBasis === 'paid') return issuedMo;
    return /^\d{4}-\d{2}$/.test(inv.period || '') ? inv.period : issuedMo;
  };
  // Day an invoice was paid (UTC date of paid_at); an invoice marked paid with
  // no timestamp falls back to its issue date rather than vanishing.
  const paidDateOf = (inv) => (inv.paid_at || '').slice(0,10) || inv.issued_date || '';
  const inPaidBounds = (inv) => {
    if (!invBounds) return true;
    const d = paidDateOf(inv);
    return d >= invBounds.start && d <= invBounds.end;
  };

  // Offer every month either date touches, so a service month with no invoice
  // issued in it (January above) is still selectable.
  const allMonths = [...new Set([curMonthStr, ...invoices.filter(i => i.status !== 'draft').flatMap(i => [i.issued_date?.slice(0,7), i.period]).filter(m => /^\d{4}-\d{2}$/.test(m || ''))])].sort().reverse();

  const fiscalStart = org?.fiscal_year_start || 1;
  const now = new Date();
  const ytdStart = (() => {
    const yr = now.getMonth() + 1 >= fiscalStart ? now.getFullYear() : now.getFullYear() - 1;
    return `${yr}-${String(fiscalStart).padStart(2,'0')}-01`;
  })();
  const ytdEnd = now.toISOString().slice(0,10);
  const today = ytdEnd;

  // Date bounds for invoice filtering (by service month or issue date, per revBasis)
  const invBounds = (() => {
    if (rangeMode === 'month')  return { start: `${selectedMonth}-01`, end: `${selectedMonth}-31` };
    if (rangeMode === 'ytd')    return { start: ytdStart, end: ytdEnd };
    if (rangeMode === 'custom' && customStart && customEnd) return { start: customStart, end: customEnd };
    return null;
  })();

  // Date bounds for entry filtering (by entry_date)
  const entBounds = (() => {
    if (rangeMode === 'month')  return { start: `${selectedMonth}-01`, end: `${selectedMonth}-31` };
    if (rangeMode === 'ytd')    return { start: ytdStart, end: ytdEnd };
    if (rangeMode === 'custom' && customStart && customEnd) return { start: customStart, end: customEnd };
    return null;
  })();

  const rangeLabel = (() => {
    if (rangeMode === 'month')  return new Date(selectedMonth+'-02').toLocaleString('en-US',{month:'long',year:'numeric'});
    if (rangeMode === 'ytd')    return `YTD (since ${new Date(ytdStart+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})})`;
    if (rangeMode === 'custom' && customStart && customEnd)
      return `${new Date(customStart+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})} – ${new Date(customEnd+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}`;
    return 'Custom Range';
  })();

  const inInvBounds = (inv) => {
    if (!invBounds) return true;
    if (revBasis === 'period') {
      // A service period is a whole month, so a custom range counts every
      // month it touches; the invoice-date view keeps exact day boundaries.
      const m = invMonthOf(inv) || '';
      return m >= invBounds.start.slice(0,7) && m <= invBounds.end.slice(0,7);
    }
    const d = inv.issued_date || '';
    return d >= invBounds.start && d <= invBounds.end;
  };
  const inEntBounds = (e) => {
    if (!entBounds) return true;
    return e.entry_date >= entBounds.start && e.entry_date <= entBounds.end;
  };

  // Drafts are excluded from every "Invoiced" figure on this page -- a draft
  // hasn't actually been sent, can still be freely edited or deleted, and
  // isn't a real invoice yet in any accounting sense. Collected/Outstanding/
  // Overdue below were already correctly excluding them (via their own
  // paid/sent filters); this is the one spot Invoiced itself wasn't.
  const periodInvoices = invoices.filter(i => i.status !== 'draft').filter(inInvBounds);
  // What counts as Collected in the period: the paid subset of the same
  // service-month set, or (cash view) every invoice paid within the dates.
  const collectedInvoices = revBasis === 'paid'
    ? invoices.filter(i => i.status === 'paid').filter(inPaidBounds)
    : periodInvoices.filter(i => i.status === 'paid');
  const periodEntries  = entries.filter(inEntBounds);

  // Member pay (src/metrics.js, shared with Client Reports and Team Analytics):
  // per member per month, the approved member invoice's lines when there is one,
  // otherwise an estimate from their assignments (computeEarnings). Expense
  // reimbursements are never pay. Before 2026-09-25 this page counted ONLY
  // approved statements, so pay was $0 (and margin 100%) until anything was
  // approved -- the tiles now say "Provisional" while any month is estimated.
  const payMonths = invBounds
    ? monthsBetween(invBounds.start, invBounds.end)
    : [...new Set([...(statements||[]).map(s => s.period), ...entries.map(e => e.entry_date?.slice(0,7))].filter(Boolean))].sort();
  const pay = memberPay({ members: orgMembers || [], assignments: assignments || [], entries, statements: statements || [], clients, months: payMonths });
  const paidToMembers = pay.total;
  const payProvisional = provisionalNote(pay);
  // Per-client pay; internal/overhead work stays in paidToMembers but isn't any client's cost.
  const perClientCost = pay.byClient;

  // ── KPI computations ─────────────────────────────────────────────────────
  const invoicedAmt   = periodInvoices.reduce((s,i) => s + parseFloat(i.total||0), 0);
  const collectedAmt  = collectedInvoices.reduce((s,i) => s + parseFloat(i.total||0), 0);
  const outstandingAmt = invoices.filter(i => i.status==='sent').reduce((s,i) => s + parseFloat(i.total||0), 0);
  const overdueInvoices = invoices.filter(i => i.status==='sent' && i.due_date && i.due_date < today);
  const overdueAmt = overdueInvoices.reduce((s,i) => s + parseFloat(i.total||0), 0);

  const totalBillableHours = periodEntries.filter(e => e.is_billable !== false && e.client_id)
    .reduce((s,e) => s + parseFloat(e.hours||0), 0);
  const effectiveRate = totalBillableHours > 0 ? invoicedAmt / totalBillableHours : null;

  const profitInvoiced  = invoicedAmt - paidToMembers;
  const profitCollected = collectedAmt - paidToMembers;
  const marginPct = invoicedAmt > 0 ? (profitInvoiced / invoicedAmt) * 100 : null;
  const marginCollectedPct = collectedAmt > 0 ? (profitCollected / collectedAmt) * 100 : null;
  const marginColor = marginPct === null ? C.muted : marginPct >= 40 ? C.success : marginPct >= 20 ? C.badgeAmber : C.danger;

  const fmt$ = (n) => fmtMoney(n, { cents: true });
  const fmtRound = (n) => fmtMoney(n);

  // ── Alerts ────────────────────────────────────────────────────────────────
  const poRequestsCount = invoices.filter(i => i.po_requested_at && !i.po_request_fulfilled_at).length;
  const statementsPendingCount = (statements||[]).filter(s => s.status==='submitted').length;

  // Signed contracts whose chosen pricing option has not been turned into
  // billing terms. Fetched rather than derived from props: contracts are not
  // loaded into this view, and the rule for "pending" lives in one place
  // server-side so this cannot disagree with the Contracts page about it.
  const [billingWritebackCount, setBillingWritebackCount] = useState(0);
  useEffect(() => {
    if (readOnly || !org?.id || !CONTRACTS_ENABLED) { setBillingWritebackCount(0); return undefined; }
    let canceled = false;
    supabase.rpc('pending_contract_billing_writebacks', { p_org_id: org.id })
      .then(({ data }) => { if (!canceled) setBillingWritebackCount((data || []).length); });
    return () => { canceled = true; };
  }, [readOnly, org?.id]);

  const alerts = [
    billingWritebackCount > 0 && { key:'billwb', tab:'contracts', tone:'warning',
      label:`${billingWritebackCount} signed contract${billingWritebackCount===1?'':'s'} set billing terms` },
    poRequestsCount > 0 && { key:'po', label:`${poRequestsCount} PO request${poRequestsCount===1?'':'s'} awaiting response`, tab:'invoices', tone:'warning' },
    statementsPendingCount > 0 && { key:'stmt', label:`${statementsPendingCount} member invoice${statementsPendingCount===1?'':'s'} to review`, tab:'statements', tone:'warning' },
    { key:'overdue', label: overdueInvoices.length>0 ? `${overdueInvoices.length} invoice${overdueInvoices.length===1?'':'s'} overdue` : 'No invoices overdue', tab:'invoices', navOpts:{statusFilter:'overdue'}, tone: overdueInvoices.length>0 ? 'danger' : 'success' },
  ].filter(Boolean);
  const alertTone = (tone) => tone==='danger' ? { bg:`${C.danger}1f`, color:C.danger }
    : tone==='success' ? { bg:`${C.success}1f`, color:C.success }
    : { bg:`${C.badgeAmber}26`, color:C.badgeAmber };

  // ── Trend chart data (also carries per-month profit, for Bar/Line/drill) ──
  const trendMonths = (() => {
    if (rangeMode === 'ytd') {
      const months = [];
      const d = new Date(ytdStart + 'T12:00:00');
      const end = new Date(ytdEnd + 'T12:00:00');
      while (d <= end) {
        months.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
        d.setMonth(d.getMonth()+1);
      }
      return months;
    }
    return [...Array(12)].map((_,i) => {
      const d = new Date(); d.setMonth(d.getMonth() - (11 - i));
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    });
  })();

  // Trailing data spans two calendar years (e.g. Oct '25 - Sep '26) and the
  // forecast runs 6 months past today -- which, past March, wraps back into
  // month ABBREVIATIONS the trailing data already used (both have an "Oct").
  // A bare "Oct" with no year is ambiguous to a reader, and duplicate
  // category labels confused Recharts' own bar positioning/animation (the
  // forecast bars all rendered at one shared wrong height regardless of
  // their real value, fixed 2026-09-14) -- always including the year makes
  // every label unique, which fixes both problems at once.
  const monthLabel = (mo) => {
    const d = new Date(mo+'-02');
    return d.toLocaleString('en-US',{month:'short'}) + " '" + String(d.getFullYear()).slice(-2);
  };

  // Member pay per month (approved, else estimated) for the chart's Profit series.
  const trendPay = memberPay({ members: orgMembers || [], assignments: assignments || [], entries, statements: statements || [], clients, months: trendMonths });
  const trendData = trendMonths.map(mo => {
    const moInv = invoices.filter(i => i.status !== 'draft' && invMonthOf(i) === mo);
    const invoicedMo  = moInv.reduce((s,i) => s + parseFloat(i.total||0), 0);
    const collectedMo = (revBasis === 'paid'
      ? invoices.filter(i => i.status==='paid' && paidDateOf(i).slice(0,7) === mo)
      : moInv.filter(i => i.status==='paid')
    ).reduce((s,i) => s + parseFloat(i.total||0), 0);
    const costMo = trendPay.byMonth[mo] || 0;
    return { month: monthLabel(mo), monthKey: mo, invoiced: parseFloat(invoicedMo.toFixed(2)), collected: parseFloat(collectedMo.toFixed(2)), profit: parseFloat((invoicedMo-costMo).toFixed(2)) };
  });

  // ── 6-month forecast ──────────────────────────────────────────────────────
  // Retainer/hybrid-retainer amounts come straight from whatever billing terms
  // are (or will already be) in effect for that month -- including any rate
  // change or seasonal period already scheduled. The hourly/overage component
  // uses each client's own trailing-6-month average hours, since that is the
  // only real signal available for what an hourly relationship will do next.
  const forecastMonthKeys = [...Array(6)].map((_,i) => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth()+1+i);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  const trailingAvgHoursByClient = (() => {
    const cutoff = (() => { const d = new Date(); d.setMonth(d.getMonth()-6); return d.toISOString().slice(0,10); })();
    const byClient = {};
    entries.filter(e => e.is_billable !== false && e.client_id && e.entry_date >= cutoff).forEach(e => {
      byClient[e.client_id] = (byClient[e.client_id]||0) + parseFloat(e.hours||0);
    });
    Object.keys(byClient).forEach(id => { byClient[id] = byClient[id] / 6; });
    return byClient;
  })();
  // Cost side of the forecast: trailing-6-month average of actually-approved
  // Paid to Members, projected forward flat. Same "recent real behavior
  // predicts the near future" methodology as the revenue side above,
  // deliberately NOT modeled on any one person's own capacity -- that would
  // bake a GuidePoint-specific assumption (who exactly absorbs overage
  // hours) into a multi-tenant feature, and stays fragile even here if that
  // ever changes. One blended org-wide figure, not per-member, since that's
  // the same level of detail paidToMembers itself is already computed at.
  // Same member-pay definition as everywhere else (approved, else estimated),
  // over the last 6 complete months.
  const trailingAvgPaidToMembers = (() => {
    const months = [...Array(6)].map((_,i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth()-6+i); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
    return memberPay({ members: orgMembers || [], assignments: assignments || [], entries, statements: statements || [], clients, months }).total / 6;
  })();
  const forecastData = forecastMonthKeys.map(mo => {
    const repDate = mo + '-15';
    let invoicedTotal = 0;
    clients.filter(c => c.is_active !== false && !c.is_internal).forEach(c => {
      const terms = billingTerms.filter(t => t.client_id === c.id)
        .find(t => t.effective_from <= repDate && (!t.effective_to || repDate < t.effective_to));
      if (!terms) return;
      const hrs = trailingAvgHoursByClient[c.id] || 0;
      invoicedTotal += calcOwedFromTerms(terms, roundBillingHours(hrs));
    });
    const invoiced = Math.round(invoicedTotal);
    const paidToMembersFc = Math.round(trailingAvgPaidToMembers);
    // collected is explicitly null, not just absent -- collection is
    // inherently about money already invoiced, there's no honest way to
    // forecast it for a month with nothing invoiced yet. Recharts needs a
    // real value (even null) present on every point once these merge with
    // trendData's differently-shaped rows.
    return { month: monthLabel(mo), monthKey: mo, invoiced, collected: null, paidToMembers: paidToMembersFc, profit: invoiced - paidToMembersFc };
  });
  const hasForecast = billingTerms.length > 0;

  // ── Snapshot chart bars: always the current selected period (Month/YTD/
  // Custom, whatever the page's own range controls are set to) -- one bar
  // per metric, never a separate per-month breakdown of its own.
  const snapshotBars = [
    { name:'Invoiced', amount:invoicedAmt, color:C.accent },
    { name:'Collected', amount:collectedAmt, color:C.success },
    { name:'Member pay', amount:paidToMembers, color:C.danger },
    { name:'Profit', amount:profitInvoiced, color:C.textDim },
  ];

  // ── Client rows: real invoiced/collected/outstanding + real per-client cost ─
  const clientRowsRaw = (() => {
    const byClient = {};
    const rowFor = (id) => byClient[id] || (byClient[id] = { id, invoiced:0, collected:0, outstanding:0, count:0 });
    periodInvoices.forEach(inv => {
      const r = rowFor(inv.client_id);
      r.invoiced += parseFloat(inv.total||0);
      r.count++;
      if (inv.status==='sent') r.outstanding += parseFloat(inv.total||0);
    });
    collectedInvoices.forEach(inv => { rowFor(inv.client_id).collected += parseFloat(inv.total||0); });
    const hoursByClient = {};
    periodEntries.filter(e => e.is_billable !== false && e.client_id).forEach(e => {
      hoursByClient[e.client_id] = (hoursByClient[e.client_id]||0) + parseFloat(e.hours||0);
    });
    return Object.values(byClient).map(v => {
      const cost = perClientCost[v.id] || 0;
      const profit = v.invoiced - cost;
      const hrs = hoursByClient[v.id] || 0;
      return {
        ...v,
        name: clients.find(c=>c.id===v.id)?.name || 'Unknown',
        cost, profit,
        marginPct: v.invoiced > 0 ? Math.round(profit / v.invoiced * 100) : null,
        hours: hrs,
        effRate: hrs > 0 ? v.invoiced / hrs : null,
      };
    });
  })();
  const clientRows = [...clientRowsRaw].sort((a,b) => {
    if (clientSort==='collected')   return b.collected - a.collected;
    if (clientSort==='outstanding') return b.outstanding - a.outstanding;
    if (clientSort==='margin')      return (b.marginPct??-1) - (a.marginPct??-1);
    return b.invoiced - a.invoiced;
  });

  // ── Client concentration ─────────────────────────────────────────────────
  const byInvoicedDesc = [...clientRowsRaw].sort((a,b) => b.invoiced - a.invoiced);
  const top3 = byInvoicedDesc.slice(0,3);
  const top3Pct = invoicedAmt > 0 ? Math.round(top3.reduce((s,c) => s+c.invoiced, 0) / invoicedAmt * 100) : null;

  // ── Pie data: profit by client, top 5 + Other ────────────────────────────
  const byProfitDesc = [...clientRowsRaw].filter(c => c.profit !== 0).sort((a,b) => b.profit - a.profit);
  const PIE_COLORS = ['#2a78d6','#eb6834','#1baf7a','#eda100','#e87ba4','#73726c'];
  const pieData = byProfitDesc.length > 6
    ? [...byProfitDesc.slice(0,5), { id:'other', name:`Other (${byProfitDesc.length-5} clients)`, profit: byProfitDesc.slice(5).reduce((s,c)=>s+c.profit,0), isOther:true, others: byProfitDesc.slice(5) }]
    : byProfitDesc;
  // ── Aging receivables ────────────────────────────────────────────────────
  const agingBuckets = (() => {
    const b = { d30:0, d60:0, d60plus:0 };
    invoices.filter(i => i.status==='sent').forEach(i => {
      const daysOver = i.due_date ? Math.floor((new Date(today) - new Date(i.due_date)) / 86400000) : 0;
      const amt = parseFloat(i.total||0);
      if (daysOver <= 30) b.d30 += amt;
      else if (daysOver <= 60) b.d60 += amt;
      else b.d60plus += amt;
    });
    return b;
  })();

  // ── Days to payment ───────────────────────────────────────────────────────
  const daysToPayment = (() => {
    const paid = invoices.filter(i => i.status==='paid' && i.paid_at && i.issued_date);
    const days = paid.map(i => Math.round((new Date(i.paid_at) - new Date(i.issued_date)) / 86400000));
    const avg = days.length ? Math.round(days.reduce((s,v)=>s+v,0)/days.length) : null;
    const months6 = [...Array(6)].map((_,i) => { const d=new Date(); d.setMonth(d.getMonth()-(5-i)); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
    const byMonth = {};
    paid.forEach(i => {
      const mo = i.paid_at.slice(0,7);
      const d = Math.round((new Date(i.paid_at) - new Date(i.issued_date)) / 86400000);
      (byMonth[mo] ||= []).push(d);
    });
    const trend = months6.map(mo => { const arr = byMonth[mo]||[]; return arr.length ? Math.round(arr.reduce((s,v)=>s+v,0)/arr.length) : null; });
    return { avg, trend };
  })();

  // ── Contractor utilization ───────────────────────────────────────────────
  const COMP_SEATS = ['contractor','employee_hourly','employee_salary'];
  const periodWeeks = invBounds ? Math.max(1, (new Date(invBounds.end) - new Date(invBounds.start)) / (7*86400000)) : 4.33;
  const utilization = orgMembers
    .filter(m => COMP_SEATS.includes(m.seat_type) && m.capacity_hours_week && !m.ended_at)
    .map(m => {
      const hrs = periodEntries.filter(e => e.user_id===m.user_id).reduce((s,e) => s+parseFloat(e.hours||0), 0);
      const cap = parseFloat(m.capacity_hours_week) * periodWeeks;
      return { name: users.find(u=>u.id===m.user_id)?.name || 'Unknown', hrs: Math.round(hrs*10)/10, cap: Math.round(cap*10)/10, pct: cap>0 ? Math.round(hrs/cap*100) : 0 };
    })
    .sort((a,b) => b.pct - a.pct);

  const pillStyle = { background:C.surface, border:`1px solid ${C.border}`, borderRadius:"10px", padding:"16px 20px", flex:"1 1 140px", minWidth:"130px" };
  const kpiLbl = { fontSize:'11px',color:C.muted,fontWeight:'600',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'6px' };
  const thS = { ...css.th, textAlign:'right', cursor:'pointer', userSelect:'none' };
  // Forecast on/off: a secondary button that reads as pressed when on.
  const chartTypeBtn = (active) => ({ ...css.btn('secondary'), fontSize:'12px', padding:'5px 12px', borderColor: active ? C.accent : C.border, color: active ? C.accent : C.text });

  const handleBarClick = (data) => {
    const payload = data?.payload || data;
    if (payload?.monthKey) setSelectedPoint({ type:'month', data: payload });
  };
  const handlePieClick = (data) => {
    const payload = data?.payload || data;
    if (payload) setSelectedPoint({ type:'client', data: payload });
  };

  const rangeControls = (
    <>
      <SegmentedControl options={[['month','Month'],['ytd','YTD'],['custom','Custom']]} value={rangeMode} onChange={setRangeMode} />
      {rangeMode==='month' && (
        <select aria-label="Month" style={{...css.sel,width:'auto',padding:'5px 10px',fontSize:'13px'}} value={selectedMonth} onChange={e=>setSelectedMonth(e.target.value)}>
          {allMonths.map(m=><option key={m} value={m}>{fmtPeriod(m)}</option>)}
        </select>
      )}
      {rangeMode==='custom' && (
        <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
          <input type="date" aria-label="From" style={{...css.inp,padding:'4px 8px',fontSize:'12px',width:'136px'}} value={customStart} onChange={e=>setCustomStart(e.target.value)} />
          <span style={{color:C.muted,fontSize:'12px'}}>to</span>
          <input type="date" aria-label="To" style={{...css.inp,padding:'4px 8px',fontSize:'12px',width:'136px'}} value={customEnd} onChange={e=>setCustomEnd(e.target.value)} />
        </div>
      )}
    </>
  );

  return (
    <Page title="Overview" subtitle={rangeMode==='month' ? `Last updated ${fmtDate(today)}` : rangeLabel}
      onTour={readOnly ? null : tour.launch}
      actions={readOnly ? null : <button style={css.btn('secondary')} onClick={()=>setShowShareModal(true)}>Share</button>}
      controls={<Controls
        filters={<div style={{display:'flex',alignItems:'center',gap:'8px'}}>
          <TKTooltip text="Which date decides the month an invoice counts toward. Service month files both Invoiced and Collected under the period the work was for — an invoice for January work issued in February counts in January, lining revenue up with the member pay it's netted against. Payment date is a cash view: Invoiced counts in the month it was issued, Collected in the month the payment arrived.">
            <span style={{fontSize:'12px',color:C.muted}}>Count invoices by ⓘ</span>
          </TKTooltip>
          <SegmentedControl options={[['period','Service month'],['paid','Payment date']]} value={revBasis} onChange={setRevBasis} />
        </div>}
        range={rangeControls} />}
      stats={<StatTiles items={[
          { label:'Invoiced', value: fmtMoney(invoicedAmt),
            basis: `${revBasis==='paid' ? 'by issue date' : 'by service month'} · ${periodInvoices.length} invoice${periodInvoices.length===1?'':'s'}`,
            tip: "Sent and paid invoices in the selected period. Drafts don't count: they haven't been sent and can still change.",
            onClick: readOnly ? undefined : () => onNavigate?.('invoices',{statusFilter:'all'}) },
          { label:'Collected', value: fmtMoney(collectedAmt), basis: revBasis==='paid' ? 'paid in range' : 'paid, of invoiced in range',
            tip: revBasis==='paid' ? 'Payments that arrived within the selected dates.' : 'The invoiced amount above that has been paid.',
            onClick: readOnly ? undefined : () => onNavigate?.('invoices',{statusFilter:'paid'}) },
          { label:'Outstanding', value: fmtMoney(outstandingAmt),
            basis: overdueAmt > 0
              ? <>as of today · <span style={{color:C.danger,fontWeight:600}}>{fmtMoney(overdueAmt)} overdue</span></>
              : 'as of today · none overdue',
            tip: 'Everything sent and unpaid, as of today. It ignores the period selector.',
            onClick: readOnly ? undefined : () => onNavigate?.('invoices',{statusFilter: overdueAmt > 0 ? 'overdue' : 'unpaid'}) },
          { label:'Profit on invoiced', value: fmtMoney(profitInvoiced),
            tone: profitInvoiced < 0 ? 'danger' : undefined,
            basis: marginPct === null ? 'nothing invoiced in range' : `${fmtPercent(marginPct)} margin · after ${fmtMoney(paidToMembers)} member pay`,
            provisional: payProvisional,
            tip: 'Invoiced minus member pay. Member pay is each member\'s approved invoice for the month, or an estimate from their assignments until it\'s approved. Expense reimbursements aren\'t pay. Client Reports and Team show Margin on work value, which is measured against the hours\' value instead of invoices.',
            onClick: readOnly ? undefined : () => onNavigate?.('statements') },
        ]} />}
      tour={{ stats: 'dashboard-kpis' }}>
      {/* The onboarding tour is app chrome for a logged-in admin; a shared-dashboard
          viewer never sees the rest of the app, so it's suppressed in readOnly mode. */}
      {!readOnly && tour.show && <WalkthroughModal tourKey="dashboard" onClose={tour.close} />}
      {showShareModal && <DashboardShareModal org={org} onClose={()=>setShowShareModal(false)} />}

      {/* Needs attention: operational nudges, each opening where it's handled.
          Hidden on a shared dashboard (the viewer has no login to act on them). */}
      {!readOnly && (
        <div style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:'6px'}}>
          <span style={{fontSize:'12px',color:C.muted,marginRight:'2px'}}>Needs attention:</span>
          {alerts.map(a => {
            const tone = alertTone(a.tone);
            return (
              <button key={a.key} type="button" onClick={()=>onNavigate?.(a.tab, a.navOpts)}
                style={{display:'flex',alignItems:'center',gap:'5px',padding:'4px 10px',borderRadius:'999px',border:'none',cursor:'pointer',background:tone.bg,color:tone.color,fontSize:'12px',fontWeight:'600',fontFamily:'inherit'}}>
                {a.label} →
              </button>
            );
          })}
        </div>
      )}


      {/* Chart: line / bar / pie, click-through detail on bar and pie */}
      <div style={{...css.card,marginBottom:'16px'}} data-tour="dashboard-chart">
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'6px',flexWrap:'wrap',gap:'8px'}}>
          <div style={{display:'flex',gap:'4px'}}>
            <SegmentedControl options={[['line','Line'],['bar','Bar'],['pie','Pie'],['snapshot','Snapshot']]} value={chartType}
              onChange={(k)=>{ setChartType(k); setSelectedPoint(null); if (k==='pie' || k==='snapshot') setShowForecast(false); }} />
          </div>
          {chartType!=='pie' && chartType!=='snapshot' && (
            <button onClick={()=>setShowForecast(f=>!f)} disabled={!hasForecast}
              style={{...chartTypeBtn(showForecast), opacity: hasForecast?1:0.5, cursor: hasForecast?'pointer':'default'}}
              title={hasForecast?'Retainer and hybrid clients project from their scheduled billing terms; hourly clients project from their trailing 6-month average billed hours.':'No billing terms on record yet for any client'}>
              + 6-month forecast
            </button>
          )}
        </div>
        <div style={{fontSize:'11px',color:C.muted,marginBottom:'10px'}}>
          {chartType==='line' && 'Trend across invoiced, collected, and profit.'}
          {chartType==='bar' && 'Invoiced and profit by month. Click a bar for that month\'s detail.'}
          {chartType==='pie' && 'Profit by client, this period. Click a slice for that client\'s detail.'}
          {chartType==='snapshot' && <TKTooltip text="Each bar starts at zero and reaches its own real value — they're not stacked on top of each other. Collected is part of Invoiced, and Profit is Invoiced minus Paid to Members, so adding all four into one total would be meaningless; this way the heights are directly comparable.">Invoiced, Collected, Paid to members, and Profit for {rangeLabel} ⓘ</TKTooltip>}
        </div>
        {/* All four chart instances stay permanently mounted -- only CSS visibility
            toggles with chartType. Pie in particular must never unmount/remount: every
            fresh mount of recharts v3's <Pie> gets caught by StrictMode's dev-only
            double-invoke (mount -> unmount -> mount) and renders zero sectors (see the
            pieReady note above). Mounting it exactly once, deferred past that cycle, and
            then only hiding/showing it fixes this for good. Line/Bar/Snapshot don't have
            the bug but are kept in the same always-mounted pattern for consistency. */}
        <div style={{position:'relative', height:240}}>
          <div style={{display: chartType==='line' ? 'block' : 'none', height:'100%'}}>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={showForecast ? [...trendData, ...forecastData] : trendData} margin={{top:4,right:16,left:0,bottom:0}}>
                <XAxis dataKey="month" tick={{fill:C.muted,fontSize:11}} axisLine={false} tickLine={false} />
                <YAxis tick={{fill:C.muted,fontSize:11}} axisLine={false} tickLine={false} width={52} tickFormatter={v => v===0?'':'$'+(v>=1000?Math.round(v/1000)+'k':v)} />
                <Tooltip contentStyle={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'6px',fontSize:'12px'}} labelStyle={{color:C.textBright,fontWeight:'600'}} formatter={(v,name)=>['$'+parseFloat(v||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}), name]} />
                <Legend wrapperStyle={{fontSize:'12px',color:C.muted}} />
                <Line type="monotone" dataKey="invoiced" name="Invoiced" stroke={C.accent} strokeWidth={2} dot={{r:3}} connectNulls />
                <Line type="monotone" dataKey="collected" name="Collected" stroke={C.success} strokeWidth={2} dot={{r:3}} connectNulls />
                <Line type="monotone" dataKey="profit" name="Profit" stroke={C.textDim} strokeWidth={2} dot={{r:3}} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div style={{display: chartType==='bar' ? 'block' : 'none', height:'100%'}}>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={showForecast ? [...trendData, ...forecastData] : trendData} margin={{top:4,right:16,left:0,bottom:0}}>
                <XAxis dataKey="month" tick={{fill:C.muted,fontSize:11}} axisLine={false} tickLine={false} />
                <YAxis tick={{fill:C.muted,fontSize:11}} axisLine={false} tickLine={false} width={52} tickFormatter={v => v===0?'':'$'+(v>=1000?Math.round(v/1000)+'k':v)} />
                <Tooltip contentStyle={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'6px',fontSize:'12px'}} labelStyle={{color:C.textBright,fontWeight:'600'}} formatter={(v,name)=>['$'+parseFloat(v||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}), name]} />
                <Legend wrapperStyle={{fontSize:'12px',color:C.muted}} />
                <Bar dataKey="invoiced" name="Invoiced" fill={C.accent} radius={[3,3,0,0]} maxBarSize={30} onClick={handleBarClick} style={{cursor:'pointer'}} />
                <Bar dataKey="profit" name="Profit" fill={C.textDim} radius={[3,3,0,0]} maxBarSize={30} onClick={handleBarClick} style={{cursor:'pointer'}} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div style={{display: chartType==='pie' ? 'block' : 'none', height:'100%'}}>
            {pieReady && (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Tooltip contentStyle={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'6px',fontSize:'12px'}} labelStyle={{color:C.textBright}} formatter={(v,n)=>[fmt$(v),n]} />
                  <Pie data={pieData} dataKey="profit" nameKey="name" cx="50%" cy="50%" outerRadius={90} onClick={handlePieClick} cursor="pointer">
                    {pieData.map((entry,i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke={C.surface} strokeWidth={2} />)}
                  </Pie>
                  <Legend wrapperStyle={{fontSize:'11px',color:C.muted}} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div style={{display: chartType==='snapshot' ? 'block' : 'none', height:'100%'}}>
            <RevenueSnapshotBar bars={snapshotBars} height={190} />
          </div>
        </div>
      </div>

      {/* Details panel — driven by chart selection */}
      <div style={{...css.card,marginBottom:'24px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
          <div style={{fontSize:'13px',fontWeight:'700',color:C.textBright}}>
            {selectedPoint ? (selectedPoint.type==='month' ? selectedPoint.data.month : selectedPoint.data.name) : `More for ${rangeLabel}`}
          </div>
          {selectedPoint && <button style={css.btn()} onClick={()=>setSelectedPoint(null)}>Back to overview</button>}
        </div>
        <div style={{display:'flex',flexWrap:'wrap',gap:'18px'}}>
          {(() => {
            if (!selectedPoint) return [
              ['Member pay', fmtMoney(paidToMembers) + (payProvisional ? ' (provisional)' : '')],
              ['Profit on collected', fmtMoney(profitCollected)], ['Margin on collected', fmtPercent(marginCollectedPct)],
              ['Invoiced per hour', effectiveRate===null ? '—' : fmtRate(Math.round(effectiveRate))], ['Billable hours', fmtHours(totalBillableHours)],
            ];
            if (selectedPoint.type==='month') {
              const m = selectedPoint.data;
              const mgn = m.invoiced ? Math.round(m.profit/m.invoiced*100) : null;
              return [['Invoiced',fmtRound(m.invoiced)], ['Collected',fmtRound(m.collected)], ['Profit on invoiced',fmtRound(m.profit)], ['Margin on invoiced', mgn===null?'—':mgn+'%']];
            }
            const c = selectedPoint.data;
            if (c.isOther) return [['Clients', String(c.others.length)], ['Combined profit', fmtRound(c.profit)]];
            return [['Invoiced',fmtRound(c.invoiced)], ['Collected',fmtRound(c.collected)], ['Outstanding', c.outstanding>0?fmtRound(c.outstanding):'—'], ['Profit on invoiced',fmtRound(c.profit)], ['Margin on invoiced', c.marginPct===null?'—':c.marginPct+'%'], ['Invoiced per hour', c.effRate?fmtRate(Math.round(c.effRate)):'—']];
          })().map(([label,value]) => (
            <div key={label}><div style={{fontSize:'11px',color:C.muted,marginBottom:'2px'}}>{label}</div><div style={{fontSize:'16px',fontWeight:'700',color:C.textBright}}>{value}</div></div>
          ))}
        </div>
      </div>

      {/* Client concentration */}
      {top3.length>0 && (
        <div style={{...css.card,marginBottom:'16px'}}>
          <div style={{fontSize:'13px',fontWeight:'700',color:C.textBright,marginBottom:'8px'}}>Client concentration</div>
          <div style={{display:'flex',alignItems:'baseline',gap:'8px',marginBottom:'8px'}}>
            <span style={{fontSize:'19px',fontWeight:'700',color:C.textBright}}>{top3Pct===null?'—':top3Pct+'%'}</span>
            <span style={{fontSize:'12px',color:C.muted}}>of revenue comes from your top {top3.length} client{top3.length===1?'':'s'}</span>
          </div>
          {/* Ranked shades of one hue (accent at 100/60/35%), then C.border for everyone else:
              readable under any accent, and no color implies good/bad. */}
          <div style={{display:'flex',gap:'2px',height:'10px',borderRadius:'5px',overflow:'hidden'}}>
            {top3.map((c,i) => <div key={c.id} style={{width:(invoicedAmt>0?c.invoiced/invoicedAmt*100:0)+'%',background:`${C.accent}${['','99','59'][i] || '59'}`}} title={`${c.name} ${Math.round(c.invoiced/invoicedAmt*100)}%`} />)}
            <div style={{flex:1,background:C.border}} />
          </div>
          <div style={{fontSize:'11px',color:C.muted,marginTop:'6px'}}>{byInvoicedDesc[0]?.name} alone is {invoicedAmt>0?Math.round(byInvoicedDesc[0].invoiced/invoicedAmt*100):0}% — worth knowing before any single-client dependency becomes a real risk.</div>
        </div>
      )}

      {/* Aging receivables + Days to payment */}
      <div style={{display:'flex',gap:'16px',flexWrap:'wrap',marginBottom:'16px'}}>
        <div style={{...css.card,flex:'1 1 260px'}}>
          <div style={{fontSize:'13px',fontWeight:'700',color:C.textBright,marginBottom:'10px'}}>Aging receivables</div>
          {[['0-30 days',agingBuckets.d30,C.success],['31-60 days',agingBuckets.d60,C.badgeAmber],['60+ days',agingBuckets.d60plus,C.danger]].map(([label,amt,color]) => (
            <div key={label} style={{marginBottom:'8px'}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'12px',marginBottom:'4px'}}><span style={{color:C.muted}}>{label}</span><span style={{fontWeight:'600',color:C.textBright}}>{fmtRound(amt)}</span></div>
              <div style={{height:'6px',borderRadius:'3px',background:C.bg,overflow:'hidden'}}><div style={{height:'100%',width:(outstandingAmt>0?amt/outstandingAmt*100:0)+'%',background:color}} /></div>
            </div>
          ))}
        </div>
        <div style={{...css.card,flex:'1 1 260px'}}>
          <div style={{fontSize:'13px',fontWeight:'700',color:C.textBright,marginBottom:'10px'}}>Days to payment</div>
          <div style={{fontSize:'19px',fontWeight:'700',color:C.textBright}}>{daysToPayment.avg===null?'—':daysToPayment.avg+' days'}</div>
          <div style={{fontSize:'11px',color:C.muted,marginTop:'2px',marginBottom:'8px'}}>Issued to paid, all time</div>
          <ResponsiveContainer width="100%" height={50}>
            <ComposedChart data={daysToPayment.trend.map((v,i)=>({i,v}))}>
              <Line type="monotone" dataKey="v" stroke={C.accent} strokeWidth={2} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
          <div style={{fontSize:'11px',color:C.muted}}>Trailing 6 months</div>
        </div>
      </div>

      {/* Client table */}
      {clientRows.length > 0 && (
        <div style={{...css.card,marginBottom:'24px'}} data-tour="dashboard-clients">
          <div style={{fontSize:'13px',fontWeight:'700',color:C.textBright,marginBottom:'14px'}}>Clients · {rangeLabel}</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
            <thead>
              <tr style={{background:C.bg}}>
                <th style={{...css.th,textAlign:'left'}}>Client</th>
                <th style={thS} onClick={()=>setClientSort('invoiced')}>Invoiced{clientSort==='invoiced'?' ↓':''}</th>
                <th style={thS} onClick={()=>setClientSort('collected')}>Collected{clientSort==='collected'?' ↓':''}</th>
                <th style={thS} onClick={()=>setClientSort('outstanding')}>Outstanding{clientSort==='outstanding'?' ↓':''}</th>
                <th style={thS} onClick={()=>setClientSort('margin')}><TKTooltip text="(Invoiced − Member pay) ÷ Invoiced for this client. Member pay is approved, or estimated until approved.">Margin ⓘ</TKTooltip>{clientSort==='margin'?' ↓':''}</th>
                <th style={{...css.th,textAlign:'right'}}><TKTooltip text="Invoiced divided by this client's billable hours logged in the period.">Per hour ⓘ</TKTooltip></th>
              </tr>
            </thead>
            <tbody>
              {clientRows.map((r,i) => (
                <tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
                  <td style={{...css.td,fontWeight:'600',color:C.textBright}}>{r.name}</td>
                  <td style={{...css.td,textAlign:'right',color:C.textBright}}>{fmtRound(r.invoiced)}</td>
                  <td style={{...css.td,textAlign:'right',color:C.success}}>{r.collected>0?fmtRound(r.collected):'—'}</td>
                  <td style={{...css.td,textAlign:'right',color:r.outstanding>0?C.textBright:C.muted}}>{r.outstanding>0?fmtRound(r.outstanding):'—'}</td>
                  <td style={{...css.td,textAlign:'right',color:C.textBright}}>{r.marginPct===null?'—':r.marginPct+'%'}</td>
                  <td style={{...css.td,textAlign:'right',color:C.muted}}>{r.effRate?fmtRate(Math.round(r.effRate)):'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{fontSize:'11px',color:C.muted,marginTop:'10px'}}>Margin is invoiced minus member pay for each client: the approved member invoice's lines for that client, or an estimate from assignments until it's approved.</div>
        </div>
      )}

      {/* Contractor utilization */}
      {utilization.length > 0 && (
        <div style={{...css.card,marginBottom:'8px'}}>
          <div style={{fontSize:'13px',fontWeight:'700',color:C.textBright,marginBottom:'14px'}}>Contractor utilization · {rangeLabel}</div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {utilization.map(u => (
              <div key={u.name}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}>
                  <span style={{fontSize:'13px',color:C.textBright}}>{u.name}</span>
                  <span style={{fontSize:'12px',color:C.muted}}>{u.hrs} / {u.cap} hrs</span>
                </div>
                <div style={{height:'6px',borderRadius:'3px',background:C.bg,overflow:'hidden'}}>
                  <div style={{height:'100%',width:Math.min(u.pct,100)+'%',background: u.pct>95 ? C.badgeAmber : C.success}} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Page>
  );
}

// ─── RECEIPT STORAGE ──────────────────────────────────────────────────────────
// Bucket is private; every read goes through a short-lived signed URL. The
// storage policy resolves access by joining the expenses row on the expense id
// in the path, so the row must exist before the file is uploaded — on a new
// expense we insert first, then upload, then stamp the file columns.

const RECEIPT_BUCKET   = 'expense-receipts';
const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;   // matches the bucket's file_size_limit
const RECEIPT_MIMES = [
  'image/jpeg','image/png','image/gif','image/webp',
  'application/pdf','text/plain','image/heic','image/heif',
];

// iPhones shoot HEIC and the bucket accepts it, but Safari is the only browser
// that will render it in an <img>. Everywhere else it downloads instead of
// previewing — so we show a file card rather than a broken image element.
const isPreviewableImage = (mime) =>
  ['image/jpeg','image/png','image/gif','image/webp'].includes(mime);

// Phone photos arrive around 4000px and several megabytes. Downscaling to
// 1600px JPEG before upload cuts that ~10x, which matters most on cell data,
// and lands inside the 1000-2000px range Gemini reads best — images are billed
// in 768px tiles, so a 4032px original wastes tokens on every scan.
//
// HEIC conversion comes along for free rather than being the goal: canvas has
// to DECODE the source, and only Safari decodes HEIC. That is fine, because
// HEIC comes from iPhones and every iOS browser is WebKit underneath. Anywhere
// decoding fails we upload the original untouched — a WASM decoder would be
// 1-2MB of bundle for a case that cannot occur.
const RECEIPT_MAX_EDGE = 1600;

const normalizeReceiptImage = async (file) => {
  if (!file.type.startsWith('image/')) return file;      // PDFs and text pass through
  const isHeic = /heic|heif/i.test(file.type);
  try {
    // from-image honours EXIF rotation — phone photos are frequently sideways.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale  = Math.min(1, RECEIPT_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.85));
    if (!blob) return file;

    // For HEIC always take the JPEG, even if bigger — it is the only way the
    // receipt previews outside Safari. Otherwise only keep a real saving.
    if (!isHeic && blob.size >= file.size) return file;

    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;   // undecodable here (HEIC outside Safari) — store the original
  }
};

const receiptFileError = (file) => {
  if (!file) return null;
  if (file.size > RECEIPT_MAX_BYTES)
    return `That file is ${(file.size/1024/1024).toFixed(1)} MB. Receipts must be under 10 MB.`;
  if (!RECEIPT_MIMES.includes(file.type))
    return `${file.type || 'That file type'} is not accepted. Use a photo, a PDF, or plain text.`;
  return null;
};

// Storage keys are opaque, but a readable name helps when browsing the bucket.
// Strip anything that could confuse a path, and keep the extension.
const receiptStorageKey = (orgId, expenseId, file) => {
  const dot   = file.name.lastIndexOf('.');
  const ext   = dot > -1 ? file.name.slice(dot).toLowerCase().replace(/[^a-z0-9.]/g,'') : '';
  const stem  = (dot > -1 ? file.name.slice(0,dot) : file.name)
                  .toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'').slice(0,40) || 'receipt';
  return `${orgId}/${expenseId}/${Date.now()}-${stem}${ext}`;
};

const signedReceiptUrl = async (path) => {
  if (!path) return null;
  const { data, error } = await supabase.storage.from(RECEIPT_BUCKET).createSignedUrl(path, 300);
  if (error) { notifyError(`Opening receipt: ${friendlyDbError(error)}.`); return null; }
  return data?.signedUrl ?? null;
};

// Mileage rates are two-tiered: TimeKeep maintains the IRS figures centrally in
// platform_mileage_rates, and an org only writes a row if it deliberately pays
// something different. Resolution is per-date, so a trip logged late still
// prices at the rate that was in force on the day it happened.
function MileageRatesPanel({ orgId }) {
  const [platform, setPlatform] = useState([]);
  const [orgRates, setOrgRates] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [newRate, setNewRate]   = useState('');
  const [newFrom, setNewFrom]   = useState('');
  const [busy, setBusy]         = useState(false);

  const load = async () => {
    const [p, o] = await Promise.all([
      supabase.from('platform_mileage_rates').select('*').order('effective_from', { ascending:false }),
      supabase.from('org_mileage_rates').select('*').eq('org_id', orgId).order('effective_from', { ascending:false }),
    ]);
    if (p.error) notifyError(`Loading platform mileage rates: ${friendlyDbError(p.error)}.`);
    if (o.error) notifyError(`Loading your mileage rates: ${friendlyDbError(o.error)}.`);
    setPlatform(p.data||[]);
    setOrgRates(o.data||[]);
    setLoading(false);
  };

  useEffect(() => { if (orgId) load(); }, [orgId]);

  const addRate = async () => {
    if (!newRate || !newFrom) return;
    setBusy(true);
    const ok = await runDb('Adding mileage rate', supabase.from('org_mileage_rates')
      .insert({ org_id: orgId, rate: parseFloat(newRate), effective_from: newFrom }));
    setBusy(false);
    if (ok) { setNewRate(''); setNewFrom(''); await load(); }
  };

  const removeRate = async (id) => {
    if (await runDb('Removing mileage rate', supabase.from('org_mileage_rates').delete().eq('id', id))) load();
  };

  const today       = new Date().toISOString().slice(0,10);
  const activeOrg   = orgRates.find(r => r.effective_from <= today);
  const activePlat  = platform.find(r => r.effective_from <= today);
  const inForce     = activeOrg ?? activePlat;
  // An override applies indefinitely forward, which is correct — a platform
  // update must never silently rewrite a rate an admin chose on purpose. But
  // that means it can quietly go stale, so say so rather than let it drift.
  const overrideStale = activeOrg && activePlat && activeOrg.effective_from < activePlat.effective_from;

  if (loading) return <div style={css.card}><div style={{color:C.muted,fontSize:'13px'}}>Loading mileage rates…</div></div>;

  return (
    <div style={css.card}>
      <div style={{...css.h2,marginBottom:"6px"}}>Mileage Rates</div>
      <div style={{fontSize:'12px',color:C.muted,marginBottom:'16px'}}>
        TimeKeep keeps the standard IRS rates current. Add your own only if you reimburse at a
        different rate — yours then takes precedence from its start date onward.
      </div>

      {!inForce ? (
        <div style={{background:'rgba(229,62,62,0.1)',border:`1px solid ${C.danger}`,borderRadius:'6px',padding:'12px',marginBottom:'16px'}}>
          <div style={{fontSize:'13px',color:C.text,fontWeight:'600',marginBottom:'4px'}}>No mileage rate is in force today</div>
          <div style={{fontSize:'12px',color:C.textDim}}>
            Mileage entries are blocked until a rate exists for the date being logged. Receipt
            expenses are unaffected. {platform.length===0 ? 'TimeKeep has not loaded the IRS standard rates yet, so add your own rate below.' : 'Add your own rate below.'}
          </div>
        </div>
      ) : (
        <div style={{...css.metric, marginBottom:'16px'}}>
          <div style={css.lbl}>Rate in force today</div>
          <div style={{...css.bigN, fontSize:'20px'}}>
            {fmtMileRate(inForce?.rate)} <span style={{fontSize:'12px',color:C.muted,fontWeight:'400'}}>
              / mile · {activeOrg ? 'your custom rate' : 'IRS standard'}
            </span>
          </div>
        </div>
      )}

      {inForce && platform.length===0 && (
        <div style={{background:'rgba(237,167,48,0.1)',border:`1px solid ${C.badgeAmber}`,borderRadius:'6px',padding:'12px',marginBottom:'16px',fontSize:'12px',color:C.text}}>
          TimeKeep has not loaded the IRS standard rates, so only your own rates apply.
          {orgRates.length>0 && ` Your earliest rate starts ${orgRates[orgRates.length-1].effective_from}, so trips dated before that cannot be priced. Add a rate with an earlier start date to cover them.`}
        </div>
      )}

      {overrideStale && (
        <div style={{background:'rgba(237,167,48,0.1)',border:`1px solid ${C.badgeAmber}`,borderRadius:'6px',padding:'12px',marginBottom:'16px',fontSize:'12px',color:C.text}}>
          Your custom rate of {fmtMileRate(activeOrg.rate)} started {activeOrg.effective_from}, but the
          IRS standard has changed since then (now {fmtMileRate(activePlat.rate)} as of {activePlat.effective_from}).
          Yours is still being used — that is intentional, but worth a look.
        </div>
      )}

      {platform.length>0 && (
        <div style={{marginBottom:'18px'}}>
          <div style={css.lbl}>IRS standard rates</div>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr><th style={css.th}>Effective from</th><th style={css.th}>Rate</th><th style={css.th}>Note</th></tr></thead>
            <tbody>
              {platform.map(r=>(
                <tr key={r.id}>
                  <td style={css.td}>{r.effective_from}</td>
                  <td style={{...css.td,fontVariantNumeric:'tabular-nums'}}>{fmtMileRate(r.rate)}</td>
                  <td style={{...css.td,color:C.muted}}>{r.note||'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={css.lbl}>Your custom rates</div>
      {orgRates.length===0 ? (
        <div style={{fontSize:'13px',color:C.muted,margin:'6px 0 14px'}}>
          None — you are using the IRS standard.
        </div>
      ) : (
        <table style={{width:'100%',borderCollapse:'collapse',marginBottom:'14px'}}>
          <thead><tr><th style={css.th}>Effective from</th><th style={css.th}>Rate</th><th style={css.th}></th></tr></thead>
          <tbody>
            {orgRates.map(r=>(
              <tr key={r.id}>
                <td style={css.td}>{r.effective_from}</td>
                <td style={{...css.td,fontVariantNumeric:'tabular-nums'}}>{fmtMileRate(r.rate)}</td>
                <td style={{...css.td,textAlign:'right'}}>
                  <button style={css.smBtn(C.badgeRed)} onClick={()=>removeRate(r.id)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{display:'flex',gap:'8px',alignItems:'flex-end',flexWrap:'wrap'}}>
        <div>
          <div style={css.lbl}>Effective from</div>
          <input type="date" style={{...css.inp,width:'170px'}} value={newFrom} onChange={e=>setNewFrom(e.target.value)} />
        </div>
        <div>
          <div style={css.lbl}>Rate per mile</div>
          <input type="number" step="0.001" min="0" placeholder="0.700"
                 style={{...css.inp,width:'130px'}} value={newRate} onChange={e=>setNewRate(e.target.value)} />
        </div>
        <button style={css.btn()} disabled={!newRate||!newFrom||busy} onClick={addRate}>
          {busy?'Adding…':'Add Rate'}
        </button>
      </div>
      <div style={{fontSize:'11px',color:C.muted,marginTop:'8px'}}>
        Rates have no end date — each new one supersedes the last from its start date.
      </div>
    </div>
  );
}

// ─── EXPENSES ─────────────────────────────────────────────────────────────────
// Two-axis model: is_billable routes an expense onto the client invoice,
// is_reimbursable routes it onto the submitter's contractor statement. They are
// independent. Markup applies to the CLIENT side only — reimbursement is always
// at cost, so a marked-up expense never overpays the person who bought it.

const EXP_BADGE = { draft:'gray', submitted:'amber', approved:'green', rejected:'red' };

// Line-level money (expenses, mileage rates): always with cents, in the org's currency.
const fmtCents = (v) => fmtMoney(v, { cents: true });
// A per-mile rate is not a money amount: IRS rates carry half-cents (72.5 cents),
// which fmtCents would round to $0.73. Shows three decimals only when needed.
const fmtMileRate = (v) => {
  const n = Number(v);
  if (v == null || !isFinite(n)) return '—';
  const t = Math.round(n * 1000) / 1000;
  return '$' + (Math.abs(t * 100 - Math.round(t * 100)) > 1e-9 ? t.toFixed(3) : t.toFixed(2));
};

const monthOf = (d) => (d||'').slice(0,7);

// Defined at module scope on purpose. Declaring these inside the component body
// gives them a new function identity every render, so React unmounts and
// remounts the subtree instead of updating it — which makes any input inside
// lose focus after a single keystroke.
function ExpField({ label, children, hint }) {
  return (
    <div style={{marginBottom:'14px'}}>
      <div style={css.lbl}>{label}</div>
      {children}
      {hint && <div style={{fontSize:'11px',color:C.muted,marginTop:'4px'}}>{hint}</div>}
    </div>
  );
}

function ExpRow({ label, children }) {
  return (
    <div style={{marginBottom:'12px'}}>
      <div style={css.lbl}>{label}</div>
      <div style={{fontSize:'13px',color:C.text}}>{children}</div>
    </div>
  );
}

function ExpensesView({ expenses=[], categories=[], clients=[], allClients=[], org, profile,
                        isAdmin, users=[], monthLocks=[], onRefresh }) {
  const tour = useTour('expenses');
  const [tab, setTab]           = useState('mine');
  const [period, setPeriod]     = useState('all');
  const [statusF, setStatusF]   = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [checked, setChecked]   = useState(() => new Set());
  const [busy, setBusy]         = useState(false);

  const clientName = (id) => allClients.find(c=>c.id===id)?.name || '—';
  const catName    = (id) => categories.find(c=>c.id===id)?.name || '—';
  const userName   = (id) => users.find(u=>u.id===id)?.name || 'Unknown';

  const mine   = expenses.filter(e => e.user_id === profile.id);
  const review = expenses.filter(e => e.status === 'submitted');
  // The queue shows everyone's non-draft expenses (drafts stay private to their
  // owner) so Approved / Rejected pills have something to show; it opens on
  // Needs review.
  const queue  = expenses.filter(e => e.status !== 'draft');
  const base   = (isAdmin && tab==='review') ? queue : mine;
  const inPeriod = base.filter(e => period==='all' || monthOf(e.expense_date)===period);
  const statusCount = (st) => inPeriod.filter(e => st==='all' || e.status===st);
  const sumAmt = (list) => list.reduce((a,e)=>a+Number(e.amount||0),0);
  const expensePills = (isAdmin && tab==='review')
    ? [['submitted','Needs review'],['approved','Approved'],['rejected','Rejected','danger'],['all','All']]
    : [['draft','Draft'],['submitted','Submitted'],['approved','Approved'],['rejected','Rejected','danger'],['all','All']];

  const periods = [...new Set(expenses.map(e=>monthOf(e.expense_date)))].filter(Boolean).sort().reverse();

  const rows = inPeriod.filter(e => statusF==='all' || e.status===statusF);

  const selected = expenses.find(e=>e.id===selectedId) || null;

  // Totals reflect what is on screen, so the number always matches the list.
  const totals = rows.reduce((a,e) => {
    a.all += Number(e.amount||0);
    if (e.is_billable)     a.billable += Number(e.amount||0);
    if (e.is_reimbursable) a.reimbursable += Number(e.amount||0);
    return a;
  }, { all:0, billable:0, reimbursable:0 });

  const isLocked = (e) => e.client_id && monthLocks.some(l =>
    l.user_id===e.user_id && l.client_id===e.client_id &&
    l.period===monthOf(e.expense_date) && !l.reopened_at);

  const refreshAfter = async (ok) => { if (ok) { setChecked(new Set()); await onRefresh(); } };

  const callRpc = async (label, fn, args) => {
    setBusy(true);
    const { error } = await supabase.rpc(fn, args);
    setBusy(false);
    if (error) { notifyError(`${label}: ${friendlyDbError(error)}.`); return false; }
    return true;
  };

  const submitOne   = async (e) => refreshAfter(await callRpc('Submitting expense','submit_expense',{ p_expense_id:e.id }));
  const withdrawOne = async (e) => refreshAfter(await callRpc('Withdrawing expense','withdraw_expense',{ p_expense_id:e.id }));
  const reopenOne   = async (e) => refreshAfter(await callRpc('Reopening expense','reopen_expense',{ p_expense_id:e.id }));

  const rejectOne = async (e, note) =>
    refreshAfter(await callRpc('Rejecting expense','reject_expense',{ p_expense_id:e.id, p_note:note }));

  // approve_expenses is all-or-nothing: one invalid row aborts the batch and
  // names the offender, rather than silently approving a partial set.
  // Takes ids explicitly rather than reading `checked` from the closure: the
  // detail panel approves a single row, and calling setChecked() then reading
  // `checked` in the same tick would still see the pre-update value.
  const approveIds = async (idList) => {
    const ids = idList ?? [...checked];
    if (!ids.length) return;
    setBusy(true);
    const { data, error } = await supabase.rpc('approve_expenses', { p_ids: ids });
    setBusy(false);
    if (error) { notifyError(`Approving expenses: ${friendlyDbError(error)}.`); return; }
    setChecked(new Set());
    await onRefresh();
    // Deliberately no toast on success — notifyError is the error channel, and
    // the rows flipping to "approved" is the confirmation.
  };

  // Nothing cascades from the expenses row to the bucket, so the file must be
  // removed explicitly. FILE FIRST, and the order is not arbitrary: the storage
  // policy authorizes by joining the expenses row, so once the row is gone the
  // join fails and the file can never be deleted by anyone but a service role.
  // The opposite risk — a row left pointing at a missing file — is visible and
  // recoverable, since the user can just re-attach.
  const removeOne = async (e) => {
    if (e.receipt_path) {
      const { error } = await supabase.storage.from(RECEIPT_BUCKET).remove([e.receipt_path]);
      if (error) { notifyError(`Removing the receipt file: ${friendlyDbError(error)}.`); return; }
    }
    refreshAfter(await runDb('Deleting expense', supabase.from('expenses').delete().eq('id', e.id)));
  };

  const toggleCheck = (id) => setChecked(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const selectableIds = rows.filter(e=>e.status==='submitted').map(e=>e.id);
  const allChecked = selectableIds.length>0 && selectableIds.every(id=>checked.has(id));

  return (
    <Page title="Expenses" onTour={tour.launch}
      primary={<button style={css.btn('primary')} onClick={()=>{ setEditing(null); setShowForm(true); }}>+ Log expense</button>}
      tabs={isAdmin ? { tabs: [['mine','My expenses'],['review',`Review queue${review.length?` (${review.length})`:''}`]], active: tab,
        onChange: (v)=>{ setTab(v); setStatusF(v==='review' ? 'submitted' : 'all'); setSelectedId(null); setChecked(new Set()); } } : null}
      tour={{ tabs: 'expenses-tabs', controls: 'expenses-controls', stats: 'expenses-stats' }}
      controls={<Controls
        pills={<StatusPills value={statusF} onChange={setStatusF}
          options={expensePills.map(([k,label,tone]) => { const l = statusCount(k); return { key:k, label, tone, count:l.length, total: fmtMoney(sumAmt(l)) }; })} />}
        range={<select aria-label="Period" style={{...css.sel,width:'auto'}} value={period} onChange={e=>setPeriod(e.target.value)}>
          <option value="all">All periods</option>
          {periods.map(p=>(<option key={p} value={p}>{fmtPeriod(p)}</option>))}
        </select>} />}
      stats={[
        { label:'Total', value: fmtMoney(totals.all), basis: `${rows.length} expense${rows.length===1?'':'s'} shown`,
          tip:'Everything in the current view.' },
        { label:'Billable', value: fmtMoney(totals.billable), basis:'goes on client invoices',
          tip:'Expenses billed to a client (markup applies on the invoice). One expense can be both billable and reimbursable, so Billable and Reimbursable can add up to more than Total.' },
        { label:'Reimbursable', value: fmtMoney(totals.reimbursable), basis:'repaid at cost',
          tip:"Expenses repaid to the person who paid them, on their member invoice, always at cost. One expense can be both billable and reimbursable." },
      ]}>
      {tour.show && <WalkthroughModal tourKey="expenses" onClose={tour.close} />}

      {isAdmin && tab==='review' && selectableIds.length>0 && (
        <div style={{...css.card, display:'flex', alignItems:'center', gap:'12px', padding:'12px 20px'}}>
          <label style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'13px',color:C.textDim,cursor:'pointer'}}>
            <input type="checkbox" checked={allChecked}
              onChange={()=>setChecked(allChecked ? new Set() : new Set(selectableIds))} />
            Select all {selectableIds.length} submitted
          </label>
          <div style={{marginLeft:'auto',display:'flex',gap:'8px',alignItems:'center'}}>
            <span style={{fontSize:'12px',color:C.muted}}>{checked.size} selected</span>
            <button style={{...css.btn('success'), opacity: (checked.size&&!busy)?1:0.5}}
              disabled={!checked.size||busy} onClick={()=>approveIds()}>
              {busy?'Working…':`Approve ${checked.size||''}`}
            </button>
          </div>
        </div>
      )}

      <div style={{display:'flex',gap:'16px',alignItems:'flex-start'}} data-tour="expenses-table">
        <div style={{...css.card, flex:1, overflowX:'auto', minWidth:0}}>
          {rows.length===0 ? (
            <div style={{color:C.muted,fontSize:'13px',padding:'20px 0',textAlign:'center'}}>
              {tab==='review' ? 'Nothing waiting for review.' : 'No expenses yet. Log one to get started.'}
            </div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>
                {isAdmin && tab==='review' && <th style={{...css.th,width:'32px'}}></th>}
                <th style={css.th}>Date</th>
                {isAdmin && tab==='review' && <th style={css.th}>Who</th>}
                <th style={css.th}>Client</th>
                <th style={css.th}>Category</th>
                <th style={css.th}>Details</th>
                <th style={{...css.th,textAlign:'right'}}>Amount</th>
                <th style={css.th}>Routing</th>
                <th style={css.th}>Status</th>
              </tr></thead>
              <tbody>
                {rows.map(e=>(
                  <tr key={e.id}
                      onClick={()=>setSelectedId(e.id===selectedId?null:e.id)}
                      style={{cursor:'pointer', background: e.id===selectedId?`${C.accent}11`:'transparent'}}>
                    {isAdmin && tab==='review' && (
                      <td style={css.td} onClick={ev=>ev.stopPropagation()}>
                        <input type="checkbox" checked={checked.has(e.id)} onChange={()=>toggleCheck(e.id)} />
                      </td>
                    )}
                    <td style={css.td}>{e.expense_date}</td>
                    {isAdmin && tab==='review' && <td style={css.td}>{userName(e.user_id)}</td>}
                    <td style={css.td}>{e.client_id?clientName(e.client_id):<span style={{color:C.muted}}>Internal</span>}</td>
                    <td style={css.td}>{catName(e.category_id)}</td>
                    <td style={css.td}>
                      {e.receipt_path && (
                        <span title={e.receipt_name||'Receipt attached'}
                              style={{marginRight:'6px'}}>📎</span>
                      )}
                      {e.entry_type==='mileage'
                        ? `${e.miles} mi @ ${fmtMileRate(e.mileage_rate)}/mi`
                        : (e.vendor || e.description || '—')}
                      {isLocked(e) && <span style={{...css.badge('red'),marginLeft:'6px'}}>locked period</span>}
                    </td>
                    <td style={{...css.td,textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmtCents(e.amount)}</td>
                    <td style={css.td}>
                      {e.is_billable     && <span style={{...css.badge('green'),marginRight:'4px'}}>bill</span>}
                      {e.is_reimbursable && <span style={css.badge('amber')}>reimb</span>}
                      {!e.is_billable && !e.is_reimbursable && <span style={{color:C.muted,fontSize:'12px'}}>overhead</span>}
                    </td>
                    <td style={css.td}><span style={css.badge(EXP_BADGE[e.status])}>{e.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selected && (
          <ExpenseDetail
            exp={selected} isAdmin={isAdmin} profile={profile} busy={busy}
            clientName={clientName} catName={catName} userName={userName}
            onClose={()=>setSelectedId(null)}
            onEdit={()=>{ setEditing(selected); setShowForm(true); }}
            onSubmit={()=>submitOne(selected)}
            onWithdraw={()=>withdrawOne(selected)}
            onApprove={()=>approveIds([selected.id])}
            onReject={(note)=>rejectOne(selected, note)}
            onReopen={()=>reopenOne(selected)}
            onDelete={()=>{ removeOne(selected); setSelectedId(null); }}
          />
        )}
      </div>

      {showForm && (
        <ExpenseForm
          existing={editing} org={org} profile={profile} isAdmin={isAdmin}
          clients={clients} categories={categories} monthLocks={monthLocks}
          onRefresh={onRefresh}
          onClose={()=>{ setShowForm(false); setEditing(null); }}
          onSaved={async()=>{ setShowForm(false); setEditing(null); await onRefresh(); }}
        />
      )}
    </Page>
  );
}

// Signed URLs expire after 5 minutes, so they are minted per view rather than
// stored. An admin reviewing a queue needs to see the receipt inline — making
// them click through to a new tab for every row is the wrong trade.
function ReceiptPreview({ exp }) {
  const [url, setUrl]         = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    signedReceiptUrl(exp.receipt_path).then(u => {
      if (canceled) return;
      setUrl(u); setLoading(false);
    });
    return () => { canceled = true; };
  }, [exp.receipt_path]);

  const viewable = isPreviewableImage(exp.receipt_mime);

  return (
    <div style={{marginBottom:'12px'}}>
      <div style={css.lbl}>Receipt</div>
      {loading ? (
        <div style={{fontSize:'12px',color:C.muted}}>Loading…</div>
      ) : !url ? (
        <div style={{fontSize:'12px',color:C.badgeRed}}>Could not load this receipt.</div>
      ) : viewable ? (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{display:'block'}}>
          <img src={url} alt={exp.receipt_name||'Receipt'}
               style={{width:'100%',maxHeight:'220px',objectFit:'contain',background:C.bg,
                       border:`1px solid ${C.border}`,borderRadius:'6px'}} />
          <div style={{fontSize:'11px',color:C.muted,marginTop:'4px'}}>Click to open full size</div>
        </a>
      ) : (
        <a href={url} target="_blank" rel="noopener noreferrer"
           style={{display:'flex',gap:'10px',alignItems:'center',background:C.bg,
                   border:`1px solid ${C.border}`,borderRadius:'6px',padding:'10px',
                   textDecoration:'none'}}>
          <span style={{fontSize:'20px'}}>📄</span>
          <span style={{flex:1,minWidth:0}}>
            <span style={{display:'block',fontSize:'13px',color:C.accent,overflow:'hidden',
                          textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
              {exp.receipt_name || 'Receipt'}
            </span>
            <span style={{fontSize:'11px',color:C.muted}}>
              {((exp.receipt_size||0)/1024).toFixed(0)} KB · opens in a new tab
            </span>
          </span>
        </a>
      )}
    </div>
  );
}

function ExpenseDetail({ exp, isAdmin, profile, busy, clientName, catName, userName,
                         onClose, onEdit, onSubmit, onWithdraw, onApprove, onReject, onReopen, onDelete }) {
  const isOwner  = exp.user_id === profile.id;
  const editable = isOwner && ['draft','rejected'].includes(exp.status);
  const claimed  = exp.billed_invoice_id || exp.reimbursed_statement_id;

  const [rejecting, setRejecting]         = useState(false);
  const [note, setNote]                   = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Reset the inline confirm states when a different row is selected, or the
  // panel would open mid-reject for an expense the admin never clicked.
  // Status is in the deps too: after a reject succeeds the id is unchanged, so
  // keying on id alone leaves the note form open over the finished result.
  useEffect(() => {
    setRejecting(false); setNote(''); setConfirmDelete(false);
  }, [exp.id, exp.status]);

  return (
    <div style={{...css.card, width:'320px', minWidth:'320px', marginBottom:0}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'14px'}}>
        <span style={css.badge(EXP_BADGE[exp.status])}>{exp.status}</span>
        <button style={css.smBtn()} onClick={onClose}>Close</button>
      </div>

      <div style={{fontSize:'24px',fontWeight:'700',color:C.textBright,marginBottom:'4px',fontVariantNumeric:'tabular-nums'}}>
        {fmtCents(exp.amount)}
      </div>
      <div style={{fontSize:'12px',color:C.muted,marginBottom:'16px'}}>
        {exp.currency} · {exp.expense_date}
      </div>

      {!isOwner && <ExpRow label="Submitted by">{userName(exp.user_id)}</ExpRow>}
      <ExpRow label="Client">{exp.client_id ? clientName(exp.client_id) : 'Internal / overhead'}</ExpRow>
      <ExpRow label="Category">{catName(exp.category_id)}</ExpRow>
      {exp.entry_type==='mileage'
        ? <ExpRow label="Mileage">{exp.miles} mi @ {fmtMileRate(exp.mileage_rate)}/mi</ExpRow>
        : <ExpRow label="Vendor">{exp.vendor||'—'}</ExpRow>}
      {exp.description && <ExpRow label="Description">{exp.description}</ExpRow>}

      <ExpRow label="Routing">
        {exp.is_billable     && <span style={{...css.badge('green'),marginRight:'4px'}}>billable</span>}
        {exp.is_reimbursable && <span style={css.badge('amber')}>reimbursable</span>}
        {!exp.is_billable && !exp.is_reimbursable && 'Overhead — neither billed nor reimbursed'}
      </ExpRow>

      {exp.is_billable && Number(exp.markup_percent)>0 && (
        <ExpRow label="Client is billed">
          {fmtCents(Number(exp.amount)*(1+Number(exp.markup_percent)/100))}
          <span style={{color:C.muted}}> ({exp.markup_percent}% markup)</span>
          <div style={{fontSize:'11px',color:C.muted,marginTop:'2px'}}>
            Reimbursement stays at {fmtCents(exp.amount)} — markup is margin, not pay.
          </div>
        </ExpRow>
      )}

      {exp.receipt_path && <ReceiptPreview exp={exp} />}

      {exp.status==='rejected' && exp.rejection_note && (
        <div style={{background:'rgba(229,62,62,0.1)',border:`1px solid ${C.danger}`,borderRadius:'6px',padding:'10px',marginBottom:'12px'}}>
          <div style={{...css.lbl,color:C.badgeRed}}>Rejected</div>
          <div style={{fontSize:'13px',color:C.text}}>{exp.rejection_note}</div>
        </div>
      )}

      {claimed && (
        <div style={{background:`${C.accent}14`,border:`1px solid ${C.accent}`,borderRadius:'6px',padding:'10px',marginBottom:'12px',fontSize:'12px',color:C.textDim}}>
          {exp.billed_invoice_id && <div>Billed on an invoice.</div>}
          {exp.reimbursed_statement_id && <div>Included on a contractor statement.</div>}
          <div style={{color:C.muted,marginTop:'4px'}}>Remove it there before reopening.</div>
        </div>
      )}

      <div style={{...css.div}} />

      {rejecting ? (
        <div>
          <div style={css.lbl}>Why is this being rejected?</div>
          <textarea
            style={{...css.inp, minHeight:'70px', resize:'vertical', fontFamily:'Inter,sans-serif'}}
            value={note} autoFocus onChange={e=>setNote(e.target.value)}
            placeholder="The submitter sees this, so say what needs fixing." />
          <div style={{display:'flex',gap:'8px',marginTop:'10px'}}>
            <button style={css.btn('danger')} disabled={!note.trim()||busy}
                    onClick={()=>onReject(note.trim())}>Confirm Reject</button>
            <button style={css.btn('ghost')} onClick={()=>{ setRejecting(false); setNote(''); }}>Cancel</button>
          </div>
        </div>
      ) : confirmDelete ? (
        <div>
          <div style={{fontSize:'13px',color:C.text,marginBottom:'10px'}}>
            Delete this expense? This cannot be undone.
          </div>
          <div style={{display:'flex',gap:'8px'}}>
            <button style={css.btn('danger')} onClick={onDelete}>Yes, delete</button>
            <button style={css.btn('ghost')} onClick={()=>setConfirmDelete(false)}>Keep it</button>
          </div>
        </div>
      ) : (
        <div style={{display:'flex',flexWrap:'wrap',gap:'8px'}}>
          {editable && <button style={css.btn('ghost')} onClick={onEdit}>Edit</button>}
          {editable && <button style={css.btn()} disabled={busy} onClick={onSubmit}>Submit</button>}
          {isOwner && exp.status==='submitted' && <button style={css.btn('ghost')} disabled={busy} onClick={onWithdraw}>Withdraw</button>}
          {isAdmin && exp.status==='submitted' && <button style={css.btn('success')} disabled={busy} onClick={onApprove}>Approve</button>}
          {isAdmin && exp.status==='submitted' && <button style={css.btn('danger')} disabled={busy} onClick={()=>setRejecting(true)}>Reject</button>}
          {isAdmin && exp.status==='approved' && !claimed && <button style={css.btn('ghost')} disabled={busy} onClick={onReopen}>Reopen</button>}
          {editable && <button style={css.btn('danger')} onClick={()=>setConfirmDelete(true)}>Delete</button>}
        </div>
      )}
    </div>
  );
}

function ExpenseForm({ existing, org, profile, isAdmin, clients=[], categories=[], monthLocks=[], onRefresh, onClose, onSaved }) {
  const [entryType, setEntryType] = useState(existing?.entry_type || 'receipt');
  const [date, setDate]           = useState(existing?.expense_date || new Date().toISOString().slice(0,10));
  const [clientId, setClientId]   = useState(existing?.client_id || '');
  const [categoryId, setCategoryId] = useState(existing?.category_id || '');
  const [vendor, setVendor]       = useState(existing?.vendor || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [amount, setAmount]       = useState(existing?.amount != null ? String(existing.amount) : '');
  const [currency, setCurrency]   = useState(existing?.currency || org?.currency || 'USD');
  const [miles, setMiles]         = useState(existing?.miles != null ? String(existing.miles) : '');
  const [billable, setBillable]   = useState(existing?.is_billable ?? false);
  const [reimb, setReimb]         = useState(existing?.is_reimbursable ?? true);
  const [markup, setMarkup]       = useState(String(existing?.markup_percent ?? org?.expense_default_markup ?? 0));
  const [rate, setRate]           = useState(existing?.mileage_rate ?? null);
  const [rateLoading, setRateLoading] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState('');
  const [catMode, setCatMode]     = useState(false);
  const [newCat, setNewCat]       = useState('');
  const [addingCat, setAddingCat] = useState(false);
  const [file, setFile]           = useState(null);          // pending upload
  const [filePreview, setFilePreview] = useState(null);      // object URL, local only
  const [dropReceipt, setDropReceipt] = useState(false);     // remove the stored one on save
  const [scanning, setScanning]   = useState(false);
  const [scan, setScan]           = useState(null);          // last parse result
  const fileInputRef              = useRef(null);

  // Object URLs leak until revoked.
  useEffect(() => () => { if (filePreview) URL.revokeObjectURL(filePreview); }, [filePreview]);

  const pickFile = async (raw) => {
    // Validate the original: a 12MB HEIC that shrinks to 400KB should still be
    // accepted, so normalize first and check the result.
    const f = await normalizeReceiptImage(raw);
    const problem = receiptFileError(f);
    if (problem) { setErr(problem); return; }
    setErr('');
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFile(f);
    setFilePreview(isPreviewableImage(f.type) ? URL.createObjectURL(f) : null);
    setDropReceipt(false);
  };

  const clearPickedFile = () => {
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFile(null); setFilePreview(null); setScan(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // The parse only ever PRE-FILLS. Nothing is written without the user seeing
  // the numbers first, because a misread total flows onto a client invoice.
  // Anything the model returns null for is left alone rather than blanked.
  const scanReceipt = async () => {
    if (!file) return;
    setScanning(true); setScan(null); setErr('');
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload  = () => resolve(String(r.result).split(',')[1]);
        r.onerror = () => reject(r.error);
        r.readAsDataURL(file);
      });

      const { data, error } = await supabase.functions.invoke('parse-receipt', {
        body: { imageBase64: b64, mimeType: file.type, categories: categories.map(c=>c.name) },
      });
      if (error)      { notifyError(`Scanning receipt: ${error.message}`); return; }
      if (data?.error){ notifyError(`Scanning receipt: ${data.error}`);    return; }

      setScan(data);
      if (data.legible === false) { setErr('That image was too unclear to read. Enter the details by hand.'); return; }

      if (data.vendorName)      setVendor(data.vendorName);
      if (typeof data.total === 'number') setAmount(String(data.total));
      if (data.transactionDate && /^\d{4}-\d{2}-\d{2}$/.test(data.transactionDate)) setDate(data.transactionDate);
      if (data.currencyCode && /^[A-Z]{3}$/.test(data.currencyCode)) setCurrency(data.currencyCode);
      const match = categories.find(c => c.name === data.suggestedCategory);
      if (match) setCategoryId(match.id);
    } catch (e) {
      notifyError(`Scanning receipt: ${String(e)}`);
    } finally {
      setScanning(false);
    }
  };

  const hasStoredReceipt = existing?.receipt_path && !dropReceipt;

  // The rate is resolved against the EXPENSE date, never today — a June trip
  // logged in August must price at June's rate. resolve_mileage_rate falls back
  // from any org override to the platform table, per date.
  useEffect(() => {
    if (entryType !== 'mileage' || !org?.id || !date) return;
    let canceled = false;
    setRateLoading(true);
    supabase.rpc('resolve_mileage_rate', { p_org_id: org.id, p_date: date })
      .then(({ data, error }) => {
        if (canceled) return;
        setRateLoading(false);
        if (error) { notifyError(`Looking up mileage rate: ${friendlyDbError(error)}.`); setRate(null); }
        else setRate(data);
      });
    return () => { canceled = true; };
  }, [entryType, date, org?.id]);

  const computedAmount = entryType==='mileage'
    ? (Number(miles||0) * Number(rate||0))
    : Number(amount||0);

  const threshold   = Number(org?.expense_require_receipt_over ?? 0);
  const overThreshold = entryType==='receipt' && computedAmount > threshold;
  const selectedClientName = clients.find(c=>c.id===clientId)?.name || '';

  const periodLocked = clientId && monthLocks.some(l =>
    l.user_id===profile.id && l.client_id===clientId &&
    l.period===monthOf(date) && !l.reopened_at);

  // Inline, not window.prompt(). Browser dialogs are blocked outright in
  // sandboxed and embedded contexts — the rejection there is unhandled and
  // takes down the whole React tree.
  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    setAddingCat(true);
    const { data, error } = await supabase.from('expense_categories')
      .insert({ org_id: org.id, name }).select().single();
    setAddingCat(false);
    if (error) { notifyError(`Adding category: ${friendlyDbError(error)}.`); return; }
    await onRefresh();          // refresh the list rather than mutating the prop array
    setCategoryId(data.id);
    setNewCat('');
    setCatMode(false);
  };

  const save = async () => {
    setErr('');
    if (!date) { setErr('Pick a date.'); return; }
    if (billable && !clientId) { setErr('A billable expense needs a client — otherwise it can never reach an invoice.'); return; }
    if (entryType==='mileage') {
      if (!Number(miles)) { setErr('Enter the distance traveled.'); return; }
      if (rate == null)   { setErr('No mileage rate covers this date. An admin needs to add one in Settings, Billing & invoices, Expenses, before mileage can be logged.'); return; }
    } else if (!(computedAmount > 0)) {
      setErr('Enter an amount greater than zero.'); return;
    }
    if (periodLocked) { setErr('That month is already closed for this client. Ask an admin to reopen it.'); return; }

    const payload = {
      org_id: org.id,
      user_id: existing?.user_id || profile.id,
      client_id: clientId || null,
      category_id: categoryId || null,
      entry_type: entryType,
      expense_date: date,
      vendor: entryType==='receipt' ? (vendor.trim() || null) : null,
      description: description.trim() || null,
      amount: Number(computedAmount.toFixed(2)),
      currency: /^[A-Z]{3}$/.test(currency) ? currency : 'USD',
      miles: entryType==='mileage' ? Number(miles) : null,
      mileage_rate: entryType==='mileage' ? Number(rate) : null,
      is_billable: billable,
      is_reimbursable: reimb,
      markup_percent: Number(markup||0),
    };

    setSaving(true);

    // Get the row in place first — the storage policy authorizes by joining it.
    let expenseId = existing?.id;
    if (existing) {
      const ok = await runDb('Saving expense', supabase.from('expenses').update(payload).eq('id', existing.id));
      if (!ok) { setSaving(false); return; }
    } else {
      const { data, error } = await supabase.from('expenses').insert(payload).select('id').single();
      if (error) { notifyError(`Logging expense: ${friendlyDbError(error)}.`); setSaving(false); return; }
      expenseId = data.id;
    }

    // Receipt changes are applied after the row is settled. A failure here
    // leaves a valid expense with no receipt rather than losing the entry —
    // the user is told, and can re-attach from the detail panel.
    const oldPath = existing?.receipt_path || null;
    if (file) {
      const key = receiptStorageKey(org.id, expenseId, file);
      const { error: upErr } = await supabase.storage.from(RECEIPT_BUCKET)
        .upload(key, file, { upsert:false, contentType:file.type });
      if (upErr) {
        notifyError(`The expense saved, but the receipt did not upload: ${friendlyDbError(upErr)}.`);
      } else {
        const ok = await runDb('Attaching receipt', supabase.from('expenses').update({
          receipt_path: key, receipt_name: file.name,
          receipt_size: file.size, receipt_mime: file.type,
        }).eq('id', expenseId));
        // Only bin the previous file once the new one is safely referenced.
        if (ok && oldPath) await supabase.storage.from(RECEIPT_BUCKET).remove([oldPath]);
      }
    } else if (dropReceipt && oldPath) {
      const ok = await runDb('Removing receipt', supabase.from('expenses').update({
        receipt_path:null, receipt_name:null, receipt_size:null, receipt_mime:null,
      }).eq('id', expenseId));
      if (ok) await supabase.storage.from(RECEIPT_BUCKET).remove([oldPath]);
    }

    setSaving(false);
    onSaved();
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'flex-start',
                 justifyContent:'center',padding:'40px 20px',overflowY:'auto',zIndex:1000}}
         onClick={onClose}>
      <div style={{...css.card, width:'560px', maxWidth:'100%', marginBottom:0}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'18px'}}>
          <h2 style={{...css.h1, marginBottom:0, fontSize:'17px'}}>{existing?'Edit Expense':'Log Expense'}</h2>
          <button style={css.smBtn()} onClick={onClose}>Close</button>
        </div>

        <div style={{marginBottom:'16px'}}>
          <SegmentedControl
            options={[['receipt','Purchase'],['mileage','Mileage']]}
            value={entryType}
            onChange={(v)=>{ setEntryType(v); setErr(''); }}
          />
        </div>

        <div style={css.g2}>
          <ExpField label="Date">
            <input type="date" style={css.inp} value={date} onChange={e=>setDate(e.target.value)} />
          </ExpField>
          <ExpField label="Client" hint={billable?'Required for billable expenses':'Leave blank for internal / overhead'}>
            <select style={css.sel} value={clientId}
                    onChange={e=>{
                      const v = e.target.value;
                      setClientId(v);
                      if (!v) setBillable(false);   // no client, nothing to bill to
                    }}>
              <option value="">Internal / no client</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </ExpField>
        </div>

        <ExpField label="Category">
          {catMode ? (
            <div style={{display:'flex',gap:'8px'}}>
              <input
                style={css.inp} value={newCat} autoFocus
                placeholder="New category name"
                onChange={e=>setNewCat(e.target.value)}
                onKeyDown={e=>{
                  if (e.key==='Enter') { e.preventDefault(); addCategory(); }
                  if (e.key==='Escape') { setCatMode(false); setNewCat(''); }
                }}
              />
              <button style={{...css.btn(), whiteSpace:'nowrap'}}
                      disabled={!newCat.trim()||addingCat} onClick={addCategory}>
                {addingCat?'Adding…':'Add'}
              </button>
              <button style={{...css.btn('ghost'), whiteSpace:'nowrap'}}
                      onClick={()=>{ setCatMode(false); setNewCat(''); }}>Cancel</button>
            </div>
          ) : (
            <div style={{display:'flex',gap:'8px'}}>
              <select style={css.sel} value={categoryId} onChange={e=>setCategoryId(e.target.value)}>
                <option value="">Uncategorized</option>
                {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button style={{...css.btn('ghost'), whiteSpace:'nowrap'}}
                      onClick={()=>setCatMode(true)}>+ New</button>
            </div>
          )}
        </ExpField>

        {entryType==='receipt' ? (
          <div style={css.g2}>
            <ExpField label="Vendor">
              <input style={css.inp} value={vendor} onChange={e=>setVendor(e.target.value)} placeholder="e.g. airline, hotel, taxi" />
            </ExpField>
            <ExpField label="Amount">
              <input type="number" step="0.01" min="0" style={css.inp} value={amount}
                     onChange={e=>setAmount(e.target.value)} placeholder="0.00" />
            </ExpField>
          </div>
        ) : (
          <div style={css.g2}>
            <ExpField label="Miles">
              <input type="number" step="0.1" min="0" style={css.inp} value={miles}
                     onChange={e=>setMiles(e.target.value)} placeholder="0" />
            </ExpField>
            <ExpField label="Rate for this date">
              <div style={{...css.inp, display:'flex', alignItems:'center',
                           color: rate==null ? C.badgeRed : C.text,
                           background:'transparent'}}>
                {rateLoading ? 'Looking up…' : rate==null ? 'No rate on file' : `${fmtMileRate(rate)} / mi`}
              </div>
            </ExpField>
          </div>
        )}

        {entryType==='mileage' && rate==null && !rateLoading && (
          <div style={{background:'rgba(229,62,62,0.1)',border:`1px solid ${C.danger}`,borderRadius:'6px',
                       padding:'10px',marginBottom:'14px',fontSize:'12px',color:C.text}}>
            No mileage rate covers {date}. Rates are effective-dated, so one has to start on or before
            this date. An admin can add one in Settings, Billing &amp; invoices, Expenses.
          </div>
        )}

        {entryType==='receipt' && (
          <ExpField label="Receipt">
            {/* capture="environment" opens the rear camera directly on a phone
                and is simply ignored on desktop, where this stays a file picker. */}
            <input
              ref={fileInputRef} type="file" accept="image/*,application/pdf,text/plain"
              capture="environment" style={{display:'none'}}
              onChange={e=>{ const f=e.target.files?.[0]; if (f) pickFile(f); }}
            />
            {(file || hasStoredReceipt) ? (
              <div style={{display:'flex',gap:'12px',alignItems:'center',background:C.bg,
                           border:`1px solid ${C.border}`,borderRadius:'8px',padding:'10px'}}>
                {filePreview ? (
                  <img src={filePreview} alt="Receipt preview"
                       style={{width:'54px',height:'54px',objectFit:'cover',borderRadius:'6px',
                               border:`1px solid ${C.border}`}} />
                ) : (
                  <div style={{width:'54px',height:'54px',borderRadius:'6px',background:C.surface,
                               border:`1px solid ${C.border}`,display:'flex',alignItems:'center',
                               justifyContent:'center',fontSize:'20px'}}>📄</div>
                )}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:'13px',color:C.text,overflow:'hidden',
                               textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {file ? file.name : existing.receipt_name}
                  </div>
                  <div style={{fontSize:'11px',color:C.muted}}>
                    {file
                      ? `${(file.size/1024).toFixed(0)} KB · attaches when you save`
                      : `${((existing.receipt_size||0)/1024).toFixed(0)} KB · already attached`}
                  </div>
                </div>
                <button style={css.smBtn()} onClick={()=>fileInputRef.current?.click()}>Replace</button>
                <button style={css.smBtn(C.badgeRed)}
                        onClick={()=>{ clearPickedFile(); if (existing?.receipt_path) setDropReceipt(true); }}>
                  Remove
                </button>
              </div>
            ) : null}

            {file && isPreviewableImage(file.type) && (
              <div style={{marginTop:'8px'}}>
                <button style={{...css.btn('ghost'), width:'100%'}}
                        disabled={scanning} onClick={scanReceipt}>
                  {scanning ? 'Reading receipt…' : '✨ Read this receipt for me'}
                </button>
                {scan && !scan.error && (
                  <div style={{marginTop:'8px',fontSize:'11px',color:C.muted}}>
                    {scan.mathOk === false ? (
                      <span style={{color:C.badgeAmber}}>
                        Filled in below — but subtotal + tax + tip doesn't equal the total it read,
                        so check the amount against the receipt before submitting.
                      </span>
                    ) : (
                      <span>Filled in below from the image. Check it before saving.</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {!file && !hasStoredReceipt && (
              <button style={{...css.btn('ghost'), width:'100%',
                              border:`1px dashed ${C.border}`, padding:'14px'}}
                      onClick={()=>fileInputRef.current?.click()}>
                📷 Add a receipt — photo, PDF, or screenshot
              </button>
            )}
            {overThreshold && !file && !hasStoredReceipt && (
              <div style={{fontSize:'11px',color:C.badgeAmber,marginTop:'6px'}}>
                Required at this amount before you can submit for approval.
              </div>
            )}
          </ExpField>
        )}

        <ExpField label="Description">
          <input style={css.inp} value={description} onChange={e=>setDescription(e.target.value)}
                 placeholder="What was this for? Appears on the invoice line." />
        </ExpField>

        <div style={{...css.div}} />

        {/* Asked as two plain questions rather than two checkboxes. "Billable"
            and "reimbursable" are accounting words — the user is answering
            "who pays?" twice, about two different parties, and naming the
            actual client and org is what makes that unambiguous. */}
        <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'14px',marginBottom:'14px'}}>
          <div style={{marginBottom:'14px'}}>
            <div style={{fontSize:'13px',color:C.text,marginBottom:'8px'}}>
              Should this be billed to {selectedClientName ? <strong>{selectedClientName}</strong> : 'a client'}?
            </div>
            {clientId ? (
              <>
                <SegmentedControl
                  options={[['yes','Yes'],['no','No']]}
                  value={billable?'yes':'no'}
                  onChange={v=>setBillable(v==='yes')}
                />
                <div style={{fontSize:'11px',color:C.muted,marginTop:'6px'}}>
                  {billable
                    ? `Adds a line to ${selectedClientName}'s next invoice.`
                    : `${selectedClientName} will not see this expense.`}
                </div>
              </>
            ) : (
              <div style={{fontSize:'12px',color:C.muted}}>
                Choose a client above to bill this to one.
              </div>
            )}
          </div>

          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:'14px'}}>
            <div style={{fontSize:'13px',color:C.text,marginBottom:'8px'}}>
              Does <strong>{org?.name || 'your organization'}</strong> need to pay you back for this?
            </div>
            <SegmentedControl
              options={[['yes','Yes'],['no','No']]}
              value={reimb?'yes':'no'}
              onChange={v=>setReimb(v==='yes')}
            />
            <div style={{fontSize:'11px',color:C.muted,marginTop:'6px'}}>
              {reimb
                ? 'You paid out of pocket — this is added to your next member invoice.'
                : 'Paid on a company card. Nothing is owed back to you.'}
            </div>
          </div>
        </div>

        {billable && isAdmin && (
          <ExpField label="Markup %" hint="Applies to the client invoice only. Reimbursement is always at cost.">
            <input type="number" step="1" min="0" style={{...css.inp, maxWidth:'140px'}}
                   value={markup} onChange={e=>setMarkup(e.target.value)} />
          </ExpField>
        )}

        <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'12px',marginBottom:'14px'}}>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:C.textDim}}>
            <span>Expense total</span>
            <strong style={{color:C.textBright,fontVariantNumeric:'tabular-nums'}}>{fmtCents(computedAmount)}</strong>
          </div>
          {billable && Number(markup)>0 && (
            <div style={{display:'flex',justifyContent:'space-between',fontSize:'12px',color:C.muted,marginTop:'4px'}}>
              <span>Client billed at {markup}% markup</span>
              <span style={{fontVariantNumeric:'tabular-nums'}}>{fmtCents(computedAmount*(1+Number(markup)/100))}</span>
            </div>
          )}
        </div>

        {overThreshold && !file && !hasStoredReceipt && (
          <div style={{background:'rgba(237,167,48,0.1)',border:`1px solid ${C.badgeAmber}`,borderRadius:'6px',
                       padding:'10px',marginBottom:'14px',fontSize:'12px',color:C.text}}>
            Over the {fmtCents(threshold)} receipt threshold — a receipt must be attached before this
            can be submitted for approval. You can still save it as a draft now.
          </div>
        )}

        {periodLocked && (
          <div style={{background:'rgba(229,62,62,0.1)',border:`1px solid ${C.danger}`,borderRadius:'6px',
                       padding:'10px',marginBottom:'14px',fontSize:'12px',color:C.text}}>
            {monthOf(date)} is already closed for this client. Ask an admin to reopen it before logging here.
          </div>
        )}

        {err && <div style={{color:C.badgeRed,fontSize:'13px',marginBottom:'12px'}}>{err}</div>}

        <div style={{display:'flex',gap:'8px',justifyContent:'flex-end'}}>
          <button style={css.btn('ghost')} onClick={onClose}>Cancel</button>
          <button style={css.btn()} disabled={saving} onClick={save}>
            {saving ? 'Saving…' : existing ? 'Save Changes' : 'Save Draft'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FEEDBACK ─────────────────────────────────────────────────────────────────
// Reached from Help > Feedback > "See all feedback ↗", opened in a new tab via
// #feedback — deliberately not a sidebar nav item. Read/vote/comment/submit
// only: status changes, responses, internal notes, and merges are exclusively
// Platform Admin actions, done in that separate console, never here.

const FB_BADGE = { new:'gray', planned:'blue', in_progress:'amber', shipped:'green', declined:'red' };
const FB_LABEL = { new:'New', planned:'Planned', in_progress:'In Progress', shipped:'Shipped', declined:'Declined' };

// The system-area taxonomy for feedback. Deliberately at nav-tab granularity —
// what a user submitting feedback would actually recognize, not the finer
// grouping the docs site uses. Explicit and user-chosen, not inferred from
// wherever they happened to be standing when they opened Help — a bug on
// Invoices noticed while sitting on Timer should say "Invoices," not "Timer."
const FB_AREAS = [
  'Timer', 'Time Logs', 'Expenses', 'Client Reports', 'Invoices',
  'Member Invoices', 'Team', 'My Business', 'Settings',
  'Chrome Extension', 'Mobile App', 'Integrations', 'Something else / not sure',
];

// Best-guess default for the quick-compose form in the Help panel, where the
// current nav tab is known — a starting point the user can still override,
// not a silent auto-fill.
const FB_AREA_FROM_NAV = {
  timer:'Timer', logs:'Time Logs', expenses:'Expenses', reports:'Client Reports',
  invoices:'Invoices', statements:'Member Invoices', team:'Team', business:'My Business',
  settings:'Settings',
};

function FeedbackScreen({ org, profile }) {
  const [items, setItems]       = useState([]);
  const [profiles, setProfiles] = useState({}); // user_id -> name
  const [votes, setVotes]       = useState({});  // feedback_id -> {count, mine}
  const [loading, setLoading]   = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showCompose, setShowCompose] = useState(false);

  const [statusF, setStatusF]   = useState('all');
  const [typeF, setTypeF]       = useState('all');
  const [areaF, setAreaF]       = useState('all');
  const [mineOnly, setMineOnly] = useState(false);
  const [search, setSearch]     = useState('');
  const [sortBy, setSortBy]     = useState('newest');

  const load = async () => {
    if (!org?.id) return;
    const { data: fb, error } = await supabase.from('feedback')
      .select('*').eq('org_id', org.id).order('created_at', { ascending:false });
    if (error) { notifyError(`Loading feedback: ${friendlyDbError(error)}.`); setLoading(false); return; }
    const rows = fb || [];
    setItems(rows);

    const uids = [...new Set(rows.map(r=>r.user_id).filter(Boolean))];
    if (uids.length) {
      const { data: profs } = await supabase.from('profiles').select('id,name').in('id', uids);
      setProfiles(Object.fromEntries((profs||[]).map(p=>[p.id,p.name])));
    }

    if (rows.length) {
      const ids = rows.map(r=>r.id);
      const { data: voteRows } = await supabase.from('feedback_votes').select('feedback_id,user_id').in('feedback_id', ids);
      const tally = {};
      (voteRows||[]).forEach(v => {
        if (!tally[v.feedback_id]) tally[v.feedback_id] = { count:0, mine:false };
        tally[v.feedback_id].count += 1;
        if (v.user_id === profile.id) tally[v.feedback_id].mine = true;
      });
      setVotes(tally);
    }

    // Stamped only after data has successfully loaded — stamping on
    // navigation start would clear the unread dot even if this load failed.
    await supabase.from('org_members').update({ feedback_last_viewed_at: new Date().toISOString() })
      .eq('org_id', org.id).eq('user_id', profile.id);

    setLoading(false);
  };

  useEffect(() => { load(); }, [org?.id]); // eslint-disable-line

  const toggleVote = async (item) => {
    const mine = votes[item.id]?.mine;
    if (mine) {
      const ok = await runDb('Removing vote', supabase.from('feedback_votes').delete().eq('feedback_id', item.id).eq('user_id', profile.id));
      if (ok) setVotes(v => ({ ...v, [item.id]: { count: Math.max(0,(v[item.id]?.count||1)-1), mine:false } }));
    } else {
      const ok = await runDb('Voting', supabase.from('feedback_votes').insert({ feedback_id: item.id, user_id: profile.id }));
      if (ok) setVotes(v => ({ ...v, [item.id]: { count:(v[item.id]?.count||0)+1, mine:true } }));
    }
  };

  const visible = items.filter(i => !i.duplicate_of); // merged-away items hidden, reachable only via the canonical item's pointer
  const filtered = visible
    .filter(i => statusF==='all' || i.status===statusF)
    .filter(i => typeF==='all' || i.type===typeF)
    .filter(i => areaF==='all' || i.area===areaF)
    .filter(i => !mineOnly || i.user_id===profile.id)
    .filter(i => !search.trim() || `${i.title} ${i.message}`.toLowerCase().includes(search.trim().toLowerCase()));
  const sorted = [...filtered].sort((a,b) => sortBy==='top'
    ? (votes[b.id]?.count||0) - (votes[a.id]?.count||0)
    : new Date(b.created_at) - new Date(a.created_at));

  const selected = items.find(i=>i.id===selectedId) || null;
  const counts = { all: visible.length };
  ['new','planned','in_progress','shipped','declined'].forEach(s => counts[s] = visible.filter(i=>i.status===s).length);

  return (
    <div style={css.sec}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'20px',flexWrap:'wrap',gap:'12px'}}>
        <div>
          <h1 style={{...css.h1, marginBottom:'4px'}}>Feedback</h1>
          <div style={{fontSize:'12px',color:C.muted}}>{org?.name}</div>
        </div>
        <button style={css.btn()} onClick={()=>setShowCompose(true)}>+ New Feedback</button>
      </div>

      <div style={{...css.card, display:'flex', gap:'12px', flexWrap:'wrap', alignItems:'flex-end'}}>
        <div style={{minWidth:'220px', flex:1}}>
          <div style={css.lbl}>Search</div>
          <input style={css.inp} value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search title and description…" />
        </div>
        <div style={{minWidth:'150px'}}>
          <div style={css.lbl}>Status</div>
          <select style={css.sel} value={statusF} onChange={e=>setStatusF(e.target.value)}>
            <option value="all">All ({counts.all})</option>
            {Object.entries(FB_LABEL).map(([k,lb])=><option key={k} value={k}>{lb} ({counts[k]||0})</option>)}
          </select>
        </div>
        <div style={{minWidth:'130px'}}>
          <div style={css.lbl}>Type</div>
          <select style={css.sel} value={typeF} onChange={e=>setTypeF(e.target.value)}>
            <option value="all">All types</option>
            <option value="bug">🐛 Bug</option>
            <option value="feature">💡 Feature</option>
            <option value="general">💬 General</option>
          </select>
        </div>
        <div style={{minWidth:'160px'}}>
          <div style={css.lbl}>Area</div>
          <select style={css.sel} value={areaF} onChange={e=>setAreaF(e.target.value)}>
            <option value="all">All areas</option>
            {FB_AREAS.map(a=><option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'13px',color:C.textDim,cursor:'pointer',paddingBottom:'8px'}}>
          <input type="checkbox" checked={mineOnly} onChange={e=>setMineOnly(e.target.checked)} /> Mine only
        </label>
        <div style={{marginLeft:'auto'}}>
          <SegmentedControl options={[['newest','Newest'],['top','Top']]} value={sortBy} onChange={setSortBy} />
        </div>
      </div>

      <div style={{display:'flex',gap:'16px',alignItems:'flex-start'}}>
        <div style={{...css.card, flex:1, overflowX:'auto', minWidth:0}}>
          {loading ? (
            <div style={{color:C.muted,fontSize:'13px',padding:'20px 0',textAlign:'center'}}>Loading…</div>
          ) : sorted.length===0 ? (
            <div style={{color:C.muted,fontSize:'13px',padding:'20px 0',textAlign:'center'}}>Nothing here yet. Be the first to say something.</div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>
                <th style={{...css.th,width:'50px'}}></th>
                <th style={css.th}>Title</th>
                <th style={css.th}>Type</th>
                <th style={css.th}>Area</th>
                <th style={css.th}>Status</th>
                <th style={css.th}>Date</th>
              </tr></thead>
              <tbody>
                {sorted.map(i=>(
                  <tr key={i.id} onClick={()=>setSelectedId(i.id)}
                      style={{cursor:'pointer', background: i.id===selectedId?`${C.accent}11`:'transparent'}}>
                    <td style={css.td} onClick={e=>{e.stopPropagation(); toggleVote(i);}}>
                      <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'1px',
                                   color: votes[i.id]?.mine ? C.accent : C.muted, fontWeight:'700', fontSize:'12px'}}>
                        <span>▲</span><span>{votes[i.id]?.count||0}</span>
                      </div>
                    </td>
                    <td style={css.td}>
                      {i.title}
                      {i.attachment_path && <span style={{marginLeft:'6px'}} title="Has an attachment">📎</span>}
                    </td>
                    <td style={css.td}>{i.type==='bug'?'🐛':i.type==='feature'?'💡':'💬'}</td>
                    <td style={{...css.td,color:C.muted}}>{i.area||'—'}</td>
                    <td style={css.td}><span style={css.badge(FB_BADGE[i.status])}>{FB_LABEL[i.status]}</span></td>
                    <td style={{...css.td,color:C.muted,whiteSpace:'nowrap'}}>{new Date(i.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selected && (
          <FeedbackDetail
            item={selected} profile={profile} org={org}
            profiles={profiles} vote={votes[selected.id]}
            onClose={()=>setSelectedId(null)}
            onToggleVote={()=>toggleVote(selected)}
            onRefresh={load}
          />
        )}
      </div>

      {showCompose && (
        <FeedbackCompose
          org={org} profile={profile}
          onClose={()=>setShowCompose(false)}
          onSaved={async()=>{ setShowCompose(false); await load(); }}
          onVotedExisting={async()=>{ setShowCompose(false); await load(); }}
        />
      )}
    </div>
  );
}

function FeedbackDetail({ item, profile, org, profiles, vote, onClose, onToggleVote, onRefresh }) {
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);
  const [editMessage, setEditMessage] = useState(item.message);
  const [saving, setSaving] = useState(false);
  const [attachUrl, setAttachUrl] = useState(null);

  const isOwner   = item.user_id === profile.id;
  const editable  = isOwner && !item.content_locked_at;
  const isClosed  = item.status==='shipped' || item.status==='declined';

  useEffect(() => {
    setEditing(false); setEditTitle(item.title); setEditMessage(item.message);
    supabase.from('feedback_comments').select('*, profiles(name)').eq('feedback_id', item.id)
      .order('created_at', { ascending:true })
      .then(({ data }) => setComments(data||[]));
    if (item.attachment_path) signedFeedbackAttachmentUrl(item.attachment_path).then(setAttachUrl);
    else setAttachUrl(null);
  }, [item.id]); // eslint-disable-line

  const postComment = async () => {
    if (!commentBody.trim()) return;
    setPosting(true);
    const ok = await runDb('Posting comment', supabase.from('feedback_comments')
      .insert({ feedback_id: item.id, user_id: profile.id, body: commentBody.trim() }));
    setPosting(false);
    if (ok) {
      setCommentBody('');
      const { data } = await supabase.from('feedback_comments').select('*, profiles(name)').eq('feedback_id', item.id).order('created_at', { ascending:true });
      setComments(data||[]);
    }
  };

  const saveEdit = async () => {
    if (!editTitle.trim() || !editMessage.trim()) return;
    setSaving(true);
    const ok = await runDb('Saving changes', supabase.from('feedback')
      .update({ title: editTitle.trim(), message: editMessage.trim() }).eq('id', item.id));
    setSaving(false);
    if (ok) { setEditing(false); onRefresh(); }
  };

  return (
    <div style={{...css.card, width:'360px', minWidth:'360px', marginBottom:0}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'12px'}}>
        <span style={css.badge(FB_BADGE[item.status])}>{FB_LABEL[item.status]}</span>
        <button style={css.smBtn()} onClick={onClose}>Close</button>
      </div>

      {editing ? (
        <>
          <input style={{...css.inp, fontWeight:'700', marginBottom:'8px'}} value={editTitle} onChange={e=>setEditTitle(e.target.value)} />
          <textarea style={{...css.inp, resize:'vertical', marginBottom:'8px'}} rows={4} value={editMessage} onChange={e=>setEditMessage(e.target.value)} />
          <div style={{display:'flex',gap:'8px',marginBottom:'14px'}}>
            <button style={css.btn()} disabled={saving} onClick={saveEdit}>{saving?'Saving…':'Save'}</button>
            <button style={css.btn('ghost')} onClick={()=>setEditing(false)}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div style={{fontSize:'16px',fontWeight:'700',color:C.textBright,marginBottom:'6px'}}>{item.title}</div>
          <div style={{fontSize:'13px',color:C.textDim,lineHeight:'1.5',marginBottom:'10px',whiteSpace:'pre-wrap'}}>{item.message}</div>
        </>
      )}

      <div style={{fontSize:'11px',color:C.muted,marginBottom:'12px'}}>
        {item.type==='bug'?'🐛 Bug':item.type==='feature'?'💡 Feature':'💬 General'}{item.area?` · ${item.area}`:''} · {profiles[item.user_id]||'Someone'} · {new Date(item.created_at).toLocaleDateString()}
      </div>

      {attachUrl && (
        <a href={attachUrl} target="_blank" rel="noopener noreferrer" style={{display:'block',marginBottom:'12px'}}>
          <img src={attachUrl} alt="Attachment" style={{width:'100%',maxHeight:'180px',objectFit:'contain',background:C.bg,border:`1px solid ${C.border}`,borderRadius:'6px'}} />
        </a>
      )}

      <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'14px'}}>
        <button onClick={onToggleVote} style={{
          display:'flex',alignItems:'center',gap:'6px',padding:'6px 12px',borderRadius:'6px',
          border:`1px solid ${vote?.mine?C.accent:C.border}`, background:vote?.mine?`${C.accent}18`:'transparent',
          color:vote?.mine?C.accent:C.textDim, cursor:'pointer', fontSize:'13px', fontWeight:'600'
        }}>
          ▲ {vote?.count||0} · {isClosed ? 'Request reconsideration' : 'Vote'}
        </button>
        {vote?.mine && <span style={{fontSize:'11px',color:C.muted}}>You voted</span>}
      </div>

      {item.admin_response && (
        <div style={{background:`${C.accent}14`,borderLeft:`2px solid ${C.accent}`,borderRadius:'0 6px 6px 0',padding:'10px 12px',marginBottom:'14px'}}>
          <div style={{fontSize:'10px',fontWeight:'700',color:C.accent,marginBottom:'4px'}}>
            TimeKeep's response{item.responded_at?` · ${new Date(item.responded_at).toLocaleDateString()}`:''}
          </div>
          <div style={{fontSize:'13px',color:C.text,lineHeight:'1.5',whiteSpace:'pre-wrap'}}>{item.admin_response}</div>
        </div>
      )}

      {editable && !editing && (
        <button style={{...css.btn('ghost'), marginBottom:'14px'}} onClick={()=>setEditing(true)}>Edit</button>
      )}

      <div style={{...css.div}} />
      <div style={{fontSize:'12px',fontWeight:'700',color:C.muted,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'10px'}}>
        Comments ({comments.length})
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:'10px',marginBottom:'12px',maxHeight:'220px',overflowY:'auto'}}>
        {comments.map(c=>(
          <div key={c.id} style={{fontSize:'12px'}}>
            <div style={{color:C.muted,marginBottom:'2px'}}>
              <strong style={{color:C.textDim}}>{c.profiles?.name||'Someone'}</strong> · {new Date(c.created_at).toLocaleDateString()}
            </div>
            <div style={{color:C.text,whiteSpace:'pre-wrap'}}>{c.body}</div>
          </div>
        ))}
        {comments.length===0 && <div style={{fontSize:'12px',color:C.muted}}>No comments yet.</div>}
      </div>
      <div style={{display:'flex',gap:'8px'}}>
        <input style={css.inp} value={commentBody} onChange={e=>setCommentBody(e.target.value)}
               placeholder="Add a comment…" onKeyDown={e=>e.key==='Enter'&&postComment()} />
        <button style={css.btn()} disabled={posting||!commentBody.trim()} onClick={postComment}>{posting?'…':'Post'}</button>
      </div>
    </div>
  );
}

const signedFeedbackAttachmentUrl = async (path) => {
  if (!path) return null;
  const { data, error } = await supabase.storage.from('feedback-attachments').createSignedUrl(path, 300);
  if (error) { notifyError(`Opening attachment: ${friendlyDbError(error)}.`); return null; }
  return data?.signedUrl ?? null;
};

function FeedbackCompose({ org, profile, onClose, onSaved, onVotedExisting }) {
  const [title, setTitle]     = useState('');
  const [message, setMessage] = useState('');
  const [type, setType]       = useState('general');
  const [area, setArea]       = useState('');
  const [file, setFile]       = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');
  const fileInputRef = useRef(null);
  const debounceRef  = useRef(null);

  useEffect(() => () => { if (filePreview) URL.revokeObjectURL(filePreview); }, [filePreview]);

  // "Did you mean this?" — 3-char minimum, ~350ms debounce, capped at 5.
  // Searches every status (including Declined, so a ruled-out request gets
  // its answer immediately) but excludes anything already merged away.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    const term = `${title} ${message}`.trim();
    if (term.length < 3) { setMatches([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      const { data } = await supabase.from('feedback')
        .select('id,title,status,admin_response')
        .eq('org_id', org.id).is('duplicate_of', null)
        .or(`title.ilike.%${term}%,message.ilike.%${term}%`)
        .limit(5);
      setMatches(data||[]);
      setSearching(false);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [title, message, org.id]);

  const voteExisting = async (id) => {
    const ok = await runDb('Voting', supabase.from('feedback_votes').insert({ feedback_id:id, user_id:profile.id }));
    if (ok) onVotedExisting();
  };

  const pickFile = (f) => {
    if (!f.type.startsWith('image/')) { setErr('Attachments must be an image.'); return; }
    if (f.size > 10*1024*1024) { setErr('That image is over 10MB.'); return; }
    setErr('');
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFile(f); setFilePreview(URL.createObjectURL(f));
  };

  const save = async () => {
    setErr('');
    if (!title.trim() || !message.trim()) { setErr('Give it a title and a description.'); return; }
    if (!area) { setErr('Pick which part of the system this is about.'); return; }
    setSaving(true);

    const { data: row, error } = await supabase.from('feedback')
      .insert({ org_id:org.id, user_id:profile.id, type, title:title.trim(), message:message.trim(), area })
      .select('id').single();
    if (error) { notifyError(`Submitting feedback: ${friendlyDbError(error)}.`); setSaving(false); return; }

    // Submitting counts as the first vote automatically.
    await supabase.from('feedback_votes').insert({ feedback_id: row.id, user_id: profile.id });

    if (file) {
      const key = `${org.id}/${row.id}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g,'')}`;
      const { error: upErr } = await supabase.storage.from('feedback-attachments').upload(key, file, { contentType:file.type });
      if (!upErr) {
        await supabase.from('feedback').update({
          attachment_path:key, attachment_name:file.name, attachment_size:file.size, attachment_mime:file.type,
        }).eq('id', row.id);
      } else {
        notifyError(`Feedback saved, but the attachment didn't upload: ${friendlyDbError(upErr)}.`);
      }
    }

    setSaving(false);
    onSaved();
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'flex-start',
                 justifyContent:'center',padding:'40px 20px',overflowY:'auto',zIndex:1000}} onClick={onClose}>
      <div style={{...css.card, width:'560px', maxWidth:'100%', marginBottom:0}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px'}}>
          <h2 style={{...css.h1, marginBottom:0, fontSize:'17px'}}>New Feedback</h2>
          <button style={css.smBtn()} onClick={onClose}>Close</button>
        </div>

        <div style={{marginBottom:'14px'}}>
          <SegmentedControl options={[['bug','🐛 Bug'],['feature','💡 Feature'],['general','💬 General']]} value={type} onChange={setType} />
        </div>

        <div style={css.g2}>
          <div style={{marginBottom:'12px'}}>
            <div style={css.lbl}>Title</div>
            <input style={css.inp} value={title} onChange={e=>setTitle(e.target.value)} placeholder="Short summary — e.g. Add custom invoice PDF templates" />
          </div>
          <div style={{marginBottom:'12px'}}>
            <div style={css.lbl}>Area</div>
            <select style={css.sel} value={area} onChange={e=>setArea(e.target.value)}>
              <option value="">Which part of the system?</option>
              {FB_AREAS.map(a=><option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>
        <div style={{marginBottom:'12px'}}>
          <div style={css.lbl}>Description</div>
          <textarea style={{...css.inp, resize:'vertical'}} rows={5} value={message} onChange={e=>setMessage(e.target.value)} placeholder="What's going on, or what would help?" />
        </div>

        {matches.length > 0 && (
          <div style={{background:C.bg,border:`1px solid ${C.accent}`,borderRadius:'8px',padding:'10px 12px',marginBottom:'14px'}}>
            <div style={{fontSize:'11px',fontWeight:'700',color:C.accent,marginBottom:'6px'}}>
              Already requested{searching?' …':''} — add your vote instead?
            </div>
            {matches.map(m=>(
              <div key={m.id} style={{display:'flex',alignItems:'center',gap:'8px',padding:'5px 0',fontSize:'12px'}}>
                <span style={{...css.badge(FB_BADGE[m.status]), flexShrink:0}}>{FB_LABEL[m.status]}</span>
                <span style={{flex:1,color:C.textDim,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.title}</span>
                <button style={css.smBtn(C.accent)} onClick={()=>voteExisting(m.id)}>Vote instead</button>
              </div>
            ))}
          </div>
        )}

        {type==='bug' && (
          <div style={{marginBottom:'14px'}}>
            <div style={css.lbl}>Screenshot (optional)</div>
            <input ref={fileInputRef} type="file" accept="image/*" style={{display:'none'}}
                   onChange={e=>{ const f=e.target.files?.[0]; if (f) pickFile(f); }} />
            {file ? (
              <div style={{display:'flex',gap:'10px',alignItems:'center',background:C.bg,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'8px'}}>
                <img src={filePreview} alt="preview" style={{width:'44px',height:'44px',objectFit:'cover',borderRadius:'6px'}} />
                <span style={{flex:1,fontSize:'12px',color:C.textDim}}>{file.name}</span>
                <button style={css.smBtn(C.badgeRed)} onClick={()=>{ setFile(null); if(filePreview) URL.revokeObjectURL(filePreview); setFilePreview(null); }}>Remove</button>
              </div>
            ) : (
              <button style={{...css.btn('ghost'), width:'100%', border:`1px dashed ${C.border}`}} onClick={()=>fileInputRef.current?.click()}>
                📷 Add a screenshot
              </button>
            )}
          </div>
        )}

        {err && <div style={{color:C.badgeRed,fontSize:'13px',marginBottom:'12px'}}>{err}</div>}

        <div style={{display:'flex',gap:'8px',justifyContent:'flex-end'}}>
          <button style={css.btn('ghost')} onClick={onClose}>Cancel</button>
          <button style={css.btn()} disabled={saving||!title.trim()||!message.trim()||!area} onClick={save}>{saving?'Submitting…':'Submit'}</button>
        </div>
      </div>
    </div>
  );
}

function InvoicesView({ invoices, clients, taskTypes, entries, expenses=[], org, monthLocks, qboConnected, onRefresh, onClientClick, initialFilter=null }) {
  const tour = useTour('invoices');
  // initialFilter comes from a dashboard-tile click (RevenueDashboardView's KPI
  // strip / alert badges via onNavigate('invoices', {statusFilter})) — this view
  // is conditionally rendered (`nav==='invoices' && <InvoicesView .../>`), so it
  // remounts fresh every time the user navigates here, and this initializer runs
  // again each time. A plain nav-bar click passes no opts, so it falls through to
  // the same heuristic as before.
  const [statusFilter, setStatusFilter] = useState(() => {
    if (initialFilter?.statusFilter) return initialFilter.statusFilter;
    if (invoices.some(i => i.status === 'sent')) return 'unpaid';
    if (invoices.some(i => i.status === 'draft')) return 'draft';
    return 'all';
  });
  const [clientFilter, setClientFilter] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [showEditor, setShowEditor]     = useState(false);
  const [editInvoice, setEditInvoice]   = useState(null);
  const [actionToast, setActionToast]   = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [sendEmailTarget, setSendEmailTarget] = useState(null);
  const [showByClient, setShowByClient] = useState(false);
  const [donationBusy, setDonationBusy] = useState(null);
  const [unmarkPaidTarget, setUnmarkPaidTarget] = useState(null);
  const [revenueChartOpen, setRevenueChartOpen] = useState(true);
  const [panelId, setPanelId] = useState(null); // invoice open in the detail panel

  // O(1) client lookups — avoids repeated .find() in filter/sort hot paths
  const clientMap = useMemo(() => Object.fromEntries(clients.map(c => [c.id, c])), [clients]);

  // Fire-and-forget QBO invoice push; no-ops if QBO not connected (edge fn handles gracefully)
  const syncInvoiceToQbo = (invoiceId) => {
    if (!org?.id || !invoiceId) return;
    supabase.functions.invoke('qbo-sync-invoice', { body: { org_id: org.id, invoice_id: invoiceId } })
      .then(({ data, error }) => {
        if (error || !data?.ok) console.warn('QBO invoice sync:', error?.message || data?.error);
      });
  };

  // Fire-and-forget QBO payment record — called when manually marking an invoice paid in TimeKeep.
  // Edge fn fetches current QBO Balance first; skips if already 0 (prevents loop with incoming webhook).
  const markPaidInQbo = (invoiceId) => {
    if (!org?.id || !invoiceId) return;
    supabase.functions.invoke('qbo-mark-paid', { body: { org_id: org.id, invoice_id: invoiceId } })
      .then(({ data, error }) => {
        if (error || !data?.ok) console.warn('QBO mark-paid:', error?.message || data?.error);
      });
  };

  // Date range filters (available on all tabs)
  const [dateRange, setDateRange]   = useState('all');
  const [dateField, setDateField]   = useState('issued_date');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');

  const toast = (msg, ms = 4500) => { setActionToast(msg); setTimeout(()=>setActionToast(''), ms); };

  // Compute date bounds for the selected range
  const dateBounds = (() => {
    if (dateRange === 'all') return null;
    const now = new Date();
    const today = now.toISOString().slice(0,10);
    const y = now.getFullYear();
    if (dateRange === 'this_month') {
      return { start: `${y}-${String(now.getMonth()+1).padStart(2,'0')}-01`, end: today };
    }
    if (dateRange === 'last_3') {
      const d = new Date(now); d.setMonth(d.getMonth()-3);
      return { start: d.toISOString().slice(0,10), end: today };
    }
    if (dateRange === 'last_6') {
      const d = new Date(now); d.setMonth(d.getMonth()-6);
      return { start: d.toISOString().slice(0,10), end: today };
    }
    if (dateRange === 'last_12') {
      const d = new Date(now); d.setFullYear(d.getFullYear()-1);
      return { start: d.toISOString().slice(0,10), end: today };
    }
    if (dateRange === 'ytd')       return { start: `${y}-01-01`, end: today };
    if (dateRange === 'last_year') return { start: `${y-1}-01-01`, end: `${y-1}-12-31` };
    if (dateRange === 'custom')    return { start: customStart||'', end: customEnd||today };
    return null;
  })();

  const DATE_RANGE_LABELS = {
    all:'All Time', this_month:'This Month', last_3:'Last 3 Months',
    last_6:'Last 6 Months', last_12:'Last 12 Months',
    ytd:'Year to Date', last_year:'Last Year', custom:'Custom Range',
  };
  const DATE_FIELD_LABELS = { issued_date:'Issued Date', due_date:'Due Date', created_at:'Created Date' };

  const today = new Date().toISOString().slice(0,10);

  // Search, client and date scope (the controls row). Status is applied on top.
  function inScopeInvoice(inv) {
    if (clientFilter && inv.client_id !== clientFilter) return false;
    if (dateBounds) {
      const val = dateField === 'created_at' ? inv.created_at?.slice(0,10) : inv[dateField];
      if (!val) return false;
      if (dateBounds.start && val < dateBounds.start) return false;
      if (dateBounds.end   && val > dateBounds.end)   return false;
    }
    if (invoiceSearch.trim()) {
      const q = invoiceSearch.toLowerCase();
      const cl = clientMap[inv.client_id];
      if (!(cl?.name||'').toLowerCase().includes(q) && !(cl?.alias||'').toLowerCase().includes(q) && !inv.invoice_number?.toLowerCase().includes(q)) return false;
    }
    return true;
  }
  const filtered = invoices.filter(inv => {
    if (statusFilter === 'unpaid' && inv.status !== 'sent') return false;
    if (statusFilter === 'overdue' && !(inv.status === 'sent' && inv.due_date && inv.due_date < today)) return false;
    if (statusFilter !== 'all' && statusFilter !== 'unpaid' && statusFilter !== 'overdue' && inv.status !== statusFilter) return false;
    if (!inScopeInvoice(inv)) return false;
    return true;
  });

  // Newest first; the table's headers re-sort from there.
  const displayInvoices = [...filtered].sort((a, b) => (b.issued_date||'').localeCompare(a.issued_date||'') || (b.invoice_number||'').localeCompare(a.invoice_number||''));

  const nextPeriod = (period) => {
    if (!period) return null;
    const [y, m] = period.split('-').map(Number);
    const nm = m === 12 ? 1 : m + 1;
    const ny = m === 12 ? y + 1 : y;
    return `${ny}-${String(nm).padStart(2,'0')}`;
  };

  const updateStatus = async (inv, newStatus) => {
    const extraFields = {};

    if (newStatus === 'paid') {
      extraFields.paid_at = new Date().toISOString();
      // Compute donation obligation when marking a nonprofit invoice paid.
      const cl = clientMap[inv.client_id];
      if (cl?.is_nonprofit && cl?.donation_percentage > 0) {
        extraFields.donation_amount = parseFloat(inv.total || 0) * cl.donation_percentage / 100;
      }
    } else if (newStatus === 'sent' && inv.status === 'paid') {
      // If donation has already been marked paid, warn and preserve — don't auto-clear.
      if (inv.donation_paid_at) {
        setUnmarkPaidTarget(inv);
        return;
      }
      extraFields.paid_at = null;
      extraFields.donation_amount = null;
    }

    const { error } = await supabase.from('invoices').update({ status: newStatus, ...extraFields }).eq('id', inv.id);
    if (error) { toast('Error: ' + error.message); return; }

    // Sync to QBO when marking paid or sent
    if (newStatus === 'paid' || newStatus === 'sent') syncInvoiceToQbo(inv.id);
    // Record a QBO Payment when manually marking paid (skips if already paid in QBO)
    if (newStatus === 'paid') markPaidInQbo(inv.id);

    // Auto-generate next recurring draft when marked as paid
    if (newStatus === 'paid' && inv.recurring && inv.period) {
      const nextPer = nextPeriod(inv.period);
      // Skip if a draft already exists for this client + next period
      const alreadyExists = invoices.some(i =>
        i.client_id === inv.client_id && i.period === nextPer && i.status === 'draft'
      );
      if (!alreadyExists && nextPer) {
        // Number it off the period it's actually billing, not off whatever
        // moment this happens to run at -- an early payment on the prior
        // invoice used to stamp the new draft with THIS month even though
        // it bills next month.
        const { data: invNum } = await supabase.rpc('generate_invoice_number', { p_org_id: org.id, p_period_start: `${nextPer}-01` });
        if (invNum) {
          const issued = new Date().toISOString().slice(0,10);
          const terms = inv.payment_terms || org?.invoice_payment_terms || 'Net 30';
          const days = {'Due on Receipt':0,'Net 7':7,'Net 15':15,'Net 30':30,'Net 45':45,'Net 60':60}[terms] ?? 30;
          const dueD = new Date(issued + 'T12:00:00'); dueD.setDate(dueD.getDate() + days);
          // Copy line items from the paid invoice (amounts only, no time-entry links)
          // Fetch fresh from DB because loadAll no longer nests invoice_line_items
          const { data: fetchedLines } = await supabase.from('invoice_line_items').select('*').eq('invoice_id', inv.id).order('sort_order');
          const srcLines = fetchedLines || [];
          const subtotal = srcLines.reduce((s, l) => s + parseFloat(l.amount || 0), 0);
          const { data: newInv, error: newInvErr } = await supabase.from('invoices').insert({
            org_id: org.id, client_id: inv.client_id,
            invoice_number: invNum, period: nextPer,
            status: 'draft', line_item_mode: inv.line_item_mode,
            issued_date: issued, due_date: dueD.toISOString().slice(0,10),
            payment_terms: terms, subtotal, total: subtotal,
            recurring: true, bill_day: inv.bill_day,
          }).select('id').single();
          if (newInvErr || !newInv?.id) {
            notifyError('Creating next recurring draft failed: ' + (newInvErr?.message || 'unknown error'));
            toast(`Invoice ${inv.invoice_number} marked as paid.`);
            onRefresh();
            return;
          }
          if (srcLines.length) {
            await runDb('Copying line items to next draft failed', supabase.from('invoice_line_items').insert(
              srcLines.map((l, i) => ({
                invoice_id: newInv.id,
                description: l.description || '',
                hours: l.hours ?? null,
                rate: l.rate ?? null,
                amount: l.amount || 0,
                sort_order: l.sort_order ?? i,
              }))
            ));
          }
          toast(`Invoice ${inv.invoice_number} marked as paid. Next draft ${invNum} created for ${nextPer}.`);
          onRefresh();
          return;
        }
      }
    }

    toast(`Invoice ${inv.invoice_number} marked as ${newStatus}.`);
    onRefresh();
  };

  // "Mark as Sent" — status change only, no email
  const markDonated = async (inv) => {
    setDonationBusy(inv.id);
    const { error } = await supabase.from('invoices').update({ donation_paid_at: new Date().toISOString() }).eq('id', inv.id);
    setDonationBusy(null);
    if (error) { toast('Error: ' + error.message); return; }
    toast('Donation marked as paid.');
    onRefresh();
  };

  const markSentFromPreview = async (inv) => {
    await updateStatus(inv, 'sent');
    setPreviewInvoice(null);
    toast(`Invoice ${inv.invoice_number} marked as sent.`);
  };

  // Open the send-email confirmation popup
  const openSendEmail = (inv) => {
    setSendEmailTarget(inv);
  };

  // Actually fire the email (called from SendInvoiceModal on confirm)
  const sendInvoiceEmail = async (inv, message, resendApCopy = false) => {
    // Mark as sent first if still draft
    if (inv.status === 'draft') {
      await updateStatus(inv, 'sent');
    }
    const client = clients.find(c => c.id === inv.client_id);
    if (!canEmailInvoice(client)) {
      setSendEmailTarget(null);
      setPreviewInvoice(null);
      toast(`No billing contact or AP inbox email on file for ${client?.name || 'this client'} — email not sent.`);
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('send-invoice', {
        body: { invoice_id: inv.id, message: message || undefined, resend_ap_copy: resendApCopy || undefined },
      });
      if (error) {
        let detail = error.message || 'unknown error';
        try { const j = await error.context?.json?.(); if (j?.error) detail = j.error; } catch(_) {}
        toast(`Email failed: ${detail}`);
      } else if (!data?.ok) {
        toast(`Email failed: ${data?.error || 'unknown'}`);
      } else if (data.emailSent) {
        const ap = data.ap || {};
        const apTo = ap.to?.join?.(', ') || '';
        const toBilling = data.to?.length ? `emailed to ${data.to.join(', ')}` : '';
        const apNote = ap.status === 'sent' ? (toBilling ? ` PDF copy sent to AP inbox ${apTo}.` : `sent as a PDF to AP inbox ${apTo}`)
          : ap.status === 'failed' ? ` The PDF copy to the AP inbox failed (${ap.error || 'unknown error'}); resend the invoice to retry it.`
          : '';
        toast(toBilling ? `Invoice ${inv.invoice_number} ${toBilling}.${apNote}` : `Invoice ${inv.invoice_number} ${apNote}.`);
        syncInvoiceToQbo(inv.id);
        if (ap.status === 'sent') onRefresh();
      } else if (data.reason === 'ap_already_sent') {
        toast(`Nothing sent: the AP inbox already has invoice ${inv.invoice_number}. Tick "Send the PDF to the AP inbox again" to send another copy.`);
      }
    } catch (_) {
      toast(`Email could not be delivered — try again later.`);
    }
    setSendEmailTarget(null);
    setPreviewInvoice(null);
  };

  // Re-triggerable from the Invoices list for a draft that's missing its PO —
  // covers both the first request and a manual re-send if the billing
  // contact never got back to it (no automated reminder loop for now).
  const requestPO = async (inv) => {
    const { data, error } = await supabase.functions.invoke('request-po-from-contact', { body: { invoice_id: inv.id } });
    if (error || !data?.ok) {
      let detail = data?.error || error?.message || 'unknown error';
      try { const j = await error?.context?.json?.(); if (j?.error) detail = j.error; } catch(_) {}
      toast(`Requesting PO failed: ${detail}`);
      return;
    }
    toast(`PO request sent to ${data.to}.`);
    onRefresh();
  };

  const deleteInvoice = async (inv) => {
    const ok = await runDb('Deleting invoice failed', supabase.from('invoices').delete().eq('id', inv.id));
    setDeleteConfirm(null);
    if (ok) toast(`Invoice ${inv.invoice_number} deleted.`);
    onRefresh();
  };

  const toggleSkipReminders = async (inv) => {
    const { error } = await supabase.from('invoices').update({ skip_reminders: !inv.skip_reminders }).eq('id', inv.id);
    if (error) { toast('Error: ' + error.message); return; }
    toast(`Reminders ${inv.skip_reminders ? 'enabled' : 'disabled'} for ${inv.invoice_number}.`);
    onRefresh();
  };

  const daysOverdue = (inv) => {
    if (!inv.due_date || inv.status !== 'sent') return 0;
    const diff = Math.floor((new Date(today) - new Date(inv.due_date)) / 86400000);
    return diff > 0 ? diff : 0;
  };

  const invTone = (st) => st==='paid' ? 'good' : st==='sent' ? 'info' : 'neutral';
  const awaitingPO = (inv) => inv.status === 'draft' && !!inv.po_requested_at && !inv.po_request_fulfilled_at;
  const poBlocked = (inv, cl) => inv.status==='draft' && cl?.po_required && cl?.po_mode!=='blanket' && !inv.po_number;
  // Tones, not colors: the last email's delivery status as the provider reported it.
  const emailStatus = (inv) => (inv.status === 'draft' || !inv.last_email_status) ? null : ({
    delivered: { label:'Delivered', tone:'good' }, opened: { label:'Opened', tone:'info' },
    clicked: { label:'Clicked', tone:'purple' }, bounced: { label:'Bounced', tone:'danger' },
    spam_complaint: { label:'Marked spam', tone:'danger' }, delayed: { label:'Delayed', tone:'warn' },
    sent: { label:'Sent', tone:'neutral' },
  })[inv.last_email_status] || null;
  // The one most likely next step, shown as the detail panel's primary button.
  const nextAction = (inv) => {
    const cl = clientMap[inv.client_id];
    if (poBlocked(inv, cl)) return cl?.billing_contact_email
      ? { label: inv.po_requested_at ? 'Resend PO request' : 'Request PO', tone:'primary', action:()=>requestPO(inv) } : null;
    if (inv.status === 'draft') return canEmailInvoice(cl)
      ? { label:'Send invoice', tone:'primary', action:()=>openSendEmail(inv) }
      : { label:'Mark sent', tone:'primary', action:()=>updateStatus(inv,'sent') };
    if (inv.status === 'sent') return { label:'Mark paid', tone:'success', action:()=>updateStatus(inv,'paid') };
    return null;
  };
  const invActions = (inv) => {
    const cl = clientMap[inv.client_id];
    return [
      { label:'Preview', show:true, action:()=>setPreviewInvoice(inv) },
      { label:inv.status==='paid'?'View':'Edit', show:true, action:()=>{setEditInvoice(inv);setShowEditor(true);} },
      { label:'Send invoice', show:inv.status==='draft'&&canEmailInvoice(cl)&&!poBlocked(inv,cl), action:()=>openSendEmail(inv) },
      { label:inv.po_requested_at?'Resend PO request':'Request PO', show:poBlocked(inv,cl)&&!!cl?.billing_contact_email, action:()=>requestPO(inv) },
      { label:'Mark sent', show:inv.status==='draft', action:()=>updateStatus(inv,'sent') },
      { label:'Resend invoice', show:(inv.status==='sent'||inv.status==='paid')&&canEmailInvoice(cl), action:()=>openSendEmail(inv) },
      { label:'Mark paid', show:inv.status==='sent', action:()=>updateStatus(inv,'paid') },
      { label:'Unmark paid', show:inv.status==='paid', action:()=>updateStatus(inv,'sent') },
      { label:inv.skip_reminders?'Turn reminders back on':'Turn off reminders', show:inv.status!=='draft', action:()=>toggleSkipReminders(inv) },
      { label:'Delete', show:true, danger:true, action:()=>setDeleteConfirm(inv) },
    ].filter(a => a.show);
  };
  const sLabel = (s) => s==='sent'?'Unpaid':s.charAt(0).toUpperCase()+s.slice(1);

  // Everything the controls row scopes (search, client, dates) -- the status pills
  // then count within that scope, and the stat tiles follow it too.
  const inScope = invoices.filter(inScopeInvoice);
  const scopeTotal = (list) => list.reduce((a,i)=>a+parseFloat(i.total||0),0);
  const scopeOverdue = inScope.filter(i=>i.status==='sent' && i.due_date && i.due_date < today);
  const statusSets = {
    unpaid: inScope.filter(i=>i.status==='sent'),
    draft: inScope.filter(i=>i.status==='draft'),
    overdue: scopeOverdue,
    paid: inScope.filter(i=>i.status==='paid'),
    all: inScope,
  };
  const hasNonprofit = clients.some(c=>c.is_nonprofit);
  const donationsOwed = invoices.filter(i=>i.status==='paid' && parseFloat(i.donation_amount||0)>0 && !i.donation_paid_at).length;
  const onDonations = statusFilter === 'donations';

  return (
    <Page title="Invoices" onTour={tour.launch}
      primary={<button style={css.btn("primary")} onClick={()=>{setEditInvoice(null);setShowEditor(true);}}>+ New invoice</button>}
      tabs={hasNonprofit ? { tabs: [['invoices','Invoices'],['donations',`Donations${donationsOwed ? ` (${donationsOwed} owed)` : ''}`]], active: onDonations ? 'donations' : 'invoices',
        onChange: (k) => { setStatusFilter(k === 'donations' ? 'donations' : 'unpaid'); setInvoiceSearch(''); } } : null}
      tour={{ header: 'invoices-header', stats: 'invoices-summary' }}
      controls={onDonations ? null : <Controls
        pills={<StatusPills value={statusFilter} onChange={(k)=>{setStatusFilter(k);}} options={[
          { key:'unpaid',  label:'Unpaid',  count: statusSets.unpaid.length,  total: fmtMoney(scopeTotal(statusSets.unpaid)) },
          { key:'draft',   label:'Draft',   count: statusSets.draft.length,   total: fmtMoney(scopeTotal(statusSets.draft)) },
          { key:'overdue', label:'Overdue', count: statusSets.overdue.length, total: fmtMoney(scopeTotal(statusSets.overdue)), tone:'danger' },
          { key:'paid',    label:'Paid',    count: statusSets.paid.length,    total: fmtMoney(scopeTotal(statusSets.paid)) },
          { key:'all',     label:'All',     count: statusSets.all.length,     total: fmtMoney(scopeTotal(statusSets.all)) },
        ]} />}
        search={<SearchInput value={invoiceSearch} onChange={setInvoiceSearch} placeholder="Search number, client or alias…" />}
        filters={<SearchableSelect options={clients.map(c=>({value:c.id,label:c.name}))} value={clientFilter} onChange={setClientFilter} allLabel="All clients" placeholder="Search clients…" minWidth={150} />}
        range={<>
          <select aria-label="Date range" style={{...css.sel,width:"auto",padding:"5px 10px",fontSize:"13px"}} value={dateRange} onChange={e=>setDateRange(e.target.value)}>
            <option value="all">All time</option>
            <option value="this_month">This month</option>
            <option value="last_3">Last 3 months</option>
            <option value="last_6">Last 6 months</option>
            <option value="last_12">Last 12 months</option>
            <option value="ytd">Year to date</option>
            <option value="last_year">Last year</option>
            <option value="custom">Custom range…</option>
          </select>
          {dateRange !== 'all' && (
            <select aria-label="Date field" style={{...css.sel,width:"auto",padding:"5px 10px",fontSize:"13px"}} value={dateField} onChange={e=>setDateField(e.target.value)}>
              <option value="issued_date">by issue date</option>
              <option value="due_date">by due date</option>
              <option value="created_at">by created date</option>
            </select>
          )}
          {dateRange === 'custom' && (
            <>
              <input type="date" aria-label="From" style={{...css.inp,width:"auto",padding:"5px 10px",fontSize:"12px"}} value={customStart} onChange={e=>setCustomStart(e.target.value)} />
              <span style={{color:C.muted,fontSize:"12px"}}>to</span>
              <input type="date" aria-label="To" style={{...css.inp,width:"auto",padding:"5px 10px",fontSize:"12px"}} value={customEnd} onChange={e=>setCustomEnd(e.target.value)} />
            </>
          )}
        </>} />}
      stats={onDonations ? null : [
        { label:'Invoiced', value: fmtMoney(scopeTotal(inScope.filter(i=>i.status!=='draft'))),
          basis: `${inScope.filter(i=>i.status!=='draft').length} sent or paid${dateRange==='all' ? ' · all time' : ''}`,
          tip:"Sent and paid invoices in the current view. Drafts don't count: they haven't been sent and can still change." },
        { label:'Collected', value: fmtMoney(scopeTotal(statusSets.paid)), basis:'marked paid',
          tip:'Invoices in the current view that are marked paid.' },
        { label:'Outstanding', value: fmtMoney(scopeTotal(statusSets.unpaid)), basis:'sent, not yet paid',
          tip:'Sent invoices in the current view that aren\'t marked paid yet.' },
        { label:'Overdue', value: fmtMoney(scopeTotal(scopeOverdue)), tone: scopeOverdue.length ? 'danger' : undefined,
          basis: `${scopeOverdue.length} invoice${scopeOverdue.length===1?'':'s'} past due`,
          tip:'Sent, unpaid invoices whose due date has passed.' },
      ]}>
      {tour.show && <WalkthroughModal tourKey="invoices" onClose={tour.close} />}

      {actionToast && (
        <div style={{padding:"12px 16px",background:`${C.success}1a`,border:`1px solid ${C.success}4d`,borderRadius:"8px",color:C.success,fontSize:"13px"}}>
          {actionToast}
        </div>
      )}

      {/* Paid invoice summary */}
      {statusFilter === 'paid' && (() => {
        const paidTotal = filtered.reduce((s,i)=>s+parseFloat(i.total||0),0);
        const byClient = {};
        filtered.forEach(inv => {
          const cName = clients.find(c=>c.id===inv.client_id)?.name||'Unknown';
          byClient[cName] = (byClient[cName]||0) + parseFloat(inv.total||0);
        });
        const rangeLabel = DATE_RANGE_LABELS[dateRange] || 'All Time';
        const fieldLabel = dateRange !== 'all' ? ` · ${DATE_FIELD_LABELS[dateField]}` : '';
        const customLabel = dateRange === 'custom' && customStart
          ? ` (${customStart}${customEnd?' → '+customEnd:''})`
          : '';
        return (
          <div style={{...css.card,marginBottom:"20px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:"14px"}}>
              <div style={css.h2}>Paid Invoice Summary</div>
              <div style={{fontSize:"11px",color:C.muted}}>{rangeLabel}{fieldLabel}{customLabel}</div>
            </div>
            {filtered.length === 0 ? (
              <div style={{color:C.muted,fontSize:"13px",padding:"8px 0"}}>No paid invoices match the current filters.</div>
            ) : (
              <>
                <div style={{display:"flex",gap:"12px",flexWrap:"wrap",marginBottom:"16px"}}>
                  <div style={css.metric}>
                    <div style={css.lbl}>Total Collected</div>
                    <div style={{...css.bigN,color:C.success}}>${Math.round(paidTotal).toLocaleString()}</div>
                  </div>
                  <div style={css.metric}>
                    <div style={css.lbl}>Invoices</div>
                    <div style={css.bigN}>{filtered.length}</div>
                  </div>
                  <div style={css.metric}>
                    <div style={css.lbl}>Clients</div>
                    <div style={{display:"flex",alignItems:"baseline",gap:"10px"}}>
                      <div style={css.bigN}>{Object.keys(byClient).length}</div>
                      <button style={{...css.smBtn(C.accent),fontSize:"11px"}} onClick={()=>setShowByClient(true)}>View breakdown →</button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Revenue by Month chart — paid tab only, always last 12 months */}
      {statusFilter === 'paid' && (() => {
        const byMonth = {};
        filtered.forEach(i => {
          const mo = i.issued_date?.slice(0,7);
          if (mo) byMonth[mo] = (byMonth[mo]||0) + parseFloat(i.total||0);
        });
        const revenueData = [...Array(12)].map((_, idx) => {
          const d = new Date();
          d.setMonth(d.getMonth() - (11 - idx));
          const mo = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
          const label = d.toLocaleString('en-US', { month: 'short' });
          return { month: label, revenue: parseFloat((byMonth[mo]||0).toFixed(2)) };
        });
        const hasData = revenueData.some(d => d.revenue > 0);
        if (!hasData) return null;
        return (
          <div style={{...css.card, marginBottom:"16px", padding:"14px 16px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom: revenueChartOpen ? "14px" : "0"}}>
              <div style={{fontSize:"12px",fontWeight:"700",color:C.textBright}}>Revenue by Month (Paid Invoices)</div>
              <button onClick={()=>setRevenueChartOpen(o=>!o)}
                style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:"16px",lineHeight:1,padding:"0 0 0 8px"}}>
                {revenueChartOpen ? '▲' : '▼'}
              </button>
            </div>
            {revenueChartOpen && (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={revenueData} margin={{top:4,right:8,left:0,bottom:0}}>
                  <XAxis dataKey="month" tick={{fill:C.muted,fontSize:11}} axisLine={false} tickLine={false} />
                  <YAxis tick={{fill:C.muted,fontSize:11}} axisLine={false} tickLine={false} width={48}
                    tickFormatter={v => v===0?'':'$'+(v>=1000?Math.round(v/1000)+'k':v)} />
                  <Tooltip
                    contentStyle={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"6px",fontSize:"12px"}}
                    labelStyle={{color:C.textBright,fontWeight:"600",marginBottom:"4px"}}
                    formatter={v => ['$'+parseFloat(v).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}),'Revenue']}
                  />
                  <Bar dataKey="revenue" fill={C.success} radius={[3,3,0,0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        );
      })()}

      {/* Overdue tab: aging kanban instead of the normal sort + list */}
      {statusFilter === 'overdue' && (() => {
        const BUCKETS = [
          { key:'d30',    label:'0–30 days',  color:C.success },
          { key:'d60',    label:'31–60 days', color:C.badgeAmber },
          { key:'d60plus',label:'60+ days',   color:C.danger },
        ];
        const columns = { d30:[], d60:[], d60plus:[] };
        filtered.forEach(inv => {
          const d = daysOverdue(inv);
          (d <= 30 ? columns.d30 : d <= 60 ? columns.d60 : columns.d60plus).push(inv);
        });
        if (filtered.length === 0) {
          return (
            <div style={css.card}>
              <div style={{textAlign:"center",color:C.muted,padding:"32px",fontSize:"13px"}}>
                Nothing overdue right now — every sent invoice is either current or already paid.
              </div>
            </div>
          );
        }
        return (
          <div style={{display:"flex",gap:"12px",flexWrap:"wrap",alignItems:"flex-start"}} data-tour="invoices-list">
            {BUCKETS.map(({key,label,color}) => {
              const invs = [...columns[key]].sort((a,b)=>daysOverdue(b)-daysOverdue(a));
              const total = invs.reduce((s,i)=>s+parseFloat(i.total||0),0);
              return (
                <div key={key} style={{flex:"1 1 260px",minWidth:"240px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:"8px",padding:"0 2px"}}>
                    <div style={{fontSize:"12px",fontWeight:"700",color}}>{label}</div>
                    <div style={{fontSize:"11px",color:C.muted}}>{invs.length} · ${Math.round(total).toLocaleString()}</div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:"8px",background:"rgba(255,255,255,0.02)",border:`1px solid ${C.border}`,borderRadius:"10px",padding:"8px",minHeight:"60px"}}>
                    {invs.length === 0 ? (
                      <div style={{fontSize:"11px",color:C.muted,textAlign:"center",padding:"12px 0"}}>None</div>
                    ) : invs.map(inv => {
                      const cl = clients.find(c=>c.id===inv.client_id);
                      const d = daysOverdue(inv);
                      return (
                        <div key={inv.id} style={{...css.card,padding:"10px 12px",cursor:"pointer"}} onClick={()=>setPreviewInvoice(inv)}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"8px"}}>
                            <div style={{minWidth:0}}>
                              <div style={{fontSize:"12px",fontWeight:"700",color:C.textBright,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{inv.invoice_number}</div>
                              <div style={{fontSize:"12px",color:C.accent,cursor:"pointer",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}
                                onClick={e=>{e.stopPropagation();cl&&onClientClick?.(cl.id);}}>{cl?.name||'—'}</div>
                            </div>
                            <span style={{...css.badge('red'),fontSize:"10px",flexShrink:0}}>{d}d</span>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:"6px"}}>
                            <span style={{fontSize:"14px",fontWeight:"700",color:C.textBright}}>${Math.round(inv.total).toLocaleString()}</span>
                            <button style={{...css.btn("success"),padding:"3px 10px",fontSize:"11px"}} onClick={e=>{e.stopPropagation();updateStatus(inv,'paid');}}>Mark Paid</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Invoice list: table + detail panel (Page System spec, Step 6) */}
      {statusFilter !== 'donations' && statusFilter !== 'overdue' && (
        <div data-tour="invoices-list">
          <DataTable
            rows={displayInvoices}
            onRowClick={(inv)=>setPanelId(inv.id)}
            selectedKey={panelId}
            columns={[
              { key:'invoice_number', label:'Invoice', sortValue:(i)=>i.invoice_number||'',
                render:(i)=><span style={{whiteSpace:'nowrap'}}>{i.invoice_number}</span> },
              { key:'client', label:'Client', sortValue:(i)=>clientMap[i.client_id]?.name||'',
                render:(i)=>clientMap[i.client_id]?.name||'—' },
              { key:'period', label:'Period', sortValue:(i)=>i.period||'',
                render:(i)=><span style={{whiteSpace:'nowrap'}}>{i.period ? fmtPeriod(i.period,{short:true}) : '—'}</span> },
              { key:'status', label:'Status', sortValue:(i)=>i.status,
                render:(i)=>(
                  <span style={{display:'inline-flex',gap:'6px',alignItems:'center',flexWrap:'wrap'}}>
                    <StatusBadge tone={invTone(i.status)}>{sLabel(i.status)}</StatusBadge>
                    {awaitingPO(i) && <StatusBadge tone="warn" title="Waiting on the billing contact to enter a PO number. The invoice sends automatically once they do.">Awaiting PO</StatusBadge>}
                    {i.status==='sent' && i.stripe_payment_pending_at && <StatusBadge tone="warn" title="Client paid by bank transfer (ACH). It clears in a few business days.">Payment pending</StatusBadge>}
                    {i.recurring && <StatusBadge tone="neutral" title="A new draft is created for the next period when this one is marked paid.">Recurring</StatusBadge>}
                  </span>
                ) },
              { key:'due', label:'Due', sortValue:(i)=>i.due_date||'',
                render:(i)=>{ const d = fmtDue(i, today); return <span style={{whiteSpace:'nowrap',color: d.tone==='danger' ? C.danger : d.tone==='good' ? C.success : C.textDim}}>{d.text}</span>; } },
              { key:'email', label:'Email', tip:'Delivery status of the last email for this invoice, as reported by the email provider. Many email clients block open tracking, so "Delivered" doesn\'t mean unread.',
                sortValue:(i)=>i.last_email_status||'',
                render:(i)=>{ const e = emailStatus(i); return e ? <StatusBadge tone={e.tone}>{e.label}</StatusBadge> : <span style={{color:C.muted}}>—</span>; } },
              { key:'total', label:'Amount', align:'right', sortValue:(i)=>parseFloat(i.total||0),
                render:(i)=><span style={{color:C.textBright,fontWeight:'600'}}>{fmtMoney(i.total)}</span> },
            ]}
            rowActions={(inv)=>invActions(inv).map(a=>({ label:a.label, danger:a.danger, onClick:a.action }))}
            empty={<EmptyState title={invoices.length === 0 ? 'No invoices yet' : 'No invoices match'}
              body={invoices.length === 0 ? 'Create one with + New invoice, or from Client Reports.' : 'Try another status, client or date range.'} />}
          />
        </div>
      )}

      {/* Invoice detail panel */}
      {(() => {
        const inv = panelId ? invoices.find(i => i.id === panelId) : null;
        if (!inv) return null;
        const cl = clientMap[inv.client_id];
        const next = nextAction(inv);
        const due = fmtDue(inv, today);
        const email = emailStatus(inv);
        const row = (label, value) => (
          <div style={{display:'flex',justifyContent:'space-between',gap:'12px',fontSize:'13px',padding:'6px 0',borderBottom:`1px solid ${C.border}`}}>
            <span style={{color:C.muted}}>{label}</span><span style={{color:C.textBright,textAlign:'right'}}>{value}</span>
          </div>
        );
        return (
          <DetailPanel open onClose={()=>setPanelId(null)} title={inv.invoice_number}
            badges={<StatusBadge tone={invTone(inv.status)}>{sLabel(inv.status)}</StatusBadge>}
            subtitle={<>
              <span style={{color:C.accent,cursor:'pointer'}} onClick={()=>cl&&onClientClick?.(cl.id)}>{cl?.name||'—'}</span>
              {inv.period ? ` · ${fmtPeriod(inv.period)}` : ''}
            </>}
            primary={next && <button style={css.btn(next.tone)} onClick={next.action}>{next.label}</button>}
            actions={<>
              <button style={css.btn('secondary')} onClick={()=>setPreviewInvoice(inv)}>Preview</button>
              <button style={css.btn('secondary')} onClick={()=>{setEditInvoice(inv);setShowEditor(true);}}>{inv.status==='paid'?'View':'Edit'}</button>
            </>}>
            <div style={{fontSize:'28px',fontWeight:'700',color:C.textBright,fontVariantNumeric:'tabular-nums'}}>{fmtMoney(inv.total,{cents:true})}</div>
            <div>
              {row('Issued', inv.issued_date ? fmtDate(inv.issued_date) : '—')}
              {row(inv.status==='paid' ? 'Paid' : 'Due', <span style={{color: due.tone==='danger' ? C.danger : C.textBright}}>{due.text.replace(/^(Due|Paid) (?!in |today)/,'')}</span>)}
              {inv.payment_terms && row('Terms', inv.payment_terms)}
              {row('PO number', inv.po_number || (awaitingPO(inv) ? 'Requested, waiting on the billing contact' : '—'))}
              {inv.status!=='draft' && row('Email', email ? email.label : '—')}
              {inv.ap_copy_sent_at && row('AP inbox copy', `Sent ${fmtDate(inv.ap_copy_sent_at)}`)}
              {inv.status!=='draft' && row('Reminders', inv.skip_reminders ? 'Off for this invoice' : 'On')}
              {row('Recurring', inv.recurring ? 'Yes, next draft is created when paid' : 'No')}
            </div>
            {inv.status==='draft' && !canEmailInvoice(cl) && (
              <div style={{fontSize:'12px',color:C.muted}}>{cl?.name||'This client'} has no billing contact or AP inbox email, so this invoice can be marked sent but not emailed.</div>
            )}
          </DetailPanel>
        );
      })()}

      {/* Donations panel */}
      {statusFilter === 'donations' && (() => {
        const nonprofitClients = clients.filter(c => c.is_nonprofit && c.donation_percentage > 0);
        const paidInvoices = invoices.filter(i => i.status === 'paid' && parseFloat(i.donation_amount || 0) > 0);

        if (nonprofitClients.length === 0) return (
          <div style={{color:C.muted,fontSize:"13px",padding:"32px",textAlign:"center"}}>
            No nonprofit clients configured. Enable the Nonprofit toggle on a client in Settings → Clients.
          </div>
        );

        const totalUnpaid = paidInvoices.filter(i=>!i.donation_paid_at).reduce((s,i)=>s+parseFloat(i.donation_amount||0),0);
        const totalDonated = paidInvoices.filter(i=>i.donation_paid_at).reduce((s,i)=>s+parseFloat(i.donation_amount||0),0);

        return (
          <div>
            {/* Summary strip */}
            <div style={{display:"flex",gap:"16px",marginBottom:"24px",flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:"160px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:"10px",padding:"16px 20px"}}>
                <div style={{fontSize:"11px",color:C.muted,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"4px"}}>Currently Owed</div>
                <div style={{fontSize:"22px",fontWeight:"700",color:totalUnpaid>0?C.success:C.muted}}>${totalUnpaid.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
              </div>
              <div style={{flex:1,minWidth:"160px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:"10px",padding:"16px 20px"}}>
                <div style={{fontSize:"11px",color:C.muted,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"4px"}}>Donated to Date</div>
                <div style={{fontSize:"22px",fontWeight:"700",color:C.textBright}}>${totalDonated.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
              </div>
            </div>

            {nonprofitClients.map(cl => {
              const clInvoices = paidInvoices.filter(i => i.client_id === cl.id);
              const unpaid = clInvoices.filter(i => !i.donation_paid_at);
              const donated = clInvoices.filter(i => i.donation_paid_at);
              const unpaidTotal = unpaid.reduce((s,i)=>s+parseFloat(i.donation_amount||0),0);
              if (clInvoices.length === 0) return null;
              return (
                <div key={cl.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"10px",padding:"20px",marginBottom:"16px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"14px"}}>
                    <div>
                      <div style={{fontSize:"15px",fontWeight:"700",color:C.textBright}}>{cl.name}</div>
                      <div style={{fontSize:"12px",color:C.muted,marginTop:"2px"}}>{cl.donation_percentage}% of invoice total → donation</div>
                    </div>
                    {unpaidTotal > 0 && (
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:"11px",color:C.muted,marginBottom:"2px"}}>Total Owed</div>
                        <div style={{fontSize:"18px",fontWeight:"700",color:C.success}}>${unpaidTotal.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                      </div>
                    )}
                  </div>
                  {unpaid.length > 0 && (
                    <div style={{marginBottom:"12px"}}>
                      <div style={{fontSize:"11px",color:C.muted,fontWeight:"600",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"8px"}}>Unpaid Obligations</div>
                      {unpaid.map(inv => {
                        const periodLabel = inv.period ? new Date(inv.period+'-02').toLocaleString('en-US',{month:'short',year:'numeric'}) : inv.invoice_number;
                        return (
                          <div key={inv.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:C.bg,borderRadius:"8px",marginBottom:"6px"}}>
                            <div>
                              <div style={{fontSize:"13px",color:C.textBright,fontWeight:"600"}}>{periodLabel}</div>
                              <div style={{fontSize:"11px",color:C.muted}}>{inv.invoice_number}</div>
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:"14px"}}>
                              <div style={{fontSize:"14px",fontWeight:"700",color:C.success}}>${parseFloat(inv.donation_amount||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                              <button style={css.smBtn(C.success)} disabled={donationBusy===inv.id} onClick={()=>markDonated(inv)}>
                                {donationBusy===inv.id ? 'Saving…' : 'Mark Donated'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {donated.length > 0 && (
                    <div>
                      <div style={{fontSize:"11px",color:C.muted,fontWeight:"600",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"8px"}}>Donation History</div>
                      {donated.map(inv => {
                        const periodLabel = inv.period ? new Date(inv.period+'-02').toLocaleString('en-US',{month:'short',year:'numeric'}) : inv.invoice_number;
                        const donatedDate = new Date(inv.donation_paid_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});
                        return (
                          <div key={inv.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 14px",borderRadius:"8px",opacity:0.6,marginBottom:"4px"}}>
                            <div>
                              <div style={{fontSize:"13px",color:C.textBright}}>{periodLabel} · {inv.invoice_number}</div>
                              <div style={{fontSize:"11px",color:C.muted}}>Donated {donatedDate}</div>
                            </div>
                            <div style={{fontSize:"13px",color:C.success,fontWeight:"600"}}>${parseFloat(inv.donation_amount||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {unpaid.length === 0 && donated.length === 0 && (
                    <div style={{fontSize:"13px",color:C.muted}}>No paid invoices yet.</div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Invoice editor modal */}
      {showEditor && (
        <InvoiceEditorModal
          org={org}
          clients={clients}
          taskTypes={taskTypes}
          entries={entries}
          expenses={expenses}
          monthLocks={monthLocks}
          existingInvoice={editInvoice}
          onClose={()=>setShowEditor(false)}
          onSaved={(msg, savedInvoice, savedAsSent)=>{
            setShowEditor(false);
            toast(msg);
            onRefresh();
            // Push to QBO on create/update (fire-and-forget; edge fn skips if QBO not connected)
            if (savedInvoice?.id) syncInvoiceToQbo(savedInvoice.id);
            // If saved as sent, open the pre-send email modal so the email is actually dispatched.
            // Override status to 'draft' so the modal shows "Send Invoice" not "Resend Invoice".
            if (savedAsSent && savedInvoice) {
              setSendEmailTarget({ ...savedInvoice, status: 'draft' });
            }
          }}
        />
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1001}}>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",padding:"28px",maxWidth:"380px",width:"90%"}}>
            <div style={{fontSize:"15px",fontWeight:"700",color:C.textBright,marginBottom:"8px"}}>Delete invoice?</div>
            <div style={{fontSize:"13px",color:C.muted,marginBottom:"20px"}}>
              {deleteConfirm.invoice_number} will be permanently deleted. This cannot be undone.
            </div>
            <div style={{display:"flex",gap:"10px"}}>
              <button style={css.btn()} onClick={()=>setDeleteConfirm(null)}>Cancel</button>
              <button style={css.btn("danger")} onClick={()=>deleteInvoice(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Unmark Paid — donation already recorded warning */}
      {unmarkPaidTarget && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1100}}>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",padding:"28px",maxWidth:"440px",width:"90%"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:C.textBright,marginBottom:"8px"}}>Donation already recorded</div>
            <div style={{fontSize:"13px",color:C.muted,marginBottom:"16px"}}>
              Invoice {unmarkPaidTarget.invoice_number}: the donation for this invoice was already marked as paid.
              Reversing the payment <strong style={{color:C.textBright}}>will not clear the donation record</strong> — the obligation stays in the Donations tracker until manually cleared.
            </div>
            <div style={{display:"flex",gap:"10px"}}>
              <button style={css.btn()} onClick={()=>setUnmarkPaidTarget(null)}>Keep as Paid</button>
              <button style={css.btn("danger")} onClick={async()=>{
                const inv = unmarkPaidTarget; setUnmarkPaidTarget(null);
                const { error } = await supabase.from('invoices').update({ status:'sent', paid_at:null }).eq('id',inv.id);
                if (error) { toast('Error: '+error.message); return; }
                toast(`Invoice ${inv.invoice_number} marked as sent. Donation record preserved.`);
                onRefresh();
              }}>Unmark Paid (Keep Donation)</button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice preview modal */}
      {previewInvoice && (
        <InvoicePreviewModal
          invoice={previewInvoice}
          client={clients.find(c=>c.id===previewInvoice.client_id)}
          org={org}
          onClose={()=>setPreviewInvoice(null)}
          onMarkSent={previewInvoice.status==='draft' ? ()=>markSentFromPreview(previewInvoice) : null}
          onSendEmail={canEmailInvoice(clients.find(c=>c.id===previewInvoice.client_id)) ? ()=>openSendEmail(previewInvoice) : null}
        />
      )}

      {/* By Client breakdown modal */}
      {showByClient && (() => {
        const byClient = {};
        filtered.forEach(inv => {
          const cName = clients.find(c=>c.id===inv.client_id)?.name||'Unknown';
          if (!byClient[cName]) byClient[cName] = { total: 0, count: 0, overdue: 0 };
          byClient[cName].total += parseFloat(inv.total||0);
          byClient[cName].count++;
          const d = daysOverdue(inv);
          if (d > 0) byClient[cName].overdue += parseFloat(inv.total||0);
        });
        const rows = Object.entries(byClient).sort((a,b)=>b[1].total-a[1].total);
        const grandTotal = rows.reduce((s,[,v])=>s+v.total,0);
        const statusLabel = statusFilter==='all'?'All':statusFilter==='draft'?'Draft':statusFilter==='sent'?'Sent':'Paid';
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1001}} onClick={()=>setShowByClient(false)}>
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",padding:"0",maxWidth:"560px",width:"90%",maxHeight:"80vh",display:"flex",flexDirection:"column"}} onClick={e=>e.stopPropagation()}>
              <div style={{padding:"20px 24px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
                <div style={{fontSize:"15px",fontWeight:"700",color:C.textBright}}>{statusLabel} Invoices — By Client</div>
                <div style={{display:"flex",gap:"8px"}}>
                  <button style={css.smBtn(C.textDim)} onClick={()=>{
                    const csv = ['Client,Invoices,Total,Overdue', ...rows.map(([name,v])=>`"${name}",${v.count},$${Math.round(v.total).toLocaleString()},${v.overdue>0?'$'+Math.round(v.overdue).toLocaleString():''}`), `"TOTAL",${rows.reduce((s,[,v])=>s+v.count,0)},$${Math.round(grandTotal).toLocaleString()},`].join('\n');
                    const a=document.createElement('a'); a.href='data:text/csv;charset=utf-8,'+encodeURIComponent(csv); a.download=`invoices-by-client-${statusFilter}.csv`; a.click();
                  }}>Export CSV</button>
                  <button style={css.smBtn(C.textDim)} onClick={()=>{
                    const w=window.open('','_blank');
                    w.document.write(`<html><head><title>Invoices by Client</title><style>body{font-family:Arial,sans-serif;padding:32px;max-width:600px;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:8px;border-bottom:2px solid #333;font-size:11px;text-transform:uppercase;}td{padding:8px;border-bottom:1px solid #eee;font-size:13px;}.total{font-weight:700;border-top:2px solid #333;}</style></head><body><h2>${statusLabel} Invoices — By Client</h2><table><thead><tr><th>Client</th><th>Invoices</th><th style="text-align:right">Total</th></tr></thead><tbody>${rows.map(([name,v])=>`<tr><td>${name}</td><td>${v.count}</td><td style="text-align:right">$${Math.round(v.total).toLocaleString()}</td></tr>`).join('')}<tr class="total"><td>Total</td><td>${rows.reduce((s,[,v])=>s+v.count,0)}</td><td style="text-align:right">$${Math.round(grandTotal).toLocaleString()}</td></tr></tbody></table></body></html>`);
                    w.document.close(); setTimeout(()=>w.print(),300);
                  }}>Print</button>
                  <button style={css.smBtn(C.muted)} onClick={()=>setShowByClient(false)}>✕ Close</button>
                </div>
              </div>
              <div style={{overflowY:"auto",flex:1}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr style={{background:C.bg,position:"sticky",top:0}}>
                    <th style={css.th}>Client</th>
                    <th style={{...css.th,textAlign:"center"}}>Invoices</th>
                    <th style={{...css.th,textAlign:"right"}}>Total</th>
                    {statusFilter==='sent' && <th style={{...css.th,textAlign:"right"}}>Overdue</th>}
                  </tr></thead>
                  <tbody>
                    {rows.map(([name,v])=>(
                      <tr key={name}>
                        <td style={{...css.td,color:C.textBright,fontWeight:"600"}}>{name}</td>
                        <td style={{...css.td,textAlign:"center",color:C.muted}}>{v.count}</td>
                        <td style={{...css.td,textAlign:"right",fontWeight:"700",color:C.accent}}>${Math.round(v.total).toLocaleString()}</td>
                        {statusFilter==='sent' && <td style={{...css.td,textAlign:"right",color:v.overdue>0?C.danger:C.muted}}>{v.overdue>0?'$'+Math.round(v.overdue).toLocaleString():'—'}</td>}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr style={{background:C.bg}}>
                    <td style={{...css.td,fontWeight:"700",color:C.textBright}}>Total</td>
                    <td style={{...css.td,textAlign:"center",fontWeight:"700",color:C.textBright}}>{rows.reduce((s,[,v])=>s+v.count,0)}</td>
                    <td style={{...css.td,textAlign:"right",fontWeight:"700",color:C.accent}}>${Math.round(grandTotal).toLocaleString()}</td>
                    {statusFilter==='sent' && <td style={{...css.td}} />}
                  </tr></tfoot>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Send Invoice Email confirmation modal */}
      {sendEmailTarget && (
        <SendInvoiceModal
          invoice={sendEmailTarget}
          client={clients.find(c=>c.id===sendEmailTarget.client_id)}
          onClose={()=>setSendEmailTarget(null)}
          onConfirm={(msg, resendAp)=>sendInvoiceEmail(sendEmailTarget, msg, resendAp)}
        />
      )}
    </Page>
  );
}

// ─── INVOICE EDITOR MODAL ─────────────────────────────────────────────────────
function InvoiceEditorModal({ org, clients, taskTypes, entries, expenses=[], monthLocks, existingInvoice, initialClientId, initialPeriod, initialCustomStart, initialCustomEnd, initialFilterTaskTypeId, initialFilterTagId, approvedStmts, onClose, onSaved }) {
  const isEdit = !!existingInvoice;
  const isCustomRange = !!(initialCustomStart && initialCustomEnd) && !existingInvoice;

  const [clientId, setClientId]       = useState(existingInvoice?.client_id || initialClientId || '');
  const [period, setPeriod]           = useState(existingInvoice?.period || initialPeriod || (isCustomRange ? `${initialCustomStart} to ${initialCustomEnd}` : curMonth()));
  const [lineMode, setLineMode]       = useState(existingInvoice?.line_item_mode || org?.invoice_line_item_mode || 'by_task');
  const [issuedDate, setIssuedDate]   = useState(existingInvoice?.issued_date || new Date().toISOString().slice(0,10));
  const [payTerms, setPayTerms]       = useState(existingInvoice?.payment_terms || org?.invoice_payment_terms || 'Net 30');
  const [notes, setNotes]             = useState(existingInvoice?.notes || '');
  const [recurring, setRecurring]     = useState(existingInvoice?.recurring || false);
  const [billDay, setBillDay]         = useState(existingInvoice?.bill_day || '1st');
  const [lines, setLines]             = useState([]);
  const [linesLoading, setLinesLoading] = useState(!!existingInvoice?.id);
  const [skipReminders, setSkipReminders] = useState(existingInvoice?.skip_reminders || false);
  const [paymentLink, setPaymentLink]   = useState(existingInvoice?.payment_link || '');
  const [poNumber, setPoNumber]         = useState(existingInvoice?.po_number || '');
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState('');
  const [attachments, setAttachments]   = useState([]);
  const [uploading, setUploading]       = useState(false);
  const [attachErr, setAttachErr]       = useState('');

  // Fetch line items on mount when editing an existing invoice.
  // If the saved invoice has zero line items and the client is retainer/hybrid,
  // auto-populate the retainer line so the invoice is never stuck at $0.
  useEffect(() => {
    if (!existingInvoice?.id) return;
    supabase.from('invoice_line_items').select('*').eq('invoice_id', existingInvoice.id).order('sort_order')
      .then(({ data }) => {
        const rawFetched = data || [];
        // Expense-billed rows are reconstructed separately as `expenseLines`
        // (below), driven live from the expenses table so unticking one in
        // the UI works. The raw invoice_line_items copy written at save time
        // is indistinguishable from a manual line once stored, so it has to
        // be excluded here by matching the same description format -- or
        // every edit would render the expense twice and, on save, write a
        // second permanent copy of it.
        const expenseDescriptions = new Set(
          expenses.filter(e => e.billed_invoice_id === existingInvoice.id).map(e => expenseLineFor(e).description)
        );
        const fetched = rawFetched.filter(l => !expenseDescriptions.has(l.description));
        // Fall back to auto-generated lines only when the invoice was truly
        // empty to begin with -- an invoice whose only content is expense
        // lines is a valid state, not a reason to conjure hours it never had.
        if (rawFetched.length === 0) {
          const cl = clients.find(c => c.id === existingInvoice.client_id);
          const fallback = cl ? buildAutoLines('by_task', cl, [], taskTypes) : [];
          setLines(fallback);
        } else {
          setLines(fetched);
        }
        setLinesLoading(false);
      });
  }, [existingInvoice?.id]); // eslint-disable-line

  // Fetch attachments for existing invoices
  useEffect(() => {
    if (!existingInvoice?.id) return;
    supabase.from('invoice_attachments').select('*').eq('invoice_id', existingInvoice.id).order('created_at')
      .then(({ data }) => setAttachments(data || []));
  }, [existingInvoice?.id]); // eslint-disable-line

  const uploadAttachment = async (file) => {
    if (!existingInvoice?.id) { setAttachErr('Save the invoice as a draft first, then add attachments.'); return; }
    if (file.size > 10 * 1024 * 1024) { setAttachErr(`${file.name} exceeds the 10 MB limit.`); return; }
    setUploading(true); setAttachErr('');
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${org.id}/${existingInvoice.id}/${Date.now()}_${safeName}`;
    const { error: upErr } = await supabase.storage.from('invoice-attachments').upload(path, file, { upsert: false });
    if (upErr) { setAttachErr('Upload failed: ' + upErr.message); setUploading(false); return; }
    const { data: row, error: dbErr } = await supabase.from('invoice_attachments').insert({
      invoice_id: existingInvoice.id, org_id: org.id,
      file_name: file.name, file_path: path,
      file_size: file.size, mime_type: file.type,
    }).select().single();
    if (dbErr) { setAttachErr('Metadata save failed: ' + dbErr.message); setUploading(false); return; }
    setAttachments(a => [...a, row]);
    setUploading(false);
  };

  const deleteAttachment = async (att) => {
    await supabase.storage.from('invoice-attachments').remove([att.file_path]);
    await supabase.from('invoice_attachments').delete().eq('id', att.id);
    setAttachments(a => a.filter(x => x.id !== att.id));
  };

  const downloadAttachment = async (att) => {
    const { data } = await supabase.storage.from('invoice-attachments').createSignedUrl(att.file_path, 300);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  };

  // Auto-calculate due date from issued date + payment terms
  const dueDate = (() => {
    if (!issuedDate) return '';
    const d = new Date(issuedDate + 'T12:00:00');
    const days = payTerms === 'Due on Receipt' ? 0 : payTerms === 'Net 7' ? 7 : payTerms === 'Net 15' ? 15 : payTerms === 'Net 30' ? 30 : payTerms === 'Net 45' ? 45 : payTerms === 'Net 60' ? 60 : 30;
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0,10);
  })();

  const client = clients.find(c=>c.id===clientId);
  // Blanket-PO clients carry the same PO number on every invoice — auto-fill it
  // once a client with po_mode='blanket' is picked, but never clobber a value
  // the admin already typed (e.g. a one-off exception, or an already-saved
  // invoice being edited).
  useEffect(() => {
    if (client?.po_mode === 'blanket' && client.blanket_po_number && !poNumber.trim()) {
      setPoNumber(client.blanket_po_number);
    }
  }, [client?.id]); // eslint-disable-line
  // Hybrid clients bill retainer for the current period but overage hours from
  // the PREVIOUS period (see buildAutoLines below) -- the review table and the
  // "fully locked" check need to look at the same period the invoice actually
  // bills from, or they show September's entries while the invoice bills August.
  const isHybridOverage = client?.model === 'hybrid' && !isCustomRange;
  const displayPeriod = (() => {
    if (!isHybridOverage || !period) return period;
    const [y, m] = period.split('-').map(Number);
    const pm = m === 1 ? 12 : m - 1, py = m === 1 ? y - 1 : y;
    return `${py}-${String(pm).padStart(2, '0')}`;
  })();
  const periodEntries = (isCustomRange
    ? entries.filter(e => e.client_id===clientId && e.entry_date >= initialCustomStart && e.entry_date <= initialCustomEnd && e.is_billable!==false)
    : entries.filter(e => e.client_id===clientId && e.entry_date?.slice(0,7)===displayPeriod && e.is_billable!==false)
  ).filter(e => {
    if (initialFilterTaskTypeId && e.task_type_id !== initialFilterTaskTypeId) return false;
    if (initialFilterTagId && !(e.time_entry_tags||[]).some(t => t.tag_id === initialFilterTagId)) return false;
    return true;
  });
  const allMonths = useMemo(() => { const months=[],d=new Date(); for(let i=0;i<24;i++){months.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);d.setMonth(d.getMonth()-1);} return months; },[]);

  // Check if all entries locked for this client+period (the period the entries
  // actually belong to -- displayPeriod, not the retainer's billing period)
  const isFullyLocked = periodEntries.length > 0 && periodEntries.every(e =>
    (monthLocks||[]).some(l => l.user_id===e.user_id && l.client_id===clientId && l.period===displayPeriod && !l.reopened_at)
  );

  // Auto-generate line items when client/period/mode changes (new invoice only)
  const [autoGenDone, setAutoGenDone] = useState(isEdit);
  useState(() => {}); // placeholder to keep hook count stable

  // Helper: previous month string from a 'YYYY-MM' period
  const prevMonthOf = (p) => {
    if (!p) return curMonth();
    const [y,m] = p.split('-').map(Number);
    const pm = m===1?12:m-1, py = m===1?y-1:y;
    return `${py}-${String(pm).padStart(2,'0')}`;
  };

  const buildAutoLines = (mode, cl, pEntries, pTaskTypes) => {
    if (!cl) return [];
    // Retainer clients: single flat-fee line regardless of mode
    if (cl.model === 'retainer') {
      const monthName = period ? new Date(period+'-02').toLocaleString("default",{month:"long",year:"numeric"}) : '';
      return [{ id:'_1', description:`Monthly Retainer — ${monthName}`, hours:null, rate:null, amount:cl.retainer||0, sort_order:0 }];
    }
    // Hybrid clients: retainer for current period + overage hours from previous period
    if (cl.model === 'hybrid') {
      const monthName = period ? new Date(period+'-02').toLocaleString("default",{month:"long",year:"numeric"}) : '';
      const prevPeriod = prevMonthOf(period);
      const prevLabel = new Date(prevPeriod+'-02').toLocaleString("default",{month:"long",year:"numeric"});
      // Get all billable entries for this client in the previous period
      const prevEntries = entries.filter(e => e.client_id===clientId && e.entry_date?.slice(0,7)===prevPeriod && e.is_billable!==false);
      const prevRawHrs = prevEntries.reduce((s,e)=>s+parseFloat(e.hours||0),0);
      const prevRoundedHrs = roundBillingHours(prevRawHrs);
      const includedHrs = cl.hours || 0;
      const overageHrs = Math.max(0, prevRoundedHrs - includedHrs);
      const result = [
        { id:'_1', description:`Monthly Retainer — ${monthName}`, hours:null, rate:null, amount:cl.retainer||0, sort_order:0 },
      ];
      if (overageHrs > 0) {
        result.push({ id:'_2', description:`Overage Hours — ${prevLabel} (${overageHrs.toFixed(2)}h @ $${cl.overage}/hr)`, hours:overageHrs, rate:cl.overage||0, amount:overageHrs*(cl.overage||0), sort_order:1 });
      }
      return result;
    }
    if (mode === 'manual') return [];
    if (mode === 'by_task') {
      const grouped = {};
      pEntries.forEach(e => {
        const tName = pTaskTypes.find(t=>t.id===e.task_type_id)?.name || 'General';
        if (!grouped[tName]) grouped[tName] = 0;
        grouped[tName] += parseFloat(e.hours||0);
      });
      // Round the MONTH, once, then spread it back over the task lines so they
      // still add up. Rounding each task separately billed more than the
      // documented rule and disagreed with every other screen.
      const taskNames = Object.keys(grouped);
      const rawPerTask = taskNames.map((n) => grouped[n]);
      const monthHrs = roundBillingHours(rawPerTask.reduce((a,b)=>a+b,0));
      const spread = distributeRoundedHours(rawPerTask, monthHrs);
      return taskNames.map((name,i) => {
        const rate = cl.model==='hourly' ? (cl.rate||0) : 0;
        const hrs = spread[i];
        return { id:`_${i}`, description:name, hours:hrs, rate, amount:hrs*rate, sort_order:i };
      });
    }
    if (mode === 'by_entry') {
      // This rounded NOTHING before, so it billed raw hours while by_task
      // rounded per task -- two modes, two answers, for one month's work.
      const raws = pEntries.map((e) => parseFloat(e.hours||0));
      const spread = distributeRoundedHours(raws, roundBillingHours(raws.reduce((a,b)=>a+b,0)));
      return pEntries.map((e,i) => {
        const tName = taskTypes.find(t=>t.id===e.task_type_id)?.name || '';
        const desc = e.description || tName || 'Time entry';
        const rate = cl.model==='hourly'?(cl.rate||0):0;
        const hrs = spread[i];
        return { id:`_${i}`, description:`${e.entry_date} · ${desc}`, hours:hrs, rate, amount:hrs*rate, sort_order:i };
      });
    }
    return [];
  };

  // Build line items from approved contractor statements at CLIENT billing rate.
  // Includes admin's own entries for users not covered by any approved statement.
  // Only used for hourly clients (retainer/hybrid use normal auto-lines).
  const buildApprovedLines = (stmts, cl, per) => {
    if (!cl || !stmts?.length) return buildAutoLines(lineMode, cl, periodEntries, taskTypes);
    if (cl.model !== 'hourly') return buildAutoLines(lineMode, cl, periodEntries, taskTypes);
    const clId = cl.id;
    const monthName = per ? new Date(per+'-02').toLocaleString('default',{month:'long',year:'numeric'}) : '';
    // Users whose hours are already captured in an approved statement.
    const approvedUserIds = new Set(stmts.map(s => s.user_id));
    // Sum hours from statement line_items (frozen at submission).
    let stmtHours = 0;
    stmts.forEach(stmt => {
      const items = Array.isArray(stmt.line_items) ? stmt.line_items : [];
      items.filter(li => li.client_id === clId && li.basis === 'hours').forEach(li => {
        stmtHours += parseFloat(li.quantity || 0);
      });
    });
    // Add admin's own entries for users NOT covered by any approved statement.
    const adminRawHours = periodEntries
      .filter(e => !approvedUserIds.has(e.user_id))
      .reduce((s, e) => s + parseFloat(e.hours || 0), 0);
    const totalRawHours = stmtHours + adminRawHours;
    if (totalRawHours === 0) return buildAutoLines(lineMode, cl, periodEntries, taskTypes);
    const totalHrs = roundBillingHours(totalRawHours);
    const rate = cl.rate || 0;
    return [{ id:'_approved', description:`Consulting Services — ${monthName}`, hours:totalHrs, rate, amount:totalHrs*rate, sort_order:0 }];
  };

  // Billing terms covering this period. billing_terms_for_period returns EVERY
  // overlapping row on purpose: >1 means the terms changed mid-period and this
  // invoice cannot honestly be priced from a single rate, and 0 means nothing
  // on record covers it. Both are surfaced rather than silently resolved.
  const [periodTerms, setPeriodTerms] = useState(null); // null until loaded
  useEffect(() => {
    if (!clientId || !period) { setPeriodTerms(null); return; }
    let canceled = false;
    (async () => {
      const { data, error } = await supabase.rpc('billing_terms_for_period',
        { p_client_id: clientId, p_period: period });
      if (!canceled) setPeriodTerms(error ? null : (data || []));
    })();
    return () => { canceled = true; };
  }, [clientId, period]);

  const termsBanner = (() => {
    if (!periodTerms) return null;
    if (periodTerms.length === 0) return { bad: true, text:
      `No billing terms on record cover ${period}. The amounts below come from the client's cached rate, not from an agreed billing period. Add a period in the client's Billing Terms panel before sending this.` };
    if (periodTerms.length > 1) return { bad: false, text:
      `Billing terms changed during ${period} — ${periodTerms.length} periods apply. This invoice spans a rate change, so check each line covers the right stretch before sending.` };
    return null;
  })();

  // Smart period + bill day defaults when client changes (new invoices only, not when initialPeriod was provided)
  const didSmartPeriod = useRef(false);
  useEffect(() => {
    if (isEdit || !client || initialPeriod) return;
    if (didSmartPeriod.current) return; // only run once per client change
    didSmartPeriod.current = true;
    const prevMonth = (() => { const d=new Date(); d.setMonth(d.getMonth()-1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();
    if (client.model === 'retainer') {
      const pref = org?.invoice_default_period_retainer || 'current';
      setPeriod(pref === 'current' ? curMonth() : prevMonth);
      setBillDay(org?.invoice_default_bill_day_retainer || '1st');
    } else if (client.model === 'hourly') {
      const pref = org?.invoice_default_period_hourly || 'previous';
      setPeriod(pref === 'current' ? curMonth() : prevMonth);
      setBillDay(org?.invoice_default_bill_day_hourly || '1st');
    } else if (client.model === 'hybrid') {
      setPeriod(curMonth());
      setBillDay(org?.invoice_default_bill_day_hybrid || '1st');
    }
  }, [clientId]); // eslint-disable-line

  // ── Billable expenses ───────────────────────────────────────────────────
  // Anything approved and billable for this client that no invoice has claimed
  // yet, plus whatever this invoice already claimed. Deliberately NOT limited
  // to the invoice period: an expense missed last month should still be
  // billable now, and hiding it is how it never gets billed at all. Only the
  // in-period ones are ticked by default.
  const billableExpenses = expenses.filter(e =>
    e.client_id === clientId &&
    e.status === 'approved' &&
    e.is_billable &&
    (!e.billed_invoice_id || e.billed_invoice_id === existingInvoice?.id)
  );

  const [pickedExpenses, setPickedExpenses] = useState(() => new Set());

  // Reset the selection whenever the client or period changes, otherwise ticks
  // from a previous client survive into the new one.
  useEffect(() => {
    setPickedExpenses(new Set(
      expenses.filter(e =>
        e.client_id === clientId &&
        e.status === 'approved' &&
        e.is_billable &&
        (e.billed_invoice_id === existingInvoice?.id ||
          (!e.billed_invoice_id && monthOf(e.expense_date) === period))
      ).map(e => e.id)
    ));
  }, [clientId, period, expenses, existingInvoice?.id]);

  const expenseLineFor = (e) => {
    const markup = Number(e.markup_percent || 0);
    const billed = Number(e.amount || 0) * (1 + markup / 100);
    const label  = [e.vendor, e.description].filter(Boolean).join(' — ')
                   || (e.entry_type === 'mileage' ? `Mileage · ${e.miles} mi` : 'Expense');
    return {
      id: `_exp_${e.id}`,
      _expenseId: e.id,                       // marks this line as expense-backed
      description: `${e.expense_date} · ${label}${markup ? ` (+${markup}%)` : ''}`,
      hours: null, rate: null,
      amount: Number(billed.toFixed(2)),
      sort_order: 0,
    };
  };

  const expenseLines = billableExpenses
    .filter(e => pickedExpenses.has(e.id))
    .sort((a,b) => a.expense_date.localeCompare(b.expense_date))
    .map(expenseLineFor);

  // Expenses append as their own block under ALL three line_item_modes.
  // line_item_mode governs how TIME becomes line items; expenses are not time,
  // so `manual` mode must not silently swallow them.
  const allLines = [...lines, ...expenseLines];

  // Regenerate lines when client / period / mode changes (only for new invoices)
  const prevKey = useRef('');
  useEffect(() => {
    if (isEdit) return;
    const key = `${clientId}|${period}|${lineMode}`;
    if (key === prevKey.current) return;
    prevKey.current = key;
    setLines(approvedStmts ? buildApprovedLines(approvedStmts, client, period) : buildAutoLines(lineMode, client, periodEntries, taskTypes));
  }, [clientId, period, lineMode]); // eslint-disable-line

  const subtotal = allLines.reduce((s,l)=>s+parseFloat(l.amount||0),0);

  const updateLine = (idx, field, val) => {
    setLines(prev => prev.map((l,i) => {
      if (i!==idx) return l;
      const updated = {...l, [field]:val};
      if (field==='hours' || field==='rate') {
        const h = parseFloat(field==='hours'?val:l.hours)||0;
        const r = parseFloat(field==='rate'?val:l.rate)||0;
        updated.amount = h*r;
      }
      if (field==='amount') updated.amount = parseFloat(val)||0;
      return updated;
    }));
  };

  const addLine = () => setLines(prev=>[...prev,{id:`_new${Date.now()}`,description:'',hours:null,rate:null,amount:0,sort_order:prev.length}]);
  const removeLine = (idx) => setLines(prev=>prev.filter((_,i)=>i!==idx));

  // opts.skipPOCheck / opts.requestPO exist for the "Request PO from Billing
  // Contact" action: it saves a draft with no PO number yet (that's the whole
  // point), so it can't run through the normal po_required validation, and it
  // needs po_requested_at stamped so the public PO-entry page has something
  // to key off of. Every other caller is unaffected (opts defaults to {}).
  const save = async (targetStatus, opts = {}) => {
    if (!clientId) { setErr('Select a client.'); return; }
    if (!period)   { setErr('Select a billing period.'); return; }
    if (allLines.length===0) { setErr('Add at least one line item.'); return; }
    if (!opts.skipPOCheck && client?.po_required && !poNumber.trim()) { setErr('A PO number is required for this client.'); return; }
    setSaving(true); setErr('');
    try {
      let invoiceId = existingInvoice?.id;
      let invoiceNumber = existingInvoice?.invoice_number;
      const poRequestFields = opts.requestPO ? { po_requested_at: new Date().toISOString() } : {};

      if (!isEdit) {
        // Generate invoice number atomically, off the invoice's own billing
        // period -- not off today, which is wrong the moment someone
        // prepares an invoice ahead of its period (e.g. September's invoice
        // written up in late August used to get stamped "08").
        const { data: numData, error: numErr } = await supabase.rpc('generate_invoice_number', { p_org_id: org.id, p_period_start: `${period}-01` });
        if (numErr) throw new Error(numErr.message);
        invoiceNumber = numData;

        const { data: invData, error: invErr } = await supabase.from('invoices').insert({
          org_id: org.id, client_id: clientId, invoice_number: invoiceNumber,
          period, status: targetStatus, line_item_mode: lineMode,
          issued_date: issuedDate, due_date: dueDate||null,
          payment_terms: payTerms, subtotal, total: subtotal,
          notes: notes||null, recurring, bill_day: recurring?billDay:null,
          skip_reminders: skipReminders, payment_link: paymentLink||null,
          po_number: poNumber.trim()||null,
          // Provenance: only when exactly one terms row covers the period.
          // Ambiguous or missing terms deliberately record nothing rather
          // than pointing at an arbitrary one.
          billing_terms_id: (periodTerms && periodTerms.length === 1) ? periodTerms[0].id : null,
          ...poRequestFields,
        }).select('id').single();
        if (invErr) throw new Error(invErr.message);
        invoiceId = invData.id;

        // Link time entries
        const entryLinks = periodEntries.map(e=>({ invoice_id:invoiceId, time_entry_id:e.id }));
        if (entryLinks.length) {
          const { error: linkErr } = await supabase.from('invoice_time_entries').insert(entryLinks);
          if (linkErr) throw new Error('Linking time entries failed: ' + linkErr.message);
        }
      } else {
        const { error: updErr } = await supabase.from('invoices').update({
          client_id:clientId, period, status:targetStatus, line_item_mode:lineMode,
          issued_date:issuedDate, due_date:dueDate||null, payment_terms:payTerms,
          subtotal, total:subtotal, notes:notes||null, recurring, bill_day:recurring?billDay:null,
          skip_reminders: skipReminders, payment_link: paymentLink||null,
          po_number: poNumber.trim()||null,
          ...poRequestFields,
        }).eq('id', invoiceId);
        if (updErr) throw new Error(updErr.message);
        // Replace line items
        const { error: delErr } = await supabase.from('invoice_line_items').delete().eq('invoice_id', invoiceId);
        if (delErr) throw new Error('Replacing line items failed: ' + delErr.message);
      }

      // Insert line items — time lines first, then the expense block.
      const lineInserts = allLines.map((l,i)=>({
        invoice_id:invoiceId, description:l.description||'',
        hours:l.hours?parseFloat(l.hours):null,
        rate:l.rate?parseFloat(l.rate):null,
        amount:parseFloat(l.amount||0), sort_order:i,
      }));
      if (lineInserts.length) {
        const { error: lineErr } = await supabase.from('invoice_line_items').insert(lineInserts);
        if (lineErr) throw new Error('Saving line items failed: ' + lineErr.message);
      }

      // Claim the expenses onto this invoice. This is a compare-and-swap in the
      // RPC (`WHERE billed_invoice_id IS NULL ... RETURNING id`), so a short
      // return means another admin claimed one first — say so rather than
      // silently producing an invoice whose lines double-bill.
      const wanted = [...pickedExpenses];
      if (wanted.length) {
        const { data: claimed, error: claimErr } =
          await supabase.rpc('claim_expenses_for_invoice', { p_invoice_id: invoiceId, p_ids: wanted });
        if (claimErr) throw new Error('Attaching expenses failed: ' + claimErr.message);
        const got = (claimed ?? []).map(r => (typeof r === 'string' ? r : r.id));
        const alreadyMine = billableExpenses.filter(e => e.billed_invoice_id === invoiceId).length;
        if (got.length + alreadyMine < wanted.length) {
          notifyError(
            `${wanted.length - got.length - alreadyMine} expense(s) were billed on another invoice first ` +
            `and were left off this one. Reopen the invoice to review.`
          );
        }
      }

      // On edit, anything unticked must be released or it stays attached to an
      // invoice that no longer has a line for it.
      if (isEdit) {
        const dropped = billableExpenses
          .filter(e => e.billed_invoice_id === invoiceId && !pickedExpenses.has(e.id));
        for (const e of dropped) {
          const { error } = await supabase.rpc('unclaim_expense', { p_expense_id: e.id });
          if (error) notifyError(`Releasing an expense failed: ${friendlyDbError(error)}.`);
        }
      }

      const verb = isEdit ? 'updated' : 'created';
      const savedInvoice = { id: invoiceId, invoice_number: invoiceNumber, status: targetStatus, client_id: clientId };

      if (opts.requestPO) {
        const { data: reqData, error: reqErr } = await supabase.functions.invoke('request-po-from-contact', { body: { invoice_id: invoiceId } });
        setSaving(false);
        if (reqErr || !reqData?.ok) {
          setErr(`Invoice saved, but requesting the PO failed: ${reqData?.error || reqErr?.message || 'unknown error'}.`);
          return;
        }
        onSaved(`Invoice ${invoiceNumber} saved. PO request sent to ${reqData.to}.`, savedInvoice, false);
        return;
      }

      onSaved(`Invoice ${invoiceNumber} ${verb} as ${targetStatus}.`, savedInvoice, targetStatus === 'sent');
    } catch(e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  const handlePreviewAndSend = () => {
    // Opens a print window then saves as sent
    // We pass a helper down to the preview component if opened from the save button
  };

  const periodLabel = period ? new Date(period+'-02').toLocaleString("default",{month:"long",year:"numeric"}) : '';
  const displayPeriodLabel = displayPeriod ? new Date(displayPeriod+'-02').toLocaleString("default",{month:"long",year:"numeric"}) : '';

  // ── Inline invoice preview (shared by wizard and edit form) ──────────────
  const fmt$ = (n) => {
    const v = parseFloat(n||0);
    return v === Math.round(v)
      ? '$' + Math.round(v).toLocaleString()
      : '$' + v.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2});
  };

  const InvoicePreviewPane = () => {
    const effectiveLogo = org?.invoice_logo_url || org?.logo_url || null;
    const orgAddrLines = formatAddressLines(org);
    const clientAddrLines = org?.invoice_show_client_address !== false ? formatAddressLines(client||{}) : [];
    const clientPhone = org?.invoice_show_client_phone !== false ? (client?.phone ? fmtPhone(client.phone) : null) : null;
    const billingContactLine = client?.billing_contact_name
      ? <div style={{fontSize:'11px',color:'#475569',marginTop:'2px'}}>Attn: {client.billing_contact_name}</div> : null;
    return (
      <div style={{background:'#ffffff',color:'#1e293b',fontFamily:'Arial,Helvetica,sans-serif',
        padding:'32px',borderRadius:'8px',fontSize:'13px',lineHeight:'1.5',minWidth:0}}>
        {/* Header bar */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',
          marginBottom:'28px',paddingBottom:'20px',borderBottom:'2px solid #e2e8f0'}}>
          <div>
            {effectiveLogo && <img src={effectiveLogo} alt="" style={{maxHeight:'48px',maxWidth:'140px',marginBottom:'8px',display:'block'}} />}
            <div style={{fontSize:'16px',fontWeight:'700',color:'#1e293b'}}>{org?.name||'Your Company'}</div>
            {org?.invoice_show_address !== false && orgAddrLines.map((l,i)=><div key={i} style={{fontSize:'11px',color:'#64748b'}}>{l}</div>)}
            {org?.invoice_show_phone !== false && org?.phone && <div style={{fontSize:'11px',color:'#64748b'}}>{fmtPhone(org.phone)}</div>}
            {org?.invoice_show_website !== false && org?.website && <div style={{fontSize:'11px',color:'#64748b'}}>{org.website}</div>}
          </div>
          <div style={{textAlign:'right'}}>
            <div style={{fontSize:'22px',fontWeight:'800',letterSpacing:'-0.5px',color:'#1e293b'}}>INVOICE</div>
            <div style={{fontSize:'13px',color:'#94a3b8',marginTop:'2px',fontStyle:'italic'}}>#{org?.invoice_number_prefix||'INV'}-XXXX</div>
            {periodLabel && <div style={{fontSize:'11px',color:'#94a3b8',marginTop:'2px'}}>{periodLabel}</div>}
          </div>
        </div>

        {/* Bill To + meta */}
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'24px'}}>
          <div>
            <div style={{fontSize:'10px',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:'4px'}}>Bill To</div>
            <div style={{fontSize:'14px',fontWeight:'600',color:'#1e293b'}}>{client ? (client.billing_name||client.name) : <span style={{color:'#94a3b8',fontStyle:'italic'}}>Select a client</span>}</div>
            {billingContactLine}
            {clientAddrLines.map((l,i)=><div key={i} style={{fontSize:'11px',color:'#475569'}}>{l}</div>)}
            {clientPhone && <div style={{fontSize:'11px',color:'#475569'}}>{clientPhone}</div>}
          </div>
          <div style={{textAlign:'right',fontSize:'12px',lineHeight:'2'}}>
            {issuedDate && <div><span style={{color:'#94a3b8'}}>Invoice Date  </span><strong>{issuedDate}</strong></div>}
            {dueDate    && <div><span style={{color:'#94a3b8'}}>Due Date  </span><strong>{dueDate}</strong></div>}
            {payTerms   && <div><span style={{color:'#94a3b8'}}>Terms  </span><strong>{payTerms}</strong></div>}
            {poNumber   && <div><span style={{color:'#94a3b8'}}>PO Number  </span><strong>{poNumber}</strong></div>}
          </div>
        </div>

        {/* Line items */}
        <table style={{width:'100%',borderCollapse:'collapse',marginBottom:'16px'}}>
          <thead>
            <tr style={{borderBottom:'2px solid #e2e8f0'}}>
              {['Description','Hours','Rate','Amount'].map((h,i)=>(
                <th key={h} style={{padding:'6px 0',fontSize:'10px',fontWeight:'700',color:'#94a3b8',
                  textTransform:'uppercase',letterSpacing:'0.5px',textAlign:i===0?'left':'right',
                  width:i===0?undefined:i===3?'80px':'60px'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr><td colSpan={4} style={{padding:'20px 0',textAlign:'center',fontSize:'12px',color:'#94a3b8',fontStyle:'italic'}}>
                Line items will appear here
              </td></tr>
            ) : lines.map((l,i)=>(
              <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                <td style={{padding:'8px 0',fontSize:'12px',color:'#1e293b'}}>{l.description||<span style={{color:'#94a3b8',fontStyle:'italic'}}>—</span>}</td>
                <td style={{padding:'8px 0',textAlign:'right',fontSize:'12px',color:'#64748b'}}>{l.hours!=null&&l.hours!==''?parseFloat(l.hours).toFixed(2):'—'}</td>
                <td style={{padding:'8px 0',textAlign:'right',fontSize:'12px',color:'#64748b'}}>{l.rate!=null&&l.rate!==''?fmt$(l.rate):'—'}</td>
                <td style={{padding:'8px 0',textAlign:'right',fontSize:'12px',fontWeight:'600',color:'#1e293b'}}>{fmt$(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Total */}
        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:'16px'}}>
          <div style={{minWidth:'180px',borderTop:'2px solid #1e293b',paddingTop:'10px',
            display:'flex',justifyContent:'space-between',alignItems:'baseline',gap:'16px'}}>
            <span style={{fontSize:'13px',fontWeight:'700',color:'#1e293b'}}>Total Due</span>
            <span style={{fontSize:'20px',fontWeight:'800',color:'#1e3a5f'}}>{fmt$(subtotal)}</span>
          </div>
        </div>

        {/* Notes */}
        {notes?.trim() && (
          <div style={{marginTop:'16px',padding:'12px',background:'#f8fafc',borderRadius:'6px',
            border:'1px solid #e2e8f0',fontSize:'12px',color:'#475569',lineHeight:'1.6'}}>
            <div style={{fontSize:'10px',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',
              letterSpacing:'0.5px',marginBottom:'4px'}}>Memo</div>
            {notes}
          </div>
        )}
      </div>
    );
  };

  // ── Wizard state (new invoices only) ─────────────────────────────────────
  const [wizStep, setWizStep] = useState(1); // 1=Client+Period, 2=Review Hours, 3=Line Items, 4=Confirm

  // Wizard: step 1 → 2 advances to review
  const wizNext = () => setWizStep(s => Math.min(s+1, 4));
  const wizBack = () => setWizStep(s => Math.max(s-1, 1));

  const STEPS = ['Client & Period','Review Hours','Line Items','Confirm & Send'];

  // Step progress bar
  const StepBar = () => (
    <div style={{display:'flex',alignItems:'center',gap:0,padding:'0 24px',marginBottom:'24px'}}>
      {STEPS.map((label, i) => {
        const n = i + 1;
        const done = wizStep > n;
        const active = wizStep === n;
        return (
          <div key={n} style={{display:'flex',alignItems:'center',flex: i < STEPS.length-1 ? 1 : 'none'}}>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'4px',cursor:done?'pointer':'default'}}
              onClick={done ? ()=>setWizStep(n) : undefined}>
              <div style={{width:'28px',height:'28px',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:'12px',fontWeight:'700',flexShrink:0,
                background: done ? C.accent : active ? C.accent : C.border,
                color: done||active ? '#fff' : C.muted,
                border: active ? `2px solid ${C.accent}` : '2px solid transparent',
                transition:'all 0.2s'}}>
                {done ? '✓' : n}
              </div>
              <div style={{fontSize:'10px',fontWeight:active?'700':'400',color:active?C.accent:done?C.text:C.muted,whiteSpace:'nowrap',transition:'color 0.2s'}}>{label}</div>
            </div>
            {i < STEPS.length-1 && (
              <div style={{flex:1,height:'2px',background:done?C.accent:C.border,margin:'0 4px',marginBottom:'16px',transition:'background 0.2s'}} />
            )}
          </div>
        );
      })}
    </div>
  );

  // ── Wizard render (new invoices only) ────────────────────────────────────
  if (!isEdit) {
    return (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:1000,overflowY:"auto",padding:"24px 16px"}}>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",width:"min(1160px,98vw)",margin:"auto",display:"flex",flexDirection:"column"}}>
          {/* Header */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 24px",borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:C.textBright}}>New Invoice</div>
            <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:"20px",cursor:"pointer",lineHeight:1}}>&times;</button>
          </div>

          {/* Split body */}
          <div style={{display:"flex",flex:1,minHeight:0,overflow:"hidden"}}>

            {/* Left: wizard */}
            <div style={{width:"460px",flexShrink:0,display:"flex",flexDirection:"column",borderRight:`1px solid ${C.border}`,overflowY:"auto"}}>
              <div style={{padding:"20px 24px 0"}}>
                <StepBar />
              </div>
              <div style={{padding:"0 24px 24px",flex:1}}>

            {/* ── Step 1: Client & Period ── */}
            {wizStep === 1 && (
              <div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"14px",marginBottom:client?.model==='hybrid'?"6px":"14px"}}>
                  <div>
                    <div style={css.lbl}>Client</div>
                    <select style={css.sel} value={clientId} onChange={e=>{setClientId(e.target.value); didSmartPeriod.current=false;}}>
                      <option value="">— Select client —</option>
                      {clients.filter(c=>!c.is_internal && c.is_active!==false).map(c=>
                        <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={css.lbl}><TKTooltip text={client?.model==='hybrid' ? "Retainer covers this month; overage hours are pulled from the prior month automatically." : "The month whose time entries are included on this invoice. Entries must be logged in this period to appear."}>{ client?.model==='hybrid' ? 'Retainer Period (Overage = prior month)' : 'Billing Period'} ⓘ</TKTooltip></div>
                    <select style={css.sel} value={period} onChange={e=>setPeriod(e.target.value)}>
                      {allMonths.map(m=><option key={m} value={m}>{new Date(m+'-02').toLocaleString("default",{month:"long",year:"numeric"})}</option>)}
                    </select>
                  </div>
                </div>
                {client?.model==='hybrid' && period && (
                  <div style={{padding:"8px 12px",background:"rgba(237,167,48,0.06)",border:"1px solid rgba(237,167,48,0.2)",borderRadius:"6px",marginBottom:"14px",fontSize:"12px",color:"#f6c26b"}}>
                    Retainer: <strong>{new Date(period+'-02').toLocaleString("default",{month:"long",year:"numeric"})}</strong> · Overage hours from: <strong>{new Date(prevMonthOf(period)+'-02').toLocaleString("default",{month:"long",year:"numeric"})}</strong>
                  </div>
                )}
                {clientId && period && (
                  <div style={{padding:"12px 16px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:"8px",marginBottom:"14px"}}>
                    <div style={{fontSize:"12px",color:C.muted,marginBottom:"8px",fontWeight:"600",textTransform:"uppercase",letterSpacing:"0.4px"}}>Summary</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"12px"}}>
                      {[
                        {label:'Model', value: client ? modelLabel(client.model, 'label') : '—'},
                        {label:'Entries', value: periodEntries.length + ' billable'},
                        {label:'Hours', value: formatExactTime(periodEntries.reduce((s,e)=>s+parseFloat(e.hours||0),0))},
                      ].map(({label,value})=>(
                        <div key={label}>
                          <div style={{fontSize:"11px",color:C.muted}}>{label}</div>
                          <div style={{fontSize:"14px",fontWeight:"600",color:C.textBright}}>{value}</div>
                        </div>
                      ))}
                    </div>
                    {periodEntries.length > 0 && !isFullyLocked && (
                      <div style={{marginTop:"10px",fontSize:"12px",color:"#f6c26b"}}>
                        ⚠ Entries not fully locked — some may still change.
                      </div>
                    )}
                  </div>
                )}
                <div style={{display:"flex",justifyContent:"flex-end",gap:"10px",marginTop:"8px"}}>
                  <button style={css.btn()} onClick={onClose}>Cancel</button>
                  <button style={css.btn("primary")} disabled={!clientId||!period} onClick={wizNext}>Next →</button>
                </div>
              </div>
            )}

            {/* ── Step 2: Review Hours ── */}
            {wizStep === 2 && (
              <div>
                <div style={{marginBottom:"14px",fontSize:"13px",color:C.muted}}>
                  {isHybridOverage ? (
                    <>Review <strong style={{color:C.text}}>{displayPeriodLabel}</strong>'s time entries for <strong style={{color:C.text}}>{client?.name}</strong> — these determine the overage line on this invoice (retainer billed for {periodLabel}).</>
                  ) : (
                    <>Review the time entries that will be included in this invoice for <strong style={{color:C.text}}>{client?.name}</strong> — <strong style={{color:C.text}}>{periodLabel}</strong>.</>
                  )}
                </div>
                {periodEntries.length === 0 ? (
                  <div style={{padding:"32px",textAlign:"center",color:C.muted,fontSize:"14px",border:`1px solid ${C.border}`,borderRadius:"8px",marginBottom:"14px"}}>
                    No billable entries found for this client and period.
                    {client?.model === 'retainer' && <div style={{marginTop:"8px",fontSize:"12px",color:C.accent}}>Retainer clients don't require entries — a flat retainer line will be generated.</div>}
                  </div>
                ) : (
                  <div style={{border:`1px solid ${C.border}`,borderRadius:"8px",overflow:"hidden",marginBottom:"14px"}}>
                    <div style={{display:"grid",gridTemplateColumns:"100px 1fr 120px 60px 60px",gap:0,padding:"8px 12px",background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                      {['Date','Description','Task Type','Hours','Billable'].map((h,i)=>(
                        <div key={h} style={{fontSize:"11px",fontWeight:"600",color:C.muted,textTransform:"uppercase",letterSpacing:"0.4px",textAlign:i>=3?'right':'left'}}>{h}</div>
                      ))}
                    </div>
                    <div style={{maxHeight:"240px",overflowY:"auto"}}>
                      {periodEntries.map(e => {
                        const ttName = taskTypes.find(t=>t.id===e.task_type_id)?.name;
                        return (
                          <div key={e.id} style={{display:"grid",gridTemplateColumns:"100px 1fr 120px 60px 60px",gap:0,padding:"8px 12px",borderBottom:`1px solid rgba(30,39,54,0.5)`,alignItems:"center"}}>
                            <div style={{fontSize:"12px",color:C.muted}}>{e.entry_date}</div>
                            <div style={{fontSize:"12px",color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.description||<span style={{color:C.muted,fontStyle:"italic"}}>—</span>}</div>
                            <div style={{fontSize:"12px",color:C.muted}}>{ttName||<span style={{fontStyle:"italic"}}>None</span>}</div>
                            <div style={{fontSize:"12px",fontWeight:"600",color:C.textBright,textAlign:"right"}}>{formatExactTime(e.hours)}</div>
                            <div style={{fontSize:"12px",textAlign:"right",color:e.is_billable===false?C.muted:C.accent}}>{e.is_billable===false?'No':'Yes'}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{display:"flex",justifyContent:"flex-end",gap:"16px",padding:"10px 14px",borderTop:`1px solid ${C.border}`,fontSize:"13px"}}>
                      <span style={{color:C.muted}}>Total hours</span>
                      <span style={{fontWeight:"700",color:C.textBright}}>
                        {formatExactTime(periodEntries.reduce((s,e)=>s+parseFloat(e.hours||0),0))}
                      </span>
                    </div>
                  </div>
                )}
                <div style={{display:"flex",justifyContent:"space-between",gap:"10px",marginTop:"8px"}}>
                  <button style={css.btn()} onClick={wizBack}>← Back</button>
                  <button style={css.btn("primary")} onClick={wizNext}>Next →</button>
                </div>
              </div>
            )}

            {/* ── Step 3: Line Items ── */}
            {wizStep === 3 && (
              <div>
                <div style={{marginBottom:"14px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"8px"}}>
                    <div style={css.lbl}><TKTooltip text="Each row on the invoice sent to the client. Auto-generated from time entries (by task or by entry), or entered manually. You can edit any line before sending.">Line Items ⓘ</TKTooltip></div>
                    <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
                      <select style={{...css.sel,width:"auto",padding:"4px 10px",fontSize:"11px"}} value={lineMode} onChange={e=>setLineMode(e.target.value)}>
                        <option value="by_task">By task type</option>
                        <option value="by_entry">By entry</option>
                        <option value="manual">Manual</option>
                      </select>
                      <button style={css.smBtn(C.accent)} onClick={addLine}>+ Add line</button>
                    </div>
                  </div>
                  <div style={{border:`1px solid ${C.border}`,borderRadius:"6px",overflow:"hidden"}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 70px 70px 80px 28px",gap:0,background:"rgba(30,39,54,0.5)",padding:"6px 10px"}}>
                      {["Description","Qty","Rate","Amount",""].map((h,i)=><div key={i} style={{...css.lbl,marginBottom:0,textAlign:i>=1?"right":"left"}}>{h}</div>)}
                    </div>
                    {lines.length===0 && (
                      <div style={{padding:"16px",textAlign:"center",color:C.muted,fontSize:"12px"}}>
                        {lineMode==='manual' ? 'Click "+ Add line" to add a line item.' : 'No billable entries found for this client and period.'}
                      </div>
                    )}
                    {lines.map((line,idx)=>(
                      <div key={line.id||idx} style={{display:"grid",gridTemplateColumns:"1fr 70px 70px 80px 28px",gap:0,padding:"6px 10px",borderTop:`1px solid rgba(30,39,54,0.5)`,alignItems:"center"}}>
                        <input style={{...css.inp,padding:"4px 8px",fontSize:"12px"}} value={line.description} onChange={e=>updateLine(idx,'description',e.target.value)} placeholder="Description" />
                        <input style={{...css.inp,padding:"4px 6px",fontSize:"12px",textAlign:"right"}} type="number" min="0" step="any"
                          value={line.hours===null?'':line.hours} onChange={e=>updateLine(idx,'hours',e.target.value)} placeholder="—" />
                        <input style={{...css.inp,padding:"4px 6px",fontSize:"12px",textAlign:"right"}} type="number" min="0" step="any"
                          value={line.rate===null?'':line.rate} onChange={e=>updateLine(idx,'rate',e.target.value)} placeholder="—" />
                        <input style={{...css.inp,padding:"4px 6px",fontSize:"12px",textAlign:"right",fontWeight:"600"}} type="number" min="0" step="any"
                          value={line.amount} onChange={e=>updateLine(idx,'amount',e.target.value)} />
                        <button onClick={()=>removeLine(idx)} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:"15px",textAlign:"center",padding:0,lineHeight:1}}>&times;</button>
                      </div>
                    ))}
                    {expenseLines.map((line)=>(
                      <div key={line.id} style={{display:"grid",gridTemplateColumns:"1fr 70px 70px 80px 28px",gap:0,padding:"6px 10px",borderTop:`1px solid rgba(30,39,54,0.5)`,alignItems:"center",background:`${C.accent}0a`}}>
                        <div style={{fontSize:"12px",color:C.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          <span style={{marginRight:"5px"}}>🧾</span>{line.description}
                        </div>
                        <div /><div />
                        <div style={{fontSize:"12px",fontWeight:"600",textAlign:"right",color:C.text}}>
                          {Number(line.amount).toFixed(2)}
                        </div>
                        <button onClick={()=>setPickedExpenses(prev=>{const n=new Set(prev);n.delete(line._expenseId);return n;})}
                                title="Leave this expense off the invoice"
                                style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:"15px",textAlign:"center",padding:0,lineHeight:1}}>&times;</button>
                      </div>
                    ))}
                    <div style={{display:"flex",justifyContent:"flex-end",padding:"10px 14px",borderTop:`1px solid ${C.border}`,gap:"20px"}}>
                      <span style={{fontSize:"12px",color:C.muted,fontWeight:"600",textTransform:"uppercase"}}>Total</span>
                      <span style={{fontSize:"16px",fontWeight:"700",color:C.accent}}>${Math.round(subtotal).toLocaleString()}</span>
                    </div>
                  </div>

                  {billableExpenses.length > 0 && (
                    <div style={{marginTop:"14px",border:`1px solid ${C.border}`,borderRadius:"6px",padding:"12px 14px"}}>
                      <div style={{...css.lbl,marginBottom:"8px"}}>
                        Billable expenses for {client?.name} ({billableExpenses.length})
                      </div>
                      {billableExpenses.map(e=>{
                        const inPeriod = monthOf(e.expense_date) === period;
                        const markup = Number(e.markup_percent||0);
                        const billed = Number(e.amount||0) * (1 + markup/100);
                        return (
                          <label key={e.id} style={{display:"flex",gap:"9px",alignItems:"center",padding:"5px 0",cursor:"pointer",fontSize:"12px"}}>
                            <input type="checkbox" checked={pickedExpenses.has(e.id)}
                              onChange={()=>setPickedExpenses(prev=>{
                                const n=new Set(prev); n.has(e.id)?n.delete(e.id):n.add(e.id); return n;
                              })} />
                            <span style={{flex:1,minWidth:0,color:C.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {e.expense_date} · {[e.vendor,e.description].filter(Boolean).join(' — ') || 'Expense'}
                              {!inPeriod && <span style={{...css.badge('amber'),marginLeft:'6px'}}>earlier period</span>}
                            </span>
                            <span style={{fontVariantNumeric:'tabular-nums',color:C.text,fontWeight:'600'}}>
                              {fmtCents(billed)}
                            </span>
                            {markup>0 && (
                              <span style={{fontSize:'10px',color:C.muted,whiteSpace:'nowrap'}}>
                                {fmtCents(e.amount)} +{markup}%
                              </span>
                            )}
                          </label>
                        );
                      })}
                      <div style={{fontSize:"11px",color:C.muted,marginTop:"6px"}}>
                        Expenses outside this period are shown too — an expense missed last month is
                        still billable. Only in-period ones are ticked by default.
                      </div>
                    </div>
                  )}
                </div>
                {err && <div style={{padding:"10px 14px",background:"rgba(229,62,62,0.08)",border:"1px solid rgba(229,62,62,0.25)",borderRadius:"6px",marginBottom:"12px",fontSize:"13px",color:C.danger}}>{err}</div>}
                {termsBanner && <div style={{padding:"10px 14px",background:termsBanner.bad?"rgba(229,62,62,0.08)":"rgba(246,194,107,0.10)",border:`1px solid ${termsBanner.bad?"rgba(229,62,62,0.25)":"rgba(246,194,107,0.30)"}`,borderRadius:"6px",marginBottom:"12px",fontSize:"12px",color:termsBanner.bad?C.danger:C.badgeAmber}}>{termsBanner.text}</div>}
                <div style={{display:"flex",justifyContent:"space-between",gap:"10px",marginTop:"8px"}}>
                  <button style={css.btn()} onClick={wizBack}>← Back</button>
                  <button style={css.btn("primary")} disabled={allLines.length===0} onClick={wizNext}>Next →</button>
                </div>
              </div>
            )}

            {/* ── Step 4: Confirm & Send ── */}
            {wizStep === 4 && (
              <div>
                {/* Invoice summary card */}
                <div style={{padding:"16px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:"8px",marginBottom:"16px"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px",marginBottom:"12px"}}>
                    <div>
                      <div style={{fontSize:"11px",color:C.muted,marginBottom:"2px"}}>Client</div>
                      <div style={{fontSize:"14px",fontWeight:"600",color:C.textBright}}>{client?.name}</div>
                    </div>
                    <div>
                      <div style={{fontSize:"11px",color:C.muted,marginBottom:"2px"}}>Period</div>
                      <div style={{fontSize:"14px",fontWeight:"600",color:C.textBright}}>{periodLabel}</div>
                    </div>
                    <div>
                      <div style={{fontSize:"11px",color:C.muted,marginBottom:"2px"}}>Line Items</div>
                      <div style={{fontSize:"14px",fontWeight:"600",color:C.textBright}}>{lines.length}</div>
                    </div>
                    <div>
                      <div style={{fontSize:"11px",color:C.muted,marginBottom:"2px"}}>Total</div>
                      <div style={{fontSize:"18px",fontWeight:"700",color:C.accent}}>${Math.round(subtotal).toLocaleString()}</div>
                    </div>
                  </div>
                  {!canEmailInvoice(client) && (
                    <div style={{fontSize:"12px",color:"#f6c26b",padding:"8px 10px",background:"rgba(237,167,48,0.06)",border:"1px solid rgba(237,167,48,0.2)",borderRadius:"6px"}}>
                      ⚠ No billing contact or AP inbox email on file — you can save as draft, but email delivery won't be available until one is added.
                    </div>
                  )}
                </div>

                {/* Invoice settings */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"14px",marginBottom:"14px"}}>
                  <div>
                    <div style={css.lbl}><TKTooltip text="The official date on the invoice. Affects due date calculation. Defaults to today.">Issued Date ⓘ</TKTooltip></div>
                    <input type="date" style={css.inp} value={issuedDate} onChange={e=>setIssuedDate(e.target.value)} />
                  </div>
                  <div>
                    <div style={css.lbl}><TKTooltip text="How many days after the issue date payment is due. Overrides your org default for this invoice only.">Payment Terms ⓘ</TKTooltip></div>
                    <select style={css.sel} value={payTerms} onChange={e=>setPayTerms(e.target.value)}>
                      {['Due on Receipt','Net 7','Net 15','Net 30','Net 45','Net 60'].map(v=><option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={css.lbl}><TKTooltip text="Calculated automatically from issue date + payment terms. Read-only — adjust via Payment Terms.">Due Date ⓘ</TKTooltip></div>
                    <input type="date" style={{...css.inp,color:C.muted}} value={dueDate} readOnly />
                  </div>
                </div>

                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"14px",marginBottom:"14px"}}>
                  <div>
                    <div style={css.lbl}>
                      <TKTooltip text="Printed on the invoice document and email when set. Blanket-PO clients auto-fill this from their standing number. If a PO is required and you don't have one yet, use Request PO from Billing Contact below instead of leaving this blank.">PO Number ⓘ</TKTooltip>{client?.po_required ? <span style={{color:C.danger,marginLeft:"4px"}}>*</span> : <span style={{color:C.muted,fontWeight:"400",marginLeft:"4px"}}>(optional)</span>}
                    </div>
                    <input style={css.inp} type="text" placeholder={client?.po_required ? 'Required for this client' : 'e.g. PO-12345'}
                      value={poNumber} onChange={e=>setPoNumber(e.target.value)} />
                  </div>
                  <div>
                    <div style={css.lbl}><TKTooltip text="Override the org-level payment link for this invoice only. Leave blank to use the org default.">Payment Link (optional) ⓘ</TKTooltip></div>
                    <input style={css.inp} type="url" placeholder={org?.invoice_payment_link || 'https://…'}
                      value={paymentLink} onChange={e=>setPaymentLink(e.target.value)} />
                  </div>
                </div>

                <div style={{marginBottom:"14px"}}>
                  <div style={css.lbl}><TKTooltip text="Optional note shown at the bottom of the invoice — payment instructions, thank-you message, or terms.">Memo (optional) ⓘ</TKTooltip></div>
                  <textarea value={notes} onChange={e=>setNotes(e.target.value)}
                    placeholder="Payment instructions, thank-you note, etc."
                    style={{...css.inp,minHeight:"56px",resize:"vertical",fontFamily:"Inter,sans-serif",lineHeight:"1.5"}} />
                </div>

                {/* Attachments */}
                <div style={{marginBottom:"14px"}}>
                  <div style={{...css.lbl,marginBottom:"6px"}}>Attachments <span style={{fontWeight:"400",color:C.muted}}>(receipts, docs — PDF, image, max 10 MB each)</span></div>
                  {attachments.length > 0 && (
                    <div style={{display:"flex",flexDirection:"column",gap:"4px",marginBottom:"8px"}}>
                      {attachments.map(att => (
                        <div key={att.id} style={{display:"flex",alignItems:"center",gap:"8px",padding:"6px 10px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:"6px"}}>
                          <span style={{fontSize:"14px"}}>{att.mime_type?.startsWith('image/') ? '🖼' : '📄'}</span>
                          <button onClick={()=>downloadAttachment(att)} style={{flex:1,textAlign:"left",background:"none",border:"none",cursor:"pointer",color:C.accent,fontSize:"12px",padding:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                            {att.file_name}
                          </button>
                          <span style={{fontSize:"11px",color:C.muted,flexShrink:0}}>{att.file_size ? (att.file_size/1024).toFixed(0)+'KB' : ''}</span>
                          <button onClick={()=>deleteAttachment(att)} style={{background:"none",border:"none",cursor:"pointer",color:C.danger,fontSize:"14px",lineHeight:1,padding:"0 2px",flexShrink:0}} title="Remove">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {!existingInvoice?.id && (
                    <div style={{fontSize:"11px",color:C.muted,padding:"8px 10px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:"6px"}}>
                      Save the invoice as a draft first to enable attachments.
                    </div>
                  )}
                  {existingInvoice?.id && (
                    <label style={{display:"flex",alignItems:"center",gap:"8px",padding:"8px 12px",border:`1px dashed ${C.border}`,borderRadius:"6px",cursor:uploading?"not-allowed":"pointer",color:uploading?C.muted:C.text,fontSize:"12px",background:C.bg}}>
                      <input type="file" multiple accept=".pdf,image/*,.png,.jpg,.jpeg,.heic,.heif"
                        style={{display:"none"}}
                        disabled={uploading}
                        onChange={e=>{ Array.from(e.target.files||[]).forEach(uploadAttachment); e.target.value=''; }} />
                      {uploading ? '⏳ Uploading…' : '📎 Click to attach files'}
                    </label>
                  )}
                  {attachErr && <div style={{fontSize:"12px",color:C.danger,marginTop:"4px"}}>{attachErr}</div>}
                </div>

                {/* Recurring + reminders toggles */}
                <div style={{display:"flex",flexDirection:"column",gap:"8px",marginBottom:"14px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:"10px",padding:"10px 14px",background:C.bg,borderRadius:"6px",border:`1px solid ${C.border}`}}>
                    <button type="button" onClick={()=>setRecurring(r=>!r)}
                      style={{width:"32px",height:"18px",borderRadius:"9px",border:"none",cursor:"pointer",flexShrink:0,
                        background:recurring?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
                      <div style={{position:"absolute",top:"2px",left:recurring?"16px":"2px",
                        width:"14px",height:"14px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
                    </button>
                    <div style={{fontSize:"13px",color:C.textBright,fontWeight:"500",flex:1}}>Recurring invoice</div>
                    {recurring && (
                      <select style={{...css.sel,width:"auto",padding:"4px 10px",fontSize:"12px"}} value={billDay} onChange={e=>setBillDay(e.target.value)}>
                        <option value="1st">Bill on 1st</option>
                        <option value="15th">Bill on 15th</option>
                        <option value="end">Bill at end of month</option>
                      </select>
                    )}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:"10px",padding:"10px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:"6px"}}>
                    <input type="checkbox" id="skipRem" checked={skipReminders} onChange={e=>setSkipReminders(e.target.checked)}
                      style={{width:"14px",height:"14px",cursor:"pointer",accentColor:C.accent}} />
                    <label htmlFor="skipRem" style={{fontSize:"13px",color:C.text,cursor:"pointer"}}>Disable automated reminder emails for this invoice</label>
                  </div>
                </div>

                {err && <div style={{padding:"10px 14px",background:"rgba(229,62,62,0.08)",border:"1px solid rgba(229,62,62,0.25)",borderRadius:"6px",marginBottom:"12px",fontSize:"13px",color:C.danger}}>{err}</div>}
                {termsBanner && <div style={{padding:"10px 14px",background:termsBanner.bad?"rgba(229,62,62,0.08)":"rgba(246,194,107,0.10)",border:`1px solid ${termsBanner.bad?"rgba(229,62,62,0.25)":"rgba(246,194,107,0.30)"}`,borderRadius:"6px",marginBottom:"12px",fontSize:"12px",color:termsBanner.bad?C.danger:C.badgeAmber}}>{termsBanner.text}</div>}

                <div style={{display:"flex",justifyContent:"space-between",gap:"10px",marginTop:"8px"}}>
                  <button style={css.btn()} onClick={wizBack} disabled={saving}>← Back</button>
                  <div style={{display:"flex",gap:"10px"}}>
                    <button style={css.btn()} onClick={()=>save('draft')} disabled={saving}>{saving?'Saving…':'Save as Draft'}</button>
                    {client?.po_required && client?.po_mode !== 'blanket' && !poNumber.trim() && (
                      <button style={css.btn()} onClick={()=>save('draft',{skipPOCheck:true,requestPO:true})}
                        disabled={saving||!client?.billing_contact_email}
                        title={!client?.billing_contact_email?'No billing contact email on file — add one before requesting a PO':'Save the invoice and email the total to the billing contact so they can enter a PO number, which sends the invoice automatically.'}>
                        {saving?'Saving…':'📩 Request PO from Billing Contact'}
                      </button>
                    )}
                    <button style={css.btn("primary")} onClick={()=>save('sent')} disabled={saving||!canEmailInvoice(client)}
                      title={!canEmailInvoice(client)?'No billing contact or AP inbox email on file':''}>
                      {saving?'Saving…':'Send Invoice'}
                    </button>
                  </div>
                </div>
              </div>
            )}

              </div>
            </div>{/* end left wizard pane */}

            {/* Right: live invoice preview */}
            <div style={{flex:1,minWidth:0,overflowY:"auto",background:"#e8edf3",padding:"24px"}}>
              <div style={{fontSize:"11px",fontWeight:"600",color:"#64748b",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"10px",textAlign:"center"}}>Live Preview</div>
              <InvoicePreviewPane />
            </div>

          </div>{/* end split body */}
        </div>
      </div>
    );
  }

  // ── Edit mode: keep existing single-screen form ──────────────────────────
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:1000,overflowY:"auto",padding:"24px 0"}}>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",width:"min(720px,96vw)",margin:"auto"}}>
        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"20px 24px",borderBottom:`1px solid ${C.border}`}}>
          <div style={{fontSize:"16px",fontWeight:"700",color:C.textBright}}>
            {isEdit ? `Edit Invoice ${existingInvoice.invoice_number}` : 'New Invoice'}
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:"20px",cursor:"pointer",lineHeight:1}}>&times;</button>
        </div>

        <div style={{padding:"24px"}}>
          {/* Client + Period row */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"14px",marginBottom:client?.model==='hybrid'?"6px":"14px"}}>
            <div>
              <div style={css.lbl}><TKTooltip text="The client this invoice will be sent to. Archived clients are excluded unless this invoice already exists for them.">Client ⓘ</TKTooltip></div>
              <select style={css.sel} value={clientId} onChange={e=>{setClientId(e.target.value); didSmartPeriod.current=false;}} disabled={isEdit}>
                <option value="">— Select client —</option>
                {clients.filter(c=>(!c.is_internal || c.id===clientId) && (c.is_active!==false || c.id===clientId)).map(c=>
                  <option key={c.id} value={c.id}>{c.name}{c.is_active===false?' (archived)':''}</option>)}
              </select>
            </div>
            <div>
              <div style={css.lbl}><TKTooltip text={client?.model==='hybrid' ? 'The month billed as the retainer period. Overage hours are automatically pulled from the prior month.' : 'The month this invoice covers. Time entries for this period will be used to populate line items.'}>{client?.model==='hybrid' ? 'Retainer Period (Overage = prior month)' : 'Billing Period'} ⓘ</TKTooltip></div>
              <select style={css.sel} value={period} onChange={e=>setPeriod(e.target.value)} disabled={isEdit}>
                {allMonths.map(m=><option key={m} value={m}>{new Date(m+'-02').toLocaleString("default",{month:"long",year:"numeric"})}</option>)}
              </select>
            </div>
          </div>
          {/* Hybrid period context */}
          {!isEdit && client?.model==='hybrid' && period && (
            <div style={{padding:"8px 12px",background:"rgba(237,167,48,0.06)",border:"1px solid rgba(237,167,48,0.2)",borderRadius:"6px",marginBottom:"14px",fontSize:"12px",color:"#f6c26b"}}>
              Retainer: <strong>{new Date(period+'-02').toLocaleString("default",{month:"long",year:"numeric"})}</strong> · Overage hours from: <strong>{new Date(prevMonthOf(period)+'-02').toLocaleString("default",{month:"long",year:"numeric"})}</strong>
            </div>
          )}

          {/* Dates + terms */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"14px",marginBottom:"14px"}}>
            <div>
              <div style={css.lbl}><TKTooltip text="The date the invoice is officially issued. Defaults to today. Shown on the invoice PDF.">Issued Date ⓘ</TKTooltip></div>
              <input type="date" style={css.inp} value={issuedDate} onChange={e=>setIssuedDate(e.target.value)} />
            </div>
            <div>
              <div style={css.lbl}><TKTooltip text="How many days the client has to pay. The due date is calculated automatically from the issued date.">Payment Terms ⓘ</TKTooltip></div>
              <select style={css.sel} value={payTerms} onChange={e=>setPayTerms(e.target.value)}>
                {['Due on Receipt','Net 7','Net 15','Net 30','Net 45','Net 60'].map(v=><option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <div style={css.lbl}><TKTooltip text="Calculated from issued date + payment terms. Shown on the invoice and used to trigger overdue reminders.">Due Date ⓘ</TKTooltip></div>
              <input type="date" style={{...css.inp,color:C.muted}} value={dueDate} readOnly />
            </div>
          </div>

          {/* Recurring toggle */}
          <div style={{display:"flex",alignItems:"center",gap:"14px",marginBottom:"14px",padding:"10px 14px",background:C.bg,borderRadius:"6px",border:`1px solid ${C.border}`}}>
            <button type="button" onClick={()=>setRecurring(r=>!r)}
              style={{width:"36px",height:"20px",borderRadius:"10px",border:"none",cursor:"pointer",flexShrink:0,
                background:recurring?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
              <div style={{position:"absolute",top:"2px",left:recurring?"18px":"2px",
                width:"16px",height:"16px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
            </button>
            <div style={{fontSize:"13px",color:C.textBright,fontWeight:"500"}}>Recurring invoice</div>
            {recurring && (
              <select style={{...css.sel,width:"auto",padding:"4px 10px",fontSize:"12px"}} value={billDay} onChange={e=>setBillDay(e.target.value)}>
                <option value="1st">Bill on 1st of month</option>
                <option value="15th">Bill on 15th of month</option>
                <option value="end">Bill at end of month</option>
              </select>
            )}
          </div>

          {/* Lock warning */}
          {clientId && period && periodEntries.length > 0 && !isFullyLocked && (
            <div style={{padding:"10px 14px",background:"rgba(237,167,48,0.08)",border:"1px solid rgba(237,167,48,0.25)",borderRadius:"6px",marginBottom:"14px",fontSize:"12px",color:"#f6c26b"}}>
              ⚠ Not all entries for {periodLabel} are locked. You can still generate this invoice, but some entries may change.
            </div>
          )}

          {/* Line Items */}
          <div style={{marginBottom:"14px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"8px"}}>
              <div style={css.lbl}><TKTooltip text="How line items are generated: by task type (one row per task), by individual entry, or manual (you write them yourself).">Line Items ⓘ</TKTooltip></div>
              <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
                <select style={{...css.sel,width:"auto",padding:"4px 10px",fontSize:"11px"}} value={lineMode} onChange={e=>setLineMode(e.target.value)}>
                  <option value="by_task">By task type</option>
                  <option value="by_entry">By entry</option>
                  <option value="manual">Manual</option>
                </select>
                <button style={css.smBtn(C.accent)} onClick={addLine}>+ Add line</button>
              </div>
            </div>
            <div style={{border:`1px solid ${C.border}`,borderRadius:"6px",overflow:"hidden"}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 70px 70px 80px 28px",gap:0,background:"rgba(30,39,54,0.5)",padding:"6px 10px"}}>
                {["Description","Qty","Rate","Amount",""].map((h,i)=><div key={i} style={{...css.lbl,marginBottom:0,textAlign:i>=1?"right":"left"}}>{h}</div>)}
              </div>
              {lines.length===0 && (
                <div style={{padding:"16px",textAlign:"center",color:C.muted,fontSize:"12px"}}>
                  {lineMode==='manual' ? 'Click "+ Add line" to add a line item.' : 'No billable entries found for this client and period.'}
                </div>
              )}
              {lines.map((line,idx)=>(
                <div key={line.id||idx} style={{display:"grid",gridTemplateColumns:"1fr 70px 70px 80px 28px",gap:0,padding:"6px 10px",borderTop:`1px solid rgba(30,39,54,0.5)`,alignItems:"center"}}>
                  <input style={{...css.inp,padding:"4px 8px",fontSize:"12px"}} value={line.description} onChange={e=>updateLine(idx,'description',e.target.value)} placeholder="Description" />
                  <input style={{...css.inp,padding:"4px 6px",fontSize:"12px",textAlign:"right"}} type="number" min="0" step="any"
                    value={line.hours===null?'':line.hours} onChange={e=>updateLine(idx,'hours',e.target.value)} placeholder="—" />
                  <input style={{...css.inp,padding:"4px 6px",fontSize:"12px",textAlign:"right"}} type="number" min="0" step="any"
                    value={line.rate===null?'':line.rate} onChange={e=>updateLine(idx,'rate',e.target.value)} placeholder="—" />
                  <input style={{...css.inp,padding:"4px 6px",fontSize:"12px",textAlign:"right",fontWeight:"600"}} type="number" min="0" step="any"
                    value={line.amount} onChange={e=>updateLine(idx,'amount',e.target.value)} />
                  <button onClick={()=>removeLine(idx)} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:"15px",textAlign:"center",padding:0,lineHeight:1}}>&times;</button>
                </div>
              ))}
              {expenseLines.map((line)=>(
                <div key={line.id} style={{display:"grid",gridTemplateColumns:"1fr 70px 70px 80px 28px",gap:0,padding:"6px 10px",borderTop:`1px solid rgba(30,39,54,0.5)`,alignItems:"center",background:`${C.accent}0a`}}>
                  <div style={{fontSize:"12px",color:C.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    <span style={{marginRight:"5px"}}>🧾</span>{line.description}
                  </div>
                  <div /><div />
                  <div style={{fontSize:"12px",fontWeight:"600",textAlign:"right",color:C.text}}>
                    {Number(line.amount).toFixed(2)}
                  </div>
                  <button onClick={()=>setPickedExpenses(prev=>{const n=new Set(prev);n.delete(line._expenseId);return n;})}
                          title="Leave this expense off the invoice"
                          style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:"15px",textAlign:"center",padding:0,lineHeight:1}}>&times;</button>
                </div>
              ))}
              <div style={{display:"flex",justifyContent:"flex-end",padding:"10px 14px",borderTop:`1px solid ${C.border}`,gap:"20px"}}>
                <span style={{fontSize:"12px",color:C.muted,fontWeight:"600",textTransform:"uppercase"}}>Total</span>
                <span style={{fontSize:"16px",fontWeight:"700",color:C.accent}}>${Math.round(subtotal).toLocaleString()}</span>
              </div>
            </div>

            {billableExpenses.length > 0 && (
              <div style={{marginTop:"12px",border:`1px solid ${C.border}`,borderRadius:"6px",padding:"12px 14px"}}>
                <div style={{...css.lbl,marginBottom:"8px"}}>
                  Billable expenses for {client?.name} ({billableExpenses.length})
                </div>
                {billableExpenses.map(e=>{
                  const inPeriod = monthOf(e.expense_date) === period;
                  const markup = Number(e.markup_percent||0);
                  const billed = Number(e.amount||0) * (1 + markup/100);
                  // unclaim_expense refuses server-side once the invoice is no
                  // longer draft, so a sent invoice's expenses can't shrink
                  // silently — this just keeps the checkbox from offering an
                  // action that would immediately bounce back as an error.
                  const alreadyOnThisSentInvoice =
                    e.billed_invoice_id === existingInvoice?.id && existingInvoice?.status !== 'draft';
                  return (
                    <label key={e.id} style={{display:"flex",gap:"9px",alignItems:"center",padding:"5px 0",
                                              cursor: alreadyOnThisSentInvoice ? 'default' : 'pointer',
                                              opacity: alreadyOnThisSentInvoice ? 0.7 : 1,
                                              fontSize:"12px"}}>
                      <input type="checkbox" checked={pickedExpenses.has(e.id)}
                        disabled={alreadyOnThisSentInvoice}
                        onChange={()=>setPickedExpenses(prev=>{
                          const n=new Set(prev); n.has(e.id)?n.delete(e.id):n.add(e.id); return n;
                        })} />
                      <span style={{flex:1,minWidth:0,color:C.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {e.expense_date} · {[e.vendor,e.description].filter(Boolean).join(' — ') || 'Expense'}
                        {!inPeriod && <span style={{...css.badge('amber'),marginLeft:'6px'}}>earlier period</span>}
                        {alreadyOnThisSentInvoice && <span style={{...css.badge('blue'),marginLeft:'6px'}}>already sent</span>}
                      </span>
                      <span style={{fontVariantNumeric:'tabular-nums',color:C.text,fontWeight:'600'}}>
                        {fmtCents(billed)}
                      </span>
                      {markup>0 && (
                        <span style={{fontSize:'10px',color:C.muted,whiteSpace:'nowrap'}}>
                          {fmtCents(e.amount)} +{markup}%
                        </span>
                      )}
                    </label>
                  );
                })}
                <div style={{fontSize:"11px",color:C.muted,marginTop:"6px"}}>
                  {existingInvoice?.status !== 'draft'
                    ? 'This invoice has been sent — expenses already on it are locked in place.'
                    : 'Unticking an expense releases it — it becomes available for a future invoice.'}
                </div>
              </div>
            )}
          </div>

          {/* PO number + payment link */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"14px",marginBottom:"20px"}}>
            <div>
              <div style={css.lbl}>
                <TKTooltip text="Printed on the invoice document and email when set. Blanket-PO clients auto-fill this from their standing number. If a PO is required and you don't have one yet, use Request PO from Billing Contact below instead of leaving this blank.">PO Number ⓘ</TKTooltip>{client?.po_required ? <span style={{color:C.danger,marginLeft:"4px"}}>*</span> : <span style={{color:C.muted,fontWeight:"400",marginLeft:"4px"}}>(optional)</span>}
              </div>
              <input style={css.inp} type="text" placeholder={client?.po_required ? 'Required for this client' : 'e.g. PO-12345'}
                value={poNumber} onChange={e=>setPoNumber(e.target.value)} />
            </div>
            <div>
              <div style={css.lbl}><TKTooltip text="Shown as a 'Pay Now' button on the emailed invoice and public invoice page. Leave blank to use the org-level default, or enter a URL to override for this invoice only.">Payment Link (optional) ⓘ</TKTooltip></div>
              <input style={css.inp} placeholder={org?.invoice_payment_link ? `Org default: ${org.invoice_payment_link}` : "https://…"}
                value={paymentLink} onChange={e=>setPaymentLink(e.target.value)} />
            </div>
          </div>

          {/* Memo */}
          <div style={{marginBottom:"20px"}}>
            <div style={css.lbl}><TKTooltip text="Optional note shown at the bottom of the invoice — payment instructions, thank-you message, or terms.">Memo (optional) ⓘ</TKTooltip></div>
            <textarea
              value={notes} onChange={e=>setNotes(e.target.value)}
              placeholder="Payment instructions, thank-you note, etc."
              style={{...css.inp,minHeight:"64px",resize:"vertical",fontFamily:"Inter,sans-serif",lineHeight:"1.5"}}
            />
          </div>

          {/* Attachments — edit mode */}
          <div style={{marginBottom:"20px"}}>
            <div style={{...css.lbl,marginBottom:"6px"}}>Attachments <span style={{fontWeight:"400",color:C.muted}}>(receipts, docs — PDF, image, max 10 MB each)</span></div>
            {attachments.length > 0 && (
              <div style={{display:"flex",flexDirection:"column",gap:"4px",marginBottom:"8px"}}>
                {attachments.map(att => (
                  <div key={att.id} style={{display:"flex",alignItems:"center",gap:"8px",padding:"6px 10px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:"6px"}}>
                    <span style={{fontSize:"14px"}}>{att.mime_type?.startsWith('image/') ? '🖼' : '📄'}</span>
                    <button onClick={()=>downloadAttachment(att)} style={{flex:1,textAlign:"left",background:"none",border:"none",cursor:"pointer",color:C.accent,fontSize:"12px",padding:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {att.file_name}
                    </button>
                    <span style={{fontSize:"11px",color:C.muted,flexShrink:0}}>{att.file_size ? (att.file_size/1024).toFixed(0)+'KB' : ''}</span>
                    <button onClick={()=>deleteAttachment(att)} style={{background:"none",border:"none",cursor:"pointer",color:C.danger,fontSize:"14px",lineHeight:1,padding:"0 2px",flexShrink:0}} title="Remove">×</button>
                  </div>
                ))}
              </div>
            )}
            <label style={{display:"flex",alignItems:"center",gap:"8px",padding:"8px 12px",border:`1px dashed ${C.border}`,borderRadius:"6px",cursor:uploading?"not-allowed":"pointer",color:uploading?C.muted:C.text,fontSize:"12px",background:C.bg}}>
              <input type="file" multiple accept=".pdf,image/*,.png,.jpg,.jpeg,.heic,.heif"
                style={{display:"none"}}
                disabled={uploading}
                onChange={e=>{ Array.from(e.target.files||[]).forEach(uploadAttachment); e.target.value=''; }} />
              {uploading ? '⏳ Uploading…' : '📎 Click to attach files'}
            </label>
            {attachErr && <div style={{fontSize:"12px",color:C.danger,marginTop:"4px"}}>{attachErr}</div>}
          </div>

          {/* Skip reminders toggle */}
          <div style={{display:"flex",alignItems:"center",gap:"10px",padding:"10px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:"6px",marginBottom:"14px"}}>
            <input type="checkbox" id="skipReminders" checked={skipReminders} onChange={e=>setSkipReminders(e.target.checked)}
              style={{width:"14px",height:"14px",cursor:"pointer",accentColor:C.accent}} />
            <label htmlFor="skipReminders" style={{fontSize:"13px",color:C.textDim,cursor:"pointer"}}>
              Disable automated reminder emails for this invoice
            </label>
          </div>

          {err && <div style={{padding:"10px 14px",background:"rgba(229,62,62,0.08)",border:"1px solid rgba(229,62,62,0.25)",borderRadius:"6px",marginBottom:"14px",fontSize:"13px",color:C.danger}}>{err}</div>}

          {/* Paid invoice warning */}
          {isEdit && existingInvoice?.status === 'paid' && (
            <div style={{padding:"12px 14px",background:"rgba(72,187,120,0.06)",border:"1px solid rgba(72,187,120,0.25)",borderRadius:"6px",marginBottom:"14px",fontSize:"13px",color:C.success}}>
              ✓ This invoice is marked as paid. You are viewing a read-only copy. To make changes, unmark it as paid from the Invoices list first.
            </div>
          )}

          {/* Actions */}
          <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
            <button style={css.btn()} onClick={onClose} disabled={saving}>Close</button>
            {(!isEdit || existingInvoice?.status !== 'paid') && <>
              <button style={css.btn()} onClick={()=>save('draft')} disabled={saving}>{saving?'Saving…':'Save as Draft'}</button>
              {client?.po_required && client?.po_mode !== 'blanket' && !poNumber.trim() && (
                <button style={css.btn()} onClick={()=>save('draft',{skipPOCheck:true,requestPO:true})}
                  disabled={saving||!client?.billing_contact_email}
                  title={!client?.billing_contact_email?'No billing contact email on file — add one before requesting a PO':'Save the invoice and email the total to the billing contact so they can enter a PO number, which sends the invoice automatically.'}>
                  {saving?'Saving…':'📩 Request PO from Billing Contact'}
                </button>
              )}
              <button style={css.btn("primary")} onClick={()=>save('sent')} disabled={saving}>{saving?'Saving…':'Mark as Sent'}</button>
            </>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SEND INVOICE EMAIL MODAL ────────────────────────────────────────────────
// A client can be emailed an invoice if it has a billing contact, an AP inbox,
// or both. An AP-only client (common for Bill.com/Ramp payers) gets just the
// PDF copy. PO requests still need a person, so those keep checking
// billing_contact_email specifically.
function canEmailInvoice(cl) {
  return !!(cl?.billing_contact_email?.trim() || cl?.ap_inbox_emails?.trim());
}

function SendInvoiceModal({ invoice, client, onClose, onConfirm }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [resendAp, setResendAp] = useState(false);
  const hasBilling = !!client?.billing_contact_email?.trim();
  const hasAp = !!client?.ap_inbox_emails?.trim();
  const apAlready = !!invoice.ap_copy_sent_at;
  // AP-only client whose AP inbox already has this invoice: the send would
  // email nobody unless the admin deliberately asks for a second copy.
  const nothingToSend = !hasBilling && hasAp && apAlready && !resendAp;

  useEffect(() => {
    if (!invoice?.id) return;
    supabase.from('invoice_attachments').select('id,file_name,file_size,mime_type').eq('invoice_id', invoice.id)
      .then(({ data }) => setAttachments(data || []));
  }, [invoice?.id]); // eslint-disable-line

  const handleSend = async () => {
    setSending(true);
    await onConfirm(message, resendAp);
    setSending(false);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1200}}>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px",padding:"28px",maxWidth:"480px",width:"90%"}}>
        <div style={{fontSize:"16px",fontWeight:"700",color:C.textBright,marginBottom:"4px"}}>
          {invoice.status === 'draft' ? 'Send Invoice' : 'Resend Invoice'}
        </div>
        <div style={{fontSize:"13px",color:C.muted,marginBottom:"20px"}}>
          {invoice.invoice_number} · {invoice.period ? new Date(invoice.period+'-02').toLocaleString("default",{month:"long",year:"numeric"}) : ''} · ${Math.round(invoice.total||0).toLocaleString()}
        </div>

        <div style={{padding:"12px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:"8px",marginBottom:"16px"}}>
          <div style={{fontSize:"11px",fontWeight:"700",color:C.muted,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"6px"}}>Sending To</div>
          {hasBilling ? (<>
            {client?.billing_contact_name && (
              <div style={{fontSize:"13px",color:C.textBright,marginBottom:"2px"}}>{client.billing_contact_name}</div>
            )}
            <div style={{fontSize:"13px",color:C.accent}}>{client?.billing_contact_email}</div>
            <div style={{fontSize:"12px",color:C.muted,marginTop:"4px"}}>{client?.name}</div>
          </>) : (
            <div style={{fontSize:"12px",color:C.muted}}>{client?.name} · no billing contact, so only the AP inbox is emailed</div>
          )}
          {hasAp && (
            <div style={hasBilling ? {marginTop:"10px",paddingTop:"10px",borderTop:`1px solid ${C.border}`} : {marginTop:"8px"}}>
              <div style={{fontSize:"11px",fontWeight:"700",color:C.muted,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"4px"}}>AP Inbox</div>
              <div style={{fontSize:"13px",color:C.accent}}>{client.ap_inbox_emails}</div>
              <div style={{fontSize:"12px",color:C.muted,marginTop:"4px"}}>
                {!apAlready
                  ? 'Will receive the invoice as a PDF attachment, with no amounts in the email body.'
                  : resendAp
                    ? `Received this invoice on ${new Date(invoice.ap_copy_sent_at).toLocaleDateString()}; another PDF copy will be sent.`
                    : `Already received this invoice as a PDF on ${new Date(invoice.ap_copy_sent_at).toLocaleDateString()}, so it won't be sent there again.`}
              </div>
              {apAlready && (
                <label style={{display:"flex",alignItems:"flex-start",gap:"8px",marginTop:"8px",fontSize:"12px",color:C.text,cursor:"pointer"}}>
                  <input type="checkbox" checked={resendAp} onChange={e=>setResendAp(e.target.checked)} style={{marginTop:"2px"}} />
                  <span>Send the PDF to the AP inbox again <span style={{color:C.muted}}>(only if their AP system lost it; a second copy can create a duplicate bill)</span></span>
                </label>
              )}
            </div>
          )}
        </div>

        {attachments.length > 0 && (
          <div style={{marginBottom:"16px"}}>
            <div style={{fontSize:"11px",fontWeight:"700",color:C.muted,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"6px"}}>📎 Attachments ({attachments.length})</div>
            <div style={{display:"flex",flexDirection:"column",gap:"4px"}}>
              {attachments.map(att => (
                <div key={att.id} style={{display:"flex",alignItems:"center",gap:"8px",padding:"5px 10px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:"6px",fontSize:"12px",color:C.text}}>
                  <span>{att.mime_type?.startsWith('image/') ? '🖼' : '📄'}</span>
                  <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{att.file_name}</span>
                  <span style={{color:C.muted,flexShrink:0}}>{att.file_size ? (att.file_size/1024).toFixed(0)+'KB' : ''}</span>
                </div>
              ))}
            </div>
            <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>These are visible on the client's invoice page via the "View & Download Invoice" link.</div>
          </div>
        )}

        {hasBilling ? (
          <div style={{marginBottom:"20px"}}>
            <div style={{fontSize:"12px",fontWeight:"600",color:C.textDim,marginBottom:"6px"}}>
              Personal Message <span style={{fontWeight:"400",color:C.muted}}>(optional)</span>
            </div>
            <textarea
              value={message}
              onChange={e=>setMessage(e.target.value)}
              placeholder="Add a note to your client — a thank-you, context about the invoice, payment instructions, etc."
              rows={4}
              style={{...css.inp,resize:"vertical",fontFamily:"Inter,sans-serif",lineHeight:"1.5",fontSize:"13px",width:"100%",boxSizing:"border-box"}}
            />
            <div style={{fontSize:"11px",color:C.muted,marginTop:"4px"}}>This message appears at the top of the billing contact's email, above the invoice details. The AP inbox copy never includes it.</div>
          </div>
        ) : (
          <div style={{fontSize:"12px",color:C.muted,marginBottom:"20px"}}>
            No personal message: AP systems only read the attached PDF. Add a billing contact to the client to send a readable email to a person too.
          </div>
        )}

        {nothingToSend && (
          <div style={{fontSize:"12px",color:C.badgeAmber,marginBottom:"12px"}}>
            Nothing to send: this client only has an AP inbox, and it already has this invoice.
          </div>
        )}

        <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
          <button style={css.btn()} onClick={onClose} disabled={sending}>Cancel</button>
          <button style={{...css.btn("primary"),opacity:nothingToSend?0.45:1,cursor:nothingToSend?"not-allowed":"pointer"}} onClick={handleSend} disabled={sending || nothingToSend}>
            {sending ? 'Sending…' : `✉ ${invoice.status === 'draft' ? 'Send Invoice' : 'Resend Invoice'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── INVOICE PREVIEW MODAL ────────────────────────────────────────────────────
function InvoicePreviewModal({ invoice, client, org, onClose, onMarkSent, onSendEmail }) {
  const [lines, setLines] = useState([]);
  useEffect(() => {
    if (!invoice?.id) return;
    supabase.from('invoice_line_items').select('*').eq('invoice_id', invoice.id).order('sort_order')
      .then(({ data }) => setLines(data || []));
  }, [invoice?.id]); // eslint-disable-line
  const periodLabel = invoice.period ? new Date(invoice.period+'-02').toLocaleString("default",{month:"long",year:"numeric"}) : '';
  const [markingSent, setMarkingSent] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [logoUploading, setLogoUploading] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const saveTimerRef = useRef(null);

  // Local settings state — initialized from org, drives live preview
  const [settings, setSettings] = useState({
    invoice_logo_url:         org?.invoice_logo_url || null,
    invoice_show_address:     org?.invoice_show_address !== false,
    invoice_show_phone:       org?.invoice_show_phone !== false,
    invoice_show_website:     org?.invoice_show_website !== false,
    invoice_show_client_address: org?.invoice_show_client_address !== false,
    invoice_show_client_phone:   org?.invoice_show_client_phone !== false,
  });
  const setSetting = (k, v) => setSettings(s => ({...s, [k]:v}));

  // Auto-save settings to org with debounce
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      if (!org?.id) return;
      const ok = await runDb('Saving invoice settings failed', supabase.from('organizations').update({
        invoice_logo_url:           settings.invoice_logo_url,
        invoice_show_address:       settings.invoice_show_address,
        invoice_show_phone:         settings.invoice_show_phone,
        invoice_show_website:       settings.invoice_show_website,
        invoice_show_client_address: settings.invoice_show_client_address,
        invoice_show_client_phone:   settings.invoice_show_client_phone,
      }).eq('id', org.id));
      if (ok) {
        setSettingsSaved(true);
        setTimeout(() => setSettingsSaved(false), 1800);
      }
    }, 800);
    return () => clearTimeout(saveTimerRef.current);
  }, [settings]); // eslint-disable-line

  // Effective logo: invoice-specific or fall back to app logo
  const effectiveLogo = settings.invoice_logo_url || org?.logo_url || null;

  const fmt$ = (n) => {
    const v = parseFloat(n||0);
    return v === Math.round(v)
      ? '$' + Math.round(v).toLocaleString()
      : '$' + v.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2});
  };

  const handlePrint = () => {
    const logoHtml = effectiveLogo ? `<img src="${effectiveLogo}" alt="${org?.name||''}" style="max-height:60px;max-width:180px;margin-bottom:10px;display:block;" />` : '';
    const orgAddrLines   = formatAddressLines(org);
    const clientAddrLines = settings.invoice_show_client_address ? formatAddressLines(client) : [];
    const clientAddrHtml  = clientAddrLines.map(l => `<div style="font-size:12px;color:#475569;">${l}</div>`).join('');
    const clientPhoneHtml = settings.invoice_show_client_phone && client?.phone ? `<div style="font-size:12px;color:#475569;">${fmtPhone(client.phone)}</div>` : '';
    const linesHtml = lines.map(l => `
      <tr>
        <td style="padding:11px 0;font-size:13px;color:#1e293b;border-bottom:1px solid #f1f5f9;">${l.description||''}</td>
        <td style="padding:11px 0;text-align:right;font-size:13px;color:#64748b;border-bottom:1px solid #f1f5f9;">${l.hours!=null?parseFloat(l.hours).toLocaleString('en-US',{maximumFractionDigits:2}):'—'}</td>
        <td style="padding:11px 0;text-align:right;font-size:13px;color:#64748b;border-bottom:1px solid #f1f5f9;">${l.rate!=null?fmt$(l.rate):'—'}</td>
        <td style="padding:11px 0;text-align:right;font-size:13px;font-weight:600;color:#1e293b;border-bottom:1px solid #f1f5f9;">${fmt$(l.amount)}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Invoice ${invoice.invoice_number}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Arial,Helvetica,sans-serif;color:#1e293b;padding:48px;max-width:800px;margin:0 auto;}
    @media print{body{padding:32px;}}
  </style>
</head>
<body>
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:40px;">
    <div>
      ${logoHtml}
      <div style="font-size:22px;font-weight:bold;">${org?.name||''}</div>
      ${settings.invoice_show_address ? orgAddrLines.map(l=>`<div style="font-size:12px;color:#64748b;">${l}</div>`).join('') : ''}
      ${settings.invoice_show_phone && org?.phone?`<div style="font-size:12px;color:#64748b;">${fmtPhone(org.phone)}</div>`:''}
      ${settings.invoice_show_website && org?.website?`<div style="font-size:12px;color:#64748b;">${org.website}</div>`:''}
    </div>
    <div style="text-align:right;">
      <div style="font-size:36px;font-weight:bold;letter-spacing:-1px;">INVOICE</div>
      <div style="font-size:15px;color:#64748b;margin-top:4px;">${invoice.invoice_number}</div>
    </div>
  </div>

  <div style="display:flex;justify-content:space-between;margin-bottom:32px;padding-bottom:20px;border-bottom:2px solid #e2e8f0;">
    <div>
      <div style="font-size:11px;font-weight:bold;color:#94a3b8;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:6px;">Bill To</div>
      <div style="font-size:16px;font-weight:600;">${client?.billing_name||client?.name||''}</div>
      ${clientAddrHtml}
      ${clientPhoneHtml}
    </div>
    <div style="text-align:right;font-size:13px;line-height:2;">
      ${invoice.issued_date?`<div><span style="color:#94a3b8;">Invoice Date&nbsp;&nbsp;</span><strong>${invoice.issued_date}</strong></div>`:''}
      ${invoice.due_date?`<div><span style="color:#94a3b8;">Due Date&nbsp;&nbsp;</span><strong>${invoice.due_date}</strong></div>`:''}
      ${invoice.payment_terms?`<div><span style="color:#94a3b8;">Terms&nbsp;&nbsp;</span><strong>${invoice.payment_terms}</strong></div>`:''}
      ${periodLabel?`<div><span style="color:#94a3b8;">Period&nbsp;&nbsp;</span><strong>${periodLabel}</strong></div>`:''}
      ${invoice.po_number?`<div><span style="color:#94a3b8;">PO Number&nbsp;&nbsp;</span><strong>${invoice.po_number}</strong></div>`:''}
    </div>
  </div>

  <table style="width:100%;border-collapse:collapse;margin-bottom:24px;">
    <thead>
      <tr style="border-bottom:2px solid #e2e8f0;">
        <th style="text-align:left;padding:8px 0;font-size:11px;font-weight:bold;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;">Description</th>
        <th style="text-align:right;padding:8px 0;font-size:11px;font-weight:bold;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;width:70px;">Qty</th>
        <th style="text-align:right;padding:8px 0;font-size:11px;font-weight:bold;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;width:80px;">Rate</th>
        <th style="text-align:right;padding:8px 0;font-size:11px;font-weight:bold;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;width:90px;">Amount</th>
      </tr>
    </thead>
    <tbody>${linesHtml}</tbody>
  </table>

  <div style="display:flex;justify-content:flex-end;margin-bottom:32px;">
    <div style="min-width:220px;border-top:2px solid #1e293b;padding-top:12px;display:flex;justify-content:space-between;align-items:baseline;">
      <span style="font-size:15px;font-weight:bold;">Total Due</span>
      <span style="font-size:22px;font-weight:bold;">${fmt$(invoice.total)}</span>
    </div>
  </div>

  ${invoice.notes?`
  <div style="padding:16px;background:#f8fafc;border-radius:6px;border:1px solid #e2e8f0;">
    <div style="font-size:11px;font-weight:bold;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Memo</div>
    <div style="font-size:13px;color:#475569;line-height:1.6;">${invoice.notes}</div>
  </div>`:''}
</body>
</html>`;

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const handleMarkSent = async () => {
    setMarkingSent(true);
    await onMarkSent();
    setMarkingSent(false);
  };

  // Sidebar logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !org?.id) return;
    setLogoUploading(true);
    try {
      const path = `${org.id}/invoice-logo-${file.name}`;
      const { error: upErr } = await supabase.storage.from('org-logos').upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: { publicUrl } } = supabase.storage.from('org-logos').getPublicUrl(path);
      setSetting('invoice_logo_url', publicUrl);
    } catch (err) {
      notifyError('Logo upload failed: ' + (err?.message || err));
    } finally {
      setLogoUploading(false);
    }
  };

  // Toggle row helper
  const ToggleRow = ({ label, field }) => (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid rgba(255,255,255,0.06)`}}>
      <span style={{fontSize:"12px",color:C.textDim}}>{label}</span>
      <button
        onClick={() => setSetting(field, !settings[field])}
        style={{
          width:"36px",height:"20px",borderRadius:"10px",border:"none",cursor:"pointer",
          background: settings[field] ? C.accent : "rgba(255,255,255,0.15)",
          position:"relative",transition:"background 0.2s",flexShrink:0,
        }}
      >
        <span style={{
          position:"absolute",top:"2px",width:"16px",height:"16px",borderRadius:"50%",
          background:"#fff",transition:"left 0.2s",
          left: settings[field] ? "18px" : "2px",
        }} />
      </button>
    </div>
  );

  const sidebarW = sidebarOpen ? 260 : 44;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.88)",display:"flex",flexDirection:"column",zIndex:1100}}>
      {/* Toolbar */}
      <div style={{position:"sticky",top:0,background:"rgba(15,17,23,0.97)",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:"10px",padding:"10px 24px",zIndex:1101,flexShrink:0}}>
        <button onClick={onClose} style={{...css.btn(),fontSize:"13px"}}>← Close</button>
        <button onClick={handlePrint} style={{...css.btn(),fontSize:"13px"}}>🖨 Print / Save as PDF</button>
        <button
          onClick={() => setSidebarOpen(o => !o)}
          title={sidebarOpen ? "Hide settings" : "Show settings"}
          style={{...css.btn(),fontSize:"13px",padding:"6px 10px"}}
        >⚙</button>
        <div style={{flex:1}} />
        {settingsSaved && <span style={{fontSize:"11px",color:C.success,opacity:0.9}}>Settings saved</span>}
        {onMarkSent && (
          <button onClick={handleMarkSent} disabled={markingSent} style={{...css.btn(),fontSize:"13px"}}>
            {markingSent ? 'Updating…' : '✓ Mark as Sent'}
          </button>
        )}
        {onSendEmail && (
          <button onClick={onSendEmail} style={{...css.btn("primary"),fontSize:"13px"}}>
            {invoice.status==='draft' ? '✉ Send Email' : '✉ Resend Email'}
          </button>
        )}
        {!onMarkSent && !onSendEmail && invoice.status==='sent' && <span style={css.badge("blue")}>Sent</span>}
        {!onMarkSent && !onSendEmail && invoice.status==='paid' && <span style={css.badge("green")}>Paid</span>}
      </div>

      {/* Body: sidebar + preview */}
      <div style={{flex:1,display:"flex",overflow:"hidden"}}>

        {/* Collapsible left sidebar */}
        <div style={{
          width:`${sidebarW}px`,flexShrink:0,
          background:"rgba(15,17,23,0.97)",
          borderRight:`1px solid ${C.border}`,
          transition:"width 0.2s",overflow:"hidden",
          display:"flex",flexDirection:"column",
        }}>
          {sidebarOpen ? (
            <div style={{padding:"16px",overflowY:"auto",flex:1,minWidth:"260px"}}>
              {/* Section: Logo */}
              <div style={{marginBottom:"20px"}}>
                <div style={{fontSize:"10px",fontWeight:"700",color:C.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"10px"}}>Invoice Logo</div>
                <div style={{marginBottom:"8px",borderRadius:"6px",overflow:"hidden",background:"rgba(255,255,255,0.04)",border:`1px solid ${C.border}`,padding:"8px",display:"flex",alignItems:"center",justifyContent:"center",minHeight:"56px"}}>
                  {effectiveLogo
                    ? <img src={effectiveLogo} alt="Invoice logo" style={{maxHeight:"50px",maxWidth:"200px",objectFit:"contain"}} />
                    : <span style={{fontSize:"11px",color:C.muted}}>No logo set</span>
                  }
                </div>
                <div style={{display:"flex",gap:"6px"}}>
                  <label style={{flex:1}}>
                    <div style={{...css.btn(),fontSize:"11px",textAlign:"center",cursor:"pointer",padding:"5px 8px"}}>
                      {logoUploading ? 'Uploading…' : '↑ Upload'}
                    </div>
                    <input type="file" accept="image/*" style={{display:"none"}} onChange={handleLogoUpload} disabled={logoUploading} />
                  </label>
                  {settings.invoice_logo_url && (
                    <button
                      onClick={() => setSetting('invoice_logo_url', null)}
                      style={{...css.btn(),fontSize:"11px",padding:"5px 8px"}}
                      title="Revert to app logo"
                    >↩ App Logo</button>
                  )}
                </div>
              </div>

              {/* Section: Your Info */}
              <div style={{marginBottom:"20px"}}>
                <div style={{fontSize:"10px",fontWeight:"700",color:C.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"8px"}}>Your Info on Invoice</div>
                <ToggleRow label="Show Address"  field="invoice_show_address" />
                <ToggleRow label="Show Phone"    field="invoice_show_phone" />
                <ToggleRow label="Show Website"  field="invoice_show_website" />
              </div>

              {/* Section: Client */}
              <div style={{marginBottom:"20px"}}>
                <div style={{fontSize:"10px",fontWeight:"700",color:C.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"8px"}}>Client Section</div>
                <ToggleRow label="Show Client Address" field="invoice_show_client_address" />
                {settings.invoice_show_client_address && formatAddressLines(client).length === 0 && (
                  <div style={{fontSize:"11px",color:C.muted,marginTop:"6px",fontStyle:"italic",lineHeight:"1.4"}}>
                    No address on file. Add it in Settings → Clients.
                  </div>
                )}
                <ToggleRow label="Show Client Phone" field="invoice_show_client_phone" />
                {settings.invoice_show_client_phone && !client?.phone && (
                  <div style={{fontSize:"11px",color:C.muted,marginTop:"6px",fontStyle:"italic",lineHeight:"1.4"}}>
                    No phone on file. Add it in Settings → Clients.
                  </div>
                )}
              </div>

              <div style={{fontSize:"10px",color:C.muted,marginTop:"auto",paddingTop:"8px",lineHeight:"1.5"}}>
                Changes save automatically and apply to all future invoice previews.
              </div>
            </div>
          ) : (
            /* Collapsed: just show expand hint */
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",paddingTop:"16px",gap:"16px"}}>
              <button
                onClick={() => setSidebarOpen(true)}
                title="Show settings"
                style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:"16px",padding:"4px"}}
              >⚙</button>
            </div>
          )}
        </div>

        {/* Invoice preview scroll area */}
        <div style={{flex:1,overflowY:"auto",padding:"32px 16px 48px",display:"flex",justifyContent:"center",alignItems:"flex-start"}}>
          <div style={{background:"#fff",color:"#1e293b",width:"min(750px,96%)",padding:"48px",fontFamily:"Arial,Helvetica,sans-serif",boxShadow:"0 4px 40px rgba(0,0,0,0.4)",borderRadius:"4px"}}>
            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"40px"}}>
              <div>
                {effectiveLogo && <img src={effectiveLogo} alt={org?.name||''} style={{maxHeight:"60px",maxWidth:"180px",marginBottom:"10px",display:"block"}} />}
                <div style={{fontSize:"22px",fontWeight:"bold",color:"#1e293b"}}>{org?.name||''}</div>
                {settings.invoice_show_address && formatAddressLines(org).map((ln,i) => (
                  <div key={i} style={{fontSize:"12px",color:"#64748b",marginTop:i===0?"4px":0}}>{ln}</div>
                ))}
                {settings.invoice_show_phone   && org?.phone   && <div style={{fontSize:"12px",color:"#64748b"}}>{org.phone}</div>}
                {settings.invoice_show_website && org?.website && <div style={{fontSize:"12px",color:"#64748b"}}>{org.website}</div>}
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:"36px",fontWeight:"bold",color:"#1e293b",letterSpacing:"-1px"}}>INVOICE</div>
                <div style={{fontSize:"15px",color:"#64748b",marginTop:"4px"}}>{invoice.invoice_number}</div>
              </div>
            </div>

            {/* Billing info */}
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:"32px",paddingBottom:"20px",borderBottom:"2px solid #e2e8f0"}}>
              <div>
                <div style={{fontSize:"11px",fontWeight:"bold",color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:"6px"}}>Bill To</div>
                <div style={{fontSize:"16px",fontWeight:"600",color:"#1e293b"}}>{client?.name||'—'}</div>
                {settings.invoice_show_client_address && formatAddressLines(client).length > 0 && (
                  <div style={{marginTop:"4px"}}>
                    {formatAddressLines(client).map((ln,i) => (
                      <div key={i} style={{fontSize:"12px",color:"#475569"}}>{ln}</div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{textAlign:"right",fontSize:"13px",lineHeight:"2"}}>
                {invoice.issued_date   && <div><span style={{color:"#94a3b8"}}>Invoice Date&nbsp;&nbsp;</span><strong>{invoice.issued_date}</strong></div>}
                {invoice.due_date      && <div><span style={{color:"#94a3b8"}}>Due Date&nbsp;&nbsp;</span><strong>{invoice.due_date}</strong></div>}
                {invoice.payment_terms && <div><span style={{color:"#94a3b8"}}>Terms&nbsp;&nbsp;</span><strong>{invoice.payment_terms}</strong></div>}
                {periodLabel           && <div><span style={{color:"#94a3b8"}}>Period&nbsp;&nbsp;</span><strong>{periodLabel}</strong></div>}
                {invoice.po_number     && <div><span style={{color:"#94a3b8"}}>PO Number&nbsp;&nbsp;</span><strong>{invoice.po_number}</strong></div>}
              </div>
            </div>

            {/* Line items */}
            <table style={{width:"100%",borderCollapse:"collapse",marginBottom:"24px"}}>
              <thead>
                <tr style={{borderBottom:"2px solid #e2e8f0"}}>
                  <th style={{textAlign:"left",padding:"8px 0",fontSize:"11px",fontWeight:"bold",color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.5px"}}>Description</th>
                  <th style={{textAlign:"right",padding:"8px 0",fontSize:"11px",fontWeight:"bold",color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.5px",width:"70px"}}>Qty</th>
                  <th style={{textAlign:"right",padding:"8px 0",fontSize:"11px",fontWeight:"bold",color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.5px",width:"80px"}}>Rate</th>
                  <th style={{textAlign:"right",padding:"8px 0",fontSize:"11px",fontWeight:"bold",color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.5px",width:"90px"}}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line,i)=>(
                  <tr key={i}>
                    <td style={{padding:"11px 0",fontSize:"13px",color:"#1e293b",borderBottom:"1px solid #f1f5f9"}}>{line.description||''}</td>
                    <td style={{padding:"11px 0",textAlign:"right",fontSize:"13px",color:"#64748b",borderBottom:"1px solid #f1f5f9"}}>{line.hours!=null?parseFloat(line.hours).toLocaleString('en-US',{maximumFractionDigits:2}):'—'}</td>
                    <td style={{padding:"11px 0",textAlign:"right",fontSize:"13px",color:"#64748b",borderBottom:"1px solid #f1f5f9"}}>{line.rate!=null?fmt$(line.rate):'—'}</td>
                    <td style={{padding:"11px 0",textAlign:"right",fontSize:"13px",fontWeight:"600",color:"#1e293b",borderBottom:"1px solid #f1f5f9"}}>{fmt$(line.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Total */}
            <div style={{display:"flex",justifyContent:"flex-end",marginBottom:"32px"}}>
              <div style={{minWidth:"220px",borderTop:"2px solid #1e293b",paddingTop:"12px",display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                <span style={{fontSize:"15px",fontWeight:"bold",color:"#1e293b"}}>Total Due</span>
                <span style={{fontSize:"22px",fontWeight:"bold",color:"#1e293b"}}>{fmt$(invoice.total)}</span>
              </div>
            </div>

            {/* Memo */}
            {invoice.notes && (
              <div style={{padding:"16px",background:"#f8fafc",borderRadius:"6px",border:"1px solid #e2e8f0"}}>
                <div style={{fontSize:"11px",fontWeight:"bold",color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"6px"}}>Notes</div>
                <div style={{fontSize:"13px",color:"#475569",lineHeight:"1.6"}}>{invoice.notes}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CLIENT DETAIL DRAWER ─────────────────────────────────────────────────────
function ClientDetailDrawer({ clientId, clients, entries, invoices, assignments, users, orgMembers, monthLocks, org, orgId, qboConnected, supabase, onClose, onEdit }) {
  const client = clients.find(c => c.id === clientId);

  const [tab, setTab]         = useState('overview');

  const [logRange, setLogRange] = useState('this_month');
  const [logStart, setLogStart] = useState('');
  const [logEnd, setLogEnd]     = useState('');

  const [emailLog, setEmailLog] = useState([]);
  const [emailLogLoading, setEmailLogLoading] = useState(false);

  useEffect(() => {
    setTab('overview');
    setEmailLog([]);
    if (clientId) {
      setEmailLogLoading(true);
      supabase.from('email_log').select('*').eq('client_id', clientId).order('created_at', { ascending: false })
        .then(({ data, error }) => {
          setEmailLogLoading(false);
          if (!error) setEmailLog(data || []);
        });
    }
  }, [clientId]);

  if (!client) return null;

  const mLabel = m => modelLabel(m, 'label');

  const clientAssignments = (assignments || []).filter(a => a.client_id === clientId);
  const memberForAssignment = (a) => {
    const u = (users || []).find(u => u.id === a.user_id);
    const om = (orgMembers || []).find(m => m.user_id === a.user_id);
    return { name: u?.name || 'Unknown', seatType: om?.seat_type || '' };
  };
  const payLabel = (a) => {
    if (a.pay_type === 'flat_monthly') return `$${a.monthly_amount}/mo flat`;
    if (a.pay_type === 'day_rate') return `$${a.rate}/day`;
    if (a.pay_type === 'fixed_project') return `$${a.project_amount} project`;
    if (a.pay_type === 'hybrid') return `$${a.hybrid_base||0}/mo + $${a.hybrid_overage_rate||0}/hr over ${a.hybrid_included_hours||0}h`;
    if (a.pay_type === 'percent') return `${a.pay_percent||0}% of client rate`;
    return `$${a.rate}/hr`;
  };

  const now = new Date();
  const curPeriod = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  const lockedUserIds = new Set(
    (monthLocks || []).filter(l => l.client_id === clientId && l.period === curPeriod && !l.reopened_at).map(l => l.user_id)
  );

  const logBounds = (() => {
    const today = now.toISOString().slice(0,10);
    const y = now.getFullYear();
    if (logRange === 'this_month') return { start: `${y}-${String(now.getMonth()+1).padStart(2,'0')}-01`, end: today };
    if (logRange === 'last_month') {
      const d = new Date(y, now.getMonth()-1, 1);
      const last = new Date(y, now.getMonth(), 0);
      return { start: d.toISOString().slice(0,10), end: last.toISOString().slice(0,10) };
    }
    if (logRange === 'last_3') { const d = new Date(now); d.setMonth(d.getMonth()-3); return { start: d.toISOString().slice(0,10), end: today }; }
    if (logRange === 'last_6') { const d = new Date(now); d.setMonth(d.getMonth()-6); return { start: d.toISOString().slice(0,10), end: today }; }
    if (logRange === 'ytd')    return { start: `${y}-01-01`, end: today };
    if (logRange === 'custom') return { start: logStart||'', end: logEnd||today };
    return { start: '', end: today };
  })();

  const clientEntries = (entries || []).filter(e => {
    if (e.client_id !== clientId) return false;
    if (logBounds.start && e.entry_date < logBounds.start) return false;
    if (logBounds.end   && e.entry_date > logBounds.end)   return false;
    return true;
  }).sort((a,b) => b.entry_date.localeCompare(a.entry_date));
  const totalHours = clientEntries.reduce((s,e) => s + parseFloat(e.hours||0), 0);

  const clientInvoices = (invoices || []).filter(i => i.client_id === clientId)
    .sort((a,b) => (b.issued_date||'').localeCompare(a.issued_date||''));

  const statusColor = s => s==='paid' ? '#16a34a' : s==='sent' ? C.accent : C.muted;

  const badgeStyle = (color) => ({
    display:"inline-block", padding:"2px 8px", borderRadius:"10px", fontSize:"11px", fontWeight:"600",
    background: color ? color + '22' : C.border, color: color || C.muted,
  });

  const toggleUI = (on, onToggle) => (
    <button onClick={onToggle} style={{width:"36px",height:"20px",borderRadius:"10px",border:"none",cursor:"pointer",flexShrink:0,
      background:on?C.accent:C.border,position:"relative",transition:"background 0.2s"}}>
      <div style={{position:"absolute",top:"2px",left:on?"18px":"2px",width:"16px",height:"16px",borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
    </button>
  );

  const sectionHead = (label) => (
    <div style={{fontSize:"11px",fontWeight:"700",color:C.muted,textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:"10px"}}>{label}</div>
  );

  const fieldCard = (children) => (
    <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:"8px",padding:"14px 16px",marginBottom:"12px"}}>{children}</div>
  );

  const TABS = [
    { key:'overview',  label:'Overview' },
    { key:'time_logs', label:`Time Logs${clientEntries.length ? ` · ${totalHours.toFixed(1)}h` : ''}` },
    { key:'invoices',  label:`Invoices${clientInvoices.length ? ` (${clientInvoices.length})` : ''}` },
    { key:'emails',    label:`Emails${emailLog.length ? ` (${emailLog.length})` : ''}` },
  ];

  // Built per-render (not module scope) so these always reflect the current
  // theme/custom colors -- see the Theming Rule freeze-at-module-load gotcha.
  const EMAIL_CATEGORY_LABEL = {
    invoice: 'Invoice', invoice_reminder: 'Reminder', po_request: 'PO Request', contract: 'Contract',
  };
  const EMAIL_SUBTYPE_LABEL = {
    sent: 'Sent', resent: 'Resent', requested: 'Requested', ap_copy: 'PDF to AP inbox',
    reminder_14d: '14 days before due', reminder_7d: '7 days before due', reminder_2d: '2 days before due',
    reminder_due: 'Due today', reminder_2d_after: '2 days overdue', reminder_7d_after: '7 days overdue',
  };
  const emailTypeLabel = (row) => {
    const cat = EMAIL_CATEGORY_LABEL[row.category] || row.category;
    if (row.subtype?.startsWith('reminder_weekly_w')) {
      const n = row.subtype.replace('reminder_weekly_w', '');
      return `${cat} — ${n} week${n === '1' ? '' : 's'} overdue`;
    }
    const sub = EMAIL_SUBTYPE_LABEL[row.subtype];
    return sub ? `${cat} — ${sub}` : cat;
  };
  const EMAIL_STATUS_CFG = {
    sent:           { label:'Sent',       color:C.muted },
    delivered:      { label:'Delivered',  color:'#16a34a' },
    opened:         { label:'Opened',     color:'#0284c7' },
    clicked:        { label:'Clicked',    color:'#7c3aed' },
    bounced:        { label:'Bounced',    color:'#dc2626' },
    spam_complaint: { label:'Spam ⚠',    color:'#b45309' },
    delayed:        { label:'Delayed',    color:'#d97706' },
    failed:         { label:'Failed',     color:'#dc2626' },
  };

  return (
    <>
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1100}} onClick={onClose} />
      <div style={{position:"fixed",top:0,right:0,bottom:0,width:"min(640px,100vw)",background:C.surface,
        borderLeft:`1px solid ${C.border}`,zIndex:1101,display:"flex",flexDirection:"column",overflowY:"auto"}}>

        {/* Header */}
        <div style={{padding:"20px 24px 0",borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"12px"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:"8px",flexWrap:"wrap",marginBottom:"4px"}}>
                <span style={{fontSize:"20px",fontWeight:"700",color:C.textBright}}>{client.name}{client.alias ? <span style={{fontSize:"13px",fontWeight:"400",color:C.muted,marginLeft:"6px"}}>({client.alias})</span> : null}</span>
                {client.is_active === false && <span style={badgeStyle()}>Archived</span>}
                {client.is_internal
                  ? <StatusBadge tone="neutral">Internal</StatusBadge>
                  : <span style={css.badge(modelBadge(client.model))}>{mLabel(client.model)}</span>}
                {qboConnected && client.qbo_customer_id && <StatusBadge tone="good" title="Synced to QuickBooks as a customer.">QBO ✓</StatusBadge>}
              </div>
              {client.billing_contact_name && (
                <div style={{fontSize:"13px",color:C.muted}}>
                  {client.billing_contact_name}
                  {client.billing_contact_email ? ` · ${client.billing_contact_email}` : ''}
                </div>
              )}
            </div>
            <div style={{display:"flex",gap:"8px",flexShrink:0,marginLeft:"12px"}}>
              <button style={{padding:"6px 14px",fontSize:"12px",fontWeight:"600",borderRadius:"6px",border:"none",cursor:"pointer",
                background:C.accent,color:"#fff"}} onClick={()=>onEdit?.(clientId)}
                title="Opens the same editor as Edit in the Clients list menu.">Edit</button>
              <button style={{padding:"6px 14px",fontSize:"12px",fontWeight:"600",borderRadius:"6px",border:`1px solid ${C.border}`,
                cursor:"pointer",background:"none",color:C.text}} onClick={onClose}>✕ Close</button>
            </div>
          </div>

          {/* Tabs */}
          <div style={{paddingTop:"8px"}}>
            <TabBar tabs={TABS.map(t=>[t.key,t.label])} active={tab} onChange={setTab} />
          </div>
        </div>

        {/* Body */}
        <div style={{padding:"20px 24px",flex:1}}>

          {/* ── OVERVIEW READ ── */}
          {tab==='overview' && (
            <div>
              {fieldCard(<>
                {sectionHead('Billing')}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
                  <div><div style={{fontSize:"11px",color:C.muted,marginBottom:"2px"}}>Model</div>
                    <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright}}>{mLabel(client.model)}</div></div>
                  {client.model==='hourly' && <div><div style={{fontSize:"11px",color:C.muted,marginBottom:"2px"}}>Rate</div>
                    <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright}}>${client.rate}/hr</div></div>}
                  {client.model==='retainer' && <div><div style={{fontSize:"11px",color:C.muted,marginBottom:"2px"}}>Monthly Fee</div>
                    <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright}}>${Number(client.retainer||0).toLocaleString()}/mo</div></div>}
                  {client.model==='hybrid' && <>
                    <div><div style={{fontSize:"11px",color:C.muted,marginBottom:"2px"}}>Base Retainer</div>
                      <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright}}>${Number(client.retainer||0).toLocaleString()}/mo</div></div>
                    <div><div style={{fontSize:"11px",color:C.muted,marginBottom:"2px"}}>Included Hours</div>
                      <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright}}>{client.hours}h</div></div>
                    <div><div style={{fontSize:"11px",color:C.muted,marginBottom:"2px"}}>Overage Rate</div>
                      <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright}}>${client.overage}/hr</div></div>
                  </>}
                  {client.monthly_hours_threshold && <div><div style={{fontSize:"11px",color:C.muted,marginBottom:"2px"}}>Alert Threshold</div>
                    <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright}}>{client.monthly_hours_threshold}h/mo</div></div>}
                </div>
              </>)}

              {(client.billing_contact_name || client.billing_contact_email || client.ap_inbox_emails || client.phone || client.address_line1 || client.city) && fieldCard(<>
                {sectionHead('Contact & Address')}
                <div style={{display:"flex",flexDirection:"column",gap:"3px",fontSize:"13px"}}>
                  {client.billing_contact_name && <div style={{fontWeight:"600",color:C.textBright}}>{client.billing_contact_name}</div>}
                  {client.billing_contact_email && <div style={{color:C.accent}}>{client.billing_contact_email}</div>}
                  {client.ap_inbox_emails && <div style={{color:C.muted}}>AP inbox: <span style={{color:C.accent}}>{client.ap_inbox_emails}</span></div>}
                  {client.phone && <div style={{color:C.muted}}>{client.phone}</div>}
                  {client.address_line1 && <div style={{color:C.muted,marginTop:"4px"}}>{client.address_line1}{client.address_line2?`, ${client.address_line2}`:''}</div>}
                  {client.city && <div style={{color:C.muted}}>{[client.city,client.state,client.zip].filter(Boolean).join(', ')}</div>}
                  {client.country && <div style={{color:C.muted}}>{client.country}</div>}
                </div>
              </>)}

              {clientAssignments.length > 0 && fieldCard(<>
                {sectionHead('Team Assignments')}
                <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                  {clientAssignments.map(a => {
                    const m = memberForAssignment(a);
                    return (
                      <div key={a.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                        padding:"8px 10px",background:C.surface,borderRadius:"6px",border:`1px solid ${C.border}`}}>
                        <div>
                          <div style={{fontSize:"13px",fontWeight:"600",color:C.textBright}}>{m.name}</div>
                          <div style={{fontSize:"11px",color:C.muted,marginTop:"2px"}}>{m.seatType} · {payLabel(a)}</div>
                        </div>
                        {lockedUserIds.has(a.user_id) && <span style={badgeStyle()}>🔒 Locked</span>}
                      </div>
                    );
                  })}
                </div>
              </>)}

              {(qboConnected || client.is_nonprofit || client.is_internal || client.invoice_reminders_enabled === false) && fieldCard(<>
                {sectionHead('Settings')}
                <div style={{display:"flex",flexDirection:"column",gap:"6px",fontSize:"13px"}}>
                  {client.is_internal && <div style={{color:C.muted}}>Marked as <span style={{color:'#f59e0b',fontWeight:"600"}}>internal</span> — excluded from billing reports</div>}
                  {client.invoice_reminders_enabled === false && <div style={{color:C.danger}}>Invoice reminders disabled</div>}
                  {client.is_nonprofit && <div style={{color:C.muted}}>Nonprofit · {client.donation_percentage}% donation on invoices</div>}
                  {qboConnected && <div style={{display:"flex",justifyContent:"space-between"}}>
                    <span style={{color:C.muted}}>QuickBooks Sync</span>
                    <span style={badgeStyle(client.qbo_customer_id?'#16a34a':undefined)}>
                      {client.qbo_sync_enabled===false?'Excluded':client.qbo_customer_id?`Customer ${client.qbo_customer_id}`:'Not synced'}
                    </span>
                  </div>}
                </div>
              </>)}
            </div>
          )}

          {/* ── TIME LOGS ── */}
          {tab==='time_logs' && (
            <div>
              <div style={{display:"flex",gap:"6px",marginBottom:"14px",flexWrap:"wrap",alignItems:"center"}}>
                {[['this_month','This Month'],['last_month','Last Month'],['last_3','Last 3 Mo'],['last_6','Last 6 Mo'],['ytd','YTD'],['custom','Custom']].map(([v,l])=>(
                  <button key={v} onClick={()=>setLogRange(v)}
                    style={{padding:"5px 10px",fontSize:"12px",fontWeight:"600",borderRadius:"5px",border:"none",cursor:"pointer",
                      background:logRange===v?C.accent:C.bg,color:logRange===v?"#fff":C.muted}}>
                    {l}
                  </button>
                ))}
                {logRange==='custom' && <>
                  <input type="date" value={logStart} onChange={e=>setLogStart(e.target.value)}
                    style={{padding:"5px 8px",fontSize:"12px",borderRadius:"5px",border:`1px solid ${C.border}`,background:C.bg,color:C.textBright}} />
                  <span style={{color:C.muted,fontSize:"12px"}}>→</span>
                  <input type="date" value={logEnd} onChange={e=>setLogEnd(e.target.value)}
                    style={{padding:"5px 8px",fontSize:"12px",borderRadius:"5px",border:`1px solid ${C.border}`,background:C.bg,color:C.textBright}} />
                </>}
              </div>
              {clientEntries.length === 0
                ? <div style={{textAlign:"center",color:C.muted,padding:"48px 0",fontSize:"13px"}}>No time entries in this range.</div>
                : (
                  <>
                    <div style={{display:"flex",gap:"20px",marginBottom:"12px",fontSize:"13px",color:C.muted}}>
                      <span><strong style={{color:C.textBright}}>{totalHours.toFixed(1)}h</strong> total</span>
                      <span><strong style={{color:C.textBright}}>{clientEntries.length}</strong> entries</span>
                    </div>
                    <div style={{overflowX:"auto"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
                        <thead>
                          <tr style={{borderBottom:`1px solid ${C.border}`}}>
                            {['Date','Member','Description','Hours'].map((h,i)=>(
                              <th key={h} style={{padding:"6px 10px",textAlign:i===3?"right":"left",color:C.muted,fontWeight:"600",whiteSpace:"nowrap"}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {clientEntries.map(e => {
                            const u = (users||[]).find(u=>u.id===e.user_id);
                            return (
                              <tr key={e.id} style={{borderBottom:`1px solid ${C.border}`}}>
                                <td style={{padding:"7px 10px",color:C.muted,whiteSpace:"nowrap"}}>{e.entry_date}</td>
                                <td style={{padding:"7px 10px",color:C.text,whiteSpace:"nowrap"}}>{u?.name||'—'}</td>
                                <td style={{padding:"7px 10px",color:C.text,maxWidth:"200px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.description||<span style={{color:C.muted}}>—</span>}</td>
                                <td style={{padding:"7px 10px",textAlign:"right",color:C.textBright,fontWeight:"600"}}>{parseFloat(e.hours).toFixed(1)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )
              }
            </div>
          )}

          {/* ── INVOICES ── */}
          {tab==='invoices' && (
            <div>
              {clientInvoices.length === 0
                ? <div style={{textAlign:"center",color:C.muted,padding:"48px 0",fontSize:"13px"}}>No invoices for this client yet.</div>
                : (
                  <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                    {clientInvoices.map(inv => {
                      const periodLabel = inv.period
                        ? new Date(inv.period+'-02').toLocaleString("default",{month:"long",year:"numeric"})
                        : '—';
                      const sc = statusColor(inv.status);
                      return (
                        <div key={inv.id} style={{padding:"12px 14px",background:C.bg,border:`1px solid ${C.border}`,
                          borderRadius:"8px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <div>
                            <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"4px"}}>
                              <span style={{fontSize:"13px",fontWeight:"700",color:C.textBright}}>{inv.invoice_number}</span>
                              <span style={badgeStyle(sc)}>{inv.status}</span>
                            </div>
                            <div style={{fontSize:"11px",color:C.muted}}>
                              {periodLabel}{inv.due_date?` · Due ${inv.due_date}`:''}
                            </div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:"14px",fontWeight:"700",color:C.textBright}}>
                              ${parseFloat(inv.total||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}
                            </div>
                            {inv.paid_at && <div style={{fontSize:"10px",color:C.muted,marginTop:"2px"}}>Paid {new Date(inv.paid_at).toLocaleDateString()}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              }
            </div>
          )}

          {/* ── EMAILS ── */}
          {tab==='emails' && (
            <div>
              {emailLogLoading ? (
                <div style={{textAlign:"center",color:C.muted,padding:"32px 0",fontSize:"13px"}}>Loading…</div>
              ) : emailLog.length === 0 ? (
                <div style={{textAlign:"center",color:C.muted,padding:"48px 0",fontSize:"13px"}}>No emails sent to this client yet.</div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                  {emailLog.map(row => {
                    const cfg = EMAIL_STATUS_CFG[row.status] || { label: row.status, color: C.muted };
                    const relatedInvoice = row.invoice_id ? clientInvoices.find(i => i.id === row.invoice_id) : null;
                    return (
                      <div key={row.id} style={{padding:"12px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:"8px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"10px"}}>
                          <div style={{minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"4px",flexWrap:"wrap"}}>
                              <span style={{fontSize:"13px",fontWeight:"700",color:C.textBright}}>{emailTypeLabel(row)}</span>
                              {relatedInvoice && <span style={{fontSize:"11px",color:C.muted}}>{relatedInvoice.invoice_number}</span>}
                            </div>
                            {row.subject && <div style={{fontSize:"12px",color:C.muted,marginBottom:"2px"}}>{row.subject}</div>}
                            <div style={{fontSize:"11px",color:C.muted}}>To {row.recipient_email} · {new Date(row.created_at).toLocaleString()}</div>
                            {row.error_message && <div style={{fontSize:"11px",color:C.danger,marginTop:"4px"}}>{row.error_message}</div>}
                          </div>
                          <div style={{textAlign:"right",flexShrink:0}}>
                            <span style={badgeStyle(cfg.color)}>{cfg.label}</span>
                            <div style={{fontSize:"10px",color:C.muted,marginTop:"4px"}}>{new Date(row.status_at).toLocaleDateString()}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
